// src/lib/i18n.js — Georgian / English translations
// Switch language: setLanguage('ka') or setLanguage('en')

const LS_KEY = 'sva_lang'
// ⚠️ localStorage is not always reachable, and the failures are not hypothetical: there is none
// under `node --test` (which is why no lib test could call t() until 2026-09-04 — a module that
// labels anything was simply untestable), and a browser with site data blocked THROWS on access
// rather than returning null. Falling back to English is always better than an exception thrown
// from a label.
export function getLanguage() {
  try { return globalThis.localStorage?.getItem(LS_KEY) || 'en' } catch { return 'en' }
}
export function setLanguage(lang) {
  try { globalThis.localStorage?.setItem(LS_KEY, lang) } catch { /* a preference that cannot be stored is not an error worth showing */ }
}

const STRINGS = {
  en: {
    // Nav tabs
    nav_candidates: 'Candidates', nav_agents: 'Agents', nav_schedule: 'Schedule',
    nav_attendance: 'Attendance', nav_history: 'History log', nav_admin: 'Admin',
    nav_listings: 'Activity',
    nav_deals: 'Deals',
    // Listings (HR activity monitor)
    listings_today: 'Today',
    listings_prev_day: 'Previous day',
    listings_next_day: 'Next day',
    listings_kpi_published: 'Publishing sessions',
    listings_kpi_failed: 'Failed',
    listings_kpi_active: 'Agents who published',
    listings_kpi_meetings: 'Meetings',
    listings_kpi_coops: 'Cooperations opened',
    listings_legend_coop: 'cooperation opened (click opens the lead)',
    listings_coop_tip: '{t} · cooperation opened · {i}',
    listings_coop_count: '{n} cooperations opened today',
    listings_open_lead: 'Open lead',
    listings_kpi_meetings_closed: '{n} closed',
    listings_kpi_meetings_cancelled: '{n} called off',
    listings_kpi_meetings_failed: '{n} no deal',
    listings_kpi_meetings_moved: '{n} postponed away',
    listings_kpi_of: 'of {n}',
    listings_month_chart_title: 'Daily published ads',
    listings_month_chart_sub: 'Published ads per day — click a bar to open that day',
    listings_month_total: 'total this month',
    listings_overlay_show: 'Compare previous month',
    listings_overlay_hide: 'Hide comparison',
    listings_overlay_note: 'aligned by weekday, not by date',
    listings_overlay_total: 'prev. period',
    listings_overlay_legend: 'previous month (same weekdays)',
    listings_prev_month: 'Previous month',
    listings_next_month: 'Next month',
    listings_timeline: "Today's timeline",
    listings_legend_listings: 'Listings',
    listings_legend_moved: 'postponed away',
    listings_legend_failed: 'failed (✕)',
    listings_legend_auto: 'automatic post',
    listings_legend_auto_band: 'automatic mode on (grey: off)',
    listings_nodraft: 'no draft',
    listings_legend_pending: 'pending / not published (hollow)',
    listings_legend_hint: 'empty hours are compressed · busy hours expanded',
    listings_legend_shift: 'scheduled shift',
    listings_legend_meeting: 'meetings',
    listings_meeting_moved: 'was here — postponed',
    listings_meeting_revisited: 'revisited — outcome on the later visit',
    listings_meeting_revisited_at: 'revisited {when}',
    listings_meeting_moved_to: 'was here — postponed to {when}',
    app_no_office_warning: 'Your account is not assigned to an office — you may see almost no agents, schedules or meetings. Ask an admin to assign you to an office (they do it on their Agents tab).',
    listings_roster_fallback: 'Names loaded from the server — shifts and attendance grouping are unavailable in this view.',
    listings_no_roster: 'Agent list unavailable — rows are shown by login, names/shifts/meeting markers are missing.',
    listings_meeting_none: 'no meetings',
    // Live meeting countdown (MeetingCountdown.jsx)
    desk_meeting_terms_missing: 'terms to fill in',
    // The meeting card's state column (2026-08-05) — row order is the owner's.
    desk_meeting_row_terms: 'Terms',
    desk_meeting_row_involved: 'Involved',
    desk_meeting_row_going: 'Going',
    desk_meeting_row_client: 'Client',
    // A core meeting carries no client contact BY DESIGN. This names who you coordinate with
    // instead, and must never be shortened to "Client" — they are different people.
    desk_meeting_row_client_rep: 'Client rep.', desk_meeting_row_property_rep: 'Property rep.',
    desk_meeting_row_outcome: 'Outcome', desk_meeting_outcome_pill: 'Outcome?', desk_meeting_outcome_title: 'How did it go?',
    desk_meeting_outcome_reason_required: 'Pick the reason to save.',
    desk_meeting_row_when: 'When',
    desk_meeting_row_restrictions: 'Restrictions',
    // "asked, and there are none" — a positive statement (B237), never the same as unasked.
    desk_meeting_restr_none: 'none',
    // Someone answered; this viewer has not earned the value. hidden !== absent.
    desk_meeting_restr_hidden: 'recorded · hidden',
    // Nobody has asked. Muted rather than amber: a gap in OUR knowledge, not a warning
    // about the flat — and an instruction to ask before going.
    desk_meeting_restr_unknown: 'not asked',
    meeting_slot_terms: 'Deal terms',
    meeting_terms_add: 'Fill in the terms',
    meeting_terms_other_hint: 'Another agent has terms on this property — for reference only:',
    meeting_terms_withheld: 'terms recorded (visible once you record yours)',
    meeting_terms_edit: 'edit',
    meeting_terms_loading: 'loading…',
    meeting_terms_no_contact: 'no contact resolved — open the property to add one',
    meeting_copy_brief: 'Copy for chat',
    meeting_copy_done: 'Copied!',
    meeting_cd_in: 'in {v}',
    meeting_cd_ago: '{v} ago',
    meeting_cd_now: 'starting now',
    meeting_cd_d: 'd',
    meeting_cd_h: 'h',
    meeting_cd_m: 'm',
    listings_loading: 'Loading…',
    listings_no_agents: 'No agents.',
    listings_no_activity: 'no activity',
    listings_now: 'now',
    listings_group_office: 'Office (by schedule)',
    listings_group_remote: 'Remote (by schedule)',
    listings_group_absent: 'Absent',
    listings_group_off: 'Off',
    listings_status_published: 'Published',
    listings_status_filled: 'Filled – not published',
    listings_status_pending: 'Pending',
    listings_status_failed: 'Failed',
    listings_published_count: '{n} published',
    listings_failed_count: '{n} failed',
    listings_counts_compact: '{p}/{f} pub./fail.',
    listings_ver_uptodate: 'up to date',
    listings_ver_outdated: 'outdated',
    listings_ver_unknown: 'app not seen',
    listings_ver_lastseen: 'last seen',
    listings_kpi_pub_split: '{a} automatic · {m} manual',
    listings_kpi_fail_sub: '{p} pending · {f} filled, not published',
    listings_kpi_auto_only: '{n} automatic only',
    listings_unavailable: 'unavailable',
    listings_nudges_unavailable: 'Meeting follow-up marks could not be loaded — a missing mark does not mean nothing is overdue.',
    listings_updated_at: 'Updated {t}',
    listings_refresh: 'Refresh',
    listings_stale: 'Not up to date — showing data from {t}',
    listings_group_unmatched: 'Logins not in the roster — not counted in the totals',
    listings_unmatched_note: 'Logins that match no agent are listed in every office view and never counted in its totals.',
    listings_checked_in: 'checked in {t}',
    listings_no_checkin: 'no check-in',
    listings_worker_enabled: 'Automation on — this is the setting, not proof the PC is working',
    listings_worker_paused: 'Automation paused',
    listings_open_advert: 'Open advert',
    listings_show_more: 'Show {n} more',
    listings_details: 'Details',
    listings_fail_reason: 'reason: {r}',
    listings_balance_latest: 'latest observed balance · {t}',
    listings_bal_gauge: '{p} balances',
    listings_bal_gauge_sub: '{e} empty · {f} with money · {n} accounts',
    listings_bal_wallet: 'main account {b}₾ · checked {t}',
    listings_bal_wallet_stale: 'main account {b}₾ · not checked since {t}',
    listings_paid_on: 'ss / myhome on',
    listings_paid_off: 'ss / myhome off',
    listings_paid_absent: 'did not come to work on a scheduled day',
    listings_paid_cap: 'top-up cap reached: {p}',
    listings_paid_reset_until: 'limits lifted until {t}',
    listings_paid_limit_until: 'limited by an admin until {t}',
    listings_paid_limit: 'Limit',
    listings_paid_reset: 'Reset',
    listings_paid_limit_hint: 'Stop automatic ss + myhome for the hours you choose',
    listings_paid_reset_hint: 'Lift every ss + myhome limit (absence, fresh cooperations, top-up cap) for the hours you choose',
    listings_paid_hours: 'For how many hours?',
    listings_paid_clear: 'Remove the override',
    listings_month_scope_all: 'all offices',
    listings_pc_working: 'PC working · {p}',
    listings_pc_idle: 'PC online · idle',
    listings_auto_on: 'automatic mode on',
    listings_auto_off: 'automatic mode off',
    listings_pc_online: 'PC online',
    listings_pc_offline: 'PC offline',
    listings_pc_gap: 'Browser silent {a}–{b} (restart?) · {d}',
    listings_legend_pc_gap: 'browser restart / short silence',
    listings_auto_off_paused: 'automatic mode off - paused by the agent',
    listings_auto_off_office: 'automatic mode off - in the office',
    apl_title: 'Portal logins for company browsers',
    apl_desc: 'For an account with no work number - argo, the top-up MacBook and Chromebook. When myhome / ss is signed out there, the extension signs in with the login saved here. Use the COMPANY ADMIN login. Passwords are never shown; leave the password empty to keep the saved one.',
    apl_none: 'Nothing saved yet.',
    apl_agent: 'agent e-mail (argo@sentavus.com)',
    apl_login: 'login (e-mail or phone)',
    apl_password: 'password',
    listings_au_on: 'auto-update',
    listings_au_on_hint: 'This PC installs each new extension version by itself',
    listings_au_error: 'auto-update failed',
    listings_au_error_hint: 'The updater is installed but its last run failed',
    listings_au_off: 'manual update',
    listings_au_off_hint_win: 'Auto-update is not set up: in the extension folder, double-click install-autoupdate.bat once',
    listings_au_off_hint_mac: 'Auto-update is not set up: in Terminal run  bash <extension folder>/install-autoupdate.sh  once',
    listings_pc_seen: 'PC last seen {t}',
    listings_pc_unknown: 'PC not seen yet',
    listings_myday_title: 'My day',
    listings_team_today: 'Team today',
    listings_axis_short: 'hours not to scale',
    listings_pc_short_on: 'on',
    listings_myday_rail: 'My timeline',
    listings_summary: 'Day summary',
    listings_legend_title: 'Key',
    listings_legend_rail: 'Timeline',
    listings_legend_shift_home: 'scheduled shift (from home)',
    listings_legend_nudge_critical: 'meeting outcome overdue',
    listings_legend_nudge_high: 'meeting outcome late',
    listings_legend_nudge_medium: 'meeting outcome missing',
    listings_myday_checkin: 'Check-in',
    listings_myday_off: 'not scheduled today',
    listings_myday_failures: 'Failed posts',
    listings_myday_no_reason: 'no reason recorded',
    listings_myday_balances: 'Balances',
    listings_myday_missing: 'Your own row is not on this list (another office is selected, or your login is not linked).',
    listings_team_show: 'Show the team ({n})',
    listings_team_hide: 'Hide the team',
    listings_actions: 'Actions',
    listings_close: 'Close',
    listings_lock_action: 'Lock this agent out',
    listings_unlock_action: 'Restore access',
    listings_locked: 'locked',
    listings_locked_hint: 'Access revoked - restore it from the ⋯ menu',
    listings_lock_confirm: 'Lock out {e}?\n\nThis revokes their senTavus extension:\n• login and token refresh are blocked immediately\n• a running extension stops working within ~5 minutes\n• a copy taken offline locks itself within 24 hours\n\nYou can restore access anytime from the ⋯ menu.',
    listings_lock_reason: 'Reason (optional):',
    listings_paid_custom: 'other',
    listings_paid_apply: 'Apply',
    listings_paid_cancel: 'Cancel',
    listings_paid_hours_invalid: 'Enter a number of hours from 1 to 168',
    listings_paid_for: '{m} for how many hours?',
    listings_rail_shift: 'shift {t}',
    listings_rail_no_shift: 'outside the shift',
    listings_deal_trade: 'sale',
    listings_deal_sale: 'sale',
    listings_deal_lease: 'rent',
    listings_deal_rent: 'rent',
    // Agents
    agents_add: 'Add agent', agents_active: 'active agents', agents_desks: 'desks',
    agents_show_inactive: 'Show inactive', agents_hide_inactive: 'Hide inactive',
    agents_sort_newest: '↑ Newest hired', agents_sort_oldest: '↑ Longest serving',
    agents_sort_hours: '↑ Most hours', agents_sort_name: '↑ Name A–Z',
    agents_sort_role: '↑ By role', agents_sort_resigned: '↑ Recently resigned',
    agents_tab_active: 'Active', agents_tab_left: 'Left / Resigned',
    // Agent card
    agent_office_wk: 'office/wk', agent_remote_wk: 'remote/wk',
    agent_hired: 'Hired', agent_left: 'Left',
    // Agent modal
    agent_modal_add: 'Add agent', agent_modal_edit: 'Edit',
    agent_modal_info: 'Info & Employment', agent_modal_schedule: 'Weekly Schedule',
    agent_modal_name: 'Full name', agent_modal_role: 'Role', agent_modal_office: 'Office',
    agent_modal_commission: 'Commission %', role_comm_default: 'default',
    deal_split_title_mine: 'Your share of this deal', deal_split_title_all: 'How this deal divides',
    deal_split_agent: 'Agent', deal_split_manager: 'manager', deal_split_done: 'Done',
    deal_split_office_unknown: 'Your office is not set, so this split cannot be scoped to it — ask an admin to set your office.',
    deal_close_recorded: 'Deal recorded.',
    deal_split_no_attendance: 'Nobody was marked as having attended, so the default split was applied (the higher-rated agent as lead).',
    agent_dupe_phone: 'This personal phone already belongs to:',
    agent_dupe_id: 'This ID number already belongs to:',
    agent_dupe_id_hint: 'A national ID identifies one person. This is almost certainly the same person already on the roster.',
    agent_dupe_phone_hint: 'Check before saving — you may be adding someone who is already on the roster. Saving anyway is allowed.',
    agent_modal_commission_manager: 'Team commission % (manager)',
    agent_modal_commission_manager_hint: 'Share of the FULL commission on every deal closed by an agent in this office. Added on top — it does not reduce the closing agent. Not paid on the manager’s own deals.',
    comm_rules_title: 'How commission income is calculated',
    comm_rules_body: `The rate used is the agent's commission % at the time the deal was CLOSED.

• Solo agent — earns their commission % of the deal commission; the agency keeps the rest.
• Two agents, higher-paid attended the meeting (default) — agency keeps 100 − highest%; the lower-paid agent earns half their own % (low ÷ 2); the higher-paid earns highest% − (low ÷ 2).
• Two agents, equal pay — each earns half their % (rate ÷ 2); agency keeps 100 − rate.
• Two agents, lower-paid attended — split equally: each earns half the lower % (low ÷ 2); agency keeps 100 − lower%.

Example: 70% + 50%, higher-paid attended → agency 30%, the 70% agent earns 45%, the 50% agent earns 25%.`,
    agent_modal_unassigned: 'Unassigned',
    agent_modal_generate_pass: 'Generate password',
    office_filter_all: 'All offices', office_filter_unassigned: 'Unassigned', office_filter_label: 'Office',
    agent_modal_email: 'Email', agent_modal_phone_work: 'Work phone',
    agent_modal_phone_personal: 'Personal phone', agent_modal_hire_date: 'Hire date',
    agent_modal_active: 'Currently active', agent_modal_resign: 'Resignation',
    agent_modal_resign_date: 'Resign date', agent_modal_resign_reason: 'Reason for leaving',
    agent_modal_dob: 'Date of birth', agent_modal_id_number: 'Personal ID number',
    agent_modal_login_id: 'Login ID', agent_modal_login_pass: 'Login password',
    agent_modal_scheduler_login: 'Scheduler login (admin/manager)', agent_modal_scheduler_login_none: '— none —',
    agent_modal_save: 'Save changes', agent_modal_delete: 'Delete agent',
    agent_modal_cancel: 'Cancel', agent_modal_adding: 'Add agent',
    agent_modal_saving: 'Saving…', agent_modal_close: 'Close',
    // Quick actions
    qa_edit: 'Edit', qa_fire: 'Mark as left', qa_activate: 'Reactivate',
    qa_remove_number: 'Remove number', qa_delete: 'Delete agent',
    agents_remove_number_confirm: 'Return {number} to the free pool? {name} will keep it shown only as a past number.',
    agents_delete_confirm: 'Permanently delete {name}? This removes their record entirely and cannot be undone.',
    phone_held_badge: 'still assigned to this agent',
    phone_pool_badge: 'free — in pool',
    phone_now_label: 'now {name}',
    // Status
    status_active: 'Active', status_left: 'Left',
    // Candidates
    cand_add: '+ Add candidate', cand_search: 'Search name, role, email…',
    cand_all: 'All', cand_active: 'Active', cand_interviews: 'Interviews', cand_rejected: 'Rejected',
    cand_status_new: 'New', cand_status_scheduled: 'Scheduled',
    cand_status_interviewed: 'Interviewed', cand_status_hired: 'Hired ✓', cand_status_rejected: 'Rejected',
    cand_status_no_show: 'No-show',
    cand_hire: 'Hire →', cand_hired_msg: 'Hired — edit schedule in Agents tab',
    cand_confirm_hire: 'Confirm hire', cand_confirm: 'Confirm',
    cand_ready_hire: 'Ready to hire?', cand_ready_msg: 'Creates agent profile. Fill schedule after.',
    cand_delete: 'Delete',
    // Attendance
    att_free_desks: 'Free desks', att_remote: 'Working remote', att_peak: 'Peak office hour',
    att_confirmed: 'Confirmed', att_absent: 'Absent', att_remote_short: 'Remote',
    att_not_entered: 'Pending',
    att_overdue_count: 'overdue',
    att_use_scheduled: 'Use scheduled times',
    att_using_scheduled: 'Using scheduled times',
    att_clear: 'Clear',
    // Interview
    int_today: 'Today', int_upcoming: 'Upcoming', int_starting: 'Starting', int_past: 'Past', int_count: 'interviews',
    int_unscheduled: 'Needs scheduling', int_unscheduled_hint: 'no interview date yet — tap to schedule',
    int_schedule: '📅 Schedule interview', int_reschedule: '📅 Reschedule interview',
    int_mark: '✅ Mark interviewed', int_hire: '🟢 Hire', int_reject: '🔴 Reject',
    int_edit: '✏️ Edit', int_delete: '🗑 Delete',
    // Log
    log_by_agent: 'By agent', log_timeline: 'Timeline', log_all: 'All agents',
    log_add_entry: '+ Add entry', log_add_manual: 'Add log entry',
    // General
    save: 'Save', cancel: 'Cancel', delete: 'Delete', edit: 'Edit', close: 'Close',
    login_subtitle: 'Scheduler sign in', login_username: 'Username', login_password: 'Password',
    login_show_password: 'Show password', login_hide_password: 'Hide password',
    login_sign_in: 'Sign in', login_signing_in: 'Signing in…', login_error: 'Wrong username or password',
    login_sign_out: 'Sign out',
    change_password: 'Change password', pw_new: 'New password', pw_confirm: 'Confirm password',
    pw_too_short: 'Password must be at least 6 characters', pw_mismatch: 'Passwords do not match',
    pw_changed: 'Password updated',
    change_login_password: 'Change login / password',
    pw_leave_blank: 'Leave blank to keep the current password',
    creds_changed: 'Credentials updated — use them on your next sign-in',
    creds_change_failed: 'Change failed — the login may already be taken',
    working_day: 'Set working', day_off: 'Set off', add_slot: '+ Add slot',
    week: 'Week', month: 'Month',
    // Office
    office_settings: 'Office settings', office_name: 'Office name',
    office_location: 'Location', office_capacity: 'Desk capacity',
    office_desks_note: 'Number of physical desks.',
    // Admin
    admin_name: 'Set admin name', admin_note: 'Appears on every log entry you create.',
    // Days

    // Attendance modal
    att_check_in: 'Check-in', att_check_out: 'Check-out',
    // Remote days: nobody checks IN anywhere from their own kitchen.
    att_start_time: 'Start', att_end_time: 'End',
    att_now: 'Now',
    att_in: 'In', att_out: 'Out',
    att_started: 'Started', att_ended: 'Ended', att_note: 'Note (optional)',
    att_note_placeholder: 'Late start, client meeting, left early…',
    att_status: 'Status', att_in_office: 'In office',
    att_worked: 'Worked', att_vs_schedule: 'vs schedule',
    att_clear_entry: 'Clear entry',
    att_scheduled_for_day: 'Scheduled for this day',
    att_total: 'Total', att_split_shift: 'Split shift',
    att_use_sched: 'Use scheduled times',
    att_using_sched: 'Using scheduled times',
    att_free: 'Clear',
    // Agent modal extra
    agent_modal_dob_age: 'Age',
    agent_modal_identity: 'Identity',
    agent_modal_basic: 'Basic',
    agent_modal_employment: 'Employment',
    agent_modal_login: 'Login credentials',
    agent_modal_show: 'Show', agent_modal_hide: 'Hide',
    // Candidates modal
    cand_modal_contact: 'Contact',
    cand_modal_application: 'Application',
    cand_modal_interview: 'Interview',
    cand_modal_name: 'Full name', cand_modal_phone: 'Phone',
    cand_modal_email: 'Email', cand_modal_link: 'Profile / social link',
    cand_modal_link_hint: '(Facebook, TikTok, LinkedIn…)',
    cand_modal_applying: 'Applying for', cand_modal_source: 'Source',
    cand_modal_status: 'Status', cand_modal_date: 'Date',
    cand_modal_time: 'Time', cand_modal_interviewer: 'Interviewer',
    cand_modal_notes: 'Notes',
    cand_modal_notes_ph: 'Interview notes, impressions, questions asked…',
    cand_modal_clear_interview: '✕ Clear interview date',
    cand_no_candidates: 'No candidates yet',
    cand_no_match: 'No candidates match',
    cand_no_rejected: 'No rejected candidates',
    // Schedule tab
    sched_office_capacity: 'Office occupancy',
    sched_free_desks: 'Free desks',
    sched_in_office_n: '{n} in the office',
    sched_free_desks_peak: 'Free desks at the planned peak', sched_capacity_unknown: 'capacity not set',
    sched_people_today: '{n} people planned in the office this day',
    sched_planned_note: 'Planned schedule for {date} - not recorded attendance.',
    sched_remote_only: 'Remote only',
    sched_peak_hour: 'Peak office hour',
    sched_agents: 'agents',
    sched_heatmap: 'Weekly office heatmap',
    sched_empty: 'Low', sched_full: 'Full / Over',
    sched_capacity: 'Capacity',
    sched_weekly: 'Weekly attendance',
    sched_office_days: 'Office days',
    // Log
    log_no_events: 'No events yet',
    log_events_auto: 'Events are logged automatically. You can also add manual notes.',
    log_show_less: '▲ Show less', log_show_more: '▼ Show',
    log_more: 'more',
    // History categories
    cat_hired: 'Hired', cat_left: 'Left / Resigned',
    cat_schedule: 'Schedule change', cat_attendance: 'Attendance',
    cat_alert: 'Alert', cat_profile: 'Profile edit',
    cat_office: 'Office', cat_info: 'Note', cat_contact: 'Contact',
    cat_client_error: 'App error',
    log_contact: '{method} ({phone}) · {by}',
    ct_opened: 'Opened contact menu', ct_copied: 'Copied number',
    days: ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'],
    fullDays: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'],
    months: ['January','February','March','April','May','June','July','August','September','October','November','December'],
    // Hardcoded strings now translated
    all_agents: 'All agents',
    all_categories: 'All categories',
    location_office: 'Office',
    location_remote: 'Remote',
    schedule_start: 'Start',
    schedule_end: 'End',
    schedule_location: 'Location',
    heatmap_empty: 'Empty',
    heatmap_full: 'Full/Over',
    int_no_scheduled: 'No interviews scheduled yet',
    int_no_scheduled_hint: 'Add an interview date to a candidate to see it here',
    cand_hired_agents_msg: '✓ Hired — edit schedule in the Agents tab.',
    // ScheduleTab
    sched_occupancy_title: 'Office occupancy by hour',
    sched_in_office: 'In office',
    sched_capacity_label: 'Capacity',
    sched_office_label: 'Office',
    sched_remote_label: 'Remote',
    sched_override_label: 'Override',
    sched_split_label: 'Split',
    sched_click_override: 'click ✎ to set one-off override',
    sched_edit_override: '★ Edit',
    sched_set_override: '✎ Override',
    sched_col_agent: 'Agent',
    sched_col_role: 'Role',
    sched_col_hours: 'Hours',
    sched_col_timeline: 'Timeline',
    sched_col_status: 'Status',
    sched_col_override: 'Override',
    // AttendanceTab
    att_status_absent: 'Absent',
    att_status_holiday: 'Holiday',
    att_day_off: 'Day off',
    my_att_arrive: 'I came',
    my_att_leave: 'I left',
    my_att_arrived_at: 'Arrived {time}',
    my_att_left_at: 'Left {time}',
    my_att_once_note: 'Recorded once — ask a manager to correct a mistake.',
    my_att_no_agent_row: 'You are signed in without an agent record, so there is no attendance to keep here.',
    my_att_loc_in_office: 'You are at the office.',
    my_att_loc_unchecked: 'No office addresses are set, so arrival is not checked against a location.',
    my_att_loc_not_in_office: 'You are not on the office network — arrival can only be marked from the office.',
    my_att_loc_address_unknown: 'Your address could not be determined, so arrival cannot be marked.',
    // 0010's new refusal. It is deliberately not worded as "you are not in the office": the
    // office IS watching and did not see this phone, and the two fixes are different.
    my_att_loc_device_not_seen: 'The office cannot see your phone on its Wi-Fi — connect to the office network, or register the phone below.',
    myphone_title: 'My phone on the office Wi-Fi',
    myphone_none: 'No phone registered yet — the office cannot recognise you by your device.',
    myphone_seen: 'the office sees it now',
    myphone_not_seen: 'not seen right now',
    myphone_remove: 'remove',
    myphone_add: 'Register',
    myphone_label_ph: 'name (optional)',
    myphone_where: 'Where do I find this?',
    myphone_help_ios: 'Settings → General → About → Wi-Fi Address.',
    myphone_help_android: 'Settings → About phone → Status → Wi-Fi MAC address (on some phones: Network → Advanced → the connected network → MAC).',
    // Said out loud because it is the one way a correct registration goes silently wrong later:
    // iPhones give each network a different private address, so the number is only right for
    // this office's Wi-Fi, and turning the setting off changes it.
    myphone_help_warning: 'Read it while connected to the office Wi-Fi — phones use a different address on each network.',
    my_att_refused_not_in_office: 'Arrival can only be marked from the office.',
    my_att_refused_address_unknown: 'Your address could not be determined, so arrival cannot be marked.',
    my_att_refused_not_an_agent: 'You have no agent record.',
    my_att_refused_not_checked_in: 'You have not marked your arrival yet.',
    shift_prompt_question: 'Your shift has started — are you at the office?',
    shift_prompt_started_at: 'from {time}',
    shift_prompt_recheck: 'Check again',
    // Added with the parse-conflict panel (2026-09-02): these nine had no desk_attr_ entry,
    // so attrLabel would have fallen back to a humanised column name on an all-Georgian
    // screen — the exact shape of the 'floors total' / 'area sqm' defect labels.js records.
    desk_attr_address: 'Address',
    desk_attr_district_code: 'District',
    desk_attr_cadastral_code: 'Cadastral code',
    desk_attr_kind: 'Type',
    desk_attr_lat: 'Latitude',
    desk_attr_lng: 'Longitude',
    desk_attr_intent: 'Deal',
    desk_attr_price: 'Price',
    desk_attr_currency: 'Currency',
    desk_attr_owner_name: 'Owner name',
    desk_attr_owner_phone: 'Owner phone',
    desk_parse_conflict_heading: 'The parse disagrees with {n} value(s) already here',
    desk_parse_conflict_replace: 'replace',
    desk_parse_conflict_apply: 'Replace {n}',
    desk_parse_conflict_keep_all: 'Keep everything',
    desk_pagination_go_to: 'Go to page {n}',
    // Client sharing (owner 2026-09-02). SHARE adds a colleague and keeps you; LEAVE removes
    // only you, and if nobody is left the client returns to the pool — the two confirmations
    // below say which of those is about to happen, because they are different facts.
    desk_client_reps_heading: 'Working this client:',
    desk_client_reps_none: 'nobody — this client is in the pool',
    desk_client_rep_unnamed: 'unnamed',
    desk_client_rep_you: '(you)',
    desk_client_share: 'Share with a colleague',
    desk_client_share_search: 'Search agents…',
    desk_client_share_note: 'They will see everything you see, contact details included. You keep the client.',
    desk_client_share_nobody: 'No one left to add.',
    desk_client_share_all: 'All ({n})',
    desk_client_share_none: 'Clear',
    desk_client_share_commit: 'Share',
    desk_client_unshare_title: 'Stop sharing with {name}',
    desk_client_share_commit_n: 'Share · {n}',
    desk_client_share_partial: '{ok} shared, {bad} failed: {names}',
    desk_client_leave: 'Leave this client',
    desk_client_leave_confirm_shared: 'Leave? {name} still works this client, so it becomes theirs.',
    desk_client_leave_confirm_pool: 'Leave? Nobody else works this client, so they go back to the pool for anyone to pick up.',
    desk_client_leave_yes: 'Yes, leave',
    // The carve in a composed restriction, on the card face. Matches the panel's own nested
    // row (↓ გამონაკლისი) so one fact reads the same on both surfaces.
    desk_restriction_except: 'exception:',
    office_ips_label: 'Office addresses (one per line)',
    office_ips_hint: 'The public IP addresses this office is reached from. Leave empty and arrival is not checked here. A range is written 91.184.115.0/24.',
    office_ips_enforcing: 'Arrival is checked against {n} address(es).',
    office_ips_open: 'No addresses — arrival is not checked for this office.',
    office_ips_router: 'No addresses needed — arrival is checked through the office router.',
    att_dayoff_visit_hint: 'Off today — record a visit if they came in',
    att_dayoff_visit_label: 'Came in / left (not on the roster today)',
    att_dayoff_visit_clear: 'Clear',
    att_dayoff_visit_now: 'now',
    att_working: 'Scheduled',
    att_confirmed_count: 'Confirmed',
    att_legend_office: 'Office',
    att_legend_remote: 'Remote',
    att_legend_not_entered: 'Pending',
    att_legend_absent: 'Absent',
    att_col_agent: 'Agent',
    att_col_scheduled: 'Scheduled',
    att_col_worked: 'Worked',
    att_col_delta: 'Delta',
    att_col_absent: 'Absent',
    att_col_confirmed: 'Confirmed',
    att_col_assumed: '~Assumed',
    att_col_remote: 'Remote',
    att_summary_late: 'Late',
    att_summary_overtime: 'Overtime',
    att_summary_absent: 'Absent',
    att_summary_remote: 'Remote',
    // AgentsTab day badges
    badge_office: 'd office',
    badge_remote: 'd remote',
    badge_split: 'd split',
    badge_off: 'd off',
    // ScheduleTab
    sched_base_note: 'base schedule · overrides not shown',
    sched_base_short: 'base schedule',
    sched_off_badge: 'Off',
    sched_weekend: 'weekend',
    sched_day_title: 'schedule',
    // AttendanceTab
    att_day_view: 'Day',
    att_scheduled_label: 'Scheduled',
    // AgentLogTab
    log_add_btn: 'Add entry',
    // B196 (2026-07-29): referenced by AgentLogTab/AgentModal but defined in no
    // language — rendered as their own key strings (the desk_coop_end_deal_closed class).
    log_message_label: 'Message',
    log_message_placeholder: 'Write a note…',
    log_save_btn: 'Save',
    log_all_count: 'All',
    log_no_events_long: 'No events yet. Events are logged automatically when you change the schedule or record attendance.',
    // Event timeline
    log_timeline_title: 'Event timeline',
    log_entries: 'entries',
    // Candidate event types
    cand_ev_added: 'Added',
    cand_ev_scheduled: 'Interview scheduled',
    cand_ev_rescheduled: 'Interview rescheduled',
    cand_ev_cancelled: 'Interview cancelled',
    cand_ev_no_show: 'No-show',
    cand_ev_interviewed: 'Interviewed',
    cand_ev_hired: 'Hired',
    cand_ev_rejected: 'Rejected',
    cand_ev_note: 'Note',
    // Candidates form hints
    cand_status_hint: 'Selecting a status other than Scheduled or Interviewed clears the interview date.',
    cand_date_hint: 'Setting a date auto-sets status to Scheduled',
    // AdminTab
    admin_no_items: 'No items yet',
    admin_add_new: 'Add new…',
    admin_add_btn: 'Add',
    phone_section_title: 'Work Phone Numbers',
    phone_none_yet: 'No numbers yet',
    phone_search_ph: 'Search number or agent…',
    phone_search_no_match: 'No numbers match',
    phone_filter_all: 'All', phone_filter_free: 'Free', phone_filter_assigned: 'Assigned', phone_filter_expiring: 'Expiring',
    phone_filter_expired: 'Expired', phone_filter_banned: 'Banned',
    phone_sort_plan: 'Expiring first', phone_sort_plan_desc: 'Longest left first',
    phone_sort_plan_title: 'Sort by plan days — click to cycle: expired first → longest left first → off',
    phone_assign_btn: 'Assign', phone_assign_ph: 'Type an agent name…', phone_assign_none: 'No matching agents',
    phone_banned_badge: 'Banned',
    phone_free_badge: 'Free',
    number_details_title: 'Logins and eSIM QR', portal_section: 'Logins on myhome.ge / ss.ge', settings_passwords: 'Passwords',
    portal_copy: 'Copy', portal_open_site: 'Open site', portal_updated: 'updated {date} · {who}', portal_edit: 'Edit', portal_add: 'Add',
    portal_login: 'Login', portal_password: 'Password', portal_show: 'Show', portal_hide: 'Hide', portal_none: 'Not recorded yet',
    portal_login_ph: 'Login (phone, email or username)', portal_password_keep_ph: 'New password — leave empty to keep the current one',
    portal_save: 'Save', portal_cancel: 'Cancel', portal_my_title: 'Your passwords on myhome.ge / ss.ge',
    portal_my_sub: 'The accounts registered with your work number. They belong to the number: when the number moves to someone else, so does this access.',
    portal_my_no_number: 'You have no work number assigned yet.',
    esim_qr_view: 'eSIM QR code', esim_qr_none: 'No eSIM QR uploaded yet', esim_qr_open: 'Open',
    esim_qr_upload: 'Upload QR (PDF)', esim_qr_add_another: 'Add another', esim_qr_replace: 'Replace with new QR',
    esim_qr_replace_hint: 'Replace uploads the new file and moves the current one to “earlier”. Nothing is deleted.',
    esim_qr_retire: 'Move to earlier', esim_qr_restore: 'Make current again', esim_qr_retired: 'replaced',
    esim_qr_retire_confirm: 'Move “{name}” to earlier files?', esim_qr_earlier: 'Earlier files ({n})',
    phone_unban: 'Unban',
    phone_ban: 'Ban',
    phone_note_placeholder: 'Add note…',
    phone_note_optional: 'Note (optional)',
    admin_save_btn: 'Save',
    admin_saving: 'Saving…',
    admin_saved: '✓ Saved',
    admin_save_changes: 'Save changes',
    admin_description: 'Manage dropdown options used across the app. Changes apply immediately everywhere.',
    admin_name_section: 'Admin name',
    admin_no_office: 'No office configured yet.',
    office_section_title: 'Office',
    office_work_start: 'Working hours start',
    office_work_end: 'Working hours end',
    office_weekend_hours: 'Weekend hours (Sat–Sun)',
    office_weekend_start: 'Weekend start',
    office_weekend_end: 'Weekend end',
    // 0015 — office-scoped numbers + manager Settings (2026-09-16)
    nav_settings: 'Settings',
    settings_numbers: 'Numbers',
    settings_office_config: 'Office Configuration',
    settings_no_offices: 'You do not manage any office yet. Admin adds office coverage for managers.',
    office_unassigned: 'Unassigned',
    phone_office_move_title: "Number's office — only Admin can change it",
    phone_office_suggest: 'holder sits in {office}',
    phone_office_suggest_btn: 'Assign to {office}',
    phone_office_unresolved: '{n} numbers have no office. Existing numbers were left Unassigned on purpose — assign each one.',
    phone_office_history: 'Office history',
    phone_office_changed_by: 'changed by',
    phone_office_deleted_reason: 'office deleted',
    office_default_wfh_day: 'Default Work From Home day',
    office_default_rest_day: 'Default Rest Day / დასვენების დღე',
    office_defaults_hint: 'Office defaults — an employee’s own schedule can override them.',
    weekday_none: 'None',
    office_admin_only_note: 'Office name, addresses, Wi-Fi and security settings are managed by Admin.',
    // 0017 — Settings → Notifications (Telegram)
    settings_notifications: 'Notifications',
    notify_bot_title: 'Company Telegram bot',
    notify_bot_sub: 'One bot for the whole company. You set it up once; everyone then connects their own Telegram to it.',
    notify_bot_none: 'Not set up',
    notify_bot_verifying: 'Checking token…',
    notify_bot_running: 'Running · {bot}',
    notify_bot_stale: 'Not responding',
    notify_bot_stale_hint: 'The token is saved but the notification service has not checked in for over 2 minutes. Nothing will be delivered until it does.',
    notify_bot_error: 'Problem',
    notify_bot_guide_title: 'How to create the bot (3 minutes)',
    notify_bot_steps: [
      'Open Telegram on your phone or computer and search for @BotFather (the official one has a blue check mark). Press Start.',
      'Send /newbot.',
      'When asked for a name, send something people will recognise, e.g. "senTavus Notifications".',
      'When asked for a username, send one that ends in "bot", e.g. sentavus_notify_bot. If it is taken, try another.',
      'BotFather replies with a token that looks like 123456789:AAH… — copy the whole line.',
      'Paste it in the field below and press Save token. Within about 20 seconds the status above turns green: "Running · @your_bot".',
      'Optional: in @BotFather send /setuserpic to give the bot the senTavus logo, and /setdescription for a short description.',
    ],
    notify_bot_rules: [
      'The token is a password for the bot. Do not send it in any chat. After saving it is kept on the server and never shown again — not even here.',
      'Use this bot ONLY for senTavus. If another program uses the same token, notifications stop.',
      'If the token ever leaks: in @BotFather send /revoke, choose the bot, and paste the new token here. Everyone will need to press "Connect Telegram" again.',
    ],
    notify_bot_token_label: 'Bot token from @BotFather',
    notify_bot_replace_label: 'Replace token (current bot id {id})',
    notify_bot_replace_warn: 'Saving a new token disconnects everyone; each person reconnects with one tap.',
    notify_bot_save: 'Save token',
    notify_bot_missing: 'Admin has not set up the company Telegram bot yet. Once they do, you can connect here.',
    notify_bot_missing_admin: 'Set up the company bot above first — then connect your own Telegram here.',
    notify_me_title: 'My Telegram',
    notify_me_sub: 'Notifications go to your personal Telegram chat with the company bot. Only you receive them.',
    notify_status_connected: 'Connected',
    notify_status_not_connected: 'Not connected',
    notify_connect_steps: [
      'Install Telegram on your phone and sign in (skip if you already use it).',
      'Press "Connect Telegram" below. Telegram opens a chat with the senTavus bot.',
      'Press START at the bottom of that chat. The bot answers "✅ Connected" — that is all.',
      'Come back here: this page shows "Connected". Press "Send test" to see a message arrive.',
    ],
    notify_connect: 'Connect Telegram',
    notify_waiting: 'Waiting for you to press START in Telegram… (the link works for 15 minutes)',
    notify_link_fallback: 'Telegram did not open? Open this link on your phone:',
    notify_connected_msg: '✅ Telegram connected.',
    claude_title: 'My Claude',
    claude_sub: 'Connect the Claude app so it can search senTavus listings for your clients — paste a client\'s messages and get matching links.',
    claude_status_on: 'Connected',
    claude_status_off: 'Not connected',
    claude_steps: [
      'Press "Create my Claude link" below. The link is copied and Claude\'s connector settings open.',
      'In Claude: Add custom connector → name it senTavus → paste the link → Add.',
      'Start a new chat and ask, e.g. "1-bedroom rent in Vake, up to $800, furnished".',
    ],
    claude_create: 'Create my Claude link',
    claude_new_link: 'Make a new link',
    claude_turn_off: 'Turn off',
    claude_replace_confirm: 'Your current link will stop working. Make a new one?',
    claude_off_confirm: 'Turn off your Claude link? Claude will stop seeing senTavus until you make a new one.',
    claude_link_ready: 'Your link — copy it into Claude:',
    claude_link_copied: '✅ Link copied — paste it in Claude:',
    claude_copy: 'Copy',
    claude_open_settings: 'Open Claude settings',
    claude_link_secret: 'This link is personal — anyone with it can search as you. It is shown only now; if you lose it, make a new one.',
    claude_made: 'Link made {date}',
    claude_last_used: 'last used {date}',
    claude_never_used: 'not used yet',
    claude_searches_7d: '{n} searches this week',
    claude_scope_note: 'Claude sees the listings published on sentavus.com — never more than you see in the Desk. Owner contacts stay in the Desk: each result has an "Open in Desk" link.',
    notify_linked_as: 'Connected as {user} since {date}',
    notify_send_test: 'Send test',
    notify_test_sent: 'Test sent — it should arrive in a few seconds.',
    notify_disconnect: 'Disconnect',
    notify_disconnect_confirm: 'Stop sending senTavus notifications to this Telegram?',
    notify_what_title: 'What you receive',
    notify_what_scope: 'You only receive what you can already see in senTavus: agents — the clients they watch and their own deals; managers — also their offices; admin — every office.',
    notify_group_team: 'Office & team',
    notify_group_clients: 'Deals & clients',
    notify_when_title: 'When',
    notify_quiet: 'Quiet hours',
    notify_quiet_hint: 'Nothing is sent during these hours. What happened overnight arrives as one message when quiet hours end (Tbilisi time).',
    notify_quiet_from: 'From',
    notify_quiet_to: 'Until',
    notify_pause: 'Pause all notifications',
    notify_pause_hint: 'While paused nothing is sent and nothing is saved for later.',
    notify_recent_title: 'Recently sent',
    notify_recent_pending: 'waiting until {at} (quiet hours)',
    notify_scope_all: 'All of senTavus',
    notify_scope_office: 'My office',
    notify_lead_label: 'Remind me',
    notify_lead_minutes: '{n} min before',
    notify_lead_hours: '{n} h before',
    // client request modal redesign 1b (2026-09-17)
    client_sum_budget: 'Budget',
    client_sum_rooms: 'Rooms',
    client_sum_where: 'Where',
    client_sum_when: 'When',
    client_sum_household: 'Who lives there',
    client_sum_unknown: 'unknown',
    client_sum_hidden: 'hidden',
    client_sum_per_month: '/ month',
    client_sum_bedrooms_abbr: 'bd',
    client_sum_lease: 'lease {n} mo',
    client_sum_adults: '{n} adults',
    client_sum_children: '{n} children',
    client_sum_occupants: '{n} occupants',
    client_sum_more: '+ {n} more',
    client_sum_any_city: 'anywhere in the city',
    client_sum_drawn_area: 'area drawn on the map',
    client_must: 'Must have',
    client_nice: 'Nice to have',
    client_owner_agent: 'agent {name}',
    client_team_me: 'me',
    client_team_me_plus: 'me + {n} colleagues',
    client_team_name_plus: '{name} + {n}',
    client_team_pool: 'in the pool',
    client_masked_badge: "colleague's client",
    client_masked_rep: 'Represented by {name}',
    client_masked_note: 'Contact, email and household details are hidden — the representative can grant access.',
    client_tab_criteria: 'Criteria',
    client_tab_history: 'History',
    client_tab_nego: 'Negotiations',
    client_tab_empty: 'Nothing here yet.',
    client_grp_money: 'Price and size',
    client_grp_where: 'Where',
    client_grp_features: 'What they want',
    client_grp_household: 'Who lives there',
    client_grp_terms: 'Terms and timing',
    lead_chip_on: 'Leads on',
    lead_chip_off: 'Lead alerts',
    lead_chip_muted: 'Leads muted',
    // 0018 — watching a client's leads
    lead_watch_off: 'Notify me of new leads',
    lead_watch_on: 'Watching new leads',
    lead_watch_muted: 'Muted — turn back on',
    lead_watch_off_title: 'Send me a Telegram message when a new lead matching this client arrives',
    lead_watch_on_title: 'Click to stop watching. Mute or manage all watched clients in Settings → Notifications.',
    lead_watch_connect_first: 'Connect Telegram in Settings → Notifications to receive these.',
    lead_watch_no_access: 'You can no longer see this client, so nothing is sent.',
    // 2026-09-21 — the alert gate: a client too vague to match is filled first, never watched
    lead_chip_fill: 'Lead alerts: fill first',
    lead_watch_fill_title: 'Too little is known to match leads for this client. Fill first: {list}. Click to open the editor.',
    lead_watch_paused_gaps: 'Paused — nothing is sent until you fill: {list}.',
    lead_watch_gap_budget: 'budget (max price)',
    lead_watch_gap_location: 'district',
    lead_watch_gap_location_polygon: 'district (a map area is not used by alerts)',
    lead_watch_gap_size: 'rooms or area',
    lead_watch_rooms: 'rooms',
    lead_watch_districts: '{n} districts',
    lead_watch_title: 'Clients I get new-lead alerts for',
    lead_watch_sub: 'A message arrives when a new lead matching the client appears in the leads database — the same search as "search in leads" on the client card.',
    lead_watch_empty: 'No clients yet. Open a client card and, in the top line next to the team chip, press 🔔 "Lead alerts".',
    lead_watch_event_off: '"New lead for my client" is switched off above, so these are not sent.',
    lead_watch_unnamed: 'Client',
    lead_watch_stats: '{n} new leads sent · last {date}',
    lead_watch_checked: 'No new leads yet · checked {date}',
    lead_watch_pending: 'Starts within a few minutes',
    lead_watch_muted_note: 'Muted — nothing is sent',
    lead_watch_delete: 'Stop watching',
    lead_watch_delete_confirm: 'Stop watching new leads for {name}?',
    office_delete: 'Delete office',
    office_delete_confirm: 'Delete "{name}"?',
    office_delete_confirm_with_agents: 'Delete "{name}"? {count} agent(s) assigned to this office will become unassigned.',
    office_location_label: 'Location / City',
    office_location_placeholder: 'e.g. Tbilisi',
    office_desk_capacity_label: 'Desk capacity',
    list_agent_roles_title: 'Agent Roles',
    list_agent_roles_desc: 'Available roles when creating or editing agents',
    list_districts_title: 'Districts',
    list_districts_desc: 'Tbilisi districts for agent address',
    list_languages_title: 'Languages',
    list_languages_desc: 'Languages agents and candidates may speak',
    list_sources_title: 'Candidate Sources',
    list_sources_desc: 'Where candidates come from',
    // AgentModal
    agent_modal_remove_slot: 'Remove slot',
    agent_modal_section_basic: 'Basic',
    agent_modal_section_identity: 'Identity',
    agent_modal_section_address: 'Address',
    agent_modal_section_district: 'District',
    agent_modal_section_languages: 'Languages',
    agent_modal_section_employment: 'Employment',
    agent_modal_section_login: 'Login credentials',
    agent_modal_split: 'Split',
    agent_modal_day_off: 'Day off',
    agent_modal_show: 'Show',
    agent_modal_hide: 'Hide',
    agent_modal_name_err: 'Name is required',
    agent_modal_delete_confirm: 'Remove {name} from the database? Log entries will be kept.',
    agent_modal_phone_reassign: '{num} is currently assigned to another agent. Reassign it to {agentName}?',
    agent_modal_email_placeholder: 'name@sentavus.com',
    agent_modal_work_phone_ph: '+995 32 …',
    agent_modal_personal_phone_ph: '+995 555 …',
    agent_modal_id_placeholder: '01001234567',
    agent_modal_address_placeholder: 'Street, building…',
    agent_modal_resign_placeholder: 'e.g. Found another position, contract ended…',
    agent_modal_login_id_placeholder: 'e.g. ana.beridze',
    agent_modal_login_taken: 'This login ID already belongs to {name}. Two agents cannot share one login — choose a different one.',
    agent_modal_login_taken_left: 'This login ID belongs to {name}, who has left — that account is locked. Choose a different one.',
    // AgentLogTab
    log_category_label: 'Category',
    log_description_label: 'Description',
    log_describe_placeholder: 'Describe what happened…',
    log_cancel_btn: 'Cancel',
    log_delete_btn: 'Delete',
    log_no_agents: 'No agents found.',
    log_no_events_short: 'No events yet.',
    // AgentsTab
    agents_view_card: 'Card view',
    agents_view_list: 'List view',
    agents_search_placeholder: 'Search…',
    agents_filter_district: 'District',
    agents_filter_language: 'Language',
    agents_no_match: 'No agents match your filters',
    agents_none_left: 'No resigned agents',
    agents_none_yet: 'No agents yet — add the first one',
    agents_empty_office_filtered: 'No agents in this office view — the top-bar office filter is hiding the rest',
    office_filter_show_all: 'Show all offices',
    // AttendanceTab
    att_team_summary: 'Team summary',
    // CandidatesTab
    cand_quick_actions: 'Quick actions',
    cand_today_badge: 'TODAY',
    cand_modal_add_btn: 'Add candidate',
    cand_modal_phone_label: 'Phone',
    cand_modal_email_label: 'Email',
    cand_modal_applying_label: 'Applying for',
    cand_modal_source_label: 'Source',
    cand_modal_district_label: 'District',
    cand_modal_languages_label: 'Languages',
    cand_modal_status_label: 'Status',
    cand_modal_interviewer_label: 'Interviewer',
    cand_added_by: 'Added by',
    cand_modal_notes_label: 'Notes',
    // B1301 — notes became a THREAD (time, who, what), so the label says so and the
    // textarea's own label stays for the create case, where there is no candidate to log against.
    cand_notes_thread_label: 'Notes — each one keeps its time and author',
    cand_note_add_ph: 'Add a note…',
    cand_note_add_button: 'Add',
    cand_notes_none: 'No notes yet.',
    cand_notes_legacy_label: 'Earlier notes (before notes were kept one by one)',
    cand_note_by: 'by {who}',
    cand_modal_creating: 'Creating…',
    cand_modal_close_btn: 'Close',
    cand_modal_cancel_btn: 'Cancel',
    cand_modal_saving: 'Saving…',
    cand_modal_save_changes: 'Save',
    cand_search_placeholder: 'Search…',
    cand_filter_status: 'Status',
    cand_filter_district: 'District',
    cand_filter_language: 'Language',
    cand_no_match_filter: 'No candidates match your filters',
    cand_name_err: 'Name is required',
    cand_delete_confirm: 'Delete {name}?',
    cand_hire_confirm: 'Hire {name}? This will move them from candidates to agents.',
    cand_reject_confirm: 'Reject {name}? Are you sure you want to reject this candidate?',
    cand_name_placeholder: 'e.g. Giorgi Beridze',
    cand_phone_placeholder: '+995 555 …',
    cand_email_placeholder: 'name@email.com',
    cand_link_placeholder: 'https://facebook.com/…',
    cand_interviewer_placeholder: 'Who will conduct the interview',
    cand_notes_placeholder: 'Interview notes, impressions, questions asked…',
    // HeatmapTab
    heatmap_low: 'Low',
    heatmap_title: 'Weekly attendance overview',
    heatmap_col_agent: 'Agent',
    heatmap_col_in_office: 'In office',
    // OverrideModal
    override_info: 'This override applies only to this date. Base schedule is not changed.',
    override_day_off_label: 'Day off on this date',
    override_reason_label: 'Reason (optional)',
    override_reason_placeholder: 'e.g. Personal request, client visit, doctor appointment…',
    override_saving: 'Saving…',
    override_no_slots: 'Enter a start and end time, or switch on Day off.',
    override_update: 'Update override',
    override_apply: 'Apply override',
    // ScheduleTab
    sched_override_active_tooltip: 'One-off override active',
    sched_edit_override_tooltip: 'Edit override for this date',
    // App
    app_switch_to_georgian: 'Switch to Georgian',
    app_switch_to_english: 'Switch to English',
    app_error_retry: 'Retry',
    app_tab_load_failed: 'This view failed to load.',
    common_action_failed: 'That didn\'t work. Please try again.',
    admin_dedup_scan_failed: 'Nightly duplicate check failed — automatic matching may be out of date',
    admin_dedup_search_near_cap: 'Client search is nearing its capacity limit',
    // ── Admin de-jargon pass (2026-07-26): crawler / drift monitor / AI / streets ──
    admin_crawler_title: 'Argo — listing auto-finder',
    admin_crawler_desc: 'Re-checks older listings for price changes and for ads that have gone (sold / rented / expired).',
    // B306 (2026-08-05): the complex linker's switch — it had none, so it could only be
    // turned on and off by editing the database by hand.
    admin_complex_linker_title: 'Complex linker',
    admin_complex_linker_desc: 'Links a property to the development it belongs to, by asking AI one question about the ads that actually name a complex. Every answer is stored and never asked twice.',
    admin_complex_linker_mention_note: 'A property whose ad names nothing can only be guessed at from distance, and the guess is kept forever — so the worker waits for a better one instead.',
    admin_complex_linker_sweep_warn: 'Off means every property gets asked about, including the ones with no name in the ad. Those answers are mostly "don\'t know", they cost money, and they are permanent — a property that has been asked about never comes back.',
    admin_complex_linker_dryrun_note: 'test run — nothing was saved',
    admin_discovery_enabled_unset: 'not set; running by default',
    admin_monitor_loading: 'Loading…',
    admin_monitor_intro: 'Changes in the platforms\' listing forms, learned automatically from what agents save. A new field means a platform changed — review it before saving starts to fail.',
    admin_monitor_new_count: '{n} new',
    admin_monitor_show_handled: 'show handled',
    admin_monitor_handled: 'handled',
    admin_monitor_mark_handled: 'Mark handled',
    admin_monitor_reload: 'Reload',
    admin_monitor_drift_title: 'Platform form changes',
    admin_monitor_no_drift: 'No changes — everything matches what we learned. 👍',
    admin_ai_streetmatch_hint: 'runs every time a listing is saved (high volume) — keep it on the light model; off → local lookup only',
    admin_ai_fields_hint: 'Untick to forbid the AI from filling a field (it then uses only what the ad itself states). The list comes from the server model — new fields appear here automatically.',
    admin_ai_reopen_confirm: 'Re-open this decision? It goes back to the pending list and the AI will ask again until you answer it.',
    admin_streets_stat_entries: 'entries',
    admin_streets_stat_entries_t: 'Everything: matched official streets plus the ones still waiting to be assigned.',
    admin_streets_stat_matched: 'matched',
    admin_streets_stat_matched_t: 'Show only matched official streets (hide the ones still waiting).',
    admin_streets_stat_review: 'review',
    admin_streets_stat_overrides: 'overrides',
    admin_streets_stat_official: 'official',
    admin_streets_stat_official_t: 'Distinct official streets — the directory with duplicates removed.',
    admin_streets_stat_review_t: 'Only streets flagged for review.',
    admin_streets_stat_overrides_t: 'Only streets with a manual admin override.',
    admin_streets_filter_ph: 'Filter by street name…',
    admin_streets_unmerge_confirm: 'Separate them? All alternative names go back to the waiting list.',
    admin_agent_section_identity: 'Login details',
    clock_skew_warning: 'Your device clock is off by about {mins} min. The schedule uses Tbilisi time regardless.',
    ext_dl_menu: 'Download extension',
    ext_dl_version: 'Latest version {v}',
    ext_dl_newer: 'newer than yours',
    ext_dl_current: 'you have it',
    ext_dl_busy: 'Downloading…',
    ext_dl_button: 'Download',
    ext_dl_failed: 'Download failed. Try again in a moment.',
    ext_dl_howto: 'Downloaded. Unzip it over your extension folder, then open chrome://extensions and press reload on senTavus, and refresh this page.',
    ext_banner_outdated: 'A new senTavus extension is available ({v}). This browser runs {have}.',
    ext_banner_missing: 'The senTavus extension is not answering on this page. Download the latest version ({v}) and load it in Chrome.',
    ext_banner_later: 'Later',
    app_offline: 'Offline · fill .env.local for Supabase',
    app_connected: 'Connected to Supabase',
    // AgentsTab filter options
    agents_filter_active: 'Active',
    agents_filter_left: 'Left / Resigned',
    agents_filter_all: 'All',
    agents_mark_left_confirm: 'Mark {name} as left / resigned?',
    agents_reactivated_log: 'Reactivated by {by}.',
    agents_left_log: 'Marked as left by {by}.',
    mark_left_title: 'Mark {name} as left?',
    mark_left_body: 'They’ll move to the resigned list — you can reactivate them anytime.',
    mark_left_remove_number: 'Also return work number {number} to the free pool',
    mark_left_move_cards: 'Also return their property cards to the pool',
    mark_left_lock_login: 'Lock their login — blocks senTavus account and extension sign-in immediately',
    agents_left_badge: 'Left',
    agents_resign_future_toast: '{name} will be automatically moved to resigned on {date}.',
    agents_resign_past_toast: '{name} has been marked as resigned (dated {date}).',
    agents_resign_today_toast: '{name} has been marked as resigned as of today.',
    agents_auto_resigned_log: 'Automatically resigned on scheduled date.',
    agents_leaving_badge: 'Leaving {date}',
    // HeatmapTab inner title
    heatmap_inner_title: 'Agents in office — by day & hour',
    // Filter / general
    // Stats tab
    stats_not_enough_data: 'Need data from at least 2 months to show charts.',
    stats_chart_attendance: 'Attendance & Late Rate Trend',
    stats_chart_location: 'Office vs Remote vs Absent',
    stats_chart_absent: 'Absenteeism Rate',
    stats_late_total: 'Late arrivals',
    stats_absent_total: 'Absent days',
    nav_stats: 'Stats',
    nav_review: 'Review',
    review_kind_complexes: 'Complexes',
    // Photo-conflict queue (0389): cross-cluster ads sharing identical photos — the
    // "same flat, two descriptions" evidence. A verdict is a label, never a merge.
    review_kind_far_links: 'Far links',
    // ⚠️ The wording never invites a decision on the METRES. COMPLEX_MATCHING.md records a
    // correct link at 1,054 m and a wrong one at 1,087 m; the discriminator is the
    // preposition in the advert, which is why the sentence is quoted on every row.
    review_far_intro: 'These flats are linked to a development they sit a long way from. Read what the advert says — "in" and "near" are different claims',
    review_far_empty: 'No far links to review.',
    review_far_ratio: '{far} of {total} links are far',
    review_far_pin_hint: '— almost all of them, so the pin is the likelier fault',
    review_far_no_mention: "(the development's name is not in this advert's text)",
    review_far_pin_wrong: 'The pin is wrong',
    review_far_keep_all: 'All correct',
    review_far_unlink_all: 'Unlink all',
    review_far_unlink_one: 'Unlink',
    review_kind_photo_conflicts: 'Photo conflicts',
    review_photo_intro: 'Two ads on different properties share identical photos — probably one flat, described twice. Say which it is; nothing merges automatically.',
    review_photo_empty: 'No unreviewed photo conflicts — the nightly scan found nothing new.',
    review_photo_shared: '{n} identical photos',
    review_photo_area_conflict: 'area disagrees: {a} vs {b} m²',
    review_photo_floor: 'floor',
    review_photo_no_thumb: 'no photo',
    review_photo_same_btn: 'same flat',
    review_photo_diff_btn: 'different',
    review_photo_unsure_btn: 'unsure',
    review_kind_public_price: 'Public price',
    review_kind_public_splits: 'Public splits',
    review_pprice_intro: 'Public flats whose price is withheld. Where our publications quote different prices, pick the one that speaks for the flat; the public page then shows that price.',
    review_pprice_empty: 'No public price is withheld.',
    review_pprice_not_actionable: 'Identity uncertain — the price is withheld until the flat itself is confirmed; price authority cannot release it.',
    review_pprice_use_btn: 'use this price',
    review_pprice_current: 'price authority',
    review_pprice_standing: 'Standing price authorities',
    review_pprice_rooms: 'rooms',
    review_psplit_intro: 'A public flat page that split into several flats. Its old link redirects nowhere until you decide: pick the flat that continues it, or say they are different flats.',
    review_psplit_empty: 'No split public pages waiting.',
    review_psplit_continue_btn: 'this one continues it',
    review_psplit_decline_btn: 'different flats, no successor',
    review_kind_cleaned: 'Cleaned photos',
    review_cleaned_intro: 'Front-page photos with text, phone numbers or logos added on top, cleaned on the office PC. Before | after. Approve to show the cleaned copy on the site; take down to show the original again.',
    review_kind_agent_feedback: 'Agent feedback',
    review_fb_intro: 'Feedback sentavus.com visitors wrote about our agents. The language check publishes clean ones at once, sends back swearing, insults, hate and advertising, and holds the rest here. Criticism of an agent’s work is never a reason to hide.',
    review_fb_held: 'Waiting',
    review_fb_flagged: 'Flagged',
    review_fb_attempts: 'Refused texts',
    review_fb_published: 'Published',
    review_fb_hidden: 'Hidden',
    review_fb_empty: 'Nothing here.',
    review_fb_anonymous: 'shown without name',
    review_fb_quotes: 'Flagged words',
    review_fb_hidden_because: 'Hidden because',
    review_fb_publish: 'Publish',
    review_fb_hide: 'Hide',
    review_fb_hide_reason: 'Why hide it? (kept with the feedback; the visitor is not told)',
    review_fb_tier_transaction: 'verified deal',
    review_fb_tier_interaction: 'verified meeting',
    review_fb_tier_unverified: 'unverified',
    review_fb_reason_profanity: 'swearing',
    review_fb_reason_hate: 'hate speech',
    review_fb_reason_insult: 'insult',
    review_fb_reason_threat: 'threat',
    review_fb_reason_sexual: 'sexual',
    review_fb_reason_advertising: 'advertising',
    review_fb_reason_personal_data: 'personal data',
    review_fb_reason_spam: 'spam',
    review_fb_reason_uncertain: 'checker unsure',
    review_fb_reason_serious_allegation: 'serious accusation',
    review_cleaned_pending: 'Waiting',
    review_cleaned_live: 'Live',
    review_cleaned_empty_pending: 'No cleaned photos waiting.',
    review_cleaned_empty_live: 'No cleaned photos are live.',
    review_cleaned_approve_btn: 'approve',
    review_cleaned_approve_all_btn: 'approve all',
    review_cleaned_reject_btn: 'reject',
    review_cleaned_takedown_btn: 'take down',
    review_cleaned_before: 'before',
    review_cleaned_after: 'after',
    review_psplit_represented: 'ours',
    review_loading: 'Loading',
    review_no_identity: 'No agent profile — link your login to an agent profile to record decisions',
    review_complex_empty: 'No ambiguous complex links to review — the AI decided everything it was asked.',
    review_complex_intro: 'The AI would not decide these property → complex links — you get the final word',
    review_complex_dist_was: 'was',
    review_complex_dist_at_decision: 'at the time of the decision',
    review_show_description: 'Show listing description',
    review_hide_description: 'Hide description',
    review_link_btn: 'Link',
    review_none_btn: 'None of these',
    review_unknown_names_title: 'AI-spotted complex names not in the database',
    review_unknown_names_hint: 'Named in listing descriptions but missing from the complex table — raw material for enriching it.',
    stats_loading: 'Loading…',
    // A failure must never render as 0 on the page that decides pay and ranking.
    stats_load_failed: 'Statistics could not load — no figures are shown. Try again.',
    stats_load_partial: 'Some data failed to load ({what}) — those figures are incomplete.',
    stats_no_data: 'No attendance data for this period.',
    stats_days: 'days',
    stats_agents: 'agents',
    stats_per_day: 'per day',
    stats_late: 'Late',
    stats_late_days: 'late days',
    stats_ontime: 'On time',
    stats_delta: 'Delta',
    stats_back: 'Back',
    stats_leaderboard: 'Leaderboard',
    stats_attendance_rate: 'Attendance rate',
    stats_ontime_rate: 'On-time rate',
    stats_avg_hours: 'Avg hours/day',
    stats_total_delta: 'vs. planned time',
    stats_daily_breakdown: 'Day-by-day',
    stats_team_attendance: 'Team attendance',
    stats_team_ontime: 'Team on-time',
    stats_team_delta: 'Team delta',
    stats_sort_attendance: '↓ Attendance',
    stats_sort_ontime: '↓ On-time',
    stats_sort_delta: '↓ Delta',
    stats_sort_name: 'Name A–Z',
    stats_sort_performance: '↓ Performance',
    stats_sort_listings: '↓ Listings',
    stats_sort_bestday: '↓ Best day',
    stats_score: 'Score',
    stats_performance_score: 'Performance score',
    stats_part_commission: 'Commission',
    stats_part_meetings: 'Meetings',
    stats_part_scrapes: 'Saved',
    stats_part_reshares: 'Re-posted',
    stats_part_office: 'Office',
    stats_pillar_attendance: 'Attendance',
    stats_pillar_activity: 'Activity',
    stats_pillar_results: 'Results',
    stats_results: 'Results',
    stats_coming_soon: 'coming soon',
    stats_total_earned: 'Earned',
    stats_earned: 'Commission',
    stats_commission: 'commission',
    stats_trend_results: 'Results trend', stats_trend_discipline: 'Discipline trend',
    stats_trend_agent_note: 'Punctuality is measured only on days with a check-in, so a gap means no check-in that period — not 0%.',
    stats_active_work_days: 'active work days', stats_vs_team: 'Compared with the team',
    stats_rank: 'Rank', stats_metric: 'Metric', stats_team_avg: 'Team average',
    stats_credit_hint: 'proportional share', stats_participation_hint: 'transactions taken part in',
    stats_credit_explain: 'a shared deal counts as a fraction, not a whole one',
    stats_view_agents: 'Agents', stats_view_managers: 'Managers', stats_view_general: 'General',
    stats_range_7d: '7 days', stats_range_30d: '30 days', stats_range_mtd: 'This month',
    stats_range_90d: '90 days', stats_range_6m: '6 months', stats_range_1y: '1 year',
    stats_vs_prev: 'vs previous period', stats_prev_new: 'new', stats_unrecorded: 'unrecorded',
    stats_cohort: 'Hires in this period', stats_cohort_hint: 'agents hired into offices this manager covered on their hire date',
    stats_hires: 'Hired', stats_hire_to_start: 'Hire → first check-in', stats_ramp_up: 'Hire → first deal',
    stats_never_earned: 'No deal after 90 days', stats_retention: 'Retained', stats_days_short: 'd', stats_median: 'median',
    stats_of_started: 'started', stats_of_earned: 'earned', stats_of_matured: 'matured',
    stats_commission_per_agent_day: 'Gross / agent-day', stats_agent_days: 'agent-days',
    stats_office_scope: 'Office filter, Agents view',
    stats_since_hire: 'From {date}',
    stats_active_agents: 'Active agents', stats_closed_transactions: 'Closed transactions',
    stats_agents_commission: 'Agent commission', stats_no_compare_roster: 'no comparison: {n} of {m} earners joined after the earlier period began', stats_manager_tag: 'manager', stats_no_work_days: 'no working days', stats_no_work_days_tip: 'The schedule of this agent has no working day, so silent and late days cannot count against them. Set their schedule in Agents.', stats_avg_commission_per_agent: 'Avg. commission / agent',
    stats_company_level: 'company level', stats_agents_word: 'agents', stats_transactions_word: 'transactions',
    stats_agent_ranking: 'Agent ranking', stats_sort_hint: 'tap a column to sort',
    stats_col_agent: 'Agent', stats_col_commission: 'Commission', stats_col_deal_credit: 'Deal credit',
    stats_col_participation: 'Participation', stats_col_per_day: 'Published/day',
    stats_col_attendance: 'Attendance', stats_col_ontime: 'Punctuality', stats_col_score: 'Score',
    stats_col_late: 'Late', stats_col_absent: 'Absent', stats_col_overtime: 'Overtime',
    stats_col_deals: 'Deals', stats_col_collabs: 'Shared deals', stats_col_coops: 'Cooperations',
    stats_col_repost_cost: 'Repost cost', stats_meetings_tip: '{done} with an outcome recorded (counted in the score) · {all} in total',
    stats_col_per_hour: 'Published/hour', stats_col_hours: 'Hours worked',
    stats_hours_tip: '{worked} worked of {sched} scheduled',
    stats_hours_estimated_tip: '{days} day(s) credited from office hours ({mins}), not clocked',
    stats_deals_tip: '{n} deals: {solo} alone, {coop} with a colleague',
    stats_late_tip: '{n} times, {mins} in total, counted from the scheduled start',
    stats_absent_tip: '{n} of {sched} scheduled days',
    stats_overtime_tip: 'Sum of the days worked past the schedule ({days} days); net balance {delta}',
    stats_manager_ranking: 'Manager ranking', stats_col_manager: 'Manager',
    stats_col_added: 'Added', stats_col_interviewed: 'Interviewed', stats_col_hired: 'Hired', stats_col_fired: 'Left',
    stats_recruiting: 'Recruiting', stats_recruiting_hint: 'in this period, by this manager',
    stats_no_rate: 'no rate', stats_since: 'since',
    stats_only_active: 'Active', stats_all_incl_past: 'Incl. past', stats_past_agent: 'left',
    stats_not_yours: 'You can open only your own statistics.',
    stats_unranked: 'no closed deals in this period',
    stats_col_manager_commission: 'Manager commission', stats_col_team_deals: 'Team transactions',
    stats_col_team_gross: 'Team gross commission',
    stats_no_managers_title: 'No manager rate set yet',
    stats_no_managers_hint: 'A manager earns a share of every deal closed by their office. Set the team commission % on a manager in the Agents tab and their deals appear here. Nothing is shown as 0, because no rate is not the same as no earnings.',
    stats_manager_unstamped_hint: 'A manager showing no commission while their team has transactions means the rate was set after those deals closed. The rate is frozen onto each deal at close, so it is never applied backwards.',
    stats_manager_result: 'Manager result', stats_manager_rate: 'Manager rate', stats_of_gross: 'of gross commission',
    stats_before_allocation: 'before allocation', stats_team_volume: 'Team volume',
    stats_team_discipline: 'Team discipline', stats_team_productivity: 'Team productivity',
    stats_days_with_checkin: 'days with a check-in',
    stats_published_per_agent_day: 'Published / agent / day', stats_deals_per_agent: 'Transactions / agent',
    stats_commission_per_agent: 'Commission / agent', stats_commission_per_transaction: 'Commission / transaction',
    stats_transactions_per_agent: 'Transactions / agent',
    stats_company_result: 'Company result', stats_reconciles_hint: 'the parts sum back to gross',
    stats_gross_commission: 'Gross commission', stats_managers_commission: 'Manager commission',
    stats_company_retained: 'Company retained', stats_rounding: 'rounding',
    stats_over_allocated: '{n} transaction(s) allocate more than their gross commission - check the agents on them.',
    stats_productivity: 'Productivity', stats_outcome_vs_volume: 'outcome, not volume',
    stats_team_activity: 'Team activity', stats_activity_hint: 'work performed',
    stats_discipline: 'Attendance & punctuality', stats_attention: 'Attention',
    stats_attention_hint: 'a management diagnostic',
    stats_attn_top_commission: 'Highest commission', stats_attn_most_deals: 'Most transactions',
    stats_attn_lowest_activity: 'Lowest publishing rate', stats_attn_most_late: 'Most late days',
    stats_attn_most_absent: 'Most absences',
    stats_scheduled_delta: 'vs. planned time', stats_result_group: 'Result', stats_deals_group: 'Transactions',
    stats_manager_share: 'team override', stats_manager_deals: 'Team override, by deal',
    stats_deals_solo: 'Alone', stats_deals_coop: 'With a partner', stats_partners: 'Closed together with',
    stats_deals: 'Deals',
    stats_closed: 'closed',
    stats_deal_value: 'Deal value',
    stats_total: 'total',
    agent_modal_deals: 'Deals',
    agent_modal_timeline: 'Timeline',
    deals_date: 'Date',
    deals_value: 'Deal value',
    deals_commission: 'Commission',
    deals_add: 'Add deal',
    deals_none: 'No deals yet',
    dv_new: 'New', dv_search_label: 'Find property by ID / cadastral / link',
    dv_search_ph: 'Draft ID, ss/myhome id, cadastral, or URL…',
    dv_searching: 'Searching…', dv_no_results: 'No matching drafts',
    dv_no_key: 'Property search is unavailable (no scheduler key configured).',
    dv_owner_agent: 'Owner agent', dv_pick: 'Select', dv_rooms: 'rooms', dv_floor: 'floor',
    dv_property: 'Property', dv_owner: 'Owner contact', dv_client: 'Client contact',
    dv_manual: 'Enter manually', dv_name: 'Name', dv_phone: 'Phone',
    dv_agents: 'Agents (commission split equally)', dv_add_agent: 'add agent', dv_commission: 'Commission',
    dv_lead_agent: 'Who attended the meeting', dv_lead_default: '— higher-paid attended (default) —',
    dv_comm_imported: 'imported', dv_comm_computed: 'computed', dv_comm_manual: 'manual', dv_comm_cooperation: 'from cooperation',
    dv_deal_price: 'Deal price', dv_comm_percent: '%', dv_comm_fixed: 'Fixed', dv_comm_khelze: 'Owner takes (ხელზე)',
    dv_open_draft: 'Draft', dv_listing: 'Listing',
    dv_search: 'Search address, client, agent…', dv_type_all: 'All types',
    dv_sort_newest: '↓ Newest', dv_sort_scheduled: '↓ Viewing time', dv_sort_comm_high: '↓ Commission',
    dv_related: 'Related:', dv_group_property: 'Property', dv_group_client: 'Client', dv_group_agent: 'Agent',
    dv_no_client: 'No client', dv_no_agent: 'No agent',
    dv_split_each: 'each', dv_status: 'Status',
    dv_status_scheduled: 'Scheduled', dv_status_viewing: 'Viewing', dv_status_pending: 'Pending', dv_status_binder: 'Binder (ბე)',
    dv_status_closed: 'Closed', dv_status_failed: 'Failed',
    dv_status_cancelled: 'Called off', dv_status_rescheduled: 'Postponed',
    dv_sched_date: 'Viewing date', dv_sched_time: 'Time', dv_time: 'Time',
    dv_kind_scheduled: 'Scheduled', dv_kind_reschedule: 'Rescheduled', dv_kind_occurred: 'Happened',
    dv_kind_no_show: 'No-show', dv_kind_rejected: 'Rejected', dv_kind_cancelled: 'Cancelled',
    dv_kind_binder: 'Binder (ბე)', dv_kind_closed: 'Closed',
    dv_what_happened: 'What happened?', dv_add_note: 'Add a note…',
    dv_undo: 'Undo',
    dv_binder_amount: 'Binder (ბე) amount', dv_binder_paid: 'Paid date',
    dv_binder_close: 'Expected close', dv_binder_holder: 'Who holds the ბე',
    // B340 (2026-08-06): deposits are recorded on the meeting now. These two label the
    // read-only remains on this screen, shown only when an old row still holds a figure.
    // ⚠️ RESTORED 2026-08-06 after being clobbered: a copy between checkouts overwrote this
    // file from a tree where these keys had been reverted, and t() then renders the raw KEY.
    dv_binder_legacy_title: 'Deposit recorded earlier',
    dv_binder_moved_hint: 'Deposits are now recorded on the meeting, where the expiry warning can see them.',
    dv_holder_agent: 'Agent', dv_holder_owner: 'Owner', dv_holder_split: 'Split 50/50',
    dv_payment_date: 'Commission paid date', dv_notes: 'Notes',
    dv_save: 'Save', dv_saving: 'Saving…', dv_cancel: 'Cancel', dv_delete: 'Delete',
    dv_delete_confirm: 'Delete this meeting — {what}? Its timeline and agent links go with it.',
    dv_retry: 'Try again',
    dv_close_confirm: 'Record this deal as closed? This marks the listing sold/rented, ends the cooperation and credits the agents — the 15-second undo only puts the status back.',
    dv_bad_date: 'Pick a date first.',
    dv_timeline: 'Timeline', dv_add_event: 'Add update', dv_event_kind: 'Type',
    dv_kind_viewing: 'Viewing', dv_kind_meeting: 'Meeting', dv_kind_call: 'Call',
    dv_kind_note: 'Note', dv_kind_status_change: 'Status change',
    dv_none: 'No deals yet', dv_deal_type_lease: 'Rent', dv_deal_type_trade: 'Sale',
    dv_pick_first: 'Find and select a property above, or enter details manually.',
    admin_perf_weights: 'Performance score weights',
    admin_perf_weights_desc: 'Coefficients for the overall performance score. Only the ratio matters. Results is reserved until that data exists.',
    stats_posted: 'Posted',
    stats_total_posted: 'Listings posted',
    stats_posted_per_day: 'Posted/day',
    stats_best_day: 'Best day',
    stats_active_days: 'active days',
    stats_in_range: 'in range',
    stats_per_day_short: '/day',
    stats_record_most_day: 'Most listings in a day',
    stats_tab_label: 'Stats',
    admin_late_threshold: 'Late arrival threshold (minutes)',
    cand_hired_as_agent: 'added as agent',
    cand_revert_status: 'Revert status',
    cand_hire_revert: 'Undo hire',
    cand_hire_revert_confirm: 'Return {name} to candidates and delete their agent profile?',
    cand_hire_revert_timer: 'Undo hire ({sec}s)',
    cand_modal_info_tab: 'Info',
    cand_interviewed_confirm: 'Mark {name} as interviewed?',
    cand_finalize_action: 'finalize',
    cand_status_starting: 'Starting',
    cand_iv_label: 'Interview',
    att_expected_badge: 'expected today',
    att_onboarding_badge: 'onboarding · first day',
    att_onboarding_note: 'Here today — fill in schedule, phone & details',
    att_onboarding_note_ready: 'First day — here today (off in schedule)',
    att_showed_up: '✓ Showed up',
    att_no_show_btn: "✗ Didn't show",
    att_showed_up_confirm: 'Hire {name}? They will become an agent and be checked in now.',
    att_no_show_confirm: 'Mark {name} as no-show? Status goes back to Interviewed.',
    att_no_show_log: "✗ Didn't show on first day",
    cand_starting_btn: '🚀 Starting',
    cand_undo_starting: '↩ Undo starting',
    cand_start_date_label: 'First day',
    cand_start_confirm: 'Set start',
    cand_starts_today: 'starts today',
    cand_no_show: "didn't show",
    cand_no_show_btn: "✗ Didn't show",
    cand_no_show_confirm: "Mark {name} as no-show for the interview? You can reschedule later if they get back in touch.",
    cand_no_show_was: 'Interview was scheduled for {date}{time} — no-show.',
    cand_reschedule_hint: "A no-show can't be undone — pick a new date to reschedule.",
    cand_reschedule_btn: '🔄 Reschedule',
    cand_view_interviews: 'Interview schedule',
    cand_interviewed_btn: '✓ Interviewed',
    cand_undo_interviewed: '↩ Undo interviewed',
    cand_hire_btn: '🎉 Hire',
    cand_reject_btn: '✕ Reject',
    cand_undo_reject: '↩ Undo reject',
    cand_clear_interview: '✕ Clear interview date',
    cand_date_label: 'Date',
    cand_time_label: 'Time',
    cand_contact: 'Contact',
    cand_fullname_label: 'Full name *',
    cand_bday_label: 'Date of birth',
    cand_selfreported: 'They said',
    cand_study_no: 'Not studying', cand_study_student: 'University student', cand_study_pupil: 'At school',
    cand_ct_label: 'When to call', cand_ct_anytime: 'Any time', cand_ct_business: 'Working hours', cand_ct_custom: 'At a set time', cand_ct_err: 'Enter both times; “from” must be earlier than “to”.',
    cand_sales_none: 'No sales experience', cand_sales_lt1: 'Sales: under a year', cand_sales_1_3: 'Sales: 1–3 years', cand_sales_3p: 'Sales: 3+ years',
    cand_avail_now: 'Can start straight away', cand_avail_1w: 'Can start in a week', cand_avail_2w: 'Can start in 2 weeks', cand_avail_other: 'Start date: other',
    cand_heard_from: 'Heard about us: {v}',
    cand_cv_open: 'Open CV',
    cand_cv_opening: 'Opening…',
    cand_cv_fail: 'Could not open the CV',
    cand_age_years: '{n} years old',
    cand_link_label: 'Profile / social link',
    cand_link_hint: '(Facebook, TikTok, LinkedIn…)',
    cand_open_link: '↗ Open link',
    cand_select_role: '— Select role —',
    cand_select_source: '— Select source —',
    cand_select: '— Select —',
    cand_add_header: 'Add candidate',
    cand_history: 'History',
    log_cand_no_show: "✗ Didn't show for the interview ({date}).",
    log_cand_starting: 'Start date set: {date}{time}',
    cand_iv_now: 'now',
    filter_clear: '✕ Clear',
    filter_clear_all: 'Clear all filters',
    cand_count_one: 'candidate',
    cand_count_many: 'candidates',
    cand_count_of: 'of',
    cand_sort_status: '↑ Status',
    cand_sort_added: '↑ Recently added',
    cand_sort_interview: '↓ Interview date',
    cand_sort_name: '↑ Name A–Z',
    // Log messages
    log_cand_added: 'Candidate added by {by}. Position: {position}. Source: {source}.',
    log_cand_added_short: 'Added{position}{source}.',
    log_cand_deleted: 'Candidate deleted by {by}.',
    log_cand_status: 'Status: {from} → {to}. By {by}.',
    log_cand_scheduled: 'Interview scheduled by {by}: {date} at {time}.',
    log_cand_rescheduled: 'Rescheduled: {from} → {to}.',
    log_cand_rescheduled_full: 'Interview rescheduled by {by}: {from} → {to}.',
    log_cand_cancelled: 'Interview cancelled (was {date}).',
    log_cand_interviewed: 'Interview completed{date}{interviewer}.',
    log_cand_rejected: 'Rejected.{reason}',
    log_cand_hired_short: 'Hired. Agent profile created — fill in schedule in Agents tab.',
    log_cand_hired: 'Hired by {by}. Agent profile created.',
    log_cand_hired_from_pipeline: 'Created from candidate pipeline by {by}.{interviewed}{interviewer}{notes}',
    log_agent_added: 'Added to system by {by}. Role: {role}.{hire_date}',
    log_agent_profile: 'Profile updated by {by}: {diff}',
    log_agent_resign: 'Resignation recorded by {by}.{date}{reason}',
    log_agent_removed: 'Agent removed from system by {by}.{reason}',
    log_schedule_change: 'Schedule change by {by}: {change}',
    log_att_entry: '{date} — {changes}. By {by}.',
    log_att_absent: 'Marked ABSENT on {date} by {by}.{note}',
    log_att_holiday: 'Holiday recorded on {date} by {by}.',
    log_att_worked: ' Worked: {duration}.',
    log_att_delta: ' Delta: {delta}.',
    log_att_deleted: 'Attendance entry for {date} deleted by {by}.',
    log_office_capacity: 'Desk capacity changed by {by}: {from} → {to}.',
    log_office_settings: 'Office settings updated by {by}: {parts}.',
    log_office_deleted: 'Office "{name}" deleted by {by}.',
    log_office_move: 'Moved office by {by}: {from} → {to}.',
    log_office_none: 'unassigned',
    log_office_name_part: 'Name: "{name}"',
    log_office_location_part: 'Location: "{location}"',
    log_diff_checkin: 'Check-in: {from} → {to}',
    log_diff_checkout: 'Check-out: {from} → {to}',
    log_diff_note: 'Note: "{note}"',
    log_diff_field: '{label}: "{from}" → "{to}"',
    log_diff_interviewed_on: ' on {date}',
    log_diff_at_time: ' at {time}',
    log_diff_with: ' with {name}',
    log_diff_reason: ' Reason: {reason}',
    log_diff_date: ' Date: {date}.',
    log_diff_hire_date: ' Hire date: {date}.',
    log_diff_interviewed_was: ' Interviewed: {date}.',
    log_diff_notes: ' Notes: {notes}.',
    log_diff_for: ' for {position}',
    log_diff_via: ' via {source}',
    // Profile field labels for diff
    log_field_name: 'Name', log_field_role: 'Role', log_field_email: 'Email',
    log_field_phone_work: 'Work phone', log_field_phone_personal: 'Personal phone',
    log_field_hire_date: 'Hire date',
    // Rate changes are money. They were the only profile fields that changed silently —
    // dated in agent_rate since scheduler 0025, and named here so a human can read WHO
    // changed a percentage and when, not just that the number is different now.
    log_field_commission: 'Commission %',
    log_field_commission_manager: 'Team commission % (manager)',
    // Live attendance card strings
    att_live_due: 'due',
    att_live_in: 'in',
    att_live_late: 'late',
    att_live_no_checkin: 'No check-in recorded', att_live_unresolved: 'Attendance unresolved', att_live_scheduled: 'scheduled',
    att_live_meetings_unknown: 'meetings could not be checked',
    att_no_checkin_short: 'no check-in', att_unresolved_short: 'unresolved',
    att_prev_period: 'Previous period', att_next_period: 'Next period', att_view_label: 'Attendance view',
    att_read_failed: 'Attendance could not be loaded - what is shown may be missing or out of date.',
    att_overrides_failed: 'One-off schedule changes could not be loaded - some days may show the regular schedule.',
    att_retry: 'Retry',
    att_show_days: 'Show days', att_hide_days: 'Hide days',
    att_save_failed: 'Could not save - nothing was changed. Your entries are still here; try again.',
    att_incl_assumed: 'incl. {d} assumed', att_planned_ahead: '{d} still planned',
    att_bulk_button: 'Check out all overdue ({n})', att_bulk_busy: 'Checking out…',
    att_bulk_confirm: 'Fill the check-out with each person’s scheduled end? It is saved as a manager correction, not a recorded exit.',
    att_bulk_skipped: 'No schedule for this date, not changed', att_bulk_none: 'Nobody here has a schedule for this date.',
    att_bulk_note: 'check-out filled from schedule by a manager', att_bulk_failed: 'Could not save',
    att_live_on_meeting: 'on a meeting since',
    att_live_leaves: 'leaves ~',
    att_live_due_out: 'due out',
    att_live_ago: 'ago',
    att_just_arrived: '✓ Just arrived',
    my_worker_title: 'My computer works for me',
    my_worker_hint: 'Your WhatsApp line and the automatic reposts run on your computer. Pause them while you use it.',
    my_worker_auto: 'Automatic', my_worker_paused: 'Pause', my_worker_running: 'Keep running',
    my_worker_start: 'Start auto mode', my_worker_pause: 'Pause auto mode',
    my_worker_mode_label: 'Automatic mode', my_worker_start_short: 'On', my_worker_pause_short: 'Pause', my_worker_part_held: 'paused by the main switch',
    my_worker_parts: 'Pause one part only:', my_worker_part_reshare: 'Automatic reposts', my_worker_part_wa: 'WhatsApp messages', my_worker_part_on: 'on', my_worker_part_off: 'paused', my_worker_part_hint_reshare: 'Automatic reposts - click to pause or resume only these', my_worker_part_hint_wa: 'WhatsApp messages - click to pause or resume only these',
    my_worker_chip_auto: '↺ By office', my_worker_chip_paused: '⏸ Paused', my_worker_chip_running: '▶ On',
    my_worker_no_lane: 'Auto mode is not on yet - press Start auto mode to join.',
    my_worker_enroll_no_pc: 'No computer with the senTavus extension is online - open Chrome on your work computer.',
    my_worker_enroll_old_version: 'Your extension is old - install the latest from the Desk, then press Start again.',
    my_worker_enroll_no_whatsapp: 'Open WhatsApp Web on your work computer, sign in with your work number, wait a minute and press Start again.',
    my_worker_enroll_number_taken: 'This WhatsApp number already works for another agent - tell the office.',
    my_worker_auto_hint: 'paused while you are marked in the office, running after you leave',
    my_worker_now_paused: 'Auto mode paused', my_worker_now_running: 'Auto mode on',
    my_worker_loading: 'Checking your computer…', my_worker_retry: 'Retry',
    my_worker_read_failed: 'Could not read your auto worker.',
    my_worker_read_stale: 'Could not refresh the auto worker - this is the last state the server confirmed.',
    my_worker_pcs_failed: 'Could not check your computer - showing the check from {t}.',
    my_worker_pcs_failed_first: 'Could not check your computer.',
    my_worker_pc_seen: 'last seen {t}',
    my_worker_ver_checking: 'Checking the extension version…',
    my_worker_ver_unknown: 'Extension version not verified - Start waits until it is checked.',
    my_worker_login_unknown: 'sign-in not reported yet',
    my_worker_save_failed: 'Could not save - your setting did not change. Try again.',
    my_worker_why_office: 'you are in the office', my_worker_why_you: 'your choice',
    my_worker_pc: 'Your computer', my_worker_pc_none: 'No computer with the senTavus extension is connected - open Chrome on your work computer.',
    my_worker_pc_offline: 'offline', my_worker_pc_online: 'online',
    my_worker_ver_old: 'An old extension version ({v}) - install the latest ({latest}) from the Desk first. Until then the switch is locked.',
    my_worker_ver_ok: 'current version',
    my_worker_login_ok: 'signed in', my_worker_login_no: 'not signed in - please sign in to {p} on this computer',
  // ==== Desk tab (IntakeTab.jsx) - desk_* keys ====
    desk_add_client: 'Add client',
    desk_add_request_title: 'New request — {name}',
    desk_addreq_bound: 'linked to this person',
    desk_person_add_request: 'Add request',
    desk_toast_request_saved: '✓ Request added',
    desk_add_property: 'Add property',
    desk_add_business: 'Add business',
    desk_add_business_pick_property: 'A business always lives at a property — pick which one first:',
    desk_add_business_search_ph: 'Search your properties by address…',
    desk_add_business_none: 'No matching property in the loaded list.',
    desk_add_meeting: 'Add meeting',
    desk_add_candidate: 'Add candidate',
    desk_new_applicants_one: 'A new candidate filled in the vacancy form — call them to book an interview',
    desk_new_applicants_failed: 'New applicants could not be loaded.',
    desk_nudge_failed: 'Meeting follow-up reminders could not be loaded - a missing reminder does not mean nothing is overdue.',
    desk_home_retry: 'Retry',
    desk_home_meetings_failed: 'Meetings could not be loaded.',
    desk_home_meetings_loading: 'Loading meetings…',
    desk_home_leads_failed: 'Recent leads could not be loaded.',
    desk_home_leads_stale: 'Could not refresh the leads - these may be out of date.',
    desk_new_applicants_many: '{n} new candidates filled in the vacancy form — call them to book interviews',
    desk_new_applicants_more: '+{n} more in the Candidates tab',
    desk_new_applicants_dismiss: 'Hide this applicant (only for you)',
    desk_new_applicants_unassigned: 'unassigned',
    desk_new_applicants_seen: 'seen',
    desk_new_applicants_new: 'new',
    cand_ev_viewed: 'Card opened',
    desk_add_meeting_pick_property: 'Which property is the meeting about?',
    desk_add_meeting_no_property: 'No property yet — book with a client only',
    desk_add_meeting_search_ph: 'Address, owner name, or phone number…',
    desk_add_meeting_searching: 'Searching every property by that number…',
    desk_add_meeting_owner_unknown: 'owner not on record',
    desk_add_meeting_reveal: 'show number',
    desk_add_meeting_no_contact: 'no number on record',
    desk_add_meeting_none_anywhere: 'No property anywhere in the database carries that number or id.',
    desk_add_meeting_anyway: 'The property is not in this list — book anyway',
    desk_biz_section_title: 'Businesses here',
    desk_biz_facet_summary: 'Businesses operating at this property',
    desk_biz_add: 'Add business',
    desk_biz_none: 'No businesses recorded at this property.',
    desk_biz_category: 'Category',
    desk_biz_name: 'Business name',
    desk_biz_description: 'Description',
    desk_biz_legal_form: 'Legal form',
    desk_biz_tenure: 'Premises',
    desk_biz_tenure_leased: 'Leased',
    desk_biz_tenure_owned: 'Owned',
    desk_biz_rent: 'Monthly rent (USD)',
    desk_biz_landlord_consent: 'Landlord consent',
    desk_biz_yes: 'Yes',
    desk_biz_no: 'No',
    desk_biz_pending: 'Pending',
    desk_biz_land_status: 'Land status',
    desk_biz_land_agricultural: 'Agricultural',
    desk_biz_land_non_agricultural: 'Non-agricultural',
    desk_biz_employees: 'Employees (full-time)',
    desk_biz_reason: 'Reason for sale',
    desk_biz_reason_retirement: 'Retirement',
    desk_biz_reason_relocation: 'Relocation',
    desk_biz_reason_partnership: 'Partnership dissolution',
    desk_biz_reason_expansion: 'Expansion',
    desk_biz_reason_health: 'Health',
    desk_biz_reason_lifestyle: 'Lifestyle',
    desk_biz_other: 'Other',
    desk_biz_grant: 'Government grant / program obligation',
    desk_biz_grant_note: 'Grant note',
    desk_biz_create_listing: 'Create sale listing now',
    desk_biz_price: 'Asking price',
    desk_biz_currency: 'Currency',
    // Renamed from desk_biz_price_negotiable 2026-07-24: it is no longer business-only —
    // ss property listings state the same thing ("ფასი შეთანხმებით"), and a desk_biz_* key
    // rendering on a property card reads like a copy-paste bug to the next person.
    desk_price_on_request: 'Price on request',
    desk_biz_save: 'Save business',
    desk_biz_cancel: 'Cancel',
    desk_biz_err_category: 'Pick a category first.',
    desk_biz_feed_empty: 'No businesses for sale yet.',
    desk_biz_locations: 'Locations',
    desk_biz_features: 'Features',
    desk_biz_assets: 'Assets',
    desk_biz_claims: 'Owner-stated figures',
    // Business offers and financial figures (core 3922, owner 2026-10-26).
    desk_biz_offers: 'Offers',
    desk_biz_no_offers: 'No offer yet',
    desk_biz_offer_add: 'Add offer',
    desk_biz_offer_kind: 'What is offered',
    desk_biz_offer_business_only_premises_leased: 'Business only — premises leased',
    desk_biz_offer_business_with_premises: 'Business with premises',
    desk_biz_offer_business_sold_premises_rented_from_seller: 'Business; the seller rents out the premises',
    desk_biz_offer_business_for_lease: 'Business for lease — they run it, the owner keeps it',
    desk_biz_offer_share: 'Share sold, %',
    desk_biz_offer_share_whole: 'whole business',
    desk_biz_offer_share_line: '{pct}% share',
    desk_biz_offer_fee: 'Monthly fee to the owner',
    desk_biz_offer_upfront: 'Payment up front (optional)',
    desk_biz_offer_upfront_line: '+ {amount} up front',
    desk_biz_offer_buyer_rent: 'Buyer\'s rent',
    desk_biz_offer_rent_line: 'rent {amount} {period}',
    desk_biz_per_month: 'a month',
    desk_biz_per_year: 'a year',
    desk_biz_offer_confirmed: 'Confirmed available {date}',
    desk_biz_offer_unconfirmed: 'Availability not confirmed',
    desk_biz_offer_confirm: 'Still available',
    desk_biz_offer_withdraw: 'Withdraw offer',
    desk_biz_from_advert: 'from the advert',
    desk_biz_financials: 'Financial figures',
    desk_biz_fin_add: 'Add figure',
    desk_biz_fin_kind: 'Figure',
    desk_biz_fin_annual_turnover: 'Annual turnover',
    desk_biz_fin_monthly_revenue: 'Monthly revenue',
    desk_biz_fin_daily_revenue: 'Daily takings',
    desk_biz_fin_net_profit: 'Net profit',
    desk_biz_fin_evidence: 'Source',
    desk_biz_evidence_seller_stated: 'seller\'s word',
    desk_biz_evidence_documents_supplied: 'documents supplied',
    desk_biz_evidence_reviewed: 'reviewed',
    desk_biz_fin_amount_min: 'Amount (from)',
    desk_biz_fin_amount_max: 'Amount (to, if a range)',
    desk_biz_fin_year: 'Year',
    desk_biz_trading: 'Operating',
    desk_biz_trading_trading: 'Trading',
    desk_biz_trading_temporarily_closed: 'Temporarily closed',
    desk_biz_trading_assets_only: 'Assets only',
    desk_biz_handover: 'Seller helps with the handover',
    desk_biz_handover_weeks: 'Handover (weeks)',
    desk_biz_weeks: 'weeks',
    desk_biz_relationship: 'This property is',
    desk_biz_rel_hq: 'Where the business operates',
    desk_biz_rel_operating_land: 'Land the business works',
    desk_biz_rel_branch: 'A branch',
    desk_biz_pub_public: 'Show on sentavus.com',
    desk_biz_pub_show_name: 'Show the business name',
    desk_biz_pub_show_location: 'Show the exact address (otherwise only the district)',
    desk_biz_pub_needs_offer: 'Appears on the site once it has at least one offer.',
    desk_clarify_yes: 'Yes',
    desk_clarify_no: 'No',
    desk_clarify_dont_know: 'Don\'t know',
    desk_clarify_skip: 'Skip',
    desk_clarify_type_answer: 'Type an answer…',
    desk_clarify_got_it: '✓ Got it',
    desk_paste_main_placeholder: 'Paste a client message (Georgian, English, or Russian) and press Enter — AI decides what to add…',
    desk_paste_analyzing: '🧠 AI is analyzing…',
    desk_paste_enter_hint: '↵ Press Enter to fill the form · Shift+Enter for a new line',
    desk_paste_unsure_type: '🤔 Not sure if this is a client, a property, or a meeting — pick a glowing button and it opens pre-filled with what I found.',
    desk_paste_matches_existing: '👇 Matches an existing entry below — glowing amber if the AI is confident, blue if it\'s just a hint. Click the card to continue it, or "Add client" for a new one.',
    desk_paste_followup_existing: '💬 Nothing new to add — looks like a follow-up from an existing contact (see the highlighted entry below).',
    desk_paste_nothing_actionable: '💬 Nothing actionable found in this message.',
    desk_paste_looks_like_property: '🏠 Looks like a property listing, not a client — added below to review (pinned to the top).',
    desk_modal_paste_heading: 'PASTE — AI fills the form, you review',
    desk_modal_paste_owner_ph: 'Paste the owner\'s message / ad text here (optional — or just type below)…',
    desk_modal_paste_client_ph: 'Paste the client\'s message (Georgian, English, or Russian) — optional, or just type below…',
    desk_modal_parsing: '⏳ Reading…',
    desk_modal_parse_to_draft: '✨ Auto-fill',
    desk_modal_paste_text_n: 'Text {n}',
    desk_modal_paste_text_remove: 'Remove',
    desk_modal_paste_add_text: '+ Add text',
    desk_modal_paste_more_ph: 'Another text about the same property — a WhatsApp message, an ad, your own description…',
    desk_modal_parsed_check_fields: '✓ filled in — check the fields below',
    desk_modal_ai_couldnt_fill: '⚠ AI couldn\'t fill: {list of field names}',
    desk_review_decision_badge: 'decision: {decision}',
    desk_review_confidence_badge: 'confidence: {pct}%',
    desk_review_continuing_demand: 'continuing existing demand',
    desk_review_preview: 'Preview',
    desk_review_edit_json: '{ } Edit JSON',
    desk_review_apply_edits: 'Apply edits',
    desk_review_fields_will_change: 'FIELDS THAT WILL CHANGE',
    desk_review_nothing_to_change: 'Nothing to change.',
    desk_review_saving: 'Saving…',
    desk_review_accept_edited_commit: '✓ Accept edited & Commit',
    desk_review_accept_commit: '✓ Accept & Commit',
    desk_review_reject: '✗ Reject',
    desk_review_section_party: 'PERSON',
    desk_review_section_intent: 'INTENT',
    desk_review_section_criteria: 'CRITERIA',
    desk_review_field_price: 'Price',
    desk_review_field_area_sqm: 'Area (sqm)',
    desk_review_field_land_sqm: 'Land/plot (sqm)',
    desk_review_field_rooms: 'Rooms',
    desk_review_field_bedrooms: 'Bedrooms',
    desk_review_drive_max: 'Drive ≤ {n} min',
    desk_review_label_mortgage: 'Mortgage',
    desk_review_label_pets: 'Pets',
    desk_review_label_children: 'Children',
    desk_review_section_districts: 'DISTRICTS',
    desk_review_exclude_prefix: 'exclude:',
    desk_review_section_features: 'FEATURES',
    desk_review_feature_condition: 'Condition',
    desk_review_feature_cond_exclude: 'Cond. exclude',
    desk_review_feature_must_have: 'Must have',
    desk_review_feature_nice_have: 'Nice to have',
    desk_review_feature_must_not_have: 'Must not have',
    desk_review_section_ai_explanation: 'AI EXPLANATION',
    // One card, four provenances — the modal picks by the server-derived `source`
    // (/api/core/requirement/:id). Claiming "the AI" for a client who filled their own
    // form link, or for a hand-typed entry, is a lie the card face already avoids.
    desk_review_intent_readonly_hint: 'INTENT — set by the AI from the client\'s message, not manually editable',
    desk_review_intent_readonly_hint_form: 'INTENT — as the client filled it in their own form, not manually editable',
    desk_review_intent_readonly_hint_manual: 'INTENT — as entered when this client was added, not manually editable',
    desk_review_intent_readonly_hint_unknown: 'INTENT — set when this client was created, not manually editable',
    desk_map_draw: '✏️ Draw',
    desk_map_move: '✋ Move map',
    desk_map_undo_point: '↩ Undo point',
    desk_map_clear_area: 'Clear area',
    desk_map_draw_hint_empty: 'Click, or click-and-drag, to sketch a search area (optional)',
    desk_map_pan_hint: 'Drag to move the map',
    desk_map_points_progress: '{n} point{s} — need 3+ to close an area',
    desk_map_area_set: '{n}-point area set',
    desk_map_clear_pin: 'Clear pin',
    desk_map_pin_coords: '📍 {lat}, {lng}',
    desk_map_pin_hint_empty: 'Click the map to drop a pin (optional)',
    desk_criteria_section_price_size: 'PRICE & SIZE',
    desk_criteria_price_min: 'Price min',
    desk_criteria_price_max: 'Price max',
    desk_criteria_currency: 'Currency',
    desk_criteria_max_drive: 'Max drive (min)',
    desk_criteria_area_min: 'Area min (sqm)',
    desk_criteria_area_max: 'Area max (sqm)',
    desk_criteria_land_min: 'Land/plot min (sqm)',
    desk_criteria_land_max: 'Land/plot max (sqm)',
    desk_criteria_rooms_min: 'Rooms min',
    desk_criteria_rooms_max: 'Rooms max',
    desk_criteria_bedrooms_min: 'Bedrooms min',
    desk_criteria_bedrooms_max: 'Bedrooms max',
    desk_criteria_mortgage_ready: 'Mortgage ready',
    desk_criteria_pets: 'Pets',
    desk_criteria_children: 'Children',
    desk_criteria_section_districts: 'DISTRICTS',
    desk_criteria_districts_must: 'Must have',
    desk_criteria_districts_wanted: 'Nice to have',
    desk_criteria_districts_excluded: 'Not acceptable',
    desk_criteria_section_features: 'FEATURES',
    desk_criteria_section_household: 'HOUSEHOLD (who will live there)',
    desk_criteria_household_tags: 'Household tags',
    desk_criteria_occupants: 'Occupants',
    desk_parse_field_districts_must: "Districts — must",
    desk_parse_field_districts_nice: "Districts — nice to have",
    desk_parse_field_districts_not: "Districts — excluded",
    desk_parse_field_features_must: "Features — must",
    desk_parse_field_features_nice: "Features — nice to have",
    desk_parse_field_features_not: "Features — must not have",
    desk_criteria_search_area: "Search area on the map",
    desk_parse_value_yes: "yes",
    desk_parse_value_no: "no",
    desk_parse_value_drawn_area: "an area drawn on the map",
    desk_enrich_paste_heading: "More details — paste, and the AI fills the gaps",
    desk_enrich_paste_ph: "Paste what the client told you since (Georgian, English, Russian)…",
    desk_enrich_parse: "Parse and enrich",
    desk_parse_reads_as_property: "This reads like a property listing, not a client message.",
    desk_parse_no_demand_found: "No client details found in this message.",
    desk_note_heading: "{n} pasted message{s} behind this record",
    desk_note_more: "show all",
    desk_note_less: "show less",
    desk_note_not_saved: "not saved",
    desk_note_manual_action: "edited by hand",
    desk_note_photo_count: "{n} files",
    desk_note_channel_scheduler: "pasted by an agent",
    desk_note_channel_client_form: "the client filled this in",
    desk_note_channel_facebook_group: "from Facebook",
    desk_note_channel_test: "test",
    desk_note_channel_manual: "uploaded",
    desk_note_channel_sentavus: "uploaded",
    office_wifi_section: "Office Wi-Fi check",
    office_wifi_ssid: "Network name",
    office_wifi_router: "Router address (MAC)",
    office_wifi_loading: "Checking…",
    office_wifi_never: "No reporter has ever posted from this office — arrival cannot be confirmed by Wi-Fi here.",
    office_wifi_live: "Reporter is live — {n} device(s) on the network right now (via {src}).",
    office_wifi_src_router_self: 'the router itself',
    office_wifi_self_title: 'Router reports by itself',
    office_wifi_self_why: 'Then no office computer has to be on: the router tells senTavus who is connected every minute, and anyone logged in to the Desk on this Wi-Fi — phone or laptop — can mark arrival and it is verified.',
    office_wifi_self_btn: 'Get the install command',
    office_wifi_self_how: 'On a computer connected to the office Wi-Fi, open PowerShell (Windows) or Terminal (Mac), paste this, press Enter, and type the router admin password when asked. Done once. A computer reporting for this office stops; the router takes over.',
    office_wifi_self_copy: 'Copy',
    office_wifi_self_copied: 'Copied',
    office_wifi_self_secret: 'The command contains this office’s key — do not share it. Getting a new command makes the old one stop working.',
    office_wifi_counts: "{registered} phone(s) registered to this office; {matched} of them on the network now.",
    // 0012: the live figures above span every office on this router, so say which.
    office_wifi_shared: "Same router as: {names} — the counts above cover both offices.",
    office_wifi_silent: "Reporter silent for {mins} minutes — the computer it runs on is off, Chrome is closed, or it left the office network.",
    office_wifi_connected_by: "Reporting from {name}'s browser{model} · connected {date}.",
    office_wifi_this_browser: "This browser is the office's reporter — keep Chrome open on this computer during office hours.",
    office_wifi_router_changed: "The reporter now sees a different router ({mac}{ssid}). If the office router was replaced, use it.",
    office_wifi_use_router: "Use this router",
    office_wifi_connect: "Connect to router",
    office_wifi_reconnect: "Reconnect router",
    office_wifi_disconnect: "Disconnect",
    office_wifi_disconnect_confirm: "Disconnect this office's router? Reporting stops until someone connects it again.",
    office_wifi_finding: "Looking for the office router…",
    office_wifi_host: "Router address",
    office_wifi_found: "Router found at {host}. Enter its admin password — it stays in this browser's extension and is never sent to Sentavus.",
    office_wifi_password: "Router admin password",
    office_wifi_connecting: "Connecting…",
    office_wifi_connected_now: "Connected — {n} device(s) on the network now.",
    office_wifi_no_ext: "Open this page in Chrome with the senTavus extension, on the office Wi-Fi.",
    office_wifi_old_ext: "This senTavus extension is too old to connect a router — update it, then reload this page.",
    office_wifi_stale_ext: "The extension was reloaded — refresh this page.",
    office_wifi_router_line: "Router {mac} · {ssid}",
    office_wifi_hint: "Connect once from a computer on the office Wi-Fi: the senTavus extension logs into the router and, while Chrome is open, reports every minute which devices are on the network. Its access renews itself. Agents register their phone once; arrival is then confirmed because the office saw it.",
    desk_criteria_children_ages: 'Children ages',
    desk_criteria_move_in: 'Move-in date',
    desk_criteria_check_in: 'Check-in date',
    desk_criteria_check_out: 'Check-out date',
    desk_criteria_type_features: 'Specific features',
    desk_criteria_power_min: 'Min. power (kW)',
    desk_facts_power_kw: 'Power capacity (kW)',
    desk_facts_functional_zone: 'Functional zone (e.g. სზ-2)',
    desk_criteria_section_quality: 'QUALITY LEVEL',
    desk_criteria_dealbreakers: 'Specific must / must-not',
    desk_criteria_more_features: 'More features…',
    desk_tier_legend: 'Tap a chip to choose what it means',
    desk_tier_chip_cycle_hint: 'Tap to change or remove',
    desk_tier_must: 'Must have',
    desk_tier_nice: 'Nice to have',
    desk_tier_not: 'Must not',
    desk_tier_clear: 'Remove',
    desk_criteria_district_search_ph: 'Search district…',
    desk_criteria_feature_search_ph: 'Search feature…',
    desk_district_group_other: 'Other',
    desk_district_search: 'Search districts…',
    desk_district_all_cities: 'All of Georgia',
    desk_district_no_match: 'Nothing matches',
    desk_district_tree_hint: 'Tap a district for the whole area, or › to narrow to a neighbourhood.',
    desk_sale_terms_title: 'SALE CONDITIONS — seller\'s stated terms (agent-visibility only)',
    // GIRAO (core 0219) — the third deal shape. No monthly rent, so none of the rental
    // money vocabulary applies; "deposit" would be actively wrong for the gira sum.
    desk_terms_other_shape: 'Conditions are also recorded here for:',
    desk_girao_terms_title: 'GIRAO CONDITIONS — owner\'s stated terms (agent-visibility only)',
    desk_girao_amount: 'gira sum',
    desk_girao_period: 'period (years)',
    desk_girao_owner_resides: 'does the owner live there too?',
    desk_girao_owner_resides_yes: 'yes — owner stays in the property',
    desk_girao_owner_resides_no: 'no — you get the property to yourself',
    desk_girao_hint: 'The gira sum is handed over at the start and returned at the end — it is not a price and not a deposit. Whether the owner keeps living there decides the deal for most clients: ask it before the viewing, not after.',
    desk_sale_policies_label: 'Who the seller sells to (structured — matches client tags)',
    desk_sale_cash_only: 'Payment accepted',
    desk_sale_cash_only_yes: 'cash only',
    desk_sale_cash_only_no: 'cash or mortgage',
    desk_sale_full_price: 'Will declare full price',
    desk_sale_full_price_yes: 'yes — full price',
    desk_sale_full_price_no: 'no — wants lower official price',
    desk_sale_occupancy: 'Occupancy',
    desk_sale_handover: 'Handover after signing',
    desk_terms_movein_timing: 'Tenant can move in',
    desk_sale_furniture: 'Furniture included',
    desk_sale_parking: 'Parking in price',
    desk_sale_encumbrance: 'Registry status',
    desk_sale_bank_appraisal: 'Bank appraisal',
    desk_sale_summary_appraisal: 'appraised {v} {c}',
    desk_sale_appraisal_opt_none: 'no bank appraisal',
    desk_sale_appraisal_opt_appraised: 'appraised by a bank',
    desk_sale_appraisal_amount: 'Appraised at (if known)',
    desk_sale_opt_vacant: 'vacant',
    desk_sale_opt_owner: 'owner lives here',
    desk_sale_opt_tenant: 'tenant lives here',
    desk_sale_opt_immediate: 'immediate',
    desk_sale_opt_one_week: 'up to 1 week',
    desk_sale_opt_two_weeks: 'up to 2 weeks',
    desk_sale_opt_one_month: 'up to 1 month',
    desk_sale_opt_flexible: 'flexible / negotiable',
    desk_sale_opt_none: 'empty / built-ins only',
    desk_sale_opt_partial: 'partial',
    desk_sale_opt_as_seen: 'as seen in photos',
    desk_sale_opt_included: 'included',
    desk_sale_opt_separate: 'sold separately',
    desk_sale_opt_no_parking: 'no parking',
    desk_sale_opt_clean: 'clean registry',
    desk_sale_opt_bank_mortgage: 'active bank mortgage',
    desk_terms_summary_deposit: 'deposit {n}mo',
    desk_terms_summary_months: '{n} months up front',
    // 1083 — of 489 adverts naming a deposit, only 115 give a sum. 87 give a number of
    // months, 7 give half a month, and 278 just say there is one.
    desk_terms_deposit_required: 'Deposit required',
    desk_terms_deposit_required_short: 'Deposit',
    desk_terms_deposit_basis_amount: 'a fixed sum',
    desk_terms_deposit_basis_percent: '% of a month',
    desk_terms_summary_deposit_amount: 'deposit {v} {c}',
    desk_terms_summary_minlease: 'min {n}mo',
    desk_terms_summary_maxocc: 'max {n} ppl',
    desk_terms_policies_label: 'Who the owner accepts (structured — matches client household tags)',
    desk_terms_policies_legend: 'Tap to cycle: ✓ allowed → ★ preferred → ✗ not allowed → ❗ only → off',
    // In-place owner-gap answer from the match panel's hoisted "ask the owner" chips.
    desk_policy_answer_prompt: 'Owner\'s stance:',
    desk_stance_allowed: 'allowed',
    desk_stance_preferred: 'preferred',
    desk_stance_not_allowed: 'not allowed',
    desk_stance_only: 'only',
    // ── Matching panel v2 (design/MATCHING_UI_BRIEF_2026-08-07.md). Georgian
    // native-speaker reviewed 2026-08-08; the review caught two drafts that meant the
    // OPPOSITE of the English (mv2_nobody_asked_owner, mv2_step2), so do not "tidy" the
    // Georgian here against the English without another review.
    // ⚠️ The per-subject owner QUESTIONS are NOT here — they are rows on
    // feature.question_ka (migration 0495), so a Georgian speaker can fix them without a
    // deploy and they cannot drift from the subject list.
    mv2_title: 'Matching clients',
    mv2_subtitle: 'Checked {n}, {m} may suit',
    mv2_updated_ago: 'Updated {n} minutes ago',
    mv2_step1: 'First this call — the owner',
    mv2_step1_sub: '{n} empty fields · unlocks {m} clients',
    mv2_unlocks: 'unlocks {n}',
    mv2_step2: 'Then — the client calls',
    mv2_wouldnt_say: 'wouldn\'t say',
    mv2_recorded_by: 'recorded {date} · {name}',
    mv2_no_longer_asked: 'no longer asked',
    mv2_recount_promise: 'The list recounts as soon as you record an answer — new clients will appear too',
    mv2_more_questions: '{n} more questions',
    mv2_each_unlocks: 'each unlocks 1–2 clients',
    mv2_expand: 'expand',
    mv2_owner_fields_only: 'Only fields the owner answers are here. District, size, budget are the client\'s side',
    mv2_group_ready: 'present now',
    mv2_group_ready_empty: 'The first recorded answer will bring cards here',
    mv2_col_client: 'client — click to expand',
    mv2_col_what_to_do: 'what to do',
    mv2_band_why: 'why it matches',
    mv2_band_mismatch: 'doesn\'t match',
    mv2_band_to_fill: 'to fill in',
    mv2_col_client_asks: 'client asks for',
    mv2_col_on_property: 'on the property',
    mv2_col_source: 'source',
    mv2_col_who_answers: 'who answers',
    mv2_col_known_today: 'what we know today',
    mv2_fill_in: 'fill in',
    // The band's "what we know today" cell for an owner gap. Deliberately SHORTER than
    // mv2_nobody_asked_owner, which names the owner again one column after the "who answers"
    // cell already did — in a 1fr column that truncated both to "მეპატრონ…".
    mv2_not_asked_yet: 'nobody has asked yet',
    // The mismatch band's lever (02-spec "✗ row + lever button"). A mismatch is not a blank
    // to fill — the fact is known and disagrees — so the button asks the only question that
    // moves it: will the client let this one go?
    mv2_will_concede: 'would they concede?',
    mv2_owner_answers: 'the owner answers',
    mv2_client_answers: 'the client answers',
    mv2_nobody_asked_owner: 'nobody has asked the owner',
    mv2_blocks_nothing: 'blocks nothing — for information',
    mv2_details_loading: 'details loading…',
    // The phone row's ONE chip for everything the strip above already asks. Never one chip
    // per question: the strip is the call, the row is the consequence.
    mv2_chip_asked_above: '? {n} above',
    mv2_one_source_verify: 'one source — verify',
    mv2_fixed_condition: 'fixed condition',
    mv2_legend_never_asked: 'the client never asked for this',
    mv2_legend_loading: 'detail loading (the structure is already here)',
    mv2_legend_fixed: 'fixed condition, not up for negotiation',
    mv2_contact_property_side: 'property side',
    mv2_contact_client_side: 'client side',
    mv2_role_owner: 'owner',
    // The third party. Not reachable from the who-answers column (an answer is the owner's
    // or the client's, never ours) — it exists for the attendee and representative rows,
    // where all three roles meet and RoleTag is the one label for them.
    mv2_role_agent: 'agent',
    mv2_role_property_rep: 'property representative',
    mv2_role_client: 'client',
    mv2_no_other_rep: 'The client has no other representative — only you',
    mv2_recorded_conditions: 'recorded conditions',
    mv2_exception: 'exception',
    mv2_another_condition: 'another condition',
    mv2_empty_no_requirement: 'We don\'t know yet what this client wants',
    mv2_empty_add_requirement: 'Add their requirement',
    mv2_empty_market: 'Nothing on the market meets all of these today',
    mv2_binding_constraint: 'The binding constraint: {field}',
    mv2_approximately: 'approximately',
    mv2_money_no_help: 'Money doesn\'t help here',
    mv2_ladder_mine: 'my properties',
    mv2_ladder_leads: 'leads',
    mv2_ladder_search: 'search yourself',
    mv2_lead_call_first: 'call the owner first',
    mv2_summary: 'Compared {total} conditions · {met} match · {missed} don\'t · {ownerGaps} to ask the owner + {clientGaps} to ask the client → fill both sides, then present it',
    mv2_summary_all_ok: 'Everything matches — present it.',
    mv2_summary_owner_only: 'One call to the owner and this is ready.',
    mv2_summary_client_only: '{n} details to clear up with the client and this is ready.',
    mv2_summary_mismatch: '{n} conditions don\'t match — decide whether it is still worth showing.',
    // Round 2 (2026-08-08), from the two designer redlines. The COLUMN labels here are panel
    // chrome for the five core scorer checks. A RESTRICTION SUBJECT's label is never a
    // constant: it comes from feature.name_ka, its question from feature.question_ka (0495).
    // Do not add a subject to this list.
    mv2_col_district: 'district',
    mv2_col_price: 'price',
    mv2_col_bedrooms: 'beds',
    mv2_col_occupants: 'people',
    mv2_col_condition: 'repair',
    mv2_col_other: 'other',
    mv2_grp_ready_means: 'nothing left to ask the owner',
    mv2_grp_verify_means: 'waiting on the owner reply',
    mv2_grp_negotiate_means: 'the client will have to concede',
    mv2_grp_discuss_means: 'the client own requirement is blocking',
    mv2_act_waiting_owner: 'waiting on {n} answers from the owner',
    mv2_act_then_concede: 'first {n} answers, then — will they concede on {subject}?',
    mv2_act_call_concede: 'call — will they concede on {subject}?',
    mv2_act_ask_client: 'ask {name} about {subject}',
    mv2_legend_met: 'matches',
    mv2_legend_missed: 'negotiable',
    mv2_legend_client_cond: 'the client own condition',
    mv2_legend_owner_unasked: 'we have not asked the owner',
    mv2_yes: 'yes',
    mv2_no: 'no',
    mv2_people: 'people',
    mv2_unlocks_clients: 'unlocks {n} clients',
    desk_feature_answer_prompt: 'Property has this?',
    desk_feature_has: 'has it',
    desk_feature_absent: 'doesn\'t have it',
    desk_term_answer_prompt: 'Owner\'s rule?',
    desk_term_occupants_prompt: 'Owner\'s max occupants?',
    desk_term_occupants_unit: 'people',
    desk_term_allowed: 'Allowed',
    desk_term_not_allowed: 'Not allowed',
    desk_term_accepts_mortgage: 'Accepts mortgage',
    desk_term_cash_only: 'Cash only',
    desk_maturity_heading: 'Confidence',
    desk_maturity_confirmed: 'confirmed by multiple sources',
    desk_maturity_conflict: 'sources disagree — verify',
    desk_maturity_single: 'single source — unconfirmed',
    desk_maturity_sources: '{n} sources',
    desk_owner_note_label: 'Owner\'s note (from intake form)',
    desk_ad_description_label: 'Owner\'s comment (from the ad)',
    desk_ad_description_from_origin: 'original ad',
    desk_ad_description_more: 'show all',
    desk_ad_description_versions: 'earlier versions ({n})',
    desk_ad_description_less: 'show less',
    desk_criteria_floor_min: 'Min floor',
    desk_criteria_floor_max: 'Max floor',
    desk_criteria_floor_not_first: 'Not ground floor',
    desk_criteria_floor_not_last: 'Not top floor',
    desk_facts_balcony_count: 'Balconies (count)',
    desk_facts_balcony_area: 'Balcony area (m²)',
    desk_facts_area_each_or_total: 'each or total, whichever known',
    desk_property_state_label: 'Condition — not habitable (frame / construction)',
    desk_cond_occupancy: 'After renovation', desk_cond_building: 'Building', desk_cond_frame: 'Frame stage', desk_cond_finish: 'Finish', desk_cond_class: 'Class',
    desk_cond_class_na: 'Does not apply to a frame or a building under construction',
    desk_property_class_label: 'or apartment class (livable — matches client demand levels)',
    desk_criteria_feature_must_have: 'Must have',
    desk_criteria_feature_nice_have: 'Nice to have',
    desk_criteria_feature_must_not_have: 'Must NOT have',
    desk_criteria_section_search_area: 'SEARCH AREA (draw on map)',
    desk_client_edit_title: 'Edit client',
    desk_loading: 'Loading…',
    desk_couldnt_load: 'Couldn\'t load — {err}',
    desk_client_status_closed: 'closed',
    desk_client_status_active: 'active',
    desk_client_see_profile: 'See everything about this person',
    desk_client_fallback_person: 'this person',
    desk_client_not_saved_err: '⚠️ NOT saved — {err}',
    // "request", NOT "client" (owner-caught 2026-07-20): close_requirement closes ONE demand;
    // since B48 a person can hold several, and the person + their other requests are untouched.
    desk_client_reopen_tooltip: 'Reopen this request',
    desk_client_close_tooltip: 'Close this request — never deleted, reopen anytime',
    desk_client_reopen_button: '↩ Reopen request',
    desk_client_close_button: '✕ Close request',
    desk_client_cancel: 'Cancel',
    desk_client_save_changes: '✓ Save changes',
    desk_client_no_changes: 'No changes',
    desk_client_close_confirm: 'Close this request? It is never deleted — you can reopen it anytime.',
    desk_client_closed_toast: '✓ Request closed — reopen anytime from the list',
    desk_client_reopened_toast: '✓ Request reopened',
    desk_client_review_title: 'Review client',
    desk_client_name_label: 'Name *',
    desk_client_name_ph: 'Client name',
    desk_client_phone: 'Phone',
    desk_client_email: 'Email',
    // phone-shape warnings — a nudge, never a block (the agent may be holding a
    // number we have no rule for; losing the whole client record is the worse bug)
    desk_phone_shape_len: '⚠ A {cc}number has {want} digits — this one has {got}. Worth re-checking before saving.',
    desk_phone_shape_lead: '⚠ Georgian numbers start with 5 (mobile) or 3 (landline). Worth re-checking before saving.',
    // contact book (core 0141/0142) — observations, never edits
    desk_contact_book: 'Contacts ({n})',
    desk_contact_empty: 'No contact history yet — only the original record above.',
    desk_contact_add_ph: 'Add phone or email…',
    desk_contact_add: 'Add',
    desk_contact_make_primary: 'Make primary',
    desk_contact_mark_unreachable: 'Mark unreachable (kept as history, never deleted)',
    desk_contact_primary: 'Primary — this is the number to call',
    desk_client_looking_to: 'Looking to',
    desk_intent_opt_buy: 'buy',
    desk_intent_opt_rent: 'rent',
    desk_intent_opt_buy_or_rent: 'buy or rent',
    desk_intent_opt_short_term_rent: 'short-term rent',
    desk_intent_opt_invest: 'invest',
    desk_intent_opt_lease: 'lease out',
    desk_intent_opt_sale: 'sell',
    desk_kind_penthouse: 'Penthouse',
    desk_kind_townhouse: 'Townhouse',
    desk_kind_duplex: 'Duplex',
    desk_kind_shop: 'Shop',
    desk_kind_cafe: 'Cafe',
    desk_kind_warehouse: 'Warehouse',
    desk_kind_basement: 'Basement',
    desk_kind_commercial_special: 'Special commercial',
    desk_status_val_active: 'Active',
    desk_status_val_paused: 'Paused',
    desk_status_val_withdrawn: 'Withdrawn',
    desk_status_val_sold: 'Sold',
    desk_status_val_rented: 'Rented',
    desk_status_val_expired: 'Expired',
    desk_delta_vs_post: 'vs this post',
    desk_delta_vs_prev: 'vs previous price',
    desk_lineage_more_postings: '+{n} more postings',
    desk_lineage_more_agents: '+{n} more agents',
    desk_client_property_type: 'Property type',
    desk_category_opt_apartment: 'apartment',
    desk_category_opt_room: 'room in a shared flat',
    desk_context_aparthotel: 'apart-hotel',
    desk_aparthotel_details: 'Apart-hotel terms',
    desk_category_opt_house: 'house',
    desk_category_opt_land: 'land',
    desk_category_opt_commercial: 'commercial',
    desk_category_opt_cottage: 'cottage',
    desk_category_opt_office: 'office',
    desk_category_opt_garage: 'garage',
    desk_category_opt_hotel: 'hotel',
    desk_category_opt_other: 'other',
    desk_client_urgency: 'Urgency',
    desk_urgency_opt_immediate: 'immediate',
    desk_urgency_opt_soon: 'soon',
    desk_urgency_opt_flexible: 'flexible',
    desk_urgency_opt_browsing: 'browsing',
    desk_client_accept_save: '✓ Accept & save',
    desk_client_add_button: '✓ Add client',
    desk_gap_heading: '🧠 Would be better to know:',
    desk_gap_client_phone: 'phone number — spots the same client twice, and the only way to call back',
    desk_gap_client_budget: 'budget ceiling — matching is blind without it',
    desk_gap_client_location: 'where they want it — districts or a drawn search area',
    desk_gap_client_rooms: 'rooms / bedrooms',
    desk_gap_client_size: 'approximate size (m²)',
    desk_gap_client_urgency: 'urgency — how soon they need it',
    desk_gap_client_dealbreakers: 'any dealbreakers (parking, balcony, pets…)',
    desk_gap_property_price: 'asking price — even "negotiable around X"',
    desk_gap_property_phone: 'contact phone — without it nobody can act on this',
    desk_gap_property_rooms_area: 'rooms + area (m²) — the two facts every client filters on',
    desk_gap_property_area_fallback: 'area (m²)',
    desk_gap_property_land_area: 'land area (m²)',
    desk_gap_property_rooms_fallback: 'rooms',
    desk_gap_property_district: 'district',
    desk_gap_property_pin: 'location pin on the map — geo matching beats address text',
    desk_gap_property_floor: 'floor',
    desk_gap_property_cadastral: 'cadastral code — the surest way to tell two ads are the same property',
    desk_gap_property_condition: 'renovation state',
    desk_completeness_client: 'Client profile',
    desk_completeness_property: 'Property record',
    desk_completeness_missing: 'Missing',
    desk_completeness_full: 'All core fields filled',
    desk_gap_fill_title: 'Fill what you know — saves to the client',
    desk_gap_fill_unit_usd: 'USD',
    desk_gap_fill_unit_sqm: 'm²',
    desk_gap_fill_unit_rooms: 'rooms',
    desk_gap_fill_save_title: 'Save',
    desk_gap_fill_more_hint: 'open the client to edit',
    desk_match_heading_properties: 'MATCHING PROPERTIES — homes in our database this client may like',
    desk_match_heading_clients: 'MATCHING CLIENTS — our clients who may want this property',
    desk_match_eligible_of: 'checked {considered}, {eligible} could fit',
    desk_match_lookup_failed: 'Match lookup failed. Please try again.',
    desk_match_scoring: '⏳ Scoring against the live database…',
    // B78 shallow-requirement soft gate (2026-07-20): shown INSTEAD of the heavy match
    // run when candidates > min(2000, 30% of category supply). Nothing is blocked —
    // "show anyway" runs the full path (logged server-side as match.forced_broad).
    desk_match_too_broad_title: '🔎 {count} listings fit — too broad to rank usefully',
    desk_match_too_broad_sub: 'Narrow it with the client (any one of these):',
    desk_match_ask_rooms_or_area: 'How many rooms, or what size?',
    desk_match_ask_district: 'Which districts?',
    desk_match_ask_budget: 'Maximum budget?',
    desk_match_make_binding: 'Client said {names} — make it binding',
    desk_match_make_binding_hint: 'Districts are recorded as a soft preference — confirm them as binding to narrow the search',
    desk_match_show_anyway: 'Show matches anyway',
    desk_match_no_fit: 'No good fit?',
    desk_match_search_leads: 'Search leads for this client',
    desk_match_search_leads_n: 'Search {n} leads for this client',
    desk_match_none_eligible: 'No eligible matches yet.',
    desk_match_fallback_client: 'Client',
    desk_match_agent_suffix: '· agent',
    // Hoisted property-gap strip (2026-07-17 density pass): the shared "ask the owner"
    // questions, shown once above the client cards instead of repeated on each.
    desk_match_owner_gap_lead: 'One owner call clears these:',
    desk_match_owner_gap_headline: 'One owner call — answers unblock {n} matches',
    desk_match_verified_short: 'verified {pct}%',
    desk_match_blocked_by_shared: 'blocked by {n} shared owner questions ↑',
    desk_match_seg_pass: 'passed',
    desk_match_seg_partial: 'partial',
    desk_match_seg_unknown: 'unknown',
    desk_match_seg_fail: 'failed',
    desk_match_tier_ready: 'ready to offer',
    desk_match_tier_confirm: 'needs confirmation',
    desk_match_tier_concession: 'client concession needed',
    desk_match_tier_ready_action: 'Offer',
    desk_match_offer_whatsapp: 'Offer via WhatsApp (marks proposed)',
    desk_match_offer_call: 'Call the client (marks proposed)',
    desk_match_tier_confirm_action: 'Confirm with the client',
    desk_match_tier_concession_action: 'Discuss with the client',
    desk_match_contact_agent: 'Contact agent {name}',
    desk_match_more_actions: 'More actions',
    // Check-by-check breakdown behind the certainty bar + registration-age chips
    // (2026-07-18 request: see what matches/unknown/conflicts, client's own blanks,
    // and how long a demand/listing has been sitting).
    desk_match_bd_toggle_hint: 'Tap for the full check-by-check breakdown',
    desk_match_bd_pass: '✓ matches',
    desk_match_bd_partial: '≈ near-miss',
    desk_match_bd_fail: '✗ conflicts',
    desk_match_bd_ask_owner: '? ask owner',
    desk_match_bd_ask_client: '? client unstated',
    desk_match_client_gaps: 'client hasn’t said: {list}',
    desk_match_reduced_because: 'Reduced because',
    desk_match_reduced_conflict: 'sources disagree',
    desk_match_reduced_single: 'one source',
    desk_needs_verify_heading: 'Confirm on your next owner call · {n}',
    desk_needs_verify_record_hint: 'Tap to record what the owner says',
    desk_match_registered_client_title: 'Client demand registered',
    desk_match_registered_listing_title: 'Listing registered',
    // Freshness follow-through set (0110 + B34 session, 2026-07-18): last-activity
    // clock, one-tap confirm, proposed-loop chip, NEW-since-last-look badge.
    desk_match_last_activity_title: 'Last activity',
    desk_match_new_badge: 'NEW',
    desk_match_confirm_active: '✓ still looking?',
    desk_match_confirm_active_hint: 'One tap records "client confirmed still looking" and resets the staleness clock — use after actually speaking to them',
    desk_match_proposed_ago: 'proposed {ago}',
    desk_match_no_viewing_yet: 'no viewing yet',
    desk_match_viewing_after: 'viewing since ✓',
    // Recommendation opportunity UI (Phase 1 surfacing, 2026-07-13): classification badge,
    // coverage-next-to-score, and humanized scorer keys (match_key_*: checks AND delta kinds
    // share one namespace; unknown keys fall back to underscores→spaces in MatchExplain).
    // Client modal read-only stage (2026-07-13): opens as a view like the property/lineage
    // modal — criteria as fact rows, ✎ Edit to switch to the form.
    desk_client_view_title: 'Client demand',
    desk_client_edit_button: '✎ Edit',
    desk_client_criteria_section: 'CRITERIA — what they are looking for',
    desk_crit_bedrooms: 'Bedrooms',
    desk_crit_land: 'Land area',
    desk_crit_max_drive: 'Max drive (min)',
    desk_crit_districts_must: 'Districts (must)',
    desk_crit_districts_excluded: 'Districts (excluded)',
    desk_crit_must_have: 'Must have',
    desk_crit_nice_to_have: 'Nice to have',
    desk_crit_must_not: 'Must not have',
    desk_crit_household: 'Household',
    desk_crit_occupants: 'Occupants',
    desk_crit_children_ages: 'Children ages',
    desk_crit_move_in: 'Move-in',
    desk_crit_check_out: 'Check-out',
    desk_crit_mortgage_ready: 'Mortgage-ready',
    desk_crit_parking_deeded: 'Parking in ownership (own cadastral code)',
    desk_crit_has_pets: 'Has pets',
    desk_crit_has_children: 'Has children',
    desk_crit_max_deposit_months: 'Max deposit (months of rent)',
    desk_crit_prepayment_months: 'Can prepay (months)',
    desk_crit_search_area: 'Search area',
    desk_crit_search_area_drawn: '✓ drawn on map',
    desk_crit_power: 'Required power (kW)',
    desk_crit_none: 'No criteria recorded yet — click Edit to add them',
    // Per-person household profile (2026-08-02) — the fill form makes sex + nationality
    // mandatory for every adult on a long-term rent; these render what was actually said,
    // under the derived `household_codes` tags rather than instead of them.
    desk_crit_people: 'Who will live there',
    // B300 — the declared couple/flatmates answer (extra.household_composition).
    desk_crit_together: 'Together as',
    desk_crit_together_couple: 'a couple',
    desk_crit_together_group: 'flatmates / friends',
    desk_crit_people_masked: 'hidden — another agent’s client',
    desk_crit_person: 'Person',
    desk_crit_child: 'Child',
    desk_crit_working: 'working',
    desk_crit_not_working: 'not working',
    desk_crit_pet: 'Pet',
    desk_crit_pet_cat: 'cat',
    desk_crit_pet_dog: 'dog',
    desk_crit_pet_other: 'animal',
    desk_crit_pet_size_small: 'small',
    desk_crit_pet_size_medium: 'medium',
    desk_crit_pet_size_large: 'large',
    desk_crit_condition_exclude: 'Condition (excluded)',
    desk_crit_condition_clash: '⚠ contradicts what they asked for',
    desk_crit_condition_clash_hint: 'The same condition is listed as both wanted and excluded. Matching treats an excluded condition as a hard fail, so this is deleting listings the client asked for — check with them and fix it in Edit.',
    desk_crit_complexes_excluded: 'Complexes (excluded)',
    desk_crit_complexes: 'Must be in complex',
    desk_crit_near_points: 'Must be near',
    desk_crit_near_walk: 'on foot',
    desk_crit_near_bus: 'by bus',
    desk_crit_floor: 'Floor',
    desk_crit_floor_not_first: 'Not the first floor',
    desk_crit_floor_not_last: 'Not the top floor',
    desk_crit_land_purpose: 'Land purpose',
    desk_crit_land_purpose_agricultural: 'agricultural',
    desk_crit_land_purpose_non_agricultural: 'non-agricultural',
    desk_crit_with_structure_ok: 'Existing structure is fine',
    desk_crit_min_lease: 'Min lease (months)',
    desk_crit_lease_window: 'Lease window (month 1–12)',
    desk_crit_registered_lease: 'Needs a registered lease',
    desk_crit_payment_currency: 'Wants to pay in',
    desk_match_class_ready: 'READY',
    desk_match_class_negotiate: 'NEGOTIATE',
    desk_match_class_verify: 'VERIFY',
    desk_match_class_discuss: 'ASK CLIENT',
    desk_match_class_ready_hint: 'Everything checks out — present it to the client',
    desk_match_class_negotiate_hint: 'Fits except a known gap — worth negotiating with the owner',
    desk_match_class_verify_hint: 'A key fact is unknown — confirm the ? items on this row first',
    desk_match_class_discuss_hint: 'Fits the known facts — ask the client about the flagged gap',
    desk_match_represented_by: 'represented by {name}',
    desk_match_your_listing: '✎ your listing',
    desk_match_also_on: 'also on {list}',
    desk_match_coop_pct: 'cooperation: {pct}%',
    desk_match_coop_min: 'min {v} {cur}',
    desk_match_coop_exclusive: 'exclusive',
    // The viewer's OWN cooperation on a suggested property — owner contact + their own terms
    desk_match_my_coop: 'You cooperate with the owner',
    desk_match_my_coop_owner: 'owner',
    desk_match_my_coop_hint: 'You are an active participant in a cooperation on this property — the owner is yours to call directly.',
    desk_match_my_coop_no_terms: 'no terms recorded yet',
    desk_match_market_listing: '🏷 owner\'s own ad — no agent in between',
    desk_match_owner_ad_also_listed: 'owner\'s own ad — also listed by our agent {name}',
    desk_lineage_owner_section: '🏠 owner ad — {name}',
    desk_lineage_owner_section_anon: '🏠 owner ad',
    // An outside agency's ad — named as such, never as the owner's (owner 2026-09-07).
    desk_lineage_agency_section: '🏢 {agency} — {name}',
    // Image-set switcher (2026-08-17). ⚠️ "clean" names a DECLARED absence of a portal logo, not
    // a screened photo: our mark signal cannot see a phone number the owner burned in. That
    // caveat used to render on the card and was REMOVED at the owner's request the same day —
    // it now lives only in design/IMAGE_SET_SWITCHER_ANSWERS_2026-08-17.md. The label is still
    // narrower than it sounds; nothing on screen says so.
    desk_gallery_zoom_in: 'zoom in',
    desk_gallery_zoom_out: 'zoom out',
    desk_imgset_more_sets: 'more sets',
    desk_imgset_section: 'image sets',
    desk_imgset_total: '{sets} sets · {photos} photos',
    desk_imgset_total_sets_only: '{sets} sets',
    desk_imgset_tab_title: '{n} photos · {mark} · {platform}',
    desk_imgset_mark_clean: 'clean',
    desk_imgset_mark_logo: 'logo',
    desk_imgset_mark_unknown: 'mark unknown',
    desk_imgset_rendition_clean: 'clean',
    desk_imgset_rendition_logo: 'watermarked',
    desk_imgset_rendition_thumb: 'thumbnail',
    desk_imgset_rendition_upload: 'uploaded',
    desk_imgset_rendition_picked: 'picked',
    desk_imgset_label_upload: 'uploaded by us',
    desk_imgset_label_whatsapp_owner: 'WA',
    desk_imgset_label_my_version: 'my version',
    desk_imgset_label_colleague_version: "a colleague's version",
    desk_imgset_only_one: 'Only this set is stored for this flat. There is nothing to switch to.',
    desk_entry_reposted_no_date: 'reposted',
    // desk_shop_terms_none is deliberately its OWN key — desk_terms_none_recorded already
    // exists as the RentalTermsModal subtitle ("none recorded"), and a duplicate literal
    // key silently loses to whichever definition comes last (caught live 2026-07-14).
    desk_shop_terms_none: 'No conditions recorded yet — ask the representing agent',
    // Earned knowledge (2026-07-28): the FACT that an agent asked the owner is shared;
    // the answers are unlocked by recording your own — same blind-first contract as the
    // cooperation "join to see terms" chip.
    desk_terms_hidden_count: '{n} recorded · record yours to see',
    desk_terms_hidden_some: 'recorded · record yours to see',
    // …and the same lock seen by a NON-REPRESENTATIVE (2026-08-06). The unlock above is an
    // instruction they cannot follow: canRecord=false strips every editor door in this panel,
    // so "record yours to see" named an action with no control behind it. Reported live —
    // irakli on NINO's flat, where the panel told him to add his own and gave him nowhere to
    // add it. The honest statement is the one desk_shop_terms_none already makes two lines
    // up: the answers exist, they are the representative's, deal through them.
    // ⚠️ Kept SHORT on purpose — this is a §1 pill (whitespace-nowrap), not a sentence.
    // The first draft said "ask the representing agent", matching desk_shop_terms_none
    // above; rendered, its Georgian overflowed the panel by 47px at 420px and clipped at
    // 280px, where the wording it replaces had fitted. desk_shop_terms_none can afford the
    // longer phrase because it is a full-width paragraph line, not a capsule.
    desk_terms_hidden_count_norep: '{n} recorded · ask the representative',
    desk_terms_hidden_some_norep: 'recorded · ask the representative',
    desk_terms_recorded_by_agent: 'an agent',
    desk_shop_deal_section: 'THE DEAL — terms from the representing agent',
    desk_shop_deal_none: 'No cooperation terms recorded yet — call the representative to agree a split',
    desk_shop_market_section: 'MARKET PRESENCE',
    desk_shop_still_available: 'On the market',
    desk_shop_off_market: 'Off the market',
    desk_shop_last_confirmed: 'last confirmed {time}',
    desk_shop_cooperate_button: 'Cooperate',
    desk_lineage_rep_banner: 'This property is represented by {name} — cooperate through them to split the deal',
    desk_lineage_captured_by_hint: 'Which of our agents saved this posting (older ones predate this stamp and show nothing)',
    match_key_policy_wrap: 'owner rule on {subject} unknown',
    desk_match_coverage: '{pct}% verified',
    desk_match_coverage_title: 'Share of applicable facts actually observed — a high score on low coverage is a guess, not a match',
    match_key_intent: 'deal type',
    match_key_category: 'property type',
    match_key_price: 'price',
    match_key_location: 'location',
    match_key_complex: 'complex',
    match_key_floor: 'floor',
    match_key_condition: 'condition',
    match_key_pets: 'pets',
    match_key_children: 'children',
    match_key_occupants: 'occupants',
    match_key_deposit: 'deposit',
    // Exposed 2026-08-08 by the quiet tier, which is the first surface to render this check
    // as a row: humanMatchKey falls through to key.replace(/_/g,' '), so it printed the raw
    // English `prepayment` inside an all-Georgian band. `match_key_prepayment_above_max`
    // existed; the bare subject did not.
    match_key_prepayment: 'prepayment',
    match_key_pay_currency: 'payment currency',
    match_key_financing: 'financing',
    // ── Reviewed vocabulary, 2026-08-08 ────────────────────────────────────────────
    // 18 labels that until today rendered through humanMatchKey's raw-token fallback
    // (`key.replace(/_/g,' ')`), i.e. as English inside an all-Georgian panel —
    // "bedrooms off by" was visible on live screens. Extracted from match-score.mjs's
    // reachable check keys and delta kinds, so this set is complete by construction rather
    // than by inspection; see design/VOCABULARY_GAPS_FOR_TRANSLATION_2026-08-08.md.
    match_key_area: 'area',
    match_key_land_area: 'land area',
    match_key_near: 'distance',
    match_key_parking_deeded: 'deeded parking',
    match_key_power_capacity: 'power capacity',
    // Quantified near-misses. ⚠️ These render with a FIGURE APPENDED (deltaLabel), so they
    // must be phrases a number can follow and must not end in punctuation.
    match_key_rooms_off_by: 'rooms off by',
    match_key_bedrooms_off_by: 'bedrooms off by',
    match_key_area_outside_range: 'area off by',
    match_key_land_area_outside_range: 'land area off by',
    match_key_power_capacity_outside_range: 'capacity short by',
    match_key_near_over_radius: 'over radius by',
    // Verification, not failure: each says "we believe this, on weaker evidence than usual —
    // confirm it", and the wording invites the call rather than reading as a rejection.
    match_key_category_verify: 'verify property type',
    match_key_complex_mismatch: 'different complex',
    match_key_complex_unverified: 'verify complex',
    match_key_policy_conflict: 'rules conflict — verify',
    // ⚠️ policy_inherited and rule_inherited are DELIBERATELY THE SAME STRING (reviewer's
    // ruling, 2026-08-08): they differ only in which mechanism produced them — the older
    // flat stance or the newer structured rule — and to an agent both mean "we are guessing
    // from a broader rule, call and check". Two labels would be a distinction the reader has
    // to learn and can never act on differently. Do not "fix" the duplication.
    match_key_policy_inherited: 'rule inferred — verify',
    match_key_rule_inherited: 'rule inferred — verify',
    match_key_rule_exception: 'under exception — verify',
    // Column-header forms. The matrix gives a header ~8 characters and abbrev() otherwise
    // cuts mid-word; a reviewed short form beats a mechanical truncation. Only the check
    // keys need these — delta kinds never head a column. Absent → abbrev() still applies.
    match_key_land_area_col: 'land',
    match_key_parking_deeded_col: 'deeded pk',
    match_key_power_capacity_col: 'capacity',
    match_key_appraisal: 'appraisal',
    match_key_occupancy: 'occupancy',
    match_key_handover: 'handover date',
    match_key_move_in: 'move-in date',
    match_key_move_in_gap: 'move-in gap',
    match_move_in_owner_holds: 'ask owner to hold {n}d',
    match_move_in_tenant_waits: 'ask client to wait {n}d',
    match_key_availability_gap: 'ask owner — when is it free?',
    match_key_availability_conflict: 'move-in date disputed — verify',
    match_key_ask_owner_to_hold: 'ask the owner to hold it',
    match_key_ask_tenant_to_wait: 'ask the client to wait',
    match_key_rooms: 'rooms',
    match_key_bedrooms: 'bedrooms',
    match_key_area_sqm: 'area',
    match_key_price_above_budget: 'over budget by',
    match_key_price_below_min: 'below minimum by',
    match_key_outside_search_area: 'outside search area',
    match_key_district_mismatch: "district doesn't match",
    match_key_district_adjacent: 'bordering wanted district — ask client',
    match_key_verify_location: 'label vs map pin disagree — verify location',
    match_key_attribute_gap: 'attribute gap',
    match_key_policy_gap: 'ask owner — policy unknown',
    match_key_household_gap: 'household restriction gap',
    match_key_terms_gap: 'ask owner — terms unknown',
    match_key_deposit_above_max: 'deposit above max by',
    match_key_prepayment_above_max: 'more months up front than client can put down',
    match_key_currency_mismatch: 'currency differs (negotiable)',
    match_key_appraisal_below_price: 'appraised below price by',
    match_key_sitting_tenant: 'sitting tenant',
    match_key_handover_after_move_in: 'handover after move-in date',
    desk_terms_facet_title_short: 'Rental terms',
    desk_sale_terms_title_short: 'Sale terms',
    desk_girao_terms_title_short: 'Girao terms',
    desk_terms_facet_title: 'RENTAL RESTRICTIONS — owner\'s stated conditions (agent-visibility only)',
    // B237 — the third state. Deliberately worded as an ANSWER ("no restrictions"), never as
    // an absence ("none recorded"), because the whole point is that it is not silence.
    desk_terms_no_restrictions: 'I asked the owner — there are no restrictions',
    desk_terms_cleared: 'no restrictions',
    desk_terms_cleared_title: 'An agent asked the owner: there are no restrictions beyond any recorded here',
    desk_terms_cleared_hidden: 'answered',
    desk_terms_cleared_hidden_title: 'Someone recorded the owner\'s restrictions answer — record yours on this property to see it',
    desk_terms_facet_hint: 'Recorded exactly as the owner stated it, with its source — internal context only, never a public filter or automated matching rule.',
    desk_terms_pets: 'Pets',
    desk_terms_smoking: 'Smoking',
    desk_terms_children: 'Children',
    desk_terms_furnished: 'Furnished',
    desk_terms_registered_lease: 'Registered lease (NAPR)',
    desk_terms_deposit_months: 'Deposit (months)',
    desk_terms_deposit_amount: 'Deposit (fixed sum, held to end of tenancy)',
    // Move-in advance (core 0213) — mirrors the gallery deal panel's two ticks
    desk_terms_prepay_first: 'First month up front',
    desk_terms_prepay_first_short: 'First month',
    desk_terms_prepay_last_short: 'Last month',
    desk_movein_chip_prompt: 'Paid up front?',
    desk_terms_prepay_first_yes: 'required',
    desk_terms_prepay_first_no: 'not required',
    desk_terms_prepay_last: 'Last month up front',
    desk_terms_prepay_last_yes: 'required',
    desk_terms_prepay_last_no: 'not required',
    desk_terms_is_final_condition: 'Flat is in final condition — nothing outstanding',
    desk_terms_min_lease: 'Min lease (months)',
    desk_terms_min_lease_under6: 'under 6 — short term',
    desk_terms_max_occupants: 'Max occupants',
    desk_terms_extra_pet_deposit: 'Extra pet deposit',
    // 0416 — the pol_pet_deposit chip's successor: does a pet cost extra HERE.
    desk_terms_pet_deposit_required: 'Does a pet cost extra?',
    desk_terms_pet_deposit_required_short: 'pet costs extra',
    // The THIRD state needs its own sentence. A ✓ beside "pet costs extra" says the opposite
    // of what it means: the tick is the answer NO, and the words have to agree with it.
    desk_terms_pet_deposit_required_no: 'pet costs nothing extra',
    // Shown beside an EMPTY pet-fee box on a listing already recorded as charging one — 21 of
    // the 23 such rows have no amount, because the flag and the field were captured years apart.
    desk_terms_pet_fee_unknown: 'costs extra — amount not recorded',
    desk_terms_none_recorded: 'none recorded',
    desk_terms_version_badge: 'v{n}',
    desk_tristate_not_stated: 'not stated',
    desk_tristate_yes: 'yes',
    desk_tristate_no: 'no',
    desk_terms_promised_improvements: 'Conditions to fulfil before move-in — record promises, make them contract contingencies',
    desk_terms_tenant_prefs: 'Owner\'s tenant preferences — owner\'s words, agent-only (e.g. nationality, students)',
    desk_terms_record_button: '✓ Record terms',
    desk_terms_recorded: '✓ Recorded',
    desk_terms_record_property_first: 'record the property first',
    desk_timeline_property_registered: '🏠 Property registered',
    desk_timeline_details_updated: '✏️ Property details updated',
    desk_timeline_context_signal: '📎 Context signal{kind}',
    desk_timeline_observed: '🔎 Observed {attribute}{source}',
    desk_timeline_feature: '✳️ Feature: {key}{" (absent)" if false}',
    desk_timeline_listing_observed: '📰 Listing observed{src}{price}',
    desk_timeline_listing_kind: '🔁 Listing {kind}: {value}',
    desk_timeline_listing_changed: 'Δ Listing changed: {field} {from} → {to}',
    desk_timeline_change_pending: ' · not settled yet',
    desk_optout_facet_title: 'Being contacted about this property',
    desk_pools_title: 'Automatic reposting: what to share',
    desk_pools_unavailable: 'Reposting choices are not available for this account.',
    desk_pools_which: 'Pools',
    desk_pool_outside: "Outside agents' flats (the owner's advert)",
    desk_pool_coop_ours: "Our agents' cooperations",
    desk_pool_coop_mine: 'My own cooperations',
    desk_pool_confirmed: 'Owner confirmed still available',
    desk_pool_office: "Office colleagues' adverts (7 days)",
    desk_pool_front_page: 'Front-page best deal',
    desk_pool_coop: 'Cooperation with our agent',
    desk_reshare_badge: 'Pool',
    desk_reshare_reposts: '{n}× by {a}',
    desk_reshare_queued: 'Queued now',
    desk_push_chase: 'another agency posted it after us',
    desk_push_front_page: 'front page',
    desk_reshare_hint_pools: 'In the reshare pool: {list}',
    desk_reshare_hint_reposts: 'Our reposts in 30 days: {n} by {a} accounts, last {at}',
    desk_reshare_hint_queued: 'Queued to repost now: {list}',
    desk_pools_count_hint: 'left to post / matching your filters / in the pool',
    desk_pools_portals: 'Post on',
    desk_pools_kinds: 'Property type (none ticked = all)',
    desk_pools_intents: 'Deal',
    desk_pool_intent_sale: 'For sale',
    desk_pool_intent_rent: 'For rent',
    desk_pools_need_intent: 'Pick sale, rent or both',
    desk_pool_kind_apartment: 'Flat',
    desk_pool_kind_house: 'House',
    desk_pool_kind_commercial: 'Commercial',
    desk_pool_kind_land: 'Land',
    desk_pools_rent: 'Rent price',
    desk_pools_sale: 'Sale price',
    desk_pools_confirmed: 'Availability confirmed within',
    desk_pools_days: 'days',
    desk_pools_confirmed_hint: 'Confirmed = someone opened a cooperation on it, or the owner told us on WhatsApp it is still for rent / sale. Empty = no limit.',
    desk_pools_save: 'Save',
    desk_pools_saved: 'Saved',
    desk_pools_need_portal: 'Pick at least one portal',
    desk_adrm_title: 'Our reposts of this flat',
    desk_adrm_request: 'Request deletion',
    desk_adrm_requested: 'deletion requested',
    desk_adrm_none: 'None of our agents has a live advert of this flat.',
    desk_adrm_remove_sel: 'Remove selected',
    desk_adrm_remove_all: 'Remove all',
    desk_adrm_confirm: 'Delete {n} advert(s) from the portals? The agents’ PCs do it from their own accounts.',
    desk_adrm_why_title: 'Why is it removed?',
    desk_adrm_why_sold_rented: 'Rented or sold',
    desk_adrm_why_agent_stopped: 'I don’t want to work on this anymore',
    desk_adrm_why_owner_asked: 'The owner asked to delete it',
    desk_adrm_why_owner_exclusive_other: 'The owner asked: it is exclusive with another agent',
    desk_adrm_why_owner_no_reshare: 'The owner asked: no reshares allowed',
    desk_adrm_why_other: 'Other',
    desk_adrm_why_note: 'Note (optional)',
    desk_adrm_why_note_required: 'Write the reason',
    desk_adrm_why_cancel: 'Cancel',
    desk_adrm_why_remove: 'Remove',
    desk_adrm_state_scheduled: 'waiting for the agent’s PC',
    desk_adrm_state_queued: 'removing…',
    desk_adrm_state_published: 'removed',
    desk_adrm_state_error: 'failed',
    desk_adrm_note: 'Each advert is deleted by the PC of the agent who posted it, from their own portal account.',
    desk_share_fb_contact_none: 'no number assigned',
    desk_share_fb_contact_assigned: 'assigned',
    desk_share_fb_contact_empty: 'Nobody on this property has a number to publish.',
    desk_optout_manage_title: 'Others on this property',
    desk_admin_comments_title: 'Admin notes',
    desk_mark_section_title: 'Road and places on the map',
    desk_mark_draw_road: 'Draw the road',
    desk_mark_drop_place: 'Mark a place',
    desk_mark_hint_pan: 'Pick a tool, then click on the map.',
    desk_mark_hint_road: 'Click along the road — at least two points.',
    desk_mark_points_progress: '{n} point{s} on the road — keep clicking or save',
    desk_mark_hint_place: 'Click where the place is.',
    desk_mark_hint_place_set: 'Point set — name it and save.',
    desk_mark_label_ph: 'Name (optional): main road, gate, well…',
    desk_mark_save: 'Save mark',
    desk_mark_road: 'road',
    desk_mark_place: 'place',
    desk_mark_retract: 'remove',
    desk_mark_road_distance: 'Distance to the marked road',
    desk_mark_no_pin: '(the plot has no pin yet, so no distance)',
    desk_facts_fill_gaps: 'Fill my gaps from this card',
    desk_facts_fill_gaps_hint: 'Copies this ad’s values into the fields you have not written yourself. Your own values stay.',
    desk_facts_filled_n: '{n} field(s) filled from this card — they become yours when you record.',
    desk_facts_yours: 'yours',
    desk_admin_comments_hint: 'Visible to admins only. Read by Claude when it works this property; resolved with what was done.',
    desk_admin_comment_ph: 'What needs doing here…',
    desk_admin_comment_add: 'Add note',
    desk_admin_comment_mark_resolved: 'Mark resolved',
    desk_admin_comment_resolve: 'Resolve',
    desk_admin_comment_resolution_ph: 'What was done, or why nothing was',
    desk_admin_comments_resolved_n: '{n} resolved',
    desk_lineage_view_my_version_tooltip: 'Open my version of this property',
    desk_my_version_not_published: 'not published',
    desk_my_version_overlay_note: 'The ad, with your recorded facts standing in. Fields you have not recorded keep the ad’s value.',
    desk_optout_action_leave_other: 'remove from contacts',
    desk_optout_action_rejoin_other: 'list again',
    desk_optout_state_in: 'You can be listed as a contact for this property.',
    desk_optout_state_out: 'You are not listed as a contact for this property.',
    desk_optout_action_leave: 'Do not list me as a contact',
    desk_optout_action_rejoin: 'List me as a contact again',
    desk_optout_scope_note: 'This only affects where your number is published. Your access, meetings and commission are unchanged.',
    desk_optout_others: '{n} other colleague(s) have opted out here.',
    desk_timeline_change_pending_title: 'Recorded. It becomes a history entry once it stays unchanged for 15 minutes.',
    desk_timeline_reshare_declared: '🔗 Re-post declared{src}',
    desk_timeline_reshare_registered: '🔗 Re-post recorded{src}',
    desk_timeline_terms_amended: '📋 Rental terms amended: {ops}',
    desk_timeline_terms_ungoverned: '⚠️ Terms changed outside the gateway (ungoverned write)',
    desk_timeline_agency_agreement: '🤝 Agency agreement {verb} — {status}',
    desk_timeline_contact_declared: '👤 Contact declared{role}{name}',
    desk_timeline_contact_dismissed: '👤 Contact dismissed',
    // ── Property history band (episode model). The collapsed line is the whole
    // feature for most viewers, so it carries the five facts an admin asks for:
    // is it live, how long, how hard is the owner pushing, how many of ours are
    // on it, and do our prices disagree.
    desk_history_title: 'History',
    desk_history_status_active: 'Active',
    desk_history_status_off: 'Off-market',
    desk_history_days: '{n}d',
    desk_history_owner_posts: '{n} owner posts',
    desk_history_owner_post_one: '1 owner post',
    desk_history_agents: '{n} agents on it',
    desk_history_agent_one: '1 agent on it',
    desk_history_prices: 'prices {min}–{max}',
    desk_history_price_one: 'price {min}',
    desk_history_owner_price: "owner's price {price}",
    desk_history_agreements: '{n} agreements',
    desk_history_agreement_one: '1 agreement',
    desk_history_floor: 'floor {price}',
    desk_history_floor_hidden: 'figures not shared with you',
    desk_history_last_seen: 'last seen {when}',
    desk_history_platforms: '{n} portals',
    desk_history_unknown_posters: '{n} external posters',
    desk_history_empty: 'Nothing recorded yet.',
    desk_history_drawer: '{n} machine events',
    desk_history_older: 'Load older',
    desk_history_loading: 'Loading…',
    desk_history_partial: 'may continue in older events',
    // Episode headlines. Actor names are substituted, never invented — an owner
    // whose name we are not allowed to show renders as the role alone.
    desk_ep_owner_post: 'Owner posted a new ad',
    desk_ep_reshare: '{who} copied it to {n} portals',
    desk_ep_reshare_one: '{who} copied it to {portal}',
    desk_ep_withdrawn: 'Ad went off-market',
    desk_ep_price_up: 'Price rose {from} → {to}',
    desk_ep_price_down: 'Price dropped {from} → {to}',
    desk_ep_correction: 'System corrected a currency error',
    desk_ep_merge: 'System linked another record to this property',
    desk_ep_agreement: 'Agency agreement — {what}',
    desk_ep_bookkeeping: 'Bookkeeping only — no terms changed',
    desk_ep_observed: 'Observed',
    desk_ep_sold: 'Marked sold',
    desk_ep_owner: 'Owner',
    desk_ep_external: 'External poster',
    desk_ep_via: 'via {who}',
    desk_ep_inferred_off: 'Inferred: the ad was no longer reachable when we re-checked. We know it is gone, not exactly when.',
    desk_timeline_facet_title: 'TIMELINE — everything this property has been observed to do',
    desk_timeline_facet_hint: 'The event log itself — facts as a sequence of revisions, nothing overwritten. Facets above are views over this.',
    desk_timeline_events_count: '{n}{+} events',
    desk_timeline_no_events: 'No events yet.',
    desk_timeline_burst_summary: '{evTime} — 🔎 {n} facts observed{sourceKind}',
    desk_timeline_loading: '⏳ Loading…',
    desk_timeline_load_older: '↓ Load older',
    desk_notes_facet_title: '🔒 PRIVATE NOTES — only you (and admins) see these',
    desk_notes_facet_hint: 'Notes never publish and never appear on the shared timeline.',
    desk_notes_count: '{n} note{s}',
    desk_notes_delete: 'Delete note',
    desk_notes_add_ph: 'Add a private note…',
    desk_notes_add_button: 'Add',
    desk_promises_facet_title: 'PROMISES — not there yet',
    desk_promises_facet_hint: 'What the seller or developer promises (the balcony will be tiled, a pool is planned) and whether the current price includes it. Not recorded as a present feature.',
    desk_promises_summary: '{n} · {incl} in price · {extra} extra',
    desk_promise_scope_flat: 'flat',
    desk_promise_scope_building: 'building',
    desk_promise_scope_complex: 'complex',
    desk_promise_scope_area: 'area',
    desk_promise_state_promised: 'promised',
    desk_promise_state_in_progress: 'in progress',
    desk_promise_state_done: 'done',
    desk_promise_state_not_kept: 'not kept',
    desk_promise_price_included: 'in the price',
    desk_promise_price_extra: 'not in the price',
    desk_promise_price_unstated: 'price not stated',
    desk_promise_extra_amount: 'amount',
    desk_promise_what_ph: 'What is promised…',
    desk_promise_due_ph: 'When (e.g. December)',
    desk_promise_add: 'Add promise',
    desk_promise_remove: 'Remove this promise',
    desk_assumed_facet_title: 'ASSUMED VALUES — check them',
    desk_assumed_facet_hint: 'Numbers or currencies the advert got wrong that we read as what was most likely meant. Confirm, or reject to go back to what the source said.',
    desk_assumed_summary: '{n} to check',
    desk_assumed_field_price: 'Price',
    desk_assumed_field_deposit: 'Deposit',
    desk_assumed_most_likely: 'most likely meant',
    desk_assumed_source_said: '· the source said',
    desk_assumed_confirm: 'Confirm',
    desk_assumed_reject: 'Reject — keep the source value',
    desk_assumed_status_confirmed: 'confirmed',
    desk_assumed_status_rejected: 'rejected — source value restored',
    desk_pool_facet_title: 'POOL — offer this property to other agents',
    desk_pool_facet_hint: 'Set your asking price + total commission, then publish. Commission is auto-filled from your agency agreement when you have one, but you can change it. It\'s split 50/50 with the cooperating agent who brings a buyer. Only facts you\'ve accepted publish; docs and private notes never do.',
    desk_pool_published_private: '✅ published / private',
    desk_pool_asking_price: 'Asking price',
    desk_pool_for_label: 'For',
    desk_pool_for_sale: 'Sale',
    desk_pool_for_rent: 'Rent',
    desk_pool_total_commission: 'Total commission %',
    desk_pool_coop_side: 'Cooperating agent\'s side (50/50): ~{amount} {currency}',
    desk_pool_update_offer: '↻ Update offer',
    desk_pool_publish: '📢 Publish to pool',
    desk_pool_withdraw: '⏸ Withdraw',
    desk_pool_disclaimer: 'Only facts you\'ve recorded on this property publish; blank fields show as unknown. Documents and private notes never publish.',
    desk_pool_validation: 'Enter a price and a commission % (0–100)',
    desk_contact_reveal: '📞 Show contact',
    desk_contact_no_phone: 'no phone on record',
    desk_access_request_link: '⚠️ Representative not responding? Request access',
    desk_access_context_ph: 'Which client, who you tried to reach, when…',
    desk_access_file: 'Request',
    // Reworded 2026-07-24 (C3 reversal, MEETINGS_MODEL §4.2): the REPRESENTATIVE
    // decides on both sides now, not the admin. The old string promised an admin review.
    desk_access_pending: 'Request sent — waiting on the representative',
    desk_access_granted: 'Access granted',
    desk_access_rejected: 'Access request rejected',
    desk_access_revoked: 'Access was revoked',
    desk_access_ask_again: 'Ask again',
    desk_access_notice: '{name} has a client for your property and reports you unreachable — please respond',
    // 0158: requirement-scoped requests — the rep decides inline in the banner
    desk_access_notice_req: '{name} asks for access to your client {client} — this request only',
    desk_access_grant_btn: 'Allow',
    desk_access_deny_btn: 'Deny',
    // Inbound requests in the bell (MEETINGS_MODEL §4.4). The copy explains WHY —
    // "people approve requests much more readily when the business context is
    // obvious". ⚠️ It must never name a street address: the requester may not be
    // entitled to see it, and this row renders for the OTHER agent (§4.5).
    desk_notif_access_ask_client: '{name} needs your client’s contact to coordinate a meeting',
    desk_notif_access_ask_owner: '{name} needs the owner’s contact to coordinate a meeting',
    desk_notif_access_meeting_at: 'Meeting {when}',
    desk_notif_access_meeting_untimed: 'Meeting — time not set yet',
    // ⚠️ These five were REFERENCED but never DEFINED (found 2026-07-24 by an
    // i18n sweep). t() returns the key itself when a string is missing, so the
    // "your access request was decided" row literally rendered
    // "desk_notif_access_granted" — the same class of bug as
    // desk_coop_end_deal_closed. The reader is the REQUESTER, so the wording is
    // second-person: this is the answer to something they asked for.
    desk_notif_access_granted: 'Your access request was granted',
    desk_notif_access_revoked: 'Your access was revoked',
    desk_notif_access_denied: 'Your access request was denied',
    // Subject fallbacks — a requirement with no title, or a property with no
    // district. Deliberately vague: naming the client here would undo the
    // masking the request exists to negotiate.
    desk_notif_access_req_generic: 'a client',
    desk_notif_access_prop_generic: 'a property',
    // Worded as INTENT, never enforcement — the system cannot make anyone forget
    // a number. Do not relabel these "temporary"/"permanent" (0195 header).
    desk_access_grant_meeting_btn: 'Grant for this meeting',
    desk_access_grant_ongoing_btn: 'Grant ongoing access',
    // ── Meeting creation (core/MEETINGS_MODEL.md §3) ──────────────────────────
    meeting_new_title: 'New meeting',
    meeting_slot_property: 'Property',
    meeting_slot_client: 'Client',
    meeting_client_via_rep: 'not shared — represented by {name}',
    meeting_slot_property_rep: 'Property representative',
    meeting_slot_client_rep: 'Client representative',
    meeting_slot_empty: 'Not set',
    meeting_slot_property_empty: 'Open from a property to attach one',
    meeting_slot_property_unresolved: 'Not identified yet — searched for "{q}"',
    meeting_pick_rep: 'Choose the representative',
    meeting_attending_yes: 'going',
    meeting_attending_no: 'not going',
    meeting_rep_none_registered: 'Nobody is registered as representing this property.',
    meeting_client_rep_handover_hint: 'Or hand the client to a colleague:',
    meeting_client_rep_handover_ask: 'Hand {client} over to {name}?',
    // The same question when we cannot name the client. Two strings rather than a fallback
    // WORD, because the fallback was the word "client" and the sentence already says it —
    // "Hand client over to Nini?" read as a typo (found by rendering, 2026-08-06).
    meeting_client_rep_handover_ask_noname: 'Hand this client over to {name}?',
    meeting_client_rep_handover_cost: '{name} stops representing this client and loses the access that came with it. Reversible by handing back.',
    meeting_client_rep_handover_do: 'Hand over',
    meeting_client_rep_outside_open: 'The client has their own agent →',
    meeting_client_rep_outside_back: '← Our agents',
    meeting_client_rep_outside_hint: 'An agent from another agency. They join the meeting but not our commission split.',
    meeting_notes_unresolved: 'Property not identified yet. Agent searched for: {q}',
    meeting_ai_prefilled: 'Filled from the pasted message — check before creating',
    meeting_upcoming_same: 'This client already has a viewing here: {when}',
    meeting_listing_unavailable: 'This property is no longer on the market ({status}) — create anyway only if you mean to',
    meeting_slot_client_empty: 'Open from a client to attach one',
    meeting_slot_rep_unknown: 'No representative on record',
    meeting_slot_rep_auto: 'Resolved automatically when the meeting is created',
    // owner 2026-09-23 — every party named by a human; suggestions, never pre-selections.
    meeting_rep_mark_suggested: 'suggested',
    meeting_rep_mark_last: 'last time',
    meeting_rep_mark_me: 'me',
    meeting_rep_pick_prompt: 'Tap to choose',
    meeting_rep_none_pickable: 'Nobody can be chosen here — ask a manager',
    meeting_rep_pick_other: 'Another agent or agency…',
    meeting_pick_client_prompt: 'Choose the client, or add a new one',
    meeting_missing_purpose: 'choose the purpose',
    meeting_missing_property: 'choose the property',
    meeting_missing_client: 'choose the client',
    meeting_missing_property_rep: "choose the owner's representative",
    meeting_missing_client_rep: "choose the client's representative",
    meeting_missing_coop: 'record the cooperation with the owner',
    meeting_err_client_required: 'A viewing needs a client — choose or add one.',
    meeting_err_property_required: 'A viewing needs the property — choose it.',
    meeting_err_representatives_required: 'Choose both representatives before creating.',
    meeting_coop_step_title: "Cooperation with the owner's side",
    meeting_coop_step_hint: "You represent the client and {rep} represents the property. Record the terms with the owner before the viewing.",
    meeting_coop_step_hint_norep: 'You represent the client and there is no cooperation on this property yet. Record the terms with the owner before the viewing.',
    meeting_coop_step_open: 'Enter the terms',
    meeting_coop_step_no_owner: "The owner's contact is not available to you. Add the owner's phone to open the cooperation.",
    meeting_coop_step_add_owner: 'Add owner',
    meeting_rep_inherited_hint: 'from the previous visit',
    meeting_slot_rep_several: 'Several representatives — recorded as unspecified',
    // ── co-broke (0449) ──────────────────────────────────────────────────────
    meeting_rep_pick_ours: 'From our agency',
    meeting_rep_pick_external: 'Another agency',
    meeting_rep_external_search_ph: 'Name, phone or agency…',
    meeting_rep_external_known: 'Worked with before',
    meeting_rep_external_directory: 'Found on the portals',
    meeting_rep_external_add: 'Add them by hand',
    meeting_rep_external_none: 'Nobody matched — add them by hand below.',
    meeting_rep_external_solo: 'Individual agent',
    meeting_edit_window_closed: 'The 15 minutes for correcting this have passed — ask a manager to change it.',
    meeting_split_from_property_rep: "The property's agent is from another agency — their cut comes off the fee first.",
    meeting_split_from_client_rep: "The client's agent is from another agency — their cut comes off the fee first.",
    meeting_roster_unavailable: 'The agent list did not load.',
    meeting_contacts_pill: 'Contacts',
    meeting_prop_quick_add: 'Add a new property',
    meeting_prop_quick_hint: 'Only the essentials — open the full card later to complete it.',
    meeting_prop_quick_address_ph: 'Address',
    meeting_prop_quick_price_ph: 'Price',
    meeting_prop_quick_area_ph: 'Area m²',
    meeting_prop_quick_rooms_ph: 'Rooms',
    meeting_prop_quick_contact_hint: 'Property contact — owner or their representative (optional)',
    meeting_prop_quick_save: 'Add property',
    meeting_prop_quick_failed: 'The property could not be saved.',
    meeting_rep_external_name_ph: 'Name',
    meeting_rep_external_phone_ph: 'Phone',
    meeting_rep_external_agency_ph: 'Agency (optional)',
    meeting_rep_external_save: 'Add',
    meeting_rep_external_is_ours: 'That phone belongs to one of our own agents — pick them under “From our agency”.',
    meeting_rep_external_badge: 'another agency',
    meeting_rep_declared_hint: 'Named by you — nobody outside this agency has confirmed it.',
    // 0662 — naming a property representative the resolvers did not return. "Working on
    // this property" names what the chips above ARE, so the link below them says what it
    // offers instead of a bare "other".
    meeting_property_rep_other_agent: 'Another agent',
    meeting_property_rep_outside_open: 'Agent from another agency →',
    meeting_property_rep_outside_hint: 'An outside representative takes the agreed co-broke share before our office divides its share.',
    meeting_property_rep_declare_hint: 'These agents do not work this property yet — picking one records that you named them.',
    meeting_rep_declared_badge: 'named',
    meeting_rep_external_ads: '{n} ads',
    meeting_split_label: 'Commission split',
    meeting_split_none: 'Not split — we keep the whole fee',
    meeting_split_standard_hint: 'Standard split with the other agency — {n}% / {n}%. Not recorded yet.',
    meeting_split_record_standard: 'Record this split',
    meeting_split_add: 'Split with the other agency',
    meeting_split_clear: 'Remove the split',
    meeting_split_us: 'Us',
    meeting_split_them: '{name}',
    meeting_split_of_fee: 'of {fee}',
    meeting_split_no_fee: 'The fee is not recorded yet — the ratio is saved and the amounts appear once it is.',
    meeting_split_total_bad: 'The two shares must add up to 100%.',
    meeting_split_needs_party: 'Choose the agent you are splitting with first.',
    meeting_split_default_note: 'Agency standard is {n}% — change it if you agreed otherwise.',
    meeting_split_many: 'This split has {n} parties and is edited by whoever recorded it.',
    meeting_pick_client: 'Pick a client…',
    meeting_pick_from_matches: 'Matching clients',
    meeting_pick_my_contacts: 'Contacts I added',
    meeting_pick_my_clients: 'My clients',
    meeting_pick_other_clients: "Other agents' clients",
    meeting_pick_search_ph: 'Search by name or phone…',
    meeting_pick_badge_match: 'matches',
    meeting_pick_badge_onprop: 'on this property',
    meeting_pick_none: 'No candidates found for this property',
    meeting_add_new_client: 'Add a new client',
    meeting_quick_add_hint: 'Details can be filled in later on the client card',
    meeting_new_client_picked: 'New client',
    meeting_comments: 'Comments',
    desk_meetings_none_at_all: 'No meetings recorded yet.',
    desk_meetings_load_failed: 'Meetings could not be loaded.',
    desk_meetings_all_filtered: 'All {n} meetings are hidden by this view — show all',
    desk_load_failed: 'Could not load — try again.',
    meeting_binder_settle_confirm: 'Settle the binder as “{label}”? This is final and cannot be undone.',
    meeting_split_clear_confirm: 'Remove the agreed co-broke split? The ratio is what decides our share of the fee.',
    meeting_history: 'History',
    meeting_history_none: 'Nothing recorded yet.',
    desk_hist_created: 'Meeting created',
    desk_hist_status: 'Status',
    desk_hist_moved: 'Moved',
    desk_hist_time_fixed: 'Time corrected',
    desk_hist_property_set: 'Property attached',
    desk_hist_property_changed: 'Property changed',
    desk_hist_client_set: 'Client attached',
    desk_hist_client_changed: 'Client changed',
    desk_hist_rep_changed: 'Representative changed',
    desk_hist_handover: 'Client handed over',
    desk_hist_attendance: 'Attendance updated',
    desk_hist_split: 'Co-broke split set',
    desk_hist_binder: 'Binder figures set',
    // ── The binder's two state machines (1100/1105) ────────────────────────────────────
    desk_binder_agreement_state: 'Agreement',
    desk_binder_money_state: 'Money',
    desk_binder_move_to: 'Move to',
    desk_binder_refund_reason: 'Why is the money going back?',
    desk_binder_why: 'Why?',
    desk_binder_confirm: 'Confirm',
    desk_binder_cancel: 'Back',
    desk_binder_moving: 'Saving…',
    desk_binder_move_failed: 'That move was refused.',
    desk_binder_counts_failure: 'Counted as a failed deal.',
    desk_binder_not_failure: 'NOT counted as a failed deal — the transaction completed.',
    binder_astate_draft: 'Draft',
    binder_astate_signed: 'Signed',
    binder_astate_funded: 'Funded',
    binder_astate_active: 'Reservation active',
    binder_astate_completed: 'Completed',
    binder_astate_payer_default: 'Payer defaulted',
    binder_astate_owner_default: 'Owner defaulted',
    binder_astate_neutral_failure: 'Failed — nobody at fault',
    binder_astate_mutually_cancelled: 'Cancelled by both sides',
    binder_mstate_none: 'Nothing received',
    binder_mstate_received: 'Received',
    binder_mstate_allocated: 'Allocated (temporarily)',
    binder_mstate_credited: 'Credited to the deal',
    binder_mstate_refunded: 'Returned',
    binder_mstate_retained: 'Retained',
    binder_refund_owner_default: 'The owner defaulted',
    binder_refund_neutral_failure: 'Failed through nobody\'s fault',
    binder_refund_mutual_cancellation: 'Both sides cancelled',
    binder_refund_agency_error: 'senTavus made the mistake',
    binder_refund_currency_settlement: 'Currency settlement — the deal completed',
    binder_refund_overpayment: 'More was received than was due',
    desk_hist_deleted: 'Meeting deleted',
    desk_hist_restored: 'Meeting restored',
    desk_hist_imported: 'imported from the old notes — author and time unknown',
    desk_hist_more: 'Show {n} earlier',
    meeting_hist_tab_all: 'All',
    meeting_hist_tab_changes: 'Changes',
    meeting_hist_tab_comments: 'Comments',
    meeting_hist_tab_visits: 'Visits',
    meeting_revisit: 'book again',
    meeting_prop_history: 'This property has {n} earlier meeting(s)',
    meeting_repeat_from: 'repeat',
    meeting_past_visits: 'Past visits',
    meeting_past_visits_none: 'No earlier visits on this client and property.',
    meeting_book_next: 'Book next viewing',
    meeting_comments_none: 'No comments yet.',
    meeting_comment_ph: 'Comment…',
    meeting_notes_ph: 'Notes (optional)…',
    meeting_will_request_access: 'Creating this meeting will ask the other representative for their client’s contact.',
    meeting_create_btn: 'Create meeting',
    meeting_creating: 'Creating…',
    meeting_cancel_btn: 'Cancel',
    meeting_created_ok: 'Meeting created.',
    meeting_access_filed_owner: 'Asked the property representative for the owner’s contact',
    meeting_access_filed_client: 'Asked the client representative for the client’s contact',
    meeting_access_filed_hint: 'The meeting is already saved — it does not wait for approval.',
    meeting_done_btn: 'Done',
    desk_meeting_contact_pending: 'Contact pending approval',
    meeting_detail_title: 'Meeting',
    meeting_detail_attendees: 'Attending',
    // Neither representative is fixed yet, so the only person we can honestly name is the
    // one filling the form. Says that rather than 'nobody', which would be false.
    meeting_attendees_creator_only: 'Just you, for now',
    // The ✕ on a REPRESENTATIVE. Deliberately not "remove": they stay on the meeting and in
    // the deal, and the only thing recorded is that they are not walking in.
    meeting_attendee_not_going: 'Not going to this one',
    meeting_attendee_not_going_note: 'Not going: {names}',
    meeting_detail_status: 'Status',
    meeting_actions_label: 'What do you want to record?',
    meeting_add_time: 'Set a date and time',
    meeting_book_next_hint: 'a new meeting',
    meeting_status_current: 'current',
    meeting_actions_menu: 'Record actions',
    meeting_delete_btn: 'Delete this meeting',
    meeting_delete_confirm: 'Delete permanently from every view?',
    meeting_delete_yes: 'Delete',

    // ── Negotiation timeline (core/MEETINGS_MODEL.md §6, §11) ─────────────────
    // ⚠️ The action labels are all VERBS THAT RECORD, never verbs that edit
    // ("Record an offer", not "Set the price"). Nothing in this thread is
    // editable — every button appends an event — and the wording is the first
    // place an agent learns that.
    nego_title: 'Negotiation',
    nego_panel_title: 'Negotiations',
    nego_tooltip: 'The price story with this client',
    nego_property_fallback: 'Property',
    nego_client_fallback: 'Client',
    nego_client_of: '{name}’s client',
    nego_latest_position: 'Latest position',
    nego_difference: 'Difference',
    nego_apart: '{amount} apart',
    nego_history: 'History',
    nego_no_events: 'Nothing recorded yet',
    nego_converging: 'Gap over time:',
    nego_accepted_at: 'Agreed at {amount}',
    nego_seed: 'Starting price {amount}',
    nego_floor: 'Owner’s minimum {amount}',
    nego_commission: '{pct}% commission',
    nego_below_floor: 'This is below the minimum agreed with the owner. Your call — but it either eats the commission or breaks the agreement.',
    // The two sides quoted different currencies, so there is no honest
    // difference to show. Said out loud rather than left blank — a missing
    // number reads as agreement.
    nego_mixed_currency: 'The two sides quoted different currencies, so the difference cannot be shown.',
    nego_mixed_currency_short: 'different currencies',
    nego_money_row: 'Money',
    nego_record_btn: 'Record',
    nego_saving: 'Saving…',
    nego_amount_ph: 'Amount',
    nego_why_ph: 'Why? (“Offered after viewing — kitchen needs work”)',
    nego_err_amount: 'An offer needs an amount.',
    nego_via_portal: 'from the client',
    nego_from_meeting: 'From meeting #{id}',
    nego_terminal_hint: 'This closes the negotiation. If they come back, that starts a new one.',
    nego_closed_banner: 'Closed — {reason}. If they come back, open a new negotiation.',
    // Whose position a number is — not who typed it. An agent writing down the
    // owner’s counter is recording the OWNER’s side.
    nego_side_client: 'Client',
    nego_side_owner: 'Owner',
    nego_side_agent: 'Agent',
    nego_accepts_client: 'The client accepted',
    nego_accepts_owner: 'The owner accepted',
    // ⚠️ You accept the OTHER side’s number (§6.7) — and the accepted amount IS
    // the deal price, so it is spelled out before the click, not after.
    nego_accept_locks: 'This agrees the deal at {amount}.',
    nego_accept_no_number: 'No price from the other side yet — record their offer first, or this agreement has no amount.',
    nego_kind_proposal: 'Offer',
    nego_kind_counterproposal: 'Counter-offer',
    nego_kind_acceptance: 'Agreed',
    nego_kind_rejection: 'Rejected',
    nego_kind_withdrawal: 'Withdrew',
    nego_kind_deposit: 'Deposit paid',
    nego_kind_contract: 'Contract signed',
    nego_kind_closing: 'Completed',
    nego_kind_fell_through: 'Fell through',
    nego_kind_note: 'Note',
    nego_stage_open: 'Just opened',
    nego_stage_negotiating: 'Negotiating',
    nego_stage_accepted: 'Agreed',
    nego_stage_deposit: 'Deposit paid',
    nego_stage_contract: 'Contract signed',
    nego_stage_closed: 'Completed',
    nego_stage_rejected: 'Rejected',
    nego_stage_withdrawn: 'Withdrawn',
    nego_stage_fell_through: 'Fell through',
    // Which rung of the price ladder the starting number came from. Shown
    // because the ladder degrades — a portal ad with no price, two reshares that
    // disagree — and “why does it say 550?” must have an answer (§6.4).
    nego_basis_share: 'what you sent this client',
    nego_basis_reshare: 'your own listing',
    nego_basis_cooperation: 'agreed with the owner',
    nego_basis_portal: 'the original ad',
    nego_basis_none: 'not known',
    // ── Client share link (core/MEETINGS_MODEL.md §5) ─────────────────────────
    desk_notif_share_meeting: '{name} asked to view a property you shared',
    desk_notif_share_opened: '{name} opened a property you shared',
    desk_notif_share_call: '{name} tapped Call on a property you shared',
    desk_notif_share_whatsapp: '{name} tapped WhatsApp on a property you shared',
    desk_notif_share_someone: 'A client',
    desk_notif_share_someone_nom: 'A client',
    desk_notif_share_engaged: '{name} read this property closely',
    desk_notif_share_engaged_gallery: '{name} read this property closely — and opened the photos',
    share_title: 'Send to a client',
    share_tooltip: 'Send this property to a client',
    // ShareMenu (2026-07-25): the FACE is kind-agnostic ("Share"), because the two kinds
    // are named on the rows inside. share_tooltip above stays client-specific — it still
    // labels the direct client-share button in the modal chip row.
    share_menu_tooltip: 'Share',
    share_kind_client: 'Link to client',
    share_kind_others: 'Share with a colleague',
    desk_property_share_note: 'They will work this property too — they see the contact, can record the owner’s terms, and join the split on meetings they attend.',
    desk_property_shared_n: 'Shared with {n}.',
    share_kind_agent: 'Link to agent',
    share_kind_location: 'Share location',
    desk_share_location_copied: 'Map link copied',
    share_kind_repost: 'Repost to portals',
    share_price_label: 'Price to show the client',
    share_price_ph: 'Leave empty for “price on request”',
    // The frozen-price rule, stated where the decision is actually made.
    share_price_frozen: 'This price is fixed on the link. Later price changes will not alter what the client sees.',
    // Rentals only. Seeded from the listing's recorded limit, then frozen on the link.
    share_max_occupants_label: 'Max occupants to show the client',
    // Sits beside the field, not in a placeholder — a placeholder vanishes as soon
    // as the seed fills the box, which is when the agent needs to read it.
    share_max_occupants_empty: 'empty = not stated',
    share_max_occupants_hint: 'Taken from the listing. Editing it here changes only this link, not the listing.',
    share_message_ph: 'A note for the client (optional)…',
    share_message_label: 'The client sees this',
    share_memo_label: 'Only you see this — who did you send it to?',
    share_memo_ph: 'e.g. Nino + husband, called about the ss ad…',
    share_memo_hint: 'Your own note. Never shown to the client.',
    share_create_btn: 'Create link',
    share_creating: 'Creating…',
    share_ready: 'Link ready — send it to your client.',
    share_copy: 'Copy',
    share_copied: 'Copied',
    share_another: 'Create another link',
    share_district_only: 'This link shows the district, not the street address — you are not the representing agent for this property.',
    share_recipient: 'For {name}',
    // B397 — the optional recipient. "(optional)" is IN the label on purpose: an
    // agent who reads this as a new required step is the failure the owner ruled
    // against, and it costs a link that would otherwise have been sent.
    share_for_label: 'Who is this for? (optional)',
    share_for_name_ph: 'Name',
    share_for_phone_ph: 'Phone',
    share_for_change: 'change',
    share_for_reqs: '{n} request(s)',
    share_for_or_new: 'Or leave the boxes as typed to add someone new.',
    share_sent_before: 'Already sent',
    // 🔗 card-face pill (2026-08-03): the links this agent minted for the property.
    desk_share_chip_title: 'Links sent to clients',
    share_open: 'Open',
    desk_share_chip_manage: 'Manage links',
    desk_share_chip_add: 'Create a client link',
    // 🔗 Pasting a sent link back into the search box (2026-08-04) — "the client
    // replied 'I like this one', which one was it?". The box resolves it to the
    // card instead of searching for the token as text.
    desk_share_lookup_none: 'No client link matches that — check the link, or it may belong to another agency.',
    desk_share_lookup_failed: 'Could not look up that link — try again.',
    // Parenthesised, so the label reads as a badge and needs no grammatical
    // agreement with the sentence — the same words are reused as chips elsewhere.
    desk_share_lookup_inactive: 'This link is no longer active ({state}) — opening the property anyway.',
    share_expired: 'Expired',
    share_representation_ended: 'Representation ended',
    share_price_on_request: 'Price on request',
    share_revoke: 'Revoke',
    share_revoked: 'Revoked',
    share_not_opened: 'Not opened yet',
    share_opens: 'opened {n}×',
    share_last_viewed: 'last viewed {when}',
    share_devices: '{n} devices',
    share_saw_photos: 'viewed photos',
    share_map: 'map {n}×',
    share_called: 'called {n}×',
    share_whatsapped: 'WhatsApp {n}×',
    share_requested_viewing: 'Requested a viewing',
    admin_access_pending: 'Pending access requests',
    admin_access_none: 'No pending requests',
    admin_access_reps: 'Representatives',
    admin_access_note_ph: 'Optional note…',
    admin_access_grant: 'Grant',
    admin_access_reject: 'Reject',
    // B196 (2026-07-29): referenced by AccessSection but previously undefined.
    admin_access_client_generic: 'Client',
    admin_access_revoke: 'Revoke',
    // B92/B105 (2026-07-29): discovery profiles editor + lead badge.
    admin_discovery_profiles_title: 'Discovery profiles',
    admin_discovery_profiles_desc: 'Portal search URLs the lead-discovery walker runs. After ANY URL edit, press Test — a wrong URL fails silently as an empty or unfiltered walk.',
    admin_discovery_profiles_seed_note: 'Showing built-in defaults — nothing stored in config yet. Any edit saves the full list.',
    admin_discovery_profiles_test: 'Test',
    admin_discovery_profiles_result: 'found {ids} ids of {total} stated · {pages} page(s) walked',
    admin_discovery_profiles_zero_warn: 'zero ids — check the URL',
    admin_discovery_profiles_del_confirm: 'Remove this profile from the walker?',
    admin_discovery_profiles_add: 'Add profile',
    desk_lead_badge: 'LEAD',
    desk_lead_badge_hint: 'Argo found this owner ad — not yet worked supply. Nobody has secured this owner through us yet.',
    admin_access_counters: 'Requests per representative',
    admin_access_requests_word: 'requests',
    admin_access_granted_word: 'granted past them',
    admin_access_history: 'Decided',
    desk_dormant_section: 'Dormant ({n}) — no activity in 60 days',
    desk_dormant_more: '+{n} more dormant — narrow the search to see them',
    desk_avail_facet_title: '📆 Available from',
    desk_avail_facet_hint: 'The date the owner says possession can begin — tenants moving out, a sale handover. One question for rent and sale alike. It reactivates in the feed a week before this date.',
    desk_avail_wake_note: 'A dormant property wakes 7 days before this date with a fresh 60-day clock.',
    desk_avail_conflicting: 'sources disagree',
    desk_avail_conflict_note: 'Two different dates are on record. Confirm with the owner — saving here records yours alongside them, it does not erase the other.',
    desk_avail_shared_note: 'Your record of what the owner told you. Other agents keep their own — if two dates disagree, the property reads as unconfirmed until someone checks. Clearing removes only yours.',
    desk_pool_auto_toggle: '⚡ Auto-share what I save',
    desk_pool_auto_hint: 'listings I save with a price are shared automatically at this commission',
    desk_pool_auto_commission_required: 'set a default commission % first',
    // B238 — a percent needs its base. Worded as a question about the money, not as a
    // schema term: an agent thinks “one month’s rent”, not “monthly_rent”.
    desk_pool_commission_of: 'of',
    desk_pool_basis_unset: '— of what? —',
    desk_pool_basis_monthly_rent: 'one month’s rent',
    desk_pool_basis_sale_price: 'the sale price',
    desk_pool_auto_basis_required: 'say what the commission is a percent of first — one month’s rent, or the sale price',
    desk_pool_card_fallback_title: 'Property',
    desk_pool_offers_count: '{n} offer{s}',
    desk_pool_market_version: 'Market version',
    desk_pool_agents_version: '{agent}\'s version',
    desk_pool_market_caption: '⚖ Market version — consensus of the published offers, not any one agent\'s data. ⚡ = offers disagree.',
    desk_pool_viewing_agents_version: '👁 Viewing {agent}\'s version — fields differing from the market version are highlighted.',
    desk_pool_market_annotation: '(market: {value})',
    desk_pool_no_facts: 'No disclosed facts yet.',
    desk_pool_offers_heading: 'Offers (cheapest first)',
    desk_pool_table_headers: 'Agent / Price / Commission / Your side (50/50)',
    desk_pool_view_version: 'view version',
    desk_pool_agent_fallback: 'agent',
    desk_pool_loading: 'Loading pool…',
    desk_pool_nothing_published: 'Nothing published to the pool yet.',
    desk_pool_card_market_label: '⚖ market version',
    desk_pool_card_disagree: '· ⚡ offers disagree on some facts',
    desk_dup_no_identity: 'No agent profile — duplicates can only be judged from an agent login',
    desk_dup_facet_title: 'POSSIBLE DUPLICATES — same physical apartment?',
    desk_dup_facet_hint: 'Your answer is recorded, not applied — nothing is joined or deleted, and answering the same pair again replaces your last one. The group shows what the records currently support.',
    desk_dup_to_review: '{n} to review',
    desk_dup_cluster_of: '{n} similar listings',
    desk_dup_last_verdict: 'Last decision: {verdict}{note}',
    desk_dup_verdict_same: '✓ Same',
    desk_dup_verdict_different: '✗ Different',
    desk_dup_verdict_unsure: '? Unsure',
    desk_dup_shown_together: 'Shown together as one apartment ({n} records):',
    desk_dup_this_one: '(this one)',
    desk_dup_contested: '⚠ Disputed — someone also judged part of this group as different. Review the decisions.',
    desk_archive_active_tooltip: 'Stops showing under your properties — nothing is deleted, restore any time',
    desk_archive_restore_tooltip: 'Add back to your working properties',
    desk_archive_button: '🗂 Archive — stop working this property',
    desk_archive_restore_button: '↩ Restore to my properties',
    desk_archive_draft_tooltip: 'Archive this draft — not saved, not deleted; it just leaves the review list',
    desk_archive_draft_busy: 'Archiving…',
    desk_archive_draft_button: '🗄 Archive draft',
    desk_property_fallback_title: 'Property',
    desk_property_review_title: 'Review property draft',
    desk_property_recorded_subtitle: 'Recorded — keep adding what you learn; every Record is a new observation, nothing is overwritten',
    desk_property_facet_title: 'PROPERTY — address or cadastral required',
    desk_property_address: 'Address',
    desk_property_address_ph: 'Street, building, apt…',
    // AddressBlock's labels for the Desk (2026-08-19). WORDED IDENTICALLY to the fill
    // form's own strings, deliberately: it is the same control asking the same questions,
    // and two vocabularies for one component is how a surface starts drifting.
    // ⚠️ ALL of them must exist. AddressBlock's internal defaults are ENGLISH, so any label
    // not passed renders in English on an all-Georgian screen — the exact defect that put
    // "floors total" and "area sqm" in front of Georgian agents (labels.js, 2026-08-06).
    desk_addr_street: 'Street',
    desk_addr_street_ph: 'Street name',
    desk_addr_number: 'No.',
    desk_addr_city: 'City',
    desk_addr_peri: 'Around Tbilisi',
    desk_addr_at_pin: 'At this pin',
    desk_addr_use_it: 'Use it',
    desk_addr_map_ph: 'Street or landmark',
    desk_addr_or_map: 'Can’t find it? Just mark it on the map',
    desk_addr_other_number: 'Different number? Fix it',
    desk_addr_unnamed: 'This spot — we could not name it',
    desk_addr_change: 'Change',
    desk_addr_other: 'Other',
    desk_addr_entrance_add: '+ Entrance / apartment',
    desk_addr_entrance_label: 'Entrance / apartment',
    desk_addr_entrance_note: 'Agent-only — never published',
    desk_addr_or: 'or',
    desk_addr_mark_on_map: '🗺 Mark on the map',
    desk_addr_my_location: '📍 My location',
    desk_addr_not_listed: 'Street not in our list? Type it anyway — it is kept as typed.',
    desk_addr_pin_says: 'The pin is on a different building',
    desk_addr_pin_keep: 'Keep what I wrote',
    desk_addr_pin_decide: 'Choose one to continue',
    desk_addr_searching: 'Searching…',
    desk_addr_no_street: 'Not in our directory — your text is kept as typed',
    desk_addr_auto: 'filled from the address',
    desk_addr_auto_pin: 'filled from the pin',
    desk_addr_conflict: 'The pin points at a different district than the one chosen.',
    desk_addr_keep_mine: 'Keep mine',
    desk_addr_use_pin: 'Use the pin’s',
    desk_property_address_suggestion: '📍 {suggestion}',
    desk_property_district: 'District',
    desk_property_cadastral: 'Cadastral code',
    desk_property_type_label: 'Type',
    desk_property_map_section: 'LOCATION ON MAP — optional',
    desk_property_photos_docs_title: 'PHOTOS & DOCUMENTS',
    desk_property_photos_docs_summary: '{n} photo · {n} doc',
    desk_property_photos_section: 'PHOTOS — public gallery · drag to reorder',
    desk_property_photos_view_clean: 'Clean',
    desk_property_photos_view_original: 'Original',
    desk_property_photos_original_note: 'The portal’s own copy, with its watermark. Kept for matching, never shown to clients — view only.',
    desk_property_remove_photo: 'Remove',
    desk_property_move_to_docs: 'Move to DOCUMENTS (agent-only — e.g. shows the cadastral code)',
    desk_property_add_photos: 'Add photos',
    desk_property_docs_section: 'DOCUMENTS — agent-only, never published (plans, PDFs/sheets, photos showing cadastral codes)',
    desk_property_move_to_photos: 'Move to PHOTOS (publishable)',
    desk_property_add_documents: 'Add documents (PDF, sheets, images…)',
    desk_property_uploading: '⏳ Uploading…',
    // Says the actual reason, not "try again". The 2026-08-19 report was three silent 413s in a
    // row — the agent retried because the message told them to, and nothing on screen could
    // have told them the file was simply too big for the proxy.
    desk_property_upload_failed: '⚠️ Upload failed: {err}',
    desk_property_files_upload_note: 'Files upload when you Record the property.',
    desk_pm_photos_from_ad: 'From the ad — kept exactly as captured',
    desk_pm_photos_to_version: 'Photos you add are saved to your version when you Record — the ad itself is not changed.',
    desk_pm_photos_mine: 'Your photos',
    desk_pm_my_version_open: 'You are editing your version of this property — every advert of it opens this same version.',
    desk_pm_my_version_other_ad: 'You opened a different advert of this property; your version keeps its own values and photos.',
    desk_property_photo_size: 'Photo size:',
    desk_property_photo_size_small: 'Small',
    desk_property_photo_size_medium: 'Medium',
    desk_property_photo_size_large: 'Large',
    desk_property_photo_size_original: 'Original',
    desk_property_photo_size_px: 'Longest side {px} px',
    desk_property_photo_size_original_hint: 'Send the file exactly as it is — largest, slowest, and may be rejected if very big',
    desk_property_photo_size_est: '≈ {size} to upload',
    desk_property_photo_size_est_saved: '≈ {size} to upload (was {was})',
    desk_property_terms_facet_title: 'OWNER\'S ASKING TERMS — what the owner asks of the market',
    desk_property_terms_facet_hint: 'The owner\'s public ask. OUR agreement with the owner (commission, exclusivity, minimum price) is separate — see AGENCY AGREEMENT below.',
    desk_property_terms_summary: '{intent} · {price} {currency} / negotiable',
    desk_property_intent_label: 'Intent',
    desk_property_price_negotiable: 'Price — empty = negotiable',
    desk_property_currency_label: 'Currency',
    desk_property_facts_title: 'PROPERTY FACTS — leave empty when unknown (empty ≠ 0)',
    desk_facts_rooms: 'Rooms',
    desk_facts_bedrooms: 'Bedrooms',
    desk_facts_bathrooms: 'Bathrooms',
    desk_facts_area_sqm: 'Area m²',
    desk_facts_kitchen_area: 'Kitchen m²',
    desk_facts_floor: 'Floor',
    desk_facts_floors_total: 'Floors total',
    desk_facts_ceiling: 'Ceiling m',
    desk_facts_ceiling_variation: 'Same ceiling everywhere?',
    desk_facts_ceiling_uniform: 'same everywhere',
    desk_facts_ceiling_varies: 'varies',
    desk_facts_ceiling_min: 'Lowest m',
    desk_facts_ceiling_max: 'Highest m',
    desk_facts_ceiling_range_note: 'The lowest point must be below the highest — these two are not saved until it is.',
    desk_property_condition_label: 'Condition',
    desk_property_condition_ph: 'renovated / white frame / old…',
    desk_facts_balcony: 'Balcony',
    desk_facts_terrace: 'Terrace',
    desk_room_unit_kind: 'The room is in a shared',
    desk_room_unit_apartment: 'apartment',
    desk_room_unit_house: 'house',
    desk_room_unit_rooms: 'Rooms in the whole unit',
    desk_room_unit_area: 'Area of the whole unit, m²',
    desk_room_unit_occupants: 'People already living there',
    desk_owner_contact_title: 'OWNER / CONTACT',
    desk_owner_contact_name: 'Name',
    desk_owner_contact_phone: 'Phone',
    desk_agreement_facet_title: 'AGENCY AGREEMENT — our deal with the owner (internal, never published)',
    desk_agreement_facet_hint: 'Commission, exclusivity, minimum price, validity — CRM data about the relationship, not listing data.',
    desk_agreement_summary: '{n} cooperation{s} / none yet',
    desk_agreement_no_contact: 'No main contact yet — add the owner\'s phone above and Record, then open the agreement here.',
    desk_meeting_facet_title: 'MEETING — schedule a viewing on this property',
    desk_meeting_add_tooltip: 'Add a meeting on this property',
    desk_meeting_add_action: 'Add a meeting',
    desk_meeting_last_outcome: 'last: {status}',
    desk_meeting_list_title: 'Meetings on this property',
    desk_draft_badge: 'draft',
    desk_lineage_col_what: 'What is this?',
    desk_lineage_map_tile: 'Map',
    desk_lineage_col_relation: 'Relationship with the owner',
    desk_meeting_no_client: 'no client',
    desk_meeting_none_yet: 'no meetings yet',
    desk_observed_from_section: 'OBSERVED FROM — sets how much the data is trusted',
    desk_posted_on_facet_title: 'POSTED ON — source platform',
    desk_posted_on_summary: '{n} posting{s}',
    desk_price_changes_summary: '{n} price change{s}',
    desk_open_link: '↗ Open',
    desk_price_history_section: 'PRICE HISTORY',
    desk_price_history_row: '{from} → {to} {currency} ({±pct}%)',
    desk_done_button: 'Done',
    desk_property_cancel: 'Cancel',
    desk_property_saving: 'Saving…',
    desk_property_record_update: '✓ Record update',
    desk_property_heic_converting: 'Converting {n} iPhone photo(s) (HEIC) to JPEG…',
    desk_property_recorded: '✓ Recorded',
    desk_property_accept_save: '✓ Accept & save',
    desk_property_record_button: '✓ Record property',
    desk_toast_client_saved: '✓ Saved to core database',
    desk_toast_property_saved: '✓ Property saved to core database',
    desk_toast_meeting_saved: '✓ Meeting saved',
    // timeAgo() in desk/primitives.jsx — the elapsed-time forms of the shared primitive, and
    // until 2026-08-08 the last hardcoded English on an otherwise all-Georgian Desk (a match
    // row read "3d ago"). These four keys pre-existed IN BOTH LOCALES and were referenced by
    // nothing; wiring them up was the whole fix. Placeholder is `{n}` in all three — it was
    // {m}/{h}/{d} while nothing consumed them, which would have made one caller pass three
    // different variable names for the same quantity.
    // Deliberately SHORT: the output lands INSIDE other sentences ('შენახულია {time}',
    // 'ბოლოს ნახა {when}'), so the unit precedes the "ago" word and never wraps the phrase.
    // None of those wrapper strings carry their own "ago"/"წინ" — check before adding one.
    desk_time_just_now: 'just now',
    desk_time_min_ago: '{n}m ago',
    desk_time_hour_ago: '{n}h ago',
    desk_time_day_ago: '{n}d ago',
    desk_activity_added_client: 'Added client',
    desk_activity_ai_parsed_client: 'AI filled in a client',
    desk_activity_you_edited_client: 'You edited client',
    desk_activity_ai_edited_client: 'AI edited client',
    desk_activity_closed_client: 'Closed client',
    desk_activity_reopened_client: 'Reopened client',
    desk_activity_parsed_property: 'Auto-filled property',
    desk_activity_archived_property: 'Archived property',
    desk_activity_saved_property: 'Saved property',
    desk_activity_draft_badge: 'draft',
    desk_activity_logged_meeting: 'Logged meeting',
    desk_activity_added_candidate: 'Added candidate',
    desk_activity_fallback: 'Activity',
    desk_meettime_today: 'Today {time}',
    desk_meettime_tomorrow: 'Tomorrow {time}',
    desk_tab_activity: '🕘 Activity',
    desk_tab_notif_short: '🔔 Notif',
    desk_tab_notifications: 'Notifications',
    desk_tab_meet_short: '📅 Meet',
    desk_tab_meetings: 'Meetings',
    desk_activity_empty: 'Nothing yet.',
    desk_notif_need_review: '🏠 {n} need review',
    desk_notif_empty: 'Nothing needs attention.',
    desk_notif_failed: 'Failed — needs a fix',
    desk_notif_review_row: '{text} — review',
    desk_notif_price_changes_section: 'PRICE CHANGES (7D)',
    desk_notif_listings_ended_section: 'LISTINGS ENDED (7D)',
    desk_notif_no_longer_found: 'No longer found on {source}',
    desk_meetings_empty: 'Nothing scheduled today or tomorrow.',
    desk_meeting_fallback: 'Meeting',
    desk_ranking_title: '🏆 AGENT RANKING',
    desk_ranking_empty: 'No activity in this period yet.',
    desk_ranking_period: 'Ranking period',
    desk_ranking_window_today: 'today (Tbilisi)',
    desk_ranking_as_of: 'as of {t}',
    desk_ranking_refresh_failed: 'could not refresh',
    desk_ranking_unavailable: 'Ranking unavailable - the data could not be read.',
    desk_ranking_retry: 'Retry',
    desk_ranking_unknown: 'Unknown',
    desk_ranking_daily: 'Day',
    desk_ranking_weekly: 'Week',
    desk_ranking_monthly: 'Month',
    desk_ranking_new: 'NEW',
    desk_ranking_to_next: '{n} pts to #{rank}',
    desk_ranking_legend: '🏁 closed deals · 🤝 meetings · 📥 saved · ↻ re-posted',
    desk_score_legend: "🏁 deals · 🤝 meetings with outcome · 👤 clients followed up · 📋 owner information · ↻ paid reposts with no cooperation",
    desk_score_why: "owner work {owner} · results {results} · wasted reposts {reposts} · attendance ×{mult}",
    desk_floor_tab: "Floor",
    desk_floor_note: "Compared with the whole office, not fixed numbers: each agent's 30-day score, scaled to their days on the board. Office median {median} ({n} agents); the line is {pct}% of it = {line}. Checked today and {days} days ago; below both times = time is up. Trial until {date}.",
    desk_floor_none: "Every agent is above the floor.",
    desk_board_median: "office median {n}",
    stats_part_money: "Money",
    stats_part_reposts_wasted: "Repost spend",
    stats_part_reposts_free: "free — owner work and money present",
    stats_part_paid_n: "{n} paid reposts",
    stats_part_silent: "Silent working days",
    stats_part_silent_sub: "remote {remote} · office {office} · absent {absent}",
    admin_score2_model_v3: "v3 — value score (default)",
    admin_value_repost_cost: "Paid repost cost ₾ (myhome/ss)",
    admin_value_owner_cap_month: "Owner-work cap ₾ / 30 days (cooperations apart)",
    admin_value_coop_rich_weight: "Cooperation with owner info = × owner item",
    admin_value_coop_default_weight: "Cooperation with defaults only = × owner item",
    admin_value_coop_cap_month: "Cooperations cap ₾ / 30 days",
    admin_value_remote_silent_weight: "Silent remote day × day value",
    admin_value_office_silent_weight: "Silent office day × day value",
    admin_value_absent_weight: "Absent day × day value",
    admin_value_late_weight: "Late arrival × day value",
    stats_part_late: "Late arrivals",
    stats_part_late_sub: "{n} late days · {mins} min late",
    admin_value_values_window_days: "Rates measured over (days)",
    desk_floor_me_vs2: "Your score {norm} · line {line}",
    desk_floor_me_rule2: "Rule: {days} days below the line = you leave.",
    desk_score_legend3: "Score in ₾: value created for the agency minus resources spent, measured against the whole agency · 🏁 deals · 🤝 meetings with outcome · 👤 clients followed up · 📋 owner information · ↻ paid reposts with nothing behind them",
    desk_score_why3: "owner work {owner} (cooperations {coops}) · money {results} · repost spend {reposts} · silent working days {silent} · late arrivals {late}",
    desk_score_legend3: "◆ cooperations opened · 🏆 closed deals · 📅 meetings · 👤 clients · 📋 owner info · 📥 saved",
    desk_floor_note3: "Score over 30 days, compared with the whole agency (all offices). Office median {median} ({n} agents); the line is {pct}% of it = {line}, and any negative score is a drain. Checked today and {days} days ago; below both times = time is up. Trial until {date}.",
    desk_floor_vs3: "score {norm} vs office median {median}",
    desk_floor_weak_silent: "silent working days",
    desk_floor_silent_remote: "silent remote days {n}: {dates}.",
    desk_floor_silent_office: "silent office days {n}: {dates}.",
    desk_floor_absent_n: "absent {n}.",
    stats_lines_month_only: "The median and bottom lines are drawn on a 1-month range (30 days / this month).",
    stats_part_owner: "Owner work",
    stats_part_owner_capped: "{raw} before the weekly cap",
    stats_part_results: "Results",
    stats_part_results_sub: "{deals} deals · {meetings} meetings · {clients} clients",
    stats_part_reposts_penalty: "Reposts without cooperation",
    stats_part_unbacked: "{n} of {paid} paid reposts",
    stats_part_attendance_mult: "Attendance",
    desk_board_line: "bottom line {n}",
    desk_floor_me_weak: "behind the group on: {parts}",
    desk_floor_me_vs: "Your score {norm} · office median {median} · line {line}",
    desk_floor_weak_reposts: "weak: reposts without cooperation",
    desk_floor_weak_attendance: "weak: attendance",
    desk_floor_weak_results: "weak: meetings / deals",
    desk_floor_weak_owner: "weak: owner work",
    desk_floor_vs: "score {norm} vs office median {median}",
    desk_floor_no_group: "Too few agents with 7+ days on the board to compare yet.",
    desk_floor_error: "The floor could not be loaded — try again in a few minutes.",
    desk_floor_me_title: "Your bottom line",
    desk_floor_me_ok: "✓ You are above the line.",
    desk_floor_me_below: "You are below the line. Cross it within {days} days, or you leave.",
    desk_floor_me_leave: "You have been below the line for {days} days — your time is up. Talk to your manager.",
    desk_floor_me_rule: "Rule: under {pct}% of the office median for {days} days = you leave.",
    desk_floor_me_trial: "Trial until {date} — no consequences yet.",
    desk_floor_leave: "time is up",
    desk_floor_at_risk: "at risk",
    desk_floor_trial: "trial",
    admin_score2_title: "Agent scorecard (v2)",
    admin_score2_desc: "Points for owner work and results, multiplied by attendance consistency. Reposts earn nothing; a paid myhome/ss repost with no owner work by the same agent on that flat costs the penalty below.",
    admin_score2_model: "Board model",
    admin_score2_model_v2: "v2 — owner work + results",
    admin_score2_model_v1: "v1 — old points",
    admin_score2_coop_rich: "Cooperation with owner info",
    admin_score2_coop_default: "Cooperation, defaults only",
    admin_score2_restriction: "Restrictions answer (per listing)",
    admin_score2_terms: "Terms recorded (per listing)",
    admin_score2_detail: "Property details / valuation",
    admin_score2_client: "Client followed up",
    admin_score2_meeting: "Meeting with outcome",
    admin_score2_close: "Closed deal",
    admin_score2_commission_per_100: "Pts per 100 ₾ commission",
    admin_score2_owner_cap_week: "Owner-work points cap / week",
    admin_score2_unbacked_repost: "Penalty: repost without cooperation",
    admin_score2_attendance_floor: "Attendance multiplier at 0%",
    admin_floor_title: "Leave floor",
    admin_floor_min_group: "Minimum agents to compare",
    admin_floor_min_days: "Not judged before (days on board)",
    admin_floor_window_days: "Comparison window (days)",
    admin_floor_bottom_share: "Also flag weakest share (0–1, 0 = off)",
    admin_floor_ratio: "Line = share of office median (0–1)",
    admin_floor_desc: "No fixed activity numbers: every agent is compared with the whole office. Below ratio × the office median of the 30-day score (scaled to days on the board) = candidate to leave; still below after the grace period = time is up. Optionally the weakest share of the group is flagged too. Management sees the list on the Desk (Floor tab); each agent sees their own line.",
    admin_floor_grace_days: "Grace period (days)",
    admin_floor_enforce_from: "Trial ends on",
    admin_cobroke_title: 'Co-broke standard',
    admin_cobroke_desc: 'The share we keep when the client is represented by an agent from another agency. Copied onto each deal when its split is recorded — changing it here does not alter deals already agreed.',
    admin_cobroke_unit: '% to us',
    admin_rankpts_title: 'Ranking points',
    admin_rankpts_desc: 'One scoring system for the whole app: these points drive BOTH the Desk leaderboard and the Stats tab performance score (weekly base — other windows scale the caps). Deals should dominate; caps stop re-post/save volume from outrunning results. Attendance adds a small bonus and is never shown as a public metric.',
    fx_stale_tooltip: "today's refresh FAILED — this is the previous day's rate",
    admin_fx_title: 'Currency rates',
    admin_freeze_title: 'Freeze commission inputs',
    admin_freeze_desc: 'Closed deals whose agent or manager shares were never recorded are recalculated on every read, so they can move later. This writes them onto the deal as of its own close date. Already-recorded shares are left untouched, and running it twice changes nothing.',
    admin_freeze_btn: 'Freeze now',
    admin_freeze_running: 'Freezing…',
    admin_freeze_done: 'Checked {swept}, froze {stamped}.',
    admin_freeze_none: 'Nothing to freeze — every closed deal already has its shares recorded.',
    admin_freeze_fail: 'Could not freeze: {err}',
    admin_fx_desc: 'GEL per 1 unit. Commission arrives in GEL or USD; every score and money total converts through this rate.',
    admin_rankpts_close: 'Closed deal',
    admin_rankpts_commission: 'Pts per 100 commission',
    admin_rankpts_meeting: 'Meeting',
    admin_rankpts_scrape: 'Save',
    admin_rankpts_scrape_cap: 'Save limit /wk',
    admin_rankpts_reshare: 'Re-post',
    admin_rankpts_reshare_cap: 'Re-post limit /wk',
    admin_rankpts_office_day: 'Office day bonus',
    admin_rankpts_perfect_week: 'Perfect week bonus',
    desk_person_intent_buy: 'Buying',
    desk_person_intent_rent: 'Renting',
    desk_person_intent_buy_or_rent: 'Buy/Rent',
    desk_person_intent_short_term: 'Short-term',
    desk_person_intent_sale: 'For sale',
    desk_person_intent_lease: 'For lease',
    desk_person_up_to_price: 'up to {price} {currency}',
    desk_person_modal_title: 'Person',
    desk_person_unnamed: 'Unnamed',
    desk_person_looking_for: 'LOOKING FOR ({n})',
    desk_person_no_active_searches: 'No active searches.',
    desk_person_owns_selling: 'OWNS / SELLING ({n})',
    desk_person_no_listings: 'No listings on record.',
    desk_person_showing_of: 'Showing {n} of {n}.',
    desk_person_cooperations: 'COOPERATIONS ({n})',
    desk_person_no_cooperation: 'No cooperation opened with this contact yet.',
    desk_person_open_coop_tooltip: 'Open cooperation for this property',
    desk_person_modal_close: 'Close',
    desk_coop_chip_tooltip: 'Cooperation — {status}',
    desk_represented_by: 'Represented by {name}',
    desk_coop_not_joined: 'not set yet',
    desk_coop_group_note: 'One relationship recorded across several rows (duplicate contact records / sibling ads) — unified for display, every record kept',
    desk_lineage_reshared_from: 'Re-posted from {who}',
    desk_entry_family_reach_tooltip: '{agents} agents reposted · {total} total postings',
    desk_coop_open_tooltip: 'Open a cooperation with this listing\'s contact',
    desk_coop_chip_default: '📞 contact',
    desk_coop_modal_title: 'Cooperation',
    desk_coop_intent_label: 'Intent',
    desk_coop_intent_sale: 'Sale',
    desk_coop_intent_rent: 'Rent',
    desk_coop_status_label: 'Status',
    desk_coop_status_proposed: 'proposed',
    desk_coop_status_negotiating: 'negotiating',
    desk_coop_status_agreed: 'agreed',
    desk_coop_status_rejected: 'rejected',
    desk_coop_status_ended: 'ended',
    desk_entry_closed_sold: 'Sold',
    desk_entry_closed_rented: 'Rented out',
    desk_terms_agent_recorded: 'recorded by an agent',
    // Reserved for GENUINE ad-stated conditions (source_kind='scrape') once a producer
    // extracts them from listing text — no such data exists yet, so nothing renders it.
    desk_terms_from_owner_ad: 'conditions from the source ad',
    desk_coop_commission_section: 'COMMISSION TERMS',
    desk_coop_commission_type_label: 'Commission type',
    desk_coop_type_percent: 'Percent of price',
    desk_coop_type_net_amount: "Owner's net ask (agent keeps the spread)",
    desk_coop_type_net_commission: 'Fixed commission (regardless of price)',
    desk_coop_type_fixed_net: 'Fixed amount (regardless of price)',
    desk_coop_type_standard: 'Standard (1 month / year, pro-rated)',
    // The commonest cooperation sentence in the corpus — „50%-ზე ვთანამშრომლობ" —
    // offers half the FEE. Read as percent-of-price it made a 100,000 commission on a
    // 200,000 flat (owner ruling 2026-09-12).
    desk_coop_type_coop_split: 'Share of the commission (co-broke)',
    desk_coop_split_pct_label: 'Share of the commission %',
    desk_coop_split_note: 'A share of the fee, not of the price — so the amount cannot be computed until the fee itself is known.',
    desk_coop_owner_net_label: 'Owner wants (net)',
    desk_coop_rate_check: '{p}% of the sale price — is that the commission, or your share of it?',
    desk_coop_rate_check_is_split: 'It is the share',
    desk_coop_net_commission_label: 'Commission amount',
    desk_coop_standard_note: 'Standard: agent gets 100% of one month\'s rent for a 12-month lease, pro-rated for shorter terms (e.g. 50% of one month for a 6-month lease).',
    desk_coop_negotiated_price: 'Negotiated price',
    desk_coop_price_from_ad_hover: 'Taken from the listing ({p})',
    desk_coop_commission_pct: 'Commission %',
    // 0–100 is a database CHECK, not a preference. Name the limit AND the way out, because
    // the number people actually type when they get this wrong is a fee, not a percent.
    desk_coop_pct_out_of_range: 'Commission % must be between 0 and 100. To record a fixed fee instead, change the commission type to a net amount.',
    desk_coop_commission_amount: '≈ commission amount',
    desk_draft_restored: 'Unsaved edits restored',
    desk_draft_discard: 'discard',
    desk_share_draft_restored: 'Your values from last time',
    desk_share_price_from_coop: 'from the agreed price',
    desk_share_price_converted: 'converted from {c}',
    desk_deal_open_source: 'Open the ad ({h})',
    desk_share_built_from_origin: 'Built from the original ad — open it',
    desk_share_reload: 'reload listing',
    desk_adhist_title: 'HOW THIS HAS BEEN ADVERTISED',
    desk_adhist_none: 'No repost of this property has been recorded yet.',
    desk_adhist_not_captured: 'price not captured',
    desk_adhist_now: 'now {p}',
    desk_adhist_note: 'The price shown is the one recorded when the ad was published. "now" appears when that listing’s price has changed since.',
    desk_share_ext_stale: 'The senTavus extension was reloaded, so this tab lost its connection. Refresh the page and it will work again.',
    desk_share_ext_reload: 'Reload page',
    desk_share_browsers: 'Your browsers',
    desk_share_browsers_none: 'No browser with the senTavus extension has checked in yet. Open Chrome with the extension, logged in as you.',
    desk_share_browser_online: 'online',
    desk_share_browser_offline: 'last seen',
    desk_share_portal_logged_out: 'not logged in',
    desk_share_portal_unknown: 'unknown',
    desk_share_browser_use: 'Share from this browser',
    desk_share_need_title: '{p} needs these before sharing:',
    desk_share_upfront_title: 'Fill these in before sharing — a portal will refuse the ad without them:',
    desk_share_upfront_done: 'Everything the portals require is filled in.',
    desk_share_need_continue: 'Continue',
    desk_share_need_photos: 'No photos — add photos to the listing first.',
    desk_share_f_street: 'Street',
    desk_share_f_street_number: 'Street number',
    desk_share_f_building_status: 'Building status',
    desk_share_f_condition: 'Condition',
    desk_share_f_land_status: 'Land status',
    desk_share_f_description: 'Description',
    desk_share_job_queued: 'Sent to {d} — starts within 30 s…',
    desk_share_job_claimed: '{d} picked it up…',
    desk_share_job_running: '{d} is filling the form…',
    desk_share_job_filled: 'Form filled on {d}.',
    desk_share_job_error: 'Failed on {d}: {m}',
    desk_share_job_expired: '{d} did not pick it up. Is Chrome open there?',
    desk_share_job_publishing: '{d} is pressing Publish…',
    desk_share_job_paying: '{d} is paying from the account balance…',
    desk_share_job_published: 'Published ✓',
    desk_share_job_open: 'Open the ad',
    desk_share_step_sent: 'Sent',
    desk_share_step_picked: 'Picked up',
    desk_share_step_filling: 'Filling',
    desk_share_step_publish: 'Publishing',
    desk_share_step_paying: 'Paying',
    desk_share_step_done: 'Published',
    desk_share_wait_for: 'Wait until {p} finishes or fails — the PC fills one portal at a time (it only works in the open tab).',
    desk_share_busy: 'Another share is still running on that PC — wait until it finishes or fails.',
    desk_share_job_no_update: 'No news from the PC for 15 minutes — check that portal tab on the PC.',
    sched_title: 'Schedule sharing',
    sched_platforms: 'Platforms',
    sched_confirm_account: 'This is my account',
    sched_not_logged: 'not logged in on this PC — log in there first',
    sched_days: 'Days',
    sched_start: 'Start',
    sched_every: 'every',
    sched_days_unit: 'days',
    sched_times: 'times',
    sched_end: 'End',
    sched_time: 'Time',
    sched_window: 'Random time between',
    sched_exact: 'Exact time',
    sched_repeat: 'Repeat shares',
    sched_repeat_new: 'Publish a new ad',
    sched_repeat_refresh: 'Refresh the existing ad (coming soon)',
    sched_save: 'Schedule',
    sched_saved: 'Scheduled ✓',
    sched_need_browser: 'Choose an online PC above first.',
    sched_plans: 'Scheduled shares',
    sched_cancel: 'Cancel',
    sched_active: 'active',
    sched_answer_first: 'Answer the questions above first: nobody will be at the PC to answer them when it runs.',
    sched_questions: 'The portal needs these to publish. Answer them now:',
    sched_pay_q: 'If the portal asks for its fee (ss 0.10₾; myhome after 3 free ads a month):',
    sched_pay_yes: 'Pay from my portal balance',
    sched_pay_no: 'Do not pay: the share stops there and I finish it on the PC',
    sched_state_scheduled: 'waiting',
    sched_state_queued: 'running',
    sched_state_published: 'published',
    sched_state_error: 'failed',
    sched_state_missed: 'missed',
    sched_state_cancelled: 'cancelled',
    notif_share_run_published: 'Scheduled share published',
    notif_client_waiting: 'Client waiting on WhatsApp',
    notif_client_waiting_min: 'waiting {n} min for your answer',
    notif_client_waiting_hours: 'waiting {n} h for your answer',
    notif_client_waiting_suggested: 'Suggested reply - check and edit before sending',
    notif_client_waiting_copy: 'Copy reply',
    notif_client_waiting_copied: 'Copied',
    notif_client_waiting_open: 'Open the chat',
    notif_share_run_error: 'Scheduled share failed',
    notif_share_run_missed: 'Scheduled share missed',
    desk_share_ext_waking: 'The senTavus extension has not answered yet — it may still be starting up. Press a platform again in a moment.',
    desk_share_confirming: 'Confirming this is current — reposting unlocks in a moment.',
    desk_share_refreshed: 'The listing changed since you last opened it. Fields you had edited were kept.',
    desk_share_unconfirmed: 'Showing your last loaded copy — it could not be confirmed as current, so reposting is held back.',
    desk_share_retry: 'try again',
    desk_share_no_fx: 'No exchange rate available — the price stays in its original currency.',
    desk_share_price_edited: 'edited for this repost',
    desk_share_price_reset: 'reset',
    desk_share_price_differs: 'Differs from the agreed cooperation price',
    desk_share_coop_autosaved: 'Cooperation terms were saved before reposting.',
    desk_coop_autosave_saving: 'Saving…',
    desk_coop_autosave_saved: '✓ Saved automatically',
    desk_coop_autosave_hint: 'Changes save automatically',
    desk_coop_autosave_retry: 'Save failed — retry',
    desk_share_save_coop_first: 'Unsaved cooperation terms: saving them would also join you to the cooperation — press Save on the cooperation tab first.',
    desk_coop_exclusive: 'Exclusive',
    // 1073 — the owner's instruction about their OWN advert, published beside the
    // commission in 1,311 of 70,580 advert texts: „არ დააკოპიროთ განცხადება".
    // Agency-level like exclusivity, not one agent's own term.
    desk_coop_no_republish: 'Do not share',
    // 1080 — a DIFFERENT refusal from "do not share", and they often appear together:
    // one is about the advert, the other about the relationship (owner 2026-09-12).
    desk_coop_no_cooperation: 'Does not cooperate',
    desk_coop_no_cooperation_hint: 'The owner does not work with agencies at all.',
    desk_coop_no_republish_hint: 'The owner forbids copying or reposting this advert.',
    desk_coop_locked: 'join to see terms',
    // B67b: terms live on a participation, so with several participants the agent
    // must say whose numbers they are editing — there is no sane default.
    desk_coop_pick_participant: 'Pick whose terms you are editing first — these numbers belong to one agent’s negotiation, not to the cooperation as a whole.',
    // B72: a participant who has ALREADY joined must be told the thing that
    // actually reveals the numbers — recording their own terms — not "join".
    desk_coop_locked_contribute: 'record your terms to see the rest',
    desk_coop_valid_until: 'Valid until',
    desk_coop_pets_label: 'Pets',
    desk_coop_pets_unspecified: 'Unspecified',
    desk_coop_pets_not_allowed: 'Not allowed',
    desk_coop_pets_small_dogs_ok: 'Small dogs OK',
    desk_coop_pets_allowed: 'Allowed',
    desk_coop_tenant_pref: 'Tenant preference',
    desk_coop_tenant_pref_ph: 'e.g. family, no students',
    desk_coop_notes: 'Notes',
    desk_coop_participants_section: 'PARTICIPANTS (agents, our side)',
    desk_dedup_kind_properties: 'Properties',
    desk_dedup_kind_people: 'People',
    desk_party_dedup_intro: 'Same person, recorded twice? Confirm to link them — nothing is deleted, and it can be undone.',
    desk_party_dedup_pending: '{n} possible-duplicate pair(s) to review',
    desk_party_dedup_same_name: 'identical name',
    desk_party_dedup_empty: 'Nothing to review — no pending duplicate people.',
    desk_phone_header_open: 'person view →',
    desk_phone_header_agent: 'agent',
    desk_phone_header_no_activity: 'no recorded activity',
    desk_phone_header_dup_records: 'possible duplicate record(s)',
    desk_party_combined: 'combined from {n} records',
    desk_party_combined_hint: 'This person was confirmed the same across {n} duplicate records; their requests and listings are shown together.',
    desk_party_dup_title: 'POSSIBLE DUPLICATES — same person?',
    desk_party_dup_cluster: 'Confirmed same person (shown together):',
    desk_party_dup_same_phone: 'same phone',
    desk_party_dup_same_email: 'same email',
    desk_party_existing_found: 'Already in the database with this phone/email:',
    desk_party_use: 'this is them →',
    desk_failed_entry_title: 'This entry was not saved',
    desk_failed_entry_error: 'It failed with: {reason}',
    desk_failed_entry_checking: 'Checking whether this person is already registered…',
    desk_failed_entry_exists: 'This person IS already in the database — so do not add them again:',
    desk_failed_entry_open: 'open the client',
    desk_failed_entry_archive: 'archive this attempt',
    desk_failed_entry_add_anyway: 'a different person with the same number — add anyway',
    desk_failed_entry_done: 'Handled — the failed entry left the list.',
    desk_party_using_existing: 'Linked to the existing client — no duplicate will be created.',
    desk_party_create_new: 'create new anyway',
    desk_coop_editing_terms_of: 'Editing {name}’s own terms — each agent records their own negotiation with the owner',
    desk_coop_editing_terms_own: 'Recording YOUR terms with the owner — each agent records their own negotiation',
    desk_coop_save_joins: 'You are not in this cooperation yet — recording and saving your own terms joins you automatically. Closing changes nothing.',
    desk_coop_record_own: 'record your own terms',
    desk_deal_editor_title: 'Deal records',
    desk_deal_tab_terms: 'Owner terms',
    // ქონების შეფასება — the valuation tab (1065, 2026-09-12)
    desk_deal_tab_valuation: 'Property valuation',
    desk_val_loading: 'Computing the comparison…',
    desk_val_error: 'The analysis could not be computed',
    desk_val_no_unit: 'No active advert on this property to compare',
    desk_val_open: 'Property valuation',
    desk_val_subject: 'Subject',
    desk_val_coverage: 'Known {k} of {n} axes ({p}%)',
    desk_val_unknown: 'unknown',
    desk_val_stats: 'Statistics',
    desk_val_stats_hint: 'Every comparable is restated at this flat’s condition, building class and floor position. Percentile = the share of peers asking less per m².',
    desk_val_col_group: 'Group',
    desk_val_col_n: 'n',
    desk_val_col_median: 'Median $/m²',
    desk_val_col_iqr: 'p25–p75',
    desk_val_col_pct: 'ask less',
    desk_val_col_dev: 'vs median',
    desk_val_too_few: 'too few to compare',
    desk_val_traits: 'Traits',
    desk_val_traits_hint: 'Rarity among the {n} peers ({label}) WHO HAVE AN ANSWER. Coverage says how many do — the rest is unknown, not absent.',
    desk_val_trait_line: '{r}% of known peers have it · known for {c}%',
    desk_val_trait_common: 'Defines the cohort (all known peers share it)',
    desk_val_trait_uncovered: 'Not comparable yet (coverage under {p}%)',
    desk_val_alike: 'Most alike',
    desk_val_alike_hint: 'Active adverts closest to this flat; the price verdict compares asked $/m² as advertised. Open them to show the owner.',
    desk_val_cheaper: 'cheaper', desk_val_similar: 'similar price', desk_val_dearer: 'dearer',
    desk_val_cond_better: 'better condition', desk_val_cond_worse: 'worse condition', desk_val_cond_same: 'same condition', desk_val_cond_different: 'other condition',
    desk_val_open_card: 'Card',
    desk_val_questions: 'Questions for the call',
    desk_val_questions_hint: 'Answer while on the phone; the numbers below recompute from your answer. If nothing fits, write a comment.',
    // The address question and comments (1066, 2026-09-12)
    desk_val_addr_stored: 'Stored',
    desk_val_addr_text: 'The advert text says',
    desk_val_addr_this_one: 'This one',
    desk_val_addr_chose: 'The agent chose',
    desk_val_addr_street: 'Street',
    desk_val_addr_house: 'No.',
    desk_val_year: 'Year',
    desk_val_addr_city: 'City',
    desk_val_addr_district: 'District',
    desk_val_addr_sub: 'Neighbourhood',
    desk_val_addr_korpus: 'Block',
    desk_val_addr_pin_basis: 'map pin',
    desk_val_addr_map: 'Pin on the map',
    desk_val_addr_map_hide: 'Hide the map',
    desk_val_addr_map_hint: 'Click the map, or drag the pin, to place the building',
    desk_val_addr_pin_save: 'Save the pin',
    // One question per open part of the address (addr0, 2026-10-09: clear, direct, never repeating what we know)
    desk_val_addr_q_street: 'Which street is it on?',
    desk_val_addr_q_street_in: 'Which street is it on? ({district})',
    desk_val_addr_q_number: '{street} — house number?',
    desk_val_addr_q_korpus: 'Which block (korpus)?',
    desk_val_addr_q_sub: 'Which neighbourhood is it in?',
    desk_val_addr_q_district: 'Which district: {a} or {b}?',
    desk_val_addr_q_which: 'Which address is right?',
    desk_val_addr_unknown: 'Owner does not know',
    desk_val_comment: 'Comment',
    desk_val_comment_ph: 'What the owner said, in your words',
    desk_val_latin_to_georgian: 'In Georgian letters',
    desk_val_comment_flat: 'Comment on the flat',
    desk_val_yes: 'Yes', desk_val_no: 'No', desk_val_cannot_tell: 'Cannot tell',
    desk_val_save: 'Save',
    desk_val_saved: 'Recorded — numbers refreshed',
    desk_val_nothing_to_ask: 'Nothing open — every axis we price on is known.',
    desk_val_metro: '{m} m to {name} metro',
    desk_val_rule: 'deterministic · no model · {v}',
    desk_val_bd_title: 'Price breakdown',
    desk_val_adj_title: 'Agent adjustment — secondary light',
    desk_val_adj_hint: 'Bedrooms without their own exterior window are not priced by the model yet. Your adjustment is stored separately from the estimate above and never changes it.',
    desk_val_adj_percent: 'Adjustment %',
    desk_val_adj_amount: 'or amount',
    desk_val_adj_beds: 'Bedrooms affected',
    desk_val_adj_beds_n: '{n} bedroom(s)',
    desk_val_adj_note: 'Note',
    desk_val_adj_save: 'Save adjustment',
    desk_val_adj_saved: 'Saved.',
    desk_val_adj_failed: 'Not saved —',
    desk_val_bd_hint: 'Median price per m² of the closest comparable flats with a trusted price, restated to renovated / middle floor; then this flat’s own condition and floor. Each step shows where its number comes from.',
    desk_val_bd_base: 'Base · {level} · {n} comparable flats',
    desk_val_bd_reference: 'renovated, middle floor',
    desk_val_bd_level_building: 'same building',
    desk_val_bd_level_complex: 'same complex',
    desk_val_bd_level_street: 'same street',
    desk_val_bd_level_area: 'same micro-district / neighbourhood',
    desk_val_bd_level_district: 'same district',
    desk_val_bd_level_wider_area: 'wider area',
    desk_val_bd_level_global: 'city',
    desk_val_bd_condition: 'Condition',
    desk_val_bd_floor: 'Floor',
    desk_val_bd_cls_frame: 'shell',
    desk_val_bd_cls_needs_renovation: 'needs renovation',
    desk_val_bd_cls_in_progress: 'renovation in progress',
    desk_val_bd_cls_old_reno: 'old renovation',
    desk_val_bd_cls_ruin: 'hazardous / stripped',
    desk_val_bd_cls_premium: 'premium renovation',
    desk_val_bd_cls_basement: 'basement',
    desk_val_bd_cls_first: 'first floor',
    desk_val_bd_cls_second: 'second floor',
    desk_val_bd_cls_top: 'top floor',
    desk_val_bd_step_meta: 'index: {level} · {n} observations · confidence {c}',
    desk_val_bd_estimate: 'Estimate',
    desk_val_bd_range: 'range',
    desk_val_bd_ask: 'asking now',
    // the simple valuation tab (owner 2026-09-23: "it must be simple")
    desk_val_est_title: 'Estimated price',
    desk_val_dearer_by: '{p}% above',
    desk_val_cheaper_by: '{p}% below',
    desk_val_in_line: 'in line',
    desk_val_ask_owner: 'Ask the owner',
    desk_val_fill: 'Fill in',
    desk_val_fill_hide: 'Hide',
    desk_val_similar_title: 'Similar flats',
    desk_val_show_all: 'All ({n})',
    desk_val_details: 'Details — how it was calculated',
    desk_val_mkt_title: 'Market',
    desk_val_mkt_district: 'this district',
    desk_val_mkt_hood: 'this neighbourhood',
    desk_val_mkt_rooms: 'rooms',
    desk_val_mkt_n: '{n} comparable',
    desk_val_mkt_median: 'Median $/m²',
    desk_val_mkt_range: 'Typical price',
    desk_val_mkt_listed: 'Listed now',
    desk_val_mkt_days: 'Days on market',
    desk_val_mkt_low: 'Few comparables — a rough figure',
    desk_val_mkt_none: 'Not enough data in this district yet',
    desk_val_unsupported_kind: "Valuation isn't supported for this property type",
    desk_val_need_size: 'Add the area to get an estimate',
    desk_val_unvalidated: 'Prices for this type are not validated yet — treat as rough',
    desk_val_bd_unknown: 'Not known — the range is widened',
    desk_val_bd_unknown_condition: 'condition',
    desk_val_bd_unknown_floor: 'floor',
    desk_val_bd_none: 'Not enough comparable flats with a trusted price',
    // Reshare tab (B244)
    desk_deal_tab_share: 'Repost',
    desk_share_loading_hint: 'Preparing the payload and running the AI — first open on a listing takes a few seconds.',
    desk_share_account_mismatch: 'Reposting is blocked — the extension is signed in as a different agent.',
    desk_share_account_desk: 'Desk',
    desk_share_account_ext: 'Extension',
    desk_share_account_fix: 'The repost is authored by whoever the EXTENSION is signed in as — the listing, and the ownership and ranking credit with it, would go to that account, not yours. Sign the extension into your own account, then reopen this tab.',
    desk_share_intro: 'This is what will be handed to the extension. The portals apply their own formatting on top, so the final ad can differ. Leave it as it is to repost the original, or edit any value — the repost takes what you see here.',
    desk_share_description: 'Ad copy',
    desk_share_ai_cached: 'AI (already analysed)',
    desk_share_ai_fresh: 'AI enhanced',
    desk_share_prov_own: 'Your own edit',
    desk_share_prov_ai: 'Filled by AI — check it',
    desk_share_fb_by_orpheus: 'Written by Orpheus',
    desk_share_fb_by_orpheus_note: 'The post body is built from recorded facts — edit it before posting if anything is off.',
    desk_share_prov_origin: 'From the original ad',
    desk_share_f_bedrooms: 'Bedrooms',
    desk_share_f_bathrooms: 'Bathrooms',
    desk_share_f_land_area: 'Land m²',
    desk_share_f_floor: 'Floor',
    desk_share_f_floors_total: 'Floors total',
    desk_share_f_ceiling: 'Ceiling h.',
    desk_share_f_deal: 'Deal',
    desk_share_f_kind: 'Type',
    desk_share_f_city: 'City',
    desk_share_block_unsupported: '{p} does not offer this deal for this property type',
    desk_share_block_no_pledge: 'We do not reshare pledge (გირაო) listings',
    desk_share_block_no_republish: 'The owner asked not to post this listing — it is not reshared anywhere',
    desk_share_block_not_automatable: '{p}: this deal needs details the extension cannot fill yet; finish it on the PC',
    desk_share_block_few_photos: '{p} needs at least 3 photos for a flat to rent',
    desk_share_block_price_phone: '{p} refuses a price that looks like a phone number',
    desk_share_block_price_low: '{p} refuses a price this low',
    desk_share_block_street_number_format: '{p} accepts a house number of at most 3 digits and 1 letter',
    desk_share_block_floor_above_total: 'The floor is above the total floors',
    desk_share_f_cadastral: 'Cadastral',
    desk_share_photos: 'Photos',
    desk_share_photos_count: '{n} photos will be sent',
    desk_share_photos_watermarked: '{n} watermarked originals excluded — reposting them would publish the other portal’s branding under your listing.',
    desk_share_photos_thumbs: 'Only thumbnails are available for this ad — the originals are all watermarked.',
    desk_share_photos_none: 'No usable photos. The portals will reject a listing with no images.',
    desk_share_photos_hint: 'Click a photo to exclude it from this repost; click again to restore it.',
    desk_share_photos_reorder: 'Reorder photos',
    desk_share_photos_reorder_done: 'Done',
    desk_share_photos_reorder_reset: 'Original order',
    desk_share_photos_reorder_hint: 'Tap the left or right half to move a photo; ★ makes it the main photo. korter wants the inside of the flat first.',
    desk_share_photos_main: 'Main',
    desk_share_photos_make_main: 'Make main photo',
    desk_share_photos_left: 'Move left',
    desk_share_photos_right: 'Move right',
    desk_share_photos_excluded: '{n} excluded by you',
    desk_share_no_extension: 'The senTavus extension is not responding in this browser. Reposting runs through it, so install or enable it, then reload this page.',
    desk_share_fb_open: 'Open on Facebook ↗',
    desk_share_fb_opening: 'staging…',
    // Says which half is missing and what to do. "Copy the post" still works and is right
    // above it, so this is a downgrade to the manual route, not a dead end.
    desk_share_fb_needs_update: 'Your senTavus extension is too old to open Facebook for you — copy the post above and paste it yourself, or update the extension to stage the photos automatically.',
    desk_share_fb_opened: 'Opened — {n} photos staged. Open Create Post in your group; text and photos insert automatically. You press Post.',
    desk_share_fb_opt_contact: 'Share contact',
    desk_share_fb_opt_price: 'Share price',
    desk_share_fb_opt_exact: 'Share exact location (street/building №)',
    desk_share_fb_contact_ph: 'Phone for the post — remembered',
    desk_share_fb_compose: 'Compose post',
    desk_share_fb_change_opts: 'change options',
    desk_share_fb_generating: 'Orpheus is writing…',
    desk_share_fb_copy: 'Copy post',
    desk_share_fb_copied: 'Copied ✓',
    desk_share_one_at_a_time: 'You can start several portals at once — each opens its own tab and fills the form while you watch. The same portal cannot run twice at the same time.',
    desk_lineage_status_section: 'Status',
    desk_status_deal_closed_line: 'The deal on this property is recorded as closed.',
    // Availability history (0304/0305) — the close is a dated fact, not a permanent label.
    desk_lifecycle_active: 'On the market',
    desk_lifecycle_withdrawn: 'Taken down',
    desk_lifecycle_reserved: 'Reserved',
    desk_lifecycle_negotiating: 'In negotiation',
    desk_lifecycle_paused: 'Paused',
    desk_lifecycle_src_scrape: 'from the ad',
    desk_lifecycle_src_scrape_reshare: 'from the ad',
    desk_lifecycle_src_agent: 'an agent',
    desk_lifecycle_src_owner: 'the owner',
    desk_lifecycle_src_admin: 'an administrator',
    desk_lifecycle_src_registry: 'the registry',
    desk_lifecycle_src_ai: 'automatic',
    desk_lifecycle_src_fill_form: 'the owner form',
    desk_lifecycle_retracted: 'withdrawn',
    desk_lifecycle_more: 'earlier ({n})',
    desk_lifecycle_less: 'Show less',
    desk_lifecycle_contested: 'Recorded as closed, but the ad is still live on the platform — worth checking.',
    desk_reopen_short: 'Back on the market',
    desk_reopen_hint: 'Undo this close — the deal fell through, or it was recorded by mistake',
    desk_reopen_title: 'Why is it back on the market?',
    desk_reopen_deal_fell_through: 'The deal fell through',
    desk_reopen_deal_fell_through_hint: 'It was agreed and then collapsed — the tenant or buyer backed out',
    desk_reopen_deal_fell_through_effect: 'The close stays in the history and you keep the credit for it. The property returns to the feed, but the cooperation stays ended — re-agree the terms if you carry on with this owner.',
    desk_reopen_recorded_by_mistake: 'I recorded it by mistake',
    desk_reopen_recorded_by_mistake_hint: 'The deal never happened — for example the wrong ad was picked',
    desk_reopen_recorded_by_mistake_effect: 'The close is marked withdrawn and its credit is removed. The property returns to the feed, and the cooperation and lead this close ended are restored with it.',
    desk_reopen_confirm: 'Back on the market',
    desk_reopen_not_yours: 'Only the agent who recorded this close can undo it.',
    desk_reopen_closed_by: 'Recorded by {who} — only they can put it back on the market.',
    desk_status_no_reports: 'No problems reported — counts as active',
    desk_market_report_note_ph: 'Comment (optional) — a detail worth recording…',
    desk_status_change: 'Update status',
    desk_status_available_again: 'I was wrong / available again — clear my report',
    deal_close_title: 'Close the deal',
    deal_close_final_price: 'Final price',
    deal_close_final_commission: 'Final commission (amount)',
    // 0449 — the field above is the WHOLE fee; this panel says what reaches us.
    deal_close_cobroke_title: 'Split with the other agency',
    deal_close_cobroke_note: 'Recorded as our income: {net}. The full fee stays on the deal record.',
    deal_close_inherited_hint: 'Filled in from the price you asked in your cooperation terms — correct it to what was actually agreed, then confirm.',
    deal_close_no_own_terms: 'You have no recorded terms on this property, so nothing is filled in — enter the price and commission that were agreed.',
    deal_close_split_unreadable: 'The co-broke split does not say what share we keep — fix the ratio above before closing.',
    deal_close_no_property: 'This meeting has no property, and a closure is recorded against an ad. Attach the property first (details & editing → object), then close the deal.',
    desk_coop_join_action: 'Add your terms',
    desk_coop_no_terms_yet: 'no terms yet',
    deal_close_no_coop: 'No live cooperation on this property, and no owner contact we can name as the counterparty — add a contact first, or close just the meeting.',
    deal_close_will_record_coop: 'No cooperation was recorded on this property — closing will record one with {name} and end it as a closed deal. Pick the deal type:',
    deal_close_pick_listing: 'Which ad closed? A closure is recorded against one ad — it is never chosen for you.',
    deal_close_group_mine: 'Your ads',
    deal_close_group_others: "Other agents' ads",
    deal_close_group_done: 'Already closed',
    deal_close_from_meeting: 'This meeting was created from this ad',
    deal_close_outcome_sold: 'marks sold',
    deal_close_outcome_rented_out: 'marks rented out',
    deal_close_no_listing: 'No ad in scope for this deal, so a closure cannot be recorded against one. Open the deal from the property card and close it there.',
    deal_close_no_coop_hint: 'No cooperation recorded on this property — the final numbers are still saved with the closure.',
    deal_close_already_closed: 'This deal is already recorded as closed — nothing to record twice.',
    deal_close_no_client_notice: 'This meeting has no client on record. The deal will be counted, but attach the client to the meeting so it is recorded on them.',
    deal_close_deal_row_missing: 'Recorded and counted. No deal record could be created because the meeting has no client — attach the client and it will be linked.',
    deal_close_no_viewer: 'Nobody on this deal to credit — add the agents who worked it, then close.',
    deal_close_status_flip_failed: 'The deal IS recorded as closed, but the meeting status could not be updated — press close again to retry.',
    deal_close_bad_number: 'Price and commission must be numbers.',
    deal_close_class_closed: 'This kind of deal is already recorded as closed on this property.',
    deal_close_pick_intent: 'Pick the deal type first',
    deal_close_confirm: '✓ Confirm & close the deal',
    deal_close_we_closed: 'We closed the deal — record final terms',
    desk_status_log_hint: 'Earlier reports by this agent — kept as the log, never deleted',
    desk_coop_edit_terms: 'terms ✎',
    desk_coop_no_terms: 'no terms yet',
    // Three DISTINCT states (2026-07-29). "no terms yet" used to cover all of them, so a
    // colleague's earned-but-withheld terms read as "nobody has spoken to this owner" —
    // causing the duplicate work the participants list exists to prevent.
    desk_coop_terms_withheld: 'has terms — record yours to see them',
    // ⚠️ Says "ask", not "join", ON PURPOSE (2026-07-29). Self-join does not exist:
    // /participants requires isMgmt || isParticipant, so a non-participant adding
    // themselves gets a 403. The intended copy is "Join this cooperation to record your own
    // terms" — restore it the moment a join/invite affordance ships (the open workflow
    // decision in COOPERATION_VISIBILITY_SPEC §7). Until then it would instruct an
    // impossible action, which is the exact dishonesty this whole pass removed.
    desk_coop_join_to_record: 'You are not part of this cooperation. Ask management or a participant to add you, then you can record your own terms.',
    desk_coop_view_only: 'You can view this cooperation, but not change it.',
    desk_coop_you: 'you',
    desk_coop_leave: 'step back',
    desk_coop_leave_title: 'Remove yourself from this cooperation. It continues without you; nothing is deleted.',
    desk_coop_exclusive_held_by: 'negotiated by',
    desk_coop_none_yet: 'None yet.',
    desk_coop_remove: 'remove',
    desk_coop_agent_party_id_ph: 'agent ID',
    desk_coop_participant_hidden: 'another agent',
    desk_coop_unnamed_property: 'unnamed property',
    desk_coop_unnamed_contact: 'unnamed contact',
    desk_coop_share_pct_ph: 'share %',
    desk_coop_add_participant: '+ Add',
    desk_coop_save_button: 'Save changes',
    desk_coop_open_button: 'Open cooperation',
    desk_contact_view_add_tooltip: 'Contacts — view / add',
    desk_contact_none: 'No contact on record.',
    desk_contact_role_missing: 'missing', desk_contact_role_missing_no_door: 'This meeting has no property to attach a contact to.',
    desk_contact_remove_confirm: 'Remove {name/phone} from this property\'s contacts?',
    desk_contact_add_phone_ph: 'Phone',
    desk_contact_add_name_ph: 'Name',
    desk_contact_notes_ph: 'Notes (what they asked, agreed follow-up…)',
    desk_contact_add_button: '＋ Add contact',
    desk_contact_hidden_by: 'A contact is on record — added by {name} (private to them)',
    desk_contact_hidden_some: 'A contact is on record — private to the agent who added it',
    desk_role_client: 'client',
    desk_role_owner: 'owner',
    desk_role_neighbor: 'neighbor',
    desk_role_keyholder: 'keyholder',
    desk_role_broker: 'broker',
    desk_role_relative: 'relative',
    desk_role_tenant: 'tenant',
    desk_role_other: 'other',
    desk_contact_unknown_fallback: 'Unknown',
    desk_contact_agent_badge: 'agent',
    desk_contact_via_agent_badge: 'their listing — contact this agent',
    // "Is this ad posted by an agent?" (0376). ONE question, two answers — 'no' means
    // not-an-agent and never asserts ownership, which contact_of role edges declare.
    desk_author_role_q: 'This contact is:',
    desk_author_role_unknown: 'Agent or owner? Not recorded',
    desk_author_role_yes: 'an agent',
    desk_author_role_no: 'the owner',
    // 4206 (owner 2026-11-03) — the third answer: the ad's number is the CURRENT TENANT, neither owner nor agent.
    desk_author_role_tenant: 'the tenant',
    // B1286 — the agent side is a DECLARATION, so it is confirmed before it is written.
    desk_author_role_agent_confirm_title: 'Declare an outside agent?',
    desk_author_role_agent_confirm_body: "You are declaring that whoever posted this ad is NOT the owner, "
      + 'but an agent from another agency representing this property. Colleagues will see this mark.',
    desk_author_role_agent_confirm_yes: 'Yes, an outside agent',
    desk_author_role_agent_confirm_cancel: 'Cancel',
    desk_author_role_marked: 'agent',
    // Per-contact question (2026-09-07). It NAMES THE AD, because on a flat merged from
    // two listings the row above may be one of two numbers and 'is this an agent?' alone
    // would not say which posting is being judged.
    desk_contact_role_ask: 'Is this an agent?',
    desk_contact_role_ask_on: 'Agent on {src}?',
    desk_contact_role_not_agent: 'not an agent',
    desk_author_role_disputed: 'disputed',
    desk_author_role_roster: 'on an agency roster',
    // The PORTAL's own words (B219), never ours — an agent is correcting this, so it
    // must read as a claim the site made and not as something we verified.
    desk_author_role_declared_owner: 'posted as an owner',
    desk_author_role_declared_agent: 'posted as an agent',
    desk_author_role_elsewhere: 'same number reported on {n} other properties',
    desk_author_role_face_tooltip: 'Reported as posted by an agent, not the owner',
    // Tooltip headings. "Colleagues" is literal — this source is always a named person who
    // called; the portal declaration and the agency roster get their own lines and say so.
    desk_author_role_tip_agent: '{n} colleague(s) report an agent posted this',
    desk_author_role_tip_owner: '{n} colleague(s) report this is not an agent',
    desk_author_role_tip_tenant: '{n} colleague(s) report this is the current tenant',
    desk_contact_tenant_since: 'tenant since {date} — closed rent deal',
    desk_contact_tenant_reported: 'a colleague reports this is the current tenant, not the owner',
    desk_author_role_tip_split: 'Disagreement — {yes} say agent, {no} say not',
    desk_author_role_hint: 'Tap to answer or change your answer',
    desk_contact_click_to_copy: 'Click to copy',
    desk_contact_copied: 'Copied ✓',
    desk_contact_call: 'Call',
    desk_contact_fb_profile: 'Open Facebook profile',
    desk_contact_unverified_tip: 'Also listed on the ad — not confirmed as this person’s number',
    desk_contact_whatsapp: 'WhatsApp',
    desk_contact_no_wa: 'No WhatsApp',
    desk_contact_no_wa_tip: 'Our WhatsApp lines found no WhatsApp on this number. Call or send an SMS.',
    desk_contact_sms: 'SMS',
    desk_contact_edit_tooltip: 'Edit this contact',
    desk_contact_remove_tooltip: 'Remove this contact',
    desk_contact_changed_from_origin: 'You changed this from the origin ad',
    desk_contact_yours_badge: '✎ yours',
    desk_lineage_fallback_title: 'Listing',
    desk_share_copy_link: 'Copy link',
    desk_share_copied: 'Copied',
    desk_share_copy_tooltip: 'Copy a link to this listing — paste into WhatsApp, Telegram, email…',
    desk_share_whatsapp_tooltip: 'WhatsApp this agent about this property (link pre-filled)',
    desk_lineage_couldnt_load: 'Couldn\'t load. Please try again.',
    desk_lineage_your_posting_section: 'YOUR POSTING (gaps filled from the origin ad)',
    desk_lineage_the_ad_section: 'THE AD (as posted)',
    desk_lineage_field_price: 'Price',
    desk_lineage_field_intent: 'Intent',
    desk_lineage_field_kind: 'Kind',
    desk_lineage_field_rooms: 'Rooms',
    desk_lineage_field_bedrooms: 'Bedrooms',
    desk_lineage_field_area: 'Area',
    desk_lineage_field_floor: 'Floor',
    desk_lineage_field_condition: 'Condition',
    desk_lineage_field_agent_read: 'Amenities (AI-read)',
    desk_lineage_field_agent_activity: 'Agent activity',
    desk_lineage_field_orpheus_note: 'Orpheus note',
    desk_agent_work_text: 'advert text read',
    desk_agent_work_photos: 'photographs checked',
    desk_agent_work_place: 'placed on the map',
    desk_agent_work_compose: 'description written',
    desk_agent_place_street: 'street',
    desk_agent_place_with_number: 'street + number',
    desk_agent_absent_tag: 'stated: none',
    desk_agent_conflict_tag: 'sources differ',
    desk_agent_tip_text_read: 'The advert text was read on {date} and {n} kinds of information were found. Facts marked ✦ come from it.',
    desk_agent_tip_text_unread: 'Nobody has read this advert text yet — missing facts here mean NOT CHECKED, not absent.',
    desk_agent_tip_photos_read: 'The photographs were examined on {date}. Anything found in them is marked ✦.',
    desk_agent_tip_photos_unread: 'The photographs have not been examined — nothing here was learned from the pictures.',
    desk_agent_tip_placed: 'The address was matched to a real street, so this property appears in street and map searches.',
    desk_agent_tip_not_placed: 'The address has not been matched to a street — this property may be missing from street and map searches.',
    desk_agent_tip_composed: 'A ready-to-post description was written on {date}.',
    desk_agent_tip_not_composed: 'No description has been written for posting yet.',
    desk_signal_tpe_title: 'Another agency holds this exclusively — do not reshare, co-broke is not free',
    desk_signal_coop_title: 'Owner offers co-broke',
    desk_signal_coop_declared: 'stated in the advert',
    desk_signal_role_conflict_title: 'The advert contradicts the declared role',
    desk_signal_role_conflict_body: 'The description says {prose}; the portal field says {portal}:',
    desk_role_agent: 'agent',
    desk_agent_not_checked: 'not checked',
    desk_agent_conflict_hint: 'Sources disagree — one says yes, another says no. Neither is hidden.',
    desk_agent_absent_hint: 'Checked, and the source states it is absent — not the same as never checked.',
    // ⚠️ STAYS "Building status". The rename to "Building age" was reverted the same day
    // (2026-07-24, owner). სტატუსი IS overloaded — listing lifecycle (active/paused/sold) vs
    // construction era — but "age" can only describe two of this field's three values:
    // მშენებარე (under construction) is a construction STATE, not an age. All three portals
    // (MyHome, SS, Korter) label the field სტატუსი with exactly these three values, so the
    // portals' word wins. Revisit only when property.year_built is actually populated — a
    // real year is an age; this categorical field is not.
    desk_lineage_field_build_status: 'Building status',
    desk_lineage_field_project: 'Project',
    // WHO recorded a fact (property_feature.source_kind). Needed by the CONFLICT popover,
    // which labels each disagreeing value by its origin. All six are here because the raw
    // token must never reach the screen — `owner` printing as "owner" is the exact failure
    // the vocabulary pass exists to stop (it still happens in the property timeline today).
    // CONFLICT row (board §9). ⚠️ "sources" counts the disagreeing RECORDED values only —
    // it must never read as "every source we looked at". A portal can emit a value we
    // deliberately don't record (the fill vocabulary is a superset of the observation one),
    // and that reading is invisible here; the footer that would say so is deferred until
    // unknownTags is persisted at ingest.
    // Keyword editor popover (owner ruling: chips describe, an editor registry edits).
    desk_kw_edit_title: 'Edit this filter',
    desk_kw_edit_find_district: 'find a district…',
    desk_kw_edit_no_options: 'no options',
    desk_kw_edit_on: 'on',
    desk_kw_edit_off: 'off',
    desk_conflict_flag: '{n} sources disagree',
    desk_conflict_title: 'Which is correct?',
    desk_conflict_confirm: "That's correct",
    desk_source_kind_scrape: 'From platform',
    desk_source_kind_agent: 'Agent',
    desk_source_kind_owner: 'Owner',
    desk_source_kind_registry: 'Registry',
    desk_source_kind_ai: 'AI',
    desk_source_kind_admin: 'Admin',
    desk_fact_fill_pill: 'fill in',
    desk_facts_project: 'Project series',
    desk_facts_year_built: 'Year built',
    desk_val_ask_on_call: 'Ask on this call',
    desk_facts_elevator: 'Lift',
    desk_facts_parking: 'Parking',
    desk_facts_heating: 'Heating',
    desk_facts_yes: 'Yes',
    desk_facts_no: 'No',
    desk_heat_central: 'Central',
    desk_heat_gas: 'Gas heater',
    desk_heat_electric: 'Electric',
    desk_heat_italian: 'Individual boiler',
    desk_heat_floor: 'Underfloor',
    desk_heat_none: 'No heating',
    desk_lineage_field_district: 'District',
    desk_district_dispute_title: 'District needs confirmation',
    desk_district_dispute_question: 'The ad\'s link says {declared}, but the map pin falls in {polygon}. Is {declared} correct?',
    desk_district_dispute_yes: '✓ Yes — {declared}',
    desk_district_dispute_no: '✗ No — {polygon}',
    desk_district_dispute_votes: 'Votes',
    desk_district_dispute_resolved: 'Confirmed: {name} ({d}:{p})',
    // B274: the card-face form. Deliberately the QUESTION, not a warning label — "district
    // unconfirmed" alone tells an agent the card is unreliable and gives them nothing to do.
    desk_district_dispute_chip: '{declared} or {polygon}?',
    // Shown on counts that are still a tally of loaded rows — the ones an agent sees climbing.
    desk_counts_loading_hint: 'Still counting — this number is rising as results load',
    desk_loc_section: 'Location',
    desk_crit_near_locations: 'Near',
    desk_crit_any_district: 'Any district (whole city)',
    desk_crit_location_note: 'Location, in their own words',
    desk_loc_copy_pitch: 'Copy pitch text for the client',
    desk_loc_copied: 'Copied!',
    desk_loc_set_pin: 'Set the exact map pin to reveal nearby metro, parks and malls',
    desk_loc_q_complex_title: 'Complex needs confirmation',
    desk_loc_q_complex_in: 'Is this apartment in {name}?',
    desk_loc_q_complex_near: 'It\'s close to {name} — is it in this complex?',
    desk_q_yes: '✓ Yes',
    desk_q_no: '✗ No',
    desk_q_skip: 'Don\'t know',
    desk_loc_unit_m: 'm',
    desk_loc_none_nearby: 'Nothing notable within walking distance',
    desk_loc_unit_km: 'km',
    // How the membership was established (0726) — a reader weighs 'the pin is close'
    // very differently from 'a colleague confirmed it', and 127 links are still over a
    // kilometre from their complex, so the label is not decoration.
    desk_cplx_basis_human: 'confirmed by a colleague',
    desk_cplx_basis_agent_vote: 'confirmed by a colleague',
    desk_cplx_basis_ad_named: 'the advert names it',
    desk_cplx_basis_proximity: 'from the location only',
    // ⚠️ `built` is the value 14 core complexes actually carry, and it had no key — the
    // banner rendered the literal `desk_cplx_status_built` on screen. Nothing caught it
    // for as long as the banner never drew (0726); the harness caught it on first render.
    desk_cplx_status_built: 'completed',
    desk_cplx_status_planned: 'planned',
    desk_cplx_status_under_construction: 'under construction',
    desk_cplx_status_partial: 'partially completed',
    desk_cplx_status_completed: 'completed',
    desk_lineage_captured: 'Saved {time}',
    desk_lineage_last_seen: '· last seen {time}',
    desk_lineage_price_unchanged: '· price unchanged since it was saved',
    desk_lineage_price_history_section: 'PRICE HISTORY',
    desk_lineage_contact_section: 'CONTACT — as listed on the ad, not necessarily the owner',
    desk_lineage_viewings_section: 'VIEWINGS',
    desk_lineage_viewing_count: '{n} viewing{s} · last {time}',
    desk_lineage_has_closed_deal: 'has closed deal',
    desk_lineage_cooperation_section: 'COOPERATION',
    desk_lineage_no_coop: 'None — and no main contact on record to open one with.',
    desk_lineage_meeting_section: 'MEETING',
    desk_lineage_posted_on_section: 'POSTED ON',
    desk_lineage_scraped_badge: '🌐 source listing',
    desk_lineage_origin_card_title: '🌐 source ad',
    desk_lineage_viewing_badge: 'viewing',
    // Step 5 display honesty (PUBLICATION_AND_OWN_LISTINGS, 0385): an ad WE created says
    // so instead of "scraped"; an observed origin with reposts hanging off it says what
    // it is the source OF.
    desk_lineage_published_badge: '✎ our posting',
    desk_lineage_source_of_reposts: '🌐 source of {n} reposts',
    desk_lineage_cov_price: 'price',
    desk_lineage_cov_active_total: 'active / total postings',
    desk_lineage_cov_origin: 'source',
    desk_lineage_latest: 'latest',
    desk_lineage_older_postings: '+{n}',
    desk_lineage_our_reposts: 'our reposts · {n} agents',
    desk_blocked_line: 'Blocked by {portal}:',
    desk_blocked_deleted: 'removed from the account',
    desk_lineage_facts_differ: 'facts differ ({n})',
    desk_lineage_admin_dot: 'large price divergence or stale posting inside',
    desk_lineage_captured_by_n: '{n} agents',
    desk_fact_rooms: 'rooms',
    desk_fact_area: 'area',
    desk_fact_floor: 'floor',
    desk_fact_condition: 'condition',
    desk_lineage_youre_viewing: 'you\'re viewing this',
    desk_lineage_open_link: '↗ Open',
    desk_lineage_fb_posts: 'posts',
    desk_lineage_fb_groups: 'groups',
    desk_coop_offer_tooltip: 'The owner offers cooperation in the ad itself — click to open a cooperation with this split prefilled',
    desk_coop_offer_of_price: 'of price',
    desk_lineage_edit_link: '✎ Edit',
    desk_lineage_close_button: 'Close',
    desk_lineage_edit_property_button: '✎ Edit',
    desk_lineage_my_version_section: 'MY VERSION — facts you recorded',
    // 0439 (2026-08-06): the old copy said "a NEWER source disagrees". It was wrong in
    // both directions — the disagreeing source is usually OLDER (the origin ad), and the
    // conflict is normally between two PORTALS, not between the agent and time. Name the
    // portal instead; {portal}/{value} are filled by tLog.
    desk_my_version_drift_tooltip: '{portal} says {value} — check which is right',
    desk_my_version_drift_tooltip_nosrc: 'Another source says {value} — check which is right',
    // 0444 side-by-side. "sources", never "portals": commit_claim supersedes by
    // (source_party_id, source_kind), so an agent reposting to three sites writes one
    // channel and the last portal wins — see the migration header.
    desk_portal_facts_section: 'WHAT EACH SOURCE SAYS',
    desk_conflicts_section: 'CONFLICTS — WHICH TO KEEP?',
    desk_conflicts_keep_tip: 'Keep this value (records your decision)',
    desk_conflicts_note: 'A machine picked the current value; click the correct one to settle it.',
    desk_conflicts_failed: 'could not save',
    desk_portal_facts_disputed: 'they disagree',
    desk_portal_facts_agree: 'agree',
    desk_portal_facts_canonical_tip: 'The value the system uses',
    desk_portal_facts_reshare_tip: 'A repost — the same ad copied, not a second opinion',
    desk_portal_facts_note: 'One row per source. A repost is marked, because it copies the ad rather than observing the flat.',
    // Claim attribute labels. Looked up as desk_attr_<claim.attribute> via tOr(), which
    // falls back to the humanised column name, so a new attribute renders readably on the
    // day it is introduced rather than breaking. Keys mirror core_ingest CLAIM_FIELDS.
    desk_attr_rooms: 'Rooms',
    desk_attr_bedrooms: 'Bedrooms',
    desk_attr_bathrooms: 'Bathrooms',
    desk_attr_area_sqm: 'Area (m²)',
    desk_attr_land_area_sqm: 'Land area (m²)',
    desk_attr_kitchen_area: 'Kitchen area (m²)',
    desk_attr_floor: 'Floor',
    desk_attr_floors_total: 'Floors in building',
    desk_attr_ceiling_height: 'Ceiling height (m)',
    desk_attr_year_built: 'Year built',
    desk_attr_parking_deeded: 'Parking registered separately',
    desk_attr_cls_new_built: 'New building',
    desk_attr_cls_old_built: 'Old building',
    desk_attr_living_room_area_sqm: 'Living room (m²)',
    desk_attr_loggia_area_sqm: 'Loggia (m²)',
    desk_attr_storeroom_area_sqm: 'Storeroom (m²)',
    desk_attr_porch_area_sqm: 'Porch (m²)',
    desk_attr_building_material: 'Material',
    desk_attr_metro_station: 'Metro station',
    desk_attr_has_balcony: 'Balcony',
    desk_attr_balcony_count: 'Balconies',
    desk_attr_balcony_area_sqm: 'Balcony area (m²)',
    desk_attr_has_terrace: 'Terrace',
    desk_attr_condition: 'Condition',
    desk_attr_building_status: 'Building status',
    desk_attr_project: 'Project',
    desk_attr_power_capacity_kw: 'Power (kW)',
    desk_attr_functional_zone: 'Functional zone',
    desk_stack_open_tooltip: 'Open details — every posting of this property in one view',
    desk_lineage_view_posting_tooltip: 'Show this posting\'s own captured data',
    desk_lineage_your_reposts_section: 'Your postings',
    desk_lineage_scroll_to_my_version_tooltip: 'Jump to your recorded version below',
    desk_type_client: 'Client',
    desk_type_properties: 'Properties',
    desk_type_property: 'Property',
    desk_type_meeting: 'Meeting',
    desk_type_listings: 'Listings',
    desk_type_business: 'Business',
    desk_listing_intent_sale: 'For sale',
    desk_listing_intent_rent: 'For rent',
    desk_listing_intent_daily: 'Daily rent',
    // 0219. "Girao" is kept untranslated on purpose: the platforms' own English
    // renderings ("Leasehold mortgage", "Lease") each describe a DIFFERENT western
    // instrument, and every agent using this app says გირავდება anyway.
    desk_listing_intent_girao: 'Girao',
    desk_listing_intent_ijara: 'Leased out (ijara)',
    desk_intent_opt_girao: 'girao',
    desk_fieldlabel_price_from: 'price from',
    desk_fieldlabel_price_to: 'price to',
    desk_fieldlabel_currency: 'currency',
    desk_fieldlabel_area_from: 'area from',
    desk_fieldlabel_area_to: 'area to',
    desk_fieldlabel_rooms_from: 'rooms from',
    desk_fieldlabel_rooms_to: 'rooms to',
    desk_fieldlabel_bedrooms_from: 'bedrooms from',
    desk_fieldlabel_bedrooms_to: 'bedrooms to',
    desk_fieldlabel_districts: 'districts',
    desk_fieldlabel_excluded_districts: 'excluded districts',
    desk_fieldlabel_excluded_complexes: 'excluded complexes',
    desk_fieldlabel_complexes: 'complex',
    desk_fieldlabel_must_haves: 'must-haves',
    desk_fieldlabel_nice_to_haves: 'nice-to-haves',
    desk_fieldlabel_must_not_haves: 'must-not-haves',
    desk_fieldlabel_condition: 'condition',
    desk_fieldlabel_condition_exclude: 'condition exclude',
    desk_fieldlabel_drive_time: 'drive time',
    desk_fieldlabel_mortgage: 'mortgage',
    desk_fieldlabel_pets: 'pets',
    desk_fieldlabel_children: 'children',
    desk_status_pending: 'Pending',
    desk_status_not_saved: 'Not saved',
    desk_status_saved: 'Saved',
    desk_status_rejected: 'Rejected',
    desk_status_edited: 'Edited',
    desk_status_failed: 'Failed',
    desk_status_archived: 'Archived',
    desk_status_on_market: 'On market',
    desk_status_off_market: 'Off market',
    desk_entry_new_client: 'New client',
    desk_entry_client_update: 'Client update',
    desk_entry_changed_fields: 'changed: {fields}',
    desk_entry_client_closed: 'Client closed',
    desk_entry_closed_from_desk: 'closed from Data Desk',
    desk_entry_client_reopened: 'Client reopened',
    desk_entry_back_on_active_list: 'back on your active list',
    desk_entry_meeting_fallback: 'Meeting',
    desk_entry_new_candidate: 'New candidate',
    desk_entry_property_fallback: 'Property',
    // Cadastral card (core 1555 / lib core_cadastral.js). "Official address" is the registry's
    // own wording for the parcel; for most rural stock it names no street, and that is correct.
    cad_section: 'Cadastral record',
    cad_code: 'Cadastral code',
    cad_truncated_from: 'from unit code',
    cad_official_address: 'Official address',
    cad_parcel_area: 'Parcel area',
    cad_sqm: 'sq m',
    cad_area_mismatch: 'differs from the outline',
    cad_parcel_type: 'Parcel type',
    cad_ownership_type: 'Ownership type',
    cad_previous_code: 'Previous number',
    cad_our_pin: 'Our pin',
    cad_pin_inside: 'inside the parcel',
    cad_pin_outside: 'outside the parcel',
    cad_pin_none: 'no pin',
    cad_open_registry: 'Open in the Public Registry',
    street_q_title: 'Which street is this?',
    street_q_thanks: 'Saved - thank you',
    area_section: 'Area',
    area_within: 'Within',
    area_decided_by: 'Decided by',
    area_basis_house_point: 'the resolved address (building)',
    area_basis_house_point_over_stated: 'the resolved address - the advert names another area',
    area_basis_stated_area: 'the area the advert names',
    area_basis_street_in_area: 'its street lies in this area',
    area_basis_both: 'the advert and its street agree',
    area_advert_says: 'Advert says',
    area_pin_inside: 'inside the area',
    area_pin_outside: 'outside the area',
    desk_entry_listing_fallback: 'Listing',
    desk_entry_rooms_abbrev: '{rooms} rm',
    desk_entry_bedrooms_abbrev: '{bedrooms} bd',
    desk_entry_area_abbrev: '{area} m²',
    // Compact fact tokens (lineage header subtitle + POSTED ON diff line). One meaning per
    // symbol: the slash is floor-of-total ONLY — bedrooms carries its own unit.
    desk_compact_area: '{area}m²',
    desk_compact_rooms: '{rooms}rm',
    desk_compact_bedrooms: '{bedrooms}bd',
    desk_compact_floor: 'fl {floor}',
    desk_compact_floor_of: 'fl {floor}/{total}',
    desk_compact_floors_total: '{total} floors',
    desk_compact_bathrooms: '{n} bath',
    desk_matched_member_one: 'Matched: {facts}',
    desk_matched_member_multi: 'Matched ({n}): {facts}',
    desk_matched_member_title: '{n} of {total} postings match the filter',
    desk_entry_land_area_abbrev: 'plot {area} m²',
    desk_entry_floor_position: 'floor {floor}/{total}',
    desk_entry_floor_only: 'floor {floor}',
    desk_entry_floor_range: 'floors {min}–{max}',
    desk_entry_floor_range_of: 'floors {min}–{max}/{total}',
    desk_dev_left_of: '{left} left of {total}',
    desk_facet_seller_whose: 'Seller',
    desk_facet_contact: 'Contact',
    desk_facet_agent_represents: 'Agent acts for',
    desk_sellerkind_private_owner: 'Private owner',
    desk_sellerkind_developer: 'Developer',
    desk_sellerkind_bank: 'Bank',
    desk_sellerkind_other_company: 'Other company',
    desk_sellerkind_unknown: 'Unknown',
    desk_contactrole_owner_direct: 'Owner directly',
    desk_contactrole_agent: 'Agent',
    desk_contactrole_company_rep: 'Company representative',
    desk_contactrole_unknown: 'Unknown',
    desk_seller_likely: 'likely',
    desk_seller_agent_for: 'acting for',
    desk_seller_likely_note: 'Owner / agent is inferred from how many adverts the author has — shown as “likely”.',
    desk_dev_on_sale: '{n} on sale',
    desk_floor_plan: 'Floor plan',
    desk_floor_plan_floor: 'Floor {n}',
    desk_floor_plan_mine: 'this card',
    desk_floor_plan_free: 'on sale',
    desk_floor_plan_sold: 'sold / not offered',
    desk_floor_plan_none: 'The developer publishes no floor plan for this building',
    desk_range_price_header: 'Price',
    desk_facet_ppm: 'Price per m²', desk_ppm_min: 'min', desk_ppm_max: 'max',
    desk_range_area_header: 'Area',
    desk_range_area_building: 'Living area m²',
    desk_range_land_area: 'Land / yard m²',
    desk_entry_by_agent: 'by {agent}',
    desk_entry_scraped_by: 'saved by {agent}',
    desk_entry_ad_by: 'ad by {agent}',
    desk_entry_price_inherited: 'inherited from the source listing — no price was set at posting',
    desk_price_observed_as: 'converted at today\'s NBG rate — the ad says {observed}',
    desk_entry_address_inherited: 'address inherited from the source listing — not set at posting',
    desk_entry_possible_match: '🔗 possible match — click to continue this',
    desk_entry_matched_contact: '🔗 matched this contact',
    desk_entry_my_property_manual: '✎ my property · manually added',
    desk_entry_my_property_ai: '✎ my property · AI auto-filled',
    desk_entry_my_property_scraped: '✎ my property · saved',
    desk_entry_scraped_listing_caption: '🌐 saved listing',
    // Capture provenance (2026-08-03). The two *_scraped/_caption strings above are now the
    // UNKNOWN fallback only — kept because ~22k active listings predate the crawler stamping
    // its session and honestly cannot be attributed either way.
    desk_entry_my_property_added_by: '✎ my property · added by {agent}',
    desk_entry_scraped_listing_added_by: '🌐 added by {agent}',
    // {bot} is the crawler's name and it is DATA, not a string — it arrives on the row
    // from crawler_generation (migration 0388). Today it resolves to Argo (the ship sailed to
    // Colchis to find something valuable and bring it back, which is the job description; it
    // reads as a name in the same slot a colleague's name occupies, and collides with no
    // agent on the roster). When the crawler is replaced, the successor generation renames
    // only what IT captures — hardcoding 'Argo' here would retro-rename the whole archive.
    // A label only: no party row is ever minted for a generation (core/DECISIONS.md:345,
    // ACTOR_MODEL capability gate).
    desk_entry_my_property_crawler: '✎ my property · found by {bot}',
    desk_entry_scraped_listing_crawler: '🌐 found by {bot}',
    desk_entry_scraped_listing_form: '🌐 filled in by the owner',
    desk_entry_my_property_form: '✎ my property · filled in by the owner',
    desk_entry_platform_ads_pick: 'Which ad?',
    desk_entry_platform_ads_undated: 'no date',
    desk_entry_you_edited: '✏️ you edited this',
    desk_entry_ai_created: '🤖 AI-created',
    desk_entry_filled_via_link: '🔗 Filled via link',
    desk_entry_from_whatsapp: '💬 Offered in a WhatsApp chat — check and add',
    desk_entry_reposted: 'reposted · {datetime}',
    desk_entry_seen_by_me: 'seen',
    desk_entry_seen_by_me_at: 'You opened this on {datetime}',
    desk_entry_seen_by_me_times: 'Opened {n} times in total',
    desk_entry_added: 'added {date}',
    desk_entry_updated: 'updated {date}',
    // The ad's OWN dates vs ours (core 0228). "posted" = the portal states when the
    // ad went up; "renewed" = the portal only states a republish/bump (korter), so
    // the true original may be older; "first seen" = the portal states nothing and
    // all we honestly know is when our crawler found it. Three separate strings on
    // purpose — one word for all three is how the crawl date got mistaken for the
    // posting date in the first place.
    desk_entry_posted: 'posted {date}',
    desk_entry_bumped: 'renewed {date}',
    desk_entry_first_seen: 'first seen {date}',
    desk_entry_first_seen_hint: 'this portal does not publish a posting date — this is when we found it',
    // The card's single date line (owner 2026-08-03): market · acquired · checked.
    // "acquired" = when OUR system got it (listed_at — crawler or agent); "checked" =
    // when we last confirmed it, changed or not (last_seen_at, observe_listing 0047).
    desk_entry_acquired: 'with us {date}',
    desk_entry_acquired_hint: 'when our system acquired it (Argo or an agent)',
    desk_entry_checked: 'checked {date}',
    desk_entry_checked_hint: 'when we last checked this ad — whether anything changed or not',
    // Units for the card date line's elapsed-time form ("posted 21d · with us 3d").
    desk_age_today: 'today',
    desk_age_unit_d: 'd',
    desk_age_unit_mo: 'mo',
    desk_age_unit_y: 'y',
    desk_entry_open: 'open',
    desk_entry_thread_updates: '🧵 {n} updates',
    desk_entry_stack_count: '{n} ×',
    desk_thread_noun_listings: 'listings',
    desk_thread_noun_updates: 'updates',
    desk_entry_thread_generic: '🧵 {n} {listings/updates}',
    desk_entry_group_tooltip: '{n} {listings/updates} — click to open the group',
    desk_entry_click_see_more: 'Click to see more',
    desk_entry_no_locations: 'No entries with a precise location to pin.',
    desk_meetingcard_fallback_title: 'Meeting',
    desk_meetingcard_property_fallback: 'Property',
    desk_meetingcard_listing_link: 'listing ↗',
    desk_meetingcard_no_time: 'no time set',
    desk_meeting_filter_all: 'All',
    desk_meeting_filter_active: 'Active',
    desk_meeting_filter_finished: 'Finished',
    desk_meeting_in_status_prefix: 'in {status}:',
    desk_meetings_no_match: 'No meetings match these filters.',
    desk_meeting_status_scheduled: 'Scheduled',
    desk_meeting_status_pending: 'Pending',
    desk_meeting_status_binder: 'Binder',
    desk_meeting_status_closed: 'Deal closed',
    desk_meeting_status_failed: 'Failed',
    desk_meeting_status_rescheduled: 'Postpone…',
    desk_meeting_status_cancelled: 'Called off',
    desk_meeting_state_rescheduled: 'Postponed',
    meeting_edit_property: 'Change property',
    meeting_edit_time: 'Change time',
    meeting_property_search_ph: 'Address, owner name, or phone number…',
    meeting_property_search_hint: 'Type at least 3 characters — searches every property, not just yours.',
    meeting_property_search_none: 'Nothing matched.',
    meeting_time_save: 'Save time',
    meeting_time_clear: 'Clear the time',
    meeting_status_note_ph: 'e.g. the client will decide by tomorrow and write on WhatsApp',
    meeting_status_note_save: 'Save',
    // 0427 — meeting purpose (chosen at creation, one tap)
    meeting_purpose_label: 'Purpose',
    meeting_purpose_suggested: 'This would be visit #{n} for this client',
    desk_meeting_purpose_first_viewing: 'First viewing',
    desk_meeting_purpose_repeat_viewing: 'Repeat viewing',
    desk_meeting_purpose_negotiation: 'Negotiation',
    desk_meeting_purpose_signing: 'Signing',
    desk_meeting_purpose_final_viewing: 'Final viewing',
    desk_meeting_purpose_inspection: 'Inspection',
    desk_meeting_purpose_owner_meeting: 'Owner meeting',
    desk_meeting_purpose_key_handover: 'Key handover',
    desk_meeting_purpose_documents: 'Documents',
    desk_meeting_purpose_other: 'Other',
    // 0427 — status reasons (required for failed/cancelled, optional otherwise)
    meeting_next_action_label: 'Follow-up:',
    meeting_reason_required_hint: 'pick a reason',
    desk_meeting_reason_client_no_show: 'Client didn’t show',
    desk_meeting_reason_client_cancelled: 'Client cancelled',
    desk_meeting_reason_owner_cancelled: 'Owner cancelled',
    desk_meeting_reason_agent_unavailable: 'Agent unavailable',
    desk_meeting_reason_client_changed_mind: 'Client changed mind',
    desk_meeting_reason_price_disagreement: 'Price disagreement',
    desk_meeting_reason_wrong_expectations: 'Wrong expectations',
    desk_meeting_reason_not_interested: 'Not interested',
    desk_meeting_reason_chose_another_property: 'Chose another property',
    desk_meeting_reason_client_thinking: 'Client is thinking',
    desk_meeting_reason_waiting_bank: 'Waiting for bank',
    desk_meeting_reason_waiting_owner: 'Waiting for owner',
    desk_meeting_reason_client_requested: 'Client asked to move it',
    desk_meeting_reason_owner_requested: 'Owner asked to move it',
    desk_meeting_reason_agent_conflict: 'Agent had a conflict',
    desk_meeting_reason_property_unavailable: 'Property unavailable',
    desk_meeting_reason_external_circumstances: 'External circumstances',
    // 0559 (B651): the meeting was put off and nobody knows when — the case agents were
    // filing as [ჩაიშალა] + free text, which claims the viewing happened and failed.
    desk_meeting_reason_postponed_no_date: 'Postponed, no date yet',
    desk_meeting_reason_closed_with_deal: 'Closed with the deal',
    desk_meeting_reason_property_gone: 'Property gone — rented / sold to another client',
    desk_deal_visits: 'Deal closed · {n} visit(s)',
    desk_deal_participants: 'participants',
    desk_deal_closed_commission: 'commission',
    desk_deal_closed_no_figures: 'closed — no figures recorded',
    deal_close_figures_required: 'Enter the price and the commission — a closed deal records what it closed on.',
    deal_close_cobroke_missing: 'The split with {name} is not recorded — the whole fee would be counted as ours.',
    deal_close_cobroke_missing_how: 'Record it on the meeting first (the commission-split panel under that agent), then close.',
    deal_figures_edit: 'Correct the figures',
    deal_close_coop_terms_hint: "Cooperation terms on this flat: {pct}% of {base} = {amount} {cur}. The box above takes the amount.",
    deal_close_percent_guard: "{pct} looks like the percentage, not the amount: {pct}% of {base} is {amount} {cur}. Correct the amount, or press close again if {pct} really is the fee.",
    deal_figures_edit_reason: 'Why (optional)',
    desk_meeting_follows_previous: 'follows a previous visit',
    desk_meeting_row_created_by: 'booked by',
    meeting_superseded_title: 'Followed by a later visit',
    meeting_superseded_hint: 'Outcome, money, people and the date are recorded on the current visit ({when}). This one keeps its comments and attendance.',
    meeting_superseded_open: 'Open the current visit',
    meeting_superseded_actions: 'Actions live on the current visit — use the door above.',
    meeting_superseded_refused: 'This visit was followed by a later one — record it on the current visit.',
    meeting_not_related: 'You are not on this meeting — only the people on it, or management, can change it.',
    desk_nudge_why_property_gone: 'The flat went to another client — tell yours',
    desk_meeting_reason_other: 'Other',
    // 0427 — follow-up nudges (banner + bell)
    desk_meeting_nudges_title: '{n} meetings need an update',
    // Designer system 2026-08-06: per-tier lines. Critical is the box header;
    // high collapses behind its count; medium is a SENTENCE, never a list.
    desk_nudge_tier_critical: '{n} meetings — time passed, record the result',
    desk_nudge_tier_high: '{n} pending — follow-up date passed',
    desk_nudge_more_medium: '{n} more meetings without a reason — shrinks as you update',
    desk_nudge_open: 'Open',
    desk_nudge_hidden_by_view: '{n} more await an answer — hidden by this view. Show them',
    desk_nudge_open_failed: 'That meeting is no longer in your list — it may have been deleted.',
    meeting_status_ask_title: 'Status: “{status}” — why?',
    meeting_status_ask_date_title: 'When should we remind you?',
    meeting_status_note_title: 'Meeting details — anything that will help on the day',
    meeting_binder_title: 'Binder — the figures',
    meeting_binder_amount: 'Amount',
    meeting_binder_expires: 'Valid until',
    meeting_binder_taken: 'Taken on',
    meeting_binder_amend_reason: 'Why the change',
    meeting_binder_amend_reason_ph: 'e.g. client paid 200 less in cash',
    meeting_binder_settle: 'What happened to it',
    meeting_binder_forfeit_why: 'The deposit is being kept. Why?',
    binder_status_applied: 'Applied to price',
    binder_status_returned: 'Returned',
    binder_status_forfeited: 'Forfeited',
    binder_status_cancelled: 'Cancelled',
    meeting_binder_price: 'Agreed price',
    meeting_binder_remaining: 'left to pay: {amount}',
    meeting_binder_no_remaining: 'different currencies — no subtraction',
    meeting_binder_split: 'Who is holding it',
    meeting_binder_required_hint: 'amount and deadline are required',
    meeting_binder_split_hint: 'the split must total 100%',
    meeting_binder_share_blank_hint: 'give every holder a share, or remove the row',
    meeting_binder_summary_expired: 'expired',
    meeting_binder_short: 'Binder',
    meeting_binder_note_ph: 'e.g. receipt signed, balance due at the notary',
    meeting_binder_holder_owner: 'Owner',
    meeting_binder_holder_agent: 'Agent',
    meeting_binder_holder_agency: 'Agency',
    meeting_binder_holder_client: 'Client',
    meeting_binder_holder_other: 'Other',
    desk_nudge_why_binder_expired: 'Binder expired — closed or fallen through?',
    meeting_prep_note_ph: 'e.g. wants to see the parking; coming with their spouse',
    meeting_reason_required_badge: 'required',
    meeting_reason_optional_badge: 'optional',
    meeting_followup_tomorrow: 'Tomorrow',
    meeting_followup_3days: 'In 3 days',
    meeting_followup_friday: 'On Friday',
    meeting_purpose_editable: 'you can change it',
    meeting_purpose_suggested_was: 'Suggested: {label}',
    desk_meeting_visit_n: 'visit #{n}',
    meeting_edit_section: 'Details & editing',
    meeting_record_result: 'Record the result',
    meeting_outcome_ask: 'What happened at the meeting? Pick one:',
    desk_nudge_why_meeting_not_updated: 'Meeting passed — what happened?',
    desk_nudge_why_next_action_date_passed: 'Follow-up date passed — update it',
    desk_nudge_why_pending_stale: 'Pending a long time — any news?',
    desk_nudge_why_missing_reason: 'Add the reason — why this status?',
    // Deposit rewritten after first save (owner ruling B, 2026-08-06). Neutral wording on
    // purpose: most of these will be honest corrections, and the row must not accuse. It
    // reports WHAT changed and leaves the judgement to whoever reads it.
    desk_binder_changed: 'Deposit amount changed',
    // An expiry-only change is a DIFFERENT statement and must not borrow the amount's
    // wording — see the render note in NotificationsPanel.
    desk_binder_deadline_changed: 'Deposit deadline changed',
    desk_binder_changed_someone: 'someone',
    desk_nudge_why_missing_next_action: 'Pending — set a follow-up date',
    desk_nudge_waiting_client: 'waiting on client',
    desk_nudge_waiting_owner: 'waiting on owner',
    desk_nudge_waiting_agent: 'waiting on agent',
    desk_nudge_waiting_bank: 'waiting on bank',
    meeting_guest_add: 'Add someone who is going',
    meeting_guest_badge: '· attending only',
    meeting_guest_remove: 'Take off the meeting',
    meeting_guest_search_ph: 'Agent name…',
    // The chain (2b) vs the duplicates band (6a). Two words on purpose: one asserts an order,
    // the other refuses to. Never make them synonyms.
    desk_chain_title: 'Chain',
    desk_chain_dupes_title: 'Duplicates',
    desk_chain_unknown_first: 'Which came first — unknown. These postings match; nothing says who copied whom.',
    desk_chain_n_sources: '{n} source',
    desk_chain_n_reposts: '{n} repost',
    desk_chain_n_platforms: '{n} platform',
    desk_chain_source_tag: 'source',
    desk_chain_closed_n: '{n} closed listings',
    desk_chain_agents_title: 'Agents in the chain',
    desk_chain_n_agents: '{n} agent',
    // Bare LABELS for the aligned state column (design 1c). Separate keys from the chip
    // strings above on purpose: a chip says 'checked today', a table row says 'checked' and
    // puts the value in its own column — folding the date into the label would break the
    // alignment the column exists for.
    desk_entry_posted_label: 'posted',
    desk_entry_bumped_label: 'renewed',
    desk_entry_first_seen_label: 'first seen',
    desk_entry_acquired_label: 'with us',
    desk_entry_checked_label: 'checked',
    desk_state_active: 'On market',
    desk_state_closed: 'Off market',
    desk_state_owner_ads: "Owner's ads",
    // Owner ruling 2026-09-02: this row shows the owner's ACTIVE ads, and a platform whose ad has
    // ended is still shown but LABELLED, not merely greyed. Before that the two were the same
    // shape of dot, so a card under the "1 platform" filter looked like it contradicted it.
    desk_state_owner_ads_ended: 'ended',
    desk_state_seen: 'Seen by',
    desk_state_seen_me: 'me ✓',
    desk_meeting_deal_sale: 'sale',
    desk_meeting_deal_rent: 'rent',
    desk_search_gallery_view_tooltip: 'Gallery view — big photos, click to switch',
    desk_search_map_view_tooltip: 'Map view — pins for every entry with a location',
    desk_map_expand_tooltip: 'Enlarge map — hide side panels',
    desk_map_collapse_tooltip: 'Shrink map — show side panels',
    // Hover on a corpus dot whose brief came back empty, or failed. Deliberately not
    // "error": from the agent's side both mean the same thing — click to see the card.
    desk_map_pin_unknown: 'Click to open',
    desk_search_debug_tooltip: 'Double-click: search compiler debug',
    desk_search_placeholder: 'Search — try: 2 room vake under 200k…',
    desk_search_placeholder_client: 'Search clients — name, phone, or criteria: 2 room vake under 200k…',
    desk_search_placeholder_meeting: 'Search meetings…',
    desk_search_placeholder_business: 'Search businesses…',
    desk_search_placeholder_leads: 'Search leads — district or address…',
    desk_type_leads: 'Leads',
    // ── Client leads (0462/0463) — DEMAND inside the Leads pool ──────────────
    // Owner ruling 2026-08-06: same pool, same type tree as our own work.
    desk_type_client_leads: 'Clients',
    desk_cl_pickup: 'Pick up',
    desk_cl_picking: 'Picking up…',
    // The claim confirmation. Claiming is EXCLUSIVE and has no undo on this card, so the
    // dialog names the two things a mis-tap actually does — the client enters YOUR book and
    // leaves the shared pool — instead of asking "are you sure", which tells the reader
    // nothing they can weigh. (The „ნომრის ნახვა" keys this replaced are gone: the number
    // is now the standard ContactPill, which carries its own label.)
    desk_cl_confirm_title: 'Take this client?',
    desk_cl_confirm_body: 'Save {name} to your clients?',
    desk_cl_confirm_note: 'They move into your clients and leave the shared pool, so no one else can take them.',
    desk_cl_confirm_ok: 'Take the client',
    desk_cl_taken: 'Another agent picked this up first.',
    desk_cl_empty: 'No unclaimed clients right now. New ones land here as they arrive.',
    desk_cl_whole_city: 'Anywhere in the city',
    // ⚠️ TEMPLATES, not bare words. Georgian marks these with POSTPOSITIONS
    // („$400-დან"), so a prefixed English word renders as „-დან $400" — backwards
    // and unreadable. The placeholder lets each language put the value where its
    // grammar needs it. Read through tLog, never concatenated at the call site.
    desk_cl_from: 'from {v}',
    desk_cl_upto: 'up to {v}',
    desk_cl_movein: 'moving in',
    desk_cl_people: 'people',
    desk_cl_pets: 'has pets',
    desk_cl_urgent: 'Urgent',
    desk_cl_no_budget: 'Budget not stated',
    desk_cl_yours: 'yours now',
    desk_cl_you: 'you',
    // Says WHY there is no number on the card, so the absence reads as a rule rather
    // than as missing data: the reveal door is requirement-scoped (0157 invariant 6).
    desk_cl_pickup_hint: 'Pick up to see the phone number',
    desk_cl_pickup_title: 'Pick this client up — the number appears right here',
    desk_cl_no_number: 'No number on file',
    desk_cl_no_budget_said: 'not stated',
    desk_cl_no_criteria: 'No criteria filled in — they were not asked',
    desk_cl_matches: '{n} matches',
    // ── Provenance (0471) — WHOLE sentences, never assembled from parts ──────
    // Georgian marks the agent's name with a case ending and puts the verb elsewhere;
    // a translated word concatenated in front of a name is wrong in every language here.
    desk_cl_prov_ad: 'Filled it in themselves · Facebook form · {campaign}',
    desk_cl_prov_fbgroup: 'Found on Facebook · the client posted this in a group',
    desk_cl_prov_link: 'Filled it in themselves · client form · link sent by {agent}',
    desk_cl_prov_link_anon: 'Filled it in themselves · client form',
    desk_cl_prov_typed: 'Typed in by {agent} — the client’s words, retold',
    desk_cl_prov_legacy: 'Source not recorded — we don’t know who brought them in, or how',
    // ── Confidence tier — a STATE, not a score. Never a percentage. ──────────
    desk_cl_tier_self: 'client’s own words',
    desk_cl_tier_self_tip: 'Read the criteria literally',
    desk_cl_tier_agent: 'agent’s note',
    desk_cl_tier_agent_tip: 'Approximate — already filtered through a person',
    desk_cl_tier_legacy: 'source unknown',
    desk_cl_tier_legacy_tip: 'Treat with caution, probably stale',
    // ── State-column row labels ─────────────────────────────────────────────
    desk_cl_row_budget: 'Budget',
    desk_cl_row_filled: 'Filled in',
    desk_cl_row_added: 'Added',
    desk_cl_row_campaign: 'Campaign',
    desk_cl_row_link_sent: 'Link sent by',
    desk_cl_row_typed: 'Typed by',
    desk_cl_row_rep: 'Represented by',
    desk_cl_v_rent: 'Renting',
    desk_cl_v_buy: 'Buying',
    desk_cl_v_short_term_rent: 'Short stay',
    desk_cl_v_buy_or_rent: 'Buying or renting',
    desk_cl_v_apartment: 'Apartment',
    desk_cl_v_house: 'House',
    desk_cl_v_cottage: 'Cottage',
    desk_cl_v_commercial: 'Commercial',
    desk_cl_v_office: 'Office',
    desk_cl_v_land: 'Land',
    // The `requirement.property_category` CHECK allows nine values; these three were
    // missing, so `tOr` fell back to the raw code and printed an English word in the
    // middle of an otherwise all-Georgian line. Found by RENDERING harness case H4
    // ("ყიდულობს · garage · მთელი ქალაქი") — no gate can see it. Measured live at the
    // time: garage 1 row, `other` 11 — and `other` is the second most common category
    // in the whole table, so this was not an edge case.
    // ⚠️ Keep this list equal to that CHECK constraint. If a tenth value is added there,
    // it silently prints its own code here.
    desk_cl_v_garage: 'Garage',
    desk_cl_v_hotel: 'Hotel',
    desk_cl_v_other: 'Other',
    // The name is the card's anchor, so its EMPTY state is load-bearing. This used to
    // borrow `dv_no_client` ("No client" / „უკლიენტო") from the deals view, where it
    // means the deal has no client attached. On a client lead that is a false statement:
    // the client exists — they are the whole card — and it is their NAME we were never
    // given. Found by rendering harness case H5.
    desk_cl_no_name: 'No name given',
    desk_rail_drafts_n: '{n} to review',
    desk_rail_drafts_sub: 'AI-found drafts are waiting.',
    desk_type_favorites: 'Favorites',
    desk_fav_add: 'Add to favorites',
    desk_fav_remove: 'Remove from favorites',
    desk_fav_empty: 'No favorites yet — tap ♡ on any property card.',
    // 🗄 Archive (0375) — MY shelf. The wording must never imply the property left
    // anyone else's feed, because it didn't.
    desk_archive_add: 'Hide from my feed',
    desk_archive_remove: 'Return to my feed',
    // NOT "Archive" (owner-caught 2026-08-02): this panel already shows an Archive — the
    // company-wide lead lifecycle — a few pixels above, and one word doing two jobs in one
    // sidebar is unreadable. "Hidden" also says what the shelf actually does.
    desk_archive_view: 'Hidden',
    desk_hidden_here_n: '{n} hidden in this view',
    desk_more: 'More',
    desk_hidden_show: 'Show',
    desk_hidden_hide_again: 'Hide again',
    desk_archive_empty: 'Nothing hidden — tap the eye on a card to hide it from your own feed.',
    desk_archive_note: 'Only you see this. The property stays in everyone else’s feed.',
    desk_leads_loading: 'Loading leads…',
    desk_leads_empty: 'Nothing new — the auto-finder hasn\'t found new owner listings yet.',
    desk_lead_claim: 'Work this lead',
    desk_lead_claimed: 'Claimed',
    desk_lead_claim_yours: 'You are working this lead',
    desk_lead_release: 'Release',
    desk_lead_no_phone: 'No phone yet — open the ad and reveal it',
    desk_lead_has_phone: 'Number found',
    desk_lead_claims_n: '{n} agent(s) working it',
    // The ownership LENS over the Leads inventory (SEARCH_GRAMMAR §3b). Deliberately
    // NOT 'My leads' — the lead is not owned, the CLAIM is: these are the ones this
    // agent holds a live claim on, and releasing one removes it from here.
    desk_lead_view_mine: 'Working',
    desk_lead_view_all: 'All leads',
    desk_lead_view_hint: 'Leads you hold a live claim on',
    // B109: 'engaged' is shown, never hidden — an agent who can see the owner is
    // already being talked to spends their next call somewhere better.
    // B111 one-tap outcomes. Short enough to sit inline next to a phone number —
    // these are buttons an agent presses while still holding the phone.
    desk_attempt_hint: 'What happened? One tap — this is what stops the same owner being called ten times.',
    desk_touch_nudge: '{n} revealed numbers with no outcome yet',
    // Outcome density, never attention. "{n} called · {x} no answer" reads as
    // still-available; "seen by {n}" would read as too-late. That is the point.
    desk_lead_density_tried: '{n} called · {x} no answer',
    desk_lead_density_warm: '{n} interested',
    desk_lead_density_taken: '{n} say they have an agent',
    desk_lead_density_hint: 'Based on {n} calls that were actually reported — not on who looked.',
    desk_coop_end_why: 'Why is this ending?',
    desk_coop_end_deal_done_by_us: 'We sold / rented it',
    desk_market_reason_deal_done_by_other: 'Sold / rented',
    desk_gone_sold: 'Report sold',
    desk_gone_rented: 'Report rented',
    desk_gone_nocoop: 'No coop',
    desk_confirm_yes: 'Yes',
    desk_confirm_no: 'No',
    desk_gone_nocoop_hint: 'The owner does not work with agencies: report it (can be withdrawn in the status card)',
    desk_gone_nocoop_confirm: 'Sure the owner does not cooperate with agencies?',
    desk_card_open: 'Open details',
    desk_search_label: 'Search the Desk',
    desk_search_category_label: 'Search category',
    desk_gone_sold_hint: 'The owner says it is sold: report it (you can undo this in the status card)',
    desk_gone_rented_hint: 'The owner says it is rented: report it (you can undo this in the status card)',
    desk_gone_sold_confirm: 'Are you sure it is sold? It will stop being offered to clients.',
    desk_gone_rented_confirm: 'Are you sure it is rented? It will stop being offered to clients.',
    desk_gone_failed: 'Could not save',
    desk_market_reason_owner_exclusive_elsewhere: 'Exclusive elsewhere',
    desk_market_reason_owner_declined_agent: 'Owner declined',
    desk_status_reported_pill: 'Reported',
    desk_status_we_closed_short: 'We closed it',
    desk_status_retract_short: 'Withdraw my report',
    desk_coop_end_deal_done_by_other: 'Sold / rented by the owner or another agency',
    desk_coop_end_owner_exclusive_elsewhere: 'Another agency has it exclusively',
    desk_coop_end_owner_declined_agent: 'Owner declines to cooperate',
    desk_coop_end_cooperation_stale: 'Owner still wants help — this cooperation ended',
    desk_coop_end_unknown: 'Other',
    // Lead archival is OWNED BY THE COOPERATION LIFECYCLE: ending a cooperation applies
    // `end_reason_policy` for its reason, and `archives_lead` is what kills the lead.
    // Without this key every closed-deal lead badge rendered the literal key string.
    // ⚠️ Do NOT read that as "record_deal_closure archives the lead" — it writes no
    // property_lead row, so grepping it for one returns nothing and reads as a missing
    // feature (that cost a review pass on 2026-07-31). The policy row is the authority.
    desk_coop_end_deal_closed: 'Deal closed',
    desk_coop_end_hint: 'This decides whether the property is archived or returns to leads. Only "does not want to work with me" holds you back from this owner — your colleagues are unaffected. If YOU closed the deal, record it from the meeting instead — that is what books the commission.',
    desk_lead_archive: 'Archive',
    desk_lead_active: 'Active leads',
    desk_lead_reported_gone: 'Reported sold/rented by {n}',
    // Market report (0258) — recorded from the LISTING, where the agent actually calls
    // the owner from. Framed as "what did you learn", not "why are you ending this":
    // the fact stands on its own and needs no cooperation behind it.
    desk_market_report_open: 'Owner says it is gone / taken',
    desk_market_report_title: 'What did the owner tell you?',
    desk_known_deal_title: 'Do you know the price it went for? (optional)',
    desk_known_deal_sale: 'Sold',
    desk_known_deal_rent: 'Rented',
    desk_known_deal_price_ph: 'Sale price',
    desk_known_deal_rent_ph: 'Monthly rent',
    desk_known_deal_date: 'When the deal closed',
    desk_known_deal_hint: 'A real deal price, not an asking price — it is kept apart and makes our price comparisons more accurate. It is never shown publicly with your entry alone.',
    desk_known_deal_saved: 'The deal price is recorded.',
    desk_known_deal_failed: 'The report is saved, but the deal price was not recorded:',
    desk_market_report_hint: 'Recorded as your report, with your name. It lowers this property for everyone but never deletes it — several independent reports are what make it convincing.',
    desk_market_report_saved: 'Recorded. Thank you — the agency now knows.',
    desk_market_report_retract: 'I was wrong — it is still available',
    desk_market_report_mine: 'You reported this',
    desk_market_report_badge_title: 'An agent was told by the owner that this is gone. Unverified — it is ranked lower, never removed.',
    desk_lead_reported_hint: 'An agent reported this closed by someone else. Unverified — the lead stays open and simply ranks lower.',
    desk_coop_end_note_required: 'Please say what happened — "Other" without a note is unusable later.',
    desk_coop_excl_when: 'When does their exclusivity end? (the owner usually knows)',
    desk_coop_excl_until_sold: 'Until it sells',
    desk_lead_exclusive_until: 'Another agency until {d}',
    desk_lead_exclusive_until_sold: 'Another agency until it sells',
    desk_lead_evaluation: 'For valuation',
    desk_unsent_title: 'Valuations started, not sent', desk_unsent_hint: 'Visitors who filled the valuation on sentavus.com and left without sending it. Their answers are saved as they typed them; no property is created yet.',
    desk_unsent_min: 'min ago', desk_unsent_h: 'h ago', desk_unsent_d: 'd ago', desk_unsent_rooms: 'rooms', desk_unsent_plot: 'plot', desk_unsent_floor: 'floor',
    desk_unsent_more_answers: '+{n} more answers', desk_unsent_show_phone: 'Show phone', desk_unsent_no_contact: 'No contact left', desk_unsent_empty_answers: 'Nothing filled in yet',
    desk_unsent_load_more: 'Show {n} more', desk_unsent_open: 'Open', desk_unsent_answers: 'Answers',
    desk_unsent_contact: 'Contact', desk_unsent_yes: 'Yes', desk_unsent_no: 'No', desk_unsent_unsure: 'Not sure', desk_unsent_step: 'Stopped at',
    desk_unsent_finish: 'Edit and send in the form', desk_unsent_finish_hint: 'Opens the sentavus.com valuation form with everything this visitor filled in. Correct or complete it there and send it: it becomes a property and a lead exactly like a visitor’s own. Opening it is recorded, like showing the phone.',
    desk_unsent_finishing: 'An agent opened it to finish', desk_unsent_finish_failed: 'Could not open the form — try again', desk_unsent_load_failed: 'Could not load the answers',
    // the valuation form's own questions that are not in the intake catalogue (their wording lives in the site's form)
    desk_unsent_q_area_basis: 'Area measured as', desk_unsent_q_area_incl_balconies: 'Area includes balconies?', desk_unsent_q_area_sources: 'Area source',
    desk_unsent_q_balcony_area: 'Balcony area', desk_unsent_q_bedroom_area: 'Bedroom area', desk_unsent_q_building_done_now: 'Building finished now?',
    desk_unsent_q_building_era: 'Built in', desk_unsent_q_building_status: 'Building status', desk_unsent_q_building_year: 'Year built',
    desk_unsent_q_cellar_area: 'Cellar area', desk_unsent_q_cellar_in_price: 'Cellar included in the price?', desk_unsent_q_house_spaces: 'Spaces in the house',
    desk_unsent_q_parking_level: 'Parking level', desk_unsent_q_parking_sep_usd: 'Parking sold separately, USD',
    desk_lead_evaluation_hint: 'The owner asked for a valuation on sentavus.com — the answers, the estimate they saw and the photos are on the card.',
    desk_lead_watched_agent: 'Agent exclusive · {name}',
    desk_lead_watched_agent_hint: 'This agent has it on their own: we hold no owner advert of this flat. No automatic cooperation request goes to them.',
    desk_lead_cooldown: 'Back in the pool at {d}',
    desk_lead_cooldown_hint: 'The last cooperation on this property ended. It returns to the pool shortly.',
    desk_attempt_promoted: '· now matchable supply',
    desk_attempt_needs_coop: '· record the cooperation to make it matchable',
    desk_attempt_ambiguous: '· several cooperations here — set the right one to agreed',
    desk_attempt_no_answer: 'No answer',
    desk_attempt_interested: 'Interested',
    desk_attempt_refused: 'Refused',
    desk_attempt_already_taken: 'Has an agent',
    desk_attempt_callback_requested: 'Call back',
    desk_attempt_wrong_number: 'Wrong number',
    desk_attempt_language_barrier: 'Language',
    desk_attempt_agreed: 'Agreed',
    desk_lead_in_conversation: 'In conversation — {n}',
    desk_lead_in_conversation_hint: 'Someone already has a cooperation with this owner. It becomes matchable supply only when that cooperation reaches “agreed”.',
    desk_lead_status_promoted: 'Promoted — cooperation recorded',
    desk_lead_status_dead: 'Dead',
    desk_facet_category: 'Category',
    desk_facet_location: 'Location',
    desk_facet_agent: 'Agent',
    desk_facet_agent_search: 'Find an agent…',
    desk_facet_agent_none: 'No agents match',
    desk_facet_agent_inactive: 'No longer with the agency',
    desk_facet_agent_show_inactive: 'Include former agents ({n})',
    desk_facet_agent_hide_inactive: 'Hide former agents',
    desk_facet_source: 'Source',
    // Design 2b §3. The reason, not just the word "unavailable": an agent who used the
    // Portal filter this morning must learn WHICH inventory answers it, not merely that
    // this one does not.
    desk_facet_source_unavailable: 'unavailable — leads only',
    // The mobile facet sheet's reason when the chosen identity cannot answer this question.
    // Phrased like the ghost chips — "not asked", never "disabled": the question was dropped
    // on purpose, the control is not broken.
    // The generic line is the FALLBACK, used when no kind is chosen or the kind has no
    // template below (a multi-kind selection has no single subject to name).
    desk_facet_not_for_identity: 'not asked for this type',
    // Per-KIND templates (designer 2026-08-18: name the cause, not the rule). One whole
    // sentence each rather than a composed phrase — Georgian inflects the possessor, and a
    // native speaker can check four strings but not a rule. {facet} arrives as the facet's
    // own title, in the nominative.
    desk_facet_not_for_kind_land: 'land has no {facet}',
    desk_facet_not_for_kind_commercial: 'commercial space has no {facet}',
    desk_facet_not_for_kind_house: 'a house has no {facet}',
    desk_facet_not_for_kind_apartment: 'an apartment has no {facet}',
    // Mobile search field resting state (Call A / A3): the field says HOW MANY, the readout
    // row under it says which. Never the words — they are already 8px below.
    desk_search_field_filter_count: '{n} filters',
    desk_search_field_clear: 'Clear the search',
    desk_facet_details: 'Details',
    // Features facet (B37): must-have amenities, answered whole-corpus by p_feature_codes.
    // The hint states the shelf rule, because that is the part an agent cannot infer from
    // a checkbox: an unrecorded amenity is a phone call, not a rejection.
    // 0433 — the state-of-the-flat facet. `condition` is the facet, `reno`/`frame` its two rows.
    desk_facet_condition: 'Condition',
    desk_facet_condition_reno: 'Renovation',
    desk_facet_condition_frame: 'Frame',
    desk_facet_condition_help: 'What does “frame” mean?',
    desk_facet_condition_unknown_note: '6,045 listings have no condition recorded — they move to possible matches and never drop out of the results.',
    desk_facet_features: 'Features',
    // 0756 — the four-state feature filter. These name the CURRENT state, not the action:
    // the control shows what is being asked, and the tooltip appends what the next click does.
    desk_feature_state_unset: 'not asked',
    desk_feature_state_yes: 'has it',
    desk_feature_state_no: 'does NOT have it (recorded as absent)',
    desk_feature_state_unknown: 'not declared — nobody has said',
    desk_feature_state_next: 'click to change',
    desk_feature_state_clear: 'click the name to clear',
    desk_feature_state_no_unavailable: 'no absence has ever been recorded for this feature',
    desk_facet_features_comfort: 'Comfort',
    desk_facet_features_kitchen: 'Kitchen',
    desk_facet_features_planning: 'Layout',
    desk_facet_features_orientation_view: 'View',
    desk_facet_features_orientation: 'Orientation',
    desk_facet_features_defect: 'Defects',
    desk_facet_features_furniture: 'Furniture & appliances',
    desk_facet_features_neighborhood: 'Neighbourhood',
    desk_facet_features_environment: 'Environment',
    desk_facet_features_communication: 'Connectivity',
    desk_facet_features_status: 'Status',
    desk_facet_features_property_type: 'Property type',
    desk_facet_features_location_character: 'Area character',
    desk_facet_features_commercial: 'Commercial',
    desk_facet_features_financial: 'Financial',
    desk_facet_features_finance: 'Payment & finance',
    desk_facet_features_tenant_terms: 'Tenant terms',
    desk_facet_features_renovation: 'Renovation',
    desk_facet_features_listing_quality: 'Listing quality',
    desk_facet_features_motivation: 'Motivation',
    desk_facet_features_location: 'Location',
    desk_facet_features_cooperation: 'Cooperation',
    desk_facet_features_amenity: 'Amenities',
    desk_facet_features_utility: 'Connections',
    desk_facet_features_security: 'Security',
    desk_facet_features_garage: 'Parking & garage',
    desk_facet_features_land: 'Land',
    desk_facet_features_other: 'Other',
    desk_facet_features_outdoor: 'Outdoor space',
    desk_facet_features_building: 'Building',
    desk_facet_features_utilities: 'Utilities & heating',
    desk_facet_features_appliances: 'Appliances',
    desk_facet_features_occupancy: 'Occupancy',
    desk_facet_features_defects: 'Recorded defects',
    // Card-face defect chips. `frames` is 1-BASED for the reader — `photo_index` is
    // 0-based in the claim and an agent counting photographs starts at one.
    desk_defect_frames: 'photo {frames}',
    desk_defect_from_sibling: 'seen in another advert for the same flat',
    // ⚠️ NOT "not stated in the advert", which is what this said until the first probe
    // against real rows. Idmon reads the advert's PROSE: the sample that caught it carries
    // the quote „აქვს აივანი" — the advert says it in words, and the portal's structured
    // balcony field is empty. What the group actually means is that the SOURCE FIELD was
    // empty and an agent filled it, which is the owner's own sentence.
    desk_lineage_agent_found: 'Filled by an agent — the source field was empty',
    // ⚠️ This note is the control's honesty, not decoration. The facet is presence-only, so a
    // tick means "a defect WAS recorded here" and never "hide the ones that have it" - and an
    // empty answer means nobody has looked yet, not that the flats are sound.
    desk_facet_features_defects_note: 'Read from the photographs. A tick finds listings where this WAS recorded — it does not hide them. Nothing recorded is not the same as nothing there.',
    desk_facet_wet_points: 'Bathrooms',
    desk_facet_balcony_count: 'Balconies',
    desk_range_balcony_count: 'balconies',
    desk_owner_contact_email: 'Email',
    desk_attr_owner_email: 'Owner email',
    desk_facts_land_unit: 'Unit',
    desk_unit_sqm: 'm²',
    desk_unit_ari: 'ares',
    desk_unit_ha: 'hectares',
    desk_val_year_invalid: 'Enter a whole year from {min} to {max}.',
    desk_facts_year_invalid: 'Year built must be a whole year between 1850 and six years from now — fix it or clear it to save.',
    desk_save_warn_title: 'Saved, but some of it did not land:',
    desk_save_warn_unknownTags: 'Features not recognised',
    desk_save_warn_unknownFeatures: 'Features that could not be stored',
    desk_save_warn_implausible: 'Values rejected as implausible',
    desk_save_warn_invalid: 'Invalid values',
    desk_save_warn_derivedSkipped: 'Computed features, not stored',
    desk_save_warn_nonFeature: 'Not a property feature',
    desk_save_warn_listingStateSkipped: 'Offer terms skipped (no price yet)',
    desk_save_warn_needsPolicyStance: 'Needs a stance in restrictions',
    desk_save_warn_claimFailed: 'Facts that failed to save',
    desk_save_warn_policyWriteFailed: 'Restrictions that failed to save',
    desk_save_warn_giraoTermsFailed: 'Girao terms that failed to save',
    desk_save_warn_petTermsFailed: 'Pet terms that failed to save',
    desk_save_warn_subtypeUnknown: 'Unknown property subtype',
    desk_save_warn_cadastralConflict: 'Cadastral code conflicts with another property',
    desk_save_warn_retract_refused: 'Cleared fields that were not removed',
    desk_save_warn_features_not_yours: 'Cleared answers recorded by someone else (kept)',
    desk_save_warn_features_retract_error: 'Cleared answers could not be removed',
    desk_pm_nav_label: 'Sections',
    desk_pm_group_record: 'The record',
    desk_pm_group_in: 'Getting information in',
    desk_pm_group_work: 'Working the property',
    desk_pm_nav_property: 'Property',
    desk_pm_nav_terms: 'Asking terms',
    desk_pm_nav_facts: 'Facts',
    desk_pm_nav_photos: 'Photos',
    desk_pm_nav_owner: 'Owner',
    desk_pm_nav_observed: 'Observed from',
    desk_pm_nav_paste: 'Paste / AI',
    desk_pm_nav_agreement: 'Agency agreement',
    desk_pm_nav_conditions: 'Conditions',
    desk_pm_nav_meeting: 'Meeting',
    desk_pm_nav_more: 'Notes · history',
    desk_pm_status_done: 'answered',
    desk_pm_status_needs: 'needs you',
    desk_pm_status_empty: 'empty',
    desk_pm_go_to_field: 'go to field',
    desk_observed_from_hint: 'Applies to everything in this save — choose it before you record.',
    desk_feature_view_unanswered: 'Unanswered first',
    desk_feature_view_ai: 'AI-filled',
    desk_feature_view_disagreeing: 'Disagreeing',
    desk_feature_view_empty: 'Nothing in this view.',
    desk_feature_disagree_mark: 'sources disagree',
    desk_pm_required: 'required',
    desk_pm_agent_only: 'agent-only',
    desk_pm_nav_business: 'Businesses here',
    desk_pm_nav_better: 'Better to know',
    desk_pm_nav_notes: 'Private notes',
    desk_pm_nav_assumed: 'Assumed values',
    desk_pm_nav_promises: 'Promises',
    desk_pm_nav_avail: 'Available from',
    desk_pm_nav_posted: 'Posted on',
    desk_pm_nav_dups: 'Possible duplicates',
    desk_pm_nav_timeline: 'Timeline',
    desk_pm_gaps_n: '{n} to fill',
    desk_pm_discard_confirm: 'Close without saving? Unsaved changes, queued files and an unparsed paste will be lost.',
    desk_pm_retry_uploads: 'Retry upload ({n})',
    desk_pm_photos_need_version: 'These photos go on your own version of this property — complete the form and press Record to save it.',
    desk_pm_land_area_hint: 'The listing gave {n} m² as its area — enter it here as the plot area if that is the plot.',
    desk_pm_subtype_label: 'Subtype',
    desk_batch_history_title: 'Updates from pasted messages',
    desk_batch_title: 'Update {n}',
    desk_batch_n_add: '{n} additions',
    desk_batch_n_conflict: '{n} conflicts',
    desk_batch_n_remove: '{n} removals',
    desk_batch_n_ambiguous: '{n} unclear',
    desk_batch_n_unchanged: '{n} unchanged',
    desk_batch_n_pending: '{n} to decide',
    desk_batch_show_text: 'Original message',
    desk_batch_hide_text: 'Hide message',
    desk_batch_col_field: 'Field',
    desk_batch_col_current: 'Current',
    desk_batch_col_proposed: 'Proposed',
    desk_batch_col_decision: 'Decision',
    desk_batch_kind_add: 'new',
    desk_batch_kind_conflict: 'conflict',
    desk_batch_kind_remove: 'withdrawn',
    desk_batch_kind_ambiguous: 'unclear',
    desk_batch_kind_unchanged: 'unchanged',
    desk_batch_correction: '· says the earlier value was wrong',
    desk_batch_removed: 'remove',
    desk_batch_decision_accept: 'accepted',
    desk_batch_decision_keep: 'kept current',
    desk_batch_decision_unresolved: 'unresolved',
    desk_batch_decision_reject: 'skipped',
    desk_batch_decision_undone: 'undone',
    desk_batch_undo: 'Undo',
    desk_batch_reconsider: 'Accept after all',
    desk_pm_private_version: 'Another agent created this property. What you save here is your own version: only you see it, and it does not change their record. Ask them to share the property if you should work on it together.',
    desk_batch_no_proposal: 'The message was read, but no update could be prepared from it. Nothing was changed. Try pasting it again.',
    desk_batch_add: 'Add',
    desk_batch_skip: 'Skip',
    desk_batch_keep: 'Keep current',
    desk_batch_use_new: 'Use new',
    desk_batch_unresolved: 'Unresolved',
    desk_batch_remove: 'Remove',
    desk_batch_no_quote: 'No supporting sentence was returned for this field.',
    desk_batch_current_source: 'Current value from: {source}{portal}',
    desk_batch_hide_unchanged: 'Hide unchanged',
    desk_batch_show_unchanged: 'Show {n} unchanged',
    desk_batch_stale_title: 'Changed since this review — check before applying:',
    desk_batch_apply_anyway: 'Apply anyway',
    desk_batch_undo_changed_title: 'A later update changed these fields since this one:',
    desk_batch_undo_changed_row: 'accepted {accepted}, now {now} ({source})',
    desk_batch_undo_anyway: 'Undo anyway',
    desk_batch_leave: 'Leave it',
    desk_batch_undo_continue: 'Continue',
    desk_batch_failures: 'Not saved, still open: {list}',
    desk_batch_not_undoable: 'Not undone: {list}',
    desk_batch_undo_all: 'Undo this whole update',
    desk_batch_apply: 'Apply selected changes ({n})',
    desk_prov_accepted_by: 'accepted by {name}',
    desk_prov_history: 'History',
    desk_feature_capture_title: 'All features',
    desk_feature_capture_ai: 'AI',
    desk_feature_capture_search: 'Find a feature… (e.g. fireplace, garden)',
    desk_feature_capture_hint: 'Only the answers you change are saved. Leaving a feature blank means not stated, never no.',
    desk_feature_capture_cap: 'More than {n} feature answers in one save are refused — save now, then continue.',
    desk_facts_material_unset: '— not stated —',
    desk_facts_areas_more: 'Other areas',
    desk_feature_capture_group_bathroom: 'Bathroom',
    desk_facts_parking_deeded: 'Parking space registered separately',
    desk_facet_features_hint: 'Listings where nobody recorded this go to possible matches, not out of the results.',
    // Terms facet (B177): exclude-known-bad filters + commission sort
    // ⚠️ `desk_facet_terms` is now UNUSED by the panel — turn 4 split that facet into
    // `desk_facet_who` (the axis rows) and `desk_facet_rental_terms` (advance + move-in),
    // because one title over two questions is what let a money dropdown sit under a
    // sentence about who may live there. Kept, not deleted: a saved-search label or an
    // older client may still resolve it, and a missing key renders as its own identifier.
    desk_facet_terms: 'Terms & restrictions',
    desk_facet_who: 'Who is it for',
    // 0431 — the modifier under the team-activity rows.
    desk_footprint_keep_mine: 'plus the ones I work',
    desk_footprint_keep_mine_chip: '+ mine',
    // 6a's dashed door — pull the axes from a client the system already knows.
    desk_who_have_client: 'Have a client on file?',
    desk_who_fill_from_client: 'Fill this from a client',
    desk_who_client_search_placeholder: 'Client name…',
    desk_who_client_none: 'No match in your loaded clients.',
    // ⚠️ The bound, not a disclaimer: "she has no profile" and "she is older than this
    // list" lead an agent to do different things next.
    desk_who_client_scope_note: 'Searching the clients loaded in your feed.',
    desk_who_client_empty: 'Nothing recorded about who will live there for this client yet.',
    desk_who_client_error: 'Could not load that client.',
    desk_facet_rental_terms: 'Terms',
    // 6b — the derived-from-a-client card. Every string here exists to keep ONE distinction
    // visible: an axis nobody answered is UNKNOWN, not "no restriction".
    desk_lens_from_named: 'Derived from {name}’s profile.',
    desk_lens_from_unnamed: 'Derived from this client’s profile.',
    desk_lens_counts: '{known} axes derived · {silent} not asked, and not assumed.',
    desk_lens_known: 'Known · filtering on this',
    desk_lens_nobody_asked: 'Nobody asked · not filtering',
    desk_lens_unknown_not_unrestricted: 'These are unknown, not unrestricted. A place that bans pets still appears — you haven’t said whether this client has one.',
    desk_lens_ratio: 'Filtering on {on} of {all} axes. The other {off} narrow nothing.',
    desk_lens_answer_now: 'Answer these now',
    desk_lens_complete_named: 'Complete {name}’s profile',
    desk_lens_complete_unnamed: 'Complete the client’s profile',
    // Deal-change carry-over (design 4b) — the pool banner's twin.
    desk_deal_changed_kept: 'Deal changed. Your other answers were kept.',
    desk_deal_undo: 'Undo deal change',
    // B842: the client fill replaces the whole search, so it announces itself and offers the
    // way back — the same contract the pool switch has. {name} is the client.
    desk_client_fill_applied: 'Search filled from {name}',
    desk_client_fill_replaced: 'the filters you had were replaced',
    desk_client_fill_undo: 'Undo — put my filters back',
    desk_filter_restrict_hint: 'My client is / has — confirmed matches first; listings with nothing recorded go to possible matches, worth a call:',
    // "Who is it for" — the restriction axis rows (design handoff desk-search 6a, 2026-08-05)
    desk_who_intro: 'Describe the person who would live there. Anything you leave alone is not asked — it never narrows the search.',
    desk_who_not_asked: 'not asked',
    desk_who_row_standard: 'Standard restrictions',
    desk_who_row_other: 'Other',
    desk_who_asking_for: 'Asking for:',
    desk_who_axes_silent: 'axes not asked',
    desk_who_shelf_note: 'Places whose rules can’t answer this land on the possible-matches shelf — never dropped.',
    desk_filter_advance_max: 'At move-in, max',
    desk_filter_advance_chip: 'Move-in',
    desk_filter_advance_month_unit: 'mo',
    desk_filter_minlease_stay: 'Client stays',
    desk_filter_minlease_chip: 'Stays',
    desk_facet_minlease: 'Stay length',
    desk_filter_movein_by: 'Move in by',
    desk_filter_movein_chip: 'in by',
    desk_filter_movein_hard: 'must be — hide the rest (otherwise they go to possible matches)',
    desk_filter_terms_honesty_note: 'Hides only listings where the restriction or term is actually on record — unrecorded ones still show.',
    // 0249 — date range. The four options are four different questions; the labels say
    // WHOSE date it is, because "date added" and "date posted" differ by who did it.
    desk_facet_dates: 'Date',
    // 2026-07-30 label pass — mirrors the ka block (see comment there for why `updated` keeps
    // its change-wording): added = capture into OUR db, last_seen = crawler liveness check.
    desk_filter_date_added: 'Added to system',
    desk_filter_date_posted: 'Posted on portal',
    desk_filter_date_posted_or_added: 'Posted on portal (≈)',
    desk_filter_date_last_n_days: 'Last {n} days',
    desk_filter_date_posted_unknown: '{n} flats carry no posting date at all — they stay in possible matches rather than being hidden.',
    desk_filter_date_substitute: 'When the portal gives no date, use the day we added it',
    desk_filter_date_last_seen: 'Last verified',
    desk_filter_date_updated: 'Last changed',
    // FACET 07B (2026-09-06): the dates facet's own strings
    desk_dates_hint_added: 'ours',
    desk_dates_hint_last_seen: 'crawler',
    desk_dates_hint_updated: 'advert',
    desk_dates_hint_portal: 'portal',
    desk_dates_hint_empty_pct: '{pct}% empty',
    desk_dates_last_n: 'last {n} days',
    desk_dates_today: 'today',
    desk_dates_unit: 'unit',
    desk_dates_unit_days: 'days',
    desk_dates_unit_custom: 'other…',
    desk_dates_substitute: 'Substitute the unknown',
    desk_dates_substitute_off: 'Off — undated adverts stay in the middle column. Turning it on changes the question, it does not refine it.',
    desk_dates_substitute_on: 'On — where the portal gives no date, the day we added the advert stands in for it (marked ≈).',
    desk_filter_date_from: 'From',
    desk_filter_date_to: 'To',
    desk_filter_date_posted_note: '14% of listings carry no posting date. Those are shown with the day we added them, marked ≈ — which reads newer than the truth, because we only see an ad once we have found it. Untick to search the published date alone.',
    // 0249 — lead workload + attention
    desk_facet_leadwork: 'Assigned & viewed',
    // B215 (0262): the exposure ladder. Stop labels are RUNG NAMES; the slider's at-most
    // semantics live in the track, and the chip prefixes '≤'.
    desk_facet_exposure: 'Exposure',
    desk_exposure_label: 'Touched at most',
    desk_exposure_0: 'Untouched',
    desk_exposure_1: 'Viewed',
    desk_exposure_2: 'Being worked',
    desk_exposure_all: 'All',
    desk_exposure_hint2: 'Our own reposts are excluded',
    // 0294 — footprint replaces the exposure ladder's control (the exposure_* keys above
    // stay: saved searches still carry the value and still chip it).
    // 0303/2026-07-30 restructure. The sections are named for the DECISION the agent is
    // making, not for the data's origin. 'Exposure' was a marketing word and ambiguous
    // (the property's market exposure, or ours?); 'Seller & motivation' claimed to measure
    // motivation when every control in it is a proxy.
    desk_facet_ourwork: 'Our work',
    desk_scope_everyones: "Everyone's work",
    desk_pool_searching_in: 'Searching in',
    desk_pool_everyones: "everyone's",
    desk_pool_yours: 'yours',
    desk_pool_mine: 'Mine',
    desk_pool_state: 'Status',
    // Why the active/archive row is ABSENT rather than empty, said on the row itself.
    // Two different absences: a client lead has no such lifecycle at all, and the ♥/🗄
    // lenses replace the pipeline instead of filtering it, so the axis has nothing to act on.
    desk_pool_state_none_clientlead: 'a client lead leaves this pool the moment it is picked up',
    desk_pool_state_none_lens: 'your own shelf — not a state of the inventory',
    // The type axis INSIDE the Leads pool (0462) — objects vs clients, the same
    // question our own work asks one section lower.
    desk_pool_type: 'Type',
    desk_facet_leadstate: 'Lead status',
    // 'Footprint' was metaphorical; the agent wants to know whether a colleague has
    // already looked at this card.
    // 2026-07-31 microcopy pass. Rule adopted: every facet label must be answerable as a
    // spoken question an agent would ask another agent. A label that reads like a column
    // name or an internal metric is carrying weight the reader has to translate.
    // Option rows are one or two words, never sentences — they are scanned, not read.
    // Stage 2 (SEARCH_GRAMMAR §5b) — the tree's Category level and apartment's status axis.
    desk_cat_residential: 'Residential',
    desk_cat_commercial: 'Commercial',
    desk_cat_land: 'Land',
    desk_status_new: 'Newly built',
    desk_status_old: 'Old build',
    desk_status_constr: 'Under construction',
    // New developments — the developers' own inventory (core 1634, DevelopmentsPanel). Desk-only for now.
    desk_dev_title: "New developments — the developers' own flats",
    desk_dev_projects: 'projects',
    desk_dev_available: 'available',
    desk_dev_reserved: 'reserved',
    desk_dev_sold: 'sold',
    desk_dev_layouts: 'layouts',
    desk_dev_loading: 'Loading new developments…',
    desk_dev_error: 'New developments could not be loaded.',
    desk_dev_show_all: 'Show all',
    desk_dev_show_less: 'Show less',
    desk_dev_back: 'All projects',
    desk_dev_developer_site: "Developer's site",
    desk_dev_no_plan: 'No plan',
    desk_dev_rooms: 'rooms',
    desk_dev_bedrooms: 'bedrooms',
    desk_dev_balcony: 'balcony',
    desk_dev_on_offer: 'on offer',
    desk_dev_pick_floor: 'Pick a floor',
    desk_dev_floor: 'Floor',
    desk_dev_room: 'Room',
    desk_dev_catalogue: "Developer's published plans",
    desk_dev_plan_matched: 'Plan matched by size split',
    desk_dev_price_switched_off: 'Prices hidden by the developer',
    desk_dev_price_not_published: 'Developer publishes no prices',
    desk_dev_price_none: 'No price on the flats on offer',
    desk_dev_nothing_on_offer: 'Nothing on offer',
    desk_dev_status_available: 'available',
    desk_dev_status_reserved: 'reserved',
    desk_dev_status_sold: 'sold',
    desk_dev_status_not_offered: 'not offered',
    desk_dev_status_unknown: 'status unknown',
    desk_dev_plan_single: 'single payment',
    desk_dev_plan_list: 'list price',
    desk_dev_plan_30_70: '30% now, 70% on completion',
    desk_dev_plan_instalment_0pct: '0% instalment',
    desk_dev_plan_instalment: 'instalment',
    desk_dev_plan_site_converted: "developer's ₾ conversion",
    // B265 — subtype tokens (SEARCH_GRAMMAR.md §5). Keys must cover every token in
    // SUBTYPES_BY_KIND or the chip renders its raw key (the desk_footprint_chip bug below).
    desk_subtype_agricultural: 'Agricultural',
    desk_subtype_non_agricultural: 'Non-agricultural',
    desk_subtype_hotel: 'Hotel',
    desk_subtype_production_space: 'Production space',
    desk_subtype_office: 'Office',
    desk_subtype_universal_space: 'Universal space',
    desk_subtype_warehouse: 'Warehouse',
    desk_subtype_restaurant_cafe: 'Restaurant / cafe',
    desk_subtype_retail: 'Retail',
    desk_subtype_garage: 'Garage',
    desk_subtype_parking_space: 'Parking space',
    desk_subtype_non_residential_space: 'Non-residential space',
    desk_subtype_special_purpose: 'Special purpose',
    desk_subtype_cottage: 'Cottage',
    desk_subtype_country_house: 'Country house',
    desk_subtype_townhouse: 'Townhouse',
    desk_subtype_villa: 'Villa',
    desk_footprint_label: 'Team activity',
    // The chip prefix. MISSING until the 2026-07-30 audit — t() returns the raw key when
    // a string is absent, so the chip rendered "desk_footprint_chip: ..." on screen. Same
    // shape as the desk_coop_end_deal_closed badge bug of 2026-07-23 noted beside tOr().
    desk_footprint_chip: 'Team',
    desk_footprint_none: 'Untouched',
    desk_footprint_viewed: 'Reviewed',
    // Still translated for saved-search chips: the control dropped this state (it selected
    // exactly the same clusters as "our listings ≥ 1"), the VALUE is still accepted.
    desk_footprint_reposted: 'Reposted',
    // NOT renamed to "Owner contact" (suggested 2026-07-31, rejected): `contact` is already
    // a distinct thing here — party_contact rows and contact_of edges, i.e. whether we hold
    // a phone number for the owner. This facet is about a live cooperation or a meeting.
    // Two facets called the same word would be worse than one long label.
    desk_engaged_label: 'Owner relationship',
    desk_engaged_no: 'None yet',
    desk_engaged_yes: 'Cooperation or meeting',
    desk_engaged_chip_no: 'No owner relationship',
    desk_engaged_chip_yes: 'Owner relationship',
    desk_engaged_no_wa: 'No WhatsApp',
    desk_engaged_chip_no_wa: 'Owner has no WhatsApp',
    // 0294 — seller & motivation
    desk_facet_seller: 'Seller & motivation',
    desk_seller_label: 'Seller holds',
    desk_seller_chip: 'Seller',
    desk_seller_retail: 'One property',
    desk_seller_small: '2–3',
    desk_seller_pro: '4+ (professional)',
    desk_seller_all: 'All',
    desk_range_age_days: 'Time on market',
    desk_age_chip: 'Time on market',
    // The four ways agents actually ask the question, plus an escape hatch. See AGE_PRESETS
    // for why these nest instead of partitioning.
    desk_age_preset_today: 'Today',
    desk_age_preset_week: 'This week',
    desk_age_preset_month: 'This month',
    desk_age_preset_older: 'Older than 30 days',
    desk_age_preset_custom: 'Custom…',
    // Labels the from/to boxes once Custom is open — the presets carry the unit in their
    // own words, but two bare number fields do not.
    desk_age_unit: 'days',
    desk_age_kind_label: 'Measured as',
    desk_age_kind_first_posted: 'Since first posted',
    desk_age_kind_current_run: 'Since last renewal',
    desk_age_kind_hint: 'Portals differ: myhome keeps the original date, ss and korter reset it on renewal. Listings with no posting date go to possible matches rather than being hidden.',
    desk_range_views: 'Views',
    desk_promoted_label: 'VIP advertising',
    desk_promoted_yes: 'Yes',
    desk_promoted_no: 'No',
    // Chips leave the facet's context, so they keep the noun the yes/no rows can shed.
    desk_promoted_chip_yes: 'VIP advertising',
    desk_promoted_chip_no: 'No VIP advertising',
    // 0303 — spread's replacements. desk_spread_* keys STAY: saved searches still chip them.
    desk_reposts_label: 'Our listings',
    desk_reposts_chip: 'Our listings',
    desk_reposts_0: 'None',
    desk_reposts_1_2: '1–2',
    desk_reposts_3p: '3+',
    // ── Advert quality (0702-0704, v55, owner-asked 2026-08-22) ──────────────
    desk_facet_advert_quality: 'Advert quality',
    desk_photos_label: 'Photos',
    desk_photos_chip: 'Photos',
    desk_photos_none: 'None',
    desk_photos_thin: '3 or fewer',
    desk_photos_ok: '4 or more',
    desk_quality_text_label: 'Information',
    desk_no_description: 'No description',
    desk_missing_facts: 'Missing information',
    // The one thing an agent cannot infer: without it the counts read as wrong to anyone
    // who has reposted a flat with better photos than the owner published.
    desk_quality_owner_note: 'Judged on the owner’s advert - our reposts are not counted',
    // "How many portals is it on" is the question, but the count EXCLUDES our own listings
    // (0303) — so the label cannot be the bare question or it silently over-promises. The
    // note carries the exclusion instead of burying it in a longer label.
    desk_owner_platforms_label: 'On how many portals',
    desk_owner_platforms_note: 'Our own listings are not counted',
    desk_owner_platforms_chip: 'Owner portals',
    desk_owner_platforms_1: '1 portal',
    desk_owner_platforms_2: '2 portals',
    desk_owner_platforms_3p: '3+ portals',
    desk_spread_label: 'Market spread',
    desk_spread_chip: 'Competitors',
    desk_spread_1: '1 agency',
    desk_spread_2_3: '2–3',
    desk_spread_4p: '4+',
    desk_spread_all: 'All',
    desk_public_facet_title: 'Public page',
    desk_public_facet_hint: 'Publish this property on the public site (/p page)',
    desk_public_on: 'Published',
    desk_public_off: 'Not published',
    desk_public_by_other: 'Published by a colleague',
    desk_public_publish: 'Publish',
    desk_public_unpublish: 'Unpublish',
    desk_public_view: 'View page',
    desk_public_price_ph: 'Asking price',
    desk_public_note: 'Only price and intent go public — commission and contacts never do. Address precision follows the disclosure policy.',
    desk_near_label: 'Near (proximity)',
    desk_complex_label: 'Complex (in this development)',
    desk_complex_hint: 'Not proximity — the flat must be IN it. Several mean any of them.',
    desk_complex_search_ph: 'Search a complex…',
    desk_complex_remove: 'Remove',
    desk_near_hint: 'Every point must hold — e.g. near the metro AND near the school.',
    desk_near_add_ph: 'Add a place — metro, park, school…',
    desk_near_walk: 'on foot',
    desk_near_bus: 'by bus',
    desk_near_meters: 'm',
    desk_near_tier_toggle: 'Toggle walk/bus tier (resets the radius to that tier)',
    desk_party_dedup_holders: '{n} holders — shared line?',
    desk_party_dedup_needs_review: 'needs review',
    desk_party_dedup_name_sim: 'names match {p}%',
    desk_shared_lines_title: 'Shared / office lines',
    desk_shared_lines_hint: 'Numbers held by 3+ differently-named people — a phone match on one of these is a shared contact point, not identity.',
    desk_shared_lines_row: '{h} holders · {n} names',
    desk_shared_lines_ours: '{n} our agents',
    desk_shared_lines_empty: 'No shared-line candidates.',
    desk_filter_claims_label: 'Agents working it',
    desk_filter_claims_chip: 'Agents',
    desk_filter_claims_lt: 'Less than',
    desk_filter_claims_gt: 'More than',
    desk_filter_claims_none_hint: 'Less than 0 or 1 means nobody is working it.',
    desk_filter_seen_label: 'View status',
    desk_filter_seen_unseen_by_me: "I haven't opened it",
    desk_filter_seen_seen_by_me: "I've opened it",
    desk_filter_seen_unseen: 'Nobody has opened it',
    desk_filter_seen_seen: 'Someone has opened it',
    desk_filter_seen_hint: 'Opened means someone looked at the property. It does not mean they rejected it.',
    desk_search_sort_commission: 'Commission',
    desk_filter_restrict_prompt: 'For my client:',
    desk_filter_mode_hide: 'accepted',
    desk_filter_mode_allow: 'only if allowed',
    desk_filter_mode_restricted: 'not accepted',
    desk_stance_clear: 'any',
    desk_terms_deposit_short: 'Deposit',
    desk_movein_heading: 'Tenant pays before keys',
    desk_movein_mixed_currency: 'Parts are in different currencies — no total shown.',
    desk_movein_incomplete: 'Not everything has been asked yet — this is part of the bill, not all of it.',
    desk_facet_see_all: 'See all ({n})',
    desk_district_direct_hint: 'Listings placed in this district itself, with no sub-district. Included in the number above — tick the district to search all of it.',
    // The own-bucket chip. It MUST read differently from the plain district chip, or two
    // different filters render as the same pill: "Vake" (the raion and everything under it)
    // versus "Vake, directly" (the raion itself, sub-districts excluded).
    desk_chip_district_own: '{name} · direct',
    // §8b — the identity card's eyebrow and the written boundary between the tiers.
    desk_search_identity_eyebrow: "What you're searching",
    desk_search_narrow_label: 'Narrow it down',
    desk_facet_not_asked: "{n} questions aren't asked here — which?",
    desk_readout_matches: 'Matches',
    desk_readout_maybe: 'May match — data incomplete',
    desk_readout_ruled_out: 'Ruled out',
    // S6 escape hatch, at the end of an exhausted result list (design 2b §5).
    desk_hatch_thats_all: 'All {n} in this view.',
    desk_hatch_none_here: 'Nothing in this view matches this search.',
    desk_hatch_leads_n: 'The same search matches {n} lead{s} nobody has called yet.',
    desk_hatch_leads_unknown: 'The same search may have leads nobody has called yet.',
    desk_hatch_go: 'Search leads for this →',
    desk_pool_kept: 'Your filters were kept.',
    // …except this one, which only exists in Leads. Named rather than silently carried.
    desk_pool_dropped_sources: 'Portal ({n}) was dropped — it only applies to leads.',
    desk_pool_in: 'in',
    desk_pool_undo: 'Undo switch',
    desk_facet_show_less: 'Show less',
    desk_search_commit_hint: 'Enter ↵ — search everywhere (including Georgian↔Latin)',
    desk_search_recent: 'Recent searches',
    desk_search_saved: 'Saved searches',
    desk_search_save: 'Save this search',
    desk_search_saved_delete: 'Delete saved search',
    desk_search_clear_all: 'Clear all searches',
    desk_search_unapplied: 'not used as a filter: {words}',
    desk_search_unapplied_hint: 'These words were not understood as a search constraint, so they narrowed nothing. If they matter, set them in the search panel — availability, terms and condition all have their own controls.',
    desk_search_rename: 'Rename saved search',
    desk_search_rooms_short: 'rm',
    desk_search_save_needs_agent: 'Saving needs an agent profile linked to your login',
    desk_search_sort_title: 'Sort',
    desk_search_map_pill: 'Map',
    desk_search_period_title: 'Time period',
    desk_search_overlay_back: 'Close search',
    desk_search_clear_box: 'Clear',
    desk_search_kw_remove: 'Remove this filter',
    desk_search_widen_all_time: 'Search all time — this view only covers the selected period',
    // ⚠️ "of the loaded" is load-bearing, not padding — this counts rows already on the client,
    // NOT the database. The whole-DB search runs on Enter (§5.3). Don't shorten it to "N found".
    desk_search_preview_loaded: '{n} of the loaded',
    // Task #8: shown INSTEAD of the count when a filter only the server can answer is
    // active. No number, because there is no honest one to give before the search runs.
    desk_search_preview_whole_base_only: 'searches the whole base',
    desk_search_id_not_captured: 'Listing ID {id} recognized — but no saved listing carries it. It is probably not in the system yet: open the ad on the platform and save it.',
    desk_search_preview_enter: 'Enter ↵ to search all',
    desk_suggest_elsewhere: 'Suggestions found elsewhere (guesses, not a full search):',
    desk_suggest_client_tag: 'client',
    desk_suggest_business_tag: 'business',
    desk_suggest_complex_tag: 'complex',
    desk_suggest_complex_family: 'all {dev} projects',
    desk_suggest_street_tag: 'street',
    desk_cl_ignored: 'Client leads cannot be filtered by: {list}. They are shown without these.',
    desk_suggest_street_whole: 'whole street — number {n} is not filtered',
    desk_search_by_image_tooltip: 'Search by photo — finds listings containing this image',
    desk_search_image_chip: 'image · {n} match(es)',
    desk_search_image_failed: 'image search failed',
    desk_suggest_unnamed_client: 'unnamed client',
    desk_search_coverage: 'Searched: addresses · districts · listing IDs · phones — not descriptions or client names',
    desk_search_cap_notice: 'The client list is near its load limit, so an older client may not be loaded here yet — this may be a load gap, not a missing record. Try a narrower search, or ask an admin (the search backend needs its Phase 2 upgrade).',
    desk_search_zero_results: 'No results for',
    // Owner, 2026-08-19: the zero state was a small grey line above the possible-matches
    // shelf, so 29 shelf cards read as results. This names what is below, and only renders
    // when the shelf actually has rows.
    desk_zero_shelf_follows: 'Nothing matched. What follows are possible matches — the database can neither confirm nor rule them out.',
    desk_search_no_exact: 'No exact matches found.',
    // Honest-failure states (2026-07-26). "We could not look" must never render as
    // "there is nothing" — see desk_search_coverage above for the same principle.
    desk_search_failed_title: 'Search could not be completed',
    desk_search_failed_hint: 'This is a connection or server problem — it does not mean nothing matched.',
    desk_search_retry: 'Try again',
    // Says only what is known: still running, and for how long. No estimate — see SearchElapsedNote.
    desk_search_still_running: 'Still searching — {n}s. A wide filter searches the whole database.',
    desk_search_partial: '{n} of {m} results could not be loaded. What you see below is incomplete.',
    desk_search_related: 'Related searches',
    desk_search_all_types: 'All types',
    desk_search_all_cities: 'All cities',
    desk_search_district_any: 'District — any',
    desk_search_district_selected: 'District — selected ({n})',
    desk_search_sort_newest: 'Added',
    desk_search_sort_activity: 'Active',
    desk_search_sort_ppm2: 'Price/m²',
    desk_search_sort_fresh: 'Fresh',
    // 0299 — newest ON THE MARKET (earliest posted_at desc). Distinct from _fresh above,
    // which is last_seen_at = most recently confirmed still live by the crawler.
    desk_search_sort_posted: 'Newly listed',
    desk_search_sort_oldest: 'Oldest first',
    desk_search_sort_updated: 'Updated',
    // Four distinct QUESTIONS, deliberately not a reversible pair — see deskSortOptions().
    desk_search_sort_cheapest: 'Lowest price',
    desk_search_sort_priciest: 'Highest price',
    // ⚠️ TEMPORARY, and the wording is the point (owner ruling 2026-07-31). This sort ranks
    // on `area_sqm` ONLY — the BUILDING. `land_area_sqm` (the yard/plot, recovered by 0226)
    // is invisible to every layer of Desk search: not in the v15 RPC's attribute whitelist,
    // not in the sort key, not in the transport, not on the card. So "Largest area" promised
    // something the system cannot define — a 200 m² house on a 5,000 m² plot ranked below a
    // 320 m² house with no yard. Renamed to describe what it actually does until plot area
    // becomes a first-class Desk concept (then this splits into two named sorts).
    desk_search_sort_largest: 'Largest building',
    // 2026-07-31 sort-menu rewrite, owner-specified (second pass). The first pass grouped
    // by TIMESTAMP — one section per date, each headed by the date's name. The owner's
    // correction: that is still the database model wearing a fake moustache. A person does
    // not think "posted_at vs created_at vs last_seen_at", they think "how old is the AD"
    // vs "how long have WE had it" vs "is it still alive". So the sections are now the
    // three THINGS a date can be about — the advertisement, our own copy, our monitoring —
    // and the option name is the answer, not the column.
    // Two consequences recorded on purpose:
    //   · these labels deliberately DIVERGE from the date-facet vocabulary (desk_filter_date_*,
    //     "Posted on portal" etc). The facet picks a range over a named date, where naming
    //     the date is right; the sort answers a question. Aligning the facet to this
    //     vocabulary is a separate, deliberate change — not something to "fix" by reverting.
    //   · "In senTavus" says our name out loud, so nobody reads "Added" as "added to the internet".
    desk_sort_group_smart: '⭐ Recommended',
    desk_sort_group_money: '💰 Price',
    desk_sort_group_ad: '📰 Advertisement',
    desk_sort_group_ours: '📥 In senTavus',
    desk_sort_group_monitor: '🤖 Monitoring',
    // Crawler-check time is a sort roughly nobody reaches for on purpose, and every visible
    // option costs attention — so it lives behind this toggle rather than in the open.
    desk_sort_more: 'More…',
    desk_sort_less: 'Hide',
    desk_sort_flip_hint: 'Click again to reverse the direction',
    desk_sort_opt_commission: 'Commission',
    desk_sort_opt_ppm2: 'Lowest price/m²',
    // A reversible sort is a NOUN plus a direction, never a superlative that swaps.
    // Earlier passes tried relabelling the whole row on flip ("Newest advertisement" →
    // "Oldest advertisement" → "Longest unchanged"). Two things went wrong: the menu
    // appeared to morph under the cursor, and the reversed names drifted into English
    // nobody speaks ("Longest unchanged" is grammatical and not a thing anyone thinks).
    // So the row's IDENTITY is constant and only the direction line under it changes —
    // which also means one pair of direction strings serves every date.
    desk_sort_id_ad_age: 'Advertisement age',
    desk_sort_id_updated: 'Last update',
    desk_sort_id_imported: 'First saved',
    desk_sort_id_added_sentavus: 'Added to senTavus',
    desk_sort_opt_best_match: 'Best Match',
    desk_sort_hint_best_match: 'The best opportunities first — ranked by senTavus intelligence, with the reasons on record',
    desk_sort_id_started_working: 'Started working',
    desk_source_sentavus: 'senTavus',
    desk_source_added_by_me: 'Added by me',
    desk_source_sentavus_hint: 'No portal: entered by hand, through the AI intake, or from the owner\'s fill link',
    desk_sort_id_verified: 'Last check',
    desk_sort_dir_newest: 'Newest first',
    desk_sort_dir_oldest: 'Oldest first',
    // Short forms for the closed pill, where the row's own context is gone. Words, not
    // ▲▼: an arrow on a date never said which end it meant, and the arrow is exactly the
    // thing the reader has to translate.
    desk_sort_dir_newest_short: 'newest',
    desk_sort_dir_oldest_short: 'oldest',
    desk_sort_hint_activity: 'Recently changed, added, or re-listed',
    desk_sort_hint_commission: 'Highest visible commission first',
    desk_sort_hint_posted: 'When it first appeared on the portal',
    desk_sort_hint_updated: 'Price, photos, or description changed',
    desk_sort_hint_added: 'When we first saved it',
    desk_sort_hint_added_sentavus: 'When this flat first reached us (later adverts and our reposts do not count)',
    desk_sort_hint_started_working: 'When you started working on this flat',
    desk_sort_hint_fresh: 'When Argo last confirmed it is still online',
    desk_search_period_today: 'Today',
    desk_search_period_7d: 'Last 7 days',
    desk_search_period_30d: 'Last 30 days',
    desk_search_period_90d: 'Last 90 days',
    desk_search_period_all_time: 'All time',
    desk_search_no_recognitions: 'no recognitions',
    desk_search_sorted_by_match_hint: '🎯 Sorted by match — the entry the AI thinks this paste belongs to is pinned to the top.',
    desk_search_remove_filter: 'Remove this filter',
    // B347: says WHY the chip is struck through. "Kept" matters as much as "not applied" —
    // the value comes back if the identity does, and a hint that only said "ignored" would
    // invite the agent to delete something they will want again.
    desk_search_stranded_hint: 'Does not apply to this property type — kept, but not filtering',
    // "of {m} LOADED" — this line counts the rows fetched so far, NOT the corpus (that is
    // ResultCount above the grid). Naming the universe is the whole point: unlabelled, it
    // read as a corpus total and made the feed look capped (owner, 2026-07-31).
    desk_search_showing_of: '{n} of {m} loaded rows shown',
    desk_search_listings_reduction: '· listings −{pct}%',
    desk_search_hidden_missing_data: '{n} hidden for missing data',
    desk_search_clear_filters: 'clear filters',
    desk_search_open_tools_tooltip: 'Open detailed search',
    desk_search_tools_rail_label: '⚙ Detailed search · {n}',
    desk_search_tools_panel_header: '⚙ DETAILED SEARCH',
    desk_search_tools_clear: 'clear',
    desk_search_collapse_tools_tooltip: 'Collapse detailed search',
    desk_search_show_results: 'Show {n} results',
    desk_search_deal_label: 'Deal',
    desk_search_any_option: 'Any',
    desk_search_type_label: 'Type',
    desk_search_status_label: 'Status',
    desk_entry_filter_all: 'All',
    desk_entry_filter_active: 'Active',
    desk_entry_filter_closed: 'Closed',
    desk_entry_filter_archived_suboption: '↳ Archived',
    desk_search_price_range_cleared: 'Price range cleared — the new deal type has a different price scale.',
    desk_range_rooms: 'Rooms',
    desk_range_bedrooms: 'Bedrooms',
    desk_range_bathrooms: 'Bathrooms',
    desk_range_floor: 'Floor',
    desk_range_total_floors: 'Total floors',
    desk_top_floor: 'Top floor',
    desk_range_from_ph: 'from',
    desk_range_to_ph: 'to',
    desk_empty_no_entries: 'No entries yet.',
    desk_empty_no_match_filters: 'No entries match these filters.',
    desk_home_meetings: 'Upcoming meetings',
    desk_next_up: 'Next up',
    desk_next_meeting: 'Next: {when} · {who}',
    desk_scope_mine: 'mine',
    desk_scope_office: 'my office',
    desk_scope_company: 'all offices',
    desk_scope_pool: 'shared pool',
    desk_home_deals: 'Ongoing deals',
    desk_home_recent_props: 'Recently added properties',
    desk_home_recent_clients: 'Recent clients',
    desk_home_recent_leads: 'Recent leads',
    desk_home_recent_viewed: 'Recently viewed',
    desk_home_no_clients: 'No recent clients.',
    desk_home_show_all: 'Show all →',
    desk_home_back: 'Back to home',
    desk_home_no_meetings: 'No viewings scheduled.',
    desk_home_browse_props: 'Browse properties',
    desk_home_add_client_cta: 'Add a client',
    desk_home_no_viewed: 'Nothing viewed yet — opened cards will appear here.',
    desk_strip_recent: 'Recent entries',
    desk_strip_notifications: 'Notifications',
    desk_strip_notifications_n: '{n} notifications',
    desk_strip_add: 'Add',
    desk_strip_cockpit_collapsed: 'cockpit · collapsed',
    desk_strip_cockpit_open: 'cockpit · expanded',
    desk_notif_panel_empty: 'Nothing yet — drafts, decisions and price moves will appear here.',
    desk_notif_panel_loading: 'Loading…',
    // ── The three zones (design round 1, 2026-08-06) ──────────────────────────
    // A/B/C are three kinds of DEBT, not three kinds of event: A is owed to a
    // person, B to your own judgement, C to nobody. Only the A zone renders when
    // empty — its empty line IS the definition of the surface, which is why it
    // reads as a statement and never as a count of zero.
    desk_notif_zone_waiting: 'Waiting on you',
    desk_notif_zone_yourwork: 'On your work',
    desk_notif_zone_streams: 'Streams · {days} days',
    desk_notif_zone_empty_a: 'Nobody is waiting on you.',
    // Shown only when ALL THREE zones are empty — the emptiest state teaches the
    // contract, so these three clauses are exactly the definition of class A.
    desk_notif_empty_hint: 'This is where a client asking to view, a colleague waiting on access, or a meeting left without an outcome will appear.',
    desk_notif_stream_price: 'Price changes',
    desk_notif_stream_ended: 'Gone from the portal',
    desk_notif_stream_opens: 'Links opened',
    // The clause that keeps the digest honest rather than a hiding place: it names
    // what was LIFTED OUT of the stream and is already a row above.
    desk_notif_stream_lifted: '{n} on yours, above',
    desk_notif_stream_rest: 'Remaining {n} — in Activity',
    desk_notif_more_salient: '+{n} more on your work',
    desk_notif_less_salient: 'Show less',
    desk_notif_mark_read: 'Mark as read',
    desk_notif_dismiss: 'Dismiss',
    desk_notif_dismissed: 'Dismissed',
    desk_notif_undo: 'Undo',
    desk_notif_queues: 'Review queues',
    desk_nudge_why_starting_soon: 'Viewing coming up',
    desk_notif_queue_rate: '{n} waiting · {done} this month',
    desk_queue_party_merge: 'Merge people',
    desk_queue_complex_link: 'Link developments',
    desk_queue_district_dispute: 'Confirm districts',
    desk_notif_ai_alert: 'AI problem — check Admin → AI',
    desk_notif_system_alert: 'Background job problem',
    desk_fresh_seen_ago: 'seen {t} ago',
    desk_fresh_not_seen: 'not seen {n}d',
    desk_fresh_hint: 'When this ad was last sighted live on its platform',
    desk_hidden_unknown_line: '{n} hidden for missing data — unknown ≠ no match',
    // 0537/0538 — the street FACET (distinct from the two proximity notes below, which
    // describe the old radius search and stay for it).
    desk_facet_street: 'Street',
    desk_facet_complex: 'Development',
    // Complex header card above the feed (owner 2026-09-10). Status values reuse desk_cplx_status_*.
    desk_cxhead_developer: 'Developer',
    desk_cxhead_all_projects: 'all projects',
    desk_cxhead_district: 'District',
    desk_cxhead_status: 'Status',
    desk_cxhead_eta: 'Handover',
    desk_cxhead_blocks: 'Blocks',
    desk_cxhead_floors: 'Floors',
    desk_cxhead_address: 'Address',
    desk_cxhead_aliases: 'Also written as',
    desk_cxhead_show_map: 'On the map',
    desk_cxhead_remove: 'Remove the development filter',
    desk_cxhead_open_image: 'Open the full image',
    desk_facet_near_radius: 'Distance from the point',
    desk_map_point_tool: 'Search around a point',
    desk_map_area_tool: 'Draw a search area',
    desk_map_clear_shapes: 'Clear the drawn area',
    desk_map_point_hint: 'Click the map to search around that spot',
    desk_map_area_hint: 'Trace with your finger or tap at least 3 corners · Apply to update results',
    desk_map_area_limit: 'Maximum 10 search areas',
    desk_map_draw_cancel: 'Cancel',
    desk_map_draw_undo: 'Undo point',
    desk_map_draw_clear: 'Clear drawing',
    desk_map_draw_apply: 'Apply area',
    desk_facet_map_area: 'Drawn area',
    desk_near_radius_hint: 'How far from {place} counts as near.',
    desk_near_default: 'default',
    desk_facet_project: 'Building project',
    desk_facet_furnishing: 'Furnishing',
    desk_facet_added_how: 'How it was added',
    desk_facet_apt_class: 'Class',
    desk_facet_apt_class_note: 'How expensive the finish is — a different question from how finished it is.',
    desk_added_how_crawler: 'Found by Argo',
    desk_added_how_agent: 'Captured by an agent',
    desk_added_how_evaluation: 'Evaluation request (sentavus.com)',
    desk_added_how_note: 'How it first entered our book — not where the advert is listed.',
    desk_cond_state_unset: 'Click: must have this state',
    desk_cond_state_yes: 'Must have this state — click to exclude it instead',
    desk_cond_state_no: 'Excluded — click to clear. Properties with no state recorded are shelved, not dropped.',
    desk_project_historical: 'Historical / traditional',
    desk_project_standard: 'Standard projects',
    desk_complex_none: 'nothing found',
    desk_facet_metro: 'Metro',
    desk_street_pick_district_first: 'Choose a district first — its streets appear here',
    desk_street_search_ph: 'Find a street…',
    desk_street_see_all: 'See all ({n})',
    desk_street_none_here: 'No street is recorded for this district yet',
    // ⚠️ Distinct from the line above ON PURPOSE. That one is a claim about the CORPUS
    // ("we hold nothing here") and an agent acts on it; this one is a claim about the
    // SEARCH. Saying the first when the second is true sends them off to capture streets
    // that are already recorded.
    desk_street_no_match: 'No street here matches your search.',
    desk_street_counts_pending: 'Numbers return when you clear the street selection',
    desk_street_shelf_note: 'Listings whose street is not recorded move to possible matches — they never disappear from the results.',
    // Q4. Every one of these names the CENTRE. That is not decoration: the distance is
    // measured from a single point per street, so a reader who assumes "from the street"
    // will read a number that is wrong for any street longer than a block.
    desk_street_see_more: '+{n} more',
    desk_street_nearby_optin: 'Also show listings within {m} m of the street centre whose address names a different street',
    desk_nearby_street_header: 'Nearby, but not on this street — {shown} within {m} m of the street centre. Their address names a different street.',
    desk_nearby_street_badge: '{street} · ~{m} m from the street centre',
    desk_street_coverage: 'A street is recorded for {n} of {district}’s {total}. The rest appear under possible matches.',
    desk_street_none_exact: 'Nothing exactly on {street} — showing {n} nearby you may like',
    desk_street_some_exact: '{on} on {street} · {near} more nearby you may like',
    desk_street_all_exact: '{on} on {street}',
    desk_hidden_unknown_show: 'Show anyway',
    desk_search_committed_base: 'whole base · {n}',
    desk_too_broad_line: '{n} results — refine:',
    // Headline result count above the grid. The UNIT is load-bearing: the server counts
    // CLUSTERS (count_unit:'properties'), not ads, so the word must say so — a bare number
    // reads as "ads" and is off by the cluster factor.
    desk_result_count_leads: '{n} leads',
    desk_result_count_properties: '{n} properties',
    desk_result_count_matching: '{n} match your search',
    desk_result_count_possible: '+{n} possible',
    desk_too_broad_show_all: 'Show all anyway',
    desk_zero_capture_now: 'Save it now',
    desk_zero_open_source: 'Open source ad',
    prefs_menu_title: 'Preferences',
    prefs_currency: 'Currency',
    prefs_language: 'Language',
    prefs_theme: 'Theme',
    prefs_office: 'Office',
    prefs_view_as: 'View as',
    desk_pagination_prev: '← Prev',
    desk_pagination_page_of: 'Page {n} of {total}',
    desk_pagination_next: 'Next →',
    desk_pagination_jump: 'Go to page',
    desk_pagination_more_pending: 'more pages will appear as the rest of the results load',
    desk_pagination_estimated_page: 'estimated — loads when you open it',
    desk_first_floor: 'First floor',
    desk_facet_first_floor: 'First floor',
    desk_facet_flag_exclude_next: 'click to exclude (✕)',
    desk_facet_exclude_hint: 'excluded — click to clear',
    desk_facet_inherited_hint: 'inherited from the parent — click to make an exception',
    desk_facet_exclude_next: 'click again to exclude',
    desk_facet_name_deselect_hint: 'click the name to deselect',
    desk_pagination_loading: 'Loading…',
    desk_load_more_listings: 'Load more listings ({loaded} of {total})',
    desk_load_more_properties: 'Load more properties ({loaded} of {total} groups)',
    desk_autoload_progress: 'Loading… {loaded} of {total}',
    // ⚠️ TWO different numbers (2026-07-29). This said "{n} listings … (showing newest {n})"
    // with ONE value bound to both slots, so with 2,248 shelved and 60 rendered it claimed to
    // be showing 2,248 — a number that does not equal what it delivers. And "newest" stopped
    // being true when v17 (0248) reordered the shelf by who is most likely to ANSWER, since
    // the shelf became the workflow rather than an exception bucket.
    // Task #11 (2026-07-29): the shelf's cursor. {loaded} counts SERVER-delivered shelf
    // groups only, never the locally-shelved ones — the button's number and the button's
    // action must describe the same set.
    desk_possible_load_more: 'Load more possible matches ({loaded} of {total})',
    desk_possible_loading: 'Loading…',
    // ⚠️ "properties", not "listings" (2026-07-29): {total} and {shown} are both CLUSTER
    // counts. The word said listings while the numbers counted properties, which is how the
    // shelf came to render 192 cards under a header reading "showing 192" of 2,367.
    desk_possible_matches_header: '🤔 possible matches — {total} properties the data can\'t confirm or rule out (showing {shown} — the owners most likely to answer first)',
    desk_search_tools_bare: 'Detailed search',
    desk_amend_modal_title: 'Continue existing demand',
    desk_amend_target_label: 'client brief {id}…',
    desk_misc_not_saved: '⚠️ NOT saved — {reason}',
    desk_misc_failed: '⚠️ Failed — {reason}',
    desk_misc_failed_no_details: 'this save attempt failed and created nothing (no details stored)',
    desk_client_owner_agent: '🧑‍💼 agent: {name}',
    desk_entry_match_pill: '🏠 {count} candidate listings',
    desk_entry_hot_lead: '🔥 urgent',
    desk_entry_has_meeting: 'viewing booked',
    desk_entry_meetings_n: '{count} viewings',
    desk_entry_deal_closed: 'deal closed',
    desk_entry_no_phone: '⚠️ no phone number',
    desk_entry_fill_criteria: '📝 fill in: {fields}',
    desk_crit_districts: 'districts',
    desk_crit_budget: 'budget',
    desk_crit_rooms: 'rooms',
    desk_match_act_shortlist: 'shortlist',
    desk_match_act_proposed: 'proposed',
    desk_match_act_reject: 'reject',
    desk_match_act_undo: '↩ undo',
    desk_match_reject_reason_prompt: 'Why is this not a fit for this client? (optional)',
    desk_match_grp_pinned: 'shortlisted',
    desk_match_grp_ready: 'ready to offer',
    desk_match_grp_verify: 'verify first',
    desk_match_grp_stretch: 'negotiate first',
    desk_match_grp_compromise: 'compromise',
    desk_match_grp_longshot: 'long shots',
    desk_match_grp_proposed: 'proposed',
    desk_match_grp_rejected: 'rejected',
    // ── matching-UI v2, the client surface (msug_*) ──────────────────────────
    msug_grp_none: 'no cards here right now',
    msug_more_cards: '{n} more',
    msug_updated: 'updated {ago}',
    msug_act_present: 'Present to the client',
    msug_act_verify: 'Verify first',
    msug_act_negotiate: 'Negotiate with the owner',
    msug_act_ask_client: 'Ask the client',
    msug_ready_counterweight: 'This number is large because the profile is nearly empty — filling it in will shrink the list, which is the right direction',
    msug_empty_form_title: 'We don\'t know what this client wants yet',
    msug_empty_form_body: 'The record is empty — not the market. Fill in the criteria via Edit and the matching will recount.',
    msug_diag_title: 'Nothing fits — what could open options',
    msug_diag_budget: 'budget +30%',
    msug_diag_district: 'without the binding districts',
    msug_diag_rooms: 'rooms ±1',
    msug_diag_opens: '{n} options',
    msug_diag_opens_nothing: 'opens nothing',
    msug_diag_unmeasured: 'could not be measured',
    msug_diag_zero_all: 'Concessions don\'t help here — none of the relaxations opens anything.',
    msug_diag_downstream: 'The obstacle is not in this table: {n} candidates passed the count and the scoring rejected them all.',
    msug_diag_not_counted: 'Outside this count: {list}.',
    msug_diag_foot: 'Directions, not promises — the counts are approximate and unscored.',
    msug_diag_call_client: 'Call the client — only they can soften a condition.',
    msug_probe_see: 'show',
    msug_probe_back: 'back',
    msug_probe_unscored: 'Not scored — these are candidates for that one concession, not a selection.',
    msug_ghost_pinned: '+{n} shortlisted',
    // Q5 (mobile): the collapsed card's ✓ chip. MatchExplain only chips what is WRONG or
    // open, so without this the phone card never says how much matched.
    msug_m_matched: '{n} matched',
    msug_diff_recorded: 'Recorded',
    msug_diff_recorded_subject: 'Recorded: {subject}',
    msug_diff_counting: 'recounting…',
    msug_diff_added: '+{n} cards',
    // Was in the Georgian block only, so an English operator saw the raw key string
    // (t() falls back ka → en → the key itself). Found by a desk-wide key audit 2026-08-15.
    msug_diff_same_answer: 'the same answer also opened other cards',
    msug_diff_moved: '{n} moved to the group “{group}”',
    msug_diff_nochange: 'Recorded — the list did not change',
    msug_diff_see_new: 'See the new ones',
    msug_grp_shut: 'collapse the group',
    msug_grp_open: 'open the group',
    msug_grp_showing_plus: 'showing {base} + {n} new',
    msug_grp_new_inside: '+{n} new',
    msug_cut_named: 'showing the {n} closest fits · of {total}',
    msug_source_withdrawn: 'from a withdrawn ad',
    msug_too_broad: '{n} options is a lot — the requirement is too broad.',
    msug_too_broad_unnamed: '{list} are not stated. Pin them down and the list becomes a real answer.',
    msug_well_specified: 'The requirement is precise — the market is simply wide on these terms. All 4 parameters are filled; nothing needs narrowing. Choose an order and work from the top.',
    msug_and_join: ' and ',
    msug_or_join: ' or ',
    msug_refine: 'Refine the requirement',
    msug_sort_label: 'order:',
    msug_sort_fit: 'closest',
    msug_sort_fresh: 'newest ad',
    msug_sort_cheap: 'cheapest',
    msug_sort_open: 'fewest questions',
    msug_sort_slow: '~1s',
    msug_open_property: 'property card',
    // Round-2 §3: the client card's fourth column. NOT "action" — the cell names whose
    // record the answer becomes, which on a property we do not represent is a person.
    msug_col_whose: 'whose record',
    msug_on_requirement: 'on the requirement',
    msug_nc_polygon: 'search area',
    msug_nc_area: 'area',
    msug_nc_must_have: 'must-have features',
    msug_nc_must_not_have: 'excluded features',
    msug_nc_condition: 'condition',
    msug_nc_floor: 'floor',
    msug_nc_complex: 'complex',
    desk_match_urgency_now: 'now',
    desk_match_urgency_soon: 'soon',
    desk_match_via_agent: 'via {name}',
    desk_misc_error_prefix: 'Error: {message}',
    desk_search_failed: 'The search did not complete. Nothing was hidden — the answer is unknown, not empty.',
    desk_address_conflict: 'street unresolved',
    desk_retry: 'Try again',
    // 2026-10-23 search/filter audit: each line names what is missing instead of reading as an answer.
    desk_possible_failed: 'Possible matches did not load — this is not "none".',
    desk_street_catalog_failed: 'The street list did not load — streets appear here with the search counts.',
    desk_map_pins_failed: 'Not every matching location loaded — the map shows only the {n} loaded properties.',
    desk_filter_invalid_number: 'Not a number, so this search was not run: {fields}. Fix or remove the filter.',
    desk_misc_rejected_not_saved: 'Rejected — not saved.',
    desk_misc_candidate_not_found: '⚠️ Candidate record not found — it may have been deleted.',
    desk_misc_no_linked_viewing: '⚠️ No linked viewing — this meeting wasn\'t saved with one.',
    desk_misc_viewing_not_found: '⚠️ Viewing not found — it may have been deleted.',
    desk_observed_from_owner: 'Owner said',
    desk_observed_from_agent: 'Agent knows / visited',
    desk_observed_from_registry: 'Documents / registry',
    desk_observed_from_admin: 'Office verified',
    desk_observed_from_ai: 'AI extracted (unreviewed)',
    // One-time fill-form links (client/property self-intake)
    desk_fill_link_btn: 'Form link',
    vlink_title: 'Vacancy link for a platform',
    vlink_sub: 'One link per posting. Choose where it goes and write why — if someone abuses it, you block only that link and every other one keeps working.',
    vlink_platform_other: 'Other',
    vlink_comment_ph: 'Where and why — e.g. FB group "Jobs in Tbilisi", 16 Sep',
    vlink_create: 'Create link',
    vlink_creating: 'Creating…',
    vlink_need_both: 'Choose a platform and write a comment to create the link.',
    vlink_copy: 'Copy',
    vlink_copied: 'Copied',
    vlink_list: 'Links',
    vlink_none: 'No links yet.',
    vlink_applications: '{n} applications',
    vlink_blocked_since: 'Blocked since {when}',
    vlink_block: 'Block',
    vlink_unblock: 'Unblock',
    vlink_block_confirm: 'Block the link "{comment}"? Nobody will be able to apply through it. Optional: why?',
    vlink_unblock_confirm: 'Unblock the link "{comment}"? Applications will be accepted through it again.',
    vlink_require_id: 'Accept only links with an ID',
    vlink_require_id_sub: 'While this is off, a blocked link can be bypassed by removing ?l=… from the address. Turn it on once the old links (?src=…) have been replaced — they stop working.',
    vlink_require_id_confirm: 'Turn on? Every old link without an ID (?src=…) stops accepting applications immediately.',
    vlink_manager: 'Manager:',
    vlink_manager_any: 'None — any manager',
    cand_link_title: 'Personal link for this candidate',
    cand_link_sub: 'Send this to one person — they fill the vacancy form themselves and land on your Desk, with their name already in it. The link works once and expires in 7 days. Links to post on platforms are made below, each with its own ID.',
    desk_fill_link_title: 'One-time form link',
    desk_fill_link_sub: 'Send this link to your client — they fill the form themselves and the entry lands on your Desk. Each link works exactly once and expires in 7 days.',
    desk_fill_link_kind_client: '🙋 Client form',
    desk_fill_link_kind_property: '🏠 Property form',
    desk_fill_link_prefill_name: 'Client name (optional, pre-fills the form)',
    desk_fill_link_prefill_phone: 'Client phone (optional)',
    desk_fill_link_generate: 'Create link',
    desk_fill_link_generating: 'Creating…',
    desk_fill_link_copy: 'Copy',
    desk_fill_link_copied: 'Copied!',
    desk_fill_link_whatsapp: 'WhatsApp',
    desk_fill_link_recent: 'Your recent links',
    desk_fill_link_st_open: 'waiting',
    desk_fill_link_st_started: '✍️ started',
    desk_fill_link_st_used: '✓ filled',
    desk_fill_link_st_expired: 'expired',
    desk_fill_link_none: 'No links yet.',
    desk_fill_link_share_text: 'Hello{name}! Please fill out this short form: {url}',
    // ── Contracts panel (ContractPanel.jsx, B1249–B1252) ──────────────────────────
    desk_contract_title: 'Contracts',
    desk_contract_new_rent: 'Rental agreement',
    desk_contract_none: 'No contracts yet for this meeting.',
    desk_contract_status_draft: 'draft',
    desk_contract_status_sent: 'sent',
    desk_contract_status_partially_signed: 'partially signed',
    desk_contract_status_signed: 'signed',
    desk_contract_status_declined: 'declined',
    desk_contract_status_void: 'void',
    desk_contract_compose_hint: 'Answer what the meeting does not know, then create.',
    desk_contract_rerender_of: 'New version after v{v} — links of the old version are revoked.',
    desk_contract_parties: 'Parties',
    desk_contract_role_landlord: 'Landlord',
    desk_contract_role_tenant: 'Tenant',
    desk_contract_role_owner: 'Owner',
    desk_contract_role_cobroker: 'Co-operating broker',
    desk_contract_role_client: 'Client',
    desk_contract_role_agency: 'Agency (senTavus)',
    desk_contract_role_agent: 'Agent',
    desk_contract_src_contract: 'from the rental contract',
    desk_contract_doc_rental: 'from the rental contract',
    desk_contract_doc_binder: 'from the earnest-money agreement',
    desk_contract_doc_advance: 'from the advance agreement',
    desk_contract_doc_agency: 'from the service agreement',
    desk_contract_doc_signed: 'signed',
    desk_contract_doc_draft: 'draft',
    desk_contract_docname_rent_residential_v1: 'Rental agreement',
    desk_contract_docname_rent_commercial_v1: 'Commercial lease',
    desk_contract_docname_binder_v1: 'Earnest-money agreement',
    desk_contract_docname_advance_v1: 'Advance agreement',
    desk_contract_docname_agency_service_v1: 'Service agreement',
    desk_contract_import_title: 'This deal already has documents',
    desk_contract_import_sub: 'Take what is already recorded, or fill everything in yourself.',
    desk_contract_import_answers: 'answers {n}',
    desk_contract_import_counts: '{n} will be filled · {d} differ from an earlier document · {c} to decide',
    desk_contract_import_decide: 'Two documents of equal standing disagree. Choose which one this contract follows:',
    desk_contract_import_go: 'Import what is known',
    desk_contract_import_manual: 'Fill in manually',
    desk_contract_src_agency: 'set by the agency',
    desk_contract_party_name_ph: 'full name as on ID',
    desk_contract_src_meeting: 'from meeting',
    desk_contract_src_added: 'you added this',
    desk_contract_src_prior_contract: 'from an earlier contract with this person',
    desk_contract_src_fill_form: 'from the client form',
    desk_contract_src_requirement: 'from the client card',
    desk_contract_src_cooperation: 'from cooperation',
    desk_contract_src_lease_start: 'from lease start',
    desk_contract_src_period: 'automatic',
    desk_contract_src_computed: 'calculated',
    desk_contract_period: 'Term',
    desk_contract_lease_unit_day: 'days',
    desk_contract_lease_unit_month: 'months',
    desk_contract_lease_unit_year: 'years',
    desk_contract_lease_preset: '{n} months',
    desk_contract_lease_edit: 'edit',
    desk_contract_today: 'today',
    desk_contract_lease_edit_title: 'Type a term — the end date is recomputed from it',
    desk_return_title: "Final inspection",
    desk_return_worse: "worse",
    desk_return_start: "start",
    desk_return_sec_condition: "Condition — at handover → on vacating",
    desk_return_sec_keys: "Keys — given → returned",
    desk_return_sec_meter: "Meters",
    desk_return_sec_deduction: "Deductions",
    desk_return_v_same: "unchanged",
    desk_return_v_worse: "worse",
    desk_return_v_better: "better",
    desk_return_v_unknown: "not comparable",
    desk_return_v_added: "recorded only now",
    desk_return_keys_short: "not returned",
    desk_return_ded_note_ph: "on what basis — invoice, photo",
    desk_return_disputed: "disputed",
    desk_return_disputed_hint: "the tenant does not agree with this deduction",
    desk_return_deposit: "Security deposit",
    desk_return_deductions: "Deductions",
    desk_return_disputed_total: "of which disputed",
    desk_return_refund: "To refund",
    desk_return_owes: "The tenant owes",
    desk_return_mixed: "the sums are in different currencies — no total is computed",
    desk_return_ded_unpaid_rent: "Unpaid rent",
    desk_return_ded_utilities: "Unpaid utilities",
    desk_return_ded_damage: "Damage",
    desk_return_ded_missing_inventory: "Missing inventory",
    desk_return_ded_missing_keys: "Keys not returned",
    desk_return_ded_cleaning: "Cleaning",
    desk_return_ded_other: "Other",
    desk_annex_photo_pick: "which photograph proves this",
    desk_annex_photo_n: "photo",
    desk_annex_record_title: "Handover record",
    desk_annex_sec_condition: "Condition of the property",
    desk_annex_sec_keys: "Keys and access devices",
    desk_annex_sec_meter: "Meter readings",
    desk_annex_sec_inventory: "Inventory",
    desk_annex_sec_defect: "Existing defects",
    desk_annex_state_good: "good",
    desk_annex_state_damaged: "damaged",
    desk_annex_state_absent: "not present",
    desk_annex_state_not_checked: "not checked",
    desk_annex_sev_minor: "minor",
    desk_annex_sev_major: "major",
    desk_annex_note_ph: "what exactly — e.g. a scratch by the window",
    desk_annex_reading_ph: "reading",
    desk_annex_meter_no_ph: "meter №",
    desk_annex_item_ph: "item",
    desk_annex_defect_ph: "what is wrong",
    desk_annex_room_ph: "room",
    desk_annex_save: "Save",
    desk_annex_saving: "Saving…",
    desk_return_signed_by: "Act signed by",
    desk_annex_promised: "The landlord promised to provide it",
    desk_annex_meter_reading: "Meter reading",
    desk_annex_meter_no: "Meter No.",
    desk_annex_promised_by: "by",
    desk_annex_promised_date_needed: "enter the date",
    desk_return_date_first: "The return date is needed before the parties can sign",
    desk_return_pdf: "Act PDF",
    desk_annex_saved: "Saved ✓",
    desk_annex_discard_warn: "this discards the signatures already given",
    desk_annex_discard_go: "continue",
    desk_contract_src_listing_terms: 'from terms',
    desk_contract_src_owner_rule: "owner's rule",
    desk_contract_src_binder: 'from the binder agreement',
    desk_contract_src_property: 'from property',
    desk_contract_src_default: 'filled automatically',
    desk_contract_src_agent: 'you changed this',
    desk_contract_src_override: 'local override',
    desk_contract_override_was: 'The source says: {v}',
    desk_contract_override_reason_ph: 'Why is it different here?',
    desk_contract_override_reason_needed: 'say why it differs',
    desk_contract_source_moved: 'The source document has moved on: you copied v{from}, it is now v{to}.',
    desk_contract_source_show: 'see what differs',
    desk_contract_source_hide: 'hide',
    desk_contract_source_same: 'Nothing this contract asks for has changed.',
    desk_contract_leg_old: 'The source said then',
    desk_contract_leg_new: 'The source says now',
    desk_contract_leg_local: 'This contract',
    desk_contract_take_source: 'take the new value',
    desk_contract_keep_mine: 'keep this one',
    desk_contract_source_save_note: 'Nothing is written until you save; a save makes a new version.',
    desk_contract_what_is_this: 'What does this field mean?',
    desk_contract_bad_email: 'This is not an email address.',
    desk_contract_bad_phone: 'This is not a phone number.',
    desk_contract_id_duplicate: 'the same ID as the other party',
    desk_contract_id_format: 'a personal number is 11 digits',
    desk_contract_src_none: 'needs an answer',
    desk_contract_langs: 'Language',
    desk_contract_lang_ka: 'Georgian',
    desk_contract_lang_en: 'English',
    desk_contract_lang_unreviewed: 'not reviewed yet',
    desk_contract_need_lang: 'Pick at least one language.',
    desk_contract_pay_signing_title: "The payment at signing — who takes it, and how",
    desk_contract_block_signing: 'How the lease is signed',
    desk_contract_block_handover: 'Key handover',
    desk_contract_pay_route_owner_cash: "In cash — to the owner",
    desk_contract_pay_route_agent_cash: "In cash — to an authorised senTavus agent",
    desk_contract_pay_route_owner_transfer: "By transfer — to the owner's account",
    desk_contract_pay_route_agent_transfer: "By transfer — to an authorised agent's approved account",
    desk_contract_pay_route_agency_transfer: "By transfer — to the agency settlement account",
    desk_contract_pay_route_owner_card: "By card or an electronic payment system — to the owner",
    desk_contract_pay_route_other: "Another way (describe it)",
    desk_contract_pay_transfer_kind: "Kind of transfer",
    desk_contract_pay_account_name: "Account name as it appears on the transfer",
    desk_contract_pay_other_who: "Who receives the money",
    desk_contract_pay_other_how: "How and when it is paid",
    desk_contract_pay_other_how_help: "Write it the way it should read in the contract — for example: in cash to the owner's representative, against a signed receipt.",
    desk_contract_pay_monthly_title: "Monthly rent afterwards — how it is paid",
    desk_contract_pay_monthly_same: "The same way as at signing",
    desk_contract_pay_monthly_any: "Any convenient way — cash, transfer or card",
    desk_contract_pay_monthly_multiple: "Several ways — tick which",
    desk_contract_pay_monthly_owner_cash: "In cash — to the owner",
    desk_contract_pay_monthly_owner_transfer: "By transfer — to the owner's account",
    desk_contract_pay_monthly_owner_card: "By card or an electronic payment system — to the owner",
    desk_contract_pay_monthly_other: "Another way (describe it)",
    desk_contract_pay_monthly_other_how: "How and when monthly rent is paid",
    desk_contract_lang_ka_required: 'Georgian is always included — it is the legally authoritative text.',
    desk_contract_need_fields: 'Fill the highlighted fields first.',
    desk_contract_cancel: 'Cancel',
    desk_contract_create_btn: 'Create',
    desk_contract_rerender_btn: 'Re-render',
    desk_contract_err_missing: 'A required field is still empty.',
    desk_contract_err_lang: 'That language is not reviewed yet.',
    desk_contract_version: 'version {v}',
    desk_contract_preview_show: 'preview',
    desk_contract_preview_hide: 'hide preview',
    desk_contract_voided: 'Voided',
    desk_contract_kind_rent_residential: 'Rental agreement',
    desk_contract_kind_rent_commercial: 'Commercial lease',
    desk_contract_legal_pending_short: 'legal review pending',
    desk_contract_legal_pending_title: 'Legal review pending.',
    desk_contract_legal_pending_body: 'This contract type can be drafted and previewed, but not sent or signed until a lawyer approves its text. The document itself is marked as a working draft.',
    desk_contract_kind_agency_service: 'Service agreement',
    desk_contract_kind_tenant_agency_service: 'Tenant agency service agreement',
    desk_contract_kind_owner_mandate: 'Exclusive mandate',
    desk_contract_tpl_owner_mandate_v1: 'Exclusive brokerage agreement',
    desk_contract_add_co_owner: 'add a co-owner',
    desk_contract_remove_co_owner: 'remove',
    // 🏠 residents (owner 2026-09-29): listed in the lease, never signing
    // 🏠 the address in English/Russian from the system (owner 2026-09-29)
    desk_contract_address_auto_used: 'From the system: {v} — printed as is; write your own to change it.',
    desk_contract_address_auto_overridden: 'The system has: {v} — your text is printed instead.',
    desk_contract_role_resident: 'Resident',
    desk_contract_add_resident: 'add details of resident {n}',
    desk_contract_remove_resident: 'remove',
    desk_contract_resident_note: 'Optional. Listed in the lease as a person who will live here; does not sign.',
    desk_contract_co_owners_must_sign: '“All co-owners sign” — add every co-owner as an owner (at least two)',
    desk_contract_owner_authority_mismatch: '“Sole ownership” with more than one owner — change the basis or remove the extra owner',
    desk_contract_range_between: 'must be between {min} and {max}',
    desk_contract_range_min: 'must be at least {min}',
    desk_contract_range_max: 'must be at most {max}',
    desk_contract_range_after: 'must be after “{f}”',
    desk_contract_rows_incomplete: 'a row has a required box empty',
    desk_contract_rows_add: 'add a row',
    desk_contract_rows_remove: 'remove this row',
    desk_contract_tenant_agreement_required: 'Tenant Agency Agreement required',
    desk_contract_tenant_needed: 'Add tenant contact',
    desk_contract_kind_binder: 'Binder agreement',
    desk_contract_kind_advance: 'Advance and reservation agreement',
    desk_contract_rel_from: 'drawn from',
    desk_contract_rel_based_on: 'based on',
    desk_contract_rel_dependent: 'drawn from this',
    desk_contract_rel_signed: 'signed',
    desk_contract_rel_open: 'not yet signed',
    desk_contract_rel_void: 'voided',
    desk_contract_rel_void_warn: 'the source contract was voided — check every inherited value',
    desk_contract_rel_none: 'No rental contract on this meeting yet — the tenant, dates and rent must be typed by hand.',
    desk_contract_rel_none_property: 'No Rental Agreement for this property yet. Verify the rent and tenant details.',
    desk_contract_link_none: 'no link yet',
    desk_contract_link_minted: 'link ready — not sent',
    desk_contract_link_sent: 'sent via {via} · {at}',
    desk_contract_link_opened: 'opened · {at}',
    desk_contract_link_signed: 'signed · {at}',
    desk_contract_link_declined: 'declined · {at} · {reason}',
    desk_contract_via_whatsapp: 'WhatsApp',
    desk_contract_via_email: 'email draft opened',
    desk_contract_via_email_auto: 'emailed automatically',
    desk_contract_event_link_mail_failed: 'signing link email failed',
    desk_contract_via_copied: 'copied link',
    desk_contract_via_in_person: 'in person, on the agent\'s device',
    desk_contract_send_whatsapp: 'WhatsApp',
    desk_contract_send_email: 'Email',
    desk_contract_copy: 'Copy link',
    desk_rental_open_title: 'Before the contract form',
    desk_rental_open_q1: 'Did the tenant inspect the property in person?',
    desk_rental_open_q1_in_person: 'Inspected in person and signs in person',
    desk_rental_open_q1_remote_inspected: 'Inspected in person earlier, but signs remotely',
    desk_rental_open_q1_remote_media: 'Has not inspected in person; reviewed photos/video and signs remotely',
    desk_rental_open_q2: 'When will the keys be handed over to the tenant?',
    desk_rental_open_q2_start: 'On the tenancy start date',
    desk_rental_open_q2_later: 'Later, by prior arrangement',
    desk_rental_open_q2_help: 'The actual date and time of the key handover is recorded at handover.',
    desk_rental_open_q3: 'How is the initial payment made?',
    desk_rental_open_m_cash: 'Cash',
    desk_rental_open_m_cash_note: 'As soon as the money is received and recorded.',
    desk_rental_open_m_instant: 'Instant bank transfer',
    desk_rental_open_m_instant_note: 'Usually credited immediately or within a few minutes.',
    desk_rental_open_m_local: 'Standard local bank transfer',
    desk_rental_open_m_local_note: 'May be credited the same or the next banking day.',
    desk_rental_open_m_swift: 'International bank transfer / SWIFT',
    desk_rental_open_m_swift_note: 'Crediting may take up to 3 banking days.',
    desk_rental_open_m_other: 'Other',
    desk_rental_open_cash_remote_no: 'Not possible when the contract is signed remotely.',
    desk_rental_open_q4: 'How is subsequent monthly rent paid?',
    desk_rental_open_r_same: 'The same way as the initial payment',
    desk_rental_open_r_spec: 'Another way — details are filled in the contract form',
    desk_rental_open_same_other_no: 'With "Other", monthly rent is set out separately in the form.',
    desk_rental_open_continue: 'Continue to the contract',
    desk_rental_open_answer_all: 'Answer all questions to continue',
    desk_rental_open_change: 'Change',
    desk_rental_open_sum_in_person: 'Inspected in person',
    desk_rental_open_sum_remote_inspected: 'Remote signing · inspected earlier',
    desk_rental_open_sum_remote_media: 'Remote signing · photos/video only',
    desk_rental_open_sum_same: 'monthly rent the same way',
    desk_rental_open_sum_spec: 'monthly rent another way',
    desk_rental_open_sum_same_long: 'Monthly rent uses the same payment method. Its recipient and account are set separately below.',
    desk_rental_open_same_unavailable: 'Choose how monthly rent is paid.',
    desk_contract_pay_recipient_title: 'Who receives the payment at signing?',
    desk_contract_err_generic: 'Could not save. Please try again.',
    desk_contract_err_details: 'Technical details',
    desk_contract_lease_break_toggle: 'With an early-exit right',
    desk_contract_commission_mismatch_bar: 'Commission — amount, status and date must match',
    desk_contract_break_month_bar: 'The break month must be positive and shorter than the full term',
    desk_contract_notice_days_bar: 'Days of notice before leaving must be a positive whole number',
    desk_contract_lease_model_bar: 'The lease model and the full term must match',
    desk_contract_check_payment_rule: 'check the payment rule',
    desk_contract_commission_mismatch: 'The commission amount, status and date do not match.',
    desk_contract_commission_change_payment: 'Change payment',
    desk_contract_payment_date: 'Payment date',
    desk_contract_break_before_end: 'The break right must come before the full term ends.',
    desk_contract_notice_days_hint: 'Enter a positive whole number of notice days.',
    desk_contract_lease_model_hint: 'Check the model and the full term in months.',
    desk_contract_partial_payment: 'Partial payment',
    desk_contract_commission_colon: 'Commission:',
    desk_contract_amount_paid: 'Amount paid',
    desk_contract_remaining_colon: 'Remaining:',
    desk_contract_close: 'Close',
    desk_contract_confirm: 'Confirm',
    desk_contract_commercial_terms: 'Commercial terms',
    desk_contract_location_unclear: 'Location to be confirmed',
    desk_contract_agent_choice_needed: 'Agent needs to choose',
    desk_contract_change: 'Change',
    desk_contract_owner_to_agency: 'Owner → senTavus',
    desk_contract_tenant_to_agency: 'Tenant → senTavus',
    desk_contract_fee_percent_of_rent: '% of one month\'s rent',
    desk_contract_fee_fixed: 'Fixed amount',
    desk_contract_fee_other_basis: 'Other basis',
    desk_contract_fee_none: 'No fee',
    desk_contract_city_tbilisi: 'Tbilisi',
    desk_contract_city_batumi: 'Batumi',
    desk_contract_policy_TBILISI_RESIDENTIAL_RENTAL_V1: 'Tbilisi residential rental — standard terms',
    desk_contract_fees_separate: 'Rent stays the owner\'s separate claim; the two commissions do not cover each other\'s obligation.',
    desk_contract_policy_moved: 'The property\'s location changed. The new suggestion is {policy}; the current terms were not changed automatically.',
    desk_contract_policy_accept: 'Accept the new rule',
    desk_contract_policy_keep: 'Keep the current terms',
    desk_contract_rel_owner_agency: 'Owner\'s agency agreement',
    desk_contract_event_initial_payment_received: 'Initial payment received',
    desk_contract_event_initial_transfer_initiated: 'Initial transfer initiated',
    desk_contract_event_initial_payment_deadline_extended: 'Payment deadline extended by agreement of the parties',
    desk_contract_event_key_collection_requested: 'Key collection requested',
    desk_contract_event_key_handover_recorded: 'Actual key handover recorded',
    desk_contract_event_post_handover_defect_reported: 'Defect reported after handover',
    desk_contract_event_reference_material_added: 'Photo/video material added',
    desk_contract_chip_remote_pending_payment: 'Signed · awaiting payment',
    desk_contract_chip_remote_failed: 'Did not take effect',
    desk_contract_chip_remote_if_paid: 'In force if paid',
    desk_contract_chip_remote_scheduled: 'Takes effect on the start date',
    desk_contract_chip_remote_awaiting_handover: 'In force · awaiting handover',
    desk_contract_chip_remote_handed_over: 'In force · handed over',
    desk_contract_ref_media_title: 'Photo/video material provided before signing',
    desk_contract_ref_media_note: 'This material describes the condition as presented. The in-person viewing and the key handover are confirmed separately later.',
    desk_contract_ref_media_optional: 'optional',
    desk_contract_ref_media_note_inspected: 'The tenant has already seen the property, so this material is optional. Anything added here is shown to the signer and becomes part of what they sign.',
    desk_contract_photo: 'Photo',
    desk_contract_annex_sha: 'Annex SHA-256:',
    desk_contract_amount: 'Amount',
    desk_contract_txn_number: 'Transaction number',
    desk_contract_evidence_ref: 'Evidence number / link',
    desk_contract_ev_account_credit: 'Account credit confirmation',
    desk_contract_ev_bank_statement: 'Bank statement',
    desk_contract_ev_provider_settlement: 'Provider settlement',
    desk_remote_state_pending: 'Signed · initial payment expected',
    desk_remote_state_failed: 'Payment condition not met · the contract did not take effect',
    desk_remote_state_if_paid: 'In force on condition the initial payment was made · no payment is recorded yet — record it below',
    desk_remote_state_scheduled: 'Payment confirmed · awaiting the lease start',
    desk_remote_state_awaiting_handover: 'In force · key handover expected',
    desk_remote_state_handed_over: 'In force · keys handed over',
    desk_contract_save_failed: 'Could not save',
    desk_remote_title: 'Remote lease status',
    desk_remote_deadlines: 'Signed deadline: {original} · current deadline: {current} (Georgian time)',
    desk_remote_bank_last_day: 'Last day of bank processing:',
    desk_remote_initial_amount: 'Initial amount:',
    desk_remote_confirmed: 'confirmed:',
    desk_remote_transfer_initiated: 'Transfer initiated:',
    desk_remote_credit_separately: 'The credit must be confirmed separately.',
    desk_remote_fully_credited: 'Fully credited:',
    desk_remote_effective: 'Took effect:',
    desk_remote_rent_starts: 'the rent period starts',
    desk_remote_keys_handed: 'Keys handed over:',
    desk_remote_release_allowed: 'The landlord no longer has to hold the property, unless the parties separately extend the deadline.',
    desk_remote_initiation_title: 'Confirm transfer initiation',
    desk_contract_ev_swift: 'SWIFT confirmation',
    desk_contract_ev_payment_order: 'Payment order',
    desk_contract_ev_bank_receipt: 'Bank receipt',
    desk_contract_ev_provider_initiation: 'Provider confirmation',
    desk_contract_txn_number_swift: 'Transaction number (SWIFT / UETR)',
    desk_remote_record_initiation: 'Record initiation',
    desk_remote_credit_title: 'Confirm credit to the designated account',
    desk_remote_record_credit: 'Record credit',
    desk_remote_extension_title: 'New deadline agreed in writing by the parties',
    desk_remote_landlord_confirmation: 'Landlord\'s confirmation',
    desk_remote_tenant_confirmation: 'Tenant\'s confirmation',
    desk_remote_record_deadline: 'Record new deadline',
    desk_remote_key_request_title: 'Key collection · at least {hours} hours in advance',
    desk_remote_tenant_notice_evidence: 'Evidence of the tenant\'s notice',
    desk_remote_record_notice: 'Record notice',
    desk_remote_handover_title: 'Actual key handover · Annex A',
    desk_remote_handover_first: 'First fill in the handover record below: new photos, condition, keys, meters, inventory and defects.',
    desk_remote_record_time: 'Record actual time',
    desk_remote_then_annex: 'After this the landlord and the tenant sign only Annex A, on the same personal link.',
    desk_remote_defect_title: 'Pre-existing defect found later',
    desk_remote_defect: 'Defect',
    desk_remote_defect_evidence: 'Photo/video evidence or link',
    desk_remote_add_record: 'Add a separate record',
    desk_tfee_pending_result: 'Awaiting the brokerage result',
    desk_tfee_due: 'Due',
    desk_tfee_partial: 'Partly paid',
    desk_tfee_paid: 'Paid',
    desk_tfee_record_failed: 'Could not record the payment',
    desk_tfee_title: 'Tenant\'s agency fee',
    desk_tfee_total: 'total',
    desk_tfee_remaining: 'remaining',
    desk_tfee_last_received: 'Last received:',
    desk_tfee_note: 'Confirm a received amount only with evidence of receipt. The signed contract stays unchanged.',
    desk_tfee_amount_received: 'Amount received',
    desk_tfee_evidence_type: 'Evidence type',
    desk_contract_ev_cash_receipt: 'Cash receipt',
    desk_tfee_payment_evidence: 'Payment evidence',
    desk_tfee_lease_evidence: 'Evidence the lease was concluded',
    desk_tfee_document_ref: 'Document number / link',
    desk_tfee_record: 'Record receipt',
    desk_contract_copied: 'Copied!',
    // ── The CONTRACTS TAB (owner 2026-09-11) ────────────────────────────────────────────
    // The list reuses desk_contract_status_* / desk_contract_kind_* / desk_contract_lang_*
    // above rather than restating them — one status word, one document name, everywhere.
    // ── The mailbox signed contracts are sent from (admin only, owner 2026-09-11) ──────
    mail_section: 'Sending mailbox',
    mail_why: 'The address signed contracts are sent from. The password goes straight to the server and is never shown again.',
    mail_on: 'Sending is on',
    mail_off: 'Not set up — nothing can be emailed yet',
    mail_provider: 'Provider',
    mail_host: 'SMTP server',
    mail_port: 'Port',
    mail_user: 'Mailbox',
    mail_pass: 'Password',
    mail_pass_keep: 'leave blank to keep the current one',
    mail_from: 'Sender shown to the client',
    mail_save: 'Check and save',
    mail_port_blocked: 'The server cannot reach the mail host on this port — for Gmail use port 587.',
    mail_saved: 'Saved — the login was accepted',
    mail_failed: 'Could not connect',
    mail_test: 'Send a test',
    mail_test_to: 'address',
    mail_test_sent: 'Test sent',
    mail_admin_only: 'Only an admin can set the sending mailbox.',
    mail_app_password_hint: 'With two-step verification on, the normal password is refused — create an app password in the mailbox account and paste that here.',
    // The figures on a card. Short on purpose: these label a number in a list, they do not
    // restate the template's form label.
    // The 'all' SUB-TAB. Not 'any status' — that was a dropdown's placeholder; a tab is a pile.
    contracts_all: 'All',
    // Why there is no "new contract" door. One sentence each, naming the thing that is
    // missing — an empty space names nothing (owner 2026-09-12).
    desk_contract_needs_core: 'This meeting is an older record and cannot hold a contract. Book a new meeting to draw one.',
    desk_contract_needs_property: 'Attach the property to this meeting and a contract can be drawn from it.',
    // On a TYPE row: what that particular document is waiting for. The service agreement is
    // agency↔owner and asks for neither a tenant nor a client (owner 2026-09-12).
    desk_contract_type_needs_client: 'needs a client',
    desk_contract_type_needs_property: 'needs a property',
    desk_contract_needs_client: 'Name the client on this meeting and a contract can be drawn from it.',
    desk_contract_needs_both: 'A contract needs both sides — the property and the client — on the meeting first.',
    desk_contract_needs_rights: 'Only someone working this meeting can draw up its contract.',
    meeting_contacts_unavailable: 'Contacts for this property could not be read.',

    // ── The browse sidebar (owner 2026-09-11) ────────────────────────────────────────
    contracts_no_match_filters: 'Nothing matches these filters.',
    desk_contract_photos_toggle: 'Include a condition annex in this contract',
    desk_contract_photos_has_photos: 'Remove the photographs first — they are signed with the contract',
    // Who signs for the agency (owner ruling 2026-09-12). The card states it; only an
    // authorised agent is offered the switch, and nobody types a name.
    agent_sign_for_agency: 'May sign contracts for senTavus',
    agent_sign_for_agency_help: 'With this on, the agent can put themselves in place of the director as the agency signatory on a contract they prepare. They still cannot name anybody else.',
    agent_sign_no_party: 'This agent has never acted in the core system, so there is no account to grant it to yet.',
    agent_sign_failed: 'could not be changed',
    // Public profile on sentavus.com (owner ruling 2026-09-23, core 2113): a new agent's profile becomes public by
    // itself 14 days after they join unless an admin decides otherwise here.
    agent_public_profile: 'Public profile on sentavus.com',
    agent_public_profile_help: 'A new agent’s profile becomes public by itself 14 days after they join, unless an admin decides otherwise here.',
    agent_public_profile_public: 'Public',
    agent_public_profile_pending: 'Becomes public automatically on',
    agent_public_profile_hidden: 'Not public',
    agent_public_profile_none: 'No profile yet — it is created when the agent is first linked to the system.',
    agent_public_profile_publish_now: 'Publish now',
    agent_public_profile_block: 'Don’t publish',
    agent_public_profile_unpublish: 'Take off the site',
    agent_public_profile_view: 'Open',
    // Messaging, phase 1 (owner 2026-09-23, core 2240): website visitors' chats with agents
    chat_title: 'Messages', chat_scope_mine: 'Mine', chat_scope_all: 'All', chat_empty: 'No messages yet. Website visitors who press “Message” on a property you know, or on your profile, appear here.',
    chat_visitor: 'Website visitor', chat_you: 'You', chat_waiting: 'waiting for a reply', chat_pick: 'Choose a conversation',
    chat_property: 'The property', chat_seen: 'Seen', chat_placeholder: 'Write a reply…', chat_send: 'Send',
    chat_send_failed: 'Not sent — please try again.', chat_read_only: 'This conversation is handled by another agent; you can read it but not reply.',
    chat_close: 'Close', chat_back: 'All conversations',
    chat_tab_clients: 'Clients', chat_tab_team: 'Team', chat_empty_team: 'No team chats yet. Start one with a colleague, or discuss a flat or a client with the team.',
    chat_new_team: 'New chat with a colleague', chat_find_colleague: 'Find a colleague…', chat_colleague: 'Colleague',
    chat_about_property: 'Flat', chat_about_client: 'Client', chat_rooms: 'rooms', chat_typing: 'is typing…',
    chat_photo: 'Photo', chat_photo_invalid: 'Only JPG, PNG or WebP photos up to 5 MB.', chat_from_site: 'sentavus.com',
    chat_flagged_hint: 'Contains a phone number — visible to managers', chat_discuss_team: 'Discuss with the team', chat_discuss_not_yours: 'Only this client’s agents and management can open its team chat.', chat_tab_flagged: 'Flagged', chat_flagged_direct: 'Direct chat', chat_flagged_intro: 'Team messages that contained a phone number. They were sent as written; this list is for review.', chat_flagged_empty: 'No flagged messages.',
    // groups + the general chat (owner 2026-09-29, core 2717)
    chat_general: 'All agents', chat_general_hint: 'Every active agent — you see messages from the day you joined', chat_new_group: 'New group', chat_group_name: 'Group name…',
    chat_group_pick: 'Choose colleagues', chat_group_create: 'Create group', chat_group_add: 'Add people', chat_group_added: 'Add', chat_mute: 'Mute sound (pop-ups stay)', chat_unmute: 'Turn sound back on',
    desk_contract_agency_locked: 'set by the agency',
    desk_contract_agency_signed_by: 'Signed for the agency by',
    desk_contract_agency_me: 'me',
    desk_contract_agency_default: 'the director',
    desk_contract_agency_no_signatory: 'No signatory has been authorised yet — an admin must grant it before a contract can be drawn.',
    contracts_filters: 'Filters',
    contracts_filters_done: 'Show results',
    contracts_search_mode: 'Search mode — the filters are off while an exact search is on.',
    contracts_f_all: 'All contracts',
    contracts_f_status: 'Status',
    contracts_f_type: 'Type',
    contracts_f_lang: 'Language',
    contracts_f_date: 'Date',
    contracts_f_agent: 'Agent',
    contracts_f_agent_mine: 'Mine',
    contracts_f_agent_all: 'Everyone',
    // Sent, and somebody followed the link. Not a stored status — see the tab's own note.
    contracts_status_opened: 'Opened',
    contracts_date_today: 'Today',
    contracts_date_7d: 'Last 7 days',
    contracts_date_month: 'This month',
    contracts_date_custom: 'Custom',
    desk_contract_kind_sale_preliminary: 'Preliminary sale agreement',

    // §6a — the masked row says what it is, and the list says how much of the book is on screen.
    // A closed contract has no review to send; what it has is the signed file.
    contracts_pdf: 'Signed PDF',
    contracts_pdf_failed: 'could not be opened',
    contracts_pdf_engine: 'the server cannot build PDFs right now',
    contracts_review_open_links: 'Review links',
    contracts_masked: 'You cannot act on this meeting — ID numbers, contacts and figures are hidden, and there is no review link to give.',
    contracts_count_unit: 'contracts',
    contracts_count_per_page: 'per page',

    // The agency's own blank forms — a pile, not a status. See the panel's header comment.
    contracts_forms: 'Forms',
    contracts_forms_why: 'The agency standard contracts, with every blank still blank. Send one so the terms can be read and agreed before a deal is typed into it. Nobody edits these, and the link never expires.',
    contracts_forms_none: 'No forms are published yet.',
    contracts_form_open: 'Open',
    contracts_form_copy: 'Copy link',
    contracts_money_rent: 'rent',
    contracts_money_deposit: 'security deposit',
    contracts_money_binder_paid: 'binder paid',
    contracts_money_fee: 'fee',
    contracts_money_binder_amount: 'binder',
    contracts_money_deal_price: 'price',
    contracts_money_refund_amount: 'refund',
    nav_contracts: 'Contracts',
    contracts_title: 'Contracts',
    contracts_search_ph: 'A hash, a link code, a name or a phone number',
    contracts_search_hint: 'A name or a phone searches your own contracts; for a hash the first 10 characters are enough.',
    contracts_no_match: 'Nothing found — check the hash',
    contracts_empty: 'No contracts yet.',
    contracts_error: 'Could not load the contracts.',
    contracts_unavailable: 'This server does not have the contracts module yet.',
    contracts_status_all: 'Any status',
    contracts_lang_all: 'Any language',
    contracts_clear: 'Clear',
    contracts_more: 'See more',
    contracts_copy_hash: 'Copy the full hash',
    contracts_copied: 'Copied',
    contracts_no_meeting: 'no meeting',
    contracts_signed_title: 'signed',
    contracts_review_send: 'Send to review',
    contracts_review_readonly: 'A review link is read-only — it cannot be signed.',
    contracts_review_links: 'Review links',
    contracts_review_note_ph: 'a note for the reader (optional)',
    contracts_review_days_ph: 'days',
    contracts_review_mint: 'Create link',
    contracts_review_minting: 'Creating…',
    contracts_review_opens: 'opened {n}×',
    contracts_review_never: 'not opened yet',
    contracts_review_expires: 'until {at}',
    contracts_review_revoke: 'Revoke this link',
    contracts_review_none: 'No review links yet.',
    contracts_share_whatsapp: 'WhatsApp',
    contracts_share_email: 'Email',
    contracts_email_subject: 'Contract for review',
    desk_contract_no_contact: 'No phone or email on file — pick the chat yourself.',
    desk_contract_mint_links: 'Create signing links',
    desk_contract_choose_client: 'Choose the client',
    desk_contract_progress_restored: 'Unsaved progress restored (kept on this device, {when})',
    desk_contract_progress_discard: 'Discard and start over',
    desk_contract_draft_unfinished: 'Unfinished draft',
    desk_contract_draft_open: 'Continue filling in',
    desk_contract_send_confirm: 'Send the signing link to the {role}: {name}{to}?',
    contracts_form_drafts: 'Unfinished forms',
    desk_contract_draft_delete: 'Delete draft',
    desk_contract_draft_delete_confirm: 'Delete this unfinished draft? The answers typed into it will be lost.',
    desk_contract_tpl_rent_residential_v1: 'Residential lease',
    desk_contract_tpl_rent_commercial_v1: 'Commercial lease',
    desk_contract_tpl_agency_service_v1: 'Agency service agreement (owner)',
    desk_contract_tpl_tenant_agency_service_v1: 'Agency service agreement (tenant)',
    desk_contract_tpl_advance_v1: 'Advance and reservation',
    desk_contract_tpl_binder_v1: 'Earnest money',
    desk_contract_void_btn: 'Void',
    desk_contract_void_why: 'why is it voided?',
    desk_contract_void_confirm: 'Void the contract',
    // The contract PAGE (#contract/<id>) and the card's type chooser (owner 2026-09-10:
    // "a separate link … choose a type … editable until signed").
    desk_contract_new: 'Contract',
    desk_contract_new_title: 'New contract',
    desk_contract_fee_model_owner: 'owner pays',
    desk_contract_fee_model_both: 'owner and tenant both pay',
    desk_contract_tenant_part_of_both: 'the tenant’s part when both pay',
    desk_contract_policy_OWNER_PAYS_RESIDENTIAL_RENTAL_V1: 'Owner pays — at the cooperation rate',
    desk_contract_policy_BOTH_PAY_RESIDENTIAL_RENTAL_V1: 'Owner and tenant both pay — 20% + 20% of the rent',
    desk_contract_choose_type: 'Choose a type',
    desk_contract_types_none: 'No contract templates are available.',
    desk_contract_open_page: 'Open the contract page',
    desk_contract_page_loading: 'Loading…',
    desk_contract_not_found: 'Contract not found.',
    desk_contract_bad_link: 'This link names no contract.',
    desk_contract_copy_page: 'Copy page link',
    desk_contract_back: 'Back',
    desk_contract_meeting_line: 'Meeting',
    desk_contract_document: 'document',
    desk_contract_links_section: 'Signing links',
    desk_contract_save_btn: 'Save',
    desk_contract_saved: 'Saved as a new version — the signing links were re-minted; send them again.',
    desk_contract_created: 'Created. Mint the signing links and send them.',
    desk_contract_edit_discard_btn: 'Edit — signatures will be discarded',
    desk_contract_edit_discard_hint: 'Already signed by: {who}. Saving a change renders a new version; their signature stays on the old one and they must sign again.',
    desk_contract_edit_discard_hint_short: 'Saving discards the signatures already given.',
    desk_contract_signatures: 'Signatures',
    desk_contract_sig_none: 'not signed',
    desk_contract_method_remote_link: 'remotely, by personal link',
    desk_contract_method_assisted_device: "from the agent's device",
    desk_contract_method_paper_uploaded: 'on paper, uploaded',
    desk_contract_method_qualified_esign: 'qualified e-signature',
    desk_contract_print: 'Print',
    desk_contract_err_signed: 'Fully signed — it can no longer be changed. Void it and create a new one.',
    desk_contract_err_partial: 'Someone has already signed this version. Use "Edit — signatures will be discarded".',
    // Addressed to the PARTY, not the agent: Georgian for a Georgian document whatever the
    // agent's UI language, English only for an English-only contract.
    desk_contract_msg_text: 'გამარჯობა, {name}. გთხოვთ გაეცნოთ და ხელი მოაწეროთ ხელშეკრულებას ამ ბმულზე: {url} — senTavus',
    desk_contract_msg_text_en: 'Hello, {name}. Please read and sign the agreement at this link: {url} — senTavus',
    // The builder (owner 2026-09-10: "make this contract buildable … modular").
    desk_contract_lang_ru: 'Russian',
    // The language PAIR (owner 2026-09-10: Georgian plus at most one other, side by side).
    desk_contract_lang_second: 'second language',
    desk_contract_lang_none: 'none',
    desk_contract_lang_primary_hint: 'prevailing text',
    desk_contract_name_in_en: 'name in English',
    desk_contract_name_in_ru: 'name in Russian',
    desk_contract_name_in_ph: 'as it should appear in that language (optional)',
    // The SET of 1–3 languages + one canonical legal name (owner 2026-09-10 night, migration 1044).
    desk_contract_name_in_ka: 'name in Georgian',
    desk_contract_legal_name: 'Name, surname — as on the ID',
    desk_contract_link_lang: 'link language',
    // Identity vs contact (owner 2026-09-11, migration 1051, design §9.13).
    desk_contract_party_kind: 'party type',
    desk_contract_party_kind_person: 'individual',
    desk_contract_party_kind_company: 'company',
    desk_contract_company_name: 'Company legal name — as registered',
    desk_contract_company_name_ph: 'e.g. LLC Alpha',
    desk_contract_id_kind: 'ID document',
    desk_contract_id_personal_no: 'ID card',
    desk_contract_id_passport_hint: 'letters and digits, any country\'s format',
    desk_contract_idnum_personal_no: 'personal number · 11 digits',
    desk_contract_idnum_passport: 'passport number · letters and digits, any country\'s format',
    desk_contract_idnum_company_reg: 'registration number',
    desk_contract_cobroke_block: 'The other agent',
    desk_contract_notice_channels: 'Notice channels',
    desk_contract_notice_channels_help: 'A message by SMS, WhatsApp or email to these contacts counts as notice, delivered by the next working day. A party with no shared contact is not named — written notice at the address still applies.',
    desk_contract_notice_other: 'another channel',
    desk_contract_contact_share: 'Share contact details with the other party',
    desk_contract_contact_hide: 'Hide contact details until signed',
    desk_contract_contact_omit: 'Hide contact details',
    desk_contract_contact_omit_on: 'Not printed in this contract; the other parties do not see them.',
    desk_contract_contact_omit_off: 'Printed in the contract:',
    desk_contract_contact_hide_on: 'Hidden from the other party until everyone signs; then printed in the signed PDF.',
    desk_contract_many_residents: 'An unusually high number of permanent residents. Confirm with the landlord that this is what was agreed.',
    desk_contract_contact_hide_off: 'Visible to the other party from the start and printed in the contract.',
    desk_contract_contact_share_on: 'After every signature the other party receives:',
    desk_contract_contact_share_off: 'Not shared — notices go through senTavus. The signer can change this on their page.',
    desk_contract_notice_same: 'The contact details above are used.',
    desk_contract_notice_hide: 'hide',
    desk_contract_notice_missing: 'no channel — links cannot be sent until one is given',
    desk_contract_notice_address: 'Physical address',
    desk_contract_notice_private_help: 'Private: the other party never sees these. Each signer confirms their own on their signing page.',
    desk_contract_notice_none: 'no shared contact — written notice at the address',
    desk_contract_notice_phone: 'Phone (SMS / WhatsApp)',
    desk_contract_notice_use_party: 'use the party contacts',
    desk_contract_fee_cobroke_note: 'With another agent involved, enter the FULL fee the owner pays — both shares together (e.g. 600 = 300 + 300).',
    desk_contract_agency_share: 'senTavus share',
    desk_contract_agency_share_zero: 'The fee must be the FULL amount the owner pays — both shares together (e.g. 600 for 300 + 300).',
    desk_contract_share_auto: 'half the fee, filled automatically',
    desk_contract_id_11_error: 'A personal number is exactly 11 digits.',
    desk_contract_id_duplicate_error: 'Both parties carry this same number. One of them belongs to somebody else.',
    desk_contract_date_end_before_start: 'The tenancy would end before it starts.',
    desk_contract_date_deadline_before_agreement: 'The deadline falls before the agreement that sets it.',
    desk_contract_amount_exceeds_advance: 'More than the advance itself.',
    desk_contract_amount_currency_differs_from_advance: 'Not in the advance\'s currency.',
    desk_contract_amount_negative: 'Cannot be negative.',
    desk_contract_reason_required_for_smaller_refund: 'Say why less than the whole advance is returned.',
    desk_contract_reason_without_smaller_refund: 'The refund is the whole advance — remove the reason.',
    desk_contract_err_advance_amount: 'A part of the advance is larger than the advance or in another currency.',
    desk_contract_date_deadline_after_move_in: 'The deadline to sign the main agreement falls after the tenancy has already started.',
    desk_contract_date_contingency_before_agreement: 'The condition’s deadline falls before the agreement that sets it.',
    desk_contract_parts_total: 'The parts come to {n}, and the sum paid is {m}.',
    desk_contract_parts_currency: 'The parts must be in the same currency as the sum paid.',
    desk_contract_agent_none: 'no authorised agents',
    desk_contract_err_fund_receiver: 'the person named as receiving the money is no longer authorised to hold company funds — choose another',
    desk_annex_date_handover: 'date and time of handover',
    desk_annex_date_return: 'date the tenant vacated and handed back the keys',
    desk_annex_date_needed: 'required before this act can be signed',
    desk_annex_carry_over: 'carry over from the handover act',
    desk_return_sec_inventory: 'Inventory — as listed at handover',
    desk_contract_fxdate_agreement: 'agreement date',
    desk_contract_fxdate_payment: 'payment date',
    desk_contract_fxdate_today: 'today',
    desk_contract_bad_value: 'This answer is not one this version of the contract can state.',
    desk_contract_share: 'share',
    desk_contract_shared: 'shared ✓',
    desk_contract_share_tip: 'shown to the other party and printed in the contract',
    desk_contract_id_passport: 'passport',
    desk_contract_id_company_reg: 'company registration number',
    desk_contract_id_number: 'number',
    desk_contract_id_number_ph: 'required — as on the document',
    desk_contract_rep_name: 'representative — name',
    desk_contract_rep_position: 'position / authority',
    desk_contract_rep_position_ph: 'e.g. director',
    desk_contract_phone: 'phone',
    desk_contract_email: 'email',
    desk_contract_share_phone: 'shown to the other party and in the contract',
    desk_contract_share_email: 'shown to the other party and in the contract',
    desk_contract_identity_note: 'You fill these in before the links go out. The parties read them and agree by signing — they cannot change them. A change after a signature is a new version.',
    desk_contract_identity_confirmed: 'confirmed by the party',
    desk_contract_lang_first_prevails: 'the first language prevails',
    desk_contract_lang_prevails_note: 'Georgian is off — the {lang} text prevails.',
    desk_contract_err_drift: 'The translations do not match — see the clauses listed below.',
    desk_contract_drift_title: 'Translation mismatch',
    desk_contract_drift_placeholders: 'Clause {clause_no}: the {lang} text names different fields',
    desk_contract_drift_missing_text: 'Clause {clause_no}: the {lang} text is missing',
    desk_contract_sections: 'Sections',
    desk_contract_sections_hint: 'Only optional sections can be switched off.',
    desk_contract_sections_locked: 'Sections are locked once someone has signed.',
    desk_contract_section_mandatory: 'Always included',
    desk_contract_section_conditional: 'Included when the answer calls for it — change the field, not the structure',
    desk_contract_section_follows_answer: 'follows the answer',
    desk_contract_section_custom: 'custom',
    desk_contract_section_custom_title: 'Custom section',
    desk_contract_section_on: 'Included — switch off',
    desk_contract_section_off: 'Switched off — switch on',
    desk_contract_section_add: 'Add a section',
    desk_contract_section_remove: 'Remove this section',
    desk_contract_section_editors: 'Section texts',
    desk_contract_clause_text_hint: '{{…}} are values from the form above — they are filled in when the document is rendered.',
    desk_contract_clause_shown_when: 'Shown when',
    desk_contract_clause_edited: 'edited',
    desk_contract_clause_reset: '↺ Default text',
    desk_contract_photos_soon: 'Photos — soon',
    desk_contract_photos_tip: 'Photos of the property’s condition — the annex the parties sign together with the contract',
    desk_contract_annex_pending_note: 'They are uploaded the moment the contract is created.',
    // ── The closed contract (migration 1046): the drawn signatures ON the document, the PDF ──
    desk_contract_closed_chip: 'closed · signed',
    desk_contract_completed_at: 'completed · {at}',
    desk_contract_signed_at: 'signed · {at}',
    desk_contract_viewed_lang: 'viewed in',
    desk_contract_doc_hash: 'Document hash (SHA-256)',
    desk_contract_prevails_short: 'prevails',
    desk_contract_annex_photo_n: 'Photo {n}',
    desk_contract_sig_email_will: 'PDF goes to {email}',
    desk_contract_sig_email_sent: 'PDF sent to {email}',
    desk_contract_sig_no_email: 'no email on file — the PDF cannot be emailed to them',
    desk_contract_pdf_btn: 'PDF',
    desk_contract_pdf_loading: 'Preparing the PDF…',
    desk_contract_pdf_missing: 'The PDF is not available yet — try again later',
    desk_contract_pdf_send_btn: 'Email the PDF',
    desk_contract_pdf_sending: 'Sending…',
    desk_contract_pdf_sent_at: 'PDF sent · {at}',
    desk_contract_mail_unconfigured: 'Email sending is not configured yet — download the PDF and send it yourself',
    desk_contract_send_ok: 'sent to {to}',
    desk_contract_send_fail: 'not sent',
    desk_contract_send_reason_no_email: 'no email on file',
    desk_contract_send_reason_send_failed: 'the mail server refused it',
    desk_contract_err_not_signed: 'Not everyone has signed yet.',
    // Prepayment list (ContractComposer PrepayListInput, template v3 / migration 1048). The
    // month labels and the sentence under the rows come from src/lib/prepaySentence.js.
    desk_contract_prepay_which: 'Which',
    desk_terms_prepay_months: 'Paid in advance (months)',
    desk_terms_prepay_part: 'part',
    desk_contract_prepay_full: 'in full',
    desk_contract_prepay_paid: 'paid',
    desk_contract_prepay_remaining: 'remaining',
    desk_contract_prepay_due: 'due by',
    desk_contract_prepay_add: '＋ add a month',
    desk_contract_prepay_remove: 'remove',
    desk_contract_prepay_none: 'Nothing is paid in advance.',
    desk_contract_prepay_none_required: 'Press “＋ add a month” and choose the month the tenant pays for at signing — at least one.',
    // ── Design build (2026-09-11, design/CONTRACTS_DESIGNER_HANDOFF_2026-09-11.md round 1) ──
    // The status chip (one component, card + page). "N of M signed" for partially_signed.
    desk_contract_chip_closed: 'closed',
    desk_contract_chip_signed_of: '{n} of {total} signed',
    // 🔁 lease editions (owner 2026-10-08, core 3097/3098)
    desk_contract_chip_superseded: 'replaced',
    desk_contract_chip_not_yet_effective: 'signed · not yet in force',
    desk_contract_status_expired: 'expired',
    desk_revision_edition: 'Edition {n}',
    desk_revision_new_title: 'New edition {n} of the lease',
    desk_revision_new_replaces: 'Replaces edition {n}, signed {at} (#{hash}). Nothing in it is changed or deleted.',
    desk_revision_new_rule: 'The current edition stays in force until every party signs this one and it takes effect (today, or the effective date you set).',
    desk_revision_new_carried: 'The move-in is not repeated: no new key handover or initial payment. The deposit already held and rent already prepaid carry over; the original handover act stays the tenancy\'s act.',
    desk_revision_replaces: 'Replaces edition {n}, signed {at}.',
    desk_revision_open: 'open',
    desk_revision_open_successor: 'Open the edition that replaces it',
    desk_revision_effective_date: 'Takes effect on {d} (or at the last signature, if later).',
    desk_revision_changes: 'What changed',
    desk_revision_wording: 'Wording updated to the current template (v{from} → v{to})',
    desk_revision_wording_clause: 'Clause {n}',
    desk_revision_wording_removed: 'This clause is no longer in the contract.',
    desk_revision_no_changes: 'Nothing has changed against the signed edition yet.',
    desk_revision_state_pending_until: 'Offered for signature until {at}',
    desk_revision_state_pending_unsent: 'Not sent yet — the 7 days start when you send it',
    desk_revision_state_expired: 'Expired unsigned — edition {n} stays in force',
    desk_revision_state_not_yet_effective: 'Fully signed — takes effect {at}; the previous edition governs until then',
    desk_revision_state_in_force: 'In force since {at}',
    desk_revision_state_in_force_plain: 'In force',
    desk_revision_state_superseded: 'Replaced from {at} — kept as the record of what was signed',
    desk_revision_state_void: 'Voided',
    desk_revision_release_pending: 'The replacement clause is waiting for legal review. This edition can be prepared and read, but not sent for signature yet.',
    desk_revision_release_btn: 'Release this contract for signing (owner approval)',
    desk_revision_released_at: 'Released for signing by the owner {at}',
    desk_revision_pending_exists: 'A new edition is being prepared:',
    desk_revision_chain: 'Editions of this tenancy',
    desk_revision_chain_pending: 'in progress',
    desk_revision_chain_expired: 'expired',
    desk_revision_chain_void: 'voided',
    desk_revision_chain_not_yet_effective: 'signed, not yet in force',
    desk_revision_chain_in_force: 'in force',
    desk_revision_chain_superseded: 'replaced',
    desk_revision_new_btn: 'New edition — change terms and re-sign',
    desk_revision_new_btn_hint: 'Everyone signs the new edition; when it takes effect it replaces this one. This contract stays as it is.',
    // The dark header's language pills (ქარ / ENG / + РУС).
    desk_contract_lang_short_ka: 'ქარ',
    desk_contract_lang_short_en: 'ENG',
    desk_contract_lang_short_ru: 'РУС',
    desk_contract_lang_add: 'Add this language',
    desk_contract_lang_remove: 'Remove this language',
    desk_contract_lang_locked: 'The languages are locked — a change is a new version',
    // The paper: the groups in document order, and the source tags the designer added.
    desk_contract_group_property: 'Property',
    desk_contract_group_money: 'Term & money',
    desk_contract_group_use: 'Use & conditions',
    desk_contract_group_other: 'Other',
    desk_contract_src_version: 'from v{v}',
    desk_contract_sections_tap_hint: 'tap a section to read or edit its text',
    // The clause editor (one section at a time).
    desk_contract_clause_edit: 'edit',
    desk_contract_clause_original: '↺ original',
    desk_contract_clause_empty: 'no text yet',
    desk_contract_marker_hint: 'Green markers are values — do not type over them.',
    desk_contract_collapse: 'collapse',
    desk_contract_done: 'Done',
    // Drift, naming the clause and the placeholder (round 1 decision 9).
    desk_contract_drift_ph_named: 'Clause {clause_no} · {detail} missing in {lang}',
    desk_contract_drift_text_named: 'Clause {clause_no} · text missing in {lang}',
    desk_contract_drift_short: 'mismatch',
    // The sections sheet / rail.
    desk_contract_section_always_on: 'always on',
    desk_contract_photos_row: 'Photos',
    desk_contract_soon: 'soon',
    // The sticky bar.
    desk_contract_bar_sections: '{on} of {total} sections on',
    desk_contract_bar_fields_left: '{n} left to fill',
    desk_contract_bar_ready: 'ready',
    desk_contract_fields_left: '{n} fields left: {list}',
    desk_contract_save_new_version: 'Save · new version',
    desk_contract_rerender_tip: 'Render version N+1 from the values on the paper; the links are re-minted.',
    // Partially signed — the frozen card and its one door.
    desk_contract_frozen_who: '{name} signed at {at}.',
    desk_contract_frozen_hint: 'The text is frozen. Changing anything discards the signatures and issues version {n} — everyone signs again.',
    desk_contract_change_btn: 'Change — signatures will be discarded',
    // Closed.
    desk_contract_closed_line: 'signed {at} · v{v} · #{hash}',
    desk_contract_pdf_download: 'Download the PDF',
    // The right column / the cards under the paper.
    desk_contract_parties_links: 'Parties & links',
    desk_contract_history: 'History',
    desk_contract_history_none: 'nothing yet',
    // The condition-photo annex card (design §9.4 / §8.1, contract/AnnexCard.jsx).
    desk_contract_annex_title: 'Annex · property condition',
    desk_contract_annex_count: '{n} photos',
    desk_contract_annex_signed_by: 'Signed by: {roles}',
    desk_contract_annex_add: '＋ Add photos',
    desk_contract_annex_uploading: 'uploading {done} / {total}',
    desk_contract_annex_empty: 'Photos to record the handover condition — take them before you send the links.',
    desk_contract_annex_note: 'The parties see exactly these photos before signing; a change after a signature is a new version.',
    desk_contract_annex_confirm: 'This discards {n} signature(s) — continue?',
    desk_contract_annex_confirm_yes: 'Continue',
    desk_contract_annex_caption_ph: 'caption',
    desk_contract_annex_room_ph: 'room',
    desk_contract_annex_rooms: 'Living room|Kitchen|Bedroom|Bathroom|Hallway|Balcony',
    desk_contract_annex_remove: 'Remove photo',
    desk_contract_annex_readonly: 'The contract is closed — the annex no longer changes.',
    desk_contract_annex_after_signing: 'This lease was signed without a handover act. Record it now, at key handover: the date, condition, keys and meters (photos if you have them). The landlord and the tenant then confirm it from the links they signed with.',
    desk_contract_annex_after_signing_home: 'The handover act belongs to the tenancy and is kept with edition {n}, which was signed without one.',
    desk_contract_annex_after_signing_open: 'Record it there →',
    desk_contract_annex_pdf: 'Handover act PDF',
    desk_contract_send_no_act: 'This lease has no handover act yet — no condition record and no photos. Signers will be told the act is made and signed at key handover. Send anyway?',
    desk_contract_send_no_act_yes: 'Send without the act',
    desk_contract_send_no_act_fill: 'Fill the act first',
    desk_contract_annex_err_signed: 'The contract is fully signed — the annex cannot change.',
    desk_contract_annex_err_type: 'Unsupported file type — send a photo (JPEG, PNG, HEIC).',
    desk_contract_annex_err_storage: 'Photo storage is not configured on the server yet.',
    desk_contract_annex_err_too_large: '"{name}" is too large to send ({mb} MB).',
    desk_contract_event_created: 'created',
    desk_contract_event_rendered: 'rendered',
    desk_contract_event_link_minted: 'links minted',
    desk_contract_event_delivered: 'sent',
    desk_contract_event_opened: 'opened',
    desk_contract_event_signed: 'signed',
    desk_contract_event_declined: 'declined',
    desk_contract_event_rerendered: 'new version',
    desk_contract_history_until: 'last',
    desk_contract_event_annex_rerendered: 'handover record updated',
    desk_contract_event_return_annex_rerendered: 'return record updated',
    desk_contract_event_annex_signed: 'handover act signed',
    desk_contract_event_return_act_signed: 'return act signed',
    desk_contract_event_annex_photo_added: 'photo added to the handover act',
    desk_contract_event_annex_photo_removed: 'photo removed from the handover act',
    desk_contract_event_annex_sign_failed: 'handover act could not be signed',
    desk_contract_event_agency_rep_changed: 'agency signatory changed',
    desk_contract_event_commercial_policy_changed: 'commercial terms changed',
    desk_contract_event_fund_receiver_changed: 'payment recipient changed',
    desk_contract_event_identity_confirmed: 'identity confirmed',
    desk_contract_event_payment_evidence_reversed: 'payment record reversed',
    desk_contract_event_pdf_downloaded: 'PDF downloaded',
    desk_contract_event_pdf_mail_failed: 'PDF email failed',
    desk_contract_event_pdf_render_failed: 'PDF could not be generated',
    desk_contract_event_review_link_minted: 'review link created',
    desk_contract_event_review_link_revoked: 'review link cancelled',
    desk_contract_event_review_opened: 'review link opened',
    desk_contract_event_sign_blocked_fund_receiver: 'signing blocked — payment recipient not authorised',
    desk_contract_event_template_upgraded: 'moved to the latest contract text',
    desk_contract_event_tenant_agency_fee_received: 'tenant agency fee received',
    desk_contract_event_voided: 'voided',
    desk_contract_event_completed: 'completed — everyone signed',
    desk_contract_event_pdf_rendered: 'PDF rendered',
    desk_contract_event_pdf_sent: 'PDF emailed',
    // The party timeline.
    desk_contract_tl_not_minted: 'no link yet',
    desk_contract_tl_ready: 'ready',
    desk_contract_tl_sent: 'sent ({via})',
    desk_contract_tl_sent_none: 'sent',
    desk_contract_tl_opened: 'opened ({lang})',
    desk_contract_tl_signed: 'signed',
    desk_contract_tl_declined: 'declined · {reason}',
    desk_contract_handover_btn: 'Hand my phone to {name}',
    desk_contract_handover_tip: "Opens their own link on this device, marked as handed over; the signature is recorded as made on the agent's device.",
    // The meeting card's block and its type chooser.
    desk_contract_new_contract: 'New contract',
    desk_contract_type_agency_soon: 'Agency service agreement',
    desk_contract_type_sale_soon: 'Sale',
  },
  ka: {
    nav_candidates: 'კანდიდატები', nav_agents: 'აგენტები', nav_schedule: 'განრიგი',
    nav_attendance: 'დასწრება', nav_history: 'ისტორია', nav_admin: 'ადმინი',
    nav_listings: 'აქტივობა',
    nav_deals: 'გარიგებები',
    // Listings (HR activity monitor)
    listings_today: 'დღეს',
    listings_prev_day: 'წინა დღე',
    listings_next_day: 'შემდეგი დღე',
    listings_kpi_published: 'გამოქვეყნების სესიები',
    listings_kpi_failed: 'ჩაიშალა',
    listings_kpi_active: 'აგენტები, ვინც გამოაქვეყნა',
    listings_kpi_meetings: 'შეხვედრები',
    listings_kpi_coops: 'გახსნილი თანამშრომლობები',
    listings_legend_coop: 'თანამშრომლობა გაიხსნა (დაჭერით იხსნება ლიდი)',
    listings_coop_tip: '{t} · თანამშრომლობა გაიხსნა · {i}',
    listings_coop_count: 'დღეს გახსნილი თანამშრომლობა: {n}',
    listings_open_lead: 'ლიდის გახსნა',
    listings_kpi_meetings_closed: '{n} დაიხურა',
    listings_kpi_meetings_cancelled: '{n} გაუქმდა',
    listings_kpi_meetings_failed: '{n} უშედეგო',
    listings_kpi_meetings_moved: '{n} გადაიდო',
    listings_kpi_of: '{n}-დან',
    listings_month_chart_title: 'დღიური გამოქვეყნებული განცხადებები',
    listings_month_chart_sub: 'გამოქვეყნებული განცხადებები დღეების მიხედვით — დააჭირეთ სვეტს დღის სანახავად',
    listings_month_total: 'სულ ამ თვეში',
    listings_overlay_show: 'წინა თვესთან შედარება',
    listings_overlay_hide: 'შედარების დამალვა',
    listings_overlay_note: 'დალაგებულია კვირის დღეებით, არა რიცხვებით',
    listings_overlay_total: 'წინა პერიოდი',
    listings_overlay_legend: 'წინა თვე (იგივე კვირის დღეები)',
    listings_prev_month: 'წინა თვე',
    listings_next_month: 'შემდეგი თვე',
    listings_timeline: 'დღევანდელი ქრონოლოგია',
    listings_legend_listings: 'განცხადებები',
    listings_legend_moved: 'გადაიდო',
    listings_legend_failed: 'ჩაშლილი (✕)',
    listings_legend_auto: 'ავტომატური გამოქვეყნება',
    listings_legend_auto_band: 'ავტომატური რეჟიმი ჩართულია (ნაცრისფერი: გამორთული)',
    listings_nodraft: 'დრაფტის გარეშე',
    listings_legend_pending: 'მიმდინარე / არ გამოქვეყნებულა (ცარიელი)',
    listings_legend_hint: 'ცარიელი საათები შეკუმშულია · დატვირთული საათები გაშლილია',
    listings_legend_shift: 'დაგეგმილი ცვლა',
    listings_legend_meeting: 'შეხვედრები',
    listings_meeting_moved: 'აქ იყო — გადაიდო',
    listings_meeting_revisited: 'განმეორებით ნახეს — შედეგი მომდევნო ვიზიტზეა',
    listings_meeting_revisited_at: 'განმეორებითი ვიზიტი {when}',
    listings_meeting_moved_to: 'აქ იყო — გადაიდო {when}-ზე',
    app_no_office_warning: 'თქვენი ანგარიში ოფისზე არ არის მიბმული — შესაძლოა თითქმის ვერ ხედავდეთ აგენტებს, განრიგებს და შეხვედრებს. სთხოვეთ ადმინს, რომ ოფისზე მიგაბათ.',
    listings_roster_fallback: 'აგენტების სახელები ჩაიტვირთა. ამ ხედში განრიგისა და დასწრების მონაცემები მიუწვდომელია.',
    listings_no_roster: 'აგენტების სია ვერ ჩაიტვირთა. სტრიქონებში სახელის ნაცვლად ლოგინი ჩანს; ცვლებისა და შეხვედრების მონაცემები აკლია.',
    listings_meeting_none: 'შეხვედრები არ არის',
    // Live meeting countdown (MeetingCountdown.jsx)
    desk_meeting_terms_missing: 'პირობები შესავსებია',
    desk_meeting_row_terms: 'პირობები',
    desk_meeting_row_involved: 'ჩართული',
    desk_meeting_row_going: 'მიდის',
    desk_meeting_row_client: 'კლიენტი',
    desk_meeting_row_client_rep: 'კლიენტის წარმ.', desk_meeting_row_property_rep: 'ობიექტის წარმ.',
    desk_meeting_row_outcome: 'შედეგი', desk_meeting_outcome_pill: 'შედეგი?', desk_meeting_outcome_title: 'როგორ ჩაიარა შეხვედრამ?',
    desk_meeting_outcome_reason_required: 'შესანახად აირჩიეთ მიზეზი.',
    desk_meeting_row_when: 'როდის',
    desk_meeting_row_restrictions: 'შეზღუდვები',
    desk_meeting_restr_none: 'არ არის',
    desk_meeting_restr_hidden: 'ჩაწერილია · დაფარულია',
    desk_meeting_restr_unknown: 'არ გვიკითხავს',
    meeting_slot_terms: 'გარიგების პირობები',
    meeting_terms_add: 'პირობების შევსება',
    meeting_terms_other_hint: 'ამ ობიექტზე სხვა აგენტს აქვს პირობები — მხოლოდ ინფორმაციისთვის:',
    meeting_terms_withheld: 'პირობები დაფიქსირებულია (გამოჩნდება, როცა თქვენსას შეავსებთ)',
    meeting_terms_edit: 'რედაქტირება',
    meeting_terms_loading: 'იტვირთება…',
    meeting_terms_no_contact: 'კონტაქტი ვერ მოიძებნა — დაამატეთ ობიექტის გვერდიდან',
    meeting_copy_brief: 'ჩატისთვის კოპირება',
    meeting_copy_done: 'დაკოპირდა!',
    meeting_cd_in: '{v}-ში',
    // ⚠️ NOT '{v}ს წინ' (proposed in the 2026-08-06 review and reverted after
    // rendering it): {v} is an ABBREVIATION string built by humanize() — "3სთ 12წთ",
    // "2დ 3სთ" — so the glued ს produces "12წთს წინ". The suffix would only work
    // after a spelled-out word. Render a format string before changing it.
    meeting_cd_ago: '{v} წინ',
    meeting_cd_now: 'ახლა იწყება',
    meeting_cd_d: 'დღ',
    meeting_cd_h: 'სთ',
    meeting_cd_m: 'წთ',
    listings_loading: 'იტვირთება…',
    listings_no_agents: 'აგენტები არ არიან.',
    listings_no_activity: 'აქტივობა არ არის',
    listings_now: 'ახლა',
    listings_group_office: 'ოფისი (განრიგით)',
    listings_group_remote: 'დისტანციური (განრიგით)',
    listings_group_absent: 'არ გამოცხადდა',
    listings_group_off: 'დასვენება',
    listings_status_published: 'გამოქვეყნდა',
    listings_status_filled: 'შევსებულია – არ გამოქვეყნებულა',
    listings_status_pending: 'მიმდინარე',
    listings_status_failed: 'ჩაიშალა',
    listings_published_count: '{n} გამოქვეყნდა',
    listings_failed_count: '{n} ჩაიშალა',
    listings_counts_compact: '{p}/{f} გამოქვ./ჩაიშ.',
    listings_ver_uptodate: 'განახლებული',
    listings_ver_outdated: 'მოძველებული',
    listings_ver_unknown: 'აპი არ ჩანს',
    listings_ver_lastseen: 'ბოლოს ნანახი',
    listings_kpi_pub_split: '{a} ავტომატური · {m} ხელით',
    listings_kpi_fail_sub: '{p} მიმდინარე · {f} შევსებული, არ გამოქვეყნებულა',
    listings_kpi_auto_only: '{n} მხოლოდ ავტომატურად',
    listings_unavailable: 'მიუწვდომელია',
    listings_nudges_unavailable: 'შეხვედრების შეხსენებები ვერ ჩაიტვირთა — ნიშნის არქონა არ ნიშნავს, რომ ვადაგადაცილებული არაფერია.',
    listings_updated_at: 'განახლდა {t}',
    listings_refresh: 'განახლება',
    listings_stale: 'მონაცემები არ არის განახლებული — ნაჩვენებია {t}-ის მდგომარეობა',
    listings_group_unmatched: 'ლოგინები, რომლებიც სიაში არ არის — ჯამში არ ითვლება',
    listings_unmatched_note: 'ლოგინები, რომლებიც არცერთ აგენტს არ ემთხვევა, ყველა ოფისის ხედში ჩანს და მის ჯამში არ ითვლება.',
    listings_checked_in: 'შემოვიდა {t}',
    listings_no_checkin: 'შემოსვლა არ დაფიქსირებულა',
    listings_worker_enabled: 'ავტომატიზაცია ჩართულია — ეს პარამეტრია და არა იმის დასტური, რომ კომპიუტერი მუშაობს',
    listings_worker_paused: 'ავტომატიზაცია შეჩერებულია',
    listings_open_advert: 'განცხადების გახსნა',
    listings_show_more: 'კიდევ {n}-ის ჩვენება',
    listings_details: 'დეტალები',
    listings_fail_reason: 'მიზეზი: {r}',
    listings_balance_latest: 'ბოლო ცნობილი ბალანსი · {t}',
    listings_bal_gauge: '{p} ბალანსები',
    listings_bal_gauge_sub: '{e} ცარიელი · {f} თანხით · {n} ანგარიში',
    listings_bal_wallet: 'მთავარი ანგარიში {b}₾ · შემოწმდა {t}',
    listings_bal_wallet_stale: 'მთავარი ანგარიში {b}₾ · არ შემოწმებულა {t}-დან',
    listings_paid_on: 'ss / myhome ჩართულია',
    listings_paid_off: 'ss / myhome გამორთულია',
    listings_paid_absent: 'სამუშაო დღეს არ გამოცხადდა',
    listings_paid_cap: 'შევსების ლიმიტი ამოიწურა: {p}',
    listings_paid_reset_until: 'ლიმიტები მოხსნილია {t}-მდე',
    listings_paid_limit_until: 'ადმინმა შეზღუდა {t}-მდე',
    listings_paid_limit: 'შეზღუდვა',
    listings_paid_reset: 'განულება',
    listings_paid_limit_hint: 'ავტომატური ss + myhome გაჩერდეს მითითებული საათებით',
    listings_paid_reset_hint: 'ss + myhome-ის ყველა ლიმიტის მოხსნა (გამოუცხადებლობა, ახალი თანამშრომლობები, შევსების ლიმიტი) მითითებული საათებით',
    listings_paid_hours: 'რამდენი საათით?',
    listings_paid_clear: 'გაუქმება',
    listings_month_scope_all: 'ყველა ოფისი',
    listings_pc_working: 'კომპიუტერი მუშაობს · {p}',
    listings_pc_idle: 'კომპიუტერი ჩართულია · უქმად',
    listings_auto_on: 'ავტომატური რეჟიმი ჩართულია',
    listings_auto_off: 'ავტომატური რეჟიმი გამორთულია',
    listings_pc_online: 'კომპიუტერი ჩართულია',
    listings_pc_offline: 'კომპიუტერი გამორთულია',
    listings_pc_gap: 'ბრაუზერი დუმდა {a}–{b} (გადატვირთვა?) · {d}',
    listings_legend_pc_gap: 'ბრაუზერის გადატვირთვა / მოკლე გათიშვა',
    listings_auto_off_paused: 'ავტომატური რეჟიმი გამორთულია - აგენტმა შეაჩერა',
    listings_auto_off_office: 'ავტომატური რეჟიმი გამორთულია - ოფისშია',
    apl_title: 'პორტალების ლოგინები კომპანიის ბრაუზერებისთვის',
    apl_desc: 'ანგარიშისთვის, რომელსაც სამუშაო ნომერი არ აქვს - argo, ბალანსის შემვსები MacBook და Chromebook. როცა იქ myhome / ss გამოსულია, გაფართოება აქ შენახული ლოგინით შედის. მიუთითეთ კომპანიის ადმინის ლოგინი. პაროლი არასდროს ჩანს; ცარიელი პაროლი შენახულს ტოვებს.',
    apl_none: 'ჯერ არაფერია შენახული.',
    apl_agent: 'აგენტის ელფოსტა (argo@sentavus.com)',
    apl_login: 'ლოგინი (ელფოსტა ან ტელეფონი)',
    apl_password: 'პაროლი',
    listings_au_on: 'ავტო-განახლება',
    listings_au_on_hint: 'ეს კომპიუტერი გაფართოების ყოველ ახალ ვერსიას თავად აყენებს',
    listings_au_error: 'ავტო-განახლება ვერ მოხერხდა',
    listings_au_error_hint: 'განახლების პროგრამა დაყენებულია, მაგრამ ბოლო ცდა ვერ შესრულდა',
    listings_au_off: 'ხელით განახლება',
    listings_au_off_hint_win: 'ავტო-განახლება ჩართული არ არის: გაფართოების საქაღალდეში ერთხელ ორჯერ დააწკაპუნეთ install-autoupdate.bat-ზე',
    listings_au_off_hint_mac: 'ავტო-განახლება ჩართული არ არის: Terminal-ში ერთხელ გაუშვით  bash <გაფართოების საქაღალდე>/install-autoupdate.sh',
    listings_pc_seen: 'კომპიუტერი ბოლოს {t}',
    listings_pc_unknown: 'კომპიუტერი ჯერ არ ჩანს',
    listings_myday_title: 'ჩემი დღე',
    listings_team_today: 'გუნდი დღეს',
    listings_axis_short: 'საათები არათანაბარ მასშტაბშია',
    listings_pc_short_on: 'ჩართ.',
    listings_myday_rail: 'ჩემი დღის ზოლი',
    listings_summary: 'დღის შეჯამება',
    listings_legend_title: 'აღნიშვნები',
    listings_legend_rail: 'ზოლი',
    listings_legend_shift_home: 'დაგეგმილი ცვლა (სახლიდან)',
    listings_legend_nudge_critical: 'შეხვედრის შედეგი ძალიან დაგვიანებულია',
    listings_legend_nudge_high: 'შეხვედრის შედეგი დაგვიანებულია',
    listings_legend_nudge_medium: 'შეხვედრის შედეგი არ არის ჩაწერილი',
    listings_myday_checkin: 'მოსვლა',
    listings_myday_off: 'დღეს განრიგში არ ხართ',
    listings_myday_failures: 'ჩაშლილი განცხადებები',
    listings_myday_no_reason: 'მიზეზი არ ჩაიწერა',
    listings_myday_balances: 'ბალანსები',
    listings_myday_missing: 'თქვენი სტრიქონი ამ სიაში არ არის (არჩეულია სხვა ოფისი, ან ლოგინი არ არის დაკავშირებული).',
    listings_team_show: 'გუნდის ჩვენება ({n})',
    listings_team_hide: 'გუნდის დამალვა',
    listings_actions: 'მოქმედებები',
    listings_close: 'დახურვა',
    listings_lock_action: 'აგენტის დაბლოკვა',
    listings_unlock_action: 'წვდომის აღდგენა',
    listings_locked: 'დაბლოკილია',
    listings_locked_hint: 'წვდომა გაუქმებულია - აღდგენა ⋯ მენიუდან',
    listings_lock_confirm: 'დაიბლოკოს {e}?\n\nეს გააუქმებს მის senTavus გაფართოებას:\n• შესვლა და ტოკენის განახლება მაშინვე იბლოკება\n• გაშვებული გაფართოება ~5 წუთში წყვეტს მუშაობას\n• ოფლაინ ასლი 24 საათში თავად იბლოკება\n\nწვდომის აღდგენა ნებისმიერ დროს შეიძლება ⋯ მენიუდან.',
    listings_lock_reason: 'მიზეზი (არასავალდებულო):',
    listings_paid_custom: 'სხვა',
    listings_paid_apply: 'დადასტურება',
    listings_paid_cancel: 'გაუქმება',
    listings_paid_hours_invalid: 'შეიყვანეთ საათების რაოდენობა 1-დან 168-მდე',
    listings_paid_for: '{m} - რამდენი საათით?',
    listings_rail_shift: 'ცვლა {t}',
    listings_rail_no_shift: 'ცვლის გარეთ',
    listings_deal_trade: 'იყიდება',
    listings_deal_sale: 'იყიდება',
    listings_deal_lease: 'ქირავდება',
    listings_deal_rent: 'ქირავდება',
    agents_add: 'აგენტის დამატება', agents_active: 'აქტიური აგენტი', agents_desks: 'სამუშაო ადგილი',
    agents_show_inactive: 'არააქტიურების ჩვენება', agents_hide_inactive: 'არააქტიურების დამალვა',
    agents_sort_newest: '↑ ახლად აყვანილი', agents_sort_oldest: '↑ ყველაზე ხანდაზმული',
    agents_sort_hours: '↑ ყველაზე მეტი საათი', agents_sort_name: '↑ სახელი A–Z',
    agents_sort_role: '↑ პოზიციით', agents_sort_resigned: '↑ ახლად წასული',
    agents_tab_active: 'აქტიური', agents_tab_left: 'გათავისუფლებული',
    agent_office_wk: 'ოფ./კვ.', agent_remote_wk: 'დისტ./კვ.',
    agent_hired: 'აყვანა', agent_left: 'გავიდა',
    agent_modal_add: 'აგენტის დამატება', agent_modal_edit: 'რედაქტირება',
    agent_modal_info: 'მონაცემები და დასაქმება', agent_modal_schedule: 'კვირის განრიგი',
    agent_modal_name: 'სრული სახელი', agent_modal_role: 'პოზიცია', agent_modal_office: 'ოფისი',
    agent_modal_commission: 'საკომისიო %', role_comm_default: 'ნაგულისხმევი',
    deal_split_title_mine: 'თქვენი წილი ამ გარიგებიდან', deal_split_title_all: 'როგორ იყოფა ეს გარიგება',
    deal_split_agent: 'აგენტი', deal_split_manager: 'მენეჯერი', deal_split_done: 'დახურვა',
    deal_split_office_unknown: 'თქვენი ოფისი მითითებული არ არის, ამიტომ გაყოფის ჩვენება ვერ ხერხდება. სთხოვეთ ადმინისტრატორს, მიუთითოს ოფისი.',
    deal_close_recorded: 'გარიგება დაფიქსირდა.',
    deal_split_no_attendance: 'შეხვედრაზე დამსწრე არავინ არის მონიშნული, ამიტომ გამოყენებულია ნაგულისხმევი გაყოფა (მაღალანაზღაურებადი აგენტი როგორც წამყვანი).',
    agent_dupe_phone: 'ეს პირადი ტელეფონის ნომერი უკვე ეკუთვნის:',
    agent_dupe_id: 'ეს პირადი ნომერი (ID) უკვე ეკუთვნის:',
    agent_dupe_id_hint: 'პირადი ნომერი ერთ ადამიანს ეკუთვნის. სავარაუდოდ ეს იგივე ადამიანია, ვინც უკვე სიაშია.',
    agent_dupe_phone_hint: 'შეამოწმეთ შენახვამდე — შესაძლოა ამატებთ ადამიანს, რომელიც უკვე სიაშია. შენახვა მაინც შესაძლებელია.',
    agent_modal_commission_manager: 'გუნდის საკომისიო % (მენეჯერი)',
    agent_modal_commission_manager_hint: 'წილი ამ ოფისის ნებისმიერი აგენტის დახურული გარიგების სრული საკომისიოდან. ემატება ზემოდან — დამხურავ აგენტს არ აკლდება. საკუთარ გარიგებებზე არ ერიცხება.',
    comm_rules_title: 'როგორ ითვლება აგენტის შემოსავალი',
    comm_rules_body: `გამოიყენება აგენტის საკომისიო % საქმის დახურვის მომენტში.

• ერთი აგენტი — იღებს თავისი საკომისიო %-ს საქმის საკომისიოდან; დანარჩენს იღებს სააგენტო.
• ორი აგენტი, შეხვედრაზე მაღალანაზღაურებადი იყო (ნაგულისხმევი) — სააგენტო იღებს 100 − მაღალ%-ს; დაბალანაზღაურებადი იღებს თავისი %-ის ნახევარს (დაბ ÷ 2); მაღალი იღებს მაღალ% − (დაბ ÷ 2).
• ორი აგენტი, თანაბარი ანაზღაურება — თითო იღებს თავისი %-ის ნახევარს (% ÷ 2); სააგენტო იღებს 100 − %-ს.
• ორი აგენტი, შეხვედრაზე დაბალანაზღაურებადი იყო — თანხა იყოფა თანაბრად: თითო იღებს დაბალი %-ის ნახევარს (დაბ ÷ 2); სააგენტო იღებს 100 − დაბალ%-ს.

მაგალითი: 70% + 50%, შეხვედრაზე მაღალი იყო → სააგენტო 30%, 70%-აგენტი 45%, 50%-აგენტი 25%.`,
    agent_modal_unassigned: 'არ არის მინიჭებული',
    agent_modal_generate_pass: 'პაროლის გენერირება',
    office_filter_all: 'ყველა ოფისი', office_filter_unassigned: 'არ არის მინიჭებული', office_filter_label: 'ოფისი',
    agent_modal_email: 'ელფოსტა', agent_modal_phone_work: 'სამსახურის ტელეფონი',
    agent_modal_phone_personal: 'პირადი ტელეფონი', agent_modal_hire_date: 'აყვანის თარიღი',
    agent_modal_active: 'აქტიური', agent_modal_resign: 'გათავისუფლება',
    agent_modal_resign_date: 'გათავისუფლების თარიღი', agent_modal_resign_reason: 'მიზეზი',
    agent_modal_dob: 'დაბადების თარიღი', agent_modal_id_number: 'პირადი ნომერი',
    agent_modal_login_id: 'Login ID', agent_modal_login_pass: 'Login პაროლი',
    agent_modal_scheduler_login: 'Scheduler-ის ლოგინი (admin/manager)', agent_modal_scheduler_login_none: '— არცერთი —',
    agent_modal_save: 'შენახვა', agent_modal_delete: 'წაშლა',
    agent_modal_cancel: 'გაუქმება', agent_modal_adding: 'დამატება',
    agent_modal_saving: 'ინახება…', agent_modal_close: 'დახურვა',
    qa_edit: 'რედაქტირება', qa_fire: 'გათავისუფლება', qa_activate: 'გააქტიურება',
    qa_remove_number: 'ნომრის მოხსნა', qa_delete: 'აგენტის წაშლა',
    agents_remove_number_confirm: '{number} დაბრუნდეს თავისუფალ ფონდში? {name}-ს ეს ნომერი მხოლოდ წარსულის ჩანაწერად დარჩება.',
    agents_delete_confirm: 'სამუდამოდ წაიშალოს {name}? ჩანაწერი მთლიანად წაიშლება და მისი აღდგენა შეუძლებელი იქნება.',
    phone_held_badge: 'ისევ ამ აგენტზე მინიჭებული',
    phone_pool_badge: 'თავისუფალი — ფონდში',
    phone_now_label: 'ახლა {name}',
    status_active: 'აქტიური', status_left: 'გავიდა',
    cand_add: '+ კანდიდატის დამატება', cand_search: 'ძიება სახელი, პოზიცია, ელფოსტა…',
    cand_all: 'ყველა', cand_active: 'აქტიური', cand_interviews: 'გასაუბრება', cand_rejected: 'უარი',
    cand_status_new: 'ახალი', cand_status_scheduled: 'გასაუბრება დაგეგმილია',
    cand_status_interviewed: 'გასაუბრება ჩატარდა', cand_status_hired: 'აყვანილი ✓', cand_status_rejected: 'უარი',
    cand_status_no_show: 'არ გამოცხადდა',
    cand_hire: 'აყვანა →', cand_hired_msg: 'აყვანილია — განრიგი შეავსეთ „აგენტების“ ჩანართში',
    cand_confirm_hire: 'აყვანის დადასტურება', cand_confirm: 'დადასტურება',
    cand_ready_hire: 'მზადაა დასაქირავებლად?', cand_ready_msg: 'შეიქმნება აგენტის პროფილი.',
    cand_delete: 'წაშლა',
    att_free_desks: 'თავისუფალი მაგიდები', att_remote: 'დისტანციურად', att_peak: 'პიკური საათი',
    att_confirmed: 'დადასტურებული', att_absent: 'არ გამოცხ.', att_remote_short: 'დისტ.',
    att_not_entered: 'მოლოდინში',
    att_overdue_count: 'ვადაგადაცილებული',
    att_use_scheduled: 'განრიგის დრო',
    att_using_scheduled: 'გამოიყენება განრიგის დრო',
    att_clear: 'გასუფთავება',
    int_today: 'დღეს', int_upcoming: 'მომავალი', int_starting: 'იწყებს', int_past: 'წარსული', int_count: 'გასაუბრება',
    int_unscheduled: 'დასაგეგმია', int_unscheduled_hint: 'გასაუბრების თარიღი არ აქვს — დააჭირეთ დასაგეგმად',
    int_schedule: '📅 გასაუბრების დაგეგმვა', int_reschedule: '📅 გადატანა',
    int_mark: '✅ გასაუბრება ჩატარდა', int_hire: '🟢 აყვანა', int_reject: '🔴 უარყოფა',
    int_edit: '✏️ რედაქტირება', int_delete: '🗑 წაშლა',
    log_by_agent: 'აგენტის მიხ.', log_timeline: 'ქრონოლოგია', log_all: 'ყველა აგენტი',
    log_add_entry: '+ ჩანაწერი', log_add_manual: 'ჩანაწერის დამატება',
    save: 'შენახვა', cancel: 'გაუქმება', delete: 'წაშლა', edit: 'რედ.', close: 'დახურვა',
    login_subtitle: 'განრიგში შესვლა', login_username: 'მომხმარებელი', login_password: 'პაროლი',
    login_show_password: 'პაროლის ჩვენება', login_hide_password: 'პაროლის დამალვა',
    login_sign_in: 'შესვლა', login_signing_in: 'შესვლა…', login_error: 'მომხმარებლის სახელი ან პაროლი არასწორია.',
    login_sign_out: 'გასვლა',
    change_password: 'პაროლის შეცვლა', pw_new: 'ახალი პაროლი', pw_confirm: 'გაიმეორეთ პაროლი',
    pw_too_short: 'პაროლი უნდა შეიცავდეს მინიმუმ 6 სიმბოლოს.', pw_mismatch: 'პაროლები არ ემთხვევა',
    pw_changed: 'პაროლი განახლდა',
    change_login_password: 'ლოგინის / პაროლის შეცვლა',
    pw_leave_blank: 'დატოვეთ ცარიელი, თუ პაროლი არ იცვლება',
    creds_changed: 'მონაცემები განახლდა — გამოიყენეთ შემდეგ შესვლაზე',
    creds_change_failed: 'ვერ შეიცვალა — ლოგინი შესაძლოა დაკავებულია',
    working_day: 'სამუშაო', day_off: 'დასვენება', add_slot: '+ დამატება',
    week: 'კვირა', month: 'თვე',
    office_settings: 'ოფისის პარამეტრები', office_name: 'ოფისის სახელი',
    office_location: 'ადგილმდებარეობა', office_capacity: 'სამუშაო ადგილები',
    office_desks_note: 'ფიზიკური სამუშაო მაგიდები.',
    admin_name: 'ადმინისტრატორის სახელი', admin_note: 'ჩანს ყველა ჩანაწერში.',

    att_check_in: 'შემოსვლა', att_check_out: 'გასვლა',
    att_start_time: 'დაწყება', att_end_time: 'დასრულება',
    att_now: 'ახლა',
    att_in: 'შემოვიდა', att_out: 'გავიდა',
    att_started: 'დაიწყო', att_ended: 'დაასრულა', att_note: 'შენიშვნა (არასავალდებულო)',
    att_note_placeholder: 'გვიანი დაწყება, კლიენტის შეხვედრა…',
    att_status: 'სტატუსი', att_in_office: 'ოფისში',
    att_worked: 'ნამუშევარი', att_vs_schedule: 'განრიგთან',
    att_clear_entry: 'გასუფთავება',
    att_scheduled_for_day: 'დაგეგმილი ამ დღეს',
    att_total: 'სულ', att_split_shift: 'გაყოფილი',
    att_use_sched: 'განრიგის დრო',
    att_using_sched: 'გამოიყენება განრიგი',
    att_free: 'გასუფთავება',
    agent_modal_dob_age: 'ასაკი',
    agent_modal_identity: 'პირადობა',
    agent_modal_basic: 'ძირითადი',
    agent_modal_employment: 'დასაქმება',
    agent_modal_login: 'სისტემაში შესვლა',
    agent_modal_show: 'ჩვენება', agent_modal_hide: 'დამალვა',
    cand_modal_contact: 'კონტაქტი',
    cand_modal_application: 'განაცხადი',
    cand_modal_interview: 'გასაუბრება',
    cand_modal_name: 'სრული სახელი', cand_modal_phone: 'ტელეფონი',
    cand_modal_email: 'ელფოსტა', cand_modal_link: 'პროფილი / სოც. ქსელი',
    cand_modal_link_hint: '(Facebook, TikTok, LinkedIn…)',
    cand_modal_applying: 'განაცხადი პოზიციაზე', cand_modal_source: 'წყარო',
    cand_modal_status: 'სტატუსი', cand_modal_date: 'თარიღი',
    cand_modal_time: 'დრო', cand_modal_interviewer: 'მომხსენებელი',
    cand_modal_notes: 'შენიშვნები',
    cand_modal_notes_ph: 'გასაუბრების ჩანაწერები, შთაბეჭდილებები…',
    cand_modal_clear_interview: '✕ გასაუბრების გაუქმება',
    cand_no_candidates: 'კანდიდატები არ არის',
    cand_no_match: 'კანდიდატები ვერ მოიძებნა',
    cand_no_rejected: 'უარყოფილი კანდიდატები არ არის',
    sched_office_capacity: 'ოფისის დატვირთულობა',
    sched_free_desks: 'თავისუფალი მაგ.',
    sched_in_office_n: 'ოფისში {n}',
    sched_free_desks_peak: 'თავისუფალი მაგ. დაგეგმილ პიკზე', sched_capacity_unknown: 'ტევადობა არ არის მითითებული',
    sched_people_today: 'ამ დღეს ოფისში დაგეგმილია {n} ადამიანი',
    sched_planned_note: 'დაგეგმილი განრიგი, {date} - არა დაფიქსირებული დასწრება.',
    sched_remote_only: 'მხოლოდ დისტ.',
    sched_peak_hour: 'პიკური საათი',
    sched_agents: 'აგენტი',
    sched_heatmap: 'ოფისის კვირის თერმული რუკა',
    sched_empty: 'ცარიელი', sched_full: 'სავსე',
    sched_capacity: 'სიმძლავრე',
    sched_weekly: 'კვირის დასწრება',
    sched_office_days: 'ოფ. დღეები',
    log_no_events: 'ჩანაწერები არ არის',
    log_events_auto: 'ცვლილებები ავტომატურად ფიქსირდება.',
    log_show_less: '▲ ნაკლები', log_show_more: '▼ კიდევ',
    log_more: 'მეტი',
    cat_hired: 'აყვანა', cat_left: 'გათავისუფლება',
    cat_schedule: 'განრიგის ცვლილება', cat_attendance: 'დასწრება',
    cat_alert: 'გაფრთხილება', cat_profile: 'პროფილის ცვლილება',
    cat_office: 'ოფისი', cat_info: 'შენიშვნა', cat_contact: 'კონტაქტი',
    cat_client_error: 'აპლიკაციის შეცდომა',
    log_contact: '{method} ({phone}) · {by}',
    ct_opened: 'კონტაქტის მენიუ გაიხსნა', ct_copied: 'ნომერი დაკოპირდა',
    days: ['ორშ','სამ','ოთხ','ხუთ','პარ','შაბ','კვი'],
    fullDays: ['ორშაბათი','სამშაბათი','ოთხშაბათი','ხუთშაბათი','პარასკევი','შაბათი','კვირა'],
    months: ['იანვარი','თებერვალი','მარტი','აპრილი','მაისი','ივნისი','ივლისი','აგვისტო','სექტემბერი','ოქტომბერი','ნოემბერი','დეკემბერი'],
    // Hardcoded strings now translated
    all_agents: 'ყველა აგენტი',
    all_categories: 'ყველა კატეგორია',
    location_office: 'ოფისი',
    location_remote: 'დისტ.',
    schedule_start: 'დაწყება',
    schedule_end: 'დასრულება',
    schedule_location: 'ადგილი',
    heatmap_empty: 'ცარიელი',
    heatmap_full: 'სავსე',
    int_no_scheduled: 'გასაუბრება დაგეგმილი არ არის',
    int_no_scheduled_hint: 'კანდიდატს გასაუბრების თარიღი მიუთითეთ, რომ აქ გამოჩნდეს',
    cand_hired_agents_msg: '✓ აყვანილი — განრიგი შეავსეთ აგენტების ჩანართში.',
    // ScheduleTab
    sched_occupancy_title: 'ოფისის დატვირთვა საათების მიხედვით',
    sched_in_office: 'ოფისში',
    sched_capacity_label: 'ტევადობა',
    sched_office_label: 'ოფისი',
    sched_remote_label: 'დისტ.',
    sched_override_label: 'ცვლილება',
    sched_split_label: 'გაყოფილი',
    sched_click_override: 'დააჭირეთ ✎ ერთჯერადი ცვლილებისთვის',
    sched_edit_override: '★ რედ.',
    sched_set_override: '✎ ცვლილება',
    sched_col_agent: 'აგენტი',
    sched_col_role: 'პოზიცია',
    sched_col_hours: 'საათები',
    sched_col_timeline: 'დრო',
    sched_col_status: 'სტატუსი',
    sched_col_override: 'ცვლილება',
    // AttendanceTab
    att_status_absent: 'არ გამოცხ.',
    att_status_holiday: 'შვებულება',
    att_day_off: 'დასვენება',
    my_att_arrive: 'მოვედი',
    my_att_leave: 'წავედი',
    my_att_arrived_at: 'მოვედი {time}',
    my_att_left_at: 'წავედი {time}',
    my_att_once_note: 'ერთხელ იწერება — შეცდომის შესწორება მენეჯერს სთხოვე.',
    my_att_no_agent_row: 'შენ აგენტის ჩანაწერი არ გაქვს, ამიტომ აქ დასწრება არ იწერება.',
    my_att_loc_in_office: 'ოფისში ხარ.',
    my_att_loc_unchecked: 'ოფისის მისამართები მითითებული არ არის, ამიტომ მოსვლა ლოკაციით არ მოწმდება.',
    my_att_loc_not_in_office: 'ოფისის ქსელში არ ხარ — მოსვლის მონიშვნა მხოლოდ ოფისიდან შეიძლება.',
    my_att_loc_address_unknown: 'შენი მისამართი ვერ დადგინდა, ამიტომ მოსვლის მონიშვნა ვერ ხერხდება.',
    my_att_loc_device_not_seen: 'ოფისი ვერ ხედავს შენს ტელეფონს თავის Wi-Fi-ზე — დაუკავშირდი ოფისის ქსელს ან დაარეგისტრირე ტელეფონი ქვემოთ.',
    myphone_title: 'ჩემი ტელეფონი ოფისის Wi-Fi-ზე',
    myphone_none: 'ტელეფონი ჯერ არ არის დარეგისტრირებული — ოფისი თქვენს მოწყობილობას ვერ ამოიცნობს.',
    myphone_seen: 'ოფისი ახლა ხედავს',
    myphone_not_seen: 'ახლა არ ჩანს',
    myphone_remove: 'წაშლა',
    myphone_add: 'რეგისტრაცია',
    myphone_label_ph: 'სახელი (სურვილისამებრ)',
    myphone_where: 'სად ვნახო ეს?',
    myphone_help_ios: 'პარამეტრები → ზოგადი → ინფორმაცია → Wi-Fi მისამართი.',
    myphone_help_android: 'პარამეტრები → ტელეფონის შესახებ → სტატუსი → Wi-Fi MAC მისამართი (ზოგ ტელეფონზე: ქსელი → დამატებითი → შეერთებული ქსელი → MAC).',
    myphone_help_warning: 'წაიკითხე ოფისის Wi-Fi-ზე შეერთებული — ტელეფონი ყოველ ქსელზე სხვა მისამართს იყენებს.',
    my_att_refused_not_in_office: 'მოსვლის მონიშვნა მხოლოდ ოფისიდან შეიძლება.',
    my_att_refused_address_unknown: 'შენი მისამართი ვერ დადგინდა, ამიტომ მოსვლის მონიშვნა ვერ ხერხდება.',
    my_att_refused_not_an_agent: 'აგენტის ჩანაწერი არ გაქვს.',
    my_att_refused_not_checked_in: 'მოსვლა ჯერ არ მოგინიშნავს.',
    shift_prompt_question: 'ცვლა დაიწყო — ოფისში ხარ?',
    shift_prompt_started_at: '{time}-დან',
    shift_prompt_recheck: 'ხელახლა შემოწმება',
    desk_attr_address: 'მისამართი',
    desk_attr_district_code: 'რაიონი',
    desk_attr_cadastral_code: 'საკადასტრო კოდი',
    desk_attr_kind: 'ტიპი',
    desk_attr_lat: 'განედი',
    desk_attr_lng: 'გრძედი',
    desk_attr_intent: 'გარიგება',
    desk_attr_price: 'ფასი',
    desk_attr_currency: 'ვალუტა',
    desk_attr_owner_name: 'მესაკუთრის სახელი',
    desk_attr_owner_phone: 'მესაკუთრის ნომერი',
    desk_parse_conflict_heading: 'გარჩევა არ ეთანხმება {n} უკვე არსებულ მნიშვნელობას',
    desk_parse_conflict_replace: 'შეცვლა',
    desk_parse_conflict_apply: 'შეცვალე {n}',
    desk_parse_conflict_keep_all: 'ყველაფერი დარჩეს',
    desk_pagination_go_to: 'გვერდი {n}',
    desk_client_reps_heading: 'კლიენტზე მუშაობს:',
    desk_client_reps_none: 'ამ კლიენტზე აგენტი ჯერ არ მუშაობს.',
    desk_client_rep_unnamed: 'უსახელო',
    desk_client_rep_you: '(თქვენ)',
    desk_client_share: 'კოლეგისთვის გაზიარება',
    desk_client_share_search: 'აგენტის ძებნა…',
    desk_client_share_note: 'კოლეგა დაინახავს ყველაფერს, რასაც თქვენ ხედავთ — კონტაქტის ჩათვლით. კლიენტი თქვენთანაც რჩება.',
    desk_client_share_nobody: 'დასამატებელი აღარავინაა.',
    desk_client_share_all: 'ყველა ({n})',
    desk_client_share_none: 'გასუფთავება',
    desk_client_share_commit: 'გაზიარება',
    desk_client_unshare_title: 'შეწყვიტე გაზიარება {name}-თან',
    desk_client_share_commit_n: 'გაზიარება · {n}',
    desk_client_share_partial: '{ok} გაზიარდა, {bad} ვერ: {names}',
    desk_client_leave: 'კლიენტზე მუშაობის შეწყვეტა',
    desk_client_leave_confirm_shared: 'დატოვებ? {name} აგრძელებს ამ კლიენტზე მუშაობას, ანუ მას რჩება.',
    desk_client_leave_confirm_pool: 'დატოვებ? სხვა არავინ მუშაობს — კლიენტი პულში ბრუნდება და ნებისმიერს შეუძლია აიღოს.',
    desk_client_leave_yes: 'დიახ, დავტოვებ',
    desk_restriction_except: 'გამონაკლისი:',
    office_ips_label: 'ოფისის IP მისამართები (თითო ხაზზე ერთი)',
    office_ips_hint: 'საჯარო IP მისამართები, საიდანაც ეს ოფისი ჩანს. თუ ცარიელს დატოვებთ, მოსვლა აქ არ შემოწმდება. დიაპაზონი იწერება ასე: 91.184.115.0/24.',
    office_ips_enforcing: 'მოსვლა მოწმდება {n} მისამართთან.',
    office_ips_open: 'მისამართები არ არის — ამ ოფისში მოსვლა არ მოწმდება.',
    office_ips_router: 'მისამართები საჭირო არ არის — მოსვლა ოფისის როუტერით მოწმდება.',
    att_dayoff_visit_hint: 'დღეს დასვენებაა — თუ მოვიდა, ჩაწერე ვიზიტი',
    att_dayoff_visit_label: 'მოვიდა / წავიდა (დღეს გრაფიკში არ არის)',
    att_dayoff_visit_clear: 'გასუფთავება',
    att_dayoff_visit_now: 'ახლა',
    att_working: 'განრიგში',
    att_confirmed_count: 'დადასტურებული',
    att_legend_office: 'ოფისი',
    att_legend_remote: 'დისტ.',
    att_legend_not_entered: 'მოლოდინში',
    att_legend_absent: 'არ გამოცხ.',
    att_col_agent: 'აგენტი',
    att_col_scheduled: 'განრიგი',
    att_col_worked: 'ნამუშ.',
    att_col_delta: 'სხვაობა',
    att_col_absent: 'გაცდენა',
    att_col_confirmed: 'დადასტ.',
    att_col_assumed: '~სავარაუდო',
    att_col_remote: 'დისტ.',
    att_summary_late: 'დაგვიანება',
    att_summary_overtime: 'ზეგანაკვეთ.',
    att_summary_absent: 'გაცდენა',
    att_summary_remote: 'დისტ.',
    // AgentsTab
    agents_none_left: 'გათავისუფლებული აგენტი არ არის',
    agents_none_yet: 'აგენტები არ არის — დაამატეთ პირველი',
    agents_empty_office_filtered: 'ამ ოფისის ხედში აგენტები არ არის — ზედა ზოლის ოფისის ფილტრი მალავს დანარჩენს',
    office_filter_show_all: 'ყველა ოფისის ჩვენება',
    // AgentsTab day badges
    badge_office: 'დ ოფ.',
    badge_remote: 'დ დისტ.',
    badge_split: 'დ გაყ.',
    badge_off: 'დ დასვ.',
    // ScheduleTab
    sched_base_note: 'ძირითადი განრიგი · ერთჯერადი ცვლილებები არ ჩანს',
    sched_base_short: 'ბაზ. განრიგი',
    sched_off_badge: 'დასვ.',
    sched_weekend: 'შაბათ-კვირა',
    sched_day_title: 'განრიგი',
    // AttendanceTab
    att_day_view: 'დღე',
    att_scheduled_label: 'განრიგი',
    // AgentLogTab
    log_add_btn: 'ჩანაწერის დამატება',
    // B196 (2026-07-29): en-side note applies — previously undefined in both languages.
    log_message_label: 'შეტყობინება',
    log_message_placeholder: 'დაწერეთ შენიშვნა…',
    log_save_btn: 'შენახვა',
    log_all_count: 'ყველა',
    log_no_events_long: 'ჩანაწერები ჯერ არ არის. განრიგისა და დასწრების ცვლილებები აქ ავტომატურად გამოჩნდება.',
    // Event timeline
    log_timeline_title: 'ცვლილებების ისტორია',
    log_entries: 'ჩანაწერი',
    // Candidate event types
    cand_ev_added: 'დამატებული',
    cand_ev_scheduled: 'გასაუბრება დაგეგმილია',
    cand_ev_rescheduled: 'გასაუბრება გადავადდა',
    cand_ev_cancelled: 'გასაუბრება გაუქმდა',
    cand_ev_no_show: 'არ გამოცხადდა',
    cand_ev_interviewed: 'გასაუბრება ჩატარდა',
    cand_ev_hired: 'აყვანილი',
    cand_ev_rejected: 'უარი',
    cand_ev_note: 'შენიშვნა',
    // Candidates form hints
    cand_status_hint: 'სტატუსი გასაუბრება დაგეგმილი" ან "გასაუბრება" გარდა სხვა — გასაუბრების თარიღს წაშლის.',
    cand_date_hint: 'თარიღის მითითება ავტომატურად ცვლის სტატუსს "დაგეგმილი"',
    // AdminTab
    admin_no_items: 'მონაცემები არ არის',
    admin_add_new: 'ახლის დამატება…',
    admin_add_btn: 'დამატება',
    phone_section_title: 'სამუშაო ნომრები',
    phone_none_yet: 'ნომრები არ არის',
    phone_search_ph: 'ძებნა ნომრით ან აგენტით…',
    phone_search_no_match: 'ვერაფერი მოიძებნა',
    phone_filter_all: 'ყველა', phone_filter_free: 'თავისუფალი', phone_filter_assigned: 'დაკავებული', phone_filter_expiring: 'იწურება',
    phone_filter_expired: 'ვადაგასული', phone_filter_banned: 'დაბლოკილი',
    phone_sort_plan: 'ჯერ ვადაგასული', phone_sort_plan_desc: 'ჯერ მეტი დარჩენილი',
    phone_sort_plan_title: 'დალაგება ვადის მიხედვით — დაჭერით იცვლება მიმართულება',
    phone_assign_btn: 'მინიჭება', phone_assign_ph: 'აგენტის სახელი…', phone_assign_none: 'აგენტი ვერ მოიძებნა',
    phone_banned_badge: 'დაბლოკილი',
    phone_free_badge: 'თავ.',
    number_details_title: 'პაროლები და eSIM QR', portal_section: 'შესვლა myhome.ge / ss.ge-ზე', settings_passwords: 'პაროლები',
    portal_copy: 'კოპირება', portal_open_site: 'საიტის გახსნა', portal_updated: 'განახლდა {date} · {who}', portal_edit: 'შეცვლა', portal_add: 'დამატება',
    portal_login: 'ლოგინი', portal_password: 'პაროლი', portal_show: 'ჩვენება', portal_hide: 'დამალვა', portal_none: 'ჯერ არ არის ჩაწერილი',
    portal_login_ph: 'ტელეფონი, ელფოსტა ან მომხმარებლის სახელი', portal_password_keep_ph: 'ახალი პაროლი — დატოვეთ ცარიელი, თუ არ იცვლება',
    portal_save: 'შენახვა', portal_cancel: 'გაუქმება', portal_my_title: 'თქვენი პაროლები myhome.ge / ss.ge-ზე',
    portal_my_sub: 'ანგარიშები, რომლებიც თქვენს სამუშაო ნომერზეა დარეგისტრირებული. ისინი ნომერს ეკუთვნის: ნომრის გადაცემისას წვდომაც გადადის.',
    portal_my_no_number: 'სამუშაო ნომერი ჯერ არ გაქვთ მინიჭებული.',
    esim_qr_view: 'eSIM QR კოდი', esim_qr_none: 'eSIM QR ჯერ არ არის ატვირთული', esim_qr_open: 'გახსნა',
    esim_qr_upload: 'QR-ის ატვირთვა (PDF)', esim_qr_add_another: 'კიდევ დამატება', esim_qr_replace: 'ახალი QR-ით ჩანაცვლება',
    esim_qr_replace_hint: 'ჩანაცვლება ტვირთავს ახალ ფაილს, მიმდინარე გადადის „ძველებში“. არაფერი იშლება.',
    esim_qr_retire: 'ძველებში გადატანა', esim_qr_restore: 'მიმდინარე ვერსიად აღდგენა', esim_qr_retired: 'ჩანაცვლდა',
    esim_qr_retire_confirm: 'გადავიტანოთ „{name}“ ძველ ფაილებში?', esim_qr_earlier: 'ძველი ფაილები ({n})',
    phone_unban: 'ბლოკის მოხსნა',
    phone_ban: 'დაბლოკვა',
    phone_note_placeholder: 'შენიშვნის დამატება…',
    phone_note_optional: 'შენიშვნა (არასავალდებულო)',
    admin_save_btn: 'შენახვა',
    admin_saving: 'ინახება…',
    admin_saved: '✓ შენახულია',
    admin_save_changes: 'ცვლილებების შენახვა',
    admin_description: 'აპლიკაციის ჩამოსაშლელი სიების მართვა. ცვლილებები აისახება მყისიერად.',
    admin_name_section: 'ადმინისტრატორის სახელი',
    admin_no_office: 'ოფისი ჯერ არ არის დამატებული.',
    office_section_title: 'ოფისი',
    office_work_start: 'სამუშაო საათები - დაწყება',
    office_work_end: 'სამუშაო საათები - დასასრული',
    office_weekend_hours: 'შაბათ-კვირის საათები',
    office_weekend_start: 'შაბათ-კვირა: დაწყება',
    office_weekend_end: 'შაბათ-კვირა: დასასრული',
    // 0015 — office-scoped numbers + manager Settings (2026-09-16)
    nav_settings: 'პარამეტრები',
    settings_numbers: 'ნომრები',
    settings_office_config: 'ოფისის კონფიგურაცია',
    settings_no_offices: 'თქვენ ჯერ არცერთ ოფისს არ მართავთ. ოფისს მენეჯერს ადმინისტრატორი ანიჭებს.',
    office_unassigned: 'ოფისის გარეშე',
    phone_office_move_title: 'ნომრის ოფისი — შეცვლა მხოლოდ ადმინისტრატორს შეუძლია',
    phone_office_suggest: 'მფლობელის ოფისი: {office}',
    phone_office_suggest_btn: 'მიანიჭე: {office}',
    phone_office_unresolved: '{n} ნომერს ოფისი არ აქვს. ძველი ნომრები განზრახ დარჩა ოფისის გარეშე — მიანიჭეთ თითოეულს.',
    phone_office_history: 'ოფისის ისტორია',
    phone_office_changed_by: 'შეცვალა',
    phone_office_deleted_reason: 'ოფისი წაიშალა',
    office_default_wfh_day: 'სტანდარტული დისტანციური დღე',
    office_default_rest_day: 'სტანდარტული დასვენების დღე',
    office_defaults_hint: 'ოფისის ნაგულისხმევი მნიშვნელობები — თანამშრომლის განრიგი შეიძლება განსხვავდებოდეს.',
    weekday_none: 'არცერთი',
    office_admin_only_note: 'ოფისის სახელს, მისამართებს, Wi-Fi-ს და უსაფრთხოების პარამეტრებს ადმინისტრატორი მართავს.',
    // 0017 — პარამეტრები → შეტყობინებები (Telegram)
    settings_notifications: 'შეტყობინებები',
    notify_bot_title: 'კომპანიის Telegram ბოტი',
    notify_bot_sub: 'ერთი ბოტი მთელი კომპანიისთვის. ერთხელ ქმნით, შემდეგ ყველა თანამშრომელი საკუთარ Telegram-ს აკავშირებს.',
    notify_bot_none: 'არ არის შექმნილი',
    notify_bot_verifying: 'ტოკენი მოწმდება…',
    notify_bot_running: 'მუშაობს · {bot}',
    notify_bot_stale: 'არ პასუხობს',
    notify_bot_stale_hint: 'ტოკენი შენახულია, თუმცა შეტყობინებების სერვისი 2 წუთზე მეტია არ პასუხობს. სერვისის ჩართვამდე შეტყობინებები ვერ გაიგზავნება.',
    notify_bot_error: 'პრობლემა',
    notify_bot_guide_title: 'როგორ შევქმნათ ბოტი (3 წუთი)',
    notify_bot_steps: [
      'გახსენით Telegram და მოძებნეთ @BotFather (ოფიციალურს ლურჯი ნიშანი აქვს). დააჭირეთ Start.',
      'გაგზავნეთ /newbot.',
      'სახელის მოთხოვნისას გაგზავნეთ ადვილად საცნობი სახელი, მაგ. "senTavus შეტყობინებები".',
      'username-ის მოთხოვნისას გაგზავნეთ "bot"-ზე დაბოლოებული, მაგ. sentavus_notify_bot. თუ დაკავებულია, სცადეთ სხვა.',
      'BotFather გამოგზავნის ტოკენს, მაგ. 123456789:AAH… — დააკოპირეთ მთლიანად.',
      'ჩასვით ქვემოთ და დააჭირეთ "შენახვა". დაახლ. 20 წამში სტატუსი გამწვანდება: "მუშაობს · @თქვენი_ბოტი".',
      'სურვილისამებრ: @BotFather-ში /setuserpic — ლოგო, /setdescription — მოკლე აღწერა.',
    ],
    notify_bot_rules: [
      'ტოკენი ბოტის პაროლია. ნუ გაგზავნით არცერთ ჩატში. შენახვის შემდეგ სერვერზე ინახება და აღარ გამოჩნდება — აქაც კი.',
      'ეს ბოტი გამოიყენეთ მხოლოდ senTavus-ისთვის. თუ იგივე ტოკენს სხვა პროგრამაც იყენებს, შეტყობინებები შეწყდება.',
      'თუ ტოკენი გაჟონდა: @BotFather-ში /revoke → აირჩიეთ ბოტი → ახალი ტოკენი ჩასვით აქ. ყველას ხელახლა მოუწევა "Telegram-ის დაკავშირება".',
    ],
    notify_bot_token_label: 'ბოტის ტოკენი (@BotFather-დან)',
    notify_bot_replace_label: 'ტოკენის შეცვლა (ამჟამინდელი ბოტი {id})',
    notify_bot_replace_warn: 'ახალი ტოკენის შენახვა ყველა თანამშრომლის Telegram-კავშირს გათიშავს. ხელახლა დაკავშირება ერთი ღილაკით იქნება შესაძლებელი.',
    notify_bot_save: 'შენახვა',
    notify_bot_missing: 'ადმინისტრატორს კომპანიის Telegram ბოტი ჯერ არ შეუქმნია. როცა შექმნის, აქ შეძლებთ დაკავშირებას.',
    notify_bot_missing_admin: 'ჯერ შექმენით კომპანიის ბოტი ზემოთ — შემდეგ აქ დააკავშირეთ თქვენი Telegram.',
    notify_me_title: 'ჩემი Telegram',
    notify_me_sub: 'შეტყობინებები კომპანიის ბოტთან თქვენს პირად ჩატში მოდის. მათ მხოლოდ თქვენ მიიღებთ.',
    notify_status_connected: 'დაკავშირებულია',
    notify_status_not_connected: 'არ არის დაკავშირებული',
    notify_connect_steps: [
      'დააინსტალირეთ Telegram ტელეფონზე და შედით (თუ უკვე იყენებთ, გადადით შემდეგზე).',
      'დააჭირეთ ქვემოთ "Telegram-ის დაკავშირება". გაიხსნება ჩატი senTavus-ის ბოტთან.',
      'ჩატის ბოლოში დააჭირეთ START. ბოტი უპასუხებს "✅ დაკავშირდა" — სულ ესაა.',
      'დაბრუნდით აქ: ეკრანზე ეწერება "დაკავშირებულია". "ტესტის გაგზავნა" — შეტყობინება მოვა ტელეფონზე.',
    ],
    notify_connect: 'Telegram-ის დაკავშირება',
    notify_waiting: 'Telegram-ში დააჭირეთ START-ს… (ბმული 15 წუთი მოქმედებს)',
    notify_link_fallback: 'Telegram არ გაიხსნა? გახსენით ეს ბმული ტელეფონზე:',
    notify_connected_msg: '✅ Telegram დაკავშირდა.',
    claude_title: 'ჩემი Claude',
    claude_sub: 'დააკავშირეთ Claude-ის აპლიკაცია, რომ კლიენტებისთვის senTavus-ის განცხადებები მოძებნოს — ჩასვით კლიენტის შეტყობინებები და მიიღეთ შესაბამისი ბმულები.',
    claude_status_on: 'დაკავშირებულია',
    claude_status_off: 'არ არის დაკავშირებული',
    claude_steps: [
      'დააჭირეთ ქვემოთ "ჩემი Claude-ის ბმულის შექმნა". ბმული დაკოპირდება და Claude-ის კონექტორების პარამეტრები გაიხსნება.',
      'Claude-ში: Add custom connector → სახელი senTavus → ჩასვით ბმული → Add.',
      'გახსენით ახალი ჩატი და ჰკითხეთ, მაგ.: "1-საძინებლიანი ქირით ვაკეში, $800-მდე, ავეჯით".',
    ],
    claude_create: 'ჩემი Claude-ის ბმულის შექმნა',
    claude_new_link: 'ახალი ბმულის შექმნა',
    claude_turn_off: 'გათიშვა',
    claude_replace_confirm: 'მიმდინარე ბმული აღარ იმუშავებს. შევქმნათ ახალი?',
    claude_off_confirm: 'გავთიშოთ Claude-ის ბმული? Claude ვეღარ დაინახავს senTavus-ს, სანამ ახალს არ შექმნით.',
    claude_link_ready: 'თქვენი ბმული — ჩასვით Claude-ში:',
    claude_link_copied: '✅ ბმული დაკოპირდა — ჩასვით Claude-ში:',
    claude_copy: 'კოპირება',
    claude_open_settings: 'Claude-ის პარამეტრების გახსნა',
    claude_link_secret: 'ეს ბმული პირადია — ვისაც ექნება, თქვენი სახელით მოძებნის. ის მხოლოდ ახლა ჩანს; თუ დაკარგავთ, შექმენით ახალი.',
    claude_made: 'ბმული შეიქმნა {date}',
    claude_last_used: 'ბოლოს გამოყენებულია {date}',
    claude_never_used: 'ჯერ არ გამოყენებულა',
    claude_searches_7d: 'ამ კვირაში {n} ძებნა',
    claude_scope_note: 'Claude ხედავს sentavus.com-ზე გამოქვეყნებულ განცხადებებს — არასდროს იმაზე მეტს, რასაც თქვენ Desk-ში ხედავთ. მესაკუთრის კონტაქტი Desk-ში რჩება: ყველა შედეგს აქვს "Desk-ში გახსნის" ბმული.',
    notify_linked_as: 'დაკავშირებულია: {user} · {date}-დან',
    notify_send_test: 'ტესტის გაგზავნა',
    notify_test_sent: 'საცდელი შეტყობინება გაიგზავნა — რამდენიმე წამში უნდა მოვიდეს.',
    notify_disconnect: 'გათიშვა',
    notify_disconnect_confirm: 'შევწყდეს senTavus-ის შეტყობინებები ამ Telegram-ზე?',
    notify_what_title: 'რას მიიღებთ',
    notify_what_scope: 'მიიღებთ მხოლოდ ამას, რაც senTavus-ში ისედაც ხედავთ: აგენტი — კლიენტებს, რომელთა ლიდებს ხედავ, და საკუთარ გარიგებებს; მენეჯერი — ასევე თავისი ოფისებს; ადმინი — ყველა ოფისს.',
    notify_group_team: 'ოფისი და გუნდი',
    notify_group_clients: 'გარიგებები და კლიენტები',
    notify_when_title: 'როდის',
    notify_quiet: 'მშვიდი საათები',
    notify_quiet_hint: 'ამ საათებში შეტყობინებები არ გაიგზავნება. დაგროვილ შეტყობინებებს პერიოდის დასრულების შემდეგ ერთ შეტყობინებად მიიღებთ. დრო მითითებულია თბილისის დროით.',
    notify_quiet_from: 'დან',
    notify_quiet_to: 'მდე',
    notify_pause: 'ყველა შეტყობინების შეჩერება',
    notify_pause_hint: 'შეჩერებისას შეტყობინებები არც გაიგზავნება და არც მოგვიანებით გასაგზავნად შეინახება.',
    notify_recent_title: 'ბოლოს გაგზავნილი',
    notify_recent_pending: 'ელის {at}-მდე (მშვიდი საათები)',
    notify_scope_all: 'მთელი senTavus',
    notify_scope_office: 'ჩემი ოფისი',
    notify_lead_label: 'შეხსენება',
    notify_lead_minutes: '{n} წუთით ადრე',
    notify_lead_hours: '{n} საათით ადრე',
    // კლიენტის მოთხოვნის ბარათის რედიზაინი 1b (2026-09-17)
    client_sum_budget: 'ბიუჯეტი',
    client_sum_rooms: 'ოთახები',
    client_sum_where: 'სად',
    client_sum_when: 'როდის',
    client_sum_household: 'ვინ ცხოვრობს',
    client_sum_unknown: 'არ ვიცით',
    client_sum_hidden: 'დამალულია',
    client_sum_per_month: '/ თვე',
    client_sum_bedrooms_abbr: 'საძ.',
    client_sum_lease: 'იჯარა {n} თვე',
    client_sum_adults: '{n} ზრდასრული',
    client_sum_children: '{n} ბავშვი',
    client_sum_occupants: '{n} მცხოვრები',
    client_sum_more: '+ კიდევ {n}',
    client_sum_any_city: 'ქალაქის ნებისმიერ ადგილას',
    client_sum_drawn_area: 'რუკაზე დახაზული არე',
    client_must: 'აუცილებელი',
    client_nice: 'სასურველი',
    client_owner_agent: 'აგენტი {name}',
    client_team_me: 'მე',
    client_team_me_plus: 'მე + {n} კოლეგა',
    client_team_name_plus: '{name} + {n}',
    client_team_pool: 'პულშია',
    client_masked_badge: 'კოლეგის კლიენტი',
    client_masked_rep: 'კლიენტს წარმოადგენს {name}',
    client_masked_note: 'კონტაქტი, ელფოსტა და ოჯახის მონაცემები დამალულია — წვდომას კლიენტის წარმომადგენელი გასცემს.',
    client_tab_criteria: 'კრიტერიუმები',
    client_tab_history: 'ისტორია',
    client_tab_nego: 'მოლაპარაკებები',
    client_tab_empty: 'ჯერ არაფერია.',
    client_grp_money: 'ფასი და ფართობი',
    client_grp_where: 'სად',
    client_grp_features: 'რა უნდა',
    client_grp_household: 'ვინ ცხოვრობს',
    client_grp_terms: 'პირობები და ვადები',
    lead_chip_on: 'ლიდები ჩართულია',
    lead_chip_off: 'ლიდების შეტყობინება',
    lead_chip_muted: 'ლიდები დადუმებულია',
    // 0018 — კლიენტის ლიდებზე თვალის დევნება
    lead_watch_off: 'შეტყობინება ახალ ლიდებზე',
    lead_watch_on: 'ახალი ლიდების შეტყობინება ჩართულია',
    lead_watch_muted: 'დადუმებულია — ჩართვა',
    lead_watch_off_title: 'Telegram-ზე შეტყობინება, როცა შემოვა ამ კლიენტის შესაბამისი ახალი ლიდი',
    lead_watch_on_title: 'დააჭირეთ გამოსართავად. ყველა კლიენტის მართვა: პარამეტრები → შეტყობინებები.',
    lead_watch_connect_first: 'შეტყობინების მისაღებად დააკავშირეთ Telegram: პარამეტრები → შეტყობინებები.',
    lead_watch_no_access: 'ამ კლიენტს აღარ ხედავთ, ამიტომ არაფერი გაიგზავნება.',
    // 2026-09-21 — შეტყობინების წინაპირობა
    lead_chip_fill: 'ლიდები: ჯერ შეავსეთ',
    lead_watch_fill_title: 'ამ კლიენტზე ლიდების შესარჩევად ინფორმაცია არ არის საკმარისი. ჯერ შეავსეთ: {list}. დააჭირეთ რედაქტირების გასახსნელად.',
    lead_watch_paused_gaps: 'შეჩერებულია — არაფერი გაიგზავნება, სანამ არ შეავსებთ: {list}.',
    lead_watch_gap_budget: 'ბიუჯეტი (მაქს. ფასი)',
    lead_watch_gap_location: 'უბანი',
    lead_watch_gap_location_polygon: 'უბანი (რუკაზე მონიშნული არე შეტყობინებისთვის არ გამოიყენება)',
    lead_watch_gap_size: 'ოთახები ან ფართობი',
    lead_watch_rooms: 'ოთ.',
    lead_watch_districts: '{n} უბანი',
    lead_watch_title: 'კლიენტები, რომელთა ახალ ლიდებზე შეტყობინება მოდის',
    lead_watch_sub: 'შეტყობინება მოვა, როცა ლიდების ბაზაში შემოვა კლიენტის შესაბამისი ლიდი — იგივე ძებნა, რაც კლიენტის ბარათზე „ლიდებში ძებნა“.',
    lead_watch_empty: 'შეტყობინებები ჯერ არცერთი კლიენტისთვის არ არის ჩართული. ჩასართავად კლიენტის ბარათზე აირჩიეთ „ლიდების შეტყობინება“.',
    lead_watch_event_off: '"ახალი ლიდი ჩემი კლიენტისთვის" ზემოთ გამორთულია, ამიტომ ეს შეტყობინებები არ გაიგზავნება.',
    lead_watch_unnamed: 'კლიენტი',
    lead_watch_stats: 'გაიგზავნა {n} ახალი ლიდი · ბოლოს {date}',
    lead_watch_checked: 'ახალი ლიდი ჯერ არ არის · შემოწმდა {date}',
    lead_watch_pending: 'დაიწყება რამდენიმე წუთში',
    lead_watch_muted_note: 'დადუმებულია — არაფერი გაიგზავნება',
    lead_watch_delete: 'შეტყობინებების გამორთვა',
    lead_watch_delete_confirm: 'გაუქმდეს {name}-ის ახალ ლიდებზე თვალის დევნება?',
    office_delete: 'ოფისის წაშლა',
    office_delete_confirm: 'წავშალოთ „{name}"?',
    office_delete_confirm_with_agents: 'წავშალოთ „{name}"? ამ ოფისზე მიბმული {count} აგენტი დარჩება ოფისის გარეშე.',
    office_location_label: 'მდებარეობა / ქალაქი',
    office_location_placeholder: 'მაგ. თბილისი',
    office_desk_capacity_label: 'მაგიდების რაოდენობა',
    list_agent_roles_title: 'აგენტის როლები',
    list_agent_roles_desc: 'ხელმისაწვდომი როლები აგენტებისთვის',
    list_districts_title: 'უბნები',
    list_districts_desc: 'თბილისის უბნები მისამართისთვის',
    list_languages_title: 'ენები',
    list_languages_desc: 'ენები, რომლებსაც ფლობენ აგენტები/კანდიდატები',
    list_sources_title: 'კანდიდატის წყაროები',
    list_sources_desc: 'საიდან მოდიან კანდიდატები',
    // AgentModal
    agent_modal_remove_slot: 'დროის მონაკვეთის წაშლა',
    agent_modal_section_basic: 'ძირითადი',
    agent_modal_section_identity: 'პირადობა',
    agent_modal_section_address: 'მისამართი',
    agent_modal_section_district: 'უბანი',
    agent_modal_section_languages: 'ენები',
    agent_modal_section_employment: 'დასაქმება',
    agent_modal_section_login: 'ავტორიზაცია',
    agent_modal_split: 'გაყოფილი',
    agent_modal_day_off: 'დასვენება',
    agent_modal_show: 'ჩვენება',
    agent_modal_hide: 'დამალვა',
    agent_modal_name_err: 'მიუთითეთ სახელი.',
    agent_modal_delete_confirm: 'წავშალოთ {name} ბაზიდან? ლოგები შენარჩუნდება.',
    agent_modal_phone_reassign: '{num} მინიჭებული აქვს სხვას. გადავცეთ აგენტს: {agentName}?',
    agent_modal_email_placeholder: 'name@sentavus.com',
    agent_modal_work_phone_ph: '+995 32 …',
    agent_modal_personal_phone_ph: '+995 555 …',
    agent_modal_id_placeholder: '01001234567',
    agent_modal_address_placeholder: 'ქუჩა, კორპუსი…',
    agent_modal_resign_placeholder: 'მაგ. იშოვა სხვა სამსახური, დაუმთავრდა კონტრაქტი…',
    agent_modal_login_id_placeholder: 'მაგ. ana.beridze',
    agent_modal_login_taken: 'ეს Login ID უკვე ეკუთვნის აგენტს: {name}. ორ აგენტს ერთი login ვერ ექნება — აირჩიეთ სხვა.',
    agent_modal_login_taken_left: 'ეს Login ID ეკუთვნის აგენტს: {name}, რომელიც წასულია — ეს ანგარიში დაბლოკილია. აირჩიეთ სხვა.',
    // AgentLogTab
    log_category_label: 'კატეგორია',
    log_description_label: 'აღწერა',
    log_describe_placeholder: 'აღწერეთ რა მოხდა…',
    log_cancel_btn: 'გაუქმება',
    log_delete_btn: 'წაშლა',
    log_no_agents: 'აგენტები ვერ მოიძებნა.',
    log_no_events_short: 'მოვლენები არ არის.',
    // AgentsTab
    agents_view_card: 'ბარათებად ჩვენება',
    agents_view_list: 'სიად ჩვენება',
    agents_search_placeholder: 'ძიება…',
    agents_filter_district: 'უბანი',
    agents_filter_language: 'ენა',
    agents_no_match: 'ფილტრის მიხედვით აგენტები ვერ მოიძებნა',
    // AttendanceTab
    att_team_summary: 'გუნდის შეჯამება',
    // CandidatesTab
    cand_quick_actions: 'სწრაფი მოქმედებები',
    cand_today_badge: 'დღეს',
    cand_modal_add_btn: 'კანდიდატის დამატება',
    cand_modal_phone_label: 'ტელეფონი',
    cand_modal_email_label: 'ელ. ფოსტა',
    cand_modal_applying_label: 'პოზიცია',
    cand_modal_source_label: 'წყარო',
    cand_modal_district_label: 'უბანი',
    cand_modal_languages_label: 'ენები',
    cand_modal_status_label: 'სტატუსი',
    cand_modal_interviewer_label: 'გასაუბრების ჩამტარებელი',
    cand_added_by: 'დაამატა',
    cand_modal_notes_label: 'შენიშვნები',
    cand_notes_thread_label: 'შენიშვნები — თითოეულს თავისი დრო და ავტორი აქვს',
    cand_note_add_ph: 'შენიშვნის დამატება…',
    cand_note_add_button: 'დამატება',
    cand_notes_none: 'შენიშვნები ჯერ არაა.',
    cand_notes_legacy_label: 'ადრინდელი შენიშვნები (სანამ თითოეული ცალკე ინახებოდა)',
    cand_note_by: '{who}',
    cand_modal_creating: 'იქმნება…',
    cand_modal_close_btn: 'დახურვა',
    cand_modal_cancel_btn: 'გაუქმება',
    cand_modal_saving: 'ინახება…',
    cand_modal_save_changes: 'შენახვა',
    cand_search_placeholder: 'ძიება…',
    cand_filter_status: 'სტატუსი',
    cand_filter_district: 'უბანი',
    cand_filter_language: 'ენა',
    cand_no_match_filter: 'კანდიდატები ვერ მოიძებნა',
    cand_name_err: 'მიუთითეთ სახელი.',
    cand_delete_confirm: 'წავშალოთ {name}?',
    cand_hire_confirm: 'ავიყვანოთ {name}? კანდიდატებიდან აგენტებში გადავა.',
    cand_reject_confirm: 'დარწმუნებული ხართ, რომ გსურთ უარყოთ კანდიდატი {name}?',
    cand_name_placeholder: 'მაგ. გიორგი ბერიძე',
    cand_phone_placeholder: '+995 555 …',
    cand_email_placeholder: 'name@email.com',
    cand_link_placeholder: 'https://facebook.com/…',
    cand_interviewer_placeholder: 'ვინ ჩაატარებს გასაუბრებას',
    cand_notes_placeholder: 'გასაუბრების შენიშვნები, შთაბეჭდილებები…',
    // HeatmapTab
    heatmap_low: 'დაბალი',
    heatmap_title: 'კვირის დასწრების მიმოხილვა',
    heatmap_col_agent: 'აგენტი',
    heatmap_col_in_office: 'ოფისში',
    // OverrideModal
    override_info: 'ცვლილება ეხება მხოლოდ ამ დღეს. ძირითადი გრაფიკი არ იცვლება.',
    override_day_off_label: 'დასვენება ამ დღეს',
    override_reason_label: 'მიზეზი (არასავალდებულო)',
    override_reason_placeholder: 'მაგ. პირადი თხოვნა, ექიმთან ვიზიტი…',
    override_saving: 'ინახება…',
    override_no_slots: 'შეიყვანეთ დაწყებისა და დასრულების დრო, ან ჩართეთ დასვენება.',
    override_update: 'ცვლილების განახლება',
    override_apply: 'ცვლილების დადასტურება',
    // ScheduleTab
    sched_override_active_tooltip: 'ერთჯერადი ცვლილება აქტიურია',
    sched_edit_override_tooltip: 'ამ დღის ცვლილების რედაქტირება',
    // App
    app_switch_to_georgian: 'ქართულზე გადართვა',
    app_switch_to_english: 'ინგლისურზე გადართვა',
    app_error_retry: 'ხელახლა ცდა',
    app_tab_load_failed: 'ამ ხედის ჩატვირთვა ვერ მოხერხდა.',
    common_action_failed: 'მოქმედება ვერ შესრულდა. სცადეთ ხელახლა.',
    admin_dedup_scan_failed: 'დუბლიკატების ღამის შემოწმება ვერ შესრულდა — ავტომატური დამთხვევები შესაძლოა მოძველებული იყოს',
    admin_dedup_search_near_cap: 'კლიენტების ძიება ლიმიტს უახლოვდება',
    // ── Admin de-jargon pass (2026-07-26) ──
    admin_crawler_title: 'არგო — განცხადებების ავტომატური მაძიებელი',
    admin_crawler_desc: 'ამოწმებს ძველ განცხადებებს ფასის ცვლილებასა და მოხსნაზე (გაიყიდა / გაქირავდა / ვადა გაუვიდა).',
    admin_complex_linker_title: 'კომპლექსებთან დამკავშირებელი',
    admin_complex_linker_desc: 'აკავშირებს ბინას იმ კომპლექსთან, რომელსაც ის ეკუთვნის — AI-ს ერთ კითხვას უსვამს მხოლოდ იმ განცხადებებზე, სადაც კომპლექსი სახელით არის ნახსენები. ყოველი პასუხი ინახება და მეორედ აღარ იკითხება.',
    admin_complex_linker_mention_note: 'თუ განცხადება კომპლექსს არ ასახელებს, პასუხი მხოლოდ მანძილით უნდა გამოიცნოს — ეს ვარაუდი კი სამუდამოდ ინახება, ამიტომ სისტემა ურჩევნია დაელოდოს უკეთეს მონაცემს.',
    admin_complex_linker_sweep_warn: 'გამორთვისას კითხვა დაისმება ყველა ბინაზე, მათ შორის იმაზეც, სადაც განცხადებაში სახელი არ წერია. ასეთი პასუხები ძირითადად „არ ვიცი“-ა, ფასიანია და სამუდამოა — ბინა, რომელზეც უკვე იკითხა, აღარ ბრუნდება.',
    admin_complex_linker_dryrun_note: 'სატესტო გაშვება — არაფერი შენახულა',
    admin_discovery_enabled_unset: 'არ არის მითითებული; ნაგულისხმევად მუშაობს',
    admin_monitor_loading: 'იტვირთება…',
    admin_monitor_intro: 'პლატფორმების ფორმებში მომხდარი ცვლილებები, ავტომატურად ნასწავლი აგენტების შენახული განცხადებებიდან. ახალი ველი ნიშნავს, რომ პლატფორმა შეიცვალა — გადახედეთ, სანამ შენახვა გაფუჭდება.',
    admin_monitor_new_count: '{n} ახალი',
    admin_monitor_show_handled: 'დამუშავებულის ჩვენება',
    admin_monitor_handled: 'დამუშავებული',
    admin_monitor_mark_handled: 'დამუშავებულად მონიშვნა',
    admin_monitor_reload: 'განახლება',
    admin_monitor_drift_title: 'პლატფორმის ფორმის ცვლილებები',
    admin_monitor_no_drift: 'ცვლილება არ არის — ყველაფერი ემთხვევა ნასწავლს. 👍',
    admin_ai_streetmatch_hint: 'მუშაობს ყოველი განცხადების შენახვისას (დიდი მოცულობა) — დატოვეთ მსუბუქ მოდელზე; გამორთვისას მხოლოდ ლოკალური ძიება',
    admin_ai_fields_hint: 'მოხსენით, რომ AI-ს ამ ველის შევსება აეკრძალოს (მაშინ მხოლოდ თავად განცხადებაში მითითებული მონაცემი გამოიყენება). სია სერვერის მოდელიდან მოდის — ახალი ველები ავტომატურად ჩნდება.',
    admin_ai_reopen_confirm: 'გადაწყვეტილების ხელახლა გახსნა? დაბრუნდება მოლოდინის სიაში და AI კვლავ იკითხავს, სანამ არ უპასუხებთ.',
    admin_streets_stat_entries: 'ჩანაწერი',
    admin_streets_stat_entries_t: 'ყველაფერი: დაკავშირებული ოფიციალური ქუჩები და ისინიც, რომლებიც ჯერ მიბმას ელოდება.',
    admin_streets_stat_matched: 'დაკავშირებული',
    admin_streets_stat_matched_t: 'მხოლოდ დაკავშირებული ოფიციალური ქუჩები (მოლოდინში მყოფების დამალვა).',
    admin_streets_stat_review: 'გადასამოწმებელი',
    admin_streets_stat_overrides: 'შესწორებული',
    admin_streets_stat_official: 'ოფიციალური',
    admin_streets_stat_official_t: 'ცალკეული ოფიციალური ქუჩები — ცნობარი დუბლიკატების გარეშე.',
    admin_streets_stat_review_t: 'მხოლოდ გადასამოწმებლად მონიშნული ქუჩები.',
    admin_streets_stat_overrides_t: 'მხოლოდ ადმინისტრატორის ხელით შესწორებული ქუჩები.',
    admin_streets_filter_ph: 'გაფილტრე ქუჩის სახელით…',
    admin_streets_unmerge_confirm: 'გავყოთ? ყველა ალტერნატიული სახელი დაბრუნდება მოლოდინის სიაში.',
    admin_agent_section_identity: 'ავტორიზაციის მონაცემები',
    clock_skew_warning: 'თქვენი მოწყობილობის საათი ~{mins} წუთით ცდება. განრიგი მაინც თბილისის დროით მუშაობს.',
    ext_dl_menu: 'გაფართოების ჩამოტვირთვა',
    ext_dl_version: 'ბოლო ვერსია {v}',
    ext_dl_newer: 'თქვენსაზე ახალია',
    ext_dl_current: 'უკვე გაქვთ',
    ext_dl_busy: 'იტვირთება…',
    ext_dl_button: 'ჩამოტვირთვა',
    ext_dl_failed: 'ჩამოტვირთვა ვერ მოხერხდა. სცადეთ ცოტა ხანში.',
    ext_dl_howto: 'ჩამოიტვირთა. გახსენით zip და ჩაანაცვლეთ გაფართოების საქაღალდის ფაილები, შემდეგ chrome://extensions-ში senTavus-ზე დააჭირეთ განახლებას (↻) და გადატვირთეთ ეს გვერდი.',
    ext_banner_outdated: 'senTavus-ის გაფართოების ახალი ვერსიაა ხელმისაწვდომი ({v}). ამ ბრაუზერში არის {have}.',
    ext_banner_missing: 'senTavus-ის გაფართოება ამ გვერდზე არ პასუხობს. ჩამოტვირთეთ ბოლო ვერსია ({v}) და ჩატვირთეთ Chrome-ში.',
    ext_banner_later: 'მოგვიანებით',
    app_offline: 'ოფლაინ · შეავსეთ .env.local Supabase-სთვის',
    app_connected: 'დაკავშირებულია Supabase-თან',
    // AgentsTab filter options
    agents_filter_active: 'აქტიური',
    agents_filter_left: 'გათავისუფლებული',
    agents_filter_all: 'ყველა',
    agents_mark_left_confirm: 'მონიშნოთ {name} გათავისუფლებულად?',
    agents_reactivated_log: 'ხელახლა გააქტიურა: {by}.',
    agents_left_log: 'გათავისუფლდა {by}-ის მიერ.',
    mark_left_title: 'მონიშნოთ {name} გათავისუფლებულად?',
    mark_left_body: 'ის გადავა გათავისუფლებულების სიაში — ნებისმიერ დროს შეგიძლიათ დააბრუნოთ.',
    mark_left_remove_number: 'ასევე დაბრუნდეს სამუშაო ნომერი {number} თავისუფალ ნომრებში',
    mark_left_move_cards: 'ასევე დაბრუნდეს მისი ბარათები ფონდში',
    mark_left_lock_login: 'ლოგინის დაბლოკვა — senTavus-ის ანგარიშსა და გაფართოებაში შესვლა დაუყოვნებლივ შეწყდება',
    agents_left_badge: 'გავიდა',
    agents_resign_future_toast: '{name} ავტომატურად გადავა გათავისუფლებულებში. თარიღი: {date}.',
    agents_resign_past_toast: '{name} მონიშნულია გათავისუფლებულად (თარიღი: {date}).',
    agents_resign_today_toast: '{name} მონიშნულია გათავისუფლებულად დღეიდან.',
    agents_auto_resigned_log: 'ავტომატურად გათავისუფლდა დაგეგმილ თარიღზე.',
    agents_leaving_badge: 'მიდის {date}',
    // HeatmapTab inner title
    heatmap_inner_title: 'აგენტები ოფისში — დღისა და საათის მიხედვით',
    // Filter / general
    // Stats tab
    stats_not_enough_data: 'გრაფიკებისთვის საჭიროა მინიმუმ 2 თვის მონაცემები.',
    stats_chart_attendance: 'დასწრებისა და დაგვიანების ტენდენცია',
    stats_chart_location: 'ოფისი / დისტ. / არ გამოცხ.',
    stats_chart_absent: 'გამოუცხადებლობის მაჩვენებელი',
    stats_late_total: 'დაგვიანება',
    stats_absent_total: 'არ გამოცხ.',
    nav_stats: 'სტატისტიკა',
    nav_review: 'გადამოწმება',
    review_kind_complexes: 'კომპლექსები',
    review_kind_far_links: 'შორეული ბმულები',
    review_far_intro: 'ეს ბინები დაკავშირებულია კომპლექსთან, რომლისგანაც შორს არიან. წაიკითხეთ რას წერს განცხადება — „-ში" და „-თან ახლოს" სხვადასხვა მტკიცებაა',
    review_far_empty: 'შორეული ბმულები არ არის გადასამოწმებელი.',
    review_far_ratio: '{total}-დან {far} ბმული შორეულია',
    review_far_pin_hint: '— თითქმის ყველა, ესე იგი უფრო სავარაუდოა რომ პინი ცდება',
    review_far_no_mention: '(კომპლექსის სახელი ამ განცხადების ტექსტში არ არის)',
    review_far_pin_wrong: 'პინი ცდება',
    review_far_keep_all: 'ყველა სწორია',
    review_far_unlink_all: 'ყველა კავშირის გაუქმება',
    review_far_unlink_one: 'გახსნა',
    review_kind_photo_conflicts: 'ფოტო კონფლიქტები',
    review_photo_intro: 'ორ სხვადასხვა ობიექტზე იდენტური ფოტოებია — სავარაუდოდ ერთი ბინაა, ორჯერ აღწერილი. მიუთითეთ, რომელია; ავტომატურად არაფერი ერთიანდება.',
    review_photo_empty: 'გადაუხედავი ფოტო კონფლიქტები არ არის — ღამის სკანმა ახალი ვერაფერი იპოვა.',
    review_photo_shared: '{n} იდენტური ფოტო',
    review_photo_area_conflict: 'ფართობი არ ემთხვევა: {a} vs {b} მ²',
    review_photo_floor: 'სართ.',
    review_photo_no_thumb: 'ფოტო არ არის',
    review_photo_same_btn: 'იგივე ბინაა',
    review_photo_diff_btn: 'სხვადასხვაა',
    review_photo_unsure_btn: 'არ ვიცი',
    review_kind_public_price: 'საჯარო ფასი',
    review_kind_public_splits: 'საჯარო გაყოფები',
    review_pprice_intro: 'საჯარო ბინები, რომელთა ფასი დამალულია. სადაც ჩვენი პუბლიკაციები სხვადასხვა ფასს აჩვენებს, აირჩიეთ ის, რომელიც ბინის ფასია; საჯარო გვერდი ამ ფასს აჩვენებს.',
    review_pprice_empty: 'დამალული საჯარო ფასი არ არის.',
    review_pprice_not_actionable: 'ბინის იდენტობა დაუზუსტებელია — ფასი დამალულია, სანამ ბინა არ დადასტურდება; ფასის ავტორიტეტი ამას არ ხსნის.',
    review_pprice_use_btn: 'ეს ფასი',
    review_pprice_current: 'ფასის ავტორიტეტი',
    review_pprice_standing: 'მოქმედი ფასის ავტორიტეტები',
    review_pprice_rooms: 'ოთ.',
    review_psplit_intro: 'საჯარო ბინის გვერდი, რომელიც რამდენიმე ბინად გაიყო. ძველი ბმული არსად გადამისამართდება, სანამ არ გადაწყვეტ: აირჩიე ბინა, რომელიც მისი გაგრძელებაა, ან თქვი, რომ სხვადასხვა ბინებია.',
    review_psplit_empty: 'გაყოფილი საჯარო გვერდები არ არის.',
    review_psplit_continue_btn: 'ეს არის გაგრძელება',
    review_psplit_decline_btn: 'სხვადასხვა ბინებია, გაგრძელების გარეშე',
    review_kind_cleaned: 'გასუფთავებული ფოტოები',
    review_cleaned_intro: 'მთავარი გვერდის ფოტოები, რომლებზეც ზემოდან დაწერილი ტექსტი, ტელეფონის ნომერი ან ლოგოა — გასუფთავებულია ოფისის კომპიუტერზე. ადრე | შემდეგ. დაამტკიცეთ, რომ საიტზე გასუფთავებული ასლი გამოჩნდეს; ჩამოხსენით, რომ ისევ ორიგინალი ჩანდეს.',
    review_kind_agent_feedback: 'აგენტების შეფასებები',
    review_fb_intro: 'sentavus.com-ის ვიზიტორების შეფასებები ჩვენს აგენტებზე. ენის შემოწმება სუფთა ტექსტს მაშინვე აქვეყნებს, უხამს სიტყვებს, შეურაცხყოფას, სიძულვილის ენას და რეკლამას უკან აბრუნებს, დანარჩენს აქ აჩერებს. აგენტის მუშაობის კრიტიკა დამალვის მიზეზი არასდროს არის.',
    review_fb_held: 'ელოდება',
    review_fb_flagged: 'მონიშნული',
    review_fb_attempts: 'უარყოფილი ტექსტები',
    review_fb_published: 'გამოქვეყნებული',
    review_fb_hidden: 'დამალული',
    review_fb_empty: 'აქ არაფერია.',
    review_fb_anonymous: 'სახელის გარეშე ჩანს',
    review_fb_quotes: 'მონიშნული სიტყვები',
    review_fb_hidden_because: 'დამალულია, რადგან',
    review_fb_publish: 'გამოქვეყნება',
    review_fb_hide: 'დამალვა',
    review_fb_hide_reason: 'რატომ იმალება? (შეფასებასთან ინახება; ვიზიტორს არ ვატყობინებთ)',
    review_fb_tier_transaction: 'დადასტურებული გარიგება',
    review_fb_tier_interaction: 'დადასტურებული შეხვედრა',
    review_fb_tier_unverified: 'დაუდასტურებელი',
    review_fb_reason_profanity: 'უხამსი სიტყვები',
    review_fb_reason_hate: 'სიძულვილის ენა',
    review_fb_reason_insult: 'შეურაცხყოფა',
    review_fb_reason_threat: 'მუქარა',
    review_fb_reason_sexual: 'სექსუალური',
    review_fb_reason_advertising: 'რეკლამა',
    review_fb_reason_personal_data: 'პირადი მონაცემები',
    review_fb_reason_spam: 'სპამი',
    review_fb_reason_uncertain: 'შემოწმება ვერ გადაწყდა',
    review_fb_reason_serious_allegation: 'სერიოზული ბრალდება',
    review_cleaned_pending: 'ელოდება',
    review_cleaned_live: 'საიტზეა',
    review_cleaned_empty_pending: 'დასამტკიცებელი ფოტო არ არის.',
    review_cleaned_empty_live: 'საიტზე გასუფთავებული ფოტო არ არის.',
    review_cleaned_approve_btn: 'დამტკიცება',
    review_cleaned_approve_all_btn: 'ყველას დამტკიცება',
    review_cleaned_reject_btn: 'უარყოფა',
    review_cleaned_takedown_btn: 'ჩამოხსნა',
    review_cleaned_before: 'ადრე',
    review_cleaned_after: 'შემდეგ',
    review_psplit_represented: 'ჩვენი',
    review_loading: 'იტვირთება',
    review_no_identity: 'აგენტის პროფილი ვერ მოიძებნა — დაუკავშირეთ თქვენი ლოგინი აგენტის პროფილს',
    review_complex_empty: 'გადასამოწმებელი კომპლექსის ბმულები არ არის — AI-მ ყველაფერი გადაწყვიტა.',
    review_complex_intro: 'AI-მ ეს ქონება → კომპლექსი ბმულები ვერ გადაწყვიტა — საბოლოო სიტყვა თქვენია',
    review_complex_dist_was: 'იყო',
    review_complex_dist_at_decision: 'გადაწყვეტის მომენტში',
    review_show_description: 'განცხადების აღწერის ჩვენება',
    review_hide_description: 'აღწერის დამალვა',
    review_link_btn: 'დაკავშირება',
    review_none_btn: 'არცერთი',
    review_unknown_names_title: 'AI-ის ნაპოვნი კომპლექსის სახელები, რომლებიც ბაზაში არ არის',
    review_unknown_names_hint: 'ნახსენებია განცხადებების აღწერებში, მაგრამ კომპლექსების ცხრილში არ არის — ბაზის გასამდიდრებლად.',
    stats_loading: 'იტვირთება…',
    stats_load_failed: 'მონაცემები ვერ ჩაიტვირთა — ციფრები ვერ ნაჩვენებია. სცადეთ თავიდან.',
    stats_load_partial: 'ნაწილი ვერ ჩაიტვირთა ({what}) — ეს ციფრები არასრულია.',
    stats_no_data: 'ამ პერიოდში დასწრების მონაცემები არ არის.',
    stats_days: 'დღე',
    stats_agents: 'აგენტი',
    stats_per_day: 'დღეში',
    stats_late: 'დაგვიანება',
    stats_late_days: 'დღე დაგვიანება',
    stats_ontime: 'დროულად',
    stats_delta: 'სხვაობა',
    stats_back: 'უკან',
    stats_leaderboard: 'რეიტინგი',
    stats_attendance_rate: 'დასწრება',
    stats_ontime_rate: 'დროულობა',
    stats_avg_hours: 'საშ. საათი/დღე',
    stats_total_delta: 'დაგეგმილთან სხვაობა',
    stats_daily_breakdown: 'დღეების მიხედვით',
    stats_team_attendance: 'გუნდის დასწრება',
    stats_team_ontime: 'გუნდის დროულობა',
    stats_team_delta: 'გუნდის სხვაობა',
    stats_sort_attendance: '↓ დასწრება',
    stats_sort_ontime: '↓ დროულობა',
    stats_sort_delta: '↓ სხვაობა',
    stats_sort_name: 'სახელი A–Z',
    stats_sort_performance: '↓ შედეგი',
    stats_sort_listings: '↓ გამოქვეყნებული',
    stats_sort_bestday: '↓ საუკეთესო დღე',
    stats_score: 'ქულა',
    stats_performance_score: 'შესრულების ქულა',
    stats_part_commission: 'საკომისიო',
    stats_part_meetings: 'შეხვედრები',
    stats_part_scrapes: 'შენახული',
    stats_part_reshares: 'რეპოსტები',
    stats_part_office: 'ოფისი',
    stats_pillar_attendance: 'დასწრება',
    stats_pillar_activity: 'აქტივობა',
    stats_pillar_results: 'შედეგები',
    stats_results: 'შედეგები',
    stats_coming_soon: 'მალე',
    stats_total_earned: 'შემოსავალი',
    stats_earned: 'საკომისიო',
    stats_commission: 'საკომისიო',
    stats_trend_results: 'შედეგების ტენდენცია', stats_trend_discipline: 'სამუშაო დისციპლინის ტენდენცია',
    stats_trend_agent_note: 'დროულობა ითვლება მხოლოდ ჩექ-ინიან დღეებზე, ამიტომ ხარვეზი ნიშნავს ჩექ-ინის არარსებობას და არა 0%-ს.',
    stats_active_work_days: 'აქტიური სამუშაო დღე', stats_vs_team: 'გუნდთან შედარება',
    stats_rank: 'რანგი', stats_metric: 'მეტრიკა', stats_team_avg: 'გუნდის საშუალო',
    stats_credit_hint: 'პროპორციული წილი', stats_participation_hint: 'გარიგება, სადაც მონაწილეობდა',
    stats_credit_explain: 'გაზიარებული გარიგება ითვლება წილად, არა მთლიანად',
    stats_view_agents: 'აგენტები', stats_view_managers: 'მენეჯერები', stats_view_general: 'ზოგადი',
    stats_range_7d: '7 დღე', stats_range_30d: '30 დღე', stats_range_mtd: 'ეს თვე',
    stats_range_90d: '90 დღე', stats_range_6m: '6 თვე', stats_range_1y: '1 წელი',
    stats_vs_prev: 'წინა პერიოდთან', stats_prev_new: 'ახალი', stats_unrecorded: 'ჩანაწერის გარეშე',
    stats_cohort: 'ამ პერიოდში აყვანილები', stats_cohort_hint: 'აგენტები, რომლებიც აყვანის დღეს ამ მენეჯერის ოფისში მოვიდნენ',
    stats_hires: 'აყვანილი', stats_hire_to_start: 'აყვანა → პირველი მოსვლა', stats_ramp_up: 'აყვანა → პირველი გარიგება',
    stats_never_earned: '90 დღეში გარიგების გარეშე', stats_retention: 'დარჩენილი', stats_days_short: 'დ', stats_median: 'მედიანა',
    stats_of_started: 'დაიწყო', stats_of_earned: 'გამოიმუშავა', stats_of_matured: 'მომწიფებული',
    stats_commission_per_agent_day: 'მთლიანი / აგენტ-დღე', stats_agent_days: 'აგენტ-დღე',
    stats_office_scope: 'ოფისის ფილტრი, აგენტების ხედი',
    stats_since_hire: '{date}-დან',
    stats_active_agents: 'აქტიური აგენტები', stats_closed_transactions: 'დახურული გარიგებები',
    stats_agents_commission: 'აგენტების საკომისიო', stats_no_compare_roster: 'შედარება არ არის: {m}-დან {n} აგენტი წინა პერიოდის დაწყების შემდეგ მოვიდა', stats_manager_tag: 'მენეჯერი', stats_no_work_days: 'სამუშაო დღე არ აქვს', stats_no_work_days_tip: 'ამ აგენტის განრიგში სამუშაო დღე არ არის, ამიტომ უმოქმედო და დაგვიანებული დღეები არ ითვლება. განრიგი შეავსეთ აგენტებში.', stats_avg_commission_per_agent: 'საშ. საკომისიო / აგენტი',
    stats_company_level: 'კომპანიის დონეზე', stats_agents_word: 'აგენტი', stats_transactions_word: 'გარიგება',
    stats_agent_ranking: 'აგენტების რანგი', stats_sort_hint: 'დასალაგებლად დააჭირეთ სვეტს',
    stats_col_agent: 'აგენტი', stats_col_commission: 'საკომისიო', stats_col_deal_credit: 'გარიგების წილი',
    stats_col_participation: 'მონაწილეობა', stats_col_per_day: 'გამოქვეყნება/დღე',
    stats_col_attendance: 'დასწრება', stats_col_ontime: 'დროულობა', stats_col_score: 'ქულა',
    stats_col_late: 'დაგვიანება', stats_col_absent: 'გაცდენა', stats_col_overtime: 'ზეგანაკვეთური',
    stats_col_deals: 'გარიგება', stats_col_collabs: 'გაზიარებული გარიგება', stats_col_coops: 'თანამშრომლობა',
    stats_col_repost_cost: 'რეპოსტის ხარჯი', stats_meetings_tip: '{done} შედეგით (ითვლება ქულაში) · სულ {all}',
    stats_col_per_hour: 'გამოქვეყნება/საათი', stats_col_hours: 'ნამუშევარი საათი',
    stats_hours_tip: '{sched} დაგეგმილიდან ნამუშევარია {worked}',
    stats_hours_estimated_tip: '{days} დღე ჩათვლილია ოფისის საათებით ({mins}), არა დაფიქსირებული',
    stats_deals_tip: '{n} გარიგება: {solo} დამოუკიდებლად, {coop} კოლეგასთან ერთად',
    stats_late_tip: '{n}-ჯერ, ჯამში {mins}, ათვლა განრიგის დაწყებიდან',
    stats_absent_tip: '{sched} დაგეგმილი დღიდან {n}',
    stats_overtime_tip: 'განრიგზე მეტად ნამუშევარი დღეების ჯამი ({days} დღე); წმინდა ბალანსი {delta}',
    stats_manager_ranking: 'მენეჯერების რანგი', stats_col_manager: 'მენეჯერი',
    stats_col_added: 'დამატებული', stats_col_interviewed: 'გასაუბრებული', stats_col_hired: 'აყვანილი', stats_col_fired: 'გასული',
    stats_recruiting: 'რეკრუტინგი', stats_recruiting_hint: 'ამ პერიოდში, ამ მენეჯერის მიერ',
    stats_no_rate: 'განაკვეთი არ არის', stats_since: 'დან',
    stats_only_active: 'აქტიური', stats_all_incl_past: 'გასულებთან ერთად', stats_past_agent: 'გასული',
    stats_not_yours: 'შეგიძლიათ იხილოთ მხოლოდ თქვენი სტატისტიკა.',
    stats_unranked: 'ამ პერიოდში დახურული გარიგება არ არის',
    stats_col_manager_commission: 'მენეჯერის საკომისიო', stats_col_team_deals: 'გუნდის გარიგებები',
    stats_col_team_gross: 'გუნდის მთლიანი საკომისიო',
    stats_no_managers_title: 'მენეჯერის განაკვეთი ჯერ არ არის მითითებული',
    stats_no_managers_hint: 'მენეჯერი იღებს წილს თავისი ოფისის ყველა დახურული გარიგებიდან. მიუთითეთ გუნდის საკომისიო % აგენტების ჩანართში და გარიგებები აქ გამოჩნდება. ნული არ იწერება, რადგან განაკვეთის არარსებობა და შემოსავლის არარსებობა ერთი და იგივე არ არის.',
    stats_manager_unstamped_hint: 'თუ მენეჯერს საკომისიო არ უჩანს, გუნდს კი გარიგებები აქვს — განაკვეთი ამ გარიგებების დახურვის შემდეგ დაემატა. განაკვეთი ფიქსირდება დახურვის მომენტში და უკან არ ვრცელდება.',
    stats_manager_result: 'მენეჯერის შედეგი', stats_manager_rate: 'მენეჯერის განაკვეთი', stats_of_gross: 'მთლიანი საკომისიოდან',
    stats_before_allocation: 'განაწილებამდე', stats_team_volume: 'გუნდის მოცულობა',
    stats_team_discipline: 'გუნდის დისციპლინა', stats_team_productivity: 'გუნდის პროდუქტიულობა',
    stats_days_with_checkin: 'დღე ჩექ-ინით',
    stats_published_per_agent_day: 'გამოქვეყნება / აგენტი / დღე', stats_deals_per_agent: 'გარიგება / აგენტი',
    stats_commission_per_agent: 'საკომისიო / აგენტი', stats_commission_per_transaction: 'საკომისიო / გარიგება',
    stats_transactions_per_agent: 'გარიგება / აგენტი',
    stats_company_result: 'კომპანიის შედეგი', stats_reconciles_hint: 'ჯამი უბრუნდება მთლიან საკომისიოს',
    stats_gross_commission: 'მთლიანი საკომისიო', stats_managers_commission: 'მენეჯერების საკომისიო',
    stats_company_retained: 'კომპანიის წილი', stats_rounding: 'დამრგვალება',
    stats_over_allocated: '{n} გარიგებაზე განაწილება მთლიან საკომისიოს აღემატება — შეამოწმეთ მონაწილე აგენტები.',
    stats_productivity: 'პროდუქტიულობა', stats_outcome_vs_volume: 'შედეგი, არა მოცულობა',
    stats_team_activity: 'გუნდის აქტივობა', stats_activity_hint: 'შესრულებული სამუშაო',
    stats_discipline: 'დასწრება და დროულობა', stats_attention: 'ყურადღება',
    stats_attention_hint: 'მმართველობითი დიაგნოსტიკა',
    stats_attn_top_commission: 'ყველაზე მაღალი საკომისიო', stats_attn_most_deals: 'ყველაზე მეტი გარიგება',
    stats_attn_lowest_activity: 'ყველაზე დაბალი აქტივობა', stats_attn_most_late: 'ყველაზე მეტი დაგვიანება',
    stats_attn_most_absent: 'ყველაზე მაღალი გაცდენა',
    stats_scheduled_delta: 'დაგეგმილთან სხვაობა', stats_result_group: 'შედეგი', stats_deals_group: 'გარიგებები',
    stats_manager_share: 'გუნდის წილი', stats_manager_deals: 'გუნდის წილი გარიგებების მიხედვით',
    stats_deals_solo: 'დამოუკიდებლად', stats_deals_coop: 'თანამშრომლობით', stats_partners: 'ერთად დახურული',
    stats_deals: 'გარიგებები',
    stats_closed: 'დახურული',
    stats_deal_value: 'გარიგების ღირ.',
    stats_total: 'სულ',
    agent_modal_deals: 'გარიგებები',
    agent_modal_timeline: 'ისტ.',
    deals_date: 'თარიღი',
    deals_value: 'გარიგების ღირებულება',
    deals_commission: 'საკომისიო',
    deals_add: 'დამატება',
    deals_none: 'გარიგებები ჯერ არ არის',
    dv_new: 'ახალი', dv_search_label: 'ქონების ძებნა ID / საკადასტრო / ბმულით',
    dv_search_ph: 'დრაფტის ID, ss/myhome id, საკადასტრო ან URL…',
    dv_searching: 'მიმდინარეობს ძებნა…', dv_no_results: 'დრაფტი ვერ მოიძებნა',
    dv_no_key: 'ქონების ძებნა მიუწვდომელია (scheduler key არ არის).',
    dv_owner_agent: 'აგენტი', dv_pick: 'არჩევა', dv_rooms: 'ოთახი', dv_floor: 'სართ.',
    dv_property: 'ქონება', dv_owner: 'მესაკუთრის კონტაქტი', dv_client: 'კლიენტის კონტაქტი',
    dv_manual: 'ხელით შეყვანა', dv_name: 'სახელი', dv_phone: 'ტელეფონი',
    dv_agents: 'აგენტები (საკომისიო იყოფა თანაბრად)', dv_add_agent: 'აგენტის დამატება', dv_commission: 'საკომისიო',
    dv_lead_agent: 'ვინ იყო შეხვედრაზე', dv_lead_default: '— მაღალანაზღაურებადი (ნაგულისხმევი) —',
    dv_comm_imported: 'იმპორტირებული', dv_comm_computed: 'გამოთვლილი', dv_comm_manual: 'ხელით', dv_comm_cooperation: 'თანამშრომლობიდან',
    dv_deal_price: 'გარიგების ფასი', dv_comm_percent: 'პროცენტი', dv_comm_fixed: 'ფიქსირებული', dv_comm_khelze: 'ხელზე',
    dv_open_draft: 'ბარათი', dv_listing: 'განცხადება',
    dv_search: 'ძიება მისამართით, კლიენტით, აგენტით…', dv_type_all: 'ყველა ტიპი',
    dv_sort_newest: '↓ უახლესი', dv_sort_scheduled: '↓ ნახვის დრო', dv_sort_comm_high: '↓ საკომისიო',
    dv_related: 'დაკავშირებული:', dv_group_property: 'ქონება', dv_group_client: 'კლიენტი', dv_group_agent: 'აგენტი',
    dv_no_client: 'უკლიენტო', dv_no_agent: 'უაგენტო',
    dv_split_each: 'თითო', dv_status: 'სტატუსი',
    dv_status_scheduled: 'დანიშნული', dv_status_viewing: 'ნახვა', dv_status_pending: 'მოლოდინში', dv_status_binder: 'ბე',
    dv_status_closed: 'დახურული', dv_status_failed: 'ჩაიშალა',
    dv_status_cancelled: 'გაუქმდა', dv_status_rescheduled: 'გადადებული',
    dv_sched_date: 'ნახვის თარიღი', dv_sched_time: 'დრო', dv_time: 'დრო',
    dv_kind_scheduled: 'დანიშნული', dv_kind_reschedule: 'გადანიშნული', dv_kind_occurred: 'შედგა',
    dv_kind_no_show: 'არ შედგა', dv_kind_rejected: 'უარყო', dv_kind_cancelled: 'გაუქმდა',
    dv_kind_binder: 'ბე', dv_kind_closed: 'დაიხურა',
    dv_what_happened: 'რა მოხდა?', dv_add_note: 'შენიშვნის დამატება…',
    dv_undo: 'დაბრუნება',
    dv_binder_amount: 'ბეს თანხა', dv_binder_paid: 'გადახდის თარიღი',
    dv_binder_close: 'დახურვის თარიღი', dv_binder_holder: 'ვისთან რჩება ბე',
    // B340 (2026-08-06) — იხ. ინგლისური მხარე.
    dv_binder_legacy_title: 'ადრე ჩაწერილი ბე',
    dv_binder_moved_hint: 'ბე ახლა შეხვედრაზე ინახება — იქ ხედავს ვადის შეხსენება.',
    dv_holder_agent: 'აგენტი', dv_holder_owner: 'მესაკუთრე', dv_holder_split: '50/50',
    dv_payment_date: 'საკომისიოს გადახდის თარიღი', dv_notes: 'შენიშვნები',
    dv_save: 'შენახვა', dv_saving: 'ინახება…', dv_cancel: 'გაუქმება', dv_delete: 'წაშლა',
    dv_delete_confirm: 'წაიშალოს ეს შეხვედრა — {what}? მასთან ერთად წაიშლება ისტორიაც და აგენტების მიბმაც',
    dv_retry: 'სცადეთ ხელახლა',
    dv_close_confirm: 'ჩაიწეროს გარიგება დახურულად? განცხადება მოინიშნება გაყიდულად/გაქირავებულად, თანამშრომლობა დასრულდება და აგენტებს ჩაეთვლებათ — 15-წამიანი გაუქმება მხოლოდ სტატუსს აბრუნებს',
    dv_bad_date: 'ჯერ აირჩიეთ თარიღი',
    dv_timeline: 'ისტორია', dv_add_event: 'განახლების დამატება', dv_event_kind: 'ტიპი',
    dv_kind_viewing: 'ნახვა', dv_kind_meeting: 'შეხვედრა', dv_kind_call: 'ზარი',
    dv_kind_note: 'შენიშვნა', dv_kind_status_change: 'სტატუსის ცვლილება',
    dv_none: 'გარიგებები ჯერ არ არის', dv_deal_type_lease: 'ქირა', dv_deal_type_trade: 'გაყიდვა',
    dv_pick_first: 'ზემოთ მოძებნეთ და აირჩიეთ ქონება, ან მონაცემები ხელით შეიყვანეთ.',
    admin_perf_weights: 'შესრულების ქულის წონები',
    admin_perf_weights_desc: 'კოეფიციენტები საერთო შესრულების ქულისთვის. მნიშვნელობა აქვს მხოლოდ თანაფარდობას. „შედეგები“ დაემატება მონაცემების გაჩენის შემდეგ.',
    stats_posted: 'გამოქვეყნებული',
    stats_total_posted: 'სულ გამოქვეყნებული',
    stats_posted_per_day: 'გამოქ./დღე',
    stats_best_day: 'საუკეთესო დღე',
    stats_active_days: 'აქტიური დღე',
    stats_in_range: 'პერიოდში',
    stats_per_day_short: '/დღე',
    stats_record_most_day: 'ყველაზე მეტი დღეში',
    stats_tab_label: 'სტატ.',
    admin_late_threshold: 'დაგვიანების ზღვარი (წუთი)',
    cand_hired_as_agent: 'დაემატა აგენტად',
    cand_revert_status: 'სტატუსის დაბრუნება',
    cand_hire_revert: 'გაუქმება',
    cand_hire_revert_confirm: 'დავაბრუნოთ {name} კანდიდატებში და წავშალოთ აგენტის პროფილი?',
    cand_hire_revert_timer: 'გაუქმება ({sec}წ)',
    cand_modal_info_tab: 'ინფო',
    cand_interviewed_confirm: 'მოინიშნოს, რომ {name} გასაუბრებაზე იყო?',
    cand_finalize_action: 'დაასრულეთ',
    cand_status_starting: 'იწყებს',
    cand_iv_label: 'გასაუბრება',
    att_expected_badge: 'დღეს ელოდებით',
    att_onboarding_badge: 'პირველი სამუშაო დღე',
    att_onboarding_note: 'დღეს აქ არის — შეავსეთ განრიგი, ტელეფონი და დეტალები',
    att_onboarding_note_ready: 'პირველი დღე — დღეს აქ არის (განრიგით დასვენება)',
    att_showed_up: '✓ გამოცხადდა',
    att_no_show_btn: '✗ არ გამოცხადდა',
    att_showed_up_confirm: 'ავიყვანოთ {name}? გახდება აგენტი და ჩაეწერება მოსვლა.',
    att_no_show_confirm: 'მოვნიშნოთ {name} არ გამოცხადებულად? სტატუსი დაბრუნდება გასაუბრებაზე.',
    att_no_show_log: '✗ არ გამოცხადდა პირველ დღეს',
    cand_starting_btn: '🚀 იწყებს',
    cand_undo_starting: '↩ გაუქმება',
    cand_start_date_label: 'პირველი დღე',
    cand_start_confirm: 'დაყენება',
    cand_starts_today: 'დღეს იწყებს',
    cand_no_show: 'არ გამოცხადდა',
    cand_no_show_btn: '✗ არ გამოცხადდა',
    cand_no_show_confirm: 'მოვნიშნოთ {name} გასაუბრებაზე არ გამოცხადებულად? თუ დაგიკავშირდებათ, თავიდან ჩანიშნავთ გასაუბრებას.',
    cand_no_show_was: 'გასაუბრება იყო დაგეგმილი: {date}{time} — არ გამოცხადდა.',
    cand_reschedule_hint: 'არ გამოცხადება არ უქმდება — ახალი თარიღი აირჩიეთ და თავიდან დაგეგმეთ.',
    cand_reschedule_btn: '🔄 თავიდან დაგეგმვა',
    cand_view_interviews: 'გასაუბრებების განრიგი',
    cand_interviewed_btn: '✓ ჩატარდა',
    cand_undo_interviewed: '↩ გასაუბრების სტატუსის გაუქმება',
    cand_hire_btn: '🎉 აყვანა',
    cand_reject_btn: '✕ უარი',
    cand_undo_reject: '↩ უარის გაუქმება',
    cand_clear_interview: '✕ გასაუბრების თარიღის წაშლა',
    cand_date_label: 'თარიღი',
    cand_time_label: 'დრო',
    cand_contact: 'კონტაქტი',
    cand_fullname_label: 'სახელი და გვარი *',
    cand_bday_label: 'დაბადების თარიღი',
    cand_selfreported: 'თავად მიუთითა',
    cand_study_no: 'არ სწავლობს', cand_study_student: 'სტუდენტი', cand_study_pupil: 'მოსწავლე',
    cand_ct_label: 'როდის დავუკავშირდეთ', cand_ct_anytime: 'ნებისმიერ დროს', cand_ct_business: 'სამუშაო საათებში', cand_ct_custom: 'კონკრეტულ დროს', cand_ct_err: 'მიუთითეთ დაწყებისა და დასრულების დრო. დასრულების დრო დაწყების დროზე გვიან უნდა იყოს.',
    cand_sales_none: 'გაყიდვების გამოცდილება არ აქვს', cand_sales_lt1: 'გაყიდვები: 1 წლამდე', cand_sales_1_3: 'გაყიდვები: 1–3 წელი', cand_sales_3p: 'გაყიდვები: 3+ წელი',
    cand_avail_now: 'იწყებს დაუყოვნებლივ', cand_avail_1w: 'იწყებს 1 კვირაში', cand_avail_2w: 'იწყებს 2 კვირაში', cand_avail_other: 'დაწყება: სხვა',
    cand_heard_from: 'როგორ გაიგო: {v}',
    cand_cv_open: 'CV-ის გახსნა',
    cand_cv_opening: 'იხსნება…',
    cand_cv_fail: 'CV ვერ გაიხსნა',
    cand_age_years: '{n} წლის',
    cand_link_label: 'პროფილის ბმული',
    cand_link_hint: '(Facebook, TikTok, LinkedIn…)',
    cand_open_link: '↗ ბმულის გახსნა',
    cand_select_role: '— აირჩიეთ პოზიცია —',
    cand_select_source: '— აირჩიეთ წყარო —',
    cand_select: '— აირჩიეთ —',
    cand_add_header: 'კანდიდატის დამატება',
    cand_history: 'ისტორია',
    log_cand_no_show: '✗ არ გამოცხადდა გასაუბრებაზე ({date}).',
    log_cand_starting: 'პირველი დღე დაინიშნა: {date}{time}',
    cand_iv_now: 'ახლა',
    filter_clear: '✕ გასუფთავება',
    filter_clear_all: 'ყველა ფილტრის გასუფთავება',
    cand_count_one: 'კანდიდატი',
    cand_count_many: 'კანდიდატი',
    cand_count_of: '/',
    cand_sort_status: '↑ სტატუსი',
    cand_sort_added: '↑ ახლად დამატებული',
    cand_sort_interview: '↓ გასაუბრების თარიღი',
    cand_sort_name: '↑ სახელი A–Z',
    // Log messages
    log_cand_added: 'კანდიდატი დაამატა {by}-მა. პოზიცია: {position}. წყარო: {source}.',
    log_cand_added_short: 'დამატებულია{position}{source}.',
    log_cand_deleted: 'კანდიდატი წაშალა {by}-მა.',
    log_cand_status: 'სტატუსი: {from} → {to}. {by}-მა.',
    log_cand_scheduled: 'გასაუბრება დაგეგმა {by}-მა: {date}, {time}.',
    log_cand_rescheduled: 'გადავადდა: {from} → {to}.',
    log_cand_rescheduled_full: 'გასაუბრება გადაიტანა {by}-მა: {from} → {to}.',
    log_cand_cancelled: 'გასაუბრება გაუქმდა (იყო {date}).',
    log_cand_interviewed: 'გასაუბრება ჩატარდა{date}{interviewer}.',
    log_cand_rejected: 'უარი.{reason}',
    log_cand_hired_short: 'აყვანილია. შეიქმნა აგენტის პროფილი — შეავსეთ განრიგი.',
    log_cand_hired: 'აყვანა {by}-მა. შეიქმნა აგენტის პროფილი.',
    log_cand_hired_from_pipeline: 'შეიქმნა კანდიდატებიდან {by}-მა.{interviewed}{interviewer}{notes}',
    log_agent_added: 'სისტემაში დაამატა {by}-მა. პოზიცია: {role}.{hire_date}',
    log_agent_profile: 'პროფილი განაახლა {by}-მა: {diff}',
    log_agent_resign: 'გათავისუფლება დაფიქსირდა {by}-მა.{date}{reason}',
    log_agent_removed: 'აგენტი წაიშალა სისტემიდან {by}-მა.{reason}',
    log_schedule_change: 'განრიგის ცვლილება {by}-მა: {change}',
    log_att_entry: '{date} — {changes}. {by}-მა.',
    log_att_absent: 'არ გამოცხ. {date}-ს, {by}-მა.{note}',
    log_att_holiday: 'შვებულება {date}-ს, {by}-მა.',
    log_att_worked: ' ნამუშ.: {duration}.',
    log_att_delta: ' სხვაობა: {delta}.',
    log_att_deleted: '{date}-ის დასწრება წაიშალა {by}-მა.',
    log_office_capacity: 'მაგიდების რ-ბა შეცვალა {by}-მა: {from} → {to}.',
    log_office_settings: 'ოფისის პარამეტრები განაახლა {by}-მა: {parts}.',
    log_office_deleted: 'ოფისი „{name}" წაშალა {by}-მა.',
    log_office_move: 'ოფისი შეუცვალა {by}-მა: {from} → {to}.',
    log_office_none: 'ოფისის გარეშე',
    log_office_name_part: 'სახელი: "{name}"',
    log_office_location_part: 'ლოკაცია: "{location}"',
    log_diff_checkin: 'შემოსვლა: {from} → {to}',
    log_diff_checkout: 'გასვლა: {from} → {to}',
    log_diff_note: 'შენიშვნა: "{note}"',
    log_diff_field: '{label}: "{from}" → "{to}"',
    log_diff_interviewed_on: ' {date}-ს',
    log_diff_at_time: ' {time}-ზე',
    log_diff_with: ' {name}-თან',
    log_diff_reason: ' მიზეზი: {reason}',
    log_diff_date: ' თარიღი: {date}.',
    log_diff_hire_date: ' აყვანის თარიღი: {date}.',
    log_diff_interviewed_was: ' გასაუბრება: {date}.',
    log_diff_notes: ' შენიშვნა: {notes}.',
    log_diff_for: ' პოზიციაზე: {position}',
    log_diff_via: ' წყარო: {source}',
    // Profile field labels for diff
    log_field_name: 'სახელი', log_field_role: 'პოზიცია', log_field_email: 'ელფოსტა',
    log_field_phone_work: 'სამსახურის ტელეფონი', log_field_phone_personal: 'პირადი ტელეფონი',
    log_field_hire_date: 'აყვანის თარიღი',
    log_field_commission: 'საკომისიო %',
    log_field_commission_manager: 'გუნდის საკომისიო % (მენეჯერი)',
    // Live attendance card strings
    att_live_due: 'მოსვლა',
    att_live_in: '',
    att_live_late: 'აგვიანებს',
    att_live_no_checkin: 'შემოსვლა არ დაფიქსირებულა', att_live_unresolved: 'დასწრება გაურკვეველია', att_live_scheduled: 'განრიგით',
    att_live_meetings_unknown: 'შეხვედრები ვერ შემოწმდა',
    att_no_checkin_short: 'არ დაფიქსირდა', att_unresolved_short: 'გაურკვეველი',
    att_prev_period: 'წინა პერიოდი', att_next_period: 'შემდეგი პერიოდი', att_view_label: 'დასწრების ხედი',
    att_read_failed: 'დასწრება ვერ ჩაიტვირთა - ნაჩვენები შეიძლება არასრული ან მოძველებული იყოს.',
    att_overrides_failed: 'ერთჯერადი ცვლილებები ვერ ჩაიტვირთა - ზოგ დღეს შეიძლება ჩვეულებრივი განრიგი ჩანდეს.',
    att_retry: 'თავიდან ცდა',
    att_show_days: 'დღეების ჩვენება', att_hide_days: 'დღეების დამალვა',
    att_save_failed: 'ვერ შეინახა - არაფერი შეცვლილა. შეყვანილი მონაცემები ადგილზეა, სცადეთ თავიდან.',
    att_incl_assumed: 'მ.შ. {d} ვარაუდით', att_planned_ahead: '{d} ჯერ დაგეგმილია',
    att_bulk_button: 'ყველა ვადაგადაცილებულის გასვლა ({n})', att_bulk_busy: 'ინიშნება…',
    att_bulk_confirm: 'ჩაიწეროს გასვლა თითოეულის განრიგის დასრულების დროით? შეინახება როგორც მენეჯერის შესწორება და არა დაფიქსირებული გასვლა.',
    att_bulk_skipped: 'ამ დღეს განრიგი არ აქვს, არ შეიცვალა', att_bulk_none: 'ამ დღეს არავის აქვს განრიგი.',
    att_bulk_note: 'გასვლა მენეჯერმა განრიგით შეავსო', att_bulk_failed: 'ვერ შეინახა',
    att_live_on_meeting: 'შეხვედრაზეა',
    att_live_leaves: 'გასვლა ~',
    att_live_due_out: 'გასვლა',
    att_live_ago: 'წინ',
    att_just_arrived: '✓ ახლახან მოვიდა',
    my_worker_title: 'ჩემი კომპიუტერი მუშაობს ჩემთვის',
    my_worker_start: 'ავტომატური რეჟიმის ჩართვა', my_worker_pause: 'ავტომატური რეჟიმის შეჩერება',
    my_worker_mode_label: 'ავტომატური რეჟიმი', my_worker_start_short: 'ჩართვა', my_worker_pause_short: 'პაუზა', my_worker_part_held: 'შეჩერებულია მთავარი გადამრთველით',
    my_worker_parts: 'მხოლოდ ერთის შეჩერება:', my_worker_part_reshare: 'ავტომატური გადაზიარება', my_worker_part_wa: 'WhatsApp მესიჯები', my_worker_part_on: 'ჩართული', my_worker_part_off: 'შეჩერებული', my_worker_part_hint_reshare: 'ავტომატური გადაზიარება - დააწკაპუნეთ მხოლოდ ამის შესაჩერებლად ან გასაგრძელებლად', my_worker_part_hint_wa: 'WhatsApp მესიჯები - დააწკაპუნეთ მხოლოდ ამის შესაჩერებლად ან გასაგრძელებლად',
    my_worker_chip_auto: '↺ ოფისით', my_worker_chip_paused: '⏸ პაუზა', my_worker_chip_running: '▶ ჩართული',
    my_worker_no_lane: 'ავტომატური რეჟიმი ჯერ ჩართული არ არის - ჩასართავად დააჭირეთ „ავტომატური რეჟიმის ჩართვა“.',
    my_worker_enroll_no_pc: 'senTavus-ის გაფართოებით კომპიუტერი ხაზზე არ არის - გახსენით Chrome სამუშაო კომპიუტერზე.',
    my_worker_enroll_old_version: 'გაფართოება ძველია - დააყენეთ ბოლო ვერსია Desk-იდან და ისევ დააჭირეთ ჩართვას.',
    my_worker_enroll_no_whatsapp: 'გახსენით WhatsApp Web სამუშაო კომპიუტერზე, შედით სამუშაო ნომრით, დაელოდეთ ერთ წუთს და ისევ დააჭირეთ ჩართვას.',
    my_worker_enroll_number_taken: 'ეს WhatsApp ნომერი უკვე სხვა აგენტს ეკუთვნის - აცნობეთ ოფისს.',
    my_worker_hint: 'თქვენი WhatsApp ხაზი და ავტომატური გაზიარებები თქვენს კომპიუტერზე მუშაობს. როცა თავად იყენებთ, შეაჩერეთ.',
    my_worker_auto: 'ავტომატური', my_worker_paused: 'შეჩერება', my_worker_running: 'გაგრძელება',
    my_worker_auto_hint: 'ჩერდება, როცა ოფისში ხართ მონიშნული; გასვლის შემდეგ აგრძელებს',
    my_worker_now_paused: 'ავტომატური რეჟიმი შეჩერებულია', my_worker_now_running: 'ავტომატური რეჟიმი ჩართულია',
    my_worker_loading: 'კომპიუტერის შემოწმება…', my_worker_retry: 'თავიდან ცდა',
    my_worker_read_failed: 'ავტომატური რეჟიმის წაკითხვა ვერ მოხერხდა.',
    my_worker_read_stale: 'განახლება ვერ მოხერხდა - ეს არის ბოლო მდგომარეობა, რომელიც სერვერმა დაადასტურა.',
    my_worker_pcs_failed: 'კომპიუტერის შემოწმება ვერ მოხერხდა - ნაჩვენებია {t}-ის შემოწმება.',
    my_worker_pcs_failed_first: 'კომპიუტერის შემოწმება ვერ მოხერხდა.',
    my_worker_pc_seen: 'ბოლოს ჩანდა {t}',
    my_worker_ver_checking: 'გაფართოების ვერსია მოწმდება…',
    my_worker_ver_unknown: 'გაფართოების ვერსია ვერ შემოწმდა - ჩართვა შემოწმებამდე დაბლოკილია.',
    my_worker_login_unknown: 'შესვლის მონაცემი ჯერ არ მოსულა',
    my_worker_save_failed: 'შენახვა ვერ მოხერხდა - პარამეტრი არ შეცვლილა. სცადეთ თავიდან.',
    my_worker_why_office: 'ოფისში ხართ', my_worker_why_you: 'თქვენი არჩევანი',
    my_worker_pc: 'თქვენი კომპიუტერი', my_worker_pc_none: 'senTavus-ის გაფართოებით კომპიუტერი არ არის დაკავშირებული - გახსენით Chrome თქვენს სამუშაო კომპიუტერზე.',
    my_worker_pc_offline: 'ხაზგარეშე', my_worker_pc_online: 'ხაზზე',
    my_worker_ver_old: 'გაფართოების ძველი ვერსია ({v}) - ჯერ დააყენეთ ბოლო ({latest}) Desk-იდან. მანამდე გადამრთველი დაბლოკილია.',
    my_worker_ver_ok: 'ბოლო ვერსია',
    my_worker_login_ok: 'შესულია', my_worker_login_no: 'არ არის შესული - გთხოვთ შეხვიდეთ {p}-ზე ამ კომპიუტერში',
  // ==== Desk tab (IntakeTab.jsx) - desk_* keys ====
    desk_add_client: 'კლიენტის დამატება',
    desk_add_request_title: 'ახალი მოთხოვნა — {name}',
    desk_addreq_bound: 'ამ პიროვნებას',
    desk_person_add_request: 'მოთხოვნის დამატება',
    desk_toast_request_saved: '✓ მოთხოვნა დაემატა',
    desk_add_property: 'ობიექტის დამატება',
    desk_add_business: 'ბიზნესის დამატება',
    desk_add_business_pick_property: 'ბიზნესი ყოველთვის ობიექტზეა — ჯერ აირჩიეთ, რომელზე:',
    desk_add_business_search_ph: 'მოძებნეთ თქვენი ობიექტები მისამართით...',
    desk_add_business_none: 'ჩატვირთულ სიაში ასეთი ობიექტი არ არის.',
    desk_add_meeting: 'შეხვედრის დამატება',
    desk_add_candidate: 'კანდიდატის დამატება',
    desk_new_applicants_one: 'ახალმა კანდიდატმა შეავსო ვაკანსიის ფორმა — დაუკავშირდით და ჩანიშნეთ გასაუბრება',
    desk_new_applicants_failed: 'ახალი კანდიდატები ვერ ჩაიტვირთა.',
    desk_nudge_failed: 'შეხვედრების შეხსენებები ვერ ჩაიტვირთა - შეხსენების არქონა არ ნიშნავს, რომ ვადაგადაცილებული არაფერია.',
    desk_home_retry: 'თავიდან ცდა',
    desk_home_meetings_failed: 'შეხვედრები ვერ ჩაიტვირთა.',
    desk_home_meetings_loading: 'შეხვედრები იტვირთება…',
    desk_home_leads_failed: 'ბოლო ლიდები ვერ ჩაიტვირთა.',
    desk_home_leads_stale: 'ლიდების განახლება ვერ მოხერხდა - შესაძლოა მოძველებული იყოს.',
    desk_new_applicants_many: '{n} ახალმა კანდიდატმა შეავსო ვაკანსიის ფორმა — დაუკავშირდით და ჩანიშნეთ გასაუბრება',
    desk_new_applicants_more: 'კიდევ {n} — „კანდიდატების“ ჩანართში',
    desk_new_applicants_dismiss: 'ამ კანდიდატის დამალვა (მხოლოდ თქვენთვის)',
    desk_new_applicants_unassigned: 'მენეჯერის გარეშე',
    desk_new_applicants_seen: 'ნანახია',
    desk_new_applicants_new: 'ახალი',
    cand_ev_viewed: 'ბარათი გაიხსნა',
    desk_add_meeting_pick_property: 'რომელ ობიექტზეა შეხვედრა?',
    desk_add_meeting_no_property: 'ობიექტის გარეშე — მხოლოდ კლიენტთან',
    desk_add_meeting_search_ph: 'მისამართი, მესაკუთრის სახელი ან ნომერი…',
    desk_add_meeting_searching: 'ვეძებთ ამ ნომრით ყველა ობიექტში...',
    desk_add_meeting_owner_unknown: 'მესაკუთრე უცნობია',
    desk_add_meeting_reveal: 'ნომრის ჩვენება',
    desk_add_meeting_no_contact: 'ნომერი არ არის',
    desk_add_meeting_none_anywhere: 'ამ ნომრით ან id-ით ბაზაში ობიექტი არ მოიძებნა.',
    desk_add_meeting_anyway: 'ჩამონათვალში არაა ობიექტი — მაინც ჩავნიშნოთ',
    desk_biz_section_title: 'ბიზნესები ამ მისამართზე',
    desk_biz_facet_summary: 'ამ ობიექტზე მოქმედი ბიზნესები',
    desk_biz_add: 'ბიზნესის დამატება',
    desk_biz_none: 'ამ ობიექტზე ბიზნესი არ არის დაფიქსირებული.',
    desk_biz_category: 'კატეგორია',
    desk_biz_name: 'ბიზნესის სახელი',
    desk_biz_description: 'აღწერა',
    desk_biz_legal_form: 'სამართლებრივი ფორმა',
    desk_biz_tenure: 'ფართი',
    desk_biz_tenure_leased: 'იჯარით',
    desk_biz_tenure_owned: 'საკუთრებაში',
    desk_biz_rent: 'თვიური ქირა (USD)',
    desk_biz_landlord_consent: 'მესაკუთრის თანხმობა',
    desk_biz_yes: 'კი',
    desk_biz_no: 'არა',
    desk_biz_pending: 'მოლოდინში',
    desk_biz_land_status: 'მიწის სტატუსი',
    desk_biz_land_agricultural: 'სასოფლო-სამეურნეო',
    desk_biz_land_non_agricultural: 'არასასოფლო-სამეურნეო',
    desk_biz_employees: 'თანამშრომლები (სრულ განაკვეთზე)',
    desk_biz_reason: 'გაყიდვის მიზეზი',
    desk_biz_reason_retirement: 'პენსიაზე გასვლა',
    desk_biz_reason_relocation: 'სხვა ადგილზე გადასვლა',
    desk_biz_reason_partnership: 'პარტნიორობის დაშლა',
    desk_biz_reason_expansion: 'გაფართოება',
    desk_biz_reason_health: 'ჯანმრთელობა',
    desk_biz_reason_lifestyle: 'ცხოვრების სტილი',
    desk_biz_other: 'სხვა',
    desk_biz_grant: 'სახელმწიფო გრანტის / პროგრამის ვალდებულება',
    desk_biz_grant_note: 'გრანტის შენიშვნა',
    desk_biz_create_listing: 'გასაყიდი განცხადების შექმნა ახლავე',
    desk_biz_price: 'მოთხოვნილი ფასი',
    desk_biz_currency: 'ვალუტა',
    desk_price_on_request: 'ფასი შეთანხმებით',
    desk_biz_save: 'ბიზნესის შენახვა',
    desk_biz_cancel: 'გაუქმება',
    desk_biz_err_category: 'ჯერ აირჩიეთ კატეგორია.',
    desk_biz_feed_empty: 'გასაყიდი ბიზნესი ჯერ არ არის.',
    desk_biz_locations: 'ლოკაციები',
    desk_biz_features: 'მახასიათებლები',
    desk_biz_assets: 'აქტივები',
    desk_biz_claims: 'მეპატრონის ნათქვამი მონაცემები',
    // Business offers and financial figures (core 3922, owner 2026-10-26).
    desk_biz_offers: 'შეთავაზებები',
    desk_biz_no_offers: 'შეთავაზება ჯერ არ არის',
    desk_biz_offer_add: 'შეთავაზების დამატება',
    desk_biz_offer_kind: 'რა იყიდება',
    desk_biz_offer_business_only_premises_leased: 'მხოლოდ ბიზნესი — ფართი იჯარით',
    desk_biz_offer_business_with_premises: 'ბიზნესი ფართთან ერთად',
    desk_biz_offer_business_sold_premises_rented_from_seller: 'ბიზნესი; ფართს გამყიდველი აქირავებს',
    desk_biz_offer_business_for_lease: 'ბიზნესი იჯარით — მართავს მოიჯარე, მფლობელი იგივე რჩება',
    desk_biz_offer_share: 'გასაყიდი წილი, %',
    desk_biz_offer_share_whole: 'მთლიანი ბიზნესი',
    desk_biz_offer_share_line: '{pct}% წილი',
    desk_biz_offer_fee: 'ყოველთვიური გადასახადი მფლობელს',
    desk_biz_offer_upfront: 'წინასწარი გადასახადი (არასავალდებულო)',
    desk_biz_offer_upfront_line: '+ {amount} წინასწარ',
    desk_biz_offer_buyer_rent: 'ქირა მყიდველისთვის',
    desk_biz_offer_rent_line: 'ქირა {amount} {period}',
    desk_biz_per_month: 'თვეში',
    desk_biz_per_year: 'წელიწადში',
    desk_biz_offer_confirmed: 'ხელმისაწვდომობა დადასტურდა: {date}',
    desk_biz_offer_unconfirmed: 'ხელმისაწვდომობა არ არის დადასტურებული',
    desk_biz_offer_confirm: 'ჯერ კიდევ იყიდება',
    desk_biz_offer_withdraw: 'შეთავაზების მოხსნა',
    desk_biz_from_advert: 'განცხადებიდან',
    desk_biz_financials: 'ფინანსური მონაცემები',
    desk_biz_fin_add: 'მონაცემის დამატება',
    desk_biz_fin_kind: 'მონაცემი',
    desk_biz_fin_annual_turnover: 'წლიური ბრუნვა',
    desk_biz_fin_monthly_revenue: 'თვიური შემოსავალი',
    desk_biz_fin_daily_revenue: 'დღიური ნავაჭრი',
    desk_biz_fin_net_profit: 'წმინდა მოგება',
    desk_biz_fin_evidence: 'წყარო',
    desk_biz_evidence_seller_stated: 'გამყიდველის ნათქვამი',
    desk_biz_evidence_documents_supplied: 'დოკუმენტები წარმოდგენილია',
    desk_biz_evidence_reviewed: 'გადამოწმებულია',
    desk_biz_fin_amount_min: 'თანხა (დან)',
    desk_biz_fin_amount_max: 'თანხა (მდე, თუ დიაპაზონია)',
    desk_biz_fin_year: 'წელი',
    desk_biz_trading: 'მუშაობს თუ არა',
    desk_biz_trading_trading: 'მოქმედია',
    desk_biz_trading_temporarily_closed: 'დროებით დახურულია',
    desk_biz_trading_assets_only: 'მხოლოდ აქტივები',
    desk_biz_handover: 'გამყიდველი დაეხმარება საქმის გადაბარებაში',
    desk_biz_handover_weeks: 'გადაბარება (კვირა)',
    desk_biz_weeks: 'კვირა',
    desk_biz_relationship: 'ეს ობიექტი არის',
    desk_biz_rel_hq: 'სადაც ბიზნესი მუშაობს',
    desk_biz_rel_operating_land: 'მიწა, რომელზეც ბიზნესი მუშაობს',
    desk_biz_rel_branch: 'ფილიალი',
    desk_biz_pub_public: 'გამოჩნდეს sentavus.com-ზე',
    desk_biz_pub_show_name: 'ჩანდეს ბიზნესის სახელი',
    desk_biz_pub_show_location: 'ჩანდეს ზუსტი მისამართი (თორემ მხოლოდ უბანი)',
    desk_biz_pub_needs_offer: 'საიტზე გამოჩნდება, როცა ერთი შეთავაზება მაინც ექნება.',
    desk_clarify_yes: 'კი',
    desk_clarify_no: 'არა',
    desk_clarify_dont_know: 'არ ვიცი',
    desk_clarify_skip: 'გამოტოვება',
    desk_clarify_type_answer: 'ჩაწერეთ პასუხი…',
    desk_clarify_got_it: '✓ გავიგე',
    desk_paste_main_placeholder: 'ჩასვით შეტყობინება (ქართ., ინგლ., რუს.) და დააჭირეთ Enter-ს — AI გადაწყვეტს, რა დაამატოს…',
    desk_paste_analyzing: '🧠 AI აანალიზებს…',
    desk_paste_enter_hint: '↵ Enter-ით შეავსეთ · Shift+Enter ახალი ხაზისთვის',
    desk_paste_unsure_type: '🤔 გაურკვეველია, კლიენტია, ობიექტი თუ ჩვენება — აირჩიეთ ღილაკი და ფორმა ნაპოვნი მონაცემებით შევსებული გაიხსნება.',
    desk_paste_matches_existing: '👇 ემთხვევა არსებულ ჩანაწერს — ნარინჯისფერია თუ AI დარწმუნებულია, ლურჯია თუ უბრალოდ ვარაუდია. დააჭირეთ გასაგრძელებლად, ან "კლიენტის დამატება"-ს ახლისთვის.',
    desk_paste_followup_existing: '💬 ახალი არაფერია — არსებული კონტაქტის გაგრძელებას ჰგავს (იხ. მონიშნული ჩანაწერი).',
    desk_paste_nothing_actionable: '💬 ამ შეტყობინებაში დასამატებელი ვერაფერი მოიძებნა.',
    desk_paste_looks_like_property: '🏠 ობიექტის განცხადებას ჰგავს და არა კლიენტს — დაემატა ქვემოთ განსახილველად (სიის თავშია).',
    desk_modal_paste_heading: 'ჩასმა — AI ავსებს, თქვენ ამოწმებთ',
    desk_modal_paste_owner_ph: 'ჩასვით მესაკუთრის შეტყობინება ან განცხადების ტექსტი (არასავალდებულო — შეგიძლიათ პირდაპირ ქვემოთ შეავსოთ)…',
    desk_modal_paste_client_ph: 'ჩასვით კლიენტის შეტყობინება (ქართ., ინგლ., რუს.) — არასავალდებულო, შეგიძლიათ პირდაპირ ქვემოთ შეავსოთ…',
    desk_modal_parsing: '⏳ იკითხება…',
    desk_modal_parse_to_draft: '✨ ავტომატური შევსება',
    desk_modal_paste_text_n: 'ტექსტი {n}',
    desk_modal_paste_text_remove: 'წაშლა',
    desk_modal_paste_add_text: '+ ტექსტის დამატება',
    desk_modal_paste_more_ph: 'კიდევ ერთი ტექსტი იმავე ქონებაზე — WhatsApp-ის შეტყობინება, განცხადება, თქვენი აღწერა…',
    desk_modal_parsed_check_fields: '✓ შევსებულია — შეამოწმეთ ველები',
    desk_modal_ai_couldnt_fill: '⚠ AI-მ ვერ შეავსო: {list of field names}',
    desk_review_decision_badge: 'გადაწყვეტილება: {decision}',
    desk_review_confidence_badge: 'სიზუსტე: {pct}%',
    desk_review_continuing_demand: 'არსებული მოთხოვნის გაგრძელება',
    desk_review_preview: 'გადახედვა',
    desk_review_edit_json: '{ } JSON-ის რედაქტირება',
    desk_review_apply_edits: 'ცვლილებების შენახვა',
    desk_review_fields_will_change: 'ველები, რომლებიც შეიცვლება',
    desk_review_nothing_to_change: 'შესაცვლელი არაფერია.',
    desk_review_saving: 'ინახება…',
    desk_review_accept_edited_commit: '✓ დადასტურება და შენახვა (რედაქტირებული)',
    desk_review_accept_commit: '✓ დადასტურება და შენახვა',
    desk_review_reject: '✗ უარყოფა',
    desk_review_section_party: 'პირი',
    desk_review_section_intent: 'მიზანი',
    desk_review_section_criteria: 'კრიტერიუმები',
    desk_review_field_price: 'ფასი',
    desk_review_field_area_sqm: 'ფართობი (მ²)',
    desk_review_field_land_sqm: 'მიწის/ეზოს ფართობი (მ²)',
    desk_review_field_rooms: 'ოთახები',
    desk_review_field_bedrooms: 'საძინებლები',
    desk_review_drive_max: 'მგზავრობა ≤ {n} წთ',
    desk_review_label_mortgage: 'იპოთეკა',
    desk_review_label_pets: 'ცხოველები',
    desk_review_label_children: 'ბავშვები',
    desk_review_section_districts: 'უბნები',
    desk_review_exclude_prefix: 'გამორიცხვა:',
    desk_review_section_features: 'მახასიათებლები',
    desk_review_feature_condition: 'მდგომარეობა',
    desk_review_feature_cond_exclude: 'მგდმ. გამორიცხვა',
    desk_review_feature_must_have: 'აუცილებელი',
    desk_review_feature_nice_have: 'სასურველი',
    desk_review_feature_must_not_have: 'მიუღებელი',
    desk_review_section_ai_explanation: 'AI-ის განმარტება',
    desk_review_intent_readonly_hint: 'მიზანი — განისაზღვრა AI-ს მიერ, ხელით ვერ შეიცვლება',
    desk_review_intent_readonly_hint_form: 'მიზანი — კლიენტმა თავად მიუთითა ფორმაში, ხელით ვერ შეიცვლება',
    desk_review_intent_readonly_hint_manual: 'მიზანი — შეყვანილია კლიენტის დამატებისას, ხელით ვერ შეიცვლება',
    desk_review_intent_readonly_hint_unknown: 'მიზანი — განისაზღვრა ჩანაწერის შექმნისას, ხელით ვერ შეიცვლება',
    desk_map_draw: '✏️ მოხაზვა',
    desk_map_move: '✋ რუკის გადაადგილება',
    desk_map_undo_point: '↩ წერტილის გაუქმება',
    desk_map_clear_area: 'არეალის წაშლა',
    desk_map_draw_hint_empty: 'საძიებო არეალის მოსანიშნად დააჭირეთ რუკას ან შემოხაზეთ (არასავალდებულო)',
    desk_map_pan_hint: 'რუკის გადასაადგილებლად გადაათრიეთ',
    desk_map_points_progress: '{n} წერტილი — საჭიროა 3+ შესაკრავად',
    desk_map_area_set: '{n}-წერტილიანი არეალი',
    desk_map_clear_pin: 'პინის წაშლა',
    desk_map_pin_coords: '📍 {lat}, {lng}',
    desk_map_pin_hint_empty: 'პინის დასასმელად დააჭირეთ რუკას (არასავალდებულო)',
    desk_criteria_section_price_size: 'ფასი და ფართობი',
    desk_criteria_price_min: 'მინ. ფასი',
    desk_criteria_price_max: 'მაქს. ფასი',
    desk_criteria_currency: 'ვალუტა',
    desk_criteria_max_drive: 'მაქს. მგზავრობა (წთ)',
    desk_criteria_area_min: 'მინ. ფართ. (მ²)',
    desk_criteria_area_max: 'მაქს. ფართ. (მ²)',
    desk_criteria_land_min: 'მინ. მიწის/ეზოს ფართ. (მ²)',
    desk_criteria_land_max: 'მაქს. მიწის/ეზოს ფართ. (მ²)',
    desk_criteria_rooms_min: 'მინ. ოთახები',
    desk_criteria_rooms_max: 'მაქს. ოთახები',
    desk_criteria_bedrooms_min: 'მინ. საძინებელი',
    desk_criteria_bedrooms_max: 'მაქს. საძინებელი',
    desk_criteria_mortgage_ready: 'იპოთეკა',
    desk_criteria_pets: 'ცხოველები',
    desk_criteria_children: 'ბავშვები',
    desk_criteria_section_districts: 'უბნები',
    desk_criteria_districts_must: 'აუცილებელი',
    desk_criteria_districts_wanted: 'სასურველი',
    desk_criteria_districts_excluded: 'მიუღებელი',
    desk_criteria_section_features: 'მახასიათებლები',
    desk_criteria_section_household: 'ვინ იცხოვრებს?',
    desk_criteria_household_tags: 'ტეგები',
    desk_criteria_occupants: 'მაცხოვრებელთა რაოდენობა',
    desk_parse_field_districts_must: "უბნები — აუცილებელი",
    desk_parse_field_districts_nice: "უბნები — სასურველი",
    desk_parse_field_districts_not: "უბნები — გამორიცხული",
    desk_parse_field_features_must: "მახასიათებლები — აუცილებელი",
    desk_parse_field_features_nice: "მახასიათებლები — სასურველი",
    desk_parse_field_features_not: "მახასიათებლები — დაუშვებელი",
    desk_criteria_search_area: "საძიებო არეალი რუკაზე",
    desk_parse_value_yes: "დიახ",
    desk_parse_value_no: "არა",
    desk_parse_value_drawn_area: "რუკაზე დახაზული არეალი",
    desk_enrich_paste_heading: "დამატებითი დეტალები — ჩასვით, AI შეავსებს",
    desk_enrich_paste_ph: "ჩასვით, რაც კლიენტმა ამის შემდეგ გითხრათ (ქართ., ინგლ., რუს.)…",
    desk_enrich_parse: "გაანალიზება და შევსება",
    desk_parse_reads_as_property: "ეს ობიექტის განცხადებას ჰგავს, კლიენტის შეტყობინებას არა.",
    desk_parse_no_demand_found: "ამ შეტყობინებაში კლიენტის დეტალები ვერ მოიძებნა.",
    desk_note_heading: "{n} ჩასმული შეტყობინება ამ ჩანაწერზე",
    desk_note_more: "სრულად",
    desk_note_less: "დამალვა",
    desk_note_not_saved: "არ შენახულა",
    desk_note_manual_action: "ხელით შეიცვალა",
    desk_note_photo_count: "{n} ფაილი",
    desk_note_channel_scheduler: "აგენტმა ჩასვა",
    desk_note_channel_client_form: "კლიენტმა შეავსო",
    desk_note_channel_facebook_group: "Facebook-იდან",
    desk_note_channel_test: "ტესტი",
    desk_note_channel_manual: "ატვირთული",
    desk_note_channel_sentavus: "ატვირთული",
    office_wifi_section: "ოფისის Wi-Fi შემოწმება",
    office_wifi_ssid: "ქსელის სახელი",
    office_wifi_router: "როუტერის მისამართი (MAC)",
    office_wifi_loading: "მოწმდება…",
    office_wifi_never: "ამ ოფისიდან რეპორტი არასდროს მოსულა — მოსვლა Wi-Fi-ით ვერ დასტურდება.",
    office_wifi_live: "რეპორტერი მუშაობს — ახლა ქსელში {n} მოწყობილობაა ({src}).",
    office_wifi_src_router_self: 'თავად როუტერი',
    office_wifi_self_title: 'როუტერი თავად აგზავნის',
    office_wifi_self_why: 'მაშინ ოფისის კომპიუტერის ჩართვა საჭირო აღარ არის: როუტერი ყოველ წუთს ატყობინებს senTavus-ს, ვინ არის დაკავშირებული, და ვინც ამ Wi-Fi-ზე Desk-ში შესულია — ტელეფონით თუ ლეპტოპით — მოსვლას მონიშნავს და ის დადასტურდება.',
    office_wifi_self_btn: 'ინსტალაციის ბრძანების მიღება',
    office_wifi_self_how: 'ოფისის Wi-Fi-ზე დაკავშირებულ კომპიუტერზე გახსენით PowerShell (Windows) ან Terminal (Mac), ჩასვით ეს, დააჭირეთ Enter-ს და შეიყვანეთ როუტერის ადმინის პაროლი. ერთხელ კეთდება. ამ ოფისისთვის მომუშავე კომპიუტერი გაჩერდება და როუტერი გადაიბარებს.',
    office_wifi_self_copy: 'კოპირება',
    office_wifi_self_copied: 'დაკოპირდა',
    office_wifi_self_secret: 'ბრძანებაში ამ ოფისის გასაღებია — ნუ გაუზიარებთ. ახალი ბრძანების მიღებისას ძველი აღარ იმუშავებს.',
    office_wifi_counts: "ამ ოფისზე რეგისტრირებულია {registered} ტელეფონი; მათგან ქსელშია {matched}.",
    office_wifi_shared: "იმავე როუტერზეა, რაც: {names} — ზემოთ მოცემული რიცხვები ორივე ოფისს მოიცავს.",
    office_wifi_silent: "რეპორტერი {mins} წუთია დუმს — კომპიუტერი, საიდანაც ის მუშაობს, გამორთულია, Chrome დახურულია, ან ოფისის ქსელს მოწყდა.",
    office_wifi_connected_by: "მონაცემებს აგზავნის ბრაუზერი ({name}){model} · დაკავშირდა {date}.",
    office_wifi_this_browser: "ოფისის მოწყობილობების მონაცემებს ეს ბრაუზერი აგზავნის. სამუშაო საათებში ამ კომპიუტერზე Chrome ღია დატოვეთ.",
    office_wifi_router_changed: "რეპორტერი ახლა სხვა როუტერს ხედავს ({mac}{ssid}). თუ ოფისის როუტერი შეიცვალა, გამოიყენეთ ეს.",
    office_wifi_use_router: "ამ როუტერის გამოყენება",
    office_wifi_connect: "როუტერთან დაკავშირება",
    office_wifi_reconnect: "როუტერთან ხელახლა დაკავშირება",
    office_wifi_disconnect: "გათიშვა",
    office_wifi_disconnect_confirm: "გავთიშოთ ოფისის როუტერი? მონაცემების გაგზავნა შეწყდება, სანამ ვინმე ხელახლა არ დააკავშირებს.",
    office_wifi_finding: "ოფისის როუტერს ვეძებთ…",
    office_wifi_host: "როუტერის მისამართი",
    office_wifi_found: "როუტერი ნაპოვნია მისამართზე {host}. შეიყვანეთ მისი ადმინისტრატორის პაროლი — ის ამ ბრაუზერის გაფართოებაში რჩება და Sentavus-ს არასდროს ეგზავნება.",
    office_wifi_password: "როუტერის ადმინისტრატორის პაროლი",
    office_wifi_connecting: "უკავშირდება…",
    office_wifi_connected_now: "დაკავშირებულია — ახლა ქსელშია {n} მოწყობილობა.",
    office_wifi_no_ext: "გახსენით ეს გვერდი Chrome-ში senTavus-ის გაფართოებით, ოფისის Wi-Fi-ზე.",
    office_wifi_old_ext: "senTavus-ის ეს გაფართოება როუტერთან დასაკავშირებლად ძველია — განაახლეთ და გადატვირთეთ გვერდი.",
    office_wifi_stale_ext: "გაფართოება გადაიტვირთა — განაახლეთ ეს გვერდი.",
    office_wifi_router_line: "როუტერი {mac} · {ssid}",
    office_wifi_hint: "დააკავშირეთ ერთხელ ოფისის Wi-Fi-ზე მყოფი კომპიუტერიდან: senTavus-ის გაფართოება შედის როუტერში და, სანამ Chrome ღიაა, ყოველ წუთს აგზავნის, რომელი მოწყობილობებია ქსელში. მისი წვდომა თავად ახლდება. აგენტი ერთხელ არეგისტრირებს ტელეფონს — შემდეგ მოსვლას ადასტურებს ის, რომ ოფისმა დაინახა.",
    desk_criteria_children_ages: 'ბავშვების ასაკები',
    desk_criteria_move_in: 'შესახლების თარიღი',
    desk_criteria_check_in: 'შესვლის თარიღი',
    desk_criteria_check_out: 'გასვლის თარიღი',
    desk_criteria_type_features: 'დამატებითი დეტალები',
    desk_criteria_power_min: 'მინ. სიმძლავრე (კვტ)',
    desk_facts_power_kw: 'გამოყოფილი სიმძლავრე (კვტ)',
    desk_facts_functional_zone: 'ფუნქციური ზონა (მაგ. სზ-2)',
    desk_criteria_section_quality: 'ხარისხის დონე',
    desk_criteria_dealbreakers: 'აუცილებელი და მიუღებელი პირობები',
    desk_criteria_more_features: 'მეტი მახასიათებელი…',
    desk_tier_legend: 'დააჭირეთ ჩიპს და აირჩიეთ მნიშვნელობა',
    desk_tier_chip_cycle_hint: 'დააჭირეთ შესაცვლელად ან მოსახსნელად',
    // The three tier words are taken VERBATIM from the old cycle legend, which is how the
    // app already named them to agents — a menu is no reason to reword a known vocabulary.
    desk_tier_must: 'აუცილებელი',
    desk_tier_nice: 'სასურველი',
    desk_tier_not: 'მიუღებელი',
    desk_tier_clear: 'წაშლა',
    desk_criteria_district_search_ph: 'უბნის ძებნა…',
    desk_criteria_feature_search_ph: 'მახასიათებლის ძებნა…',
    desk_district_group_other: 'სხვა',
    desk_district_search: 'უბნის ძებნა…',
    desk_district_all_cities: 'მთელი საქართველო',
    desk_district_no_match: 'ვერაფერი მოიძებნა',
    desk_district_tree_hint: 'დააჭირეთ უბანს მთლიანად ასარჩევად, ან › — შესავიწროებლად.',
    desk_sale_terms_title: 'გაყიდვის პირობები — გამყიდველის მოთხოვნები (მხოლოდ აგენტებისთვის)',
    // GIRAO (core 0219). "დამგირავებელი" is the platforms' own word for the side that
    // puts up the money; the yes/no wording below says what actually happens instead of
    // repeating the legal term, because the two platform labels disagree about it.
    desk_terms_other_shape: 'პირობები ჩაწერილია აგრეთვე:',
    desk_girao_terms_title: 'გირავნობის პირობები — მესაკუთრის მოთხოვნები (მხოლოდ აგენტებისთვის)',
    desk_girao_amount: 'გირაოს თანხა',
    desk_girao_period: 'პერიოდი (წელი)',
    desk_girao_owner_resides: 'მეპატრონე იცხოვრებს იქვე?',
    desk_girao_owner_resides_yes: 'დიახ — მეპატრონე რჩება ბინაში',
    desk_girao_owner_resides_no: 'არა — ბინა მთლიანად თქვენია',
    desk_girao_hint: 'გირაოს თანხა თავიდან გადაეცემა და ბოლოს უკან ბრუნდება — ეს არც ფასია და არც დეპოზიტი. კლიენტისთვის ხშირად გადამწყვეტია, დარჩება თუ არა მეპატრონე ბინაში: ჰკითხეთ დათვალიერებამდე, არა მის შემდეგ.',
    desk_sale_policies_label: 'ვის მიჰყიდის მეპატრონე (სტრუქტურირებული — ემთხვევა კლიენტის ტეგებს)',
    desk_sale_cash_only: 'ანგარიშსწორება',
    desk_sale_cash_only_yes: 'მხოლოდ ნაღდი',
    desk_sale_cash_only_no: 'ნაღდი ან იპოთეკა',
    desk_sale_full_price: 'სრული ფასის დეკლარირება',
    desk_sale_full_price_yes: 'კი — სრულ ფასს აცხადებს',
    desk_sale_full_price_no: 'არა — შემცირებული ოფიციალური ფასი უნდა',
    desk_sale_occupancy: 'ვინ ცხოვრობს',
    desk_sale_handover: 'გათავისუფლება ხელმოწერიდან',
    desk_terms_movein_timing: 'მოიჯარე შეძლებს გადასვლას',
    desk_sale_furniture: 'ავეჯი ფასში',
    desk_sale_parking: 'პარკინგი ფასში',
    desk_sale_encumbrance: 'რეესტრის სტატუსი',
    desk_sale_bank_appraisal: 'ბანკის შეფასება',
    desk_sale_summary_appraisal: 'შეფასება {v} {c}',
    desk_sale_appraisal_opt_none: 'ბანკის შეფასება არ აქვს',
    desk_sale_appraisal_opt_appraised: 'ბანკის მიერ შეფასებულია',
    desk_sale_appraisal_amount: 'შეფასების თანხა (თუ ცნობილია)',
    desk_sale_opt_vacant: 'ცარიელია',
    desk_sale_opt_owner: 'მეპატრონე ცხოვრობს',
    desk_sale_opt_tenant: 'დამქირავებელი ცხოვრობს',
    desk_sale_opt_immediate: 'დაუყოვნებლივ',
    desk_sale_opt_one_week: '1 კვირამდე',
    desk_sale_opt_two_weeks: '2 კვირამდე',
    desk_sale_opt_one_month: '1 თვემდე',
    desk_sale_opt_flexible: 'მოქნილი / შეთანხმებით',
    desk_sale_opt_none: 'ცარიელი / მხოლოდ ჩაშენებული',
    desk_sale_opt_partial: 'ნაწილობრივ',
    desk_sale_opt_as_seen: 'როგორც ფოტოებზეა',
    desk_sale_opt_included: 'შედის',
    desk_sale_opt_separate: 'ცალკე იყიდება',
    desk_sale_opt_no_parking: 'პარკინგი არ აქვს',
    desk_sale_opt_clean: 'სუფთა რეესტრი',
    desk_sale_opt_bank_mortgage: 'იპოთეკით დატვირთული',
    desk_terms_summary_deposit: 'დეპოზიტი — {n} თვის ქირა',
    desk_terms_summary_months: '{n} თვე წინასწარ',
    desk_terms_deposit_required: 'დეპოზიტი საჭიროა',
    desk_terms_deposit_required_short: 'დეპოზიტი',
    desk_terms_deposit_basis_amount: 'ფიქსირებული თანხა',
    desk_terms_deposit_basis_percent: 'თვის ქირის %',
    desk_terms_summary_deposit_amount: 'დეპოზიტი {v} {c}',
    desk_terms_summary_minlease: 'მინ {n}თვე',
    desk_terms_summary_maxocc: 'მაქს {n} ად.',
    desk_terms_policies_label: 'ვის იღებს მეპატრონე (სტრუქტურირებული — ემთხვევა კლიენტის ტეგებს)',
    desk_terms_policies_legend: 'დააჭირე: ✓ დაიშვება → ★ სასურველია → ✗ არ დაიშვება → ❗ მხოლოდ → წაშლა',
    // In-place owner-gap answer from the match panel's hoisted "ask the owner" chips.
    desk_policy_answer_prompt: 'მესაკუთრის პასუხი:',
    desk_stance_allowed: 'დაიშვება',
    desk_stance_preferred: 'სასურველი',
    desk_stance_not_allowed: 'არ დაიშვება',
    desk_stance_only: 'მხოლოდ',
    // ── Matching panel v2 — native-speaker reviewed 2026-08-08.
    // ⚠️ TWO of these were INVERTED in the draft and are the reason nothing machine-drafted
    // ships here unreviewed:
    //   mv2_nobody_asked_owner — "მეპატრონეს არ უკითხავს" reads as *the owner has not asked
    //     anyone*. The subject flips. Correct form is "მეპატრონისთვის არ უკითხავთ".
    //   mv2_step2 — "კლიენტების ზარები" reads as *calls FROM the clients*. Correct is
    //     "კლიენტებთან ზარები" (calls TO them).
    // Also settled by the reviewer: agents say ობიექტი, not ქონება, for a listing.
    mv2_title: 'შესაბამისი კლიენტები',
    mv2_subtitle: 'შემოწმდა {n}, {m} შეიძლება მოერგოს',
    mv2_updated_ago: 'განახლდა {n} წუთის წინ',
    mv2_step1: 'ჯერ ეს ზარი — მეპატრონესთან',
    mv2_step1_sub: '{n} ცარიელი ველი · ხსნის {m} კლიენტს',
    mv2_unlocks: 'ხსნის {n}-ს',
    mv2_step2: 'მერე — კლიენტებთან ზარები',
    mv2_wouldnt_say: 'არ მითხრა',
    mv2_recorded_by: 'ჩაწერილია {date} · {name}',
    mv2_no_longer_asked: 'აღარ იკითხება',
    mv2_recount_promise: 'პასუხის ჩაწერისთანავე სია გადაითვლება — ახალი კლიენტებიც გამოჩნდება',
    mv2_more_questions: 'კიდევ {n} კითხვა',
    mv2_each_unlocks: 'თითო 1–2 კლიენტს ხსნის',
    mv2_expand: 'გაშლა',
    mv2_owner_fields_only: 'აქ მხოლოდ ის ველებია, რასაც მეპატრონე პასუხობს. უბანი, ფართი, ბიუჯეტი — კლიენტის მხარეა',
    mv2_group_ready: 'შესთავაზე ახლავე',
    mv2_group_ready_empty: 'პირველივე ჩაწერილი პასუხი აქ ბარათებს გადმოიტანს',
    mv2_col_client: 'კლიენტი — გასაშლელად დააჭირეთ',
    mv2_col_what_to_do: 'რა ვქნათ',
    mv2_band_why: 'რატომ ემთხვევა',
    mv2_band_mismatch: 'არ ემთხვევა',
    mv2_band_to_fill: 'შესავსები',
    mv2_col_client_asks: 'კლიენტი ითხოვს',
    mv2_col_on_property: 'ობიექტზე',
    mv2_col_source: 'წყარო',
    mv2_col_who_answers: 'ვინ პასუხობს',
    mv2_col_known_today: 'დღეს რა ვიცით',
    mv2_fill_in: 'შეავსე',
    mv2_not_asked_yet: 'არავის უკითხავს',
    mv2_will_concede: 'დათმობს?',
    mv2_owner_answers: 'მეპატრონე პასუხობს',
    mv2_client_answers: 'კლიენტი პასუხობს',
    mv2_nobody_asked_owner: 'მეპატრონისთვის არ უკითხავთ',
    mv2_blocks_nothing: 'არაფერს ბლოკავს — უბრალოდ ინფორმაციისთვის',
    mv2_details_loading: 'დეტალები იტვირთება…',
    mv2_chip_asked_above: '? ზემოთ {n}',
    mv2_one_source_verify: 'ერთი წყარო — გადაამოწმე',
    mv2_fixed_condition: 'დაფიქსირებული პირობა',
    mv2_legend_never_asked: 'კლიენტს ეს არ უთხოვია',
    mv2_legend_loading: 'დეტალები იტვირთება…',
    mv2_legend_fixed: 'დაფიქსირებული პირობა, არ ექვემდებარება მოლაპარაკებას',
    mv2_contact_property_side: 'ობიექტის მხარე',
    mv2_contact_client_side: 'კლიენტის მხარე',
    mv2_role_owner: 'მესაკუთრე',
    mv2_role_agent: 'აგენტი',
    mv2_role_property_rep: 'ობიექტის წარმომადგენელი',
    mv2_role_client: 'კლიენტი',
    mv2_no_other_rep: 'კლიენტს სხვა წარმომადგენელი არ ჰყავს — მხოლოდ თქვენ ხართ',
    mv2_recorded_conditions: 'ჩაწერილი პირობები',
    mv2_exception: 'გამონაკლისი',
    mv2_another_condition: 'სხვა პირობა',
    mv2_empty_no_requirement: 'ჯერ არ ვიცით, რა უნდა ამ კლიენტს',
    mv2_empty_add_requirement: 'დაამატე მოთხოვნა',
    mv2_empty_market: 'დღეს ბაზარზე ამ პირობებს არაფერი აკმაყოფილებს',
    mv2_binding_constraint: 'შემზღუდავი პირობა: {field}',
    mv2_approximately: 'დაახლოებით',
    mv2_money_no_help: 'ფული აქ არ შველის',
    mv2_ladder_mine: 'ჩემი ობიექტები',
    mv2_ladder_leads: 'ლიდები',
    mv2_ladder_search: 'მოძებნე თვითონ',
    mv2_lead_call_first: 'ჯერ მეპატრონეს დაურეკე',
    mv2_summary: 'შედარდა {total} პირობა · ემთხვევა {met} · არ ემთხვევა {missed} · შესავსებია {ownerGaps} მეპატრონესთან + {clientGaps} კლიენტთან → შეავსე ორივე მხარე, მერე შესთავაზე',
    mv2_summary_all_ok: 'ყველაფერი ემთხვევა — შესთავაზე.',
    mv2_summary_owner_only: 'ერთი ზარი მეპატრონესთან და მზადაა.',
    mv2_summary_client_only: 'კლიენტთან {n} დეტალია გასარკვევი და მზადაა.',
    mv2_summary_mismatch: '{n} პირობა არ ემთხვევა — გადაწყვიტე, ღირს თუ არა შეთავაზება.',
    // Reviewed by a native speaker 2026-08-08 (round 2). Three answers worth keeping in view,
    // because a non-speaker would have got each of them wrong:
    //   • `რემონტი` is the word an agent actually says for a flat's CONDITION, and it
    //     removes the `მდ` collision between მდებარეობა and მდგომარეობა — which is
    //     what made two truncated column headers indistinguishable.
    //   • `ხსნის {n}-ს` KEEPS its `-ს` even with the noun dropped: the numeral takes the
    //     noun's role and must carry the dative. A bare "ხსნის 4" is wrong.
    //   • `≈` was DROPPED on the reviewer's advice — "weakly evidenced" does not compress into
    //     two Georgian words without sounding robotic. Use ✓ + `ერთი წყარო` instead.
    mv2_col_district: 'უბანი',
    mv2_col_price: 'ფასი',
    mv2_col_bedrooms: 'საძინ.',
    mv2_col_occupants: 'მცხოვრ.',
    mv2_col_condition: 'რემონტი',
    mv2_col_other: 'სხვა',
    mv2_grp_ready_means: 'მეპატრონისთვის საკითხავი აღარაფერია',
    mv2_grp_verify_means: 'ველოდებით მეპატრონის პასუხს',
    mv2_grp_negotiate_means: 'კლიენტს დათმობა მოუწევს',
    mv2_grp_discuss_means: 'კლიენტის მოთხოვნა ბლოკავს',
    mv2_act_waiting_owner: 'ველოდებით მეპატრონის {n} პასუხს',
    mv2_act_then_concede: 'ჯერ {n} პასუხი, მერე — დათმობს {subject}-ს?',
    mv2_act_call_concede: 'დარეკე — დათმობს {subject}-ს?',
    mv2_act_ask_client: 'ჰკითხე {name}-ს {subject}-ზე',
    mv2_legend_met: 'ემთხვევა',
    mv2_legend_missed: 'მოსალაპარაკებელია',
    mv2_legend_client_cond: 'კლიენტის მოთხოვნაა',
    mv2_legend_owner_unasked: 'მეპატრონისთვის არ გვიკითხავს',
    mv2_yes: 'კი',
    mv2_no: 'არა',
    mv2_people: 'ადამიანი',
    mv2_unlocks_clients: 'ხსნის {n} კლიენტს',
    desk_feature_answer_prompt: 'ობიექტს აქვს?',
    desk_feature_has: 'აქვს',
    desk_feature_absent: 'არ აქვს',
    desk_term_answer_prompt: 'მესაკუთრის პირობა?',
    desk_term_occupants_prompt: 'მესაკუთრის მაქს. მაცხოვრებელი?',
    desk_term_occupants_unit: 'კაცი',
    desk_term_allowed: 'დაიშვება',
    desk_term_not_allowed: 'არ დაიშვება',
    desk_term_accepts_mortgage: 'იპოთეკას იღებს',
    desk_term_cash_only: 'მხოლოდ ქეში',
    desk_maturity_heading: 'სანდოობა',
    desk_maturity_confirmed: 'დადასტურებულია რამდენიმე წყაროთი',
    desk_maturity_conflict: 'წყაროები არ ემთხვევა — გადაამოწმეთ',
    desk_maturity_single: 'ერთი წყარო — დაუდასტურებელი',
    desk_maturity_sources: '{n} წყარო',
    desk_owner_note_label: 'მესაკუთრის შენიშვნა (ფორმიდან)',
    desk_ad_description_label: 'მესაკუთრის კომენტარი (განცხადებიდან)',
    desk_ad_description_from_origin: 'ორიგინალი განცხადება',
    desk_ad_description_more: 'სრულად',
    desk_ad_description_versions: 'ძველი ვერსიები ({n})',
    desk_ad_description_less: 'შეკეცვა',
    desk_criteria_floor_min: 'მინ. სართული',
    desk_criteria_floor_max: 'მაქს. სართული',
    desk_criteria_floor_not_first: 'არა პირველი სართული',
    desk_criteria_floor_not_last: 'არა ბოლო სართული',
    desk_facts_balcony_count: 'აივანი (რაოდენობა)',
    desk_facts_balcony_area: 'აივნის ფართობი (მ²)',
    desk_facts_area_each_or_total: 'თითო ან ჯამში, რაც ცნობილია',
    desk_property_state_label: 'მდგომარეობა — საცხოვრებლად მოუმზადებელი (კარკასი / მშენებარე)',
    desk_cond_occupancy: 'რემონტის შემდეგ', desk_cond_building: 'შენობა', desk_cond_frame: 'კარკასის ეტაპი', desk_cond_finish: 'მოპირკეთება', desk_cond_class: 'კლასი',
    desk_cond_class_na: 'კარკასსა და მშენებარეს არ ეხება',
    desk_property_class_label: 'ან ბინის კლასი (საცხოვრებელი — ემთხვევა კლიენტის მოთხოვნის დონეებს)',
    desk_criteria_feature_must_have: 'აუცილებელი',
    desk_criteria_feature_nice_have: 'სასურველი',
    desk_criteria_feature_must_not_have: 'მიუღებელი',
    desk_criteria_section_search_area: 'საძიებო არეალი (რუკაზე)',
    desk_client_edit_title: 'კლიენტის რედაქტირება',
    desk_loading: 'იტვირთება…',
    desk_couldnt_load: 'ვერ ჩაიტვირთა — {err}',
    desk_client_status_closed: 'დახურული',
    desk_client_status_active: 'აქტიური',
    desk_client_see_profile: 'სრული პროფილის ნახვა',
    desk_client_fallback_person: 'ეს ადამიანი',
    desk_client_not_saved_err: '⚠️ არ შეინახა — {err}',
    desk_client_reopen_tooltip: 'მოთხოვნის გახსნა',
    desk_client_close_tooltip: 'მოთხოვნის დახურვა — არ იშლება, გახსნა ნებისმიერ დროს შეგიძლიათ',
    desk_client_reopen_button: '↩ მოთხოვნის გახსნა',
    desk_client_close_button: '✕ მოთხოვნის დახურვა',
    desk_client_cancel: 'გაუქმება',
    desk_client_save_changes: '✓ ცვლილებების შენახვა',
    desk_client_no_changes: 'ცვლილებები არაა',
    desk_client_close_confirm: 'დავხუროთ მოთხოვნა? ჩანაწერი არ წაიშლება — აღდგენა ყოველთვის შეგიძლიათ.',
    desk_client_closed_toast: '✓ მოთხოვნა დაიხურა — გახსნა სიიდან შეგიძლიათ',
    desk_client_reopened_toast: '✓ მოთხოვნა გაიხსნა',
    desk_client_review_title: 'კლიენტის გადახედვა',
    desk_client_name_label: 'სახელი *',
    desk_client_name_ph: 'კლიენტის სახელი',
    desk_client_phone: 'ტელეფონი',
    desk_client_email: 'ელფოსტა',
    desk_phone_shape_len: '⚠ {cc}ნომერი {want} ციფრიანია — აქ {got}-ია. ჯობია შენახვამდე გადაამოწმოთ.',
    desk_phone_shape_lead: '⚠ ქართული ნომერი იწყება 5-ით (მობილური) ან 3-ით (ქალაქის). ჯობია შენახვამდე გადაამოწმოთ.',
    desk_contact_book: 'კონტაქტები ({n})',
    desk_contact_empty: 'კონტაქტების ისტორია ჯერ არ არის — მხოლოდ ზემოთ მოცემული პირვანდელი ჩანაწერი.',
    desk_contact_add_ph: 'დაამატეთ ტელეფონი ან ელფოსტა…',
    desk_contact_add: 'დამატება',
    desk_contact_make_primary: 'მთავარ კონტაქტად არჩევა',
    desk_contact_mark_unreachable: 'მიუწვდომელად მონიშვნა (ისტორიაში რჩება, არ იშლება)',
    desk_contact_primary: 'მთავარი — ამ ნომერზე დარეკეთ',
    desk_client_looking_to: 'მიზანი',
    desk_intent_opt_buy: 'ყიდვა',
    desk_intent_opt_rent: 'ქირაობა',
    desk_intent_opt_invest: 'ინვესტიცია',
    desk_intent_opt_lease: 'გაქირავება',
    desk_intent_opt_sale: 'გაყიდვა',
    desk_kind_penthouse: 'პენტჰაუსი',
    desk_kind_townhouse: 'თაუნჰაუსი',
    desk_kind_duplex: 'დუპლექსი',
    desk_kind_shop: 'მაღაზია',
    desk_kind_cafe: 'კაფე',
    desk_kind_warehouse: 'საწყობი',
    desk_kind_basement: 'სარდაფი',
    desk_kind_commercial_special: 'სპეც. კომერციული',
    desk_status_val_active: 'აქტიური',
    desk_status_val_paused: 'შეჩერებული',
    desk_status_val_withdrawn: 'მოხსნილი',
    desk_status_val_sold: 'გაყიდული',
    desk_status_val_rented: 'გაქირავებული',
    desk_status_val_expired: 'ვადაგასული',
    desk_delta_vs_post: 'ამ პოსტთან',
    desk_delta_vs_prev: 'წინა ფასიდან',
    desk_lineage_more_postings: '+{n} კიდევ პოსტი',
    desk_lineage_more_agents: '+{n} კიდევ აგენტი',
    desk_intent_opt_buy_or_rent: 'ყიდვა ან ქირაობა',
    desk_intent_opt_short_term_rent: 'დღიური ქირაობა',
    desk_client_property_type: 'ობიექტის ტიპი',
    desk_category_opt_apartment: 'ბინა',
    desk_category_opt_room: 'ოთახი (საერთო ბინაში)',
    desk_context_aparthotel: 'აპარტოტელი',
    desk_aparthotel_details: 'აპარტოტელის პირობები',
    desk_category_opt_house: 'სახლი',
    desk_category_opt_land: 'მიწა',
    desk_category_opt_commercial: 'კომერციული',
    desk_category_opt_cottage: 'კოტეჯი',
    desk_category_opt_office: 'ოფისი',
    desk_category_opt_garage: 'ავტოფარეხი',
    desk_category_opt_hotel: 'სასტუმრო',
    desk_category_opt_other: 'სხვა',
    desk_client_urgency: 'როდის სჭირდება ქონება?',
    desk_urgency_opt_immediate: 'სასწრაფოდ',
    desk_urgency_opt_soon: 'მალე',
    desk_urgency_opt_flexible: 'მოქნილად',
    desk_urgency_opt_browsing: 'ათვალიერებს',
    desk_client_accept_save: '✓ დადასტურება და შენახვა',
    desk_client_add_button: '✓ კლიენტის დამატება',
    desk_gap_heading: '🧠 კარგი იქნებოდა ვიცოდეთ:',
    desk_gap_client_phone: 'ტელ. ნომერი — იდენტიფიკაციისთვის და დასაკავშირებლად',
    desk_gap_client_budget: 'ბიუჯეტის ზედა ზღვარი — ამის გარეშე ძებნა რთულია',
    desk_gap_client_location: 'სასურველი მდებარეობა — უბნები ან მოხაზული არეალი',
    desk_gap_client_rooms: 'ოთახები / საძინებლები',
    desk_gap_client_size: 'დაახლოებითი ფართობი (მ²)',
    desk_gap_client_urgency: 'როდის სჭირდება ქონება?',
    desk_gap_client_dealbreakers: 'მიუღებელი პირობები (პარკინგის უქონლობა, ცხოველები...)',
    desk_gap_property_price: 'საწყისი ფასი — თუნდაც "X-ის ფარგლებში"',
    desk_gap_property_phone: 'საკონტაქტო ნომერი — ამის გარეშე ვერავინ დაუკავშირდება',
    desk_gap_property_rooms_area: 'ოთახები + ფართობი (მ²) — 2 მთავარი ფილტრი',
    desk_gap_property_area_fallback: 'ფართობი (მ²)',
    desk_gap_property_land_area: 'მიწის ფართობი (მ²)',
    desk_gap_property_rooms_fallback: 'ოთახები',
    desk_gap_property_district: 'უბანი',
    desk_gap_property_pin: 'მდებარეობის პინი რუკაზე — უკეთესია, ვიდრე მხოლოდ მისამართი',
    desk_gap_property_floor: 'სართული',
    desk_gap_property_cadastral: 'საკადასტრო კოდი — საუკეთესო იდენტიფიკატორი',
    desk_gap_property_condition: 'რემონტის მდგომარეობა',
    desk_completeness_client: 'კლიენტის პროფილი',
    desk_completeness_property: 'ობიექტის ჩანაწერი',
    desk_completeness_missing: 'აკლია',
    desk_completeness_full: 'ყველა ძირითადი ველი შევსებულია',
    desk_gap_fill_title: 'შეავსეთ რაც იცით — შეინახება კლიენტზე',
    desk_gap_fill_unit_usd: 'USD',
    desk_gap_fill_unit_sqm: 'მ²',
    desk_gap_fill_unit_rooms: 'ოთახი',
    desk_gap_fill_save_title: 'შენახვა',
    desk_gap_fill_more_hint: 'დეტალურად რედაქტირებისთვის გახსენით კლიენტი',
    desk_match_heading_properties: 'შესაბამისი ობიექტები — ბინები ჩვენს ბაზაში, რომლებიც ამ კლიენტს შეიძლება მოეწონოს',
    desk_match_heading_clients: 'შესაბამისი კლიენტები — ჩვენი კლიენტები, ვისაც ეს ობიექტი შეიძლება მოეწონოს',
    desk_match_eligible_of: 'შემოწმდა {considered}, შესაძლოა მოერგოს {eligible}',
    desk_match_lookup_failed: 'ძიება ვერ შესრულდა. სცადეთ ხელახლა.',
    desk_match_scoring: '⏳ ბაზასთან შედარება…',
    desk_match_too_broad_title: '🔎 {count} განცხადება ერგება — ძალიან ფართოა სასარგებლო დალაგებისთვის',
    desk_match_too_broad_sub: 'დააზუსტეთ კლიენტთან (რომელიმე ერთი):',
    desk_match_ask_rooms_or_area: 'რამდენი ოთახი, ან რა ფართობი?',
    desk_match_ask_district: 'რომელი უბნები?',
    desk_match_ask_budget: 'მაქსიმალური ბიუჯეტი?',
    desk_match_make_binding: 'კლიენტმა თქვა {names} — გავხადოთ სავალდებულო',
    desk_match_make_binding_hint: 'უბნები სურვილად არის ჩაწერილი — დაადასტურეთ სავალდებულოდ, რომ ძებნა დავიწროვდეს',
    desk_match_show_anyway: 'შედეგების მაინც ჩვენება',
    desk_match_no_fit: 'შესაფერისი ვერ მოიძებნა?',
    desk_match_search_leads: 'ლიდებში ძებნა',
    desk_match_search_leads_n: 'მოძებნე {n} ლიდში',
    desk_match_none_eligible: 'შესაბამისი დამთხვევები ჯერ არაა.',
    desk_match_fallback_client: 'კლიენტი',
    desk_match_agent_suffix: '· აგენტი',
    desk_match_owner_gap_lead: 'ერთი ზარი მესაკუთრესთან ხსნის ამ კითხვებს:',
    desk_match_owner_gap_headline: 'ერთი ზარი მესაკუთრესთან — პასუხები ხსნის {n} შესაბამისობას',
    desk_match_verified_short: 'დადასტ. {pct}%',
    desk_match_blocked_by_shared: 'დაბლოკილია {n} საერთო კითხვით ↑',
    desk_match_seg_pass: 'გავიდა',
    desk_match_seg_partial: 'ნაწილობრივ',
    desk_match_seg_unknown: 'უცნობი',
    desk_match_seg_fail: 'ჩავარდა',
    desk_match_tier_ready: 'მზადაა შეთავაზებისთვის',
    desk_match_tier_confirm: 'დაზუსტება საჭიროა',
    desk_match_tier_concession: 'კლიენტის დათმობა საჭიროა',
    desk_match_tier_ready_action: 'შეთავაზება',
    desk_match_offer_whatsapp: 'WhatsApp-ით შეთავაზება (მოინიშნება შეთავაზებულად)',
    desk_match_offer_call: 'კლიენტთან დარეკვა (მოინიშნება შეთავაზებულად)',
    desk_match_tier_confirm_action: 'კლიენტთან დაზუსტება',
    desk_match_tier_concession_action: 'კლიენტთან განხილვა',
    desk_match_contact_agent: 'დაუკავშირდი აგენტ {name}-ს',
    desk_match_more_actions: 'სხვა მოქმედებები',
    desk_match_bd_toggle_hint: 'დააჭირეთ სრული, პუნქტობრივი შედარებისთვის',
    desk_match_bd_pass: '✓ ემთხვევა',
    desk_match_bd_partial: '≈ თითქმის',
    desk_match_bd_fail: '✗ არ ემთხვევა',
    desk_match_bd_ask_owner: '? ჰკითხეთ მესაკუთრეს',
    desk_match_bd_ask_client: '? კლიენტს არ უთქვამს',
    desk_match_client_gaps: 'კლიენტს არ უთქვამს: {list}',
    desk_match_reduced_because: 'ქულა დაკლდა',
    desk_match_reduced_conflict: 'წყაროები არ ემთხვევა',
    desk_match_reduced_single: 'ერთი წყარო',
    desk_needs_verify_heading: 'დაადასტურე მესაკუთართან დარეკვისას · {n}',
    desk_needs_verify_record_hint: 'დააჭირეთ მესაკუთრის პასუხის ჩასაწერად',
    desk_match_registered_client_title: 'კლიენტის მოთხოვნა დარეგისტრირდა',
    desk_match_registered_listing_title: 'განცხადება დარეგისტრირდა',
    desk_match_last_activity_title: 'ბოლო აქტივობა',
    desk_match_new_badge: 'ახალი',
    desk_match_confirm_active: '✓ კიდევ ეძებს?',
    desk_match_confirm_active_hint: 'ერთი დაჭერით ფიქსირდება „კლიენტმა დაადასტურა, რომ კიდევ ეძებს" და სიძველის საათი ნულდება — გამოიყენეთ მასთან საუბრის შემდეგ',
    desk_match_proposed_ago: 'შეთავაზებულია {ago}',
    desk_match_no_viewing_yet: 'ნახვა ჯერ არაა',
    desk_match_viewing_after: 'ნახვა შედგა ✓',
    desk_client_view_title: 'კლიენტის მოთხოვნა',
    desk_client_edit_button: '✎ რედაქტირება',
    desk_client_criteria_section: 'კრიტერიუმები — რას ეძებს',
    desk_crit_bedrooms: 'საძინებელი',
    desk_crit_land: 'მიწის ფართობი',
    desk_crit_max_drive: 'მაქს. მგზავრობა (წთ)',
    desk_crit_districts_must: 'უბნები (აუცილებელი)',
    desk_crit_districts_excluded: 'უბნები (გამორიცხული)',
    desk_crit_must_have: 'აუცილებელი',
    desk_crit_nice_to_have: 'სასურველი',
    desk_crit_must_not: 'დაუშვებელი',
    desk_crit_household: 'ოჯახის შემადგენლობა',
    desk_crit_occupants: 'მაცხოვრებლები',
    desk_crit_children_ages: 'ბავშვების ასაკი',
    desk_crit_move_in: 'შესახლება',
    desk_crit_check_out: 'გამოსვლა',
    desk_crit_mortgage_ready: 'იპოთეკისთვის მზად',
    desk_crit_parking_deeded: 'პარკინგი საკუთრებაში (ცალკე ს/კ-ით)',
    desk_crit_has_pets: 'ჰყავს ცხოველი',
    desk_crit_has_children: 'ჰყავს ბავშვები',
    desk_crit_max_deposit_months: 'მაქსიმალური დეპოზიტი (რამდენი თვის ქირა)',
    desk_crit_prepayment_months: 'შეუძლია წინასწარ გადაიხადოს (თვე)',
    desk_crit_search_area: 'საძიებო არეალი',
    desk_crit_search_area_drawn: '✓ მონიშნულია რუკაზე',
    desk_crit_power: 'საჭირო სიმძლავრე (კვტ)',
    desk_crit_none: 'კრიტერიუმები ჯერ არაა ჩაწერილი — დაამატეთ რედაქტირებით',
    desk_crit_people: 'ვინ იცხოვრებს',
    // B300 — the declared couple/flatmates answer (extra.household_composition).
    desk_crit_together: 'ერთად არიან',
    desk_crit_together_couple: 'წყვილი',
    desk_crit_together_group: 'თანამცხოვრებლები / მეგობრები',
    desk_crit_people_masked: 'დამალულია — სხვა აგენტის კლიენტი',
    desk_crit_person: 'პიროვნება',
    desk_crit_child: 'ბავშვი',
    desk_crit_working: 'მუშაობს',
    desk_crit_not_working: 'არ მუშაობს',
    desk_crit_pet: 'ცხოველი',
    desk_crit_pet_cat: 'კატა',
    desk_crit_pet_dog: 'ძაღლი',
    desk_crit_pet_other: 'ცხოველი',
    desk_crit_pet_size_small: 'პატარა',
    desk_crit_pet_size_medium: 'საშუალო',
    desk_crit_pet_size_large: 'დიდი',
    desk_crit_condition_exclude: 'მდგ. (გამორიცხული)',
    desk_crit_condition_clash: '⚠ ეწინააღმდეგება მოთხოვნას',
    desk_crit_condition_clash_hint: 'ერთი და იგივე მდგომარეობა მითითებულია როგორც სასურველად, ისე გამორიცხულად. შერჩევა გამორიცხულს კატეგორიულად ჭრის, ანუ სწორედ ის ბინები იშლება, რასაც კლიენტი ითხოვს — დააზუსტეთ და გაასწორეთ რედაქტირებით.',
    desk_crit_complexes_excluded: 'კომპლექსები (გამორიცხული)',
    desk_crit_complexes: 'უნდა იყოს კომპლექსში',
    desk_crit_near_points: 'უნდა იყოს ახლოს',
    desk_crit_near_walk: 'ფეხით',
    desk_crit_near_bus: 'ავტობუსით',
    desk_crit_floor: 'სართული',
    desk_crit_floor_not_first: 'არა პირველი სართული',
    desk_crit_floor_not_last: 'არა ბოლო სართული',
    desk_crit_land_purpose: 'მიწის დანიშნულება',
    desk_crit_land_purpose_agricultural: 'სასოფლო-სამეურნეო',
    desk_crit_land_purpose_non_agricultural: 'არასასოფლო-სამეურნეო',
    desk_crit_with_structure_ok: 'არსებული ნაგებობა მისაღებია',
    desk_crit_min_lease: 'მინ. ვადა (თვე)',
    desk_crit_lease_window: 'ქირავდება თვეებში (1–12)',
    desk_crit_registered_lease: 'სჭირდება რეგისტრირებული ხელშეკრულება',
    desk_crit_payment_currency: 'გადახდის ვალუტა',
    desk_match_class_ready: 'მზადაა',
    desk_match_class_negotiate: 'მოლაპარაკება',
    desk_match_class_verify: 'გადასამოწმებელი',
    desk_match_class_discuss: 'ჰკითხე კლიენტს',
    desk_match_class_ready_hint: 'ყველაფერი ემთხვევა — შესთავაზეთ კლიენტს',
    desk_match_class_negotiate_hint: 'ერგება, გარდა ცნობილი სხვაობისა — ღირს მესაკუთრესთან მოლაპარაკება',
    desk_match_class_verify_hint: 'მთავარი ფაქტი უცნობია — ჯერ გადაამოწმეთ ამ რიგის ? პუნქტები',
    desk_match_class_discuss_hint: 'ცნობილ ფაქტებს ერგება — ჰკითხეთ კლიენტს მონიშნულ სხვაობაზე',
    desk_match_represented_by: 'წარმოადგენს {name}',
    desk_match_your_listing: '✎ თქვენი განცხადება',
    desk_match_also_on: 'ასევე: {list}',
    desk_match_coop_pct: 'თანამშრომლობა: {pct}%',
    desk_match_coop_min: 'მინ. {v} {cur}',
    desk_match_coop_exclusive: 'ექსკლუზიური',
    desk_match_my_coop: 'თქვენ თანამშრომლობთ მესაკუთრესთან',
    desk_match_my_coop_owner: 'მესაკუთრე',
    desk_match_my_coop_hint: 'თქვენ ხართ ამ ობიექტზე არსებული თანამშრომლობის აქტიური მონაწილე — მესაკუთრეს პირდაპირ დაურეკავთ.',
    desk_match_my_coop_no_terms: 'პირობები ჯერ არ არის ჩაწერილი',
    desk_match_market_listing: '🏷 მესაკუთრის საკუთარი განცხადება — შუამავალი აგენტის გარეშე',
    desk_match_owner_ad_also_listed: 'მესაკუთრის განცხადება — ასევე აქვს განთავსებული ჩვენს აგენტს: {name}',
    desk_lineage_owner_section: '🏠 მესაკუთრის განცხადება — {name}',
    desk_lineage_owner_section_anon: '🏠 მესაკუთრის განცხადება',
    desk_lineage_agency_section: '🏢 {agency} — {name}',
    // Image-set switcher (2026-08-17) — see the en block for why the caveat is unconditional.
    desk_gallery_zoom_in: 'გადიდება',
    desk_gallery_zoom_out: 'დაპატარავება',
    desk_imgset_more_sets: 'მეტი ნაკრები',
    desk_imgset_section: 'სურათების ნაკრებები',
    desk_imgset_total: '{sets} ნაკრები · {photos} სურათი',
    desk_imgset_total_sets_only: '{sets} ნაკრები',
    desk_imgset_tab_title: '{n} სურათი · {mark} · {platform}',
    desk_imgset_mark_clean: 'სუფთა',
    desk_imgset_mark_logo: 'ლოგო',
    desk_imgset_mark_unknown: 'ნიშანი უცნობია',
    desk_imgset_rendition_clean: 'სუფთა',
    desk_imgset_rendition_logo: 'წყალნიშნიანი',
    desk_imgset_rendition_thumb: 'მინიატურა',
    desk_imgset_rendition_upload: 'ატვირთული',
    desk_imgset_rendition_picked: 'არჩეული',
    desk_imgset_label_upload: 'ჩვენ მიერ ატვირთული',
    desk_imgset_label_whatsapp_owner: 'WA',   // owner 2026-10-23: "just call it WA"
    desk_imgset_label_my_version: 'ჩემი ვერსია',
    desk_imgset_label_colleague_version: 'კოლეგის ვერსია',
    desk_imgset_only_one: 'ამ ბინაზე მხოლოდ ეს ნაკრები ინახება. გადასართავი არაფერია.',
    desk_entry_reposted_no_date: 'რეპოსტი',
    desk_shop_terms_none: 'პირობები ჯერ არაა ჩაწერილი — ჰკითხეთ წარმომადგენელ აგენტს',
    desk_terms_hidden_count: 'ჩაწერილია {n} — დაამატე შენი, რომ ნახო',
    desk_terms_hidden_some: 'ჩაწერილია — დაამატეთ თქვენი, რომ ნახოთ',
    desk_terms_hidden_count_norep: 'ჩაწერილია {n} — ჰკითხეთ წარმომადგენელს',
    desk_terms_hidden_some_norep: 'ჩაწერილია — ჰკითხეთ წარმომადგენელს',
    desk_terms_recorded_by_agent: 'აგენტი',
    desk_shop_deal_section: 'გარიგება — წარმომადგენელი აგენტის პირობები',
    desk_shop_deal_none: 'თანამშრომლობის პირობები ჯერ არაა ჩაწერილი — დაურეკეთ წარმომადგენელს გასაყოფის შესათანხმებლად',
    desk_shop_market_section: 'ბაზარზე ყოფნა',
    desk_shop_still_available: 'ბაზარზეა',
    desk_shop_off_market: 'ბაზრიდან მოხსნილია',
    desk_shop_last_confirmed: 'ბოლოს დადასტურდა {time}',
    desk_shop_cooperate_button: 'თანამშრომლობა',
    desk_lineage_rep_banner: 'ამ ობიექტს წარმოადგენს {name} — გარიგებისთვის ითანამშრომლეთ მასთან',
    desk_lineage_captured_by_hint: 'ჩვენი რომელმა აგენტმა შეინახა ეს განცხადება (ძველ ჩანაწერებს ეს ნიშანი არ აქვთ)',
    match_key_policy_wrap: 'მესაკუთრის წესი — {subject} — უცნობია',
    desk_match_coverage: '{pct}% დადასტურებული',
    desk_match_coverage_title: 'რამდენი შესადარებელი ფაქტია რეალურად ცნობილი — მაღალი ქულა დაბალ დაფარვაზე ვარაუდია და არა შესატყვისი',
    match_key_intent: 'გარიგების ტიპი',
    match_key_category: 'ქონების ტიპი',
    match_key_price: 'ფასი',
    match_key_location: 'მდებარეობა',
    match_key_complex: 'კომპლექსი',
    match_key_floor: 'სართული',
    match_key_condition: 'მდგომარეობა',
    match_key_pets: 'ცხოველები',
    match_key_children: 'ბავშვები',
    match_key_occupants: 'მაცხოვრებლები',
    match_key_deposit: 'დეპოზიტი',
    match_key_prepayment: 'წინასწარი გადახდა',
    match_key_pay_currency: 'გადახდის ვალუტა',
    match_key_financing: 'დაფინანსება',
    match_key_area: 'ფართობი',
    match_key_land_area: 'მიწის ფართობი',
    match_key_near: 'მანძილი',
    match_key_parking_deeded: 'გაფორმებული პარკინგი',
    match_key_power_capacity: 'დენის სიმძლავრე',
    match_key_rooms_off_by: 'ოთახების სხვაობა',
    match_key_bedrooms_off_by: 'საძინებლების სხვაობა',
    match_key_area_outside_range: 'ფართობის სხვაობა',
    match_key_land_area_outside_range: 'მიწის ფართობის სხვაობა',
    match_key_power_capacity_outside_range: 'სიმძლავრის სხვაობა',
    match_key_near_over_radius: 'რადიუსს აღემატება',
    match_key_category_verify: 'ქონების ტიპი დასაზუსტებელია — გადაამოწმეთ.',
    match_key_complex_mismatch: 'სხვა კომპლექსია',
    match_key_complex_unverified: 'კომპლექსი დასაზუსტებელია',
    match_key_policy_conflict: 'წესი სადავოა — გადაამოწმეთ',
    match_key_policy_inherited: 'წესი ნავარაუდევია — გადაამოწმეთ',
    match_key_rule_inherited: 'წესი ნავარაუდევია — გადაამოწმეთ',
    match_key_rule_exception: 'გამონაკლისში ჯდება — გადაამოწმეთ',
    match_key_land_area_col: 'მიწა',
    match_key_parking_deeded_col: 'გაფ.პარკ',
    match_key_power_capacity_col: 'სიმძლავრე',
    match_key_appraisal: 'შეფასება',
    match_key_occupancy: 'დაკავებულობა',
    match_key_handover: 'გადაცემის თარიღი',
    match_key_move_in: 'გადასვლის თარიღი',
    match_key_move_in_gap: 'გადასვლის სხვაობა',
    match_move_in_owner_holds: 'სთხოვეთ მესაკუთრეს, დაიცადოს {n} დღე',
    match_move_in_tenant_waits: 'სთხოვეთ კლიენტს, დაიცადოს {n} დღე',
    match_key_availability_gap: 'ჰკითხეთ მესაკუთრეს — როდის თავისუფლდება?',
    match_key_availability_conflict: 'გადასვლის თარიღი სადავოა — გადაამოწმეთ',
    match_key_ask_owner_to_hold: 'სთხოვეთ მესაკუთრეს, დაიცადოს',
    match_key_ask_tenant_to_wait: 'სთხოვეთ კლიენტს, დაიცადოს',
    match_key_rooms: 'ოთახები',
    match_key_bedrooms: 'საძინებლები',
    match_key_area_sqm: 'ფართობი',
    match_key_price_above_budget: 'ბიუჯეტს აღემატება',
    match_key_price_below_min: 'მინიმუმზე ნაკლები',
    match_key_outside_search_area: 'საძიებო არეალის გარეთ',
    match_key_district_mismatch: 'უბანი არ ემთხვევა',
    match_key_district_adjacent: 'ესაზღვრება სასურველ უბანს — ჰკითხეთ კლიენტს',
    match_key_verify_location: 'იარლიყი და რუკის წერტილი არ ემთხვევა — გადაამოწმეთ მდებარეობა',
    match_key_attribute_gap: 'მონაცემის სხვაობა',
    match_key_policy_gap: 'ჰკითხეთ მესაკუთრეს — პირობა უცნობია',
    match_key_household_gap: 'შემადგენლობის შეზღუდვა',
    match_key_terms_gap: 'ჰკითხეთ მესაკუთრეს — პირობები უცნობია',
    match_key_deposit_above_max: 'დეპოზიტი მაქსიმუმზე მეტია',
    match_key_prepayment_above_max: 'წინასწარ მეტ თვეს ითხოვს, ვიდრე კლიენტს შეუძლია',
    match_key_currency_mismatch: 'ვალუტა განსხვავდება (მოლაპარაკებადი)',
    match_key_appraisal_below_price: 'შეფასება ფასზე დაბალია',
    match_key_sitting_tenant: 'მოქმედი დამქირავებელი',
    match_key_handover_after_move_in: 'გადაცემა შესახლების შემდეგ',
    desk_terms_facet_title_short: 'გაქირავების პირობები',
    desk_sale_terms_title_short: 'გაყიდვის პირობები',
    desk_girao_terms_title_short: 'გირავნობის პირობები',
    desk_terms_facet_title: 'გაქირავების პირობები — მესაკუთრის მოთხოვნები (მხოლოდ აგენტებისთვის)',
    desk_terms_no_restrictions: 'ვკითხე მესაკუთრეს — შეზღუდვები არ არის',
    desk_terms_cleared: 'შეზღუდვების გარეშე',
    desk_terms_cleared_title: 'აგენტმა ჰკითხა მესაკუთრეს: შეზღუდვები არ არის (გარდა აქ ჩაწერილისა)',
    desk_terms_cleared_hidden: 'პასუხი გაცემულია',
    desk_terms_cleared_hidden_title: 'ვიღაცამ უკვე ჩაწერა მესაკუთრის პასუხი — სანახავად ჩაწერე შენი',
    desk_terms_facet_hint: 'დაფიქსირებულია ზუსტად მესაკუთრის სიტყვებით — შიდა მოხმარებისთვის, არ გამოიყენება ავტო-ფილტრაციისთვის.',
    desk_terms_pets: 'ცხოველები',
    desk_terms_smoking: 'მოწევა',
    desk_terms_children: 'ბავშვები',
    desk_terms_furnished: 'ავეჯი',
    desk_terms_registered_lease: 'საჯარო რეესტრი (NAPR)',
    desk_terms_deposit_months: 'დეპოზიტი (თვე)',
    desk_terms_deposit_amount: 'დეპოზიტი (ფიქსირებული თანხა, ქირაობის ბოლომდე)',
    // წინასწარი გადახდა (core 0213) — გალერეის პანელის ორი მონიშვნის სარკე
    desk_terms_prepay_first: 'პირველი თვის ქირა წინასწარ',
    desk_terms_prepay_first_short: 'პირველი თვე წინასწარ',
    desk_terms_prepay_last_short: 'ბოლო თვე წინასწარ',
    desk_movein_chip_prompt: 'წინასწარ გადახდა:',
    desk_terms_prepay_first_yes: 'ითხოვს',
    desk_terms_prepay_first_no: 'არ ითხოვს',
    desk_terms_prepay_last: 'ბოლო თვის ქირა წინასწარ',
    desk_terms_prepay_last_yes: 'ითხოვს',
    desk_terms_prepay_last_no: 'არ ითხოვს',
    desk_terms_is_final_condition: 'ბინა საბოლოო მდგომარეობაშია — შესასრულებელი არაფერია',
    desk_terms_min_lease: 'მინ. ვადა (თვე)',
    desk_terms_min_lease_under6: '6-ზე ნაკლები — მოკლევადიანი',
    desk_terms_max_occupants: 'მობინადრეების მაქს. რაოდენობა',
    desk_terms_extra_pet_deposit: 'ცხოველის დეპოზიტი',
    // 0416 — the pol_pet_deposit chip's successor: does a pet cost extra HERE.
    desk_terms_pet_deposit_required: 'ცხოველი დამატებით ღირს?',
    desk_terms_pet_deposit_required_short: 'ცხოველი დამატებით ღირს',
    desk_terms_pet_deposit_required_no: 'ცხოველი დამატებით არ ღირს',
    desk_terms_pet_fee_unknown: 'ფასიანია — თანხა მითითებული არ არის',
    desk_terms_none_recorded: 'არ არის მითითებული',
    desk_terms_version_badge: 'v{n}',
    desk_tristate_not_stated: 'არ არის მითითებული',
    desk_tristate_yes: 'კი',
    desk_tristate_no: 'არა',
    desk_terms_promised_improvements: 'გადასვლამდე შესასრულებელი პირობები — დააფიქსირეთ დაპირებები',
    desk_terms_tenant_prefs: 'მესაკუთრის პრეფერენციები — (მაგ. ეროვნება, სტუდენტები) მხოლოდ აგენტებისთვის',
    desk_terms_record_button: '✓ პირობების დაფიქსირება',
    desk_terms_recorded: '✓ დაფიქსირებულია',
    desk_terms_record_property_first: 'ჯერ დააფიქსირეთ ობიექტი',
    desk_timeline_property_registered: '🏠 ობიექტი დაემატა',
    desk_timeline_details_updated: '✏️ დეტალები განახლდა',
    desk_timeline_context_signal: '📎 სიგნალი{kind}',
    desk_timeline_observed: '🔎 დაფიქსირდა {attribute}{source}',
    desk_timeline_feature: '✳️ მახასიათებელი: {key}{" (არ არის)" if false}',
    desk_timeline_listing_observed: '📰 განცხადება დაფიქსირდა{src}{price}',
    desk_timeline_listing_kind: '🔁 განცხადება {kind}: {value}',
    desk_timeline_listing_changed: 'Δ შეიცვალა: {field} {from} → {to}',
    desk_timeline_change_pending: ' · ჯერ არ დამკვიდრებულა',
    desk_optout_facet_title: 'ამ ობიექტზე დანიშნული კონტაქტი',
    desk_pools_title: 'ავტომატური გაზიარება: რა გავაზიაროთ',
    desk_pools_unavailable: 'ამ ანგარიშისთვის გაზიარების არჩევანი მიუწვდომელია.',
    desk_pools_which: 'პულები',
    desk_pool_outside: 'სხვა აგენტების ბინები (მესაკუთრის განცხადება)',
    desk_pool_coop_ours: 'ჩვენი აგენტების თანამშრომლობები',
    desk_pool_coop_mine: 'ჩემი თანამშრომლობები',
    desk_pool_confirmed: 'მესაკუთრემ დაადასტურა, რომ ჯერ კიდევ აქტუალურია',
    desk_pool_office: 'ოფისის კოლეგების განცხადებები (7 დღე)',
    desk_pool_front_page: 'მთავარი გვერდის საუკეთესო შეთავაზება',
    desk_pool_coop: 'თანამშრომლობა ჩვენს აგენტთან',
    desk_reshare_badge: 'პული',
    desk_reshare_reposts: '{n}× · {a} აგ.',
    desk_reshare_queued: 'რიგშია',
    desk_push_chase: 'სხვა სააგენტომ ჩვენ შემდეგ განათავსა',
    desk_push_front_page: 'მთავარი გვერდი',
    desk_reshare_hint_pools: 'გაზიარების პულში: {list}',
    desk_reshare_hint_reposts: 'ჩვენი რეპოსტები 30 დღეში: {n}, {a} ანგარიშიდან, ბოლო {at}',
    desk_reshare_hint_queued: 'ახლა რიგშია გასაზიარებლად: {list}',
    desk_pools_count_hint: 'დარჩა / ფილტრებს ემთხვევა / სულ პულში',
    desk_pools_portals: 'სად გავაზიაროთ',
    desk_pools_kinds: 'ქონების ტიპი (არაფერი მონიშნული = ყველა)',
    desk_pools_intents: 'გარიგება',
    desk_pool_intent_sale: 'იყიდება',
    desk_pool_intent_rent: 'ქირავდება',
    desk_pools_need_intent: 'აირჩიე იყიდება, ქირავდება ან ორივე',
    desk_pool_kind_apartment: 'ბინა',
    desk_pool_kind_house: 'სახლი',
    desk_pool_kind_commercial: 'კომერციული',
    desk_pool_kind_land: 'მიწა',
    desk_pools_rent: 'ქირის ფასი',
    desk_pools_sale: 'გაყიდვის ფასი',
    desk_pools_confirmed: 'აქტუალურობა დადასტურებულია ბოლო',
    desk_pools_days: 'დღეში',
    desk_pools_confirmed_hint: 'დადასტურება = ვინმემ გახსნა თანამშრომლობა, ან მესაკუთრემ WhatsApp-ზე გვითხრა, რომ ჯერ კიდევ ქირავდება / იყიდება. ცარიელი = შეზღუდვის გარეშე.',
    desk_pools_save: 'შენახვა',
    desk_pools_saved: 'შენახულია',
    desk_pools_need_portal: 'აირჩიე ერთი პორტალი მაინც',
    desk_adrm_title: 'ჩვენი გაზიარებები ამ ბინაზე',
    desk_adrm_request: 'წაშლის მოთხოვნა',
    desk_adrm_requested: 'წაშლა მოთხოვნილია',
    desk_adrm_none: 'ჩვენს აგენტებს ამ ბინის აქტიური განცხადება არ აქვთ.',
    desk_adrm_remove_sel: 'მონიშნულის წაშლა',
    desk_adrm_remove_all: 'ყველას წაშლა',
    desk_adrm_confirm: 'წავშალოთ {n} განცხადება პორტალებიდან? აგენტების კომპიუტერები წაშლიან საკუთარი ანგარიშიდან.',
    desk_adrm_why_title: 'რატომ იშლება?',
    desk_adrm_why_sold_rented: 'გაქირავდა ან გაიყიდა',
    desk_adrm_why_agent_stopped: 'აღარ მინდა ამაზე მუშაობა',
    desk_adrm_why_owner_asked: 'მესაკუთრემ მთხოვა წაშლა',
    desk_adrm_why_owner_exclusive_other: 'მესაკუთრემ მთხოვა: სხვა აგენტთან აქვს ექსკლუზივი',
    desk_adrm_why_owner_no_reshare: 'მესაკუთრემ მთხოვა: გადაზიარებას არ უშვებს',
    desk_adrm_why_other: 'სხვა',
    desk_adrm_why_note: 'შენიშვნა (არასავალდებულო)',
    desk_adrm_why_note_required: 'დაწერეთ მიზეზი',
    desk_adrm_why_cancel: 'გაუქმება',
    desk_adrm_why_remove: 'წაშლა',
    desk_adrm_state_scheduled: 'ელოდება აგენტის კომპიუტერს',
    desk_adrm_state_queued: 'იშლება…',
    desk_adrm_state_published: 'წაშლილია',
    desk_adrm_state_error: 'ვერ წაიშალა',
    desk_adrm_note: 'თითოეულ განცხადებას შლის მისი გამომქვეყნებელი აგენტის კომპიუტერი, საკუთარი ანგარიშიდან.',
    desk_share_fb_contact_none: 'ნომერი არ აქვს',
    desk_share_fb_contact_assigned: 'მინიჭებული',
    desk_share_fb_contact_empty: 'ამ ობიექტზე გამოსაქვეყნებელი ნომერი არ არის.',
    desk_optout_manage_title: 'სხვები ამ ობიექტზე',
    desk_admin_comments_title: 'ადმინის შენიშვნები',
    desk_mark_section_title: 'გზა და ადგილები რუკაზე',
    desk_mark_draw_road: 'გზის დახაზვა',
    desk_mark_drop_place: 'ადგილის მონიშვნა',
    desk_mark_hint_pan: 'აირჩიეთ ხელსაწყო, მერე დააწკაპუნეთ რუკაზე.',
    desk_mark_hint_road: 'დააწკაპუნეთ გზის გასწვრივ — მინიმუმ ორი წერტილი.',
    desk_mark_points_progress: '{n} წერტილი გზაზე — გააგრძელეთ ან შეინახეთ',
    desk_mark_hint_place: 'დააწკაპუნეთ, სად არის ადგილი.',
    desk_mark_hint_place_set: 'წერტილი დასმულია — დაარქვით და შეინახეთ.',
    desk_mark_label_ph: 'სახელი (არასავალდებულო): მთავარი გზა, ჭიშკარი, ჭა…',
    desk_mark_save: 'ნიშნის შენახვა',
    desk_mark_road: 'გზა',
    desk_mark_place: 'ადგილი',
    desk_mark_retract: 'წაშლა',
    desk_mark_road_distance: 'მანძილი მონიშნულ გზამდე',
    desk_mark_no_pin: '(ნაკვეთს ჯერ არ აქვს პინი, ამიტომ მანძილი არ არის)',
    desk_facts_fill_gaps: 'შევავსო ჩემი ცარიელი ველები ამ ბარათიდან',
    desk_facts_fill_gaps_hint: 'ამ განცხადების მნიშვნელობებს ჩასვამს იმ ველებში, რომლებიც თავად არ შეგივსიათ. თქვენი მნიშვნელობები რჩება.',
    desk_facts_filled_n: '{n} ველი შეივსო ამ ბარათიდან — ჩაწერისას თქვენი გახდება.',
    desk_facts_yours: 'თქვენი',
    desk_admin_comments_hint: 'მხოლოდ ადმინები ხედავენ. Claude კითხულობს, როცა ამ ობიექტზე მუშაობს; იხურება იმის ჩანაწერით, რაც გაკეთდა.',
    desk_admin_comment_ph: 'რა არის გასაკეთებელი…',
    desk_admin_comment_add: 'შენიშვნის დამატება',
    desk_admin_comment_mark_resolved: 'მოგვარებულად მონიშვნა',
    desk_admin_comment_resolve: 'მოგვარება',
    desk_admin_comment_resolution_ph: 'რა გაკეთდა, ან რატომ არაფერი',
    desk_admin_comments_resolved_n: '{n} მოგვარებული',
    desk_lineage_view_my_version_tooltip: 'ჩემი ვერსიის გახსნა',
    desk_my_version_not_published: 'არ არის გამოქვეყნებული',
    desk_my_version_overlay_note: 'განცხადება თქვენი ჩაწერილი ფაქტებით. რაც არ ჩაგიწერიათ, რჩება განცხადების მნიშვნელობა.',
    desk_optout_action_leave_other: 'კონტაქტებიდან ამოღება',
    desk_optout_action_rejoin_other: 'დაბრუნება',
    desk_optout_state_in: 'თქვენ შეიძლება მითითებული იყოთ ამ ობიექტის კონტაქტად.',
    desk_optout_state_out: 'თქვენ ამ ობიექტის კონტაქტად არ ხართ მითითებული.',
    desk_optout_action_leave: 'ნუ მიმითითებთ კონტაქტად',
    desk_optout_action_rejoin: 'კვლავ მიმითითეთ კონტაქტად',
    desk_optout_scope_note: 'ეს ეხება მხოლოდ თქვენი ნომრის გამოქვეყნებას. წვდომა, შეხვედრები და სააგენტო უცვლელია.',
    desk_optout_others: 'აქ კიდევ {n} კოლეგაა გამოსული.',
    desk_timeline_change_pending_title: 'ჩაწერილია. ისტორიაში აისახება, როცა 15 წუთი უცვლელი დარჩება.',
    desk_timeline_reshare_declared: '🔗 რეპოსტი გამოცხადდა{src}',
    desk_timeline_reshare_registered: '🔗 რეპოსტი დარეგისტრირდა{src}',
    desk_timeline_terms_amended: '📋 ქირის პირობები შეიცვალა: {ops}',
    desk_timeline_terms_ungoverned: '⚠️ პირობები შეიცვალა გეითვეის გარეთ',
    desk_timeline_agency_agreement: '🤝 შეთანხმება {verb} — {status}',
    desk_timeline_contact_declared: '👤 კონტაქტი დაემატა{role}{name}',
    desk_timeline_contact_dismissed: '👤 კონტაქტი წაიშალა',
    // ⚠️ Georgian written by the implementer, NOT a native speaker — worth a
    // review pass. Left in place rather than omitted: a missing ka key renders
    // English inside a Georgian UI, which is worse than an awkward translation.
    desk_history_title: 'ისტორია',
    desk_history_status_active: 'აქტიური',
    desk_history_status_off: 'მოხსნილია',
    desk_history_days: '{n} დღე',
    desk_history_owner_posts: 'მფლობელმა {n}-ჯერ განათავსა',
    desk_history_owner_post_one: 'მფლობელმა 1-ჯერ განათავსა',
    desk_history_agents: '{n} აგენტი მუშაობს',
    desk_history_agent_one: '1 აგენტი მუშაობს',
    desk_history_prices: 'ფასები {min}–{max}',
    desk_history_price_one: 'ფასი {min}',
    desk_history_owner_price: 'მფლობელის ფასი {price}',
    desk_history_agreements: '{n} შეთანხმება',
    desk_history_agreement_one: '1 შეთანხმება',
    desk_history_floor: 'ზღვარი {price}',
    desk_history_floor_hidden: 'ციფრები თქვენთვის დახურულია',
    desk_history_last_seen: 'ბოლოს ნახვა {when}',
    desk_history_platforms: '{n} პლატფორმა',
    desk_history_unknown_posters: '{n} გარე განმთავსებელი',
    desk_history_empty: 'ჯერ არაფერია დაფიქსირებული.',
    desk_history_drawer: '{n} მანქანური ჩანაწერი',
    desk_history_older: 'ადრინდელი ჩანაწერების ჩატვირთვა',
    desk_history_loading: 'იტვირთება…',
    desk_history_partial: 'შესაძლოა გაგრძელდეს ძველ ჩანაწერებში',
    desk_ep_owner_post: 'მფლობელმა განათავსა ახალი განცხადება',
    desk_ep_reshare: '{who}-მ გადააქვეყნა {n} პლატფორმაზე',
    desk_ep_reshare_one: '{who}-მ გადააქვეყნა — {portal}',
    desk_ep_withdrawn: 'განცხადება მოიხსნა',
    desk_ep_price_up: 'ფასი გაიზარდა {from} → {to}',
    desk_ep_price_down: 'ფასი დაეცა {from} → {to}',
    desk_ep_correction: 'სისტემამ შეასწორა ვალუტის შეცდომა',
    desk_ep_merge: 'სისტემამ დააკავშირა სხვა ჩანაწერი ამ ობიექტთან',
    desk_ep_agreement: 'სააგენტო შეთანხმება — {what}',
    desk_ep_bookkeeping: 'მხოლოდ ჩანაწერი — პირობები არ შეცვლილა',
    desk_ep_observed: 'დაფიქსირდა',
    desk_ep_sold: 'მონიშნულია როგორც გაყიდული',
    desk_ep_owner: 'მფლობელი',
    desk_ep_external: 'გარე განმთავსებელი',
    desk_ep_via: '{who}-ის მეშვეობით',
    desk_ep_inferred_off: 'დასკვნა: ხელახლა შემოწმებისას განცხადება ვეღარ მოიძებნა. ვიცით რომ აღარაა, ზუსტი დრო — არა.',
    desk_timeline_facet_title: 'ისტორია — ყველაფერი რაც ამ ობიექტზე დაფიქსირდა',
    desk_timeline_facet_hint: 'მოვლენების ჟურნალი, სადაც არაფერი იშლება და ფაქტები თანმიმდევრულადაა.',
    desk_timeline_events_count: '{n}{+} მოვლენა',
    desk_timeline_no_events: 'მოვლენები ჯერ არაა.',
    desk_timeline_burst_summary: '{evTime} — 🔎 {n} ფაქტი დაფიქსირდა{sourceKind}',
    desk_timeline_loading: '⏳ იტვირთება…',
    desk_timeline_load_older: '↓ ძველების ჩატვირთვა',
    desk_notes_facet_title: '🔒 პირადი შენიშვნები — ხედავთ მხოლოდ თქვენ',
    desk_notes_facet_hint: 'შენიშვნები არ საჯაროვდება და არ ჩანს საერთო ისტორიაში.',
    desk_notes_count: '{n} შენიშვნა',
    desk_notes_delete: 'შენიშვნის წაშლა',
    desk_notes_add_ph: 'პირადი შენიშვნის დამატება…',
    desk_notes_add_button: 'დამატება',
    desk_promises_facet_title: 'დაპირებები — ჯერ არ არის',
    desk_promises_facet_hint: 'რას ჰპირდება გამყიდველი ან დეველოპერი (აივანი მოპირკეთდება, დაგეგმილია აუზი) და შედის თუ არა ეს ახლანდელ ფასში. არსებულ მახასიათებლად არ ჩაიწერება.',
    desk_promises_summary: '{n} · {incl} ფასში · {extra} დამატებით',
    desk_promise_scope_flat: 'ბინა',
    desk_promise_scope_building: 'კორპუსი',
    desk_promise_scope_complex: 'კომპლექსი',
    desk_promise_scope_area: 'უბანი',
    desk_promise_state_promised: 'დაპირებული',
    desk_promise_state_in_progress: 'მიმდინარე',
    desk_promise_state_done: 'შესრულებული',
    desk_promise_state_not_kept: 'არ შესრულდა',
    desk_promise_price_included: 'ფასში შედის',
    desk_promise_price_extra: 'ფასში არ შედის',
    desk_promise_price_unstated: 'ფასზე არაფერია ნათქვამი',
    desk_promise_extra_amount: 'თანხა',
    desk_promise_what_ph: 'რა არის დაპირებული…',
    desk_promise_due_ph: 'როდის (მაგ. დეკემბერში)',
    desk_promise_add: 'დაპირების დამატება',
    desk_promise_remove: 'დაპირების წაშლა',
    desk_assumed_facet_title: 'სავარაუდო მნიშვნელობები — გადაამოწმეთ',
    desk_assumed_facet_hint: 'რიცხვები ან ვალუტები, რომლებიც განცხადებაში შეცდომით ეწერა და ყველაზე სავარაუდო მნიშვნელობით წავიკითხეთ. დაადასტურეთ, ან უარყავით და დაბრუნდება წყაროს მნიშვნელობა.',
    desk_assumed_summary: '{n} გადასამოწმებელი',
    desk_assumed_field_price: 'ფასი',
    desk_assumed_field_deposit: 'დეპოზიტი',
    desk_assumed_most_likely: 'სავარაუდოდ',
    desk_assumed_source_said: '· წყაროში ეწერა',
    desk_assumed_confirm: 'დადასტურება',
    desk_assumed_reject: 'უარყოფა — წყაროს მნიშვნელობა',
    desk_assumed_status_confirmed: 'დადასტურებული',
    desk_assumed_status_rejected: 'უარყოფილი — წყაროს მნიშვნელობა აღდგა',
    desk_pool_facet_title: 'ბირჟა — შესთავაზეთ ობიექტი სხვა აგენტებს',
    desk_pool_facet_hint: 'მიუთითეთ ფასი და საკომისიო (იყოფა 50/50-ზე). საჯაროვდება მხოლოდ ფაქტები; დოკუმენტები და შენიშვნები რჩება პირადად.',
    desk_pool_published_private: '✅ ბირჟაზეა / პირადია',
    desk_pool_asking_price: 'საწყისი ფასი',
    desk_pool_for_label: 'ტიპი',
    desk_pool_for_sale: 'იყიდება',
    desk_pool_for_rent: 'ქირავდება',
    desk_pool_total_commission: 'ჯამური საკომისიო %',
    desk_pool_coop_side: 'თანამშრომლობის წილი (50/50): ~{amount} {currency}',
    desk_pool_update_offer: '↻ შეთავაზების განახლება',
    desk_pool_publish: '📢 ბირჟაზე დამატება',
    desk_pool_withdraw: '⏸ ბირჟიდან მოხსნა',
    desk_pool_disclaimer: 'საჯაროვდება მხოლოდ თქვენს მიერ დაფიქსირებული ფაქტები; დოკუმენტები და შენიშვნები პირადია.',
    desk_pool_validation: 'შეიყვანეთ ფასი და საკომისიო % (0–100)',
    desk_contact_reveal: '📞 კონტაქტის ნახვა',
    desk_contact_no_phone: 'ტელეფონი არ არის დაფიქსირებული',
    desk_access_request_link: '⚠️ წარმომადგენელი არ პასუხობს? მოითხოვეთ წვდომა',
    desk_access_context_ph: 'რომელი კლიენტი, ვის მიმართეთ, როდის…',
    desk_access_file: 'მოთხოვნა',
    desk_access_pending: 'მოთხოვნა გაგზავნილია — ველოდებით წარმომადგენლის პასუხს',
    desk_access_granted: 'თანხმობა მიღებულია',
    desk_access_rejected: 'მოთხოვნა უარყოფილია',
    desk_access_revoked: 'წვდომა გაუქმებულია',
    desk_access_ask_again: 'ხელახლა მოთხოვნა',
    desk_access_notice: '{name}-ს ჰყავს კლიენტი თქვენს ობიექტზე და ვერ დაგიკავშირდათ — გთხოვთ უპასუხოთ',
    desk_access_notice_req: '{name} ითხოვს წვდომას თქვენს კლიენტთან ({client}) — მხოლოდ ერთჯერადად',
    desk_access_grant_btn: 'თანხმობა',
    desk_access_deny_btn: 'უარყოფა',
    desk_notif_access_ask_client: '{name}-ს სჭირდება თქვენი კლიენტის კონტაქტი შეხვედრის შესათანხმებლად',
    desk_notif_access_ask_owner: '{name}-ს სჭირდება მესაკუთრის კონტაქტი შეხვედრის შესათანხმებლად',
    desk_notif_access_meeting_at: 'შეხვედრა {when}',
    desk_notif_access_meeting_untimed: 'შეხვედრა — დრო ჯერ შერჩეული არ არის',
    desk_notif_access_granted: 'თქვენი მოთხოვნა დაკმაყოფილდა',
    desk_notif_access_revoked: 'თქვენი წვდომა გაუქმდა',
    desk_notif_access_denied: 'თქვენს მოთხოვნაზე უარი გითხრეს',
    desk_notif_access_req_generic: 'კლიენტი',
    desk_notif_access_prop_generic: 'ობიექტი',
    desk_access_grant_meeting_btn: 'მხოლოდ ამ შეხვედრისთვის თანხმობა',
    desk_access_grant_ongoing_btn: 'მუდმივი წვდომის მიცემა',
    meeting_new_title: 'ახალი შეხვედრა',
    meeting_slot_property: 'ობიექტი',
    meeting_slot_client: 'კლიენტი',
    meeting_client_via_rep: 'არ არის გაზიარებული — წარმომადგენელი {name}',
    meeting_slot_property_rep: 'ობიექტის წარმომადგენელი',
    meeting_slot_client_rep: 'კლიენტის წარმომადგენელი',
    meeting_slot_empty: 'ცარიელია',
    meeting_slot_property_empty: 'გახსენით ობიექტიდან მისამაგრებლად',
    meeting_slot_property_unresolved: 'ობიექტი უცნობია — ძებნა: "{q}"',
    meeting_pick_rep: 'აირჩიეთ წარმომადგენელი',
    meeting_attending_yes: 'ესწრება',
    meeting_attending_no: 'არ ესწრება',
    meeting_rep_none_registered: 'ამ ობიექტს წარმომადგენელი არ ჰყავს',
    meeting_client_rep_handover_hint: 'ან კლიენტი კოლეგას გადააბარეთ:',
    meeting_client_rep_handover_ask: 'კლიენტი {client} გადაეცეს {name}-ს?',
    meeting_client_rep_handover_ask_noname: 'კლიენტი გადაეცეს {name}-ს?',
    meeting_client_rep_handover_cost: '{name} აღარ იქნება ამ კლიენტის წარმომადგენელი და დაკარგავს შესაბამის წვდომას. უკან დაბრუნება შესაძლებელია.',
    meeting_client_rep_handover_do: 'გადაბარება',
    meeting_client_rep_outside_open: 'კლიენტს სხვა აგენტი ჰყავს →',
    meeting_client_rep_outside_back: '← ჩვენი აგენტები',
    meeting_client_rep_outside_hint: 'სხვა სააგენტოს აგენტი. ესწრება შეხვედრას, მაგრამ ჩვენს საკომისიოში არ მონაწილეობს.',
    meeting_notes_unresolved: 'ობიექტი ჯერ უცნობია. აგენტმა მოძებნა: {q}',
    meeting_ai_prefilled: 'შევსებულია ჩასმული შეტყობინებიდან — შექმნამდე შეამოწმეთ',
    meeting_upcoming_same: 'ამ კლიენტს აქ უკვე აქვს ჩვენება: {when}',
    meeting_listing_unavailable: 'ეს ობიექტი აღარ არის ბაზარზე ({status}) — შექმენით მხოლოდ თუ ნამდვილად გსურთ',
    meeting_slot_client_empty: 'გახსენით კლიენტიდან მისამაგრებლად',
    meeting_slot_rep_unknown: 'წარმომადგენელი არ ჰყავს',
    meeting_slot_rep_auto: 'აირჩევა ავტომატურად შეხვედრის შექმნისას',
    // owner 2026-09-23 — ყველა მხარე ადამიანის არჩეულია; შეთავაზება, არა წინასწარი არჩევანი.
    meeting_rep_mark_suggested: 'შემოთავაზებული',
    meeting_rep_mark_last: 'წინა ჯერზე',
    meeting_rep_mark_me: 'მე',
    meeting_rep_pick_prompt: 'აირჩიეთ წარმომადგენელი',
    meeting_rep_none_pickable: 'აქ ვერავის აირჩევთ — მიმართეთ მენეჯერს',
    meeting_rep_pick_other: 'სხვა აგენტი ან სააგენტო…',
    meeting_pick_client_prompt: 'აირჩიეთ კლიენტი ან დაამატეთ ახალი',
    meeting_missing_purpose: 'აირჩიეთ მიზანი',
    meeting_missing_property: 'აირჩიეთ ობიექტი',
    meeting_missing_client: 'აირჩიეთ კლიენტი',
    meeting_missing_property_rep: 'აირჩიეთ მესაკუთრის წარმომადგენელი',
    meeting_missing_client_rep: 'აირჩიეთ კლიენტის წარმომადგენელი',
    meeting_missing_coop: 'შეავსეთ თანამშრომლობა მესაკუთრესთან',
    meeting_err_client_required: 'ნახვას სჭირდება კლიენტი — აირჩიეთ ან დაამატეთ.',
    meeting_err_property_required: 'ნახვას სჭირდება ობიექტი — აირჩიეთ.',
    meeting_err_representatives_required: 'შექმნამდე აირჩიეთ ორივე წარმომადგენელი.',
    meeting_coop_step_title: 'თანამშრომლობა მესაკუთრის მხარესთან',
    meeting_coop_step_hint: 'თქვენ წარმოადგენთ კლიენტს, ობიექტს კი — {rep}. ნახვამდე შეავსეთ პირობები მესაკუთრესთან.',
    meeting_coop_step_hint_norep: 'თქვენ წარმოადგენთ კლიენტს და ამ ობიექტზე თანამშრომლობა ჯერ არ არის. ნახვამდე შეავსეთ პირობები მესაკუთრესთან.',
    meeting_coop_step_open: 'პირობების შევსება',
    meeting_coop_step_no_owner: 'მესაკუთრის კონტაქტი თქვენთვის ხელმისაწვდომი არ არის. თანამშრომლობის გასახსნელად დაამატეთ მესაკუთრის ტელეფონი.',
    meeting_coop_step_add_owner: 'მესაკუთრის დამატება',
    meeting_rep_inherited_hint: 'წინა ვიზიტიდან',
    meeting_slot_rep_several: 'რამდენიმე წარმომადგენელია — ჩაიწერება დაუზუსტებლად',
    // ── co-broke (0449) ──────────────────────────────────────────────────────
    meeting_rep_pick_ours: 'ჩვენი სააგენტოდან',
    meeting_rep_pick_external: 'სხვა სააგენტო',
    meeting_rep_external_search_ph: 'სახელი, ნომერი ან სააგენტო…',
    meeting_rep_external_known: 'ადრე გვითანამშრომლია',
    meeting_rep_external_directory: 'ნაპოვნია პორტალებზე',
    meeting_rep_external_add: 'ხელით დამატება',
    meeting_rep_external_none: 'ვერაფერი მოიძებნა — დაამატეთ ხელით ქვემოთ.',
    meeting_rep_external_solo: 'დამოუკიდებელი აგენტი',
    meeting_edit_window_closed: 'შესწორებისთვის განკუთვნილი 15 წუთი გავიდა. ცვლილებისთვის მიმართეთ მენეჯერს.',
    meeting_split_from_property_rep: 'ობიექტის აგენტი სხვა სააგენტოდანაა — მისი წილი ჯერ ჩამოიჭრება საკომისიოდან.',
    meeting_split_from_client_rep: 'კლიენტის აგენტი სხვა სააგენტოდანაა — მისი წილი ჯერ ჩამოიჭრება საკომისიოდან.',
    meeting_roster_unavailable: 'აგენტების სია ვერ ჩაიტვირთა.',
    meeting_contacts_pill: 'კონტაქტები',
    meeting_prop_quick_add: 'ახალი ობიექტის დამატება',
    meeting_prop_quick_hint: 'მხოლოდ მთავარი ველები — სრულ ბარათს მოგვიანებით შეავსებთ.',
    meeting_prop_quick_address_ph: 'მისამართი',
    meeting_prop_quick_price_ph: 'ფასი',
    meeting_prop_quick_area_ph: 'ფართობი (მ²)',
    meeting_prop_quick_rooms_ph: 'ოთახები',
    meeting_prop_quick_contact_hint: 'საკონტაქტო პირი — მესაკუთრე ან წარმომადგენელი (არასავალდებულო)',
    meeting_prop_quick_save: 'ობიექტის დამატება',
    meeting_prop_quick_failed: 'ქონების მონაცემების შენახვა ვერ მოხერხდა. სცადეთ ხელახლა.',
    meeting_rep_external_name_ph: 'სახელი',
    meeting_rep_external_phone_ph: 'ტელეფონი',
    meeting_rep_external_agency_ph: 'სააგენტო (არასავალდებულო)',
    meeting_rep_external_save: 'დამატება',
    meeting_rep_external_is_ours: 'ეს ნომერი ჩვენი აგენტისაა — აირჩიეთ „ჩვენი სააგენტოდან“.',
    meeting_rep_external_badge: 'სხვა სააგენტო',
    meeting_rep_declared_hint: 'თქვენ მიუთითეთ — სააგენტოს გარეთ არავის დაუდასტურებია.',
    meeting_property_rep_other_agent: 'სხვა აგენტი',
    meeting_property_rep_outside_open: 'სხვა სააგენტოს აგენტი →',
    meeting_property_rep_outside_hint: 'გარე წარმომადგენელი ჯერ იღებს შეთანხმებულ წილს, შემდეგ ჩვენი ოფისი ანაწილებს საკუთარ წილს.',
    meeting_property_rep_declare_hint: 'ეს აგენტები ჯერ არ მუშაობენ ამ ობიექტზე — არჩევა ჩაიწერება, რომ თქვენ მიუთითეთ.',
    meeting_rep_declared_badge: 'მითითებული',
    meeting_rep_external_ads: '{n} განცხადება',
    meeting_split_label: 'საკომისიოს გაყოფა',
    meeting_split_none: 'არ იყოფა — მთელი საკომისიო ჩვენია',
    meeting_split_standard_hint: 'სტანდარტული გაყოფა სხვა სააგენტოსთან — {n}% / {n}%. ჯერ არ არის ჩაწერილი.',
    meeting_split_record_standard: 'გაყოფის ჩაწერა',
    meeting_split_add: 'გაყოფა სხვა სააგენტოსთან',
    meeting_split_clear: 'გაყოფის მოხსნა',
    meeting_split_us: 'ჩვენ',
    meeting_split_them: '{name}',
    meeting_split_of_fee: '{fee}-დან',
    meeting_split_no_fee: 'საკომისიო ჯერ არ არის ჩაწერილი — პროპორცია შეინახება და თანხები გამოჩნდება მას შემდეგ.',
    meeting_split_total_bad: 'ორივე წილის ჯამი უნდა იყოს 100%.',
    meeting_split_needs_party: 'ჯერ აირჩიეთ აგენტი, ვისთანაც იყოფა.',
    meeting_split_default_note: 'სააგენტოს სტანდარტია {n}% — შეცვალეთ, თუ სხვაგვარად შეთანხმდით.',
    meeting_split_many: 'ამ გაყოფაში {n} მხარეა და მას ის არედაქტირებს, ვინც ჩაწერა.',
    meeting_pick_client: 'აირჩიეთ კლიენტი…',
    meeting_pick_from_matches: 'შესაბამისი კლიენტები',
    meeting_pick_my_contacts: 'ჩემი დამატებული კონტაქტები',
    meeting_pick_my_clients: 'ჩემი კლიენტები',
    meeting_pick_other_clients: 'სხვა აგენტების კლიენტები',
    meeting_pick_search_ph: 'ძებნა სახელით ან ნომრით…',
    meeting_pick_badge_match: 'ემთხვევა',
    meeting_pick_badge_onprop: 'ამ ობიექტზე',
    meeting_pick_none: 'ამ ობიექტზე კანდიდატი ვერ მოიძებნა',
    meeting_add_new_client: 'ახალი კლიენტის დამატება',
    meeting_quick_add_hint: 'დეტალების შევსება მოგვიანებით შეიძლება კლიენტის ბარათზე',
    meeting_new_client_picked: 'ახალი კლიენტი',
    meeting_comments: 'კომენტარები',
    desk_meetings_none_at_all: 'შეხვედრები ჯერ არ არის',
    desk_meetings_load_failed: 'შეხვედრები ვერ ჩაიტვირთა.',
    desk_meetings_all_filtered: 'ამ ხედში {n} შეხვედრაა დამალული — ყველას ჩვენება',
    desk_load_failed: 'ვერ ჩაიტვირთა — სცადეთ ხელახლა',
    meeting_binder_settle_confirm: 'დაიხუროს ბე როგორც „{label}“? ეს საბოლოოა და ვეღარ დაბრუნდება',
    meeting_split_clear_confirm: 'მოიხსნას შეთანხმებული გაყოფა? სწორედ ეს თანაფარდობა განსაზღვრავს ჩვენს წილს',
    meeting_history: 'ისტორია',
    meeting_history_none: 'ჯერ არაფერია დაფიქსირებული',
    desk_hist_created: 'შეხვედრა შეიქმნა',
    desk_hist_status: 'სტატუსი',
    desk_hist_moved: 'გადაიდო',
    desk_hist_time_fixed: 'დრო შესწორდა',
    desk_hist_property_set: 'ობიექტი მიება',
    desk_hist_property_changed: 'ობიექტი შეიცვალა',
    desk_hist_client_set: 'კლიენტი მიება',
    desk_hist_client_changed: 'კლიენტი შეიცვალა',
    desk_hist_rep_changed: 'წარმომადგენელი შეიცვალა',
    desk_hist_handover: 'კლიენტი გადაეცა',
    desk_hist_attendance: 'დასწრება განახლდა',
    desk_hist_split: 'გაყოფა ჩაიწერა',
    desk_hist_binder: 'ბეს მონაცემები ჩაიწერა',
    // ── ბეს ორი მდგომარეობა (1100/1105) ────────────────────────────────────────────────
    desk_binder_agreement_state: 'შეთანხმება',
    desk_binder_money_state: 'თანხა',
    desk_binder_move_to: 'გადაყვანა',
    desk_binder_refund_reason: 'რატომ ბრუნდება თანხა?',
    desk_binder_why: 'რატომ?',
    desk_binder_confirm: 'დადასტურება',
    desk_binder_cancel: 'უკან',
    desk_binder_moving: 'ინახება…',
    desk_binder_move_failed: 'ეს ცვლილება არ დაიშვა.',
    desk_binder_counts_failure: 'ჩაითვლება ჩაშლილ გარიგებად.',
    desk_binder_not_failure: 'არ ჩაითვლება ჩაშლილ გარიგებად — გარიგება შედგა.',
    binder_astate_draft: 'მუშავდება',
    binder_astate_signed: 'ხელმოწერილია',
    binder_astate_funded: 'თანხა შევიდა',
    binder_astate_active: 'ჯავშანი აქტიურია',
    binder_astate_completed: 'გარიგება შედგა',
    binder_astate_payer_default: 'ბეს გადამცემის ბრალით',
    binder_astate_owner_default: 'მესაკუთრის ბრალით',
    binder_astate_neutral_failure: 'ჩაიშალა ბრალის გარეშე',
    binder_astate_mutually_cancelled: 'გაუქმდა შეთანხმებით',
    binder_mstate_none: 'თანხა არ მიღებულა',
    binder_mstate_received: 'მიღებულია',
    binder_mstate_allocated: 'გადანაწილებულია (დროებით)',
    binder_mstate_credited: 'ჩაითვალა გარიგებაში',
    binder_mstate_refunded: 'დაბრუნდა',
    binder_mstate_retained: 'დარჩა მიმღებთან',
    binder_refund_owner_default: 'მესაკუთრის ბრალით',
    binder_refund_neutral_failure: 'ჩაიშალა ბრალის გარეშე',
    binder_refund_mutual_cancellation: 'მხარეთა შეთანხმებით',
    binder_refund_agency_error: 'სააგენტოს შეცდომა',
    binder_refund_currency_settlement: 'სავალუტო ანგარიშსწორება — გარიგება შედგა',
    binder_refund_overpayment: 'მიღებულია მეტი, ვიდრე ეკუთვნოდა',
    desk_hist_deleted: 'შეხვედრა წაიშალა',
    desk_hist_restored: 'შეხვედრა აღდგა',
    desk_hist_imported: 'გადმოტანილია ძველი ჩანაწერებიდან — ავტორი და დრო უცნობია',
    desk_hist_more: 'კიდევ {n}-ის ჩვენება',
    meeting_hist_tab_all: 'ყველა',
    meeting_hist_tab_changes: 'ცვლილებები',
    meeting_hist_tab_comments: 'კომენტარები',
    meeting_hist_tab_visits: 'ვიზიტები',
    meeting_revisit: 'ხელახლა ჩანიშვნა',
    meeting_prop_history: 'ამ ობიექტზე {n} წინა შეხვედრაა',
    meeting_repeat_from: 'გამეორება',
    meeting_past_visits: 'წინა ვიზიტები',
    meeting_past_visits_none: 'ამ კლიენტზე და ობიექტზე ადრინდელი ვიზიტი არ არის',
    meeting_book_next: 'შემდეგი ვიზიტის ჩანიშვნა',
    meeting_comments_none: 'ჯერ კომენტარები არ არის',
    meeting_comment_ph: 'კომენტარი…',
    meeting_notes_ph: 'შენიშვნები (არასავალდებულო)…',
    meeting_will_request_access: 'შეხვედრის შექმნისას მეორე წარმომადგენელს გაეგზავნება კონტაქტის მოთხოვნა.',
    meeting_create_btn: 'შეხვედრის შექმნა',
    meeting_creating: 'იქმნება…',
    meeting_cancel_btn: 'გაუქმება',
    meeting_created_ok: 'შეხვედრა შეიქმნა.',
    meeting_access_filed_owner: 'ობიექტის წარმომადგენელს გაეგზავნა მესაკუთრის კონტაქტის მოთხოვნა',
    meeting_access_filed_client: 'კლიენტის წარმომადგენელს გაეგზავნა კონტაქტის მოთხოვნა',
    meeting_access_filed_hint: 'შეხვედრა უკვე შენახულია და დადასტურებას არ ელოდება.',
    meeting_done_btn: 'დასრულება',
    desk_meeting_contact_pending: 'კონტაქტი ელოდება დასტურს',
    meeting_detail_title: 'შეხვედრა',
    meeting_detail_attendees: 'დამსწრეები',
    meeting_attendees_creator_only: 'ჯერჯერობით მხოლოდ თქვენ',
    meeting_attendee_not_going: 'ამ შეხვედრას არ ესწრება',
    meeting_attendee_not_going_note: 'არ მიდის: {names}',
    meeting_detail_status: 'სტატუსი',
    meeting_actions_label: 'რისი ჩაწერა გინდათ?',
    meeting_add_time: 'თარიღისა და დროის მითითება',
    meeting_book_next_hint: 'ახალი შეხვედრა',
    meeting_status_current: 'მიმდინარე',
    meeting_actions_menu: 'ჩანაწერის მოქმედებები',
    meeting_delete_btn: 'შეხვედრის წაშლა',
    meeting_delete_confirm: 'სრულად წავშალოთ ყველა სიიდან?',
    meeting_delete_yes: 'წაშლა',

    // ── მოლაპარაკება (core/MEETINGS_MODEL.md §6, §11) ────────────────────────
    nego_title: 'მოლაპარაკება',
    nego_panel_title: 'მოლაპარაკებები',
    nego_tooltip: 'ფასის ისტორია ამ კლიენტთან',
    nego_property_fallback: 'ობიექტი',
    nego_client_fallback: 'კლიენტი',
    nego_client_of: '{name}-ის კლიენტი',
    nego_latest_position: 'ბოლო პოზიცია',
    nego_difference: 'სხვაობა',
    nego_apart: 'სხვაობა {amount}',
    nego_history: 'ისტორია',
    nego_no_events: 'ჯერ არაფერია დაფიქსირებული',
    nego_converging: 'სხვაობა დროში:',
    nego_accepted_at: 'შეთანხმდნენ {amount}-ზე',
    nego_seed: 'საწყისი ფასი {amount}',
    nego_floor: 'მესაკუთრის მინიმუმი {amount}',
    nego_commission: 'საკომისიო {pct}%',
    nego_below_floor: 'ეს მესაკუთრესთან შეთანხმებულ მინიმუმზე დაბალია. გადაწყვეტილება თქვენია — მაგრამ ან საკომისიოს შეჭამს, ან შეთანხმებას არღვევს.',
    nego_mixed_currency: 'მხარეებმა სხვადასხვა ვალუტა დაასახელეს — სხვაობის ჩვენება შეუძლებელია.',
    nego_mixed_currency_short: 'სხვადასხვა ვალუტა',
    nego_money_row: 'თანხა',
    nego_record_btn: 'დაფიქსირება',
    nego_saving: 'ინახება…',
    nego_amount_ph: 'თანხა',
    nego_why_ph: 'რატომ? („დათვალიერების შემდეგ — სამზარეულო რემონტს საჭიროებს“)',
    nego_err_amount: 'შეთავაზებას თანხა სჭირდება.',
    nego_via_portal: 'კლიენტისგან',
    nego_from_meeting: 'შეხვედრიდან #{id}',
    nego_terminal_hint: 'ეს დახურავს მოლაპარაკებას. თუ დაბრუნდებიან, ახალი დაიწყება.',
    nego_closed_banner: 'დახურულია — {reason}. თუ დაბრუნდებიან, ახალი მოლაპარაკება გახსენით.',
    nego_side_client: 'კლიენტი',
    nego_side_owner: 'მესაკუთრე',
    nego_side_agent: 'აგენტი',
    nego_accepts_client: 'კლიენტი დათანხმდა',
    nego_accepts_owner: 'მესაკუთრე დათანხმდა',
    nego_accept_locks: 'გარიგება ფიქსირდება {amount}-ზე.',
    nego_accept_no_number: 'მეორე მხარის ფასი ჯერ არ არის — ჯერ მათი შეთავაზება დააფიქსირეთ, თორემ შეთანხმებას თანხა არ ექნება.',
    nego_kind_proposal: 'შეთავაზება',
    nego_kind_counterproposal: 'საპასუხო შეთავაზება',
    nego_kind_acceptance: 'შეთანხმდნენ',
    nego_kind_rejection: 'უარი',
    nego_kind_withdrawal: 'გამოითხოვა',
    nego_kind_deposit: 'ბე გადახდილია',
    nego_kind_contract: 'ხელშეკრულება გაფორმდა',
    nego_kind_closing: 'დასრულდა',
    nego_kind_fell_through: 'ჩაიშალა',
    nego_kind_note: 'შენიშვნა',
    nego_stage_open: 'ახლად გახსნილი',
    nego_stage_negotiating: 'მიმდინარეობს',
    nego_stage_accepted: 'შეთანხმდნენ',
    nego_stage_deposit: 'ბე გადახდილია',
    nego_stage_contract: 'ხელშეკრულება გაფორმდა',
    nego_stage_closed: 'დასრულდა',
    nego_stage_rejected: 'უარი',
    nego_stage_withdrawn: 'შეთავაზება გამოითხოვა',
    nego_stage_fell_through: 'ჩაიშალა',
    nego_basis_share: 'რაც ამ კლიენტს გაუგზავნეთ',
    nego_basis_reshare: 'თქვენივე განცხადება',
    nego_basis_cooperation: 'მესაკუთრესთან შეთანხმებული',
    nego_basis_portal: 'თავდაპირველი განცხადება',
    nego_basis_none: 'უცნობია',
    desk_notif_share_meeting: '{name} მოითხოვა თქვენ მიერ გაზიარებული ობიექტის ნახვა',
    desk_notif_share_opened: '{name} გახსნა თქვენ მიერ გაზიარებული ობიექტი',
    desk_notif_share_call: '{name} დააჭირა „დარეკვას“ გაზიარებულ ობიექტზე',
    desk_notif_share_whatsapp: '{name} დააჭირა WhatsApp-ს გაზიარებულ ობიექტზე',
    desk_notif_share_someone: 'კლიენტმა',
    desk_notif_share_someone_nom: 'კლიენტი',
    desk_notif_share_engaged: '{name} ჩაერთო',
    desk_notif_share_engaged_gallery: '{name} ჩაერთო — ფოტოებიც ნახა',
    share_title: 'კლიენტისთვის გაგზავნა',
    share_tooltip: 'გაუგზავნეთ ეს ობიექტი კლიენტს',
    share_menu_tooltip: 'გაზიარება',
    share_kind_client: 'ბმული კლიენტს',
    share_kind_others: 'კოლეგისთვის გაზიარება',
    desk_property_share_note: 'ისიც იმუშავებს ამ ობიექტზე — დაინახავს კონტაქტს, ჩაწერს მესაკუთრის პირობებს და ჩაერთვება იმ შეხვედრების გაყოფაში, სადაც დაესწრება.',
    desk_property_shared_n: 'გაზიარდა {n}-თან.',
    share_kind_agent: 'ბმული აგენტს',
    share_kind_location: 'მდებარეობის გაზიარება',
    desk_share_location_copied: 'რუკის ბმული დაკოპირდა',
    share_kind_repost: 'გადაზიარება პლატფორმებზე',
    share_price_label: 'ფასი, რომელსაც კლიენტი ნახავს',
    share_price_ph: 'ცარიელი — „ფასი მოთხოვნით“',
    share_price_frozen: 'ეს ფასი ფიქსირდება ბმულზე. მოგვიანებით ფასის ცვლილება კლიენტის ხედვას არ შეცვლის.',
    share_max_occupants_label: 'მობინადრეების მაქს. რაოდენობა, რომელსაც კლიენტი ნახავს',
    share_max_occupants_empty: 'ცარიელი — არ არის მითითებული',
    share_max_occupants_hint: 'აღებულია განცხადებიდან. აქ შეცვლა მხოლოდ ამ ბმულს შეეხება, განცხადებას არა.',
    share_message_ph: 'შენიშვნა კლიენტისთვის (არასავალდებულო)…',
    share_message_label: 'ამას კლიენტი ხედავს',
    share_memo_label: 'ამას მხოლოდ თქვენ ხედავთ — ვის გაუგზავნეთ?',
    share_memo_ph: 'მაგ.: ნინოს ქმარი, დარეკა ss-ის განცხადებაზე…',
    share_memo_hint: 'თქვენი პირადი ჩანაწერი. კლიენტს არასოდეს უჩვენდება.',
    share_create_btn: 'ბმულის შექმნა',
    share_creating: 'იქმნება…',
    share_ready: 'ბმული მზადაა — გაუგზავნეთ კლიენტს.',
    share_copy: 'კოპირება',
    share_copied: 'დაკოპირდა',
    share_another: 'კიდევ ერთი ბმული',
    share_district_only: 'ეს ბმული აჩვენებს რაიონს და არა ზუსტ მისამართს — თქვენ არ ხართ ამ ობიექტის წარმომადგენელი.',
    share_recipient: '{name}-სთვის',
    share_for_label: 'ვისთვის არის? (არასავალდებულო)',
    share_for_name_ph: 'სახელი',
    share_for_phone_ph: 'ტელეფონი',
    share_for_change: 'შეცვლა',
    share_for_reqs: '{n} მოთხოვნა',
    share_for_or_new: 'ან დატოვეთ ჩაწერილი მონაცემები ახალი კლიენტის დასამატებლად.',
    share_sent_before: 'უკვე გაგზავნილი',
    desk_share_chip_title: 'კლიენტებზე გაგზავნილი ბმულები',
    share_open: 'გახსნა',
    desk_share_chip_manage: 'ბმულების მართვა',
    desk_share_chip_add: 'კლიენტის ბმულის შექმნა',
    desk_share_lookup_none: 'ასეთი ბმული ვერ მოიძებნა — შეამოწმეთ ბმული, ან ის სხვა სააგენტოს ეკუთვნის.',
    desk_share_lookup_failed: 'ბმულის მოძებნა ვერ მოხერხდა — სცადეთ ხელახლა.',
    desk_share_lookup_inactive: 'ეს ბმული აღარ არის აქტიური ({state}) — ობიექტი მაინც გაიხსნა.',
    share_expired: 'ვადაგასულია',
    share_representation_ended: 'წარმომადგენლობა დასრულდა',
    share_price_on_request: 'ფასი მოთხოვნით',
    share_revoke: 'გაუქმება',
    share_revoked: 'გაუქმებულია',
    share_not_opened: 'ჯერ არ გახსნილა',
    share_opens: 'გახსნა {n}×',
    share_last_viewed: 'ბოლოს ნახა {when}',
    share_devices: '{n} მოწყობილობა',
    share_saw_photos: 'ნახა ფოტოები',
    share_map: 'რუკა {n}×',
    share_called: 'დარეკა {n}×',
    share_whatsapped: 'WhatsApp {n}×',
    share_requested_viewing: 'მოითხოვა ჩვენება',
    admin_access_pending: 'მოლოდინში მყოფი წვდომის მოთხოვნები',
    admin_access_none: 'მოლოდინში მოთხოვნები არ არის',
    admin_access_reps: 'წარმომადგენლები',
    admin_access_note_ph: 'შენიშვნა (არასავალდებულო)…',
    admin_access_grant: 'მინიჭება',
    admin_access_reject: 'უარყოფა',
    // B196 (2026-07-29): en-side note applies.
    admin_access_client_generic: 'კლიენტი',
    admin_access_revoke: 'ჩამორთმევა',
    // B92/B105 (2026-07-29): discovery profiles editor + lead badge.
    admin_discovery_profiles_title: 'ძიების პროფილები',
    admin_discovery_profiles_desc: 'პორტალის საძიებო URL-ები, რომლებსაც ლიდების მაძიებელი დადის. URL-ის ნებისმიერი ცვლილების შემდეგ დააჭირეთ ტესტს — არასწორი URL ჩუმად ბრუნდება ცარიელი ან გაუფილტრავი შედეგით.',
    admin_discovery_profiles_seed_note: 'ნაჩვენებია ჩაშენებული ნაგულისხმევები — კონფიგში ჯერ არაფერია შენახული. ნებისმიერი ცვლილება ინახავს სრულ სიას.',
    admin_discovery_profiles_test: 'ტესტი',
    admin_discovery_profiles_result: 'ნაპოვნია {ids} ID {total} განცხადებულიდან · {pages} გვერდი',
    admin_discovery_profiles_zero_warn: 'ნული ID — შეამოწმეთ URL',
    admin_discovery_profiles_del_confirm: 'წავშალოთ ეს პროფილი?',
    admin_discovery_profiles_add: 'პროფილის დამატება',
    desk_lead_badge: 'ლიდი',
    desk_lead_badge_hint: 'არგოს ნაპოვნი მესაკუთრის განცხადება — ჯერ არ არის დამუშავებული. ამ მესაკუთრესთან ჩვენგან ჯერ არავის უმუშავია.',
    admin_access_counters: 'მოთხოვნები წარმომადგენლების მიხედვით',
    admin_access_requests_word: 'მოთხოვნა',
    admin_access_granted_word: 'მინიჭებული მათ გვერდის ავლით',
    admin_access_history: 'გადაწყვეტილი',
    desk_dormant_section: 'უმოქმედო ({n}) — 60 დღეა აქტივობა არ ყოფილა',
    desk_dormant_more: '+{n} კიდევ უმოქმედო — დავიწროვეთ ძებნა სანახავად',
    desk_avail_facet_title: '📆 ხელმისაწვდომია თარიღიდან',
    desk_avail_facet_hint: 'მესაკუთრის მიერ ნათქვამი თარიღი, როდიდანაც ობიექტი გასაყიდად/გასაქირავებლად თავისუფალია. ლენტაში ამ თარიღამდე ერთი კვირით ადრე გააქტიურდება.',
    desk_avail_wake_note: 'უმოქმედო ობიექტი ამ თარიღამდე 7 დღით ადრე იღვიძებს ახალი 60-დღიანი ათვლით.',
    desk_avail_conflicting: 'წყაროები არ ემთხვევა',
    desk_avail_conflict_note: 'ჩანაწერში ორი განსხვავებული თარიღია. დაუზუსტეთ მესაკუთრეს — შენახვა თქვენს თარიღს მათ გვერდით დაამატებს და მეორეს არ წაშლის.',
    desk_avail_shared_note: 'თქვენი ჩანაწერი იმისა, რაც მესაკუთრემ გითხრათ. სხვა აგენტებს თავიანთი აქვთ — თუ ორი თარიღი არ ემთხვევა, ობიექტი დაუდასტურებლად ჩაითვლება, სანამ ვინმე გადაამოწმებს. წაშლა მხოლოდ თქვენსას შლის.',
    desk_pool_auto_toggle: '⚡ ჩემი დამატებული ობიექტების ავტო-გამოქვეყნება',
    desk_pool_auto_hint: 'მომავალი ობიექტები ფასით ავტომატურად გამოქვეყნდება ამ საკომისიოთი',
    desk_pool_auto_commission_required: 'ჯერ მიუთითეთ ნაგულისხმევი საკომისიო %',
    desk_pool_commission_of: 'რისგან',
    desk_pool_basis_unset: '— რისგან? —',
    desk_pool_basis_monthly_rent: 'ერთი თვის ქირა',
    desk_pool_basis_sale_price: 'გაძვის ფასი',
    desk_pool_auto_basis_required: 'ჯერ მითითეთ რისგანია საკომისიო',
    desk_pool_card_fallback_title: 'ობიექტი',
    desk_pool_offers_count: '{n} შეთავაზება',
    desk_pool_market_version: 'ბაზრის ვერსია',
    desk_pool_agents_version: '{agent}-ის ვერსია',
    desk_pool_market_caption: '⚖ ბაზრის ვერსია — გამოქვეყნებული შეთავაზებების კონსენსუსი. ⚡ = ფაქტები განსხვავდება.',
    desk_pool_viewing_agents_version: '👁 უყურებთ {agent}-ის ვერსიას — განსხვავებული ველები მონიშნულია.',
    desk_pool_market_annotation: '(ბაზარი: {value})',
    desk_pool_no_facts: 'ფაქტები ჯერ არაა გაზიარებული.',
    desk_pool_offers_heading: 'შეთავაზებები (ზრდადობით)',
    desk_pool_table_headers: 'აგენტი / ფასი / საკომისიო / თქვენი წილი (50/50)',
    desk_pool_view_version: 'ვერსიის ნახვა',
    desk_pool_agent_fallback: 'აგენტი',
    desk_pool_loading: 'ბირჟა იტვირთება…',
    desk_pool_nothing_published: 'ბირჟაზე ჯერ არაფერი გამოქვეყნებულა.',
    desk_pool_card_market_label: '⚖ ბაზრის ვერსია',
    desk_pool_card_disagree: '· ⚡ ფაქტები განსხვავდება',
    desk_dup_no_identity: 'ავტორიზაცია სავალდებულოა დუბლიკატების შესაფასებლად',
    desk_dup_facet_title: 'შესაძლო დუბლიკატები — ერთი და იგივე ბინაა?',
    desk_dup_facet_hint: 'თქვენი პასუხი მხოლოდ ფიქსირდება — ჩანაწერები არ ერთიანდება და არ იშლება.',
    desk_dup_to_review: '{n} განსახილველი',
    desk_dup_cluster_of: '{n} მსგავსი განცხადება',
    desk_dup_last_verdict: 'ბოლო გადაწყვეტილება: {verdict}{note}',
    desk_dup_verdict_same: '✓ იგივეა',
    desk_dup_verdict_different: '✗ განსხვავდება',
    desk_dup_verdict_unsure: '? არ ვიცი',
    desk_dup_shown_together: 'დაჯგუფებულია როგორც ერთი ბინა ({n} ჩანაწერი):',
    desk_dup_this_one: '(ეს ჩანაწერი)',
    desk_dup_contested: '⚠ სადავო — სხვამ ესენი სხვადასხვა ობიექტებად შეაფასა. გადახედეთ.',
    desk_archive_active_tooltip: 'აღარ გამოჩნდება თქვენს ობიექტებში — არ იშლება, აღდგენა ნებისმიერ დროს შეგიძლიათ',
    desk_archive_restore_tooltip: 'სამუშაო ობიექტებში დაბრუნება',
    desk_archive_button: '🗂 დაარქივება',
    desk_archive_restore_button: '↩ ჩემს ობიექტებში აღდგენა',
    desk_archive_draft_tooltip: 'მონახაზის დაარქივება — არ იშლება, უბრალოდ ტოვებს განსახილველთა სიას',
    desk_archive_draft_busy: 'არქივდება…',
    desk_archive_draft_button: '🗄 მონახაზის დაარქივება',
    desk_property_fallback_title: 'ობიექტი',
    desk_property_review_title: 'ობიექტის მონახაზის გადახედვა',
    desk_property_recorded_subtitle: 'დაფიქსირებულია — ყოველი შენახვა ახალი ჩანაწერია, ძველი არ იშლება',
    desk_property_facet_title: 'ობიექტი — მისამართი ან საკადასტრო სავალდებულოა',
    desk_property_address: 'მისამართი',
    desk_property_address_ph: 'ქუჩა, კორპუსი, ბინა…',
    // Taken verbatim from the fill form's own Georgian (src/fill/FillApp.jsx STR.ka) —
    // same control, same words. See the English block for why every key is present.
    desk_addr_street: 'ქუჩა',
    desk_addr_street_ph: 'ქუჩის სახელი',
    desk_addr_number: 'ნომერი',
    desk_addr_city: 'ქალაქი',
    desk_addr_peri: 'თბილისის შემოგარენი',
    desk_addr_at_pin: 'ამ პინზე',
    desk_addr_use_it: 'გამოვიყენოთ',
    desk_addr_map_ph: 'ქუჩა ან ორიენტირი',
    desk_addr_or_map: 'ვერ პოულობთ? უბრალოდ მონიშნეთ რუკაზე',
    desk_addr_other_number: 'სხვა ნომერია? შეასწორეთ',
    desk_addr_unnamed: 'ეს ადგილი — სახელი ვერ დავადგინეთ',
    desk_addr_change: 'შეცვლა',
    desk_addr_other: 'სხვა',
    desk_addr_entrance_add: '+ სადარბაზო / ბინა',
    desk_addr_entrance_label: 'სადარბაზო / ბინა',
    desk_addr_entrance_note: 'მხოლოდ აგენტისთვის — არსად ქვეყნდება',
    desk_addr_or: 'ან',
    desk_addr_mark_on_map: '🗺 რუკაზე მონიშვნა',
    desk_addr_my_location: '📍 ჩემი მდებარეობა',
    desk_addr_not_listed: 'ქუჩა ჩვენს სიაში არ არის? მაინც აკრიფეთ — ტექსტი შენარჩუნდება.',
    desk_addr_pin_says: 'პინი სხვა შენობაზეა',
    desk_addr_pin_keep: 'დავტოვოთ ჩემი',
    desk_addr_pin_decide: 'გასაგრძელებლად აირჩიეთ ერთი',
    desk_addr_searching: 'იტვირთება…',
    desk_addr_no_street: 'ჩვენს ცნობარში არ მოიძებნა — ტექსტი შენარჩუნდება',
    desk_addr_auto: 'შეივსო მისამართიდან',
    desk_addr_auto_pin: 'შეივსო პინიდან',
    desk_addr_conflict: 'პინი სხვა უბანზე მიუთითებს, ვიდრე არჩეული.',
    desk_addr_keep_mine: 'ჩემი დავტოვო',
    desk_addr_use_pin: 'რუკის მიხედვით შევსება',
    desk_property_address_suggestion: '📍 {suggestion}',
    desk_property_district: 'უბანი',
    desk_property_cadastral: 'საკადასტრო კოდი',
    desk_property_type_label: 'ტიპი',
    desk_property_map_section: 'მდებარეობა რუკაზე — არასავალდებულო',
    desk_property_photos_docs_title: 'ფოტოები და დოკუმენტები',
    desk_property_photos_docs_summary: '{n} ფოტო · {n} დოკ.',
    desk_property_photos_section: 'ფოტოები — საჯარო გალერეა · გადაათრიეთ რიგის შესაცვლელად',
    desk_property_photos_view_clean: 'სუფთა',
    desk_property_photos_view_original: 'ორიგინალი',
    desk_property_photos_original_note: 'პორტალის საკუთარი ასლი, წყალნიშნით. ინახება დამთხვევებისთვის, კლიენტს არასოდეს ეჩვენება — მხოლოდ სანახავად.',
    desk_property_remove_photo: 'წაშლა',
    desk_property_move_to_docs: 'დოკუმენტებში გადატანა (მხოლოდ აგენტებისთვის)',
    desk_property_add_photos: 'ფოტოების დამატება',
    desk_property_docs_section: 'დოკუმენტები — შიდა მოხმარებისთვის (გეგმები, PDF, ამონაწერები)',
    desk_property_move_to_photos: 'ფოტოებში გადატანა (საჯარო)',
    desk_property_add_documents: 'დოკუმენტის დამატება (PDF, ფოტო…)',
    desk_property_uploading: '⏳ იტვირთება…',
    desk_property_upload_failed: '⚠️ ატვირთვა ვერ შესრულდა: {err}',
    desk_property_files_upload_note: 'ფაილები აიტვირთება ობიექტის დაფიქსირებისას.',
    desk_pm_photos_from_ad: 'განცხადებიდან — ინახება ისე, როგორც დაფიქსირდა',
    desk_pm_photos_to_version: 'დამატებული ფოტოები შეინახება თქვენს ვერსიაში დაფიქსირებისას — თავად განცხადება არ იცვლება.',
    desk_pm_photos_mine: 'თქვენი ფოტოები',
    desk_pm_my_version_open: 'თქვენ არედაქტირებთ ამ ობიექტის თქვენს ვერსიას — მისი ნებისმიერი განცხადება ამავე ვერსიას ხსნის.',
    desk_pm_my_version_other_ad: 'თქვენ ამ ობიექტის სხვა განცხადება გახსენით; თქვენი ვერსია ინარჩუნებს საკუთარ მნიშვნელობებს და ფოტოებს.',
    desk_property_photo_size: 'ფოტოს ზომა:',
    desk_property_photo_size_small: 'პატარა',
    desk_property_photo_size_medium: 'საშუალო',
    desk_property_photo_size_large: 'დიდი',
    desk_property_photo_size_original: 'ორიგინალი',
    desk_property_photo_size_px: 'გრძელი გვერდი {px} px',
    desk_property_photo_size_original_hint: 'თავდაპირველი ზომით ატვირთვას მეტი დრო სჭირდება. ძალიან დიდი ფაილი შესაძლოა ვერ აიტვირთოს.',
    desk_property_photo_size_est: '≈ {size} ასატვირთი',
    desk_property_photo_size_est_saved: '≈ {size} ასატვირთი (იყო {was})',
    desk_property_terms_facet_title: 'მესაკუთრის მოთხოვნები',
    desk_property_terms_facet_hint: 'მესაკუთრის საჯარო მოთხოვნა. ჩვენი შეთანხმება (საკომისიო, ექსკლუზივი) — იხ. "სააგენტოს შეთანხმება" ქვემოთ.',
    desk_property_terms_summary: '{intent} · {price} {currency} / მორიგებით',
    desk_property_intent_label: 'მიზანი',
    desk_property_price_negotiable: 'ფასი — ცარიელი = მორიგებით',
    desk_property_currency_label: 'ვალუტა',
    desk_property_facts_title: 'ფაქტები — თუ არ იცით, დატოვეთ ცარიელი (ცარიელი ≠ 0)',
    desk_facts_rooms: 'ოთახი',
    desk_facts_bedrooms: 'საძინებელი',
    desk_facts_bathrooms: 'სველი წერტილი',
    desk_facts_area_sqm: 'ფართი მ²',
    desk_facts_kitchen_area: 'სამზარეულო მ²',
    desk_facts_floor: 'სართული',
    desk_facts_floors_total: 'სულ სართული',
    desk_facts_ceiling: 'ჭერის სიმაღლე მ',
    desk_facts_ceiling_variation: 'ჭერი ყველგან ერთნაირია?',
    desk_facts_ceiling_uniform: 'ყველგან ერთნაირი',
    desk_facts_ceiling_varies: 'ცვალებადი',
    desk_facts_ceiling_min: 'ყველაზე დაბალი მ',
    desk_facts_ceiling_max: 'ყველაზე მაღალი მ',
    desk_facts_ceiling_range_note: 'ყველაზე დაბალი ნიშნული ყველაზე მაღალზე ნაკლები უნდა იყოს — მანამდე ეს ორი ველი არ შეინახება.',
    desk_property_condition_label: 'მდგომარეობა',
    desk_property_condition_ph: 'გარემონტებული / თეთრი კარკასი / ძველი…',
    desk_facts_balcony: 'აივანი',
    desk_facts_terrace: 'ვერანდა',
    desk_room_unit_kind: 'ოთახი არის საერთო',
    desk_room_unit_apartment: 'ბინაში',
    desk_room_unit_house: 'სახლში',
    desk_room_unit_rooms: 'ოთახები მთლიან ბინაში / სახლში',
    desk_room_unit_area: 'მთლიანი ბინის ან სახლის ფართობი (მ²)',
    desk_room_unit_occupants: 'ვინ ცხოვრობს უკვე (ადამიანი)',
    desk_owner_contact_title: 'მესაკუთრე / კონტაქტი',
    desk_owner_contact_name: 'სახელი',
    desk_owner_contact_phone: 'ტელეფონი',
    desk_agreement_facet_title: 'სააგენტოს შეთანხმება — ჩვენი გარიგება (არ საჯაროვდება)',
    desk_agreement_facet_hint: 'საკომისიო, ექსკლუზივი, მინ. ფასი — შიდა CRM მონაცემები.',
    desk_agreement_summary: '{n} თანამშრომლობა / ჯერ არაა',
    desk_agreement_no_contact: 'კონტაქტი არაა — დაამატეთ ტელეფონი, დააფიქსირეთ და მერე გახსენით შეთანხმება.',
    desk_meeting_facet_title: 'ჩვენება — დაგეგმეთ ობიექტის ჩვენება',
    desk_meeting_add_tooltip: 'ამ ობიექტზე შეხვედრის დამატება',
    desk_meeting_add_action: 'შეხვედრის დამატება',
    desk_meeting_last_outcome: 'ბოლო: {status}',
    desk_meeting_list_title: 'შეხვედრები ამ ობიექტზე',
    desk_draft_badge: 'დრაფტი',
    desk_meeting_no_client: 'უკლიენტოდ',
    desk_lineage_col_what: 'რა არის ეს?',
    desk_lineage_map_tile: 'რუკა',
    desk_lineage_col_relation: 'ურთიერთობა მესაკუთრესთან',
    desk_meeting_none_yet: 'შეხვედრები ჯერ არ არის',
    desk_observed_from_section: 'წყარო — რამდენად სანდოა მონაცემები',
    desk_posted_on_facet_title: 'განთავსებულია — პლატფორმის წყარო',
    desk_posted_on_summary: '{n} განცხადება',
    desk_price_changes_summary: 'ფასის {n} ცვლილება',
    desk_open_link: '↗ გახსნა',
    desk_price_history_section: 'ფასის ისტორია',
    desk_price_history_row: '{from} → {to} {currency} ({±pct}%)',
    desk_done_button: 'მზადაა',
    desk_property_cancel: 'გაუქმება',
    desk_property_saving: 'ინახება…',
    desk_property_record_update: '✓ განახლების დაფიქსირება',
    desk_property_heic_converting: 'iPhone-ის {n} ფოტო (HEIC) გადადის JPEG-ში…',
    desk_property_recorded: '✓ დაფიქსირებულია',
    desk_property_accept_save: '✓ დადასტურება და შენახვა',
    desk_property_record_button: '✓ ობიექტის დაფიქსირება',
    desk_toast_client_saved: 'კლიენტის მონაცემები შენახულია',
    desk_toast_property_saved: 'ქონების მონაცემები შენახულია',
    desk_toast_meeting_saved: '✓ შეხვედრა შეინახა',
    desk_time_just_now: 'ახლახან',
    desk_time_min_ago: '{n} წთ-ის წინ',
    desk_time_hour_ago: '{n} სთ-ის წინ',
    // Native-speaker check 2026-08-08: წთ-ის / სთ-ის are the normal digital-UI abbreviations,
    // but "დღ-ის" is unidiomatic — the root დღე is only three letters, so abbreviating it
    // ADDS a character. Days are spelled out. Do not "make it consistent" with the two above.
    desk_time_day_ago: '{n} დღის წინ',
    desk_activity_added_client: 'დაამატა კლიენტი',
    desk_activity_ai_parsed_client: 'AI-მ შეავსო კლიენტი',
    desk_activity_you_edited_client: 'თქვენ შეცვალეთ კლიენტი',
    desk_activity_ai_edited_client: 'AI-მ შეცვალა კლიენტი',
    desk_activity_closed_client: 'დახურა კლიენტი',
    desk_activity_reopened_client: 'აღადგინა კლიენტი',
    desk_activity_parsed_property: 'ავტომატურად შევსებული ობიექტი',
    desk_activity_archived_property: 'დაარქივა ობიექტი',
    desk_activity_saved_property: 'შეინახა ობიექტი',
    desk_activity_draft_badge: 'მონახაზი',
    desk_activity_logged_meeting: 'დაამატა შეხვედრა',
    desk_activity_added_candidate: 'დაამატა კანდიდატი',
    desk_activity_fallback: 'აქტივობა',
    desk_meettime_today: 'დღეს {time}',
    desk_meettime_tomorrow: 'ხვალ {time}',
    desk_tab_activity: '🕘 აქტივობა',
    desk_tab_notif_short: '🔔 შეტყ.',
    desk_tab_notifications: 'შეტყობინებები',
    desk_tab_meet_short: '📅 შეხვ.',
    desk_tab_meetings: 'შეხვედრები',
    desk_activity_empty: 'ჯერ არაფერია.',
    desk_notif_need_review: '🏠 {n} განსახილველია',
    desk_notif_empty: 'საყურადღებო არაფერია.',
    desk_notif_failed: 'შეფერხდა — გასასწორებელია',
    desk_notif_review_row: '{text} — განხილვა',
    desk_notif_price_changes_section: 'ფასის ცვლილებები (7დღ)',
    desk_notif_listings_ended_section: 'დასრულებული განცხადებები (7დღ)',
    desk_notif_no_longer_found: 'აღარ იძებნება {source}-ზე',
    desk_meetings_empty: 'დღეს ან ხვალ არაფერია დაგეგმილი.',
    desk_meeting_fallback: 'შეხვედრა',
    desk_ranking_title: '🏆 რეიტინგი',
    desk_ranking_empty: 'ამ პერიოდში აქტივობა ჯერ არაა.',
    desk_ranking_period: 'რეიტინგის პერიოდი',
    desk_ranking_window_today: 'დღეს (თბილისი)',
    desk_ranking_as_of: 'მდგომარეობა {t}',
    desk_ranking_refresh_failed: 'განახლება ვერ მოხერხდა',
    desk_ranking_unavailable: 'რეიტინგი მიუწვდომელია - მონაცემები ვერ წაიკითხა.',
    desk_ranking_retry: 'თავიდან ცდა',
    desk_ranking_unknown: 'უცნობი',
    desk_ranking_daily: 'დღე',
    desk_ranking_weekly: 'კვირა',
    desk_ranking_monthly: 'თვე',
    desk_ranking_new: 'ახალი',
    desk_ranking_to_next: '{n} ქულა #{rank}-მდე',
    desk_ranking_legend: '🏁 დახურული გარიგება · 🤝 შეხვედრა · 📥 შენახული · ↻ რეპოსტი',
    desk_score_legend: "🏁 გარიგება · 🤝 შეხვედრა შედეგით · 👤 კლიენტი (გაგრძელებით) · 📋 მესაკუთრის ინფორმაცია · ↻ ფასიანი რეპოსტი თანამშრომლობის გარეშე",
    desk_score_why: "მესაკუთრის ინფო {owner} · შედეგი {results} · უსარგებლო რეპოსტი {reposts} · დასწრება ×{mult}",
    desk_floor_tab: "ზღვარი",
    desk_floor_note: "შედარება მთელ ოფისთან და არა ფიქსირებულ რიცხვებთან: თითოეული აგენტის 30-დღიანი ქულა, გადაანგარიშებული მის სამუშაო დღეებზე. ოფისის მედიანა {median} ({n} აგენტი); ზღვარი მისი {pct}% = {line}. შემოწმებულია დღეს და {days} დღის წინ; ორივეჯერ ქვემოთ = ვადა ამოიწურა. {date}-მდე საცდელი რეჟიმია.",
    desk_floor_none: "ყველა აგენტი ზღვარს ზემოთაა.",
    desk_board_median: "ოფისის მედიანა {n}",
    stats_part_money: "შემოსავალი",
    stats_part_reposts_wasted: "რეპოსტის ხარჯი",
    stats_part_reposts_free: "უფასო — მესაკუთრის ინფო და შემოსავალი არის",
    stats_part_paid_n: "{n} ფასიანი რეპოსტი",
    stats_part_silent: "უმოქმედო სამუშაო დღეები",
    stats_part_silent_sub: "დისტანციური {remote} · ოფისი {office} · გაცდენა {absent}",
    admin_score2_model_v3: "v3 — ღირებულების ქულა (ნაგულისხმევი)",
    admin_value_repost_cost: "ფასიანი რეპოსტის ღირებულება ₾ (myhome/ss)",
    admin_value_owner_cap_month: "მესაკუთრის ინფოს ზღვარი ₾ / 30 დღე (თანამშრომლობის გარდა)",
    admin_value_coop_rich_weight: "თანამშრომლობა მესაკუთრის ინფოთი = × ინფოს ერთეული",
    admin_value_coop_default_weight: "თანამშრომლობა მხოლოდ სტანდარტული პირობებით = × ინფოს ერთეული",
    admin_value_coop_cap_month: "თანამშრომლობის ზღვარი ₾ / 30 დღე",
    admin_value_remote_silent_weight: "უმოქმედო დისტანციური დღე × დღის ღირებულება",
    admin_value_office_silent_weight: "უმოქმედო ოფისის დღე × დღის ღირებულება",
    admin_value_absent_weight: "გაცდენილი დღე × დღის ღირებულება",
    admin_value_late_weight: "დაგვიანება × დღის ღირებულება",
    stats_part_late: "დაგვიანებები",
    stats_part_late_sub: "{n} დაგვიანებული დღე · {mins} წთ დაგვიანება",
    admin_value_values_window_days: "კოეფიციენტების პერიოდი (დღე)",
    desk_floor_me_vs2: "შენი ქულა {norm} · ზღვარი {line}",
    desk_floor_me_rule2: "წესი: {days} დღე ზღვარს ქვემოთ = წასვლა.",
    desk_score_legend3: "ქულა ₾-ში: სააგენტოსთვის შექმნილი ღირებულება მინუს დახარჯული რესურსი, შედარებული მთელ სააგენტოსთან · 🏁 გარიგება · 🤝 შეხვედრა შედეგით · 👤 კლიენტი (გაგრძელებით) · 📋 მესაკუთრის ინფორმაცია · ↻ ფასიანი რეპოსტი უსაფუძვლოდ",
    desk_score_legend3: "◆ გახსნილი თანამშრომლობა · 🏆 დახურული გარიგება · 📅 შეხვედრა · 👤 კლიენტი · 📋 მესაკუთრის ინფო · 📥 შენახული",
    desk_score_why3: "მესაკუთრის ინფო {owner} (თანამშრომლობა {coops}) · შემოსავალი {results} · რეპოსტის ხარჯი {reposts} · უმოქმედო სამუშაო დღეები {silent} · დაგვიანებები {late}",
    desk_floor_note3: "ქულა 30 დღეზე, შედარებული მთელ სააგენტოსთან (ყველა ოფისი). ოფისის მედიანა {median} ({n} აგენტი); ზღვარი მისი {pct}% = {line}, ხოლო ნებისმიერი უარყოფითი ქულა — ზარალი. შემოწმებულია დღეს და {days} დღის წინ; ორივეჯერ ქვემოთ = ვადა ამოიწურა. {date}-მდე საცდელი რეჟიმია.",
    desk_floor_vs3: "ქულა {norm} · ოფისის მედიანა {median}",
    desk_floor_weak_silent: "უმოქმედო სამუშაო დღეები",
    desk_floor_silent_remote: "უმოქმედო დისტანციური დღე {n}: {dates}.",
    desk_floor_silent_office: "უმოქმედო ოფისის დღე {n}: {dates}.",
    desk_floor_absent_n: "გაცდენა {n}.",
    stats_lines_month_only: "მედიანისა და მინიმალური ზღვრის ხაზები ჩანს 1-თვიან პერიოდზე (30 დღე / ეს თვე).",
    stats_part_owner: "მესაკუთრის ინფორმაცია",
    stats_part_owner_capped: "{raw} კვირის ზღვრამდე",
    stats_part_results: "შედეგი",
    stats_part_results_sub: "{deals} გარიგება · {meetings} შეხვედრა · {clients} კლიენტი",
    stats_part_reposts_penalty: "რეპოსტი თანამშრომლობის გარეშე",
    stats_part_unbacked: "{paid} ფასიანიდან {n}",
    stats_part_attendance_mult: "დასწრება",
    desk_board_line: "მინიმალური ზღვარი {n}",
    desk_floor_me_weak: "ჯგუფს ჩამორჩები: {parts}",
    desk_floor_me_vs: "შენი ქულა {norm} · ოფისის მედიანა {median} · ზღვარი {line}",
    desk_floor_weak_reposts: "სუსტი: რეპოსტი თანამშრომლობის გარეშე",
    desk_floor_weak_attendance: "სუსტი: დასწრება",
    desk_floor_weak_results: "სუსტი: შეხვედრები / გარიგებები",
    desk_floor_weak_owner: "სუსტი: მესაკუთრის ინფო",
    desk_floor_vs: "ქულა {norm} · ოფისის მედიანა {median}",
    desk_floor_no_group: "შესადარებლად ჯერ ცოტა აგენტია (7+ სამუშაო დღით).",
    desk_floor_error: "ზღვრის ჩატვირთვა ვერ მოხერხდა — სცადეთ რამდენიმე წუთში.",
    desk_floor_me_title: "თქვენი მინიმალური ზღვარი",
    desk_floor_me_ok: "✓ ზღვარს ზემოთ ხარ.",
    desk_floor_me_below: "ზღვარს ქვემოთ ხარ. {days} დღეში უნდა გადააჭარბო, თორემ წახვალ.",
    desk_floor_me_leave: "{days} დღეა ზღვარს ქვემოთ ხარ — ვადა ამოიწურა. დაელაპარაკე მენეჯერს.",
    desk_floor_me_rule: "წესი: ოფისის მედიანის {pct}%-ზე ნაკლები {days} დღის განმავლობაში = წასვლა.",
    desk_floor_me_trial: "{date}-მდე საცდელი რეჟიმია — შედეგები ჯერ არ აქვს.",
    desk_floor_leave: "ვადა ამოიწურა",
    desk_floor_at_risk: "რისკის ქვეშ",
    desk_floor_trial: "საცდელი",
    admin_score2_title: "აგენტის შეფასება (v2)",
    admin_score2_desc: "ქულები მესაკუთრესთან მუშაობისა და შედეგისთვის, გამრავლებული დასწრების სტაბილურობაზე. რეპოსტი ქულას არ იძლევა; ფასიანი myhome/ss რეპოსტი, რომელსაც იმავე აგენტის მესაკუთრის ინფორმაცია არ უმაგრებს, ქვემოთ მითითებულ ჯარიმას იწვევს.",
    admin_score2_model: "რეიტინგის მოდელი",
    admin_score2_model_v2: "v2 — მესაკუთრის ინფო + შედეგი",
    admin_score2_model_v1: "v1 — ძველი ქულები",
    admin_score2_coop_rich: "თანამშრომლობა მესაკუთრის ინფოთი",
    admin_score2_coop_default: "თანამშრომლობა, მხოლოდ სტანდარტული",
    admin_score2_restriction: "შეზღუდვების პასუხი (განცხადებაზე)",
    admin_score2_terms: "პირობები ჩაწერილი (განცხადებაზე)",
    admin_score2_detail: "ქონების დეტალები / შეფასება",
    admin_score2_client: "კლიენტი გაგრძელებით",
    admin_score2_meeting: "შეხვედრა შედეგით",
    admin_score2_close: "დახურული გარიგება",
    admin_score2_commission_per_100: "ქულა 100 ₾ საკომისიოზე",
    admin_score2_owner_cap_week: "მესაკუთრის ინფოს ქულების ზღვარი / კვირა",
    admin_score2_unbacked_repost: "ჯარიმა: რეპოსტი თანამშრომლობის გარეშე",
    admin_score2_attendance_floor: "დასწრების კოეფიციენტი 0%-ზე",
    admin_floor_title: "წასვლის ზღვარი",
    admin_floor_min_group: "შესადარებლად მინ. აგენტი",
    admin_floor_min_days: "არ ფასდება (სამუშაო დღემდე)",
    admin_floor_window_days: "შედარების ფანჯარა (დღე)",
    admin_floor_bottom_share: "ასევე მოინიშნოს ყველაზე სუსტი წილი (0–1, 0 = გამორთ.)",
    admin_floor_ratio: "ზღვარი = ოფისის მედიანის წილი (0–1)",
    admin_floor_desc: "ფიქსირებული რიცხვები არ არის: თითოეული აგენტი მთელ ოფისს ედარება. 30-დღიანი ქულა (გადაანგარიშებული სამუშაო დღეებზე) ოფისის მედიანის „წილზე\" ნაკლები = წასვლის კანდიდატი; საშეღავათო ვადის შემდეგაც ქვემოთ = ვადა ამოიწურა. სურვილისამებრ ჯგუფის ყველაზე სუსტი წილიც მოინიშნება. მენეჯმენტი სიას Desk-ზე ხედავს (ზღვარის ტაბი); თითოეული აგენტი საკუთარ ხაზს ხედავს.",
    admin_floor_grace_days: "საშეღავათო ვადა (დღე)",
    admin_floor_enforce_from: "საცდელი მთავრდება",
    admin_cobroke_title: 'თანაგაყოფის სტანდარტი',
    admin_cobroke_desc: 'რამდენი რჩება ჩვენთან, როცა კლიენტს სხვა სააგენტოს აგენტი წარმოადგენს. ჩაიწერება თითოეულ გარიგებაზე გაყოფის მომენტში — აქ შეცვლა უკვე შეთანხმებულ გარიგებებს არ ცვლის.',
    admin_cobroke_unit: '% ჩვენ',
    admin_rankpts_title: 'რეიტინგის ქულები',
    admin_rankpts_desc: 'ერთი საერთო ქულების სისტემა: ეს ქულები კვებავს როგორც Desk-ის რეიტინგს, ისე სტატისტიკის ტაბის შესრულების ქულას (კვირის ბაზა — სხვა ფანჯრები ლიმიტებს მასშტაბირებს). გარიგებები უნდა დომინირებდეს; ლიმიტები რეპოსტების რაოდენობას შედეგების გადაფარვას უშლის. დასწრება მცირე ბონუსია და საჯაროდ არასდროს ჩანს.',
    fx_stale_tooltip: 'დღევანდელი განახლება ვერ მოხერხდა — ეს წინა დღის კურსია',
    admin_fx_title: 'ვალუტის კურსები',
    admin_freeze_title: 'საკომისიოს მონაცემების დაფიქსირება',
    admin_freeze_desc: 'დახურული გარიგებები, რომლებსაც აგენტის ან მენეჯერის წილი ჩაწერილი არ აქვთ, ყოველ ჯერზე თავიდან გამოითვლება და შეიძლება შეიცვალოს. ეს ჩაწერს მათ გარიგებაზე მისივე დახურვის თარიღით. უკვე ჩაწერილი წილები ხელუხლებელი რჩება, ხელახლა გაშვება კი არაფერს ცვლის.',
    admin_freeze_btn: 'დაფიქსირება',
    admin_freeze_running: 'მიმდინარეობს…',
    admin_freeze_done: 'შემოწმდა {swept}, დაფიქსირდა {stamped}.',
    admin_freeze_none: 'დასაფიქსირებელი არაფერია — ყველა დახურულ გარიგებას წილები ჩაწერილი აქვს.',
    admin_freeze_fail: 'ვერ დაფიქსირდა: {err}',
    admin_fx_desc: 'ლარი 1 ერთეულზე. საკომისიო შემოდის ლარში ან დოლარში; ყველა ქულა და თანხის ჯამი ამ კურსით კონვერტირდება.',
    admin_rankpts_close: 'დახურული გარიგება',
    admin_rankpts_commission: 'ქულა 100 საკომისიოზე',
    admin_rankpts_meeting: 'შეხვედრა',
    admin_rankpts_scrape: 'შენახვა',
    admin_rankpts_scrape_cap: 'შენახვის ლიმიტი/კვ',
    admin_rankpts_reshare: 'რეპოსტი',
    admin_rankpts_reshare_cap: 'რეპოსტის ლიმიტი/კვ',
    admin_rankpts_office_day: 'ოფისის დღის ბონუსი',
    admin_rankpts_perfect_week: 'სრული კვირის ბონუსი',
    desk_person_intent_buy: 'ყიდულობს',
    desk_person_intent_rent: 'ქირაობს',
    desk_person_intent_buy_or_rent: 'ყიდულობს/ქირაობს',
    desk_person_intent_short_term: 'დღიურად',
    desk_person_intent_sale: 'ყიდის',
    desk_person_intent_lease: 'აქირავებს',
    desk_person_up_to_price: 'მაქს. {price} {currency}',
    desk_person_modal_title: 'პიროვნება',
    desk_person_unnamed: 'უსახელო',
    desk_person_looking_for: 'ეძებს ({n})',
    desk_person_no_active_searches: 'აქტიური ძიება არაა.',
    desk_person_owns_selling: 'ფლობს / ყიდის ({n})',
    desk_person_no_listings: 'განცხადებები არ მოიძებნა.',
    desk_person_showing_of: 'ნაჩვენებია {n} / {n}.',
    desk_person_cooperations: 'თანამშრომლობა ({n})',
    desk_person_no_cooperation: 'ამ კონტაქტთან თანამშრომლობა ჯერ არაა.',
    desk_person_open_coop_tooltip: 'ამ ობიექტზე თანამშრომლობის დაწყება',
    desk_person_modal_close: 'დახურვა',
    desk_coop_chip_tooltip: 'თანამშრომლობა — {status}',
    desk_represented_by: 'წარმომადგენელი: {name}',
    desk_coop_not_joined: 'ჯერ არ არის მითითებული',
    desk_coop_group_note: 'ერთი ურთიერთობა რამდენიმე ჩანაწერშია (დუბლირებული კონტაქტი / მონათესავე განცხადებები) — ჩვენებისთვის გაერთიანებულია, ყველა ჩანაწერი შენახულია',
    desk_lineage_reshared_from: 'რეპოსტი: {who}',
    desk_entry_family_reach_tooltip: '{agents} აგენტმა გადადო · სულ {total} განცხადება',
    desk_coop_open_tooltip: 'განცხადების ავტორთან თანამშრომლობის დაწყება',
    desk_coop_chip_default: '📞 კონტაქტი',
    desk_coop_modal_title: 'თანამშრომლობა',
    desk_coop_intent_label: 'მიზანი',
    desk_coop_intent_sale: 'გაყიდვა',
    desk_coop_intent_rent: 'გაქირავება',
    desk_coop_status_label: 'სტატუსი',
    desk_coop_status_proposed: 'შეთავაზებული',
    desk_coop_status_negotiating: 'მოლაპარაკება',
    desk_coop_status_agreed: 'შეთანხმებული',
    desk_coop_status_rejected: 'უარყოფილი',
    desk_coop_status_ended: 'დასრულებული',
    desk_entry_closed_sold: 'გაყიდულია',
    desk_entry_closed_rented: 'გაქირავებულია',
    desk_terms_agent_recorded: 'აგენტის მიერ ჩაწერილი',
    // Reserved for GENUINE ad-stated conditions (source_kind='scrape') — see en block.
    desk_terms_from_owner_ad: 'პირობები წყაროდან',
    desk_coop_commission_section: 'საკომისიოს პირობები',
    desk_coop_commission_type_label: 'საკომისიოს ტიპი',
    desk_coop_type_percent: 'პროცენტი ფასიდან',
    desk_coop_type_net_amount: 'მესაკუთრის სასურველი წმინდა თანხა (სხვაობა რჩება აგენტს)',
    desk_coop_type_net_commission: 'ფიქსირებული საკომისიო (ფასის მიუხედავად)',
    desk_coop_type_fixed_net: 'ფიქსირებული თანხა (ფასის მიუხედავად)',
    desk_coop_type_standard: 'სტანდარტული (1 თვე / წელი, პროპორციულად)',
    desk_coop_type_coop_split: 'საკომისიოს გაყოფა (თანამშრომლობა)',
    desk_coop_split_pct_label: 'საკომისიოს წილი %',
    desk_coop_split_note: 'წილი საკომისიოდან და არა ფასიდან — თანხა ვერ დაითვლება, სანამ თავად საკომისიო არ არის ცნობილი.',
    desk_coop_owner_net_label: 'მესაკუთრის სასურველი წმინდა თანხა',
    desk_coop_rate_check: 'გაყიდვის ფასის {p}% — ეს საკომისიოა, თუ საკომისიოს წილი?',
    desk_coop_rate_check_is_split: 'ეს წილია',
    desk_coop_net_commission_label: 'საკომისიოს თანხა',
    desk_coop_standard_note: 'სტანდარტული: აგენტი იღებს ერთი თვის ქირის 100%-ს წლიური იჯარისთვის; მოკლე ვადაზე — პროპორციულად (მაგ. 6 თვეზე — ერთი თვის 50%).',
    desk_coop_negotiated_price: 'შეთანხმებული ფასი',
    desk_coop_price_from_ad_hover: 'აღებულია განცხადებიდან ({p})',
    desk_coop_commission_pct: 'საკომისიო %',
    desk_coop_pct_out_of_range: 'საკომისიო % უნდა იყოს 0-დან 100-მდე. ფიქსირებული თანხის ჩასაწერად შეცვალეთ საკომისიოს ტიპი ნეტო თანხაზე.',
    desk_coop_commission_amount: '≈ საკომისიოს თანხა',
    desk_draft_restored: 'აღდგა შეუნახავი ცვლილებები',
    desk_draft_discard: 'გაუქმება',
    desk_share_draft_restored: 'თქვენი წინა მნიშვნელობები',
    desk_share_price_from_coop: 'შეთანხმებული ფასიდან',
    desk_share_price_converted: 'კონვერტირებულია {c}-დან',
    desk_deal_open_source: 'განცხადების გახსნა ({h})',
    desk_share_built_from_origin: 'აგებულია ორიგინალი განცხადებიდან — გახსენით',
    desk_share_reload: 'განცხადების გადატვირთვა',
    desk_adhist_title: 'როგორ იყო გაზიარებული',
    desk_adhist_none: 'ამ ობიექტის გაზიარება ჯერ არ დაფიქსირებულა.',
    desk_adhist_not_captured: 'ფასი არ დაფიქსირდა',
    desk_adhist_now: 'ახლა {p}',
    desk_adhist_note: 'ნაჩვენებია ფასი, რომელიც განცხადების გამოქვეყნებისას ჩაიწერა. „ახლა“ ჩნდება, თუ ამ განცხადების ფასი მას შემდეგ შეიცვალა.',
    desk_share_ext_stale: 'senTavus-ის გაფართოება გადაიტვირთა და ამ ტაბმა კავშირი დაკარგა. განაახლეთ გვერდი და ისევ იმუშავებს.',
    desk_share_ext_reload: 'გვერდის განახლება',
    desk_share_browsers: 'თქვენი ბრაუზერები',
    desk_share_browsers_none: 'senTavus-ის გაფართოებით ჯერ არცერთი ბრაუზერი არ დაკავშირებულა. გახსენით Chrome გაფართოებით, თქვენი ანგარიშით.',
    desk_share_browser_online: 'ონლაინ',
    desk_share_browser_offline: 'ბოლოს',
    desk_share_portal_logged_out: 'არ არის შესული',
    desk_share_portal_unknown: 'უცნობია',
    desk_share_browser_use: 'ამ ბრაუზერიდან გაზიარება',
    desk_share_need_title: '{p}-ზე გასაზიარებლად საჭიროა:',
    desk_share_upfront_title: 'გაზიარებამდე შეავსეთ — ამის გარეშე პორტალი განცხადებას არ მიიღებს:',
    desk_share_upfront_done: 'პორტალებისთვის საჭირო ყველაფერი შევსებულია.',
    desk_share_need_continue: 'გაგრძელება',
    desk_share_need_photos: 'ფოტოები არ არის — ჯერ დაამატეთ ფოტოები განცხადებას.',
    desk_share_f_street: 'ქუჩა',
    desk_share_f_street_number: 'ქუჩის ნომერი',
    desk_share_f_building_status: 'სტატუსი',
    desk_share_f_condition: 'მდგომარეობა',
    desk_share_f_land_status: 'მიწის სტატუსი',
    desk_share_f_description: 'აღწერა',
    desk_share_job_queued: 'გაიგზავნა {d}-ზე — 30 წამში დაიწყება…',
    desk_share_job_claimed: '{d}-მა მიიღო…',
    desk_share_job_running: '{d} ავსებს ფორმას…',
    desk_share_job_filled: '{d}-ზე ფორმა შევსებულია.',
    desk_share_job_error: '{d}-ზე ვერ შესრულდა: {m}',
    desk_share_job_expired: 'მოწყობილობამ დავალება ვერ მიიღო. მოწყობილობა: {d}. გადაამოწმეთ, გახსნილია თუ არა მასზე Chrome.',
    desk_share_job_publishing: '{d} აჭერს გამოქვეყნებას…',
    desk_share_job_paying: '{d} იხდის ბალანსიდან…',
    desk_share_job_published: 'გამოქვეყნდა ✓',
    desk_share_job_open: 'განცხადების გახსნა',
    desk_share_step_sent: 'გაიგზავნა',
    desk_share_step_picked: 'მიიღო',
    desk_share_step_filling: 'ივსება',
    desk_share_step_publish: 'ქვეყნდება',
    desk_share_step_paying: 'გადახდა',
    desk_share_step_done: 'გამოქვეყნდა',
    desk_share_wait_for: 'დაელოდეთ, სანამ {p} დასრულდება ან შეწყდება. კომპიუტერზე ერთდროულად მხოლოდ ერთი პლატფორმის ფორმა ივსება და მხოლოდ ღია ჩანართში მუშაობს.',
    desk_share_busy: 'იმ კომპიუტერზე სხვა გაზიარება ჯერ მიმდინარეობს. დაელოდეთ მის დასრულებას ან შეწყვეტას.',
    desk_share_job_no_update: 'კომპიუტერიდან 15 წუთია ახალი ინფორმაცია არ მოსულა — შეამოწმე ეს ტაბი კომპიუტერზე.',
    sched_title: 'გაზიარების დაგეგმვა',
    sched_platforms: 'პლატფორმები',
    sched_confirm_account: 'ეს ჩემი ანგარიშია',
    sched_not_logged: 'ამ კომპიუტერზე შესული არ არის — ჯერ იქ შედით',
    sched_days: 'დღეები',
    sched_start: 'დაწყება',
    sched_every: 'ყოველ',
    sched_days_unit: 'დღეში',
    sched_times: 'ჯერ',
    sched_end: 'დასრულება',
    sched_time: 'დრო',
    sched_window: 'შემთხვევითი დრო შუალედში',
    sched_exact: 'ზუსტი დრო',
    sched_repeat: 'განმეორებითი გაზიარება',
    sched_repeat_new: 'ახალი განცხადება',
    sched_repeat_refresh: 'არსებულის განახლება (მალე)',
    sched_save: 'დაგეგმვა',
    sched_saved: 'დაიგეგმა ✓',
    sched_need_browser: 'ჯერ ზემოთ აირჩიეთ ონლაინ კომპიუტერი.',
    sched_plans: 'დაგეგმილი გაზიარებები',
    sched_cancel: 'გაუქმება',
    sched_active: 'აქტიური',
    sched_answer_first: 'ჯერ უპასუხეთ ზემოთ მოცემულ კითხვებს: გაშვებისას კომპიუტერთან არავინ იქნება.',
    sched_questions: 'პორტალს გამოსაქვეყნებლად ეს სჭირდება. უპასუხეთ ახლავე:',
    sched_pay_q: 'თუ პორტალი საფასურს მოითხოვს (ss 0.10₾; myhome თვეში 3 უფასოს შემდეგ):',
    sched_pay_yes: 'გადავიხადო პორტალის ბალანსიდან',
    sched_pay_no: 'არ გადავიხადო: გაზიარება იქ გაჩერდება და კომპიუტერზე დავასრულებ',
    sched_state_scheduled: 'ელოდება',
    sched_state_queued: 'მიმდინარეობს',
    sched_state_published: 'გამოქვეყნდა',
    sched_state_error: 'ვერ შესრულდა',
    sched_state_missed: 'გამოტოვდა',
    sched_state_cancelled: 'გაუქმდა',
    notif_share_run_published: 'დაგეგმილი გაზიარება გამოქვეყნდა',
    notif_client_waiting: 'კლიენტი გელოდებათ WhatsApp-ზე',
    notif_client_waiting_min: 'პასუხს {n} წუთია ელოდება',
    notif_client_waiting_hours: 'პასუხს {n} საათია ელოდება',
    notif_client_waiting_suggested: 'შემოთავაზებული პასუხი - გაგზავნამდე გადაამოწმეთ',
    notif_client_waiting_copy: 'პასუხის კოპირება',
    notif_client_waiting_copied: 'დაკოპირდა',
    notif_client_waiting_open: 'ჩატის გახსნა',
    notif_share_run_error: 'დაგეგმილი გაზიარება ვერ შესრულდა',
    notif_share_run_missed: 'დაგეგმილი გაზიარება გამოტოვდა',
    desk_share_ext_waking: 'senTavus-ის გაფართოებამ ჯერ არ უპასუხა — შესაძლოა ჯერ იტვირთება. წამში ისევ დააჭირე პლატფორმას.',
    desk_share_confirming: 'მოწმდება აქტუალურობა — გაზიარება წამში გაიხსნება.',
    desk_share_refreshed: 'განცხადება შეიცვალა ბოლო გახსნის შემდეგ. თქვენი შესწორებული ველები შენარჩუნდა.',
    desk_share_unconfirmed: 'ნაჩვენებია ბოლოს ჩატვირთული ასლი — აქტუალურობა ვერ დადასტურდა, ამიტომ გაზიარება შეჩერებულია.',
    desk_share_retry: 'ხელახლა ცდა',
    desk_share_no_fx: 'კურსი მიუწვდომელია — ფასი რჩება საწყის ვალუტაში.',
    desk_share_price_edited: 'შეცვლილია ამ გაზიარებისთვის',
    desk_share_price_reset: 'დაბრუნება',
    desk_share_price_differs: 'განსხვავდება შეთანხმებული ფასისგან',
    desk_share_coop_autosaved: 'თანამშრომლობის პირობები შეინახა გაზიარებამდე.',
    desk_coop_autosave_saving: 'ინახება…',
    desk_coop_autosave_saved: '✓ ავტომატურად შენახულია',
    desk_coop_autosave_hint: 'ცვლილებები ავტომატურად ინახება',
    desk_coop_autosave_retry: 'შენახვა ვერ მოხერხდა — ხელახლა ცდა',
    desk_share_save_coop_first: 'თანამშრომლობის შეუნახავი პირობებია: შენახვა თანამშრომლობაში გაწევრიანებასაც ნიშნავს — ჯერ დააჭირეთ შენახვას თანამშრომლობის ჩანართზე.',
    desk_coop_exclusive: 'ექსკლუზივი',
    desk_coop_no_republish: 'არ გააზიაროთ',
    desk_coop_no_cooperation: 'არ თანამშრომლობს',
    desk_coop_no_cooperation_hint: 'მესაკუთრე საერთოდ არ თანამშრომლობს სააგენტოებთან.',
    desk_coop_no_republish_hint: 'მესაკუთრე კრძალავს განცხადების კოპირებას ან ხელახლა გამოქვეყნებას.',
    desk_coop_locked: 'შეუერთდი პირობების სანახავად',
    desk_coop_pick_participant: 'ჯერ აირჩიეთ, ვისი პირობები რედაქტირდება — ეს ციფრები კონკრეტული აგენტის მოლაპარაკებას ეკუთვნის.',
    desk_coop_locked_contribute: 'ჩაწერეთ თქვენი პირობები დანარჩენის სანახავად',
    desk_coop_valid_until: 'ვადა',
    desk_coop_pets_label: 'ცხოველები',
    desk_coop_pets_unspecified: 'არ არის მითითებული',
    desk_coop_pets_not_allowed: 'აკრძალულია',
    desk_coop_pets_small_dogs_ok: 'პატარა ძაღლები შეიძლება',
    desk_coop_pets_allowed: 'ნებადართულია',
    desk_coop_tenant_pref: 'დამქირავებლის პრეფერენცია',
    desk_coop_tenant_pref_ph: 'მაგ. ოჯახი, სტუდენტების გარეშე',
    desk_coop_notes: 'შენიშვნები',
    desk_coop_participants_section: 'მონაწილეები (ჩვენი აგენტები)',
    desk_dedup_kind_properties: 'ქონება',
    desk_dedup_kind_people: 'ხალხი',
    desk_party_dedup_intro: 'ერთი ადამიანი ორჯერაა ჩაწერილი? დაადასტურეთ დასაკავშირებლად — არაფერი იშლება და დაკავშირების გაუქმება შეიძლება.',
    desk_party_dedup_pending: '{n} შესაძლო დუბლიკატი წყვილი შესამოწმებლად',
    desk_party_dedup_same_name: 'იდენტური სახელი',
    desk_party_dedup_empty: 'შესამოწმებელი არაფერია — გადასამოწმებელი დუბლიკატები არ არის.',
    desk_phone_header_open: 'პირის მონაცემების ნახვა →',
    desk_phone_header_agent: 'აგენტი',
    desk_phone_header_no_activity: 'აქტივობა არ ფიქსირდება',
    desk_phone_header_dup_records: 'შესაძლო დუბლიკატი ჩანაწერი',
    desk_party_combined: 'გაერთიანებულია {n} ჩანაწერიდან',
    desk_party_combined_hint: 'ეს ადამიანი დადასტურდა როგორც იგივე {n} დუბლიკატ ჩანაწერში; მისი მოთხოვნები და განცხადებები ერთად ჩანს.',
    desk_party_dup_title: 'შესაძლო დუბლიკატები — იგივე ადამიანია?',
    desk_party_dup_cluster: 'დადასტურებული იგივე ადამიანი (ერთად ჩანს):',
    desk_party_dup_same_phone: 'იგივე ნომერი',
    desk_party_dup_same_email: 'იგივე ელფოსტა',
    desk_party_existing_found: 'ამ ნომრით/ელფოსტით ბაზაში უკვე არის:',
    desk_party_use: 'ეს არის →',
    desk_failed_entry_title: 'ეს ჩანაწერი არ შეინახა',
    desk_failed_entry_error: 'ვერ შეინახა: {reason}',
    desk_failed_entry_checking: 'მოწმდება, ეს ადამიანი უკვე რეგისტრირებულია თუ არა…',
    desk_failed_entry_exists: 'ეს ადამიანი ბაზაში უკვე არის — ხელახლა ნუ დაამატებთ:',
    desk_failed_entry_open: 'კლიენტის გახსნა',
    desk_failed_entry_archive: 'ჩანაწერის დაარქივება',
    desk_failed_entry_add_anyway: 'სხვა ადამიანია იმავე ნომრით — მაინც დამატება',
    desk_failed_entry_done: 'დასრულდა — ჩანაწერი სიიდან მოიხსნა.',
    desk_party_using_existing: 'დაუკავშირდა არსებულ კლიენტს — დუბლიკატი არ შეიქმნება.',
    desk_party_create_new: 'მაინც ახლის შექმნა',
    desk_coop_editing_terms_of: 'ასწორებთ ამ აგენტის პირობებს: {name} — თითოეული აგენტი საკუთარ მოლაპარაკებას აფიქსირებს',
    desk_coop_editing_terms_own: 'წერთ საკუთარ პირობებს მესაკუთრესთან — თითოეული აგენტი საკუთარ მოლაპარაკებას აფიქსირებს',
    desk_coop_save_joins: 'ჯერ არ ხართ ამ თანამშრომლობაში — საკუთარი პირობების ჩაწერა და შენახვა ავტომატურად ჩაგრთავთ. დახურვა არაფერს ცვლის.',
    desk_coop_record_own: 'ჩაწერეთ თქვენი პირობები',
    desk_deal_editor_title: 'გარიგების ჩანაწერები',
    desk_deal_tab_terms: 'მესაკუთრის პირობები',
    // ქონების შეფასება — შეფასების ტაბი (1065, 2026-09-12)
    desk_deal_tab_valuation: 'ქონების შეფასება',
    desk_val_loading: 'შედარება ითვლება…',
    desk_val_error: 'შეფასება ვერ დაითვალა',
    desk_val_no_unit: 'ამ ქონებაზე აქტიური განცხადება არ არის, შესადარებელი არაფერია',
    desk_val_open: 'ქონების შეფასება',
    desk_val_subject: 'ობიექტი',
    desk_val_coverage: 'ცნობილია {k} მახასიათებელი {n}-დან ({p}%).',
    desk_val_unknown: 'უცნობია',
    desk_val_stats: 'სტატისტიკა',
    desk_val_stats_hint: 'ყველა მსგავსი განცხადება გადათვლილია ამ ბინის მდგომარეობაზე, კორპუსის კლასზე და სართულის პოზიციაზე. პროცენტილი = მსგავსთა წილი, რომელიც 1 მ²-ზე ნაკლებს ითხოვს.',
    desk_val_col_group: 'ჯგუფი',
    desk_val_col_n: 'n',
    desk_val_col_median: 'მედიანა $/მ²',
    desk_val_col_iqr: 'p25–p75',
    desk_val_col_pct: 'ნაკლებს ითხოვს',
    desk_val_col_dev: 'მედიანასთან',
    desk_val_too_few: 'ცოტაა შესადარებლად',
    desk_val_traits: 'ნიშნები',
    desk_val_traits_hint: 'იშვიათობა {n} მსგავსში ({label}), რომლებზეც პასუხი ვიცით. დაფარვა ამბობს, რამდენზე ვიცით — დანარჩენი უცნობია და არა „არ აქვს".',
    desk_val_trait_line: 'ცნობილთა {r}%-ს აქვს · ვიცით {c}%-ზე',
    desk_val_trait_common: 'ჯგუფის საერთო ნიშანი (ყველა ცნობილ მსგავსს აქვს)',
    desk_val_trait_uncovered: 'ჯერ არ შედარდება (დაფარვა {p}%-ზე ნაკლები)',
    desk_val_alike: 'ყველაზე მსგავსი',
    desk_val_alike_hint: 'ამ ბინასთან ყველაზე ახლოს მდგომი აქტიური განცხადებები; ფასის შეფასება ადარებს მოთხოვნილ 1 მ²-ის ფასს. გახსენით და აჩვენეთ მესაკუთრეს.',
    desk_val_cheaper: 'იაფია', desk_val_similar: 'იგივე ფასია', desk_val_dearer: 'ძვირია',
    desk_val_cond_better: 'უკეთესი მდგომარეობა', desk_val_cond_worse: 'უარესი მდგომარეობა', desk_val_cond_same: 'იგივე მდგომარეობა', desk_val_cond_different: 'სხვა მდგომარეობა',
    desk_val_open_card: 'ბარათი',
    desk_val_questions: 'კითხვები ზარისთვის',
    desk_val_questions_hint: 'უპასუხეთ საუბრისას; ქვემოთ რიცხვები თქვენი პასუხით გადაითვლება. თუ არცერთი არ ჯდება — დაწერეთ კომენტარი.',
    // მისამართის კითხვა და კომენტარები (1066, 2026-09-12)
    desk_val_addr_stored: 'შენახული',
    desk_val_addr_text: 'განცხადების ტექსტი ამბობს',
    desk_val_addr_this_one: 'ეს არის სწორი',
    desk_val_addr_chose: 'აგენტმა აირჩია',
    desk_val_addr_street: 'ქუჩა',
    desk_val_addr_house: 'ნომერი',
    desk_val_year: 'წელი',
    desk_val_addr_city: 'ქალაქი',
    desk_val_addr_district: 'რაიონი',
    desk_val_addr_sub: 'უბანი',
    desk_val_addr_korpus: 'კორპუსი',
    desk_val_addr_pin_basis: 'რუკაზე პინი',
    desk_val_addr_map: 'პინი რუკაზე',
    desk_val_addr_map_hide: 'რუკის დამალვა',
    desk_val_addr_map_hint: 'დააჭირეთ რუკას ან გადაათრიეთ პინი კორპუსზე',
    desk_val_addr_pin_save: 'პინის შენახვა',
    desk_val_addr_q_street: 'რომელ ქუჩაზეა?',
    desk_val_addr_q_street_in: 'რომელ ქუჩაზეა? ({district})',
    desk_val_addr_q_number: '{street} — სახლის ნომერი?',
    desk_val_addr_q_korpus: 'რომელი კორპუსი?',
    desk_val_addr_q_sub: 'რომელ უბანშია?',
    desk_val_addr_q_district: 'რომელ რაიონშია: {a} თუ {b}?',
    desk_val_addr_q_which: 'რომელი მისამართია სწორი?',
    desk_val_addr_unknown: 'მეპატრონემ არ იცის',
    desk_val_comment: 'კომენტარი',
    desk_val_comment_ph: 'რა თქვა მესაკუთრემ — თქვენი სიტყვებით',
    desk_val_latin_to_georgian: 'ქართული ასოებით',
    desk_val_comment_flat: 'კომენტარი ბინაზე',
    desk_val_yes: 'კი', desk_val_no: 'არა', desk_val_cannot_tell: 'ვერ ვიგებ',
    desk_val_save: 'შენახვა',
    desk_val_saved: 'ჩაიწერა — რიცხვები განახლდა',
    desk_val_nothing_to_ask: 'ღია კითხვა არ არის — ყველა ღერძი, რომლითაც ვაფასებთ, ცნობილია.',
    desk_val_metro: '{m} მ მეტრო „{name}"-მდე',
    desk_val_rule: 'დეტერმინისტული · მოდელის გარეშე · {v}',
    desk_val_bd_title: 'ფასის გაანგარიშება',
    desk_val_adj_title: 'აგენტის კორექტირება — მეორადი განათება',
    desk_val_adj_hint: 'საძინებლები საკუთარი გარე ფანჯრის გარეშე მოდელში ჯერ არ ფასდება. თქვენი კორექტირება ზემოთ მოცემული შეფასებისგან ცალკე ინახება და მას არ ცვლის.',
    desk_val_adj_percent: 'კორექტირება %',
    desk_val_adj_amount: 'ან თანხა',
    desk_val_adj_beds: 'საძინებლების რაოდენობა',
    desk_val_adj_beds_n: '{n} საძინებელი',
    desk_val_adj_note: 'შენიშვნა',
    desk_val_adj_save: 'კორექტირების შენახვა',
    desk_val_adj_saved: 'შენახულია.',
    desk_val_adj_failed: 'შენახვა ვერ მოხერხდა —',
    desk_val_bd_hint: 'ყველაზე ახლო მსგავსი ბინების 1 მ²-ის ფასის მედიანა (სანდო ფასით), გადათვლილი გარემონტებულ, შუა სართულის ბინაზე; შემდეგ ამ ბინის მდგომარეობა და სართული. ყოველ ნაბიჯს ჩანს, საიდან მოდის რიცხვი.',
    desk_val_bd_base: 'ბაზა · {level} · {n} მსგავსი ბინა',
    desk_val_bd_reference: 'გარემონტებული, შუა სართული',
    desk_val_bd_level_building: 'იგივე კორპუსი',
    desk_val_bd_level_complex: 'იგივე კომპლექსი',
    desk_val_bd_level_street: 'იგივე ქუჩა',
    desk_val_bd_level_area: 'იგივე მიკრორაიონი / უბანი',
    desk_val_bd_level_district: 'იგივე უბანი',
    desk_val_bd_level_wider_area: 'რაიონი',
    desk_val_bd_level_global: 'ქალაქი',
    desk_val_bd_condition: 'მდგომარეობა',
    desk_val_bd_floor: 'სართული',
    desk_val_bd_cls_frame: 'კარკასი',
    desk_val_bd_cls_needs_renovation: 'სარემონტო',
    desk_val_bd_cls_in_progress: 'მიმდინარე რემონტი',
    desk_val_bd_cls_old_reno: 'ძველი რემონტი',
    desk_val_bd_cls_ruin: 'ავარიული / დემონტაჟირებული',
    desk_val_bd_cls_premium: 'პრემიუმ რემონტი',
    desk_val_bd_cls_basement: 'სარდაფი',
    desk_val_bd_cls_first: 'პირველი სართული',
    desk_val_bd_cls_second: 'მეორე სართული',
    desk_val_bd_cls_top: 'ბოლო სართული',
    desk_val_bd_step_meta: 'ინდექსი: {level} · {n} დაკვირვება · სანდოობა {c}',
    desk_val_bd_estimate: 'შეფასება',
    desk_val_bd_range: 'დიაპაზონი',
    desk_val_bd_ask: 'მოთხოვნილი ფასი',
    // მარტივი შეფასების ტაბი (მფლობელი 2026-09-23: „უნდა იყოს მარტივი")
    desk_val_est_title: 'სავარაუდო ფასი',
    desk_val_dearer_by: '{p}% ძვირია',
    desk_val_cheaper_by: '{p}% იაფია',
    desk_val_in_line: 'ფასი ნორმაშია',
    desk_val_ask_owner: 'ჰკითხეთ მეპატრონეს',
    desk_val_fill: 'შევსება',
    desk_val_fill_hide: 'დამალვა',
    desk_val_similar_title: 'მსგავსი ბინები',
    desk_val_show_all: 'ყველა ({n})',
    desk_val_details: 'დეტალები — როგორ დაითვალა',
    desk_val_mkt_title: 'ბაზარი',
    desk_val_mkt_district: 'ამ რაიონში',
    desk_val_mkt_hood: 'ამ უბანში',
    desk_val_mkt_rooms: 'ოთ.',
    desk_val_mkt_n: '{n} მსგავსი',
    desk_val_mkt_median: 'მედიანა $/მ²',
    desk_val_mkt_range: 'ტიპური ფასი',
    desk_val_mkt_listed: 'ახლა ბაზარზეა',
    desk_val_mkt_days: 'დღე ბაზარზე',
    desk_val_mkt_low: 'მსგავსი ცოტაა — მიახლოებითი რიცხვია',
    desk_val_mkt_none: 'ამ უბანში ჯერ საკმარისი მონაცემი არ არის',
    desk_val_unsupported_kind: 'ამ ტიპის ქონებისთვის შეფასება არ ხდება',
    desk_val_need_size: 'შეფასებისთვის მიუთითეთ ფართობი.',
    desk_val_unvalidated: 'ამ ტიპის ფასები ჯერ არ მოწმდება — მიახლოებითია',
    desk_val_bd_unknown: 'უცნობია — დიაპაზონი გაფართოვდა',
    desk_val_bd_unknown_condition: 'მდგომარეობა',
    desk_val_bd_unknown_floor: 'სართული',
    desk_val_bd_none: 'სანდო ფასით მსგავსი ბინა საკმარისი არ არის',
    // Reshare tab (B244)
    desk_deal_tab_share: 'გაზიარება',
    desk_share_loading_hint: 'მზადდება მონაცემები და AI-ის პასუხები. პირველად გახსნას რამდენიმე წამი სჭირდება.',
    desk_share_account_mismatch: 'გადაზიარება შეჩერებულია — გაფართოებაში სხვა აგენტია შესული.',
    desk_share_account_desk: 'Desk',
    desk_share_account_ext: 'გაფართოება',
    desk_share_account_fix: 'განცხადების ავტორი ხდება ის, ვინც გაფართოებაშია შესული — ობიექტი და მასთან ერთად მფლობელობა და რეიტინგის კრედიტი იმ ანგარიშზე წავიდოდა, თქვენზე არა. შედით გაფართოებაში თქვენი ანგარიშით და ხელახლა გახსენით ეს ჩანართი.',
    desk_share_intro: 'ეს არის ის, რაც გადაეცემა გაფართოებას. პლატფორმები საკუთარ ფორმატირებას ამატებენ, ამიტომ საბოლოო განცხადება შეიძლება განსხვავდებოდეს. დატოვეთ როგორც არის ორიგინალის დასაპოსტად, ან შეცვალეთ ნებისმიერი მნიშვნელობა — გაზიარება აიღებს იმას, რასაც აქ ხედავთ.',
    desk_share_description: 'განცხადების ტექსტი',
    desk_share_ai_cached: 'AI (უკვე გაანალიზებული)',
    desk_share_ai_fresh: 'AI-თი გაუმჯობესებული',
    desk_share_prov_own: 'თქვენი რედაქტირება',
    desk_share_prov_ai: 'AI-მ შეავსო — გადაამოწმეთ',
    desk_share_fb_by_orpheus: 'დაწერა ორფევსმა',
    desk_share_fb_by_orpheus_note: 'პოსტის ტექსტი აწყობილია ჩაწერილი ფაქტებიდან — გამოქვეყნებამდე შეასწორეთ, თუ რამე არასწორია.',
    desk_share_prov_origin: 'ორიგინალი განცხადებიდან',
    desk_share_f_bedrooms: 'საძინებელი',
    desk_share_f_bathrooms: 'სველი წერტილი',
    desk_share_f_land_area: 'ეზო მ²',
    desk_share_f_floor: 'სართული',
    desk_share_f_floors_total: 'სულ სართული',
    desk_share_f_ceiling: 'ჭერის სიმაღლე',
    desk_share_f_deal: 'გარიგება',
    desk_share_f_kind: 'ტიპი',
    desk_share_f_city: 'ქალაქი',
    desk_share_block_unsupported: '{p}-ზე ამ ტიპის ქონებაზე ეს გარიგება არ არის',
    desk_share_block_no_pledge: 'გირაოს განცხადებებს არ ვაზიარებთ',
    desk_share_block_no_republish: 'მესაკუთრემ გვთხოვა განცხადება არ დავდოთ — არსად ვაზიარებთ',
    desk_share_block_not_automatable: '{p}: ამ გარიგებას სჭირდება ველები, რომლებსაც გაფართოება ჯერ ვერ ავსებს; დაასრულეთ კომპიუტერზე',
    desk_share_block_few_photos: '{p}-ს გასაქირავებელ ბინაზე მინიმუმ 3 ფოტო სჭირდება',
    desk_share_block_price_phone: '{p} არ იღებს ფასს, რომელიც ტელეფონის ნომერს ჰგავს',
    desk_share_block_price_low: '{p} არ იღებს ასე დაბალ ფასს',
    desk_share_block_street_number_format: '{p} იღებს მაქს. 3 ციფრს და 1 ასოს სახლის ნომერში',
    desk_share_block_floor_above_total: 'სართული სართულების რაოდენობაზე მეტია',
    desk_share_f_cadastral: 'საკადასტრო',
    desk_share_photos: 'ფოტოები',
    desk_share_photos_count: 'გაიგზავნება {n} ფოტო',
    desk_share_photos_watermarked: '{n} დამღიანი ორიგინალი გამოტოვებულია — მათი დაპოსტვა სხვა პლატფორმის ლოგოს გამოაქვეყნებდა თქვენს განცხადებაზე.',
    desk_share_photos_thumbs: 'ამ განცხადებისთვის მხოლოდ მინიატურებია ხელმისაწვდომი — ყველა ორიგინალი დამღიანია.',
    desk_share_photos_none: 'გამოსადეგი ფოტო არ არის. პლატფორმები უფოტოო განცხადებას არ მიიღებენ.',
    desk_share_photos_hint: 'დააჭირეთ ფოტოს, რომ ამ გაზიარებიდან გამოირიცხოს; ხელახლა დაჭერით დაბრუნდება.',
    desk_share_photos_reorder: 'ფოტოების რიგი',
    desk_share_photos_reorder_done: 'მზადაა',
    desk_share_photos_reorder_reset: 'თავდაპირველი რიგი',
    desk_share_photos_reorder_hint: 'ფოტოს გადასაადგილებლად შეეხეთ მის მარცხენა ან მარჯვენა ნახევარს; ★ მას მთავარ ფოტოდ აქცევს. korter-ს პირველად ბინის ინტერიერი სჭირდება.',
    desk_share_photos_main: 'მთავარი',
    desk_share_photos_make_main: 'მთავარ ფოტოდ',
    desk_share_photos_left: 'მარცხნივ',
    desk_share_photos_right: 'მარჯვნივ',
    desk_share_photos_excluded: '{n} გამორიცხულია თქვენ მიერ',
    desk_share_no_extension: 'senTavus-ის გაფართოება არ პასუხობს ამ ბრაუზერში. გაზიარება მასზე გადის — დააინსტალირეთ ან ჩართეთ და გადატვირთეთ გვერდი.',
    desk_share_fb_open: 'Facebook-ზე გახსნა ↗',
    desk_share_fb_opening: 'მზადდება…',
    desk_share_fb_needs_update: 'თქვენი senTavus-ის გაფართოება ძველია და Facebook-ს ვერ გაგიხსნით — დააკოპირეთ ზემოთ მოცემული პოსტი და თავად ჩასვით, ან განაახლეთ გაფართოება, რომ ფოტოებიც ავტომატურად მომზადდეს.',
    desk_share_fb_opened: 'გაიხსნა — {n} ფოტო მომზადდა. გახსენით Create Post თქვენს ჯგუფში; ტექსტი და ფოტოები ავტომატურად ჩაისმება. Post-ს თქვენ აჭერთ.',
    desk_share_fb_opt_contact: 'კონტაქტის გაზიარება',
    desk_share_fb_opt_price: 'ფასის გაზიარება',
    desk_share_fb_opt_exact: 'ზუსტი მდებარეობა (ქუჩის/კორპუსის ნომერი)',
    desk_share_fb_contact_ph: 'ტელეფონი პოსტისთვის — დამახსოვრდება',
    desk_share_fb_compose: 'პოსტის შედგენა',
    desk_share_fb_change_opts: 'პარამეტრების შეცვლა',
    desk_share_fb_generating: 'ორფევსი წერს…',
    desk_share_fb_copy: 'პოსტის კოპირება',
    desk_share_fb_copied: 'დაკოპირდა ✓',
    desk_share_one_at_a_time: 'რამდენიმე პლატფორმის დაწყება ერთდროულად შეიძლება — თითო ხსნის თავის ტაბს და ავსებს ფორმას თქვენს თვალწინ. ერთი და იგივე პლატფორმა ორჯერ ერთდროულად ვერ გაეშვება.',
    desk_lineage_status_section: 'სტატუსი',
    desk_status_deal_closed_line: 'ამ ობიექტზე გარიგება ჩაწერილია დახურულად.',
    // ხელმისაწვდომობის ისტორია (0304/0305) — დახურვა თარიღიანი ფაქტია, არა მუდმივი სტატუსი.
    desk_lifecycle_active: 'ბაზარზეა',
    desk_lifecycle_withdrawn: 'მოხსნილია',
    desk_lifecycle_reserved: 'დაჯავშნილია',
    desk_lifecycle_negotiating: 'მოლაპარაკებაში',
    desk_lifecycle_paused: 'შეჩერებულია',
    desk_lifecycle_src_scrape: 'განცხადებიდან',
    desk_lifecycle_src_scrape_reshare: 'განცხადებიდან',
    desk_lifecycle_src_agent: 'აგენტი',
    desk_lifecycle_src_owner: 'მესაკუთრე',
    desk_lifecycle_src_admin: 'ადმინისტრატორი',
    desk_lifecycle_src_registry: 'რეესტრი',
    desk_lifecycle_src_ai: 'ავტომატური',
    desk_lifecycle_src_fill_form: 'მესაკუთრის ფორმა',
    desk_lifecycle_retracted: 'გაუქმდა',
    desk_lifecycle_more: 'უფრო ადრე ({n})',
    desk_lifecycle_less: 'ნაკლების ჩვენება',
    desk_lifecycle_contested: 'ჩაწერილია დახურულად, მაგრამ განცხადება ჯერ კიდევ აქტიურია პლატფორმაზე — გადაამოწმეთ.',
    desk_reopen_short: 'ისევ ბაზარზეა',
    desk_reopen_hint: 'დახურვის გაუქმება — გარიგება ჩაიშალა ან შეცდომით ჩაიწერა',
    desk_reopen_title: 'რატომ დაბრუნდა ბაზარზე?',
    desk_reopen_deal_fell_through: 'გარიგება ჩაიშალა',
    desk_reopen_deal_fell_through_hint: 'შედგა და შემდეგ ჩაიშალა — მოიჯარემ ან მყიდველმა უარი თქვა',
    desk_reopen_deal_fell_through_effect: 'დახურვა რჩება ისტორიაში და ჩათვლა თქვენთან რჩება. ობიექტი ბრუნდება ნაკადში, თანამშრომლობა კი დასრულებული რჩება — თუ ამ მესაკუთრესთან აგრძელებთ, პირობები თავიდან შეათანხმეთ.',
    desk_reopen_recorded_by_mistake: 'შეცდომით ჩავწერე',
    desk_reopen_recorded_by_mistake_hint: 'გარიგება არ შემდგარა — მაგალითად, არასწორი განცხადება მოინიშნა',
    desk_reopen_recorded_by_mistake_effect: 'დახურვა აღინიშნება გაუქმებულად და ჩათვლა უქმდება. ობიექტი ბრუნდება ნაკადში და მასთან ერთად აღდგება ამ დახურვით დასრულებული თანამშრომლობა და ლიდი.',
    desk_reopen_confirm: 'ისევ ბაზარზეა',
    desk_reopen_not_yours: 'დახურვის გაუქმება მხოლოდ იმ აგენტს შეუძლია, ვინც ის ჩაწერა.',
    desk_reopen_closed_by: 'ჩამწერი: {who} — ბაზარზე დაბრუნება მხოლოდ მას შეუძლია.',
    desk_status_no_reports: 'პრობლემა არ დაფიქსირებულა — ითვლება აქტიურად',
    desk_market_report_note_ph: 'კომენტარი (არასავალდებულო) — დეტალი, რაც უნდა დაფიქსირდეს…',
    desk_status_change: 'სტატუსის განახლება',
    desk_status_available_again: 'ობიექტი კვლავ ხელმისაწვდომია — ჩემი ჩანაწერის მოხსნა',
    deal_close_title: 'გარიგების დახურვა',
    deal_close_final_price: 'საბოლოო ფასი',
    deal_close_final_commission: 'საბოლოო საკომისიო (თანხა)',
    // 0449 — ზემოთ მთელი საკომისიოა; აქ ჩანს, რამდენი რჩება ჩვენთან.
    deal_close_cobroke_title: 'გაყოფა სხვა სააგენტოსთან',
    deal_close_cobroke_note: 'ჩვენს შემოსავლად ჩაიწერება: {net}. სრული საკომისიო რჩება გარიგების ჩანაწერში.',
    deal_close_inherited_hint: 'შევსებულია თქვენს პირობებში მითითებული მოთხოვნილი ფასიდან — შეასწორეთ რეალურად შეთანხმებულზე და დაადასტურეთ',
    deal_close_no_own_terms: 'ამ ობიექტზე თქვენი პირობები არ ფიქსირდება, ამიტომ ველები ცარიელია — შეიყვანეთ შეთანხმებული ფასი და საკომისიო',
    deal_close_split_unreadable: 'გაყოფაში არ ჩანს ჩვენი წილი — ჯერ გაასწორეთ თანაფარდობა, მერე დახურეთ',
    deal_close_no_property: 'ამ შეხვედრას ობიექტი არ აქვს, გარიგება კი განცხადებაზე ეწერება. ჯერ მიაბით ობიექტი (დეტალები და რედაქტირება → ობიექტი), მერე დახურეთ',
    desk_coop_join_action: 'დაამატეთ თქვენი პირობები',
    desk_coop_no_terms_yet: 'პირობები ჯერ არაა',
    deal_close_no_coop: 'აქტიური თანამშრომლობა და მესაკუთრის კონტაქტი არ მოიძებნა — ჯერ კონტაქტი დაამატეთ, ან მხოლოდ შეხვედრა დახურეთ',
    deal_close_will_record_coop: 'თანამშრომლობა არ ფიქსირდება — დახურვისას გაფორმდება {name}-თან და დასრულდება. აირჩიეთ გარიგების ტიპი:',
    deal_close_pick_listing: 'რომელი განცხადება დაიხურა? აირჩიეთ, რადგან სისტემა თავისით არ ირჩევს',
    deal_close_group_mine: 'თქვენი განცხადებები',
    deal_close_group_others: 'სხვა აგენტების განცხადებები',
    deal_close_group_done: 'უკვე დახურული',
    deal_close_from_meeting: 'ეს შეხვედრა ამ განცხადებიდან შეიქმნა',
    deal_close_outcome_sold: 'მოინიშნება გაყიდულად',
    deal_close_outcome_rented_out: 'მოინიშნება გაქირავებულად',
    deal_close_no_listing: 'ამ გარიგებას განცხადება არ აქვს, ამიტომ აქედან ვერ დაიხურება. გახსენით ობიექტის ბარათი და დახურეთ იქიდან',
    deal_close_no_coop_hint: 'ამ ობიექტზე თანამშრომლობა არ ფიქსირდება — საბოლოო მონაცემები დახურვისას მაინც შეინახება',
    deal_close_already_closed: 'ეს გარიგება უკვე დახურულია — ხელახლა დამატება საჭირო აღარ არის',
    deal_close_no_client_notice: 'ამ შეხვედრას კლიენტი არ აქვს მითითებული. გარიგება დაითვლება, მაგრამ შეხვედრაში მიუთითეთ კლიენტი, რომ მასზე ჩაიწეროს.',
    deal_close_deal_row_missing: 'ჩაიწერა და დაითვალა. გარიგების ჩანაწერი ვერ შეიქმნა, რადგან შეხვედრას კლიენტი არ აქვს — მიუთითეთ კლიენტი და დაუკავშირდება.',
    deal_close_no_viewer: 'ამ გარიგებაზე ჩასაწერი აგენტი არ არის — ჯერ დაამატეთ მონაწილე აგენტები, მერე დახურეთ.',
    deal_close_status_flip_failed: 'გარიგება დახურულად ჩაიწერა, მაგრამ შეხვედრის სტატუსი ვერ განახლდა — დააჭირეთ ხელახლა',
    deal_close_bad_number: 'ფასი და საკომისიო რიცხვებით უნდა მიუთითოთ',
    deal_close_class_closed: 'ამ ობიექტზე ასეთი ტიპის გარიგება უკვე დახურულია',
    deal_close_pick_intent: 'ჯერ აირჩიე გარიგების ტიპი',
    deal_close_confirm: '✓ გარიგების დადასტურება და დახურვა',
    deal_close_we_closed: 'გარიგება შედგა — მიუთითეთ საბოლოო პირობები',
    desk_status_log_hint: 'ამ აგენტის ადრინდელი ჩანაწერები — ინახება ლოგად, არ იშლება',
    desk_coop_edit_terms: 'პირობები ✎',
    desk_coop_no_terms: 'პირობები ჯერ არაა',
    desk_coop_terms_withheld: 'პირობები აქვს — ჩაწერეთ თქვენი, რომ ნახოთ',
    desk_coop_join_to_record: 'ამ თანამშრომლობაში არ ხართ. სთხოვეთ მენეჯმენტს ან მონაწილეს დაგამატოთ — შემდეგ შეძლებთ საკუთარი პირობების ჩაწერას.',
    desk_coop_view_only: 'ამ თანამშრომლობის ნახვა შეგიძლიათ, შეცვლა — არა.',
    desk_coop_you: 'თქვენ',
    desk_coop_leave: 'გავდივარ',
    desk_coop_leave_title: 'გამოხვალთ ამ თანამშრომლობიდან. ის თქვენ გარეშე გაგრძელდება; არაფერი იშლება.',
    desk_coop_exclusive_held_by: 'ვინ მოილაპარაკა',
    desk_coop_none_yet: 'ჯერ არავინ.',
    desk_coop_remove: 'წაშლა',
    desk_coop_agent_party_id_ph: 'აგენტის ID',
    desk_coop_participant_hidden: 'სხვა აგენტი',
    desk_coop_unnamed_property: 'უსახელო ობიექტი',
    desk_coop_unnamed_contact: 'უსახელო კონტაქტი',
    desk_coop_share_pct_ph: 'წილი %',
    desk_coop_add_participant: '+ დამატება',
    desk_coop_save_button: 'შენახვა',
    desk_coop_open_button: 'თანამშრომლობის დაწყება',
    desk_contact_view_add_tooltip: 'კონტაქტები — ნახვა / დამატება',
    desk_contact_none: 'კონტაქტი არ არის.',
    desk_contact_role_missing: 'არ არის', desk_contact_role_missing_no_door: 'ამ შეხვედრას ობიექტი არ აქვს, კონტაქტის მისაბმელად.',
    desk_contact_remove_confirm: 'წავშალოთ {name/phone} ამ ობიექტის კონტაქტებიდან?',
    desk_contact_add_phone_ph: 'ტელეფონი',
    desk_contact_add_name_ph: 'სახელი',
    desk_contact_notes_ph: 'შენიშვნები (რა ითხოვეს, რაზე შევთანხმდით…)',
    desk_contact_add_button: '＋ კონტაქტის დამატება',
    desk_contact_hidden_by: 'კონტაქტი ჩაწერილია — დაამატა {name} (მხოლოდ მისთვის ჩანს)',
    desk_contact_hidden_some: 'კონტაქტი ჩაწერილია — ჩანს მხოლოდ იმ აგენტისთვის, ვინც დაამატა',
    desk_role_client: 'კლიენტი',
    desk_role_owner: 'მესაკუთრე',
    desk_role_neighbor: 'მეზობელი',
    desk_role_keyholder: 'გასაღების მფლობელი',
    desk_role_broker: 'მაკლერი',
    desk_role_relative: 'ნათესავი',
    desk_role_tenant: 'დამქირავებელი',
    desk_role_other: 'სხვა',
    desk_contact_unknown_fallback: 'უცნობი',
    desk_contact_agent_badge: 'აგენტი',
    desk_contact_via_agent_badge: 'მათი განცხადებაა — დაუკავშირდით ამ აგენტს',
    desk_author_role_q: 'ეს კონტაქტი:',
    desk_author_role_unknown: 'აგენტია თუ მესაკუთრე? — ჩაწერილი არაა',
    desk_author_role_yes: 'აგენტია',
    desk_author_role_no: 'მესაკუთრეა',
    desk_author_role_tenant: 'დამქირავებელია',
    // B1286 — იხ. ინგლისური ბლოკი: აგენტად მონიშვნა განცხადებაა, ამიტომ ჯერ დასტურდება.
    desk_author_role_agent_confirm_title: 'დაადასტურეთ: გარე აგენტია?',
    desk_author_role_agent_confirm_body: 'თქვენ აცხადებთ, რომ ამ განცხადების ავტორი არ არის მესაკუთრე, '
      + 'არამედ სხვა სააგენტოს აგენტია, რომელიც ამ ქონებას წარმოადგენს. ამ ნიშანს კოლეგებიც დაინახავენ.',
    desk_author_role_agent_confirm_yes: 'დიახ, გარე აგენტია',
    desk_author_role_agent_confirm_cancel: 'გაუქმება',
    desk_author_role_marked: 'აგენტი',
    desk_contact_role_ask: 'აგენტია?',
    desk_contact_role_ask_on: 'აგენტია {src}-ზე?',
    desk_contact_role_not_agent: 'აგენტი არ არის',
    desk_author_role_disputed: 'სადავოა',
    desk_author_role_roster: 'სააგენტოს სიაშია',
    desk_author_role_declared_owner: 'დადებულია მესაკუთრედ',
    desk_author_role_declared_agent: 'დადებულია აგენტად',
    desk_author_role_elsewhere: 'იგივე ნომერი მონიშნულია კიდევ {n} ობიექტზე',
    desk_author_role_face_tooltip: 'მონიშნულია — განცხადება აგენტმა დადო, არა მესაკუთრემ',
    desk_author_role_tip_agent: '{n} კოლეგა ამბობს, რომ განცხადება აგენტმა დადო',
    desk_author_role_tip_owner: '{n} კოლეგა ამბობს, რომ აგენტი არ არის',
    desk_author_role_tip_tenant: '{n} კოლეგა ამბობს, რომ ეს მიმდინარე დამქირავებელია',
    desk_contact_tenant_since: 'დამქირავებელია {date}-დან — დახურული გაქირავება',
    desk_contact_tenant_reported: 'კოლეგამ მონიშნა მიმდინარე დამქირავებლად, არა მესაკუთრედ',
    desk_author_role_tip_split: 'აზრთა სხვადასხვაობა — {yes} ამბობს აგენტი, {no} ამბობს არა',
    desk_author_role_hint: 'დააჭირეთ პასუხის გასაცემად ან შესაცვლელად',
    desk_contact_click_to_copy: 'დააჭირეთ დასაკოპირებლად',
    desk_contact_copied: 'დაკოპირდა ✓',
    desk_contact_call: 'დარეკვა',
    desk_contact_fb_profile: 'Facebook პროფილის გახსნა',
    desk_contact_unverified_tip: 'განცხადებაშია მითითებული — არ არის დადასტურებული ამ პირის ნომრად',
    desk_contact_whatsapp: 'WhatsApp',
    desk_contact_no_wa: 'WhatsApp არ აქვს',
    desk_contact_no_wa_tip: 'ჩვენმა WhatsApp ხაზებმა ამ ნომერზე WhatsApp ვერ იპოვა. დარეკეთ ან გაგზავნეთ SMS.',
    desk_contact_sms: 'SMS',
    desk_contact_edit_tooltip: 'კონტაქტის რედაქტირება',
    desk_contact_remove_tooltip: 'კონტაქტის წაშლა',
    desk_contact_changed_from_origin: 'თქვენ შეცვალეთ ორიგინალი განცხადებიდან',
    desk_contact_yours_badge: '✎ თქვენი',
    desk_lineage_fallback_title: 'განცხადება',
    desk_share_copy_link: 'ბმულის კოპირება',
    desk_share_copied: 'დაკოპირდა',
    desk_share_copy_tooltip: 'ამ განცხადების ბმულის კოპირება — ჩასვით WhatsApp-ში, Telegram-ში, ელფოსტაში…',
    desk_share_whatsapp_tooltip: 'დაუკავშირდით ამ აგენტს WhatsApp-ით ამ ობიექტზე (ბმული უკვე ჩასმულია)',
    desk_lineage_couldnt_load: 'ვერ ჩაიტვირთა. სცადეთ თავიდან.',
    desk_lineage_your_posting_section: 'თქვენი განცხადება (შეივსო ორიგინალიდან)',
    desk_lineage_the_ad_section: 'განცხადება (როგორც გამოქვეყნდა)',
    desk_lineage_field_price: 'ფასი',
    desk_lineage_field_intent: 'მიზანი',
    desk_lineage_field_kind: 'ტიპი',
    desk_lineage_field_rooms: 'ოთახი',
    desk_lineage_field_bedrooms: 'საძინებელი',
    desk_lineage_field_area: 'ფართ.',
    desk_lineage_field_floor: 'სართ.',
    desk_lineage_field_condition: 'მგდმ.',
    desk_lineage_field_agent_read: 'აღჭურვილობა',
    desk_lineage_field_agent_activity: 'აგენტების აქტივობა',
    desk_lineage_field_orpheus_note: 'ორფევსის შენიშვნა',
    desk_agent_work_text: 'ტექსტი წაკითხულია',
    desk_agent_work_photos: 'ფოტოები ნანახია',
    desk_agent_work_place: 'რუკაზე დადებულია',
    desk_agent_work_compose: 'აღწერა დაწერილია',
    desk_agent_place_street: 'ქუჩა',
    desk_agent_place_with_number: 'ქუჩა + ნომერი',
    desk_agent_absent_tag: 'უწერია: არ აქვს',
    desk_agent_conflict_tag: 'წყაროები არ ემთხვევა',
    desk_agent_tip_text_read: 'განცხადების ტექსტი წაკითხულია {date}-ს და ნაპოვნია {n} სახის ინფორმაცია. ✦-ით მონიშნული ფაქტები აქედანაა.',
    desk_agent_tip_text_unread: 'ამ განცხადების ტექსტი ჯერ არავის წაუკითხავს — აქ დაკლებული ფაქტი ნიშნავს არშემოწმებულს და არა იმას რომ არ არის.',
    desk_agent_tip_photos_read: 'ფოტოები დათვალიერებულია {date}-ს. რაც მათში მოიძებნა, ✦-ითაა მონიშნული.',
    desk_agent_tip_photos_unread: 'ფოტოები არ დათვალიერებულა — აქედან არაფერი გვიცნობია.',
    desk_agent_tip_placed: 'მისამართი დაკავშირდა რეალურ ქუჩასთან, ამიტომ ობიექტი ჩანს ქუჩისა და რუკის ძიებაში.',
    desk_agent_tip_not_placed: 'მისამართი ქუჩას ვერ დაუკავშირდა — ობიექტი შესაძლოა არ ჩანდეს ქუჩისა და რუკის ძიებაში.',
    desk_agent_tip_composed: 'გამოსაქვეყნებელი აღწერა დაიწერა {date}-ს.',
    desk_agent_tip_not_composed: 'გამოსაქვეყნებელი აღწერა ჯერ არ დაწერილა.',
    desk_signal_tpe_title: 'სხვა სააგენტოს ექსკლუზივია — არ გადადოთ, თანამშრომლობა უფასო არ იქნება',
    desk_signal_coop_title: 'მესაკუთრე თანამშრომლობს',
    desk_signal_coop_declared: 'განცხადებაშია ნათქვამი',
    desk_signal_role_conflict_title: 'განცხადება ეწინააღმდეგება მითითებულ როლს',
    desk_signal_role_conflict_body: 'აღწერაში წერია {prose}; პორტალის ველში — {portal}:',
    desk_role_agent: 'აგენტი',
    desk_agent_not_checked: 'არ შემოწმებულა',
    desk_agent_conflict_hint: 'წყაროები არ თანხმდებიან — ერთი ამბობს კი, მეორე არა. არცერთი არ იმალება.',
    desk_agent_absent_hint: 'წყაროში მითითებულია „არა“. ეს პასუხი განსხვავდება უცნობი პასუხისგან.',
    // Reverted to სტატუსი (2026-07-24) — see the EN block: მშენებარე is not an age.
    desk_lineage_field_build_status: 'სტატუსი',
    desk_lineage_field_project: 'პროექტი',
    // source_kind — სკრეიპი/აგენტი come from the designer's popover mock; the other four
    // were approved 2026-07-24. ⚠️ რეესტრი is GENERIC on purpose: if it turns out to mean
    // the Public Registry specifically, this string gets more explicit, not the code.
    // Wording verbatim from the designer's mock (2026-07-24) — see the EN block on why the
    // count is values-recorded, not sources-consulted.
    desk_kw_edit_title: 'ფილტრის შეცვლა',
    desk_kw_edit_find_district: 'მოძებნეთ უბანი…',
    desk_kw_edit_no_options: 'ვარიანტები არ არის',
    desk_kw_edit_on: 'ჩართული',
    desk_kw_edit_off: 'გამორთული',
    desk_conflict_flag: '{n} წყარო არ ეთანხმება',
    desk_conflict_title: 'რომელია სწორი?',
    desk_conflict_confirm: 'ეს სწორია',
    desk_source_kind_scrape: 'პლატფორმიდან',
    desk_source_kind_agent: 'აგენტი',
    desk_source_kind_owner: 'მესაკუთრე',
    desk_source_kind_registry: 'რეესტრი',
    desk_source_kind_ai: 'AI',
    desk_source_kind_admin: 'ადმინი',
    desk_fact_fill_pill: 'შეავსეთ',
    desk_facts_project: 'პროექტის ტიპი',
    desk_facts_year_built: 'აშენების წელი',
    desk_val_ask_on_call: 'ზარის დროს დასაზუსტებელი',
    desk_facts_elevator: 'ლიფტი',
    desk_facts_parking: 'პარკინგი',
    desk_facts_heating: 'გათბობა',
    desk_facts_yes: 'კი',
    desk_facts_no: 'არა',
    desk_heat_central: 'ცენტრალური',
    desk_heat_gas: 'გაზის გამათბობელი',
    desk_heat_electric: 'ელ. ენერგიით',
    desk_heat_italian: 'ინდივიდუალური ქვაბი',
    desk_heat_floor: 'იატაკის გათბობა',
    desk_heat_none: 'გათბობის გარეშე',
    desk_lineage_field_district: 'უბანი',
    desk_district_dispute_title: 'უბანი გადასამოწმებელია',
    desk_district_dispute_question: 'განცხადების ბმულში წერია „{declared}“, რუკის პინი კი „{polygon}“-ში დგას. სწორია {declared}?',
    desk_district_dispute_yes: '✓ დიახ — {declared}',
    desk_district_dispute_no: '✗ არა — {polygon}',
    desk_district_dispute_votes: 'ხმები',
    desk_district_dispute_resolved: 'დადასტურებულია: {name} ({d}:{p})',
    desk_district_dispute_chip: '{declared} თუ {polygon}?',
    desk_counts_loading_hint: 'ჯერ ითვლება — შედეგების ჩატვირთვასთან ერთად იზრდება',
    desk_loc_section: 'ლოკაცია',
    desk_crit_near_locations: 'ახლოს',
    desk_crit_any_district: 'ნებისმიერი უბანი (მთელი ქალაქი)',
    desk_crit_location_note: 'ადგილმდებარეობა, თავისი სიტყვებით',
    desk_loc_copy_pitch: 'ტექსტის კოპირება კლიენტისთვის',
    desk_loc_copied: 'დაკოპირდა!',
    desk_loc_set_pin: 'დასვით ზუსტი პინი, რომ გამოჩნდეს ახლომდებარე მეტრო, პარკები და მოლები',
    desk_loc_q_complex_title: 'კომპლექსი გადასამოწმებელია',
    desk_loc_q_complex_in: 'ეს ბინა „{name}"-შია?',
    desk_loc_q_complex_near: 'ახლოსაა „{name}"-თან — ამ კომპლექსშია?',
    desk_q_yes: '✓ კი',
    desk_q_no: '✗ არა',
    desk_q_skip: 'არ ვიცი',
    desk_loc_unit_m: 'მ',
    desk_loc_none_nearby: 'ახლოს გამორჩეული არაფერია',
    desk_loc_unit_km: 'კმ',
    desk_cplx_basis_human: 'კოლეგამ დაადასტურა',
    desk_cplx_basis_agent_vote: 'კოლეგამ დაადასტურა',
    desk_cplx_basis_ad_named: 'განცხადება ასახელებს',
    desk_cplx_basis_proximity: 'მხოლოდ მდებარეობით',
    desk_cplx_status_built: 'დასრულებული',
    desk_cplx_status_planned: 'დაგეგმილი',
    desk_cplx_status_under_construction: 'მშენებარე',
    desk_cplx_status_partial: 'ნაწილობრივ დასრულებული',
    desk_cplx_status_completed: 'დასრულებული',
    desk_lineage_captured: 'შენახულია {time}',
    desk_lineage_last_seen: '· ბოლოს ნახა {time}',
    desk_lineage_price_unchanged: '· ფასი არ შეცვლილა',
    desk_lineage_price_history_section: 'ფასის ისტორია',
    desk_lineage_contact_section: 'კონტაქტი — განცხადებიდან, შესაძლოა არ იყოს მესაკუთრე',
    desk_lineage_viewings_section: 'ჩვენებები',
    desk_lineage_viewing_count: '{n} ჩვენება · ბოლო {time}',
    desk_lineage_has_closed_deal: 'დახურული გარიგება',
    desk_lineage_cooperation_section: 'თანამშრომლობა',
    desk_lineage_no_coop: 'არ არის — და არც კონტაქტია რომ დავიწყოთ.',
    desk_lineage_meeting_section: 'შეხვედრა',
    desk_lineage_posted_on_section: 'განთავსებულია',
    desk_lineage_scraped_badge: '🌐 საწყისი განცხადება',
    desk_lineage_origin_card_title: '🌐 საწყისი განცხადება',
    desk_lineage_viewing_badge: 'ვხედავთ',
    desk_lineage_published_badge: '✎ ჩვენი განცხადება',
    desk_lineage_source_of_reposts: '🌐 {n} რეპოსტის წყარო',
    desk_lineage_cov_price: 'ფასი',
    desk_lineage_cov_active_total: 'აქტიური / სულ განთავსება',
    desk_lineage_cov_origin: 'წყარო',
    desk_lineage_latest: 'უახლესი',
    desk_lineage_older_postings: '+{n}',
    desk_lineage_our_reposts: 'ჩვენი რეპოსტები · {n} აგენტი',
    desk_blocked_line: '{portal}-მა დაბლოკა:',
    desk_blocked_deleted: 'ანგარიშიდან წაშლილია',
    desk_lineage_facts_differ: 'ფაქტები განსხვავდება ({n})',
    desk_lineage_admin_dot: 'დიდი ფასის გადახრა ან ძველი განცხადება შიგნით',
    desk_lineage_captured_by_n: '{n} აგენტი',
    desk_fact_rooms: 'ოთახი',
    desk_fact_area: 'ფართობი',
    desk_fact_floor: 'სართული',
    desk_fact_condition: 'მდგომარეობა',
    desk_lineage_youre_viewing: 'თქვენ უყურებთ ამას',
    desk_lineage_open_link: '↗ გახსნა',
    desk_lineage_fb_posts: 'პოსტი',
    desk_lineage_fb_groups: 'ჯგუფი',
    desk_coop_offer_tooltip: 'მესაკუთრე თავად სთავაზობს თანამშრომლობას განცხადებაში — დააჭირეთ და გაიხსნება ამ პირობით შევსებული',
    desk_coop_offer_of_price: 'ფასიდან',
    desk_lineage_edit_link: '✎ რედაქტირება',
    desk_lineage_close_button: 'დახურვა',
    desk_lineage_edit_property_button: '✎ რედაქტირება',
    desk_lineage_my_version_section: 'ჩემი ვერსია — თქვენ მიერ ჩაწერილი ფაქტები',
    desk_my_version_drift_tooltip: '{portal}-ზე წერია {value} — გადაამოწმეთ რომელია სწორი',
    desk_my_version_drift_tooltip_nosrc: 'სხვა წყაროში წერია {value} — გადაამოწმეთ რომელია სწორი',
    desk_portal_facts_section: 'რას წერს თითოეული წყარო',
    desk_conflicts_section: 'შეუთავსებლობა — რომელი დავტოვოთ?',
    desk_conflicts_keep_tip: 'დატოვეთ ეს მნიშვნელობა (ჩაიწერება თქვენი გადაწყვეტილება)',
    desk_conflicts_note: 'მიმდინარე მნიშვნელობა ავტომატურად შეირჩა; დააჭირეთ სწორს, რომ დაფიქსირდეს.',
    desk_conflicts_failed: 'ვერ შეინახა',
    desk_portal_facts_disputed: 'არ ემთხვევა',
    desk_portal_facts_agree: 'ემთხვევა',
    desk_portal_facts_canonical_tip: 'მნიშვნელობა, რომელსაც სისტემა იყენებს',
    desk_portal_facts_reshare_tip: 'გადაქვეყნება — იგივე განცხადების ასლი, და არა მეორე წყარო',
    desk_portal_facts_note: 'თითო რიგი — თითო წყარო. გადაქვეყნება ცალკეა აღნიშნული, რადგან ის განცხადებას იმეორებს და არა ბინას ათვალიერებს.',
    desk_attr_rooms: 'ოთახები',
    desk_attr_bedrooms: 'საძინებლები',
    desk_attr_bathrooms: 'სველი წერტილები',
    desk_attr_area_sqm: 'ფართობი (მ²)',
    desk_attr_land_area_sqm: 'მიწის ფართობი (მ²)',
    desk_attr_kitchen_area: 'სამზარეულოს ფართობი (მ²)',
    desk_attr_floor: 'სართული',
    desk_attr_floors_total: 'სართულების რაოდენობა',
    desk_attr_ceiling_height: 'ჭერის სიმაღლე (მ)',
    desk_attr_year_built: 'აშენების წელი',
    desk_attr_parking_deeded: 'პარკინგი ცალკე რეგისტრირებული',
    desk_attr_cls_new_built: 'ახალ აშენებული',
    desk_attr_cls_old_built: 'ძველ აშენებული',
    desk_attr_living_room_area_sqm: 'მისაღების ფართობი (მ²)',
    desk_attr_loggia_area_sqm: 'ლოჯიის ფართობი (მ²)',
    desk_attr_storeroom_area_sqm: 'სათავსოს ფართობი (მ²)',
    desk_attr_porch_area_sqm: 'ვერანდის ფართობი (მ²)',
    desk_attr_building_material: 'მასალა',
    desk_attr_metro_station: 'მეტროს სადგური',
    desk_attr_has_balcony: 'აივანი',
    desk_attr_balcony_count: 'აივნების რაოდენობა',
    desk_attr_balcony_area_sqm: 'აივნის ფართობი (მ²)',
    desk_attr_has_terrace: 'ტერასა',
    desk_attr_condition: 'მდგომარეობა',
    desk_attr_building_status: 'შენობის სტატუსი',
    desk_attr_project: 'პროექტი',
    desk_attr_power_capacity_kw: 'სიმძლავრე (კვტ)',
    desk_attr_functional_zone: 'ფუნქციური ზონა',
    desk_stack_open_tooltip: 'დეტალების გახსნა — ამ ქონების ყველა განცხადება ერთ ფანჯარაში',
    desk_lineage_view_posting_tooltip: 'ამ განცხადების საკუთარი მონაცემების ჩვენება',
    desk_lineage_your_reposts_section: 'თქვენი განცხადებები',
    desk_lineage_scroll_to_my_version_tooltip: 'გადასვლა თქვენს ჩაწერილ ვერსიაზე ქვემოთ',
    desk_type_client: 'კლიენტი',
    desk_type_properties: 'ობიექტები',
    desk_type_property: 'ობიექტი',
    desk_type_meeting: 'შეხვედრა',
    desk_type_listings: 'განცხადებები',
    desk_type_business: 'ბიზნესი',
    desk_listing_intent_sale: 'იყიდება',
    desk_listing_intent_rent: 'ქირავდება',
    desk_listing_intent_daily: 'ქირავდება დღიურად',
    // 0219 — the platforms' own wording, verbatim (ss/myhome/home.ge all use these).
    desk_listing_intent_girao: 'გირავდება',
    desk_listing_intent_ijara: 'გაიცემა იჯარით',
    desk_intent_opt_girao: 'გირაო',
    desk_fieldlabel_price_from: 'მინ. ფასი',
    desk_fieldlabel_price_to: 'მაქს. ფასი',
    desk_fieldlabel_currency: 'ვალუტა',
    desk_fieldlabel_area_from: 'მინ. ფართ.',
    desk_fieldlabel_area_to: 'მაქს. ფართ.',
    desk_fieldlabel_rooms_from: 'ოთახ. დან',
    desk_fieldlabel_rooms_to: 'ოთახ. მდე',
    desk_fieldlabel_bedrooms_from: 'საძინ. დან',
    desk_fieldlabel_bedrooms_to: 'საძინ. მდე',
    desk_fieldlabel_districts: 'უბნები',
    desk_fieldlabel_excluded_districts: 'გამორიცხული უბნები',
    desk_fieldlabel_excluded_complexes: 'გამორიცხული კომპლექსები',
    desk_fieldlabel_complexes: 'კომპლექსი',
    desk_fieldlabel_must_haves: 'აუცილებელი',
    desk_fieldlabel_nice_to_haves: 'სასურველი',
    desk_fieldlabel_must_not_haves: 'მიუღებელი',
    desk_fieldlabel_condition: 'მდგომარეობა',
    desk_fieldlabel_condition_exclude: 'მგდმ. გამორიცხვა',
    desk_fieldlabel_drive_time: 'მგზავრობის დრო',
    desk_fieldlabel_mortgage: 'იპოთეკა',
    desk_fieldlabel_pets: 'ცხოველები',
    desk_fieldlabel_children: 'ბავშვები',
    desk_status_pending: 'მომლოდინე',
    desk_status_not_saved: 'არ შეინახა',
    desk_status_saved: 'შენახულია',
    desk_status_rejected: 'უარყოფილია',
    desk_status_edited: 'შეიცვალა',
    desk_status_failed: 'შეფერხდა',
    desk_status_archived: 'არქივშია',
    desk_status_on_market: 'აქტიური',
    desk_status_off_market: 'არააქტიური',
    desk_entry_new_client: 'ახალი კლიენტი',
    desk_entry_client_update: 'კლიენტის განახლება',
    desk_entry_changed_fields: 'შეიცვალა: {fields}',
    desk_entry_client_closed: 'კლიენტი დაიხურა',
    desk_entry_closed_from_desk: 'დაიხურა Desk-დან',
    desk_entry_client_reopened: 'კლიენტი გაიხსნა',
    desk_entry_back_on_active_list: 'დაბრუნდა აქტიურ სიაში',
    desk_entry_meeting_fallback: 'შეხვედრა',
    desk_entry_new_candidate: 'ახალი კანდიდატი',
    desk_entry_property_fallback: 'ობიექტი',
    cad_section: 'საკადასტრო მონაცემები',
    cad_code: 'საკადასტრო კოდი',
    cad_truncated_from: 'ბინის კოდიდან',
    cad_official_address: 'ოფიციალური მისამართი',
    cad_parcel_area: 'ნაკვეთის ფართობი',
    cad_sqm: 'კვ.მ',
    cad_area_mismatch: 'განსხვავდება კონტურისგან',
    cad_parcel_type: 'ნაკვეთის ტიპი',
    cad_ownership_type: 'საკუთრების ტიპი',
    cad_previous_code: 'წინა ნომერი',
    cad_our_pin: 'ჩვენი წერტილი',
    cad_pin_inside: 'ნაკვეთის შიგნით',
    cad_pin_outside: 'ნაკვეთის გარეთ',
    cad_pin_none: 'წერტილი არ არის',
    cad_open_registry: 'საჯარო რეესტრში გახსნა',
    street_q_title: 'რომელ ქუჩაზეა?',
    street_q_thanks: 'შენახულია - მადლობა',
    area_section: 'მიკრორაიონი / უბანი',
    area_within: 'შედის',
    area_decided_by: 'განისაზღვრა',
    area_basis_house_point: 'დადგენილი მისამართით (შენობა)',
    area_basis_house_point_over_stated: 'დადგენილი მისამართით - განცხადებაში სხვა უბანია',
    area_basis_stated_area: 'განცხადებაში დასახელებული უბნით',
    area_basis_street_in_area: 'ქუჩა ამ უბანშია',
    area_basis_both: 'განცხადება და ქუჩა ემთხვევა',
    area_advert_says: 'განცხადებაში',
    area_pin_inside: 'უბნის შიგნით',
    area_pin_outside: 'უბნის გარეთ',
    desk_entry_listing_fallback: 'განცხადება',
    desk_entry_rooms_abbrev: '{rooms} ოთ.',
    desk_entry_bedrooms_abbrev: '{bedrooms} საძ.',
    desk_entry_area_abbrev: '{area} მ²',
    // კომპაქტური ფაქტები — დახრილი ხაზი მხოლოდ სართულს ნიშნავს, საძინებელს თავისი ერთეული აქვს.
    desk_compact_area: '{area}მ²',
    desk_compact_rooms: '{rooms}ოთ',
    desk_compact_bedrooms: '{bedrooms}საძ',
    desk_compact_floor: '{floor}ს',
    desk_compact_floor_of: '{floor}/{total}ს',
    desk_compact_floors_total: '{total}ს',
    desk_compact_bathrooms: '{n} სვ.წ',
    desk_matched_member_one: 'ემთხვევა: {facts}',
    desk_matched_member_multi: 'ემთხვევა {n}: {facts}',
    desk_matched_member_title: 'ფილტრს ემთხვევა {total}-დან {n}',
    desk_entry_land_area_abbrev: 'ეზო {area}მ²',
    desk_entry_floor_position: 'სართ. {floor}/{total}',
    desk_entry_floor_only: 'სართ. {floor}',
    desk_entry_floor_range: 'სართ. {min}–{max}',
    desk_entry_floor_range_of: 'სართ. {min}–{max}/{total}',
    desk_dev_left_of: '{left} დარჩა {total}-დან',
    desk_facet_seller_whose: 'გამყიდველი',
    desk_facet_contact: 'კონტაქტი',
    desk_facet_agent_represents: 'აგენტი წარმოადგენს',
    desk_sellerkind_private_owner: 'კერძო მესაკუთრე',
    desk_sellerkind_developer: 'დეველოპერი',
    desk_sellerkind_bank: 'ბანკი',
    desk_sellerkind_other_company: 'სხვა კომპანია',
    desk_sellerkind_unknown: 'უცნობი',
    desk_contactrole_owner_direct: 'პირდაპირ მესაკუთრე',
    desk_contactrole_agent: 'აგენტი',
    desk_contactrole_company_rep: 'კომპანიის წარმომადგენელი',
    desk_contactrole_unknown: 'უცნობი',
    desk_seller_likely: 'სავარაუდოდ',
    desk_seller_agent_for: 'წარმოადგენს',
    desk_seller_likely_note: 'მესაკუთრე / აგენტი განისაზღვრება ავტორის განცხადებების რაოდენობით — აღნიშნულია „სავარაუდოდ“.',
    desk_dev_on_sale: '{n} იყიდება',
    desk_floor_plan: 'სართულის გეგმა',
    desk_floor_plan_floor: '{n} სართული',
    desk_floor_plan_mine: 'ეს ბარათი',
    desk_floor_plan_free: 'იყიდება',
    desk_floor_plan_sold: 'გაყიდული / არ იყიდება',
    desk_floor_plan_none: 'დეველოპერი ამ შენობის სართულის გეგმას არ აქვეყნებს',
    desk_range_price_header: 'ფასი',
    desk_facet_ppm: 'ფასი 1 მ²-ზე', desk_ppm_min: 'მინ.', desk_ppm_max: 'მაქს.',
    desk_range_area_header: 'ფართობი',
    // Owner wording 2026-08-06: "საცხოვრებელი" is the standard real-estate word, and the
    // abbreviated form is the one that fits a 300px rail's input. The full phrase lives on
    // the facet header above it, so the field does not have to carry it twice.
    desk_range_area_building: 'საცხ. ფართობი მ²',
    desk_range_land_area: 'მიწის/ეზოს ფართობი მ²',
    desk_entry_by_agent: 'ავტორი: {agent}',
    desk_entry_scraped_by: 'შეინახა: {agent}',
    desk_entry_ad_by: 'განცხ. ავტორი: {agent}',
    desk_entry_price_inherited: 'გადმოვიდა წყაროდან — გამოქვეყნებისას ფასი არ მითითებულა',
    desk_price_observed_as: 'გადაყვანილია დღევანდელი ეროვნული ბანკის კურსით — განცხადებაში წერია {observed}',
    desk_entry_address_inherited: 'გადმოვიდა წყაროდან — გამოქვეყნებისას მისამართი არ მითითებულა',
    desk_entry_possible_match: '🔗 შესაძლო დამთხვევა — გაგრძელება',
    desk_entry_matched_contact: '🔗 დაემთხვა კონტაქტს',
    desk_entry_my_property_manual: '✎ ჩემი ობიექტი · ხელით დამატებული',
    desk_entry_my_property_ai: '✎ ჩემი ობიექტი · AI-ის შევსებული',
    desk_entry_my_property_scraped: '✎ ჩემი ობიექტი · შენახული',
    desk_entry_scraped_listing_caption: '🌐 შენახული განცხადება',
    desk_entry_my_property_added_by: '✎ ჩემი ობიექტი · დაამატა: {agent}',
    desk_entry_my_property_crawler: '✎ ჩემი ობიექტი · იპოვა: {bot}',
    desk_entry_scraped_listing_added_by: '🌐 დაამატა: {agent}',
    desk_entry_scraped_listing_crawler: '🌐 იპოვა: {bot}',
    desk_entry_scraped_listing_form: '🌐 მფლობელმა შეავსო',
    desk_entry_my_property_form: '✎ ჩემი ობიექტი · მფლობელმა შეავსო',
    desk_entry_platform_ads_pick: 'რომელი განცხადება?',
    desk_entry_platform_ads_undated: 'თარიღის გარეშე',
    desk_entry_you_edited: '✏️ თქვენ შეცვალეთ',
    desk_entry_ai_created: '🤖 AI-შექმნილი',
    desk_entry_filled_via_link: '🔗 ბმულით შევსებული',
    desk_entry_from_whatsapp: '💬 შემოთავაზებულია WhatsApp-ის ჩატში — შეამოწმეთ და დაამატეთ',
    desk_entry_reposted: 'რეპოსტი · {datetime}',
    desk_entry_seen_by_me: 'ნანახი',
    desk_entry_seen_by_me_at: 'თქვენ ნახეთ {datetime}',
    desk_entry_seen_by_me_times: 'სულ {n}-ჯერ გახსენით',
    desk_entry_added: 'დაემატა {date}',
    desk_entry_updated: 'განახლდა {date}',
    desk_entry_posted: 'გამოქვეყნდა {date}',
    desk_entry_bumped: 'განაახლა {date}',
    desk_entry_first_seen: 'ვნახეთ {date}',
    desk_entry_first_seen_hint: 'ამ პლატფორმას გამოქვეყნების თარიღი არ აქვს — ეს ჩვენი ნახვის თარიღია',
    desk_entry_acquired: 'ჩვენთან {date}',
    desk_entry_acquired_hint: 'როდის შემოვიდა ჩვენს სისტემაში (არგოთი ან აგენტის მიერ)',
    desk_entry_checked: 'შემოწმდა {date}',
    desk_entry_checked_hint: 'როდის შევამოწმეთ ბოლოს — შეიცვალა თუ არა',
    desk_age_today: 'დღეს',
    desk_age_unit_d: 'დ',
    desk_age_unit_mo: 'თვე',
    desk_age_unit_y: 'წ',
    desk_entry_open: 'გახსნა',
    desk_entry_thread_updates: '🧵 {n} განახლება',
    desk_entry_stack_count: '{n} ×',
    desk_thread_noun_listings: 'განცხადება',
    desk_thread_noun_updates: 'განახლება',
    desk_entry_thread_generic: '🧵 {n} {listings/updates}',
    desk_entry_group_tooltip: '{n} {listings/updates} — ჯგუფის გახსნა',
    desk_entry_click_see_more: 'მეტის სანახავად',
    desk_entry_no_locations: 'ზუსტი მდებარეობის მქონე ჩანაწერები არ არის — რუკაზე საჩვენებელი არაფერია.',
    desk_meetingcard_fallback_title: 'შეხვედრა',
    desk_meetingcard_property_fallback: 'ობიექტი',
    desk_meetingcard_listing_link: 'განცხადება ↗',
    desk_meetingcard_no_time: 'დრო უცნობია',
    desk_meeting_filter_all: 'ყველა',
    desk_meeting_filter_active: 'აქტიური',
    desk_meeting_filter_finished: 'დასრულებული',
    desk_meeting_in_status_prefix: '{status}-ში:',
    desk_meetings_no_match: 'შეხვედრები არ მოიძებნა.',
    desk_meeting_status_scheduled: 'ჩანიშნული',
    desk_meeting_status_pending: 'მოლოდინში',
    desk_meeting_status_binder: 'ბე',
    desk_meeting_status_closed: 'გარიგება შედგა',
    desk_meeting_status_failed: 'ჩაიშალა',
    desk_meeting_status_rescheduled: 'გადადება…',
    desk_meeting_status_cancelled: 'გაუქმდა',
    desk_meeting_state_rescheduled: 'გადაიდო',
    meeting_edit_property: 'ობიექტის შეცვლა',
    meeting_edit_time: 'დროის შეცვლა',
    meeting_property_search_ph: 'მისამართი, მესაკუთრის სახელი ან ნომერი…',
    meeting_property_search_hint: 'საძიებლად შეიყვანეთ მინიმუმ 3 სიმბოლო. ძიება ყველა ქონებას მოიცავს, მათ შორის სხვა აგენტების ქონებასაც.',
    meeting_property_search_none: 'ვერაფერი მოიძებნა.',
    meeting_time_save: 'დროის შენახვა',
    meeting_time_clear: 'დროის წაშლა',
    meeting_status_note_ph: 'მაგ. კლიენტი ხვალამდე გადაწყვეტს და ვოტსაპში მომწერს',
    meeting_status_note_save: 'შენახვა',
    // 0427 — შეხვედრის მიზანი. Georgian reviewed via Gemini 2026-08-06.
    meeting_purpose_label: 'შეხვედრის მიზანი',
    // მე-{n} is grammatical ONLY for n≥2 (n=1 would need "პირველი"). Safe here by
    // construction: the line renders only when prior_meeting_count > 0, so n ≥ 2
    // always. If that guard ever moves, switch to "ვიზიტი N{n}".
    meeting_purpose_suggested: 'ამ კლიენტისთვის ეს იქნება მე-{n} ვიზიტი',
    desk_meeting_purpose_first_viewing: 'პირველი ნახვა',
    desk_meeting_purpose_repeat_viewing: 'განმეორებითი ნახვა',
    desk_meeting_purpose_negotiation: 'მოლაპარაკება',
    desk_meeting_purpose_signing: 'გაფორმება',
    desk_meeting_purpose_final_viewing: 'საბოლოო ნახვა',
    desk_meeting_purpose_inspection: 'ტექნიკური შემოწმება',
    desk_meeting_purpose_owner_meeting: 'შეხვედრა მესაკუთრესთან',
    desk_meeting_purpose_key_handover: 'გასაღების გადაცემა',
    desk_meeting_purpose_documents: 'დოკუმენტები',
    desk_meeting_purpose_other: 'სხვა',
    // 0427 — სტატუსის მიზეზები
    meeting_next_action_label: 'შემდეგი ნაბიჯი:',
    meeting_reason_required_hint: 'აირჩიეთ მიზეზი',
    desk_meeting_reason_client_no_show: 'კლიენტი არ მოვიდა',
    desk_meeting_reason_client_cancelled: 'კლიენტმა გააუქმა',
    desk_meeting_reason_owner_cancelled: 'მესაკუთრემ გააუქმა',
    desk_meeting_reason_agent_unavailable: 'აგენტი ვერ მოვიდა',
    desk_meeting_reason_client_changed_mind: 'კლიენტმა გადაიფიქრა',
    desk_meeting_reason_price_disagreement: 'ფასზე ვერ შეთანხმდნენ',
    desk_meeting_reason_wrong_expectations: 'მოლოდინი არ გამართლდა',
    desk_meeting_reason_not_interested: 'არ დაინტერესდა',
    // "ობიექტი", not "ბინა" (Gemini review 2026-08-06): the agency also handles
    // houses/land, and "ბინა" strictly means a flat.
    desk_meeting_reason_chose_another_property: 'სხვა ობიექტი აირჩია',
    desk_meeting_reason_client_thinking: 'კლიენტი ფიქრობს',
    desk_meeting_reason_waiting_bank: 'ველოდებით ბანკს',
    desk_meeting_reason_waiting_owner: 'ველოდებით მესაკუთრეს',
    desk_meeting_reason_client_requested: 'კლიენტმა გადადო',
    desk_meeting_reason_owner_requested: 'მესაკუთრემ გადადო',
    desk_meeting_reason_agent_conflict: 'აგენტს სხვა შეხვედრა დაემთხვა',
    desk_meeting_reason_property_unavailable: 'ობიექტი მიუწვდომელია',
    desk_meeting_reason_external_circumstances: 'გარე ფაქტორები',
    // 0559 (B651) — the agent's OWN wording, lifted verbatim from the event log
    // (irakli, 11 Aug: «გადაიდო გაურკვეველი ვადით»), not a translation of the English.
    desk_meeting_reason_postponed_no_date: 'გადაიდო გაურკვეველი ვადით',
    desk_meeting_reason_closed_with_deal: 'დაიხურა გარიგებასთან ერთად',
    desk_meeting_reason_property_gone: 'ობიექტი წავიდა — სხვა კლიენტმა იქირავა / იყიდა',
    desk_deal_visits: 'გარიგება დახურულია · {n} ვიზიტი',
    desk_deal_participants: 'მონაწილეები',
    desk_deal_closed_commission: 'საკომისიო',
    desk_deal_closed_no_figures: 'დაიხურა — თანხები არ ჩაწერილა',
    deal_close_figures_required: 'შეიყვანე ფასი და საკომისიო — დახურული გარიგება იწერს, რა პირობებით დაიხურა.',
    deal_close_cobroke_missing: '{name}-სთან გაყოფა ჩაწერილი არ არის — მთელი საკომისიო ჩვენად ჩაითვლება.',
    deal_close_cobroke_missing_how: 'ჯერ ჩაწერე შეხვედრაზე (საკომისიოს გაყოფის პანელი იმ აგენტის ქვეშ), მერე დახურე.',
    deal_figures_edit: 'თანხების შესწორება',
    deal_close_coop_terms_hint: "ამ ბინის თანამშრომლობის პირობები: {base}-ის {pct}% = {amount} {cur}. ზემოთ ველში იწერება თანხა.",
    deal_close_percent_guard: "{pct} პროცენტს ჰგავს და არა თანხას: {base}-ის {pct}% არის {amount} {cur}. შეასწორეთ თანხა, ან თუ საკომისიო მართლა {pct}-ია, კიდევ ერთხელ დააჭირეთ დახურვას.",
    deal_figures_edit_reason: 'რატომ (არასავალდებულო)',
    desk_meeting_follows_previous: 'წინა ვიზიტის გაგრძელება',
    desk_meeting_row_created_by: 'ჩანიშნა',
    meeting_superseded_title: 'მოჰყვა მომდევნო ვიზიტი',
    meeting_superseded_hint: 'შედეგი, თანხები, მონაწილეები და თარიღი იწერება მიმდინარე ვიზიტზე ({when}). აქ რჩება მხოლოდ კომენტარები და დასწრება.',
    meeting_superseded_open: 'მიმდინარე ვიზიტის გახსნა',
    meeting_superseded_actions: 'მოქმედებები მიმდინარე ვიზიტზეა — გამოიყენეთ ზემოთ ღილაკი.',
    meeting_superseded_refused: 'ამ ვიზიტს მოჰყვა მომდევნო — ჩაწერეთ მიმდინარე ვიზიტზე.',
    meeting_not_related: 'ამ შეხვედრაზე არ ხარ — ცვლილებას მხოლოდ მასზე მყოფები ან მენეჯმენტი შეძლებს.',
    desk_nudge_why_property_gone: 'ობიექტი სხვა კლიენტს ერგო — შეატყობინე შენსას',
    desk_meeting_reason_other: 'სხვა',
    // 0427 — შეხსენებები. დიზაინ-სისტემა 2026-08-06.
    desk_meeting_nudges_title: '{n} შეხვედრა საჭიროებს განახლებას',
    desk_nudge_tier_critical: '{n} შეხვედრა — დრო გავიდა, მიუთითეთ შედეგი',
    desk_nudge_tier_high: '{n} მოლოდინშია — შემდეგი ნაბიჯის ვადა გავიდა',
    desk_nudge_more_medium: 'კიდევ {n} შეხვედრა მიზეზის გარეშეა — განახლებისას სია შემცირდება',
    desk_nudge_open: 'გახსნა',
    desk_nudge_hidden_by_view: 'კიდევ {n} ელოდება პასუხს — ამ ხედში არ ჩანს. ჩვენება',
    desk_nudge_open_failed: 'ეს შეხვედრა თქვენს სიაში აღარ არის — შესაძლოა წაშლილია.',
    meeting_status_ask_title: 'სტატუსი: „{status}“ — რატომ?',
    meeting_status_ask_date_title: 'როდის შევახსენოთ?',
    meeting_status_note_title: 'შეხვედრის დეტალები — რაც შეხვედრაზე გამოგადგებათ',
    meeting_binder_title: 'ბე — ციფრები',
    meeting_binder_amount: 'თანხა',
    meeting_binder_expires: 'ბეს ვადა',
    meeting_binder_taken: 'აღების თარიღი',
    meeting_binder_amend_reason: 'ცვლილების მიზეზი',
    meeting_binder_amend_reason_ph: 'მაგ. კლიენტმა 200-ით ნაკლები გადაიხადა',
    meeting_binder_settle: 'რა მოხდა ბესთან',
    meeting_binder_forfeit_why: 'ბე რჩება. რატომ?',
    binder_status_applied: 'ჩაითვალა ფასში',
    binder_status_returned: 'დაბრუნდა',
    binder_status_forfeited: 'დარჩა',
    binder_status_cancelled: 'გაუქმდა',
    meeting_binder_price: 'შეთანხმებული ფასი',
    meeting_binder_remaining: 'დარჩენილია: {amount}',
    meeting_binder_no_remaining: 'სხვადასხვა ვალუტა — გამოკლება არ ხდება',
    meeting_binder_split: 'ვისთან რჩება',
    meeting_binder_required_hint: 'თანხა და ვადა სავალდებულოა',
    meeting_binder_split_hint: 'წილები ჯამში 100% უნდა იყოს',
    meeting_binder_share_blank_hint: 'მიუთითეთ ყველას წილი, ან წაშალეთ სტრიქონი',
    meeting_binder_summary_expired: 'ვადა გასულია',
    meeting_binder_short: 'ბე',
    meeting_binder_note_ph: 'მაგ. ქვითარი ხელმოწერილია, დანარჩენი ნოტარიუსთან',
    meeting_binder_holder_owner: 'მესაკუთრე',
    meeting_binder_holder_agent: 'აგენტი',
    meeting_binder_holder_agency: 'სააგენტო',
    meeting_binder_holder_client: 'კლიენტი',
    meeting_binder_holder_other: 'სხვა',
    desk_nudge_why_binder_expired: 'ბეს ვადა გავიდა — გარიგება შედგა თუ ჩაიშალა?',
    meeting_prep_note_ph: 'მაგ. აინტერესებს პარკინგი; მოვა მეუღლესთან ერთად',
    meeting_reason_required_badge: 'სავალდებულო',
    meeting_reason_optional_badge: 'სურვილისამებრ',
    meeting_followup_tomorrow: 'ხვალ',
    meeting_followup_3days: '3 დღეში',
    meeting_followup_friday: 'პარასკევს',
    meeting_purpose_editable: 'შეგიძლია შეცვალო',
    meeting_purpose_suggested_was: 'შემოთავაზებული: {label}',
    desk_meeting_visit_n: 'მე-{n} ვიზიტი',
    meeting_edit_section: 'დეტალები და რედაქტირება',
    meeting_record_result: 'შედეგის მითითება',
    meeting_outcome_ask: 'რა შედეგი აქვს შეხვედრას? აირჩიეთ:',
    desk_nudge_why_meeting_not_updated: 'შეხვედრის დრო გავიდა — რა შედეგია?',
    desk_nudge_why_next_action_date_passed: 'შემდეგი ნაბიჯის თარიღი გავიდა — განაახლეთ',
    desk_nudge_why_pending_stale: 'დიდი ხანია მოლოდინშია — არის რამე სიახლე?',
    desk_nudge_why_missing_reason: 'მიუთითეთ მიზეზი — რატომ მიანიჭეთ ეს სტატუსი?',
    // B (2026-08-06) — იხ. ინგლისური მხარე. ნეიტრალური ფორმულირება განზრახ.
    desk_binder_changed: 'ბეს თანხა შეიცვალა',
    desk_binder_deadline_changed: 'ბეს ვადა შეიცვალა',
    desk_binder_changed_someone: 'ვიღაცამ',
    desk_nudge_why_missing_next_action: 'მოლოდინშია — მიუთითეთ შემდეგი ნაბიჯის თარიღი',
    desk_nudge_waiting_client: 'ელოდება კლიენტს',
    desk_nudge_waiting_owner: 'ელოდება მესაკუთრეს',
    desk_nudge_waiting_agent: 'ელოდება აგენტს',
    desk_nudge_waiting_bank: 'ელოდება ბანკს',
    meeting_guest_add: 'დაამატეთ დამსწრე',
    meeting_guest_badge: '· მხოლოდ დამსწრე',
    meeting_guest_remove: 'შეხვედრიდან ამოშლა',
    meeting_guest_search_ph: 'აგენტის სახელი…',
    desk_chain_title: 'ჯაჭვი',
    desk_chain_dupes_title: 'დუბლიკატები',
    desk_chain_unknown_first: 'რომელი იყო პირველი — უცნობია. განცხადებები ემთხვევა, მაგრამ ვინ ვისგან გადაიღო — არ ვიცით.',
    desk_chain_n_sources: '{n} წყარო',
    desk_chain_n_reposts: '{n} გადაზიარება',
    desk_chain_n_platforms: '{n} პლატფორმა',
    desk_chain_source_tag: 'წყარო',
    desk_chain_closed_n: '{n} დახურული განცხადება',
    desk_chain_agents_title: 'აგენტების ჯაჭვი',
    desk_chain_n_agents: '{n} აგენტი',
    desk_entry_posted_label: 'გამოქვეყნდა',
    desk_entry_bumped_label: 'განაახლა',
    desk_entry_first_seen_label: 'ვნახეთ',
    desk_entry_acquired_label: 'ჩვენთან',
    desk_entry_checked_label: 'შემოწმდა',
    desk_state_active: 'აქტიური',
    desk_state_closed: 'აღარ არის აქტიური',
    desk_state_owner_ads: 'მესაკუთრის განცხ.',
    // ⚠️ Georgian is UNICASE — never style this with `uppercase`, it is inert and the marker
    // would carry no visual weight at all. Size and colour do the work instead.
    desk_state_owner_ads_ended: 'დასრულებული',
    desk_state_seen: 'ნანახია',
    desk_state_seen_me: 'მე ✓',
    desk_meeting_deal_sale: 'გაყიდვა',
    desk_meeting_deal_rent: 'გაქირავება',
    desk_search_gallery_view_tooltip: 'გალერეის ხედი — დიდი ფოტოები, შესაცვლელად დააკლიკეთ',
    desk_search_map_view_tooltip: 'რუკის ხედი — პინები ლოკაციებით',
    desk_map_expand_tooltip: 'რუკის გადიდება — გვერდითი პანელების დამალვა',
    desk_map_collapse_tooltip: 'რუკის შემცირება — გვერდითი პანელების ჩვენება',
    desk_map_pin_unknown: 'დააკლიკეთ გასახსნელად',
    desk_search_debug_tooltip: 'ორმაგი კლიკი: დებაგი',
    desk_search_placeholder: 'ძებნა — მაგ: 2 ოთახი ვაკე 200k მდე...',
    desk_search_placeholder_client: 'კლიენტების ძებნა — სახელი, ტელეფონი ან კრიტერიუმები: 2 ოთახი ვაკე 200k მდე...',
    desk_search_placeholder_meeting: 'შეხვედრების ძებნა...',
    desk_search_placeholder_business: 'ბიზნესების ძებნა...',
    desk_search_placeholder_leads: 'ლიდების ძებნა — უბანი ან მისამართი...',
    desk_type_leads: 'ლიდები',
    // ── კლიენტ-ლიდები (0462/0463) — მოთხოვნა ლიდების პულში ───────────────────
    desk_type_client_leads: 'კლიენტები',
    desk_cl_pickup: 'აღება',
    desk_cl_picking: 'მიმდინარეობს…',
    // See the EN block.
    desk_cl_confirm_title: 'აიღებთ ამ კლიენტს?',
    desk_cl_confirm_body: 'შევინახოთ {name} თქვენს კლიენტებში?',
    desk_cl_confirm_note: 'კლიენტი გადავა თქვენს სიაში და გავა საერთო სიიდან — სხვა ვეღარ აიღებს.',
    desk_cl_confirm_ok: 'კლიენტის აღება',
    desk_cl_taken: 'ეს კლიენტი უკვე აიღო სხვა აგენტმა.',
    desk_cl_empty: 'ამჟამად თავისუფალი კლიენტი არ არის. ახლები აქ გამოჩნდება.',
    desk_cl_whole_city: 'მთელი ქალაქი',
    desk_cl_from: '{v}-დან',
    desk_cl_upto: '{v}-მდე',
    desk_cl_movein: 'შესვლა',
    desk_cl_people: 'ადამიანი',
    desk_cl_pets: 'ცხოველი ჰყავს',
    desk_cl_urgent: 'სასწრაფო',
    desk_cl_no_budget: 'ბიუჯეტი მითითებული არ არის',
    desk_cl_yours: 'თქვენია',
    desk_cl_you: 'თქვენ',
    desk_cl_pickup_hint: 'ნომრის სანახავად აიღეთ კლიენტი',
    desk_cl_pickup_title: 'აიღე კლიენტი — ნომერი გამოჩნდება აქვე',
    desk_cl_no_number: 'ნომერი არ არის',
    desk_cl_no_budget_said: 'არ არის ნათქვამი',
    desk_cl_no_criteria: 'კრიტერიუმები არ არის შევსებული — არ უკითხავთ',
    desk_cl_matches: '{n} ემთხვევა',
    // ── წყარო — მთლიანი წინადადებები, არასოდეს აწყობილი ნაწილებისგან ─────────
    desk_cl_prov_ad: 'შეავსო თავად · Facebook-ის ფორმა · {campaign}',
    desk_cl_prov_fbgroup: 'ნაპოვნია Facebook-ზე · კლიენტმა ჯგუფში დადო',
    desk_cl_prov_link: 'შეავსო თავად · კლიენტის ფორმა · ბმული გაუგზავნა {agent}-მ',
    desk_cl_prov_link_anon: 'შეავსო თავად · კლიენტის ფორმა',
    desk_cl_prov_typed: 'ჩაწერა {agent}-მ — კლიენტის სიტყვები გადმოცემულია',
    desk_cl_prov_legacy: 'წყარო არ არის აღრიცხული — ვინ და როგორ შემოიყვანა, არ ვიცით',
    // ── ნდობის დონე — მდგომარეობაა, არა ქულა. პროცენტი — არასოდეს. ───────────
    desk_cl_tier_self: 'კლიენტის ნათქვამი',
    desk_cl_tier_self_tip: 'კრიტერიუმები სიტყვასიტყვით იკითხება',
    desk_cl_tier_agent: 'აგენტის ჩანაწერი',
    desk_cl_tier_agent_tip: 'მიახლოებითია',
    desk_cl_tier_legacy: 'წყარო უცნობია',
    desk_cl_tier_legacy_tip: 'სიფრთხილით, სავარაუდოდ ძველია',
    // ── მდგომარეობის სვეტის ველები ───────────────────────────────────────────
    desk_cl_row_budget: 'ბიუჯეტი',
    desk_cl_row_filled: 'შევსება',
    desk_cl_row_added: 'დამატება',
    desk_cl_row_campaign: 'კამპანია',
    desk_cl_row_link_sent: 'ბმული გააგზავნა',
    desk_cl_row_typed: 'ჩაწერა',
    desk_cl_row_rep: 'წარმომადგენელი',
    desk_cl_v_rent: 'ქირაობს',
    desk_cl_v_buy: 'ყიდულობს',
    desk_cl_v_short_term_rent: 'დღიურად',
    desk_cl_v_buy_or_rent: 'ყიდულობს ან ქირაობს',
    desk_cl_v_apartment: 'ბინა',
    desk_cl_v_house: 'სახლი',
    desk_cl_v_cottage: 'აგარაკი',
    desk_cl_v_commercial: 'კომერციული',
    desk_cl_v_office: 'ოფისი',
    desk_cl_v_land: 'მიწა',
    // See the EN block — these three were missing and printed as English codes.
    desk_cl_v_garage: 'ავტოფარეხი',
    desk_cl_v_hotel: 'სასტუმრო',
    desk_cl_v_other: 'სხვა',
    // See the EN block — „უკლიენტო" said the card had no client.
    desk_cl_no_name: 'სახელის გარეშე',
    desk_type_favorites: 'რჩეულები',
    desk_rail_drafts_n: '{n} განსახილველი',
    desk_rail_drafts_sub: 'AI-ის ნაპოვნი დრაფტები გელოდებათ.',
    desk_fav_add: 'რჩეულებში დამატება',
    desk_fav_remove: 'რჩეულებიდან ამოღება',
    desk_fav_empty: 'რჩეულები ცარიელია — დააჭირე ♡-ს ნებისმიერ ბარათზე.',
    desk_archive_add: 'ჩემი ნაკადიდან დამალვა',
    desk_archive_remove: 'ნაკადში დაბრუნება',
    desk_archive_view: 'დამალული',
    desk_hidden_here_n: 'ამ ხედში დამალულია {n}',
    desk_more: 'მეტი',
    desk_hidden_show: 'ჩვენება',
    desk_hidden_hide_again: 'ისევ დამალვა',
    desk_archive_empty: 'დამალული არაფერია — დააჭირე თვალს ბარათზე, რომ საკუთარი ნაკადიდან დამალო.',
    desk_archive_note: 'ხედავ მხოლოდ შენ. სხვების ნაკადში ობიექტი რჩება.',
    desk_leads_loading: 'ლიდები იტვირთება...',
    desk_leads_empty: 'ღია ლიდები არ არის — მესაკუთრის ახალი განცხადებები ჯერ არ მოიძებნა.',
    desk_lead_claim: 'ამ ლიდზე მუშაობა',
    desk_lead_claimed: 'აღებულია',
    desk_lead_claim_yours: 'შენ მუშაობ ამ ლიდზე',
    desk_lead_release: 'გაშვება',
    desk_lead_no_phone: 'ნომერი ჯერ არ არის — გახსენი განცხადება და ნახე',
    desk_lead_has_phone: 'ნომერი ნაპოვნია',
    desk_lead_claims_n: '{n} აგენტი მუშაობს',
    desk_lead_view_mine: 'ვმუშაობ',
    desk_lead_view_all: 'ყველა ლიდი',
    desk_lead_view_hint: 'ლიდები, რომლებზეც შენ მუშაობ',
    desk_attempt_hint: 'რა შედეგით დასრულდა ზარი? მონიშნეთ, რომ მესაკუთრეს ზედმეტად ბევრჯერ არ დავურეკოთ',
    desk_touch_nudge: '{n} ნანახი ნომერი შედეგის გარეშე',
    desk_lead_density_tried: '{n} დარეკვა · {x} არ პასუხობს',
    desk_lead_density_warm: '{n} დაინტერესდა',
    desk_lead_density_taken: '{n} ამბობს რომ ჰყავს აგენტი',
    desk_lead_density_hint: 'ეფუძნება {n} რეალურად დაფიქსირებულ ზარს — არა იმას, ვინ ნახა.',
    desk_coop_end_why: 'რატომ სრულდება?',
    desk_coop_end_deal_done_by_us: 'ჩვენ გავყიდეთ / გავაქირავეთ',
    desk_market_reason_deal_done_by_other: 'გაიყიდა / გაქირავდა',
    desk_gone_sold: 'გაიყიდა',
    desk_gone_rented: 'გაქირავდა',
    desk_gone_nocoop: 'არ თანამშ.',
    desk_confirm_yes: 'დიახ',
    desk_confirm_no: 'არა',
    desk_gone_nocoop_hint: 'მესაკუთრე არ თანამშრომლობს სააგენტოებთან: მონიშნე (გაუქმება შესაძლებელია სტატუსის ბარათში)',
    desk_gone_nocoop_confirm: 'დარწმუნებული ხართ, რომ მესაკუთრე არ თანამშრომლობს?',
    desk_card_open: 'დეტალების გახსნა',
    desk_search_label: 'ძიება Desk-ში',
    desk_search_category_label: 'ძიების კატეგორია',
    desk_gone_sold_hint: 'მესაკუთრე ამბობს, რომ გაიყიდა: მონიშნე (გაუქმება შესაძლებელია სტატუსის ბარათში)',
    desk_gone_rented_hint: 'მესაკუთრე ამბობს, რომ გაქირავდა: მონიშნე (გაუქმება შესაძლებელია სტატუსის ბარათში)',
    desk_gone_sold_confirm: 'დარწმუნებული ხართ, რომ გაიყიდა? კლიენტებს აღარ შესთავაზებთ.',
    desk_gone_rented_confirm: 'დარწმუნებული ხართ, რომ გაქირავდა? კლიენტებს აღარ შესთავაზებთ.',
    desk_gone_failed: 'ვერ შეინახა',
    desk_market_reason_owner_exclusive_elsewhere: 'ექსკლუზივი სხვაგან',
    // renamed 2026-09-12 (owner): „მესაკუთრე უარს ამბობს" said only that a refusal
    // happened; „არ თანამშრომლობს" names WHAT the owner refuses — working with
    // agencies — which is the same fact the cooperation card now carries as a flag.
    desk_market_reason_owner_declined_agent: 'არ თანამშრომლობს',
    desk_status_reported_pill: 'რეპორტირებულია',
    desk_status_we_closed_short: 'ჩვენ დავხურეთ',
    desk_status_retract_short: 'ჩაწერის მოხსნა',
    desk_coop_end_deal_done_by_other: 'გაიყიდა / გაქირავდა (სხვა სააგენტოს ან მესაკუთრის მიერ)',
    desk_coop_end_owner_exclusive_elsewhere: 'სხვა სააგენტოს აქვს ექსკლუზივი',
    desk_coop_end_owner_declined_agent: 'მესაკუთრე არ თანამშრომლობს სააგენტოებთან',
    desk_coop_end_cooperation_stale: 'მესაკუთრეს კვლავ სჭირდება დახმარება — ეს თანამშრომლობა დასრულდა',
    desk_coop_end_unknown: 'სხვა',
    desk_coop_end_deal_closed: 'გარიგება დაიდო',
    desk_coop_end_hint: 'ეს განსაზღვრავს, ქონება არქივში წავა თუ ლიდებში დაბრუნდება. მხოლოდ „არ სურს ჩემთან მუშაობა“ ზღუდავს შენ — კოლეგებს არა. თუ შენ დახურე გარიგება, შეხვედრიდან დააფიქსირე — სწორედ ის ატარებს საკომისიოს.',
    desk_lead_archive: 'არქივი',
    desk_lead_active: 'აქტიური ლიდები',
    desk_lead_reported_gone: '{n} აგენტმა მონიშნა როგორც გაყიდული/გაქირავებული',
    desk_market_report_open: 'მესაკუთრე ამბობს, რომ აღარაა / აყვანილია',
    desk_market_report_title: 'რა გითხრათ მესაკუთრემ?',
    desk_known_deal_title: 'იცით, რა ფასად გაიყიდა / გაქირავდა? (არასავალდებულო)',
    desk_known_deal_sale: 'გაიყიდა',
    desk_known_deal_rent: 'გაქირავდა',
    desk_known_deal_price_ph: 'გაყიდვის ფასი',
    desk_known_deal_rent_ph: 'თვიური ქირა',
    desk_known_deal_date: 'როდის დაიდო გარიგება',
    desk_known_deal_hint: 'გარიგების რეალური ფასი და არა მოთხოვნილი — ცალკე ინახება და ჩვენს ფასების შედარებას აზუსტებს. მხოლოდ თქვენი ჩანაწერით საჯაროდ არ გამოჩნდება.',
    desk_known_deal_saved: 'გარიგების ფასი ჩაიწერა.',
    desk_known_deal_failed: 'რეპორტი შენახულია, მაგრამ გარიგების ფასი ვერ ჩაიწერა:',
    desk_market_report_hint: 'რეპორტი ფიქსირდება თქვენი სახელით — ობიექტის ხილვადობა შემცირდება. ჩანაწერი არ იშლება; რამდენიმე დამოუკიდებელი დასტური აძლიერებს მას.',
    desk_market_report_saved: 'ჩაიწერა. მადლობა — სააგენტომ უკვე იცის.',
    desk_market_report_retract: 'შევცდი — ობიექტი კვლავ ხელმისაწვდომია',
    desk_market_report_mine: 'თქვენ დააფიქსირეთ',
    desk_market_report_badge_title: 'აგენტს მესაკუთრემ უთხრა, რომ ობიექტი აღარაა. დაუდასტურებელია — ჩამოდაბლებულია, არა წაშლილი.',
    desk_lead_reported_hint: 'აგენტმა მიუთითა, რომ სხვამ დახურა. დაუდასტურებელია — ლიდი ღია რჩება, უბრალოდ ქვევით ჩამოდის.',
    desk_coop_end_note_required: 'გთხოვთ, მიუთითოთ, რა მოხდა — „სხვა“ ახსნის გარეშე მოგვიანებით უსარგებლოა.',
    desk_coop_excl_when: 'როდის სრულდება მათი ექსკლუზივი? (მესაკუთრემ ჩვეულებრივ იცის)',
    desk_coop_excl_until_sold: 'გაყიდვამდე',
    desk_lead_exclusive_until: 'სხვა სააგენტო {d}-მდე',
    desk_lead_exclusive_until_sold: 'სხვა სააგენტო გაყიდვამდე',
    desk_lead_evaluation: 'შეფასებისთვის',
    desk_unsent_title: 'დაწყებული, გაუგზავნელი შეფასებები', desk_unsent_hint: 'ვიზიტორები, რომლებმაც sentavus.com-ზე შეფასება შეავსეს და გაუგზავნელად წავიდნენ. პასუხები შევსებისთანავე ინახება; ქონება ჯერ არ არის შექმნილი.',
    desk_unsent_min: 'წთ წინ', desk_unsent_h: 'სთ წინ', desk_unsent_d: 'დღის წინ', desk_unsent_rooms: 'ოთახი', desk_unsent_plot: 'ნაკვეთი', desk_unsent_floor: 'სართული',
    desk_unsent_more_answers: '+{n} პასუხი', desk_unsent_show_phone: 'ნომრის ნახვა', desk_unsent_no_contact: 'კონტაქტი არ დაუტოვებია', desk_unsent_empty_answers: 'ჯერ არაფერია შევსებული',
    desk_unsent_load_more: 'კიდევ {n} შეფასება', desk_unsent_open: 'გახსნა', desk_unsent_answers: 'პასუხები',
    desk_unsent_contact: 'კონტაქტი', desk_unsent_yes: 'კი', desk_unsent_no: 'არა', desk_unsent_unsure: 'არ ვიცი', desk_unsent_step: 'გაჩერდა ეტაპზე',
    desk_unsent_finish: 'რედაქტირება და გაგზავნა ფორმაში', desk_unsent_finish_hint: 'იხსნება sentavus.com-ის შეფასების ფორმა ვიზიტორის ყველა შევსებული პასუხით. იქ შეასწორეთ ან შეავსეთ და გააგზავნეთ: შეიქმნება ქონება და ლიდი, ზუსტად ისე, როგორც ვიზიტორის გაგზავნისას. გახსნა აღირიცხება, როგორც ნომრის ნახვა.',
    desk_unsent_finishing: 'აგენტმა გახსნა დასასრულებლად', desk_unsent_finish_failed: 'ფორმა ვერ გაიხსნა — სცადეთ ხელახლა', desk_unsent_load_failed: 'პასუხები ვერ ჩაიტვირთა',
    desk_unsent_q_area_basis: 'ფართი გაზომილია როგორც', desk_unsent_q_area_incl_balconies: 'ფართში აივნები შედის?', desk_unsent_q_area_sources: 'ფართის წყარო',
    desk_unsent_q_balcony_area: 'აივნის ფართი', desk_unsent_q_bedroom_area: 'საძინებლის ფართი', desk_unsent_q_building_done_now: 'შენობა ახლა დასრულებულია?',
    desk_unsent_q_building_era: 'აშენების პერიოდი', desk_unsent_q_building_status: 'შენობის სტატუსი', desk_unsent_q_building_year: 'აშენების წელი',
    desk_unsent_q_cellar_area: 'სარდაფის ფართი', desk_unsent_q_cellar_in_price: 'სარდაფი ფასში შედის?', desk_unsent_q_house_spaces: 'სახლის სივრცეები',
    desk_unsent_q_parking_level: 'პარკინგის დონე', desk_unsent_q_parking_sep_usd: 'პარკინგის ცალკე ფასი, USD',
    desk_lead_evaluation_hint: 'მფლობელმა sentavus.com-ზე შეფასება მოითხოვა — პასუხები, ნანახი შეფასება და ფოტოები ბარათზეა.',
    desk_lead_watched_agent: 'აგენტის ექსკლუზივი · {name}',
    desk_lead_watched_agent_hint: 'ეს ბინა მხოლოდ ამ აგენტთანაა: მესაკუთრის განცხადება ჩვენს ბაზაში არ გვაქვს. ავტომატური თანამშრომლობის თხოვნა მას არ ეგზავნება.',
    desk_lead_cooldown: 'პაუზაზეა {d}-მდე',
    desk_lead_cooldown_hint: 'ამ ქონებაზე ბოლო თანამშრომლობა დასრულდა. მიეცი მესაკუთრეს სივრცე — ამ თარიღზე დაბრუნდება პულში.',
    desk_attempt_promoted: '· უკვე მატჩინგშია',
    desk_attempt_needs_coop: '· ჩაწერე თანამშრომლობა, რომ მატჩინგში მოხვდეს',
    desk_attempt_ambiguous: '· რამდენიმე თანამშრომლობაა — სწორს დაუყენე „შეთანხმებული“',
    desk_attempt_no_answer: 'არ პასუხობს',
    desk_attempt_interested: 'დაინტერესდა',
    desk_attempt_refused: 'უარი',
    desk_attempt_already_taken: 'ჰყავს აგენტი',
    desk_attempt_callback_requested: 'გადარეკვა',
    desk_attempt_wrong_number: 'არასწორი ნომერი',
    desk_attempt_language_barrier: 'ენის ბარიერი',
    desk_attempt_agreed: 'შეთანხმდა',
    desk_lead_in_conversation: 'მოლაპარაკებაშია — {n}',
    desk_lead_in_conversation_hint: 'ამ მესაკუთრესთან უკვე აქვს ვიღაცას თანამშრომლობა. მატჩინგში მოხვდება მხოლოდ მაშინ, როცა თანამშრომლობა „შეთანხმებულს“ მიაღწევს.',
    desk_lead_status_promoted: 'დაწინაურებული — თანამშრომლობა ჩაწერილია',
    desk_lead_status_dead: 'მკვდარი',
    desk_facet_category: 'კატეგორია',
    desk_facet_location: 'მდებარეობა',
    desk_facet_agent: 'აგენტი',
    desk_facet_agent_search: 'მოძებნე აგენტი…',
    desk_facet_agent_none: 'აგენტი ვერ მოიძებნა',
    desk_facet_agent_inactive: 'აღარ მუშაობს',
    desk_facet_agent_show_inactive: 'ყოფილი აგენტებიც ({n})',
    desk_facet_agent_hide_inactive: 'ყოფილების დამალვა',
    desk_facet_source: 'წყარო',
    desk_facet_source_unavailable: 'მიუწვდომელია — მხოლოდ ლიდებში',
    desk_facet_not_for_identity: 'ამ ტიპისთვის არ ისმება',
    // ✅ NATIVE-CHECKED 2026-08-18, all six strings returned OK, no edits. The grammar, so the
    // next person writing one of these does not have to re-ask:
    //   · The possessor takes the DATIVE with აქვს — მიწა→მიწას, ბინა→ბინას, სახლი→სახლს, and
    //     the adjective agrees by dropping its final ი (კომერციული ფართი → კომერციულ ფართს).
    //   · The possessed thing takes the NOMINATIVE, which is the case every facet title is
    //     already in — that is why {facet} can be substituted with no per-facet handling, and
    //     why the abbreviated საძინ. survives: it holds the same case it abbreviates.
    //   · 🔑 აქვს is for INANIMATE possessors only. A template about a tenant, an owner or a
    //     client takes ჰყავს — do not copy these four for a person.
    //   · A numeral leaves the noun SINGULAR: `3 ფილტრი`, never `3 ფილტრები`.
    // ⚠️ Two of the combinations the check wrote out — `სახლს ფართობი არ აქვს`, `ბინას სართული
    // არ აქვს` — are grammatical but cannot occur: a house has area and a flat has a floor in
    // IDENTITY_BASE, and the only sheet a pill can open on a land search is `rooms`. Today the
    // one reachable sentence is `მიწას ოთახი არ აქვს`. The other three templates are there for
    // the identity that earns a swap next, not for anything on screen now.
    desk_facet_not_for_kind_land: 'მიწას {facet} არ აქვს',
    desk_facet_not_for_kind_commercial: 'კომერციულ ფართს {facet} არ აქვს',
    desk_facet_not_for_kind_house: 'სახლს {facet} არ აქვს',
    desk_facet_not_for_kind_apartment: 'ბინას {facet} არ აქვს',
    desk_search_field_filter_count: '{n} ფილტრი',
    desk_search_field_clear: 'ძებნის გასუფთავება',
    desk_facet_details: 'დეტალები',
    // მახასიათებლების ფასეტი (B37)
    // 0433 — the state-of-the-flat facet. The unknown note is the designer's Q6 wording.
    desk_facet_condition: 'მდგომარეობა',
    desk_facet_condition_reno: 'რემონტი',
    desk_facet_condition_frame: 'კარკასი',
    desk_facet_condition_help: 'რას ნიშნავს კარკასი?',
    desk_facet_condition_unknown_note: '6 045 განცხადებას მდგომარეობა ჩაწერილი არ აქვს — ისინი შესაძლო დამთხვევებში გადადის და შედეგებიდან არ ქრება.',
    desk_facet_features: 'მახასიათებლები',
    desk_feature_state_unset: 'არ არის კითხული',
    desk_feature_state_yes: 'აქვს',
    desk_feature_state_no: 'არ აქვს (ჩაწერილია, რომ არ აქვს)',
    desk_feature_state_unknown: 'არ არის მითითებული — არავის უთქვამს',
    desk_feature_state_next: 'დააჭირე შესაცვლელად',
    desk_feature_state_clear: 'დააჭირე სახელს გასასუფთავებლად',
    desk_feature_state_no_unavailable: 'ამ მახასიათებელზე „არ აქვს" არავის ჩაუწერია',
    desk_facet_features_comfort: 'კომფორტი',
    desk_facet_features_kitchen: 'სამზარეულო',
    desk_facet_features_planning: 'დაგეგმარება',
    desk_facet_features_orientation_view: 'ხედი',
    desk_facet_features_orientation: 'ორიენტაცია',
    desk_facet_features_defect: 'დეფექტები',
    desk_facet_features_furniture: 'ავეჯი და ტექნიკა',
    desk_facet_features_neighborhood: 'სამეზობლო',
    desk_facet_features_environment: 'გარემო',
    desk_facet_features_communication: 'კავშირი',
    desk_facet_features_status: 'სტატუსი',
    desk_facet_features_property_type: 'ობიექტის ტიპი',
    desk_facet_features_location_character: 'უბნის ხასიათი',
    desk_facet_features_commercial: 'კომერციული',
    desk_facet_features_financial: 'ფინანსური',
    desk_facet_features_finance: 'გადახდა და ფინანსები',
    desk_facet_features_tenant_terms: 'მოიჯარის პირობები',
    desk_facet_features_renovation: 'რემონტი',
    desk_facet_features_listing_quality: 'განცხადების ხარისხი',
    desk_facet_features_motivation: 'მოტივაცია',
    desk_facet_features_location: 'მდებარეობა',
    desk_facet_features_cooperation: 'თანამშრომლობა',
    desk_facet_features_amenity: 'აღჭურვილობა',
    desk_facet_features_utility: 'კომუნიკაციები',
    desk_facet_features_security: 'უსაფრთხოება',
    desk_facet_features_garage: 'პარკინგი და გარაჟი',
    desk_facet_features_land: 'მიწა',
    desk_facet_features_other: 'სხვა',
    desk_facet_features_outdoor: 'გარე სივრცე',
    desk_facet_features_building: 'შენობა',
    desk_facet_features_utilities: 'კომუნიკაციები და გათბობა',
    desk_facet_features_appliances: 'ტექნიკა',
    desk_facet_features_occupancy: 'სტატუსი',
    desk_facet_features_defects: 'დაფიქსირებული დეფექტი',
    desk_defect_frames: 'ფოტო {frames}',
    desk_defect_from_sibling: 'დანახულია იმავე ბინის სხვა განცხადებაში',
    desk_lineage_agent_found: 'აგენტმა შეავსო — წყაროში ცარიელი იყო',
    desk_facet_features_defects_note: 'ფოტოებიდან წაკითხული. მონიშვნა პოულობს განცხადებებს, სადაც ეს დაფიქსირდა — არ მალავს მათ. თუ არაფერია ჩაწერილი, ეს არ ნიშნავს, რომ პრობლემა არ არის.',
    desk_facet_wet_points: 'სველი წერტილი',
    desk_facet_balcony_count: 'აივნები',
    desk_range_balcony_count: 'აივანი',
    desk_owner_contact_email: 'ელ. ფოსტა',
    desk_attr_owner_email: 'მესაკუთრის ელ. ფოსტა',
    desk_facts_land_unit: 'ერთეული',
    desk_unit_sqm: 'მ²',
    desk_unit_ari: 'არი',
    desk_unit_ha: 'ჰექტარი',
    desk_val_year_invalid: 'შეიყვანე მთელი წელი {min}-დან {max}-მდე.',
    desk_facts_year_invalid: 'აშენების წელი უნდა იყოს მთელი წელი 1850-დან ექვსი წლის შემდეგ ჩათვლით — გაასწორე ან წაშალე, რომ შეინახოს.',
    desk_save_warn_title: 'შეინახა, მაგრამ ნაწილი არ ჩაიწერა:',
    desk_save_warn_unknownTags: 'მახასიათებლები, რომლებიც არ მოიძებნა',
    desk_save_warn_unknownFeatures: 'მახასიათებლები, რომლებიც არ ჩაიწერა',
    desk_save_warn_implausible: 'არადამაჯერებელი მნიშვნელობები',
    desk_save_warn_invalid: 'არასწორი მნიშვნელობები',
    desk_save_warn_derivedSkipped: 'გამოთვლადი მახასიათებლები, არ ჩაიწერა',
    desk_save_warn_nonFeature: 'ქონების მახასიათებელი არ არის',
    desk_save_warn_listingStateSkipped: 'შეთავაზების პირობები არ ჩაიწერა (ფასი არ არის)',
    desk_save_warn_needsPolicyStance: 'საჭიროებს პოზიციას შეზღუდვებში',
    desk_save_warn_claimFailed: 'ფაქტები, რომლებიც არ ჩაიწერა',
    desk_save_warn_policyWriteFailed: 'შეზღუდვები, რომლებიც არ ჩაიწერა',
    desk_save_warn_giraoTermsFailed: 'გირაოს პირობები, რომლებიც არ ჩაიწერა',
    desk_save_warn_petTermsFailed: 'ცხოველების პირობები, რომლებიც არ ჩაიწერა',
    desk_save_warn_subtypeUnknown: 'უცნობი ქვეტიპი',
    desk_save_warn_cadastralConflict: 'საკადასტრო კოდი ემთხვევა სხვა ქონებას',
    desk_save_warn_retract_refused: 'წაშლილი ველები, რომლებიც არ მოიშალა',
    desk_save_warn_features_not_yours: 'წაშლილი პასუხები, რომლებიც სხვა ჩაწერა (დარჩა)',
    desk_save_warn_features_retract_error: 'წაშლილი პასუხების მოშლა ვერ მოხერხდა',
    desk_pm_nav_label: 'სექციები',
    desk_pm_group_record: 'ჩანაწერი',
    desk_pm_group_in: 'ინფორმაციის შემოტანა',
    desk_pm_group_work: 'ობიექტზე მუშაობა',
    desk_pm_nav_property: 'ობიექტი',
    desk_pm_nav_terms: 'მოთხოვნები',
    desk_pm_nav_facts: 'ფაქტები',
    desk_pm_nav_photos: 'ფოტოები',
    desk_pm_nav_owner: 'მესაკუთრე',
    desk_pm_nav_observed: 'წყარო',
    desk_pm_nav_paste: 'ჩასმა / AI',
    desk_pm_nav_agreement: 'სააგენტო შეთანხმება',
    desk_pm_nav_conditions: 'პირობები',
    desk_pm_nav_meeting: 'ჩვენება',
    desk_pm_nav_more: 'ჩანაწერები · ისტორია',
    desk_pm_status_done: 'შევსებული',
    desk_pm_status_needs: 'გელოდებათ',
    desk_pm_status_empty: 'ცარიელი',
    desk_pm_go_to_field: 'ველზე გადასვლა',
    desk_observed_from_hint: 'ეხება ამ შენახვის ყველა მონაცემს — აირჩიე შენახვამდე.',
    desk_feature_view_unanswered: 'უპასუხო კითხვები',
    desk_feature_view_ai: 'AI-ის მიერ შევსებული',
    desk_feature_view_disagreeing: 'განსხვავებული',
    desk_feature_view_empty: 'ამ ხედში არაფერია.',
    desk_feature_disagree_mark: 'წყაროები არ ემთხვევა',
    desk_pm_required: 'სავალდებულო',
    desk_pm_agent_only: 'მხოლოდ აგენტებისთვის',
    desk_pm_nav_business: 'ბიზნესები ამ მისამართზე',
    desk_pm_nav_better: 'სჯობს იცოდე',
    desk_pm_nav_notes: 'პირადი ჩანაწერები',
    desk_pm_nav_assumed: 'ვარაუდები',
    desk_pm_nav_promises: 'დაპირებები',
    desk_pm_nav_avail: 'ხელმისაწვდომია',
    desk_pm_nav_posted: 'განთავსებულია',
    desk_pm_nav_dups: 'შესაძლო დუბლიკატები',
    desk_pm_nav_timeline: 'ისტორია',
    desk_pm_gaps_n: '{n} შესავსები',
    desk_pm_discard_confirm: 'დახუროს შენახვის გარეშე? შეუნახავი ცვლილებები, რიგში მყოფი ფაილები და დაუმუშავებელი ჩასმა დაიკარგება.',
    desk_pm_retry_uploads: 'ატვირთვის გამეორება ({n})',
    desk_pm_photos_need_version: 'ეს ფოტოები თქვენს ვერსიაზე აიტვირთება — შეავსეთ ფორმა და დააჭირეთ დაფიქსირებას.',
    desk_pm_land_area_hint: 'განცხადებაში ფართობი {n} მ² — თუ ეს ნაკვეთია, ჩაწერე აქ ნაკვეთის ფართობად.',
    desk_pm_subtype_label: 'ქვეტიპი',
    desk_batch_history_title: 'ჩასმული შეტყობინებების განახლებები',
    desk_batch_title: 'განახლება {n}',
    desk_batch_n_add: '{n} დამატება',
    desk_batch_n_conflict: '{n} წინააღმდეგობა',
    desk_batch_n_remove: '{n} მოშლა',
    desk_batch_n_ambiguous: '{n} გაურკვეველი',
    desk_batch_n_unchanged: '{n} უცვლელი',
    desk_batch_n_pending: '{n} ასარჩევი',
    desk_batch_show_text: 'ორიგინალი შეტყობინება',
    desk_batch_hide_text: 'შეტყობინების დამალვა',
    desk_batch_col_field: 'ველი',
    desk_batch_col_current: 'ახლანდელი',
    desk_batch_col_proposed: 'შემოთავაზებული',
    desk_batch_col_decision: 'გადაწყვეტილება',
    desk_batch_kind_add: 'ახალი',
    desk_batch_kind_conflict: 'წინააღმდეგობა',
    desk_batch_kind_remove: 'მოშლილი',
    desk_batch_kind_ambiguous: 'გაურკვეველი',
    desk_batch_kind_unchanged: 'უცვლელი',
    desk_batch_correction: '· წინა მნიშვნელობა არასწორია',
    desk_batch_removed: 'მოშლა',
    desk_batch_decision_accept: 'მიღებული',
    desk_batch_decision_keep: 'ახლანდელი დარჩა',
    desk_batch_decision_unresolved: 'გაურკვეველი',
    desk_batch_decision_reject: 'გაშვებული',
    desk_batch_decision_undone: 'გაუქმებული',
    desk_batch_undo: 'გაუქმება',
    desk_batch_reconsider: 'მაინც მიღება',
    desk_pm_private_version: 'ეს ქონება სხვა აგენტმა შექმნა. აქ შენახული ინფორმაცია თქვენს ვერსიაში რჩება: მხოლოდ თქვენ ხედავთ და მის ჩანაწერს არ ცვლის. თუ ერთად უნდა იმუშაოთ, სთხოვეთ, ქონება გაგიზიაროთ.',
    desk_batch_no_proposal: 'შეტყობინება წაიკითხა, მაგრამ მისგან განახლების მომზადება ვერ მოხერხდა. არაფერი შეიცვალა. სცადეთ ხელახლა ჩასმა.',
    desk_batch_add: 'დამატება',
    desk_batch_skip: 'გამოტოვება',
    desk_batch_keep: 'ახლანდელის დატოვება',
    desk_batch_use_new: 'ახალის გამოყენება',
    desk_batch_unresolved: 'გაურკვეველი',
    desk_batch_remove: 'მოშლა',
    desk_batch_no_quote: 'ამ ველისთვის დამადასტურებელი წინადადება არ მოვიდა.',
    desk_batch_current_source: 'ახლანდელი მნიშვნელობის წყარო: {source}{portal}',
    desk_batch_hide_unchanged: 'უცვლელების დამალვა',
    desk_batch_show_unchanged: '{n} უცვლელის ჩვენება',
    desk_batch_stale_title: 'განხილვის შემდეგ შეიცვალა — გადაამოწმე:',
    desk_batch_apply_anyway: 'მაინც გამოყენება',
    desk_batch_undo_changed_title: 'ეს ველები შემდეგ განახლებამ შეცვალა:',
    desk_batch_undo_changed_row: 'მიღებული {accepted}, ახლა {now} ({source})',
    desk_batch_undo_anyway: 'მაინც გაუქმება',
    desk_batch_leave: 'დატოვება',
    desk_batch_undo_continue: 'გაგრძელება',
    desk_batch_failures: 'არ შეინახა, ღიაა: {list}',
    desk_batch_not_undoable: 'არ გაუქმდა: {list}',
    desk_batch_undo_all: 'მთელი განახლების გაუქმება',
    desk_batch_apply: 'შერჩეული ცვლილებების გამოყენება ({n})',
    desk_prov_accepted_by: 'მიიღო {name}',
    desk_prov_history: 'ისტორია',
    desk_feature_capture_title: 'ყველა მახასიათებელი',
    desk_feature_capture_ai: 'AI',
    desk_feature_capture_search: 'მოძებნე მახასიათებელი… (მაგ. ბუხარი, ბაღი)',
    desk_feature_capture_hint: 'ინახება მხოლოდ ის პასუხი, რომელიც შეცვალე. ცარიელი ნიშნავს „არ ვიცი", არა „არა".',
    desk_feature_capture_cap: 'ერთ შენახვაში {n}-ზე მეტი პასუხი არ მიიღება — შეინახე და გააგრძელე.',
    desk_facts_material_unset: '— არ ვიცი —',
    desk_facts_areas_more: 'სხვა ფართობები',
    desk_feature_capture_group_bathroom: 'სველი წერტილი',
    desk_facts_parking_deeded: 'პარკინგი ცალკე რეგისტრირებული',
    desk_facet_features_hint: 'განცხადებები, სადაც ეს ჩაწერილი არ არის, შესაძლო დამთხვევებში გადადის და შედეგებიდან არ ქრება.',
    // პირობების ფასეტი (B177)
    desk_facet_terms: 'პირობები და შეზღუდვები',
    desk_facet_who: 'ვისთვისაა',
    desk_footprint_keep_mine: 'და ისინიც, რომლებზეც ვმუშაობ',
    desk_footprint_keep_mine_chip: '+ ჩემი',
    desk_who_have_client: 'გყავს კლიენტი ბაზაში?',
    desk_who_fill_from_client: 'შეავსე კლიენტიდან',
    desk_who_client_search_placeholder: 'კლიენტის სახელი…',
    desk_who_client_none: 'ჩატვირთულ კლიენტებში ვერ მოიძებნა.',
    desk_who_client_scope_note: 'ძებნა მიმდინარეობს ფიდში ჩატვირთულ კლიენტებში.',
    desk_who_client_empty: 'ამ კლიენტზე ჯერ არაფერია ჩაწერილი — ვინ იცხოვრებს.',
    desk_who_client_error: 'კლიენტის ჩატვირთვა ვერ მოხერხდა.',
    desk_facet_rental_terms: 'პირობები',
    // ⚠️ The name is NEVER inflected. Georgian genitive would need "ბერიძის", and a
    // template that glues `-ის` onto whatever arrives prints "ბერიძე-ის" — the exact
    // class of error the owner-reviewed sentence generator exists to prevent. An em-dash
    // apposition carries the same meaning and inflects nothing.
    desk_lens_from_named: 'აღებულია კლიენტის პროფილიდან — {name}.',
    desk_lens_from_unnamed: 'აღებულია ამ კლიენტის პროფილიდან.',
    desk_lens_counts: '{known} ღერძი შევსებულია · {silent} არ გვიკითხავს და არც ვვარაუდობთ.',
    desk_lens_known: 'ცნობილია · ვფილტრავთ',
    desk_lens_nobody_asked: 'არავის უკითხავს · არ ვფილტრავთ',
    desk_lens_unknown_not_unrestricted: 'ეს უცნობია და არა შეუზღუდავი. ბინა, სადაც ცხოველი აკრძალულია, მაინც გამოჩნდება — არ გვითქვამს, ჰყავს თუ არა ამ კლიენტს.',
    desk_lens_ratio: 'ვფილტრავთ {all}-დან {on} ღერძზე. დანარჩენი {off} არაფერს ავიწროებს.',
    desk_lens_answer_now: 'შეავსე ახლა',
    desk_lens_complete_named: 'შეავსე პროფილი — {name}',
    desk_lens_complete_unnamed: 'შეავსე კლიენტის პროფილი',
    desk_deal_changed_kept: 'გარიგების ტიპი შეიცვალა. დანარჩენი პასუხები შენარჩუნდა.',
    desk_deal_undo: 'ცვლილების გაუქმება',
    desk_client_fill_applied: 'ძებნა შევსებულია — {name}',
    desk_client_fill_replaced: 'წინა ფილტრები ჩანაცვლდა',
    desk_client_fill_undo: 'ჩემი ფილტრების დაბრუნება',
    desk_filter_restrict_hint: 'ჩემი კლიენტი არის / ჰყავს — ჯერ დადასტურებულები; სადაც არაფერია ჩაწერილი — შესაძლო დამთხვევებში, დარეკვა ღირს:',
    // „ვისთვისაა" — შეზღუდვების ღერძების რიგები (design handoff desk-search 6a, 2026-08-05)
    desk_who_intro: 'აღწერეთ ვინ იცხოვრებს. რასაც არ შეავსებთ — არ იკითხება და ძებნას არ ავიწროებს.',
    desk_who_not_asked: 'არ არის მითითებული',
    desk_who_row_standard: 'სტანდარტული შეზღუდვები',
    desk_who_row_other: 'სხვა',
    desk_who_asking_for: 'ვეძებთ:',
    desk_who_axes_silent: 'ღერძი მიუთითებელია',
    desk_who_shelf_note: 'სადაც პასუხი არ არის ჩაწერილი — შესაძლო დამთხვევებში გადადის, არასდროს იკარგება.',
    desk_filter_advance_max: 'გადასვლისას, მაქს.',
    desk_filter_advance_chip: 'გადასვლისას',
    desk_filter_advance_month_unit: 'თვე',
    desk_filter_minlease_stay: 'ქირაობს',
    desk_filter_minlease_chip: 'ვადა',
    desk_facet_minlease: 'ქირის ვადა',
    desk_filter_movein_by: 'გადასვლა თარიღამდე',
    desk_filter_movein_chip: 'გადასვლა',
    desk_filter_movein_hard: 'აუცილებელია — დანარჩენი დამალე (თორემ სავარაუდოებში წავა)',
    desk_filter_terms_honesty_note: 'იმალება მხოლოდ ის განცხადებები, სადაც შეზღუდვა ან პირობა რეალურად ჩაწერილია — ჩაუწერელი კვლავ ჩანს.',
    // 0249 — თარიღის ფილტრი
    desk_facet_dates: 'თარიღი',
    // 2026-07-30 label pass (values only — keys are contracts shared by the date facet AND the
    // sort menu, and saved searches store the keys): added→"ბაზაში დაემატა" (min listed_at = when
    // WE captured it), posted→"პორტალზე განთავსდა" (posted_min, RE idiom), last_seen→"ბოლოს
    // შემოწმდა" (crawler/popup liveness confirmation, listing_crawler.js:330 — NOT owner
    // activity). updated deliberately KEPT "ბოლოს შეიცვალა": updated_max is a content-change
    // timestamp; "შემოწმდა" there would falsely claim a recheck that finding-nothing also bumps.
    desk_filter_date_added: 'ბაზაში დაემატა',
    desk_filter_date_posted: 'პორტალზე განთავსდა',
    desk_filter_date_posted_or_added: 'პორტალზე განთავსდა (≈)',
    desk_filter_date_last_n_days: 'ბოლო {n} დღე',
    desk_filter_date_posted_unknown: '{n} ბინას გამოქვეყნების თარიღი საერთოდ არ აქვს — ისინი არ იმალება, შესაძლო დამთხვევებში რჩება.',
    desk_filter_date_substitute: 'როცა პორტალზე თარიღი არ არის, გამოიყენოს ჩვენთან დამატების დღე',
    desk_filter_date_last_seen: 'ბოლოს შემოწმდა',
    desk_filter_date_updated: 'ბოლოს შეიცვალა',
    desk_dates_hint_added: 'ჩვენი',
    desk_dates_hint_last_seen: 'კრაულერი',
    desk_dates_hint_updated: 'განცხადება',
    desk_dates_hint_portal: 'პორტალი',
    desk_dates_hint_empty_pct: '{pct}% ცარიელი',
    desk_dates_last_n: 'ბოლო {n} დღე',
    desk_dates_today: 'დღეს',
    desk_dates_unit: 'ერთეული',
    desk_dates_unit_days: 'დღე',
    desk_dates_unit_custom: 'სხვა…',
    desk_dates_substitute: 'უცნობის ჩანაცვლება',
    desk_dates_substitute_off: 'გამორთულია — უთარიღო შუა სვეტში რჩება. ჩართვა კითხვას ცვლის, არ აზუსტებს.',
    desk_dates_substitute_on: 'ჩართულია — სადაც პორტალი თარიღს არ იძლევა, ჩვენი დამატების დღე დგას (≈).',
    desk_filter_date_from: 'დან',
    desk_filter_date_to: 'მდე',
    desk_filter_date_posted_note: 'განცხადებების 14%-ს პორტალი გამოქვეყნების თარიღს არ უწერს. ასეთ განცხადებებს ჩვენთან დამატების დღით ვაჩვენებთ, ≈ ნიშნით — ეს თარიღი რეალურზე ახალია, რადგან განცხადებას მხოლოდ მისი პოვნის შემდეგ ვხედავთ. მოხსენით მონიშვნა, თუ გსურთ მხოლოდ გამოქვეყნების თარიღით ძიება.',
    // 0249 — ლიდზე მუშაობა და ნანახობა
    desk_facet_leadwork: 'აგენტები და ნანახობა',
    // B215 (0262): ექსპოზიციის კიბე — საფეხურების სახელები დიზაინ-სესიიდან (2026-07-29).
    desk_facet_exposure: 'ექსპოზიცია',
    desk_exposure_label: 'შეხებულია არაუმეტეს',
    desk_exposure_0: 'ხელუხლებელი',
    desk_exposure_1: 'ნანახი',
    desk_exposure_2: 'მუშავდება',
    desk_exposure_all: 'ყველა',
    desk_exposure_hint2: 'ჩვენ გავრცელებული გამორიცხულია',
    // 0294 — ჩვენი კვალი ცვლის ექსპოზიციის კიბის კონტროლს (ზემოთა exposure_* გასაღებები
    // რჩება: შენახული ძებნა კვლავ ატარებს მნიშვნელობას და ჩიპსაც აჩვენებს).
    desk_facet_ourwork: 'ჩვენი მუშაობა',
    desk_scope_everyones: 'ყველა აგენტის ნამუშევარი',
    desk_pool_searching_in: 'ეძებ',
    desk_pool_everyones: 'ყველასი',
    desk_pool_yours: 'შენი',
    desk_pool_mine: 'ჩემი',
    desk_pool_state: 'სტატუსი',
    desk_pool_state_none_clientlead: 'კლიენტის ლიდი აყვანისთანავე ტოვებს ამ პულს',
    desk_pool_state_none_lens: 'შენი თარო — არა ინვენტარის მდგომარეობა',
    desk_pool_type: 'ტიპი',
    desk_facet_leadstate: 'ლიდის მდგომარეობა',
    // 2026-07-31 — იხ. en-ის კომენტარი: სათაური = კითხვა, რომელსაც აგენტი დასვამდა;
    // ვარიანტები ერთი-ორი სიტყვაა, არა წინადადება. "ხელუხლებელი/ნანახი" შეგნებულად იმავე
    // სიტყვებია, რაც ექსპოზიციის ჩიპებში (desk_exposure_0/1) — ერთი ლექსიკა, არა ორი.
    // Stage 2 — Category level + apartment status axis (the portals' own words).
    desk_cat_residential: 'საცხოვრებელი',
    desk_cat_commercial: 'კომერციული',
    desk_cat_land: 'მიწა',
    desk_status_new: 'ახალი აშენებული',
    desk_status_old: 'ძველი აშენებული',
    desk_status_constr: 'მშენებარე',
    // ახალი პროექტები — დეველოპერების საკუთარი ბინები (core 1634, DevelopmentsPanel). ჯერ მხოლოდ Desk-ზე.
    desk_dev_title: 'ახალი პროექტები — დეველოპერების ბინები',
    desk_dev_projects: 'პროექტი',
    desk_dev_available: 'თავისუფალი',
    desk_dev_reserved: 'დაჯავშნილი',
    desk_dev_sold: 'გაყიდული',
    desk_dev_layouts: 'გეგმარება',
    desk_dev_loading: 'ახალი პროექტები იტვირთება…',
    desk_dev_error: 'ახალი პროექტები ვერ ჩაიტვირთა.',
    desk_dev_show_all: 'ყველას ნახვა',
    desk_dev_show_less: 'ნაკლები',
    desk_dev_back: 'ყველა პროექტი',
    desk_dev_developer_site: 'დეველოპერის საიტი',
    desk_dev_no_plan: 'გეგმა არ არის',
    desk_dev_rooms: 'ოთახი',
    desk_dev_bedrooms: 'საძინებელი',
    desk_dev_balcony: 'აივანი',
    desk_dev_on_offer: 'იყიდება',
    desk_dev_pick_floor: 'აირჩიე სართული',
    desk_dev_floor: 'სართ.',
    desk_dev_room: 'ოთახი',
    desk_dev_catalogue: 'დეველოპერის გამოქვეყნებული გეგმები',
    desk_dev_plan_matched: 'გეგმა ზომებით შერჩეული',
    desk_dev_price_switched_off: 'ფასები დეველოპერმა დამალა',
    desk_dev_price_not_published: 'დეველოპერი ფასებს არ აქვეყნებს',
    desk_dev_price_none: 'გასაყიდ ბინებს ფასი არ აქვს',
    desk_dev_nothing_on_offer: 'არაფერი იყიდება',
    desk_dev_status_available: 'თავისუფალი',
    desk_dev_status_reserved: 'დაჯავშნილი',
    desk_dev_status_sold: 'გაყიდული',
    desk_dev_status_not_offered: 'აღარ იყიდება',
    desk_dev_status_unknown: 'სტატუსი უცნობია',
    desk_dev_plan_single: 'ერთიანი გადახდა',
    desk_dev_plan_list: 'საფასო',
    desk_dev_plan_30_70: '30% ახლა, 70% დასრულებისას',
    desk_dev_plan_instalment_0pct: '0%-იანი განვადება',
    desk_dev_plan_instalment: 'განვადება',
    desk_dev_plan_site_converted: 'დეველოპერის ₾ კონვერტაცია',
    // B265 — subtype tokens. The Georgian words are the portals' own category names
    // (ss.ge/myhome vocabulary), so agents read the words they already know.
    desk_subtype_agricultural: 'სასოფლო-სამეურნეო',
    desk_subtype_non_agricultural: 'არასასოფლო-სამეურნეო',
    desk_subtype_hotel: 'სასტუმრო',
    desk_subtype_production_space: 'საწარმოო ფართი',
    desk_subtype_office: 'ოფისი',
    desk_subtype_universal_space: 'უნივერსალური ფართი',
    desk_subtype_warehouse: 'საწყობი',
    desk_subtype_restaurant_cafe: 'რესტორანი / კაფე',
    desk_subtype_retail: 'სავაჭრო ფართი',
    desk_subtype_garage: 'ავტოფარეხი',
    desk_subtype_parking_space: 'პარკინგის ადგილი',
    desk_subtype_non_residential_space: 'არასაცხოვრებელი ფართი',
    desk_subtype_special_purpose: 'სპეციალური დანიშნულების',
    desk_subtype_cottage: 'კოტეჯი',
    desk_subtype_country_house: 'აგარაკი',
    desk_subtype_townhouse: 'თაუნჰაუსი',
    desk_subtype_villa: 'ვილა',
    desk_footprint_label: 'გუნდის აქტივობა',
    desk_footprint_chip: 'გუნდი',
    desk_footprint_none: 'ხელუხლებელი',
    desk_footprint_viewed: 'ნანახი',
    desk_footprint_reposted: 'გავრცელებული',
    desk_engaged_label: 'მესაკუთრესთან ურთიერთობა',
    desk_engaged_no: 'ჯერ არავითარი',
    desk_engaged_yes: 'თანამშრომლობა ან შეხვედრა',
    desk_engaged_chip_no: 'ურთიერთობის გარეშე',
    desk_engaged_chip_yes: 'აქვს ურთიერთობა',
    desk_engaged_no_wa: 'WhatsApp არ აქვს',
    desk_engaged_chip_no_wa: 'მესაკუთრეს WhatsApp არ აქვს',
    // 0294 — გამყიდველი და მოტივაცია
    desk_facet_seller: 'გამყიდველი და მოტივაცია',
    desk_seller_label: 'გამყიდველს აქვს',
    desk_seller_chip: 'გამყიდველი',
    desk_seller_retail: '1 ობიექტი',
    desk_seller_small: '2–3',
    desk_seller_pro: '4+ (პროფესიონალი)',
    desk_seller_all: 'ყველა',
    desk_range_age_days: 'ბაზარზე გატარებული დრო',
    desk_age_chip: 'ბაზარზე გატარებული დრო',
    // მოძრავი ფანჯრებია, არა კალენდარული — "ბოლო კვირა", არა "ამ კვირაში".
    desk_age_preset_today: 'დღეს',
    desk_age_preset_week: 'ბოლო კვირა',
    desk_age_preset_month: 'ბოლო თვე',
    desk_age_preset_older: '30 დღეზე ძველი',
    desk_age_preset_custom: 'სხვა…',
    desk_age_unit: 'დღე',
    desk_age_kind_label: 'როგორ იზომება',
    desk_age_kind_first_posted: 'პირველი გამოქვეყნებიდან',
    desk_age_kind_current_run: 'ბოლო განახლებიდან',
    desk_age_kind_hint: 'პორტალები განსხვავდება: myhome თავდაპირველ თარიღს ინახავს, ss და korter განახლებისას ნულდება. თარიღის გარეშე განცხადებები იმალება არა, არამედ შესაძლო დამთხვევებში გადადის.',
    desk_range_views: 'ნახვები',
    desk_promoted_label: 'VIP რეკლამა',
    desk_promoted_yes: 'დიახ',
    desk_promoted_no: 'არა',
    desk_promoted_chip_yes: 'VIP რეკლამით',
    desk_promoted_chip_no: 'VIP რეკლამის გარეშე',
    desk_reposts_label: 'ჩვენი განცხადებები',
    desk_reposts_chip: 'ჩვენი განცხადებები',
    desk_reposts_0: 'არ გვაქვს',
    desk_reposts_1_2: '1–2',
    desk_reposts_3p: '3+',
    // ── Advert quality (0702-0704, v55, owner-asked 2026-08-22) ──────────────
    desk_facet_advert_quality: 'განცხადების ხარისხი',
    desk_photos_label: 'ფოტოები',
    desk_photos_chip: 'ფოტოები',
    desk_photos_none: 'არ არის',
    desk_photos_thin: '3 ან ნაკლები',
    desk_photos_ok: '4 და მეტი',
    desk_quality_text_label: 'ინფორმაცია',
    desk_no_description: 'აღწერა არ არის',
    desk_missing_facts: 'აკლია ინფორმაცია',
    desk_quality_owner_note: 'ითვლება მესაკუთრის განცხადებით — ჩვენი გადაზიარებები არ ითვლება',
    desk_owner_platforms_label: 'რამდენ პორტალზე დევს',
    desk_owner_platforms_note: 'ჩვენი განცხადებები არ ითვლება',
    desk_owner_platforms_chip: 'მესაკუთრის პორტალები',
    desk_owner_platforms_1: '1 პორტალზე',
    desk_owner_platforms_2: '2 პორტალზე',
    desk_owner_platforms_3p: '3+ პორტალზე',
    desk_spread_label: 'ბაზარზე გავრცელება',
    desk_spread_chip: 'კონკურენტები',
    desk_spread_1: '1 სააგენტო',
    desk_spread_2_3: '2–3',
    desk_spread_4p: '4+',
    desk_spread_all: 'ყველა',
    desk_public_facet_title: 'საჯარო გვერდი',
    desk_public_facet_hint: 'ამ ობიექტის გამოქვეყნება საჯარო საიტზე (/p გვერდი)',
    desk_public_on: 'გამოქვეყნებულია',
    desk_public_off: 'არ არის გამოქვეყნებული',
    desk_public_by_other: 'კოლეგამ გამოაქვეყნა',
    desk_public_publish: 'გამოქვეყნება',
    desk_public_unpublish: 'მოხსნა',
    desk_public_view: 'გვერდის ნახვა',
    desk_public_price_ph: 'სასურველი ფასი',
    desk_public_note: 'საჯაროდ ჩანს მხოლოდ ფასი და გარიგების ტიპი — საკომისიო და კონტაქტები არასდროს. მისამართის სიზუსტე გამჟღავნების პოლიტიკას მიჰყვება.',
    desk_near_label: 'ახლოს (სიახლოვე)',
    desk_complex_label: 'კომპლექსი (სწორედ ამ კომპლექსში)',
    desk_complex_hint: 'ეს არ არის სიახლოვე — ბინა უნდა იყოს მასში. რამდენიმე ნიშნავს ნებისმიერს.',
    desk_complex_search_ph: 'მოძებნეთ კომპლექსი…',
    desk_complex_remove: 'წაშლა',
    desk_near_hint: 'ყველა წერტილი უნდა შესრულდეს — მაგ. მეტროსთან ახლოს ᲓᲐ სკოლასთან ახლოს.',
    desk_near_add_ph: 'დაამატე ადგილი — მეტრო, პარკი, სკოლა…',
    desk_near_walk: 'ფეხით',
    desk_near_bus: 'ავტობუსით',
    desk_near_meters: 'მ',
    desk_near_tier_toggle: 'ფეხით/ავტობუსით რეჟიმის გადართვა (რადიუსი ამ რეჟიმის ნაგულისხმევზე დაბრუნდება)',
    desk_party_dedup_holders: '{n} მფლობელი — საზიარო ნომერი?',
    desk_party_dedup_needs_review: 'გადასამოწმებელი',
    desk_party_dedup_name_sim: 'სახელები ემთხვევა {p}%',
    desk_shared_lines_title: 'საზიარო / საოფისე ნომრები',
    desk_shared_lines_hint: 'ნომრები, რომლებსაც 3+ სხვადასხვა სახელის ადამიანი ფლობს — ასეთ ნომერზე დამთხვევა საკონტაქტო წერტილია და არა იდენტობა.',
    desk_shared_lines_row: '{h} მფლობელი · {n} სახელი',
    desk_shared_lines_ours: '{n} ჩვენი აგენტი',
    desk_shared_lines_empty: 'საზიარო ნომრის კანდიდატები არ არის.',
    desk_filter_claims_label: 'რამდენი აგენტი მუშაობს',
    desk_filter_claims_chip: 'აგენტი',
    desk_filter_claims_lt: 'ნაკლები ვიდრე',
    desk_filter_claims_gt: 'მეტი ვიდრე',
    desk_filter_claims_none_hint: '0 ან 1-ზე ნაკლები ნიშნავს, რომ არავინ მუშაობს.',
    desk_filter_seen_label: 'ნანახობა',
    desk_filter_seen_unseen_by_me: 'მე არ გამიხსნია',
    desk_filter_seen_seen_by_me: 'მე უკვე გავხსენი',
    desk_filter_seen_unseen: 'არავის გაუხსნია',
    desk_filter_seen_seen: 'ვიღაცას გახსნილი აქვს',
    desk_filter_seen_hint: 'გახსნილი ნიშნავს, რომ ვიღაცამ ნახა ობიექტი. ეს არ ნიშნავს, რომ უარი თქვა.',
    desk_search_sort_commission: 'საკომისიო',
    desk_filter_restrict_prompt: 'ჩემი კლიენტისთვის:',
    desk_filter_mode_hide: 'დაიშვება',
    desk_filter_mode_allow: 'მხოლოდ თუ დაიშვება',
    desk_filter_mode_restricted: 'არ დაიშვება',
    desk_stance_clear: 'ნებისმიერი',
    desk_terms_deposit_short: 'დეპოზიტი',
    desk_movein_heading: 'დამქირავებელი იხდის გასაღების მიღებამდე',
    desk_movein_mixed_currency: 'ნაწილები სხვადასხვა ვალუტაშია — ჯამი არ ჩანს.',
    desk_movein_incomplete: 'ყველაფერი ჯერ არ არის დაზუსტებული — ეს ჯამის ნაწილია, არა სრული.',
    desk_facet_see_all: 'ყველას ნახვა ({n})',
    desk_district_direct_hint: 'ამ უბანში პირდაპირ მიმაგრებული განცხადებები, ქვე-უბნის გარეშე. ზემოთ მოცემულ რიცხვში უკვე შედის — მთლიანის სანახავად მონიშნე უბანი.',
    desk_chip_district_own: '{name} · პირდაპირ',
    desk_search_identity_eyebrow: 'რას ეძებ',
    desk_search_narrow_label: 'დააზუსტე',
    desk_facet_not_asked: '{n} კითხვა აქ არ ისმება — რომელი?',
    desk_readout_matches: 'ემთხვევა',
    desk_readout_maybe: 'შეიძლება ემთხვეოდეს — მონაცემი არასრულია',
    desk_readout_ruled_out: 'გამოირიცხა',
    desk_hatch_thats_all: 'სულ ესაა — {n} ამ ხედში.',
    desk_hatch_none_here: 'ამ ხედში ამის შესაბამისი არაფერია.',
    desk_hatch_leads_n: 'იმავე ძიებას {n} ლიდი ემთხვევა, რომელსაც ჯერ არავინ დაურეკა.',
    desk_hatch_leads_unknown: 'იმავე ძიებას შესაძლოა ლიდებიც ემთხვევა, რომელსაც ჯერ არავინ დაურეკა.',
    desk_hatch_go: 'ლიდებში ძებნა →',
    desk_pool_kept: 'ფილტრები შენარჩუნდა.',
    desk_pool_dropped_sources: 'წყარო ({n}) მოიხსნა — ის მხოლოდ ლიდებზე მუშაობს.',
    desk_pool_in: '—',
    desk_pool_undo: 'გადართვის გაუქმება',
    desk_facet_show_less: 'ნაკლების ჩვენება',
    desk_search_commit_hint: 'Enter ↵ — სრული ძებნა (ქართული↔ლათინური ჩათვლით)',
    desk_search_recent: 'ბოლო ძებნები',
    desk_search_saved: 'შენახული ძებნები',
    desk_search_save: 'ამ ძებნის შენახვა',
    desk_search_saved_delete: 'შენახული ძებნის წაშლა',
    desk_search_clear_all: 'ყველა ძებნის გასუფთავება',
    desk_search_unapplied: 'ფილტრად არ გამოყენებულა: {words}',
    desk_search_unapplied_hint: 'ეს სიტყვები ძიების პირობად ვერ ამოიცნო, ამიტომ შედეგი მათ არ შეუმცირებია. თუ მნიშვნელოვანია, მიუთითეთ პანელში — თარიღი, პირობები და მდგომარეობა ცალკე კონტროლებია.',
    desk_search_rename: 'შენახული ძებნის გადარქმევა',
    desk_search_rooms_short: 'ოთ.',
    desk_search_save_needs_agent: 'შესანახად საჭიროა თქვენს ლოგინთან დაკავშირებული აგენტის პროფილი',
    desk_search_sort_title: 'დალაგება',
    desk_search_map_pill: 'რუკა',
    desk_search_period_title: 'პერიოდი',
    desk_search_overlay_back: 'ძებნის დახურვა',
    desk_search_clear_box: 'გასუფთავება',
    desk_search_kw_remove: 'ფილტრის მოხსნა',
    desk_search_widen_all_time: 'მოძებნე მთელ ისტორიაში — ეს ხედი მხოლოდ არჩეულ პერიოდს ფარავს',
    desk_search_preview_loaded: '{n} ჩატვირთულიდან',
    desk_search_preview_whole_base_only: 'მთელ ბაზაში ეძებს',
    desk_search_id_not_captured: 'განცხადების ID {id} ამოცნობილია — მაგრამ ამ ID-ით შენახული განცხადება არ არსებობს. სავარაუდოდ ჯერ არ არის სისტემაში: გახსენი განცხადება პლატფორმაზე და შეინახე.',
    desk_search_preview_enter: 'Enter ↵ — ყველაფერში ძებნა',
    desk_suggest_elsewhere: 'შემოთავაზებები სხვაგან (მიახლოებითი, არა სრული ძებნა):',
    desk_suggest_client_tag: 'კლიენტი',
    desk_suggest_business_tag: 'ბიზნესი',
    desk_suggest_complex_tag: 'კომპლექსი',
    desk_suggest_complex_family: '{dev} — ყველა პროექტი',
    desk_suggest_street_tag: 'ქუჩა',
    desk_cl_ignored: 'კლიენტების ლიდები ამით ვერ იფილტრება: {list}. ნაჩვენებია მათ გარეშე.',
    desk_suggest_street_whole: 'მთელი ქუჩა — ნომერი {n} არ იფილტრება',
    desk_search_by_image_tooltip: 'ფოტოთი ძებნა — იპოვის განცხადებებს ამ სურათით',
    desk_search_image_chip: 'სურათი · {n} დამთხვევა',
    desk_search_image_failed: 'ფოტოთი ძებნა ვერ მოხერხდა',
    desk_suggest_unnamed_client: 'უსახელო კლიენტი',
    desk_search_coverage: 'მოძებნილია: მისამართები · უბნები · განცხადების ID · ტელეფონები — აღწერები და კლიენტის სახელები არა',
    desk_search_cap_notice: 'კლიენტების სია ჩატვირთვის ლიმიტს უახლოვდება, ამიტომ ძველი კლიენტი შესაძლოა აქ ჯერ არ იყოს ჩატვირთული — ეს ჩატვირთვის ხარვეზი შეიძლება იყოს და არა არარსებული ჩანაწერი. სცადეთ უფრო ვიწრო ძებნა, ან მიმართეთ ადმინს (ძებნის backend-ს სჭირდება Phase 2 განახლება).',
    desk_search_zero_results: 'შედეგი ვერ მოიძებნა:',
    desk_zero_shelf_follows: 'ზუსტი დამთხვევა არ არის. ქვემოთ — შესაძლო დამთხვევები, რომელსაც ბაზა ვერ ადასტურებს და ვერც გამორიცხავს.',
    desk_search_no_exact: 'ზუსტი დამთხვევა ვერ მოიძებნა.',
    desk_search_failed_title: 'ძებნა ვერ დასრულდა',
    desk_search_failed_hint: 'ეს კავშირის ან სერვერის პრობლემაა — არ ნიშნავს, რომ შედეგი არ არსებობს.',
    desk_search_retry: 'ხელახლა ცდა',
    desk_search_still_running: 'ძებნა გრძელდება — {n} წმ. ფართო ფილტრი მთელ ბაზაში ეძებს.',
    desk_search_partial: '{m}-დან {n} შედეგი ვერ ჩაიტვირთა. ქვემოთ ნაჩვენები სია არასრულია.',
    desk_search_related: 'მსგავსი ძებნები',
    desk_search_all_types: 'ყველა ტიპი',
    desk_search_all_cities: 'ყველა ქალაქი',
    desk_search_district_any: 'უბანი — ნებისმიერი',
    desk_search_district_selected: 'უბანი — არჩეულია ({n})',
    desk_search_sort_newest: 'დამატებული',
    desk_search_sort_activity: 'აქტიური',
    desk_search_sort_ppm2: 'ფასი/მ²',
    desk_search_sort_fresh: 'ახალი ნანახი',
    desk_search_sort_posted: 'ახლად გამოქვეყნებული',
    desk_search_sort_oldest: 'ჯერ ძველები',
    desk_search_sort_updated: 'განახლებული',
    desk_search_sort_cheapest: 'ყველაზე იაფი',
    desk_search_sort_priciest: 'ყველაზე ძვირი',
    // იხ. en-ის კომენტარი: ეს სორტი მხოლოდ `area_sqm`-ს ეყრდნობა (შენობა), ნაკვეთს არა.
    desk_search_sort_largest: 'ყველაზე დიდი შენობა',
    // 2026-07-31 — იხ. en-ის კომენტარი. ჯგუფი აღარ არის თარიღის სახელი, არამედ ის, თუ
    // რაზეა ეს თარიღი: განცხადება / ჩვენი ბაზა / მონიტორინგი.
    desk_sort_group_smart: '⭐ რეკომენდებული',
    desk_sort_group_money: '💰 ფასი',
    desk_sort_group_ad: '📰 განცხადება',
    desk_sort_group_ours: '📥 senTavus-ში',
    desk_sort_group_monitor: '🤖 მონიტორინგი',
    desk_sort_more: 'სხვა…',
    desk_sort_less: 'დამალვა',
    desk_sort_flip_hint: 'ხელახლა დააჭირე მიმართულების შესაცვლელად',
    desk_sort_opt_commission: 'საკომისიო',
    desk_sort_opt_ppm2: '1 მ²-ის ყველაზე დაბალი ფასი',
    // იხ. en-ის კომენტარი: სტრიქონის სახელი მუდმივია, იცვლება მხოლოდ მიმართულების ხაზი.
    desk_sort_id_ad_age: 'განცხადების ასაკი',
    desk_sort_id_updated: 'ბოლო განახლება',
    desk_sort_id_imported: 'პირველად შენახული',
    desk_sort_id_added_sentavus: 'senTavus-ში დამატებული',
    desk_sort_opt_best_match: 'საუკეთესო შანსი',
    desk_sort_hint_best_match: 'ყველაზე ღირებული შანსები პირველ რიგში — senTavus-ის ანალიზის მიხედვით',
    desk_sort_id_started_working: 'მუშაობის დაწყება',
    desk_source_sentavus: 'senTavus',
    desk_source_added_by_me: 'ჩემი დამატებული',
    desk_source_sentavus_hint: 'პორტალის გარეშე: ხელით, AI-ით ან მფლობელის ბმულით შემოსული',
    desk_sort_id_verified: 'ბოლო შემოწმება',
    desk_sort_dir_newest: 'ჯერ ახალი',
    desk_sort_dir_oldest: 'ჯერ ძველი',
    desk_sort_dir_newest_short: 'ახალი',
    desk_sort_dir_oldest_short: 'ძველი',
    desk_sort_hint_activity: 'ბოლოს შეცვლილი, დამატებული ან ხელახლა განთავსებული',
    desk_sort_hint_commission: 'ჯერ ყველაზე მაღალი ხილული საკომისიო',
    desk_sort_hint_posted: 'როდის გამოჩნდა პირველად პორტალზე',
    desk_sort_hint_updated: 'შეიცვალა ფასი, ფოტოები ან აღწერა',
    desk_sort_hint_added: 'როდის შევინახეთ პირველად',
    desk_sort_hint_added_sentavus: 'როდის მოგვივიდა ეს ბინა პირველად (შემდეგი განცხადებები და ჩვენი გაზიარებები არ ითვლება)',
    desk_sort_hint_started_working: 'როდის დაიწყე ამ ბინაზე მუშაობა',
    desk_sort_hint_fresh: 'როდის დაადასტურა ბოლოს არგომ, რომ განცხადება ჯერ კიდევ ონლაინაა',
    desk_search_period_today: 'დღეს',
    desk_search_period_7d: 'ბოლო 7 დღე',
    desk_search_period_30d: 'ბოლო 30 დღე',
    desk_search_period_90d: 'ბოლო 90 დღე',
    desk_search_period_all_time: 'ყოველთვის',
    desk_search_no_recognitions: 'ამოცნობის გარეშე',
    desk_search_sorted_by_match_hint: '🎯 სორტირებულია დამთხვევით — რასაც AI მიაკუთვნებს, დაპინულია ზემოთ.',
    desk_search_remove_filter: 'ფილტრის წაშლა',
    desk_search_stranded_hint: 'ამ ტიპის ქონებას არ ეხება — შენახულია, მაგრამ არ ფილტრავს',
    desk_search_showing_of: 'ჩატვირთულია {m}, ნაჩვენებია {n}',
    desk_search_listings_reduction: '· განცხადებები −{pct}%',
    desk_search_hidden_missing_data: '{n} დამალულია მონაცემების ნაკლებობის გამო',
    desk_search_clear_filters: 'ფილტრების გასუფთავება',
    desk_search_open_tools_tooltip: 'დეტალური ძიების გახსნა',
    desk_search_tools_rail_label: '⚙ დეტალური ძიება · {n}',
    desk_search_tools_panel_header: '⚙ დეტალური ძიება',
    desk_search_tools_clear: 'გასუფთავება',
    desk_search_collapse_tools_tooltip: 'დეტალური ძიების ჩაკეცვა',
    desk_search_show_results: 'ნახე {n} შედეგი',
    desk_search_deal_label: 'გარიგება',
    desk_search_any_option: 'ნებისმიერი',
    desk_search_type_label: 'ტიპი',
    desk_search_status_label: 'სტატუსი',
    desk_entry_filter_all: 'ყველა',
    desk_entry_filter_active: 'აქტიური',
    desk_entry_filter_closed: 'დახურული',
    desk_entry_filter_archived_suboption: '↳ არქივირებული',
    desk_search_price_range_cleared: 'ფასის ფილტრი გასუფთავდა — ახალ ტიპს სხვა ფასები აქვს.',
    desk_range_rooms: 'ოთახი',
    desk_range_bedrooms: 'საძინ.',
    desk_range_bathrooms: 'სველი წ.',
    desk_range_floor: 'სართული',
    desk_range_total_floors: 'სულ სართ.',
    desk_top_floor: 'ბოლო სართული',
    desk_range_from_ph: '-დან',
    desk_range_to_ph: '-მდე',
    desk_empty_no_entries: 'ჩანაწერები ჯერ არაა.',
    desk_empty_no_match_filters: 'ამ ფილტრებით არაფერი მოიძებნა.',
    desk_home_meetings: 'მოახლოებული შეხვედრები',
    desk_next_up: 'შემდეგი',
    desk_next_meeting: 'შემდეგი: {when} · {who}',
    desk_scope_mine: 'ჩემი',
    desk_scope_office: 'ჩემი ოფისი',
    desk_scope_company: 'ყველა ოფისი',
    desk_scope_pool: 'საერთო ბაზა',
    desk_home_deals: 'მიმდინარე გარიგებები',
    desk_home_recent_props: 'ბოლოს დამატებული ობიექტები',
    desk_home_recent_clients: 'ბოლო კლიენტები',
    desk_home_recent_leads: 'ბოლო ლიდები',
    desk_home_recent_viewed: 'ბოლოს ნანახი',
    desk_home_no_clients: 'ბოლო კლიენტები არაა.',
    desk_home_show_all: 'ყველას ნახვა →',
    desk_home_back: 'მთავარზე დაბრუნება',
    desk_home_no_meetings: 'დაგეგმილი ჩვენება არაა.',
    desk_home_browse_props: 'ობიექტების დათვალიერება',
    desk_home_add_client_cta: 'კლიენტის დამატება',
    desk_home_no_viewed: 'ჯერ არაფერი გინახავს — გახსნილი ბარათები აქ გამოჩნდება.',
    desk_strip_recent: 'ბოლო ჩანაწერები',
    desk_strip_notifications: 'შეტყობინებები',
    desk_strip_notifications_n: '{n} შეტყობინება',
    desk_strip_add: 'დამატება',
    desk_strip_cockpit_collapsed: 'კოკპიტი · აკეცილი',
    desk_strip_cockpit_open: 'კოკპიტი · გახსნილი',
    desk_notif_panel_empty: 'ჯერ არაფერია — დრაფტები, გადაწყვეტილებები და ფასის ცვლილებები აქ გამოჩნდება.',
    desk_notif_panel_loading: 'იტვირთება…',
    desk_notif_zone_waiting: 'თქვენ გელოდებათ',
    desk_notif_zone_yourwork: 'თქვენს საქმეზე',
    desk_notif_zone_streams: 'ნაკადები · {days} დღე',
    desk_notif_zone_empty_a: 'არავინ გელოდებათ.',
    desk_notif_empty_hint: 'აქ გამოჩნდება, როცა კლიენტი ნახვას მოითხოვს, კოლეგა წვდომას დაელოდება, ან შეხვედრა შედეგის გარეშე დარჩება.',
    desk_notif_stream_price: 'ფასის ცვლილება',
    desk_notif_stream_ended: 'პორტალიდან მოხსნილი',
    desk_notif_stream_opens: 'ბმული გახსნეს',
    desk_notif_stream_lifted: '{n} თქვენსზე, ზემოთ',
    desk_notif_stream_rest: 'დანარჩენი {n} — აქტივობაში',
    desk_notif_more_salient: 'კიდევ {n} თქვენს საქმეზე',
    desk_notif_less_salient: 'ნაკლების ჩვენება',
    desk_notif_mark_read: 'წაკითხულად მონიშვნა',
    desk_notif_dismiss: 'დახურვა',
    desk_notif_dismissed: 'დახურულია',
    desk_notif_undo: 'დაბრუნება',
    desk_notif_queues: 'რიგები',
    desk_nudge_why_starting_soon: 'შეხვედრა მალე',
    desk_notif_queue_rate: '{n} ელოდება · {done} ამ თვეში',
    desk_queue_party_merge: 'პიროვნების გაერთიანება',
    desk_queue_complex_link: 'განვითარების დაბმა',
    desk_queue_district_dispute: 'უბნების დადასტურება',
    desk_notif_ai_alert: 'AI-ს პრობლემა — შეამოწმეთ Admin → AI',
    desk_notif_system_alert: 'ფონური სამუშაოს პრობლემა',
    desk_fresh_seen_ago: 'ნანახია {t} წინ',
    desk_fresh_not_seen: 'არ ჩანს {n} დღე',
    desk_fresh_hint: 'როდის ჩანდა ეს განცხადება ბოლოს პლატფორმაზე',
    desk_hidden_unknown_line: '{n} დამალულია არასრული მონაცემის გამო — უცნობი ≠ არ ემთხვევა',
    // 0537/0538 — ქუჩის ფასეტი. ⚠️ ქართული ვერსია მშობლიურ შემმოწმებელს ჯერ არ უნახავს
    // (იგივე რიგი, რაც MATCHING_UI_STRINGS_ROUND3-ს) — თუ ფრაზა უხერხულია, აქ შეასწორე.
    desk_facet_street: 'ქუჩა',
    desk_facet_complex: 'კომპლექსი',
    // კომპლექსის სათაურის ბარათი ფიდის თავზე (მფლობელი 2026-09-10).
    desk_cxhead_developer: 'დეველოპერი',
    desk_cxhead_all_projects: 'ყველა პროექტი',
    desk_cxhead_district: 'უბანი',
    desk_cxhead_status: 'სტატუსი',
    desk_cxhead_eta: 'ჩაბარება',
    desk_cxhead_blocks: 'ბლოკი',
    desk_cxhead_floors: 'სართული',
    desk_cxhead_address: 'მისამართი',
    desk_cxhead_aliases: 'ასევე იწერება',
    desk_cxhead_show_map: 'რუკაზე',
    desk_cxhead_remove: 'კომპლექსის ფილტრის მოხსნა',
    desk_cxhead_open_image: 'სურათის სრულად გახსნა',
    desk_facet_near_radius: 'მანძილი წერტილიდან',
    desk_map_point_tool: 'ძებნა წერტილის გარშემო',
    desk_map_area_tool: 'არეალის დახატვა',
    desk_map_clear_shapes: 'არეალის გასუფთავება',
    desk_map_point_hint: 'დააჭირეთ რუკას იმ ადგილის გარშემო საძებნად',
    desk_map_area_hint: 'შემოხაზეთ თითით ან მონიშნეთ მინიმუმ 3 კუთხე · შედეგებისთვის დააჭირეთ გამოყენებას',
    desk_map_area_limit: 'მაქსიმუმ 10 საძიებო არეალი',
    desk_map_draw_cancel: 'გაუქმება',
    desk_map_draw_undo: 'წერტილის გაუქმება',
    desk_map_draw_clear: 'ნახაზის გასუფთავება',
    desk_map_draw_apply: 'არეალის გამოყენება',
    desk_facet_map_area: 'დახატული არეალი',
    desk_near_radius_hint: 'რამდენად შორს ითვლება „{place}"-თან ახლოს.',
    desk_near_default: 'ნაგულისხმევი',
    desk_facet_project: 'პროექტი',
    desk_facet_furnishing: 'ავეჯი',
    desk_facet_added_how: 'როგორ დაემატა',
    desk_facet_apt_class: 'კლასი',
    desk_facet_apt_class_note: 'რამდენად ძვირია მოპირკეთება — სხვა კითხვაა, ვიდრე რამდენად დასრულებულია.',
    desk_added_how_crawler: 'არგომ იპოვა',
    desk_added_how_agent: 'აგენტმა დაამატა',
    desk_added_how_evaluation: 'შეფასების მოთხოვნა (sentavus.com)',
    desk_added_how_note: 'როგორ მოხვდა ჩვენს ბაზაში — და არა სად არის განთავსებული.',
    desk_cond_state_unset: 'დააჭირეთ: უნდა ჰქონდეს ეს მდგომარეობა',
    desk_cond_state_yes: 'უნდა ჰქონდეს — დააჭირეთ გამოსარიცხად',
    desk_cond_state_no: 'გამოირიცხა — დააჭირეთ გასასუფთავებლად. უცნობი მდგომარეობა არ იშლება, გადადის „შეიძლება ემთხვეოდეს“-ში.',
    desk_project_historical: 'ისტორიული / ტრადიციული',
    desk_project_standard: 'სტანდარტული პროექტები',
    desk_complex_none: 'ვერაფერი მოიძებნა',
    desk_facet_metro: 'მეტრო',
    desk_street_pick_district_first: 'ჯერ აირჩიე უბანი — მისი ქუჩები აქ გამოჩნდება',
    desk_street_search_ph: 'მოძებნე ქუჩა…',
    desk_street_see_all: 'ყველას ნახვა ({n})',
    desk_street_none_here: 'ამ უბანში ქუჩა ჯერ ჩაწერილი არ არის',
    desk_street_no_match: 'ამ უბნის ქუჩებში ვერაფერი მოიძებნა.',
    desk_street_counts_pending: 'რიცხვები დაბრუნდება, როცა ქუჩის არჩევანს მოხსნი',
    desk_street_shelf_note: 'განცხადებები, სადაც ქუჩა ჩაწერილი არ არის, შესაძლო დამთხვევებში გადადის — შედეგებიდან არ ქრება.',
    // ⚠️ UNREVIEWED BY A NATIVE SPEAKER, like the three string sets before it. "ქუჩის
    // ცენტრიდან" is the load-bearing phrase — it is what keeps this from reading as
    // "on the street". If it gets reworded, that phrase must survive the rewording.
    desk_street_see_more: 'კიდევ +{n}',
    desk_street_nearby_optin: 'ასევე მაჩვენე ქუჩის ცენტრიდან {m} მ-ის რადიუსში მდებარე განცხადებები, რომელთა მისამართიც სხვა ქუჩას ასახელებს',
    desk_nearby_street_header: 'ახლოს, მაგრამ არა ამ ქუჩაზე — {shown} ქუჩის ცენტრიდან {m} მ-ის რადიუსში. მათი მისამართი სხვა ქუჩას ასახელებს.',
    desk_nearby_street_badge: '{street} · ~{m} მ ქუჩის ცენტრიდან',
    desk_street_coverage: 'ქუჩა ჩაწერილია {district}ს {total}-დან {n}-ზე. დანარჩენები შესაძლო დამთხვევებში ჩანს.',
    desk_street_none_exact: 'ზუსტად {street}-ზე არაფერია — ნაჩვენებია {n} ახლომდებარე',
    desk_street_some_exact: '{on} {street}-ზე · კიდევ {near} ახლომდებარე',
    desk_street_all_exact: '{on} {street}-ზე',
    desk_hidden_unknown_show: 'მაინც ჩართე',
    desk_search_committed_base: 'მთელი ბაზა · {n}',
    desk_too_broad_line: '{n} შედეგი — დააზუსტე:',
    // იხ. en-ის კომენტარი: სერვერი ითვლის კლასტერებს (ობიექტებს), არა განცხადებებს.
    desk_result_count_leads: '{n} ლიდი',
    desk_result_count_properties: '{n} ობიექტი',
    desk_result_count_matching: '{n} ემთხვევა თქვენს ძიებას',
    desk_result_count_possible: '+{n} შესაძლო',
    desk_too_broad_show_all: 'მაინც მაჩვენე ყველა',
    desk_zero_capture_now: 'შეინახე ახლა',
    desk_zero_open_source: 'გახსენი წყარო',
    prefs_menu_title: 'პარამეტრები',
    prefs_currency: 'ვალუტა',
    prefs_language: 'ენა',
    prefs_theme: 'თემა',
    prefs_office: 'ოფისი',
    prefs_view_as: 'ნახვა როგორც',
    desk_pagination_prev: '← წინა',
    desk_pagination_page_of: 'გვერდი {n} / {total}',
    desk_pagination_next: 'შემდეგი →',
    desk_pagination_jump: 'გვერდზე გადასვლა',
    desk_pagination_estimated_page: 'სავარაუდო — გახსნისას ჩაიტვირთება',
    desk_first_floor: 'პირველი სართული',
    desk_facet_first_floor: 'პირველი სართული',
    desk_facet_flag_exclude_next: 'დააჭირეთ გამოსარიცხად (✕)',
    desk_facet_exclude_hint: 'გამორიცხულია — დააჭირეთ გასაუქმებლად',
    desk_facet_inherited_hint: 'მშობლისგან მემკვიდრეობით — დააჭირეთ გამონაკლისისთვის',
    desk_facet_exclude_next: 'კიდევ დააჭირეთ გამოსარიცხად',
    desk_facet_name_deselect_hint: 'სახელზე დაჭერა მოხსნის არჩევანს',
    desk_pagination_more_pending: 'დარჩენილი შედეგების ჩატვირთვისას გვერდები დაემატება',
    desk_pagination_loading: 'იტვირთება…',
    desk_load_more_listings: 'მეტი განცხადების ჩატვირთვა ({loaded} / {total})',
    desk_load_more_properties: 'მეტი ობიექტის ჩატვირთვა ({loaded} / {total} ჯგუფი)',
    desk_autoload_progress: 'იტვირთება… {loaded} / {total}',
    desk_possible_load_more: 'მეტი შესაძლო დამთხვევის ჩატვირთვა ({loaded} / {total})',
    desk_possible_loading: 'იტვირთება…',
    desk_possible_matches_header: '🤔 შესაძლო დამთხვევები — {total} ობიექტი, რომელსაც ბაზა ვერ ადასტურებს ან გამორიცხავს (ნაჩვენებია {shown} — თავში ისინი, ვისაც მსგავს კითხვებზე უკვე უპასუხია)',
    desk_search_tools_bare: 'დეტალური ძიება',
    desk_amend_modal_title: 'არსებული მოთხოვნის გაგრძელება',
    desk_amend_target_label: 'კლიენტის მოთხოვნა {id}…',
    desk_misc_not_saved: '⚠️ არ შეინახა — {reason}',
    desk_misc_failed: '⚠️ შეფერხდა — {reason}',
    desk_misc_failed_no_details: 'ეს შენახვის მცდელობა ჩავარდა და ჩანაწერი არ შეიქმნა (დეტალები არ შენახულა)',
    desk_client_owner_agent: '🧑‍💼 აგენტი: {name}',
    desk_entry_match_pill: '🏠 {count} შესაძლო ვარიანტი',
    desk_entry_hot_lead: '🔥 სასწრაფო',
    desk_entry_has_meeting: 'ჩანიშნულია ჩვენება',
    desk_entry_meetings_n: '{count} ჩვენება',
    desk_entry_deal_closed: 'გარიგება დახურულია',
    desk_entry_no_phone: '⚠️ ტელეფონი არ არის',
    desk_entry_fill_criteria: '📝 შეავსეთ: {fields}',
    desk_crit_districts: 'უბნები',
    desk_crit_budget: 'ბიუჯეტი',
    desk_crit_rooms: 'ოთახები',
    desk_match_act_shortlist: 'შერჩეული',
    desk_match_act_proposed: 'შეთავაზებული',
    desk_match_act_reject: 'უარყოფა',
    desk_match_act_undo: '↩ დაბრუნება',
    desk_match_reject_reason_prompt: 'რატომ არ უხდება ამ კლიენტს? (არასავალდებულო)',
    desk_match_grp_pinned: 'შერჩეული',
    desk_match_grp_ready: 'მზადაა შესათავაზებლად',
    desk_match_grp_verify: 'ჯერ გადაამოწმეთ',
    desk_match_grp_stretch: 'ჯერ მოლაპარაკება',
    desk_match_grp_compromise: 'კომპრომისი',
    desk_match_grp_longshot: 'ნაკლებად სავარაუდო',
    desk_match_grp_proposed: 'შეთავაზებული',
    desk_match_grp_rejected: 'უარყოფილი',
    // ── matching-UI v2, კლიენტის მხარე (msug_*) — ⚠️ ახალი ტექსტი, ქართველი
    // რეცენზენტის მოლოდინში: design/MATCHING_UI_STRINGS_ROUND3_FOR_TRANSLATION.md ──
    msug_grp_none: 'აქ ბარათები ჯერ არ არის',
    msug_more_cards: 'კიდევ {n}',
    msug_updated: 'განახლდა {ago}',
    msug_act_present: 'შესთავაზე კლიენტს',
    msug_act_verify: 'ჯერ გადაამოწმე',
    msug_act_negotiate: 'მოლაპარაკება მესაკუთრესთან',
    msug_act_ask_client: 'ჰკითხე კლიენტს',
    msug_ready_counterweight: 'ეს რიცხვი დიდია, რადგან პროფილი თითქმის ცარიელია — შევსება სიას შეამცირებს, და ეს სწორი მიმართულებაა',
    msug_empty_form_title: 'არ ვიცით, რა უნდა ამ კლიენტს',
    msug_empty_form_body: 'ჩანაწერია ცარიელი — არა ბაზარი. შეავსეთ კრიტერიუმები რედაქტირებით და შერჩევა თავიდან გადაითვლება.',
    msug_diag_title: 'ვერაფერი მოერგო — რა გახსნიდა ვარიანტებს',
    msug_diag_budget: 'ბიუჯეტი +30%',
    msug_diag_district: 'სავალდებულო უბნების გარეშე',
    msug_diag_rooms: 'ოთახები ±1',
    msug_diag_opens: '{n} ვარიანტი',
    msug_diag_opens_nothing: 'არაფერს ხსნის',
    msug_diag_unmeasured: 'ვერ გაიზომა',
    msug_diag_zero_all: 'დათმობები აქ არ შველის — ვერცერთი შემსუბუქება ვერაფერს ხსნის.',
    msug_diag_downstream: 'დაბრკოლება ამ ცხრილში არ არის: {n} კანდიდატმა გაიარა თვლა და ქულებმა ყველა გამორიცხა.',
    msug_diag_not_counted: 'ამ თვლაში არ შედის: {list}.',
    msug_diag_foot: 'მიმართულებაა და არა დაპირება — რიცხვები მიახლოებითია, ქულების გარეშე.',
    msug_diag_call_client: 'დაურეკე კლიენტს — პირობის შემსუბუქება მხოლოდ მას შეუძლია.',
    // §1: the designer's own word for the one button the ladder earns.
    msug_probe_see: 'ნახე',
    msug_probe_back: 'უკან',
    // ⚠️ The caveat is part of the answer: an agent about to say "six" to a client on the
    // phone must know nothing has scored these.
    msug_probe_unscored: 'შეფასებული არ არის — ეს ამ ერთი დათმობის კანდიდატებია, არა შერჩევა.',
    msug_ghost_pinned: '+{n} შერჩეულში',
    // Q5 (mobile): the collapsed card's ✓ chip. MatchExplain only chips what is WRONG or
    // open, so without this the phone card never says how much matched.
    msug_m_matched: '{n} ემთხვევა',
    msug_diff_recorded: 'ჩაწერილია',
    msug_diff_recorded_subject: 'ჩაწერილია: {subject}',
    // Round-3 note: `ითვლება…` read as a system state. The loading shape beside it already
    // says "working", so the verb is first person and the ellipsis carries the rest.
    msug_diff_counting: 'ითვლება…',
    msug_diff_added: '+{n} ბარათი',
    // ⚠️ The group name is QUOTED and apposed, never declined: `{group}` is a whole reviewed
    // label and a case ending glued onto an interpolated noun is exactly the dangling-suffix
    // bug the matrix shipped once (`ჰკითხე saanvi-ს -ზე`, 2026-08-08).
    msug_diff_moved: '{n} გადავიდა ჯგუფში „{group}“',
    // ⚠️ METER FILLS ONLY (round-3 §2 narrowed round-1's rule). A meter fill has no local
    // receipt — the agent is looking elsewhere and silence reads as a failed save. An
    // in-band press has a receipt an inch from the finger, so there silence is correct.
    msug_diff_nochange: 'ჩაწერილია — სია არ შეიცვალა',
    msug_diff_same_answer: 'იგივე პასუხმა სხვა ბარათებიც გახსნა',
    msug_diff_see_new: 'ნახე ახლები',
    msug_grp_shut: 'ჯგუფის დაკეცვა',
    msug_grp_open: 'ჯგუფის გაშლა',
    // §4: what the VIEW shows, said in words beside a count that stays the group total.
    msug_grp_showing_plus: 'ჩანს {base} + {n} ახალი',
    msug_grp_new_inside: '+{n} ახალი',
    // §5: name the cut. `ახლოს`, never `საუკეთესო` — naming the SORT is a statement about
    // the list; `საუკეთესო` would be a claim about the properties, which is the one thing
    // the no-scores rule forbids.
    // Round-3 note: the designer flagged that the one-phrase form may not parse to a native
    // ear; the dashed form keeps the point and cannot be misread as one noun phrase.
    msug_cut_named: 'ჩანს {n} — ყველაზე ახლოს · {total}-დან',
    msug_source_withdrawn: 'მოხსნილიდან',
    // §5's four orders — the designer's own words, not invented here.
    // §5's over-broad line. The first sentence and the button are the designer's own words;
    // the middle clause names whichever dimensions the requirement left unstated, using the
    // criteria panel's existing nouns so both screens call the same thing by the same name.
    msug_too_broad: '{n} ვარიანტი ბევრია — მოთხოვნა ზედმეტად ფართოა.',
    msug_too_broad_unnamed: '{list} არ არის დასახელებული. დააზუსტე და სია გახდება ნამდვილი პასუხი.',
    // A — the COMMON case: well specified, and the market is simply wide. No button; the
    // last clause points at the sort control directly above, in words.
    msug_well_specified: 'მოთხოვნა ზუსტია — ბაზარი ამ პირობებზე ფართოა. ყველა 4 პარამეტრი შევსებულია, არაფრის დავიწროება არ სჭირდება. აირჩიე რიგი და იმუშავე თავიდან.',
    msug_and_join: ' და ',
    msug_or_join: ' ან ',
    msug_refine: 'დააზუსტე მოთხოვნა',
    msug_sort_label: 'დაალაგე:',
    msug_sort_fit: 'ახლოს',
    msug_sort_fresh: 'ახალი განცხადება',
    msug_sort_cheap: 'იაფი',
    msug_sort_open: 'ნაკლები კითხვა',
    // §3: the three orders the stored table cannot serve say so, quietly, so the default's
    // speed is a legible property rather than a mystery.
    msug_sort_slow: '~1წმ',
    msug_open_property: 'ობიექტის ბარათი',
    msug_col_whose: 'ვისი ჩასაწერია',
    // §4: the quiet tier's door. Names a PLACE (the client's record), never a role.
    msug_on_requirement: 'მოთხოვნაში',
    msug_nc_polygon: 'საძიებო არეალი',
    msug_nc_area: 'ფართობი',
    msug_nc_must_have: 'აუცილებელი ნიშნები',
    msug_nc_must_not_have: 'გამორიცხული ნიშნები',
    msug_nc_condition: 'მდგომარეობა',
    msug_nc_floor: 'სართული',
    msug_nc_complex: 'კომპლექსი',
    desk_match_urgency_now: 'ახლავე',
    desk_match_urgency_soon: 'მალე',
    desk_match_via_agent: '{name}-ის კლიენტი',
    desk_misc_error_prefix: 'შეცდომა: {message}',
    desk_search_failed: 'ძიება ვერ დასრულდა. არაფერი დამალულა — პასუხი უცნობია, არა ცარიელი.',
    desk_address_conflict: 'ქუჩა გადაუწყვეტელია',
    desk_retry: 'თავიდან სცადე',
    desk_possible_failed: 'შესაძლო დამთხვევები ვერ ჩაიტვირთა — ეს „არცერთი“ არ ნიშნავს.',
    desk_street_catalog_failed: 'ქუჩების სია ვერ ჩაიტვირთა — ქუჩები აქ ძიების რაოდენობებთან ერთად გამოჩნდება.',
    desk_map_pins_failed: 'ყველა შესაბამისი მდებარეობა ვერ ჩაიტვირთა — რუკაზე მხოლოდ {n} ჩატვირთული ობიექტია.',
    desk_filter_invalid_number: 'რიცხვი არ არის, ამიტომ ძიება არ შესრულდა: {fields}. შეასწორე ან მოხსენი ფილტრი.',
    desk_misc_rejected_not_saved: 'უარყოფილია — არ შეინახა.',
    desk_misc_candidate_not_found: '⚠️ კანდიდატი არ მოიძებნა — შესაძლოა წაიშალა.',
    desk_misc_no_linked_viewing: '⚠️ ჩვენება არაა მობმული — ეს შეხვედრა მის გარეშე შეინახა.',
    desk_misc_viewing_not_found: '⚠️ ჩვენება არ მოიძებნა — შესაძლოა წაიშალა.',
    desk_observed_from_owner: 'მესაკუთრემ თქვა',
    desk_observed_from_agent: 'აგენტმა ნახა / იცის',
    desk_observed_from_registry: 'დოკუმენტები / რეესტრი',
    desk_observed_from_admin: 'ოფისის გადამოწმ.',
    desk_observed_from_ai: 'AI-მ ამოიცნო · გადაუმოწმებელია',
    // One-time fill-form links (client/property self-intake)
    desk_fill_link_btn: 'ფორმის ბმული',
    vlink_title: 'ვაკანსიის ბმული პლატფორმისთვის',
    vlink_sub: 'ყოველ პოსტს — თავის ბმული. აირჩიეთ, სად დებთ, და დაწერეთ, რატომ. თუ ვინმე ბოროტად გამოიყენებს, ბლოკავთ მხოლოდ ამ ბმულს — დანარჩენები ჩვეულებრივ იმუშაობენ.',
    vlink_platform_other: 'სხვა',
    vlink_comment_ph: 'სად და რატომ — მაგ. FB ჯგუფი „ვაკანსიები თბილისში“, 16 სექ.',
    vlink_create: 'ბმულის შექმნა',
    vlink_creating: 'იქმნება…',
    vlink_need_both: 'ბმულის შესაქმნად აირჩიეთ პლატფორმა და დაწერეთ კომენტარი.',
    vlink_copy: 'კოპირება',
    vlink_copied: 'კოპირებულია',
    vlink_list: 'ბმულები',
    vlink_none: 'ბმულები ჯერ არ არის.',
    vlink_applications: '{n} განაცხადი',
    vlink_blocked_since: 'ბლოკირებულია: {when}',
    vlink_block: 'ბლოკირება',
    vlink_unblock: 'განბლოკვა',
    vlink_block_confirm: 'ბლოკავთ ბმულს „{comment}“? მასზე აღარ ვინ შეძლებს განაცხადის გაგზავნას. სურვილისამებრ — რატომ?',
    vlink_unblock_confirm: 'განბლოკავთ ბმულს „{comment}“? მასზე განაცხადები ისევ მიიღება.',
    vlink_require_id: 'მიიღოს მხოლოდ ID-ანი ბმულები',
    vlink_require_id_sub: 'სანამ ეს გამორთულია, ბლოკირებული ბმული შეიძლება ავლის ?l=… მისამართიდან წაშლით. ჩართეთ მას მერე, როცა ძველი ბმულები (?src=…) ჩანაცვლდებიან — ისინი მუშაობას შეწყვეტენ.',
    vlink_require_id_confirm: 'ჩართოთ? ყოველი ძველი ბმული ID-ის გარეშე (?src=…) მომენტალურად შეწყვეტს განაცხადების მიღებას.',
    vlink_manager: 'მენეჯერი:',
    vlink_manager_any: 'არცერთი — ნებისმიერი მენეჯერი',
    cand_link_title: 'პირადი ბმული ამ კანდიდატისთვის',
    cand_link_sub: 'გაუგზავნეთ კონკრეტულ ადამიანს — ის თავად შეავსებს ვაკანსიის ფორმას და თქვენს Desk-ზე მოხვდება, სახელით. ბმული მუშაობს ერთხელ და ვადა გასდის 7 დღეში. პლატფორმებზე დასადები ბმულები — ქვემოთ, ყოველი თავის ID-ით.',
    desk_fill_link_title: 'ერთჯერადი ფორმის ბმული',
    desk_fill_link_sub: 'გაუგზავნეთ ეს ბმული კლიენტს — ის თავად შეავსებს ფორმას და ჩანაწერი თქვენს Desk-ზე გამოჩნდება. ბმული მუშაობს ზუსტად ერთხელ და ვადა გასდის 7 დღეში.',
    desk_fill_link_kind_client: '🙋 კლიენტის ფორმა',
    desk_fill_link_kind_property: '🏠 ქონების ფორმა',
    desk_fill_link_prefill_name: 'კლიენტის სახელი (არასავალდებულო)',
    desk_fill_link_prefill_phone: 'კლიენტის ტელეფონი (არასავალდებულო)',
    desk_fill_link_generate: 'ბმულის შექმნა',
    desk_fill_link_generating: 'იქმნება…',
    desk_fill_link_copy: 'კოპირება',
    desk_fill_link_copied: 'დაკოპირდა!',
    desk_fill_link_whatsapp: 'WhatsApp',
    desk_fill_link_recent: 'თქვენი ბოლო ბმულები',
    desk_fill_link_st_open: 'ელოდება',
    desk_fill_link_st_started: '✍️ დაწყებულია',
    desk_fill_link_st_used: '✓ შევსებულია',
    desk_fill_link_st_expired: 'ვადაგასული',
    desk_fill_link_none: 'ბმულები ჯერ არ არის.',
    desk_fill_link_share_text: 'გამარჯობა{name}! გთხოვთ, შეავსოთ ფორმა: {url}',
    // ── Contracts panel (ContractPanel.jsx, B1249–B1252) ──────────────────────────
    desk_contract_title: 'ხელშეკრულებები',
    desk_contract_new_rent: 'ქირავნობის ხელშეკრულება',
    desk_contract_none: 'ამ შეხვედრაზე ხელშეკრულება ჯერ არ არის.',
    desk_contract_status_draft: 'მონახაზი',
    desk_contract_status_sent: 'გაგზავნილია',
    desk_contract_status_partially_signed: 'ნაწილობრივ ხელმოწერილი',
    desk_contract_status_signed: 'ხელმოწერილია',
    desk_contract_status_declined: 'უარყოფილია',
    desk_contract_status_void: 'გაუქმებული',
    desk_contract_compose_hint: 'შეავსეთ, რაც შეხვედრამ არ იცის, და შექმენით.',
    desk_contract_rerender_of: 'ახალი ვერსია v{v}-ის შემდეგ — ძველი ვერსიის ბმულები უქმდება.',
    desk_contract_parties: 'მხარეები',
    desk_contract_role_landlord: 'გამქირავებელი',
    desk_contract_role_tenant: 'დამქირავებელი',
    desk_contract_role_owner: 'მესაკუთრე',
    desk_contract_role_cobroker: 'პარტნიორი ბროკერი',
    desk_contract_role_client: 'კლიენტი',
    desk_contract_role_agency: 'სააგენტო (senTavus)',
    desk_contract_role_agent: 'აგენტი',
    desk_contract_src_contract: 'ქირავნობის ხელშეკრულებიდან',
    desk_contract_doc_rental: 'ქირავნობის ხელშეკრულებიდან',
    desk_contract_doc_binder: 'ბეს შეთანხმებიდან',
    desk_contract_doc_advance: 'ავანსის შეთანხმებიდან',
    desk_contract_doc_agency: 'მომსახურების ხელშეკრულებიდან',
    desk_contract_doc_signed: 'ხელმოწერილი',
    desk_contract_doc_draft: 'მუშა ვერსია',
    desk_contract_docname_rent_residential_v1: 'ქირავნობის ხელშეკრულება',
    desk_contract_docname_rent_commercial_v1: 'კომერციული ფართის ქირავნობა',
    desk_contract_docname_binder_v1: 'ბეს შეთანხმება',
    desk_contract_docname_advance_v1: 'ავანსის შეთანხმება',
    desk_contract_docname_agency_service_v1: 'მომსახურების ხელშეკრულება',
    desk_contract_import_title: 'ამ გარიგებაზე უკვე არსებობს დოკუმენტები',
    desk_contract_import_sub: 'აიღეთ ის, რაც უკვე ჩაწერილია, ან ყველაფერი თავად შეავსეთ.',
    desk_contract_import_answers: 'პასუხობს {n}-ს',
    desk_contract_import_counts: 'შეივსება {n} · {d} განსხვავდება ადრინდელი დოკუმენტისგან · {c} გადასაწყვეტია',
    desk_contract_import_decide: 'თანაბარი ძალის ორი დოკუმენტი ერთმანეთს ეწინააღმდეგება. აირჩიეთ, რომელს მიჰყვება ეს ხელშეკრულება:',
    desk_contract_import_go: 'ცნობილის იმპორტი',
    desk_contract_import_manual: 'ხელით შევსება',
    desk_contract_src_agency: 'სააგენტოს მიერ',
    desk_contract_party_name_ph: 'სრული სახელი, როგორც პირადობაშია',
    desk_contract_src_meeting: 'შეხვედრიდან',
    desk_contract_src_added: 'თქვენ დაამატეთ',
    desk_contract_src_prior_contract: 'ამ პირთან წინა ხელშეკრულებიდან',
    desk_contract_src_fill_form: 'კლიენტის ფორმიდან',
    desk_contract_src_requirement: 'კლიენტის ბარათიდან',
    desk_contract_src_cooperation: 'თანამშრომლობის პირობებიდან',
    desk_contract_src_lease_start: 'ავტომატურად ქირავნობის დაწყების თარიღიდან',
    desk_contract_src_period: 'ავტომატურად',
    desk_contract_src_computed: 'გამოთვლილი',
    desk_contract_period: 'ვადა',
    desk_contract_lease_unit_day: 'დღე',
    desk_contract_lease_unit_month: 'თვე',
    desk_contract_lease_unit_year: 'წელი',
    desk_contract_lease_preset: '{n} თვე',
    desk_contract_lease_edit: 'შეცვლა',
    desk_contract_today: 'დღეს',
    desk_contract_lease_edit_title: 'ჩაწერეთ ვადა — დასრულების თარიღი მისგან გამოითვლება',
    desk_return_title: "საბოლოო დათვალიერება",
    desk_return_worse: "გაუარესდა",
    desk_return_start: "დაწყება",
    desk_return_sec_condition: "მდგომარეობა — ჩაბარებისას → დაბრუნებისას",
    desk_return_sec_keys: "გასაღებები — გაცემული → დაბრუნებული",
    desk_return_sec_meter: "მრიცხველები",
    desk_return_sec_deduction: "დაქვითვები",
    desk_return_v_same: "უცვლელი",
    desk_return_v_worse: "გაუარესდა",
    desk_return_v_better: "გაუმჯობესდა",
    desk_return_v_unknown: "შედარება არ ხერხდება",
    desk_return_v_added: "მხოლოდ ახლა დაფიქსირდა",
    desk_return_keys_short: "დაუბრუნებელი",
    desk_return_ded_note_ph: "რის საფუძველზე — ინვოისი, ფოტო",
    desk_return_disputed: "სადავო",
    desk_return_disputed_hint: "დამქირავებელი არ ეთანხმება ამ დაქვითვას",
    desk_return_deposit: "უზრუნველყოფის თანხა",
    desk_return_deductions: "დაქვითვები",
    desk_return_disputed_total: "აქედან სადავო",
    desk_return_refund: "დასაბრუნებელი",
    desk_return_owes: "დამქირავებელს ერიცხება",
    desk_return_mixed: "თანხები სხვადასხვა ვალუტაშია — ჯამი არ გამოითვლება",
    desk_return_ded_unpaid_rent: "გადაუხდელი ქირა",
    desk_return_ded_utilities: "გადაუხდელი კომუნალური",
    desk_return_ded_damage: "ზიანის ანაზღაურება",
    desk_return_ded_missing_inventory: "დაკარგული ინვენტარი",
    desk_return_ded_missing_keys: "დაუბრუნებელი გასაღები",
    desk_return_ded_cleaning: "დასუფთავება",
    desk_return_ded_other: "სხვა",
    desk_annex_photo_pick: "რომელი ფოტო ადასტურებს",
    desk_annex_photo_n: "ფოტო",
    desk_annex_record_title: "ჩაბარების ჩანაწერი",
    desk_annex_sec_condition: "ქონების მდგომარეობა",
    desk_annex_sec_keys: "გასაღებები და წვდომის საშუალებები",
    desk_annex_sec_meter: "მრიცხველების ჩვენებები",
    desk_annex_sec_inventory: "ინვენტარი",
    desk_annex_sec_defect: "არსებული ხარვეზები",
    desk_annex_state_good: "წესრიგში",
    desk_annex_state_damaged: "დაზიანებული",
    desk_annex_state_absent: "არ არის",
    desk_annex_state_not_checked: "არ შემოწმდა",
    desk_annex_sev_minor: "მცირე",
    desk_annex_sev_major: "მნიშვნელოვანი",
    desk_annex_note_ph: "რა ზუსტად — მაგ. ნაკაწრი ფანჯარასთან",
    desk_annex_reading_ph: "ჩვენება",
    desk_annex_meter_no_ph: "მრიცხველის №",
    desk_annex_item_ph: "დასახელება",
    desk_annex_defect_ph: "რა ხარვეზია",
    desk_annex_room_ph: "ოთახი",
    desk_annex_save: "შენახვა",
    desk_annex_saving: "ინახება…",
    desk_return_signed_by: "აქტს ხელი მოაწერა",
    desk_annex_promised: "გამქირავებელმა უზრუნველყოფა დაპირდა",
    desk_annex_meter_reading: "მრიცხველის ჩვენება",
    desk_annex_meter_no: "მრიცხველის №",
    desk_annex_promised_by: "არაუგვიანეს",
    desk_annex_promised_date_needed: "მიუთითეთ თარიღი",
    desk_return_date_first: "ხელმოსაწერად საჭიროა დაბრუნების თარიღი",
    desk_return_pdf: "აქტის PDF",
    desk_annex_saved: "შენახულია ✓",
    desk_annex_discard_warn: "ეს გააუქმებს უკვე დადებულ ხელმოწერებს",
    desk_annex_discard_go: "გაგრძელება",
    desk_contract_src_listing_terms: 'პირობებიდან',
    desk_contract_src_owner_rule: 'მესაკუთრის წესიდან',
    desk_contract_src_binder: 'ბეს შეთანხმებიდან',
    desk_contract_src_property: 'ობიექტის მონაცემებიდან',
    desk_contract_src_default: 'ავტომატურად',
    desk_contract_src_agent: 'თქვენ შეცვალეთ',
    desk_contract_src_override: 'ლოკალური ცვლილება',
    desk_contract_override_was: 'წყაროში წერია: {v}',
    desk_contract_override_reason_ph: 'რატომ განსხვავდება აქ?',
    desk_contract_override_reason_needed: 'მიუთითეთ, რატომ განსხვავდება',
    desk_contract_source_moved: 'წყარო-დოკუმენტი შეიცვალა: მონაცემები აღებულია v{from}-დან, ახლა წყაროს ვერსიაა v{to}.',
    desk_contract_source_show: 'ნახეთ, რა განსხვავდება',
    desk_contract_source_hide: 'დამალვა',
    desk_contract_source_same: 'ამ ხელშეკრულების არცერთი პასუხი არ შეცვლილა.',
    desk_contract_leg_old: 'წყაროში მაშინ ეწერა',
    desk_contract_leg_new: 'წყაროში ახლა წერია',
    desk_contract_leg_local: 'ამ ხელშეკრულებაში',
    desk_contract_take_source: 'ახალი მნიშვნელობის აღება',
    desk_contract_keep_mine: 'დარჩეს ეს',
    desk_contract_source_save_note: 'შენახვამდე არაფერი იწერება; შენახვა ქმნის ახალ ვერსიას.',
    desk_contract_src_none: 'შესავსები',
    desk_contract_what_is_this: 'რას ნიშნავს ეს ველი?',
    desk_contract_bad_email: 'ეს ელფოსტის მისამართი არ არის.',
    desk_contract_bad_phone: 'ეს ტელეფონის ნომერი არ არის.',
    desk_contract_id_duplicate: 'იგივე ნომერი, რაც მეორე მხარეს',
    desk_contract_id_format: 'პირადი ნომერი 11 ციფრია',
    desk_contract_langs: 'ენა',
    desk_contract_lang_ka: 'ქართული',
    desk_contract_lang_en: 'ინგლისური',
    desk_contract_lang_unreviewed: 'ჯერ არ არის გადამოწმებული',
    desk_contract_need_lang: 'აირჩიეთ ერთი ენა მაინც.',
    desk_contract_pay_signing_title: "ხელმოწერისას გადასახდელი თანხა — ვინ იღებს და როგორ",
    desk_contract_block_signing: 'როგორ ფორმდება ხელშეკრულება',
    desk_contract_block_handover: 'გასაღებების გადაცემა',
    desk_contract_pay_route_owner_cash: "ნაღდი ანგარიშსწორებით — მესაკუთრეს",
    desk_contract_pay_route_agent_cash: "ნაღდი ანგარიშსწორებით — senTavus-ის უფლებამოსილ აგენტს",
    desk_contract_pay_route_owner_transfer: "გადარიცხვით — მესაკუთრის ანგარიშზე",
    desk_contract_pay_route_agent_transfer: "გადარიცხვით — უფლებამოსილი აგენტის დადასტურებულ ანგარიშზე",
    desk_contract_pay_route_agency_transfer: "გადარიცხვით — სააგენტოს ანგარიშსწორების ანგარიშზე",
    desk_contract_pay_route_owner_card: "ბარათით ან ელექტრონული გადახდის სისტემით — მესაკუთრეს",
    desk_contract_pay_route_other: "სხვაგვარად (აღწერეთ)",
    desk_contract_pay_transfer_kind: "გადარიცხვის სახე",
    desk_contract_pay_account_name: "ანგარიშის დასახელება, როგორც გადარიცხვაში მიეთითება",
    desk_contract_pay_other_who: "ვინ იღებს თანხას",
    desk_contract_pay_other_how: "როგორ და როდის გადაიხდება",
    desk_contract_pay_other_how_help: "ჩაწერეთ ისე, როგორც ხელშეკრულებაში უნდა ეწეროს — მაგალითად: ნაღდი ანგარიშსწორებით მესაკუთრის წარმომადგენელს, ხელმოწერილი ხელწერილის სანაცვლოდ.",
    desk_contract_pay_monthly_title: "ყოველთვიური ქირა — როგორ გადაიხდება",
    desk_contract_pay_monthly_same: "ისევე, როგორც ხელმოწერისას",
    desk_contract_pay_monthly_any: "ნებისმიერი ხელსაყრელი გზით — ნაღდი, გადარიცხვა ან ბარათი",
    desk_contract_pay_monthly_multiple: "რამდენიმე გზით — მონიშნეთ რომელი",
    desk_contract_pay_monthly_owner_cash: "ნაღდი ანგარიშსწორებით — მესაკუთრეს",
    desk_contract_pay_monthly_owner_transfer: "გადარიცხვით — მესაკუთრის ანგარიშზე",
    desk_contract_pay_monthly_owner_card: "ბარათით ან ელექტრონული გადახდის სისტემით — მესაკუთრეს",
    desk_contract_pay_monthly_other: "სხვაგვარად (აღწერეთ)",
    desk_contract_pay_monthly_other_how: "როგორ და როდის გადაიხდება ყოველთვიური ქირა",
    desk_contract_lang_ka_required: 'ქართული ტექსტი ყოველთვის შედის — იგი იურიდიულად უპირატესია.',
    desk_contract_need_fields: 'ჯერ შეავსეთ მონიშნული ველები.',
    desk_contract_cancel: 'გაუქმება',
    desk_contract_create_btn: 'შექმნა',
    desk_contract_rerender_btn: 'ხელახლა შედგენა',
    desk_contract_err_missing: 'სავალდებულო ველი ცარიელია.',
    desk_contract_err_lang: 'ეს ენა ჯერ არ არის გადამოწმებული.',
    desk_contract_version: 'ვერსია {v}',
    desk_contract_preview_show: 'ნახვა',
    desk_contract_preview_hide: 'დამალვა',
    desk_contract_voided: 'გაუქმებულია',
    desk_contract_kind_rent_residential: 'ქირავნობის ხელშეკრულება',
    desk_contract_kind_rent_commercial: 'კომერციული ფართის ქირავნობა',
    desk_contract_legal_pending_short: 'იურიდიული შემოწმება მიმდინარეობს',
    desk_contract_legal_pending_title: 'იურიდიული შემოწმება არ დასრულებულა.',
    desk_contract_legal_pending_body: 'ამ ტიპის ხელშეკრულების შევსება და წინასწარი ნახვა შესაძლებელია, თუმცა მისი გაგზავნა და ხელმოწერა შეუძლებელია, სანამ იურისტი ტექსტს არ დაამტკიცებს. თავად დოკუმენტზეც აღნიშნულია, რომ ეს სამუშაო ვერსიაა.',
    desk_contract_kind_agency_service: 'მომსახურების ხელშეკრულება',
    desk_contract_kind_tenant_agency_service: 'დამქირავებლისთვის სააგენტოს მომსახურების ხელშეკრულება',
    desk_contract_kind_owner_mandate: 'ექსკლუზიური შუამავლობა',
    desk_contract_tpl_owner_mandate_v1: 'ექსკლუზიური შუამავლობის ხელშეკრულება',
    desk_contract_add_co_owner: 'თანამესაკუთრის დამატება',
    desk_contract_remove_co_owner: 'წაშლა',
    desk_contract_address_auto_used: 'სისტემიდან: {v} — ასე ჩაიწერება; შესაცვლელად ჩაწერეთ საკუთარი.',
    desk_contract_address_auto_overridden: 'სისტემაშია: {v} — ჩაიწერება თქვენი ტექსტი.',
    desk_contract_role_resident: 'მაცხოვრებელი',
    desk_contract_add_resident: 'მაცხოვრებელი {n}-ის მონაცემების დამატება',
    desk_contract_remove_resident: 'წაშლა',
    desk_contract_resident_note: 'არასავალდებულო. ხელშეკრულებაში ჩაიწერება როგორც ბინაში მცხოვრები პირი; ხელს არ აწერს.',
    desk_contract_co_owners_must_sign: '„ყველა თანამესაკუთრე აწერს ხელს“ — დაამატეთ თითოეული თანამესაკუთრე მესაკუთრედ (მინიმუმ ორი)',
    desk_contract_owner_authority_mismatch: '„ერთპიროვნული საკუთრება“, მაგრამ მესაკუთრე ერთზე მეტია — შეცვალეთ საფუძველი ან წაშალეთ ზედმეტი',
    desk_contract_range_between: 'უნდა იყოს {min}-დან {max}-მდე',
    desk_contract_range_min: 'უნდა იყოს არანაკლებ {min}',
    desk_contract_range_max: 'უნდა იყოს არაუმეტეს {max}',
    desk_contract_range_after: 'უნდა იყოს „{f}“-ის შემდეგ',
    desk_contract_rows_incomplete: 'სტრიქონში სავალდებულო ველი ცარიელია',
    desk_contract_rows_add: 'სტრიქონის დამატება',
    desk_contract_rows_remove: 'სტრიქონის წაშლა',
    desk_contract_tenant_agreement_required: 'დამქირავებლის სააგენტოს ხელშეკრულება საჭიროა',
    desk_contract_tenant_needed: 'დაამატეთ დამქირავებელი',
    desk_contract_kind_binder: 'ბეს გადაცემის შეთანხმება',
    desk_contract_kind_advance: 'ავანსისა და ქონების დაჯავშნის შეთანხმება',
    desk_contract_rel_from: 'მონაცემები აღებულია',
    desk_contract_rel_based_on: 'ეფუძნება',
    desk_contract_rel_dependent: 'ამის საფუძველზე შედგენილია',
    desk_contract_rel_signed: 'ხელმოწერილი',
    desk_contract_rel_open: 'ჯერ არ არის ხელმოწერილი',
    desk_contract_rel_void: 'გაუქმებული',
    desk_contract_rel_void_warn: 'საწყისი ხელშეკრულება გაუქმდა — გადაამოწმეთ ყველა მემკვიდრეობითი მნიშვნელობა',
    desk_contract_rel_none: 'ამ შეხვედრაზე ქირავნობის ხელშეკრულება ჯერ არ არის — დამქირავებელი, ვადები და ქირა ხელით შეივსება.',
    desk_contract_rel_none_property: 'ამ ქონებაზე ქირავნობის ხელშეკრულება ჯერ არ არის. გადაამოწმეთ ქირა და დამქირავებლის მონაცემები.',
    desk_contract_link_none: 'ბმული ჯერ არ არის',
    desk_contract_link_minted: 'ბმული მზადაა — არ გაგზავნილა',
    desk_contract_link_sent: 'გაიგზავნა {via} · {at}',
    desk_contract_link_opened: 'გახსნა · {at}',
    desk_contract_link_signed: 'ხელი მოაწერა · {at}',
    desk_contract_link_declined: 'უარი თქვა · {at} · {reason}',
    desk_contract_via_whatsapp: 'WhatsApp-ით',
    desk_contract_via_email: 'ელფოსტის მონახაზი გაიხსნა',
    desk_contract_via_email_auto: 'ელფოსტით ავტომატურად გაიგზავნა',
    desk_contract_event_link_mail_failed: 'ხელმოსაწერი ბმული ელფოსტით ვერ გაიგზავნა',
    desk_contract_via_copied: 'ბმულის კოპირებით',
    desk_contract_via_in_person: 'პირადად, აგენტის მოწყობილობიდან',
    desk_contract_send_whatsapp: 'WhatsApp',
    desk_contract_send_email: 'ელფოსტა',
    desk_contract_copy: 'ბმულის კოპირება',
    desk_rental_open_title: 'ხელშეკრულების შევსებამდე',
    desk_rental_open_q1: 'როგორ ფორმდება ხელშეკრულება და დაათვალიერა თუ არა დამქირავებელმა ქონება?',
    desk_rental_open_q1_in_person: 'პირადად დაათვალიერა და ხელშეკრულებას ადგილზე აფორმებს',
    desk_rental_open_q1_remote_inspected: 'ქონება ადრე პირადად დაათვალიერა, მაგრამ ხელშეკრულებას დისტანციურად აფორმებს',
    desk_rental_open_q1_remote_media: 'ქონება პირადად არ დაუთვალიერებია; გაეცნო მხოლოდ ფოტო- და ვიდეომასალას და ხელშეკრულებას დისტანციურად აფორმებს',
    desk_rental_open_q2: 'როდის გადაეცემა დამქირავებელს გასაღებები?',
    desk_rental_open_q2_start: 'ქირავნობის დაწყების დღეს',
    desk_rental_open_q2_later: 'მოგვიანებით, წინასწარი შეთანხმებით',
    desk_rental_open_q2_help: 'გასაღებების ფაქტობრივი გადაცემის თარიღი და დრო დაფიქსირდება ჩაბარებისას.',
    desk_rental_open_q3: 'როგორ განხორციელდება პირველადი გადახდა?',
    desk_rental_open_m_cash: 'ნაღდი ანგარიშსწორება',
    desk_rental_open_m_cash_note: 'თანხის მიღებისა და აღრიცხვისთანავე.',
    desk_rental_open_m_instant: 'მყისიერი საბანკო გადარიცხვა',
    desk_rental_open_m_instant_note: 'თანხა, როგორც წესი, ირიცხება დაუყოვნებლივ ან რამდენიმე წუთში.',
    desk_rental_open_m_local: 'სტანდარტული ადგილობრივი საბანკო გადარიცხვა',
    desk_rental_open_m_local_note: 'თანხა შესაძლოა ჩაირიცხოს იმავე ან მომდევნო საბანკო დღეს.',
    desk_rental_open_m_swift: 'საერთაშორისო საბანკო გადარიცხვა / SWIFT',
    desk_rental_open_m_swift_note: 'თანხის ჩარიცხვას შესაძლოა დასჭირდეს 3 საბანკო დღემდე.',
    desk_rental_open_m_other: 'სხვა',
    desk_rental_open_cash_remote_no: 'დისტანციური გაფორმებისას ნაღდი ანგარიშსწორება შეუძლებელია.',
    desk_rental_open_q4: 'როგორ გადაიხდება შემდგომი ყოველთვიური ქირა?',
    desk_rental_open_r_same: 'იმავე მეთოდით, როგორც პირველადი გადახდა',
    desk_rental_open_r_spec: 'სხვა წესით — დეტალები შეივსება ხელშეკრულების ფორმაში',
    desk_rental_open_same_other_no: '„სხვა“ წესის შემთხვევაში ყოველთვიური ქირა ცალკე განისაზღვრება ფორმაში.',
    desk_rental_open_continue: 'ხელშეკრულების შევსება',
    desk_rental_open_answer_all: 'გასაგრძელებლად უპასუხეთ ყველა კითხვას',
    desk_rental_open_change: 'შეცვლა',
    desk_rental_open_sum_in_person: 'პირადად დაათვალიერა',
    desk_rental_open_sum_remote_inspected: 'დისტანციური გაფორმება · ადრე პირადად დაათვალიერა',
    desk_rental_open_sum_remote_media: 'დისტანციური გაფორმება · მხოლოდ ფოტო- და ვიდეომასალა',
    desk_rental_open_sum_same: 'ყოველთვიური ქირა — იმავე მეთოდით',
    desk_rental_open_sum_spec: 'ყოველთვიური ქირა — სხვა წესით',
    desk_rental_open_sum_same_long: 'ყოველთვიური ქირა გადაიხდება იმავე მეთოდით. გადახდის მიმღები და ანგარიში ქვემოთ ცალ-ცალკე მიეთითება.',
    desk_rental_open_same_unavailable: 'აირჩიეთ, როგორ გადაიხდება ყოველთვიური ქირა.',
    desk_contract_pay_recipient_title: 'ვინ იღებს ხელმოწერისას გადასახდელ თანხას?',
    desk_contract_err_generic: 'შენახვა ვერ მოხერხდა. სცადეთ ხელახლა.',
    desk_contract_err_details: 'ტექნიკური დეტალები',
    desk_contract_lease_break_toggle: 'ადრეული გასვლის უფლებით',
    desk_contract_commission_mismatch_bar: 'საკომისიო — თანხა, სტატუსი და თარიღი უნდა ემთხვეოდეს',
    desk_contract_break_month_bar: 'გასვლის უფლების თვე უნდა იყოს დადებითი და სრულ ვადაზე ნაკლები',
    desk_contract_notice_days_bar: 'გასვლის შესახებ შეტყობინების დღეები უნდა იყოს დადებითი მთელი რიცხვი',
    desk_contract_lease_model_bar: 'ქირავნობის მოდელი და სრული ვადა უნდა ემთხვეოდეს',
    desk_contract_check_payment_rule: 'გადახდის წესი შეამოწმეთ',
    desk_contract_commission_mismatch: 'საკომისიოს თანხა, სტატუსი და თარიღი არ ემთხვევა.',
    desk_contract_commission_change_payment: 'გადახდის შეცვლა',
    desk_contract_payment_date: 'გადახდის თარიღი',
    desk_contract_break_before_end: 'გასვლის უფლება უნდა დადგეს სრული ვადის დასრულებამდე.',
    desk_contract_notice_days_hint: 'მიუთითეთ შეტყობინების დადებითი მთელი დღეები.',
    desk_contract_lease_model_hint: 'შეამოწმეთ მოდელი და სრული ვადა თვეებში.',
    desk_contract_partial_payment: 'ნაწილობრივი გადახდა',
    desk_contract_commission_colon: 'საკომისიო:',
    desk_contract_amount_paid: 'გადახდილი თანხა',
    desk_contract_remaining_colon: 'დარჩენილი:',
    desk_contract_close: 'დახურვა',
    desk_contract_confirm: 'დადასტურება',
    desk_contract_commercial_terms: 'კომერციული პირობები',
    desk_contract_location_unclear: 'ლოკაცია დასაზუსტებელია',
    desk_contract_agent_choice_needed: 'აგენტის არჩევანი საჭიროა',
    desk_contract_change: 'შეცვლა',
    desk_contract_owner_to_agency: 'მესაკუთრე → senTavus',
    desk_contract_tenant_to_agency: 'დამქირავებელი → senTavus',
    desk_contract_fee_percent_of_rent: 'ერთი თვის ქირის %',
    desk_contract_fee_fixed: 'ფიქსირებული თანხა',
    desk_contract_fee_other_basis: 'სხვა საფუძველი',
    desk_contract_fee_none: 'საკომისიო არ არის',
    desk_contract_city_tbilisi: 'თბილისი',
    desk_contract_city_batumi: 'ბათუმი',
    desk_contract_policy_TBILISI_RESIDENTIAL_RENTAL_V1: 'თბილისი, საცხოვრებლის ქირავნობა — სტანდარტული პირობები',
    desk_contract_fees_separate: 'ქირა რჩება მესაკუთრის ცალკე მოთხოვნად; ეს ორი საკომისიო ერთმანეთის ვალდებულებას არ ფარავს.',
    desk_contract_policy_moved: 'ქონების მდებარეობა შეიცვალა. ახალი შეთავაზებაა {policy}; მიმდინარე პირობები ავტომატურად არ შეცვლილა.',
    desk_contract_policy_accept: 'ახალი წესის მიღება',
    desk_contract_policy_keep: 'მიმდინარე პირობების დატოვება',
    desk_contract_rel_owner_agency: 'მესაკუთრის სააგენტოს ხელშეკრულება',
    desk_contract_event_initial_payment_received: 'პირველადი თანხის ჩარიცხვა',
    desk_contract_event_initial_transfer_initiated: 'პირველადი გადარიცხვის ინიცირება',
    desk_contract_event_initial_payment_deadline_extended: 'გადახდის ვადა მხარეთა შეთანხმებით გაგრძელდა',
    desk_contract_event_key_collection_requested: 'გასაღებების მიღების მოთხოვნა',
    desk_contract_event_key_handover_recorded: 'გასაღებების ფაქტობრივი ჩაბარება ჩაიწერა',
    desk_contract_event_post_handover_defect_reported: 'ჩაბარების შემდეგ ხარვეზი დაფიქსირდა',
    desk_contract_event_reference_material_added: 'წარმოდგენილი ფოტო/ვიდეომასალა დაემატა',
    desk_contract_chip_remote_pending_payment: 'ხელმოწერილია · გადახდას ელოდება',
    desk_contract_chip_remote_failed: 'ძალაში არ შესულა',
    desk_contract_chip_remote_if_paid: 'ძალაშია, თუ გადახდილია',
    desk_contract_chip_remote_scheduled: 'ძალაში შევა დაწყების დღეს',
    desk_contract_chip_remote_awaiting_handover: 'ძალაშია · ჩაბარებას ელოდება',
    desk_contract_chip_remote_handed_over: 'ძალაშია · ჩაბარებულია',
    desk_contract_ref_media_title: 'ხელმოწერამდე წარმოდგენილი ფოტო- და ვიდეომასალა',
    desk_contract_ref_media_note: 'ეს მასალა აღწერს წარმოდგენილ მდგომარეობას. ქონების ფაქტობრივი მდგომარეობა და გასაღებების ჩაბარება მოგვიანებით მიღება-ჩაბარების აქტით დასტურდება.',
    desk_contract_ref_media_optional: 'არასავალდებულო',
    desk_contract_ref_media_note_inspected: 'დამქირავებელმა ქონება უკვე დაათვალიერა, ამიტომ ეს მასალა არასავალდებულოა. აქ დამატებული მასალა ხელმომწერს ეჩვენება და ხელმოწერილ ხელშეკრულებას ერთვის.',
    desk_contract_photo: 'ფოტო',
    desk_contract_annex_sha: 'დანართის SHA-256:',
    desk_contract_amount: 'თანხა',
    desk_contract_txn_number: 'ტრანზაქციის ნომერი',
    desk_contract_evidence_ref: 'მტკიცებულების ნომერი / ბმული',
    desk_contract_ev_account_credit: 'ანგარიშზე ჩარიცხვის დასტური',
    desk_contract_ev_bank_statement: 'საბანკო ამონაწერი',
    desk_contract_ev_provider_settlement: 'პროვაიდერის ანგარიშსწორება',
    desk_remote_state_pending: 'ხელმოწერილია · პირველადი გადახდა მოსალოდნელია',
    desk_remote_state_failed: 'გადახდის წინაპირობა არ შესრულდა · ხელშეკრულება ძალაში არ შესულა',
    desk_remote_state_if_paid: 'ძალაშია იმ შემთხვევაში, თუ გადახდა განხორციელდა · გადახდა ჯერ არ არის აღრიცხული — აღრიცხეთ ქვემოთ',
    desk_remote_state_scheduled: 'გადახდა დადასტურებულია · ქირავნობის დაწყებას ელოდება',
    desk_remote_state_awaiting_handover: 'ძალაშია · გასაღებების ჩაბარება მოსალოდნელია',
    desk_remote_state_handed_over: 'ძალაშია · გასაღებები ჩაბარებულია',
    desk_contract_save_failed: 'შენახვა ვერ მოხერხდა',
    desk_remote_title: 'დისტანციური ქირავნობის მდგომარეობა',
    desk_remote_deadlines: 'ხელმოწერილი ვადა: {original} · მიმდინარე ვადა: {current} (საქართველოს დრო)',
    desk_remote_bank_last_day: 'საბანკო დამუშავების ბოლო დღე:',
    desk_remote_initial_amount: 'პირველადი თანხა:',
    desk_remote_confirmed: 'დადასტურებულია:',
    desk_remote_transfer_initiated: 'გადარიცხვა ინიცირებულია:',
    desk_remote_credit_separately: 'თანხის ჩარიცხვა ცალკე უნდა დადასტურდეს.',
    desk_remote_fully_credited: 'სრულად ჩაირიცხა:',
    desk_remote_effective: 'ძალაში შესვლა:',
    desk_remote_rent_starts: 'ქირის პერიოდი იწყება',
    desk_remote_keys_handed: 'გასაღებები ჩაბარდა:',
    desk_remote_release_allowed: 'გამქირავებელს ქონების შენარჩუნების ვალდებულება აღარ აქვს, თუ მხარეები ვადას ცალკე არ გააგრძელებენ.',
    desk_remote_initiation_title: 'გადარიცხვის ინიცირების დადასტურება',
    desk_contract_ev_swift: 'SWIFT დადასტურება',
    desk_contract_ev_payment_order: 'გადახდის დავალება',
    desk_contract_ev_bank_receipt: 'ბანკის ქვითარი',
    desk_contract_ev_provider_initiation: 'პროვაიდერის დადასტურება',
    desk_contract_txn_number_swift: 'ტრანზაქციის ნომერი (SWIFT / UETR)',
    desk_remote_record_initiation: 'ინიცირების დაფიქსირება',
    desk_remote_credit_title: 'მითითებულ ანგარიშზე ჩარიცხვის დადასტურება',
    desk_remote_record_credit: 'ჩარიცხვის დაფიქსირება',
    desk_remote_extension_title: 'მხარეთა წერილობით შეთანხმებული ახალი ვადა',
    desk_remote_landlord_confirmation: 'გამქირავებლის დადასტურება',
    desk_remote_tenant_confirmation: 'დამქირავებლის დადასტურება',
    desk_remote_record_deadline: 'ახალი ვადის დაფიქსირება',
    desk_remote_key_request_title: 'გასაღებების მიღება · მინიმუმ {hours} საათით ადრე',
    desk_remote_tenant_notice_evidence: 'დამქირავებლის შეტყობინების მტკიცებულება',
    desk_remote_record_notice: 'შეტყობინების დაფიქსირება',
    desk_remote_handover_title: 'გასაღებების ფაქტობრივი ჩაბარება · დანართი A',
    desk_remote_handover_first: 'ჯერ შეავსეთ ქვემოთ ჩაბარების აქტი: ახალი ფოტოები, მდგომარეობა, გასაღებები, მრიცხველები, ინვენტარი და ხარვეზები.',
    desk_remote_record_time: 'ფაქტობრივი დროის დაფიქსირება',
    desk_remote_then_annex: 'ამის შემდეგ გამქირავებელი და დამქირავებელი იმავე პირად ბმულზე ხელს აწერენ მხოლოდ დანართი A-ს.',
    desk_remote_defect_title: 'დამატებით აღმოჩენილი ძველი ხარვეზი',
    desk_remote_defect: 'ხარვეზი',
    desk_remote_defect_evidence: 'ფოტო/ვიდეო მტკიცებულება ან ბმული',
    desk_remote_add_record: 'ცალკე ჩანაწერის დამატება',
    desk_tfee_pending_result: 'საშუამავლო შედეგს ელოდება',
    desk_tfee_due: 'გადასახდელია',
    desk_tfee_partial: 'ნაწილობრივ გადახდილია',
    desk_tfee_paid: 'გადახდილია',
    desk_tfee_record_failed: 'გადახდის დაფიქსირება ვერ მოხერხდა',
    desk_tfee_title: 'დამქირავებლის სააგენტოს საფასური',
    desk_tfee_total: 'სულ',
    desk_tfee_remaining: 'დარჩენილი',
    desk_tfee_last_received: 'ბოლო მიღება:',
    desk_tfee_note: 'მიღებული თანხა დაადასტურეთ მხოლოდ თანხის მიღების მტკიცებულებით. ხელმოწერილი ხელშეკრულება უცვლელი რჩება.',
    desk_tfee_amount_received: 'მიღებული თანხა',
    desk_tfee_evidence_type: 'მტკიცებულების ტიპი',
    desk_contract_ev_cash_receipt: 'ნაღდი თანხის მიღების ქვითარი',
    desk_tfee_payment_evidence: 'გადახდის მტკიცებულება',
    desk_tfee_lease_evidence: 'ქირავნობის დადების მტკიცებულება',
    desk_tfee_document_ref: 'დოკუმენტის ნომერი / ბმული',
    desk_tfee_record: 'მიღების დაფიქსირება',
    desk_contract_copied: 'დაკოპირდა!',
    // ── ხელშეკრულებების ტაბი (მფლობელის გადაწყვეტილება 2026-09-11) ──────────────────────
    // ── გამგზავნი ფოსტა ხელმოწერილი ხელშეკრულებებისთვის (მფლობელი, 2026-09-11) ─────────
    mail_section: 'გამგზავნი ფოსტა',
    mail_why: 'მისამართი, საიდანაც იგზავნება ხელმოწერილი ხელშეკრულებები. პაროლი პირდაპირ სერვერზე მიდის და აღარსად ჩანს.',
    mail_on: 'გაგზავნა ჩართულია',
    mail_off: 'არ არის გამართული — ფოსტით ჯერ ვერაფერი იგზავნება',
    mail_provider: 'პროვაიდერი',
    mail_host: 'SMTP სერვერი',
    mail_port: 'პორტი',
    mail_user: 'ფოსტა',
    mail_pass: 'პაროლი',
    mail_pass_keep: 'ცარიელი დატოვეთ არსებულის შესანარჩუნებლად',
    mail_from: 'გამგზავნი, როგორც კლიენტი ხედავს',
    mail_save: 'შემოწმება და შენახვა',
    mail_port_blocked: 'სერვერი ამ პორტზე ფოსტას ვერ უკავშირდება — Gmail-ისთვის გამოიყენეთ პორტი 587.',
    mail_saved: 'შენახულია — ავტორიზაცია წარმატებულია',
    mail_failed: 'დაკავშირება ვერ მოხერხდა',
    mail_test: 'სატესტო წერილი',
    mail_test_to: 'მისამართი',
    mail_test_sent: 'სატესტო წერილი გაიგზავნა',
    mail_admin_only: 'გამგზავნი ფოსტის გამართვა მხოლოდ ადმინისტრატორს შეუძლია.',
    mail_app_password_hint: 'ორსაფეხურიანი ავტორიზაციისას ჩვეულებრივი პაროლი არ მიიღება — შექმენით აპლიკაციის პაროლი ფოსტის ანგარიშში და ის ჩასვით აქ.',
    // ბარათის ციფრები — მოკლედ, რადგან ეს სიაშია და არა ფორმაში.
    contracts_all: 'ყველა',
    desk_contract_needs_core: 'ეს შეხვედრა ძველი ჩანაწერია და ხელშეკრულებას ვერ დაიტევს. ახალი შეხვედრიდან შედგება.',
    desk_contract_needs_property: 'მიაბით ობიექტი ამ შეხვედრას და ხელშეკრულების შედგენა შესაძლებელი გახდება.',
    desk_contract_type_needs_client: 'სჭირდება კლიენტი',
    desk_contract_type_needs_property: 'სჭირდება ობიექტი',
    desk_contract_needs_client: 'მიუთითეთ კლიენტი ამ შეხვედრაზე და ხელშეკრულების შედგენა შესაძლებელი გახდება.',
    desk_contract_needs_both: 'ხელშეკრულებისთვის საჭიროა როგორც ობიექტი, ისე კლიენტი — ჯერ ორივე მიუთითეთ შეხვედრაზე.',
    desk_contract_needs_rights: 'ხელშეკრულებას მხოლოდ ამ შეხვედრაზე მომუშავე შეადგენს.',
    meeting_contacts_unavailable: 'ამ ობიექტის საკონტაქტოები ვერ წაიკითხა.',

    contracts_no_match_filters: 'ამ ფილტრებით ვერაფერი მოიძებნა.',
    desk_contract_photos_toggle: 'ფოტოების დანართი ამ ხელშეკრულებაში',
    desk_contract_photos_has_photos: 'ჯერ წაშალეთ ფოტოები — ისინი ხელშეკრულებასთან ერთად იწერება',
    agent_sign_for_agency: 'შეუძლია ხელი მოაწეროს senTavus-ის სახელით',
    agent_sign_for_agency_help: 'ჩართვისას აგენტს შეეძლება, მის მიერ მომზადებულ ხელშეკრულებაში დირექტორის ნაცვლად საკუთარი თავი მიუთითოს ხელმომწერად. სხვა ვინმეს მითითება მაინც არ შეუძლია.',
    agent_sign_no_party: 'ამ აგენტს core-ში ჯერ არაფერი უმოქმედია, ამიტომ ანგარიში, რომელსაც უფლება მიენიჭება, ჯერ არ არსებობს.',
    agent_sign_failed: 'ვერ შეიცვალა',
    agent_public_profile: 'საჯარო პროფილი sentavus.com-ზე',
    agent_public_profile_help: 'ახალი აგენტის პროფილი შემოსვლიდან 14 დღეში თავისით ხდება საჯარო, თუ ადმინისტრატორი აქ სხვაგვარად არ გადაწყვეტს.',
    agent_public_profile_public: 'საჯაროა',
    agent_public_profile_pending: 'ავტომატურად გასაჯაროვდება:',
    agent_public_profile_hidden: 'არ არის საჯარო',
    agent_public_profile_none: 'პროფილი ჯერ არ არსებობს — ის იქმნება, როცა აგენტი სისტემას პირველად დაუკავშირდება.',
    agent_public_profile_publish_now: 'ახლავე გასაჯაროება',
    agent_public_profile_block: 'არ გასაჯაროვდეს',
    agent_public_profile_unpublish: 'საიტიდან მოხსნა',
    agent_public_profile_view: 'ნახვა',
    chat_title: 'შეტყობინებები', chat_scope_mine: 'ჩემი', chat_scope_all: 'ყველა', chat_empty: 'შეტყობინებები ჯერ არ არის. აქ გამოჩნდებიან საიტის ვიზიტორები, რომლებიც „მიწერას“ დააჭერენ თქვენთვის ნაცნობ ქონებაზე ან თქვენს პროფილზე.',
    chat_visitor: 'საიტის ვიზიტორი', chat_you: 'თქვენ', chat_waiting: 'პასუხს ელოდება', chat_pick: 'აირჩიეთ მიმოწერა',
    chat_property: 'ქონება', chat_seen: 'ნანახია', chat_placeholder: 'დაწერეთ პასუხი…', chat_send: 'გაგზავნა',
    chat_send_failed: 'ვერ გაიგზავნა — სცადეთ ხელახლა.', chat_read_only: 'ამ მიმოწერას სხვა აგენტი მართავს. შეგიძლიათ წაიკითხოთ, თუმცა პასუხს ვერ გაგზავნით.',
    chat_close: 'დახურვა', chat_back: 'ყველა მიმოწერა',
    chat_tab_clients: 'კლიენტები', chat_tab_team: 'გუნდი', chat_empty_team: 'გუნდური მიმოწერა ჯერ არ არის. დაიწყეთ კოლეგასთან, ან განიხილეთ ბინა ან კლიენტი გუნდთან.',
    chat_new_team: 'ახალი მიმოწერა კოლეგასთან', chat_find_colleague: 'მოძებნეთ კოლეგა…', chat_colleague: 'კოლეგა',
    chat_about_property: 'ბინა', chat_about_client: 'კლიენტი', chat_rooms: 'ოთახი', chat_typing: 'წერს…',
    chat_photo: 'ფოტო', chat_photo_invalid: 'აირჩიეთ JPG, PNG ან WebP ფორმატის ფოტო, არაუმეტეს 5 MB.', chat_from_site: 'sentavus.com',
    chat_flagged_hint: 'შეიცავს ტელეფონის ნომერს — ხედავენ მენეჯერები', chat_discuss_team: 'გუნდთან განხილვა', chat_discuss_not_yours: 'ამ კლიენტის გუნდურ ჩატს მხოლოდ მისი აგენტები და მენეჯმენტი ხსნიან.', chat_tab_flagged: 'მონიშნული', chat_flagged_direct: 'პირადი ჩატი', chat_flagged_intro: 'გუნდის შეტყობინებები, რომლებიც ტელეფონის ნომერს შეიცავდა. ისინი ისე გაიგზავნა, როგორც დაიწერა; ეს სია გადასახედადაა.', chat_flagged_empty: 'მონიშნული შეტყობინებები არ არის.',
    // ჯგუფები + საერთო ჩატი (მფლობელი 2026-09-29, core 2717)
    chat_general: 'ყველა აგენტი', chat_general_hint: 'ყველა აქტიური აგენტი — ხედავთ შეტყობინებებს სააგენტოში მოსვლის დღიდან', chat_new_group: 'ახალი ჯგუფი', chat_group_name: 'ჯგუფის სახელი…',
    chat_group_pick: 'აირჩიეთ კოლეგები', chat_group_create: 'ჯგუფის შექმნა', chat_group_add: 'ხალხის დამატება', chat_group_added: 'დამატება', chat_mute: 'ხმის გამორთვა (შეტყობინება მაინც გამოჩნდება)', chat_unmute: 'ხმის ჩართვა',
    desk_contract_agency_locked: 'სააგენტოს მიერ',
    desk_contract_agency_signed_by: 'სააგენტოს სახელით ხელს აწერს',
    desk_contract_agency_me: 'მე',
    desk_contract_agency_no_signatory: 'ხელმომწერი ჯერ არ არის დანიშნული — ჯერ ადმინისტრატორმა უნდა მიანიჭოს უფლება.',
    desk_contract_agency_default: 'დირექტორი',
    contracts_filters: 'ფილტრები',
    contracts_filters_done: 'შედეგების ჩვენება',
    contracts_search_mode: 'ძებნის რეჟიმი — ზუსტი ძებნისას ფილტრები გამორთულია.',
    contracts_f_all: 'ყველა ხელშეკრულება',
    contracts_f_status: 'სტატუსი',
    contracts_f_type: 'ტიპი',
    contracts_f_lang: 'ენა',
    contracts_f_date: 'თარიღი',
    contracts_f_agent: 'აგენტი',
    contracts_f_agent_mine: 'ჩემი',
    contracts_f_agent_all: 'ყველა',
    contracts_status_opened: 'გახსნილია',
    contracts_date_today: 'დღეს',
    contracts_date_7d: 'ბოლო 7 დღე',
    contracts_date_month: 'ამ თვეში',
    contracts_date_custom: 'პერიოდი',
    desk_contract_kind_sale_preliminary: 'წინასწარი ნასყიდობის ხელშეკრულება',

    contracts_pdf: 'ხელმოწერილი PDF',
    contracts_pdf_failed: 'ვერ გაიხსნა',
    contracts_pdf_engine: 'სერვერი ამჟამად PDF-ს ვერ ქმნის',
    contracts_review_open_links: 'გასაცნობი ბმულები',
    contracts_masked: 'ამ შეხვედრაზე მოქმედება არ შეგიძლიათ — პირადი ნომრები, კონტაქტები და თანხები დამალულია, გასაცნობი ბმულის გაცემაც ვერ მოხერხდება.',
    contracts_count_unit: 'ხელშეკრულება',
    contracts_count_per_page: 'გვერდზე',

    contracts_forms: 'ფორმები',
    contracts_forms_why: 'სააგენტოს სტანდარტული ხელშეკრულებები — შევსების და ხელმოწერის გარეშე, მხოლოდ გასაცნობად. ბმული მუდმივია.',
    contracts_forms_none: 'ფორმები ჯერ არ არის გამოქვეყნებული.',
    contracts_form_open: 'გახსნა',
    contracts_form_copy: 'ბმულის კოპირება',
    contracts_money_rent: 'ქირა',
    contracts_money_deposit: 'უზრუნველყოფის თანხა',
    contracts_money_binder_paid: 'ბე გადახდილი',
    contracts_money_fee: 'საფასური',
    contracts_money_binder_amount: 'ბე',
    contracts_money_deal_price: 'ფასი',
    contracts_money_refund_amount: 'დაბრუნება',
    nav_contracts: 'ხელშეკრულებები',
    contracts_title: 'ხელშეკრულებები',
    contracts_search_ph: 'ჰეში, ბმულის კოდი, სახელი ან ტელეფონი',
    contracts_search_hint: 'სახელი და ტელეფონი — თქვენს ხელშეკრულებებში; ჰეშით ძებნისას საკმარისია პირველი 10 სიმბოლო.',
    contracts_no_match: 'ვერაფერი მოიძებნა — შეამოწმეთ ჰეში',
    contracts_empty: 'ხელშეკრულებები ჯერ არ არის.',
    contracts_error: 'ხელშეკრულებების ჩატვირთვა ვერ მოხერხდა.',
    contracts_unavailable: 'ამ სერვერზე ხელშეკრულებების მოდული ჯერ არ არის.',
    contracts_status_all: 'ნებისმიერი სტატუსი',
    contracts_lang_all: 'ნებისმიერი ენა',
    contracts_clear: 'გასუფთავება',
    contracts_more: 'ნახე მეტი',
    contracts_copy_hash: 'სრული ჰეშის კოპირება',
    contracts_copied: 'დაკოპირდა',
    contracts_no_meeting: 'შეხვედრის გარეშე',
    contracts_signed_title: 'ხელმოწერილია',
    contracts_review_send: 'გასაცნობად გაგზავნა',
    contracts_review_readonly: 'გასაცნობი ბმული მხოლოდ საკითხავია — ხელმოწერა შეუძლებელია.',
    contracts_review_links: 'გასაცნობი ბმულები',
    contracts_review_note_ph: 'შენიშვნა მკითხველისთვის (არასავალდებულო)',
    contracts_review_days_ph: 'დღე',
    contracts_review_mint: 'ბმულის შექმნა',
    contracts_review_minting: 'იქმნება…',
    contracts_review_opens: 'გაიხსნა {n}-ჯერ',
    contracts_review_never: 'ჯერ არ გახსნილა',
    contracts_review_expires: '{at}-მდე',
    contracts_review_revoke: 'ბმულის გაუქმება',
    contracts_review_none: 'გასაცნობი ბმულები ჯერ არ არის.',
    contracts_share_whatsapp: 'WhatsApp',
    contracts_share_email: 'ელფოსტა',
    contracts_email_subject: 'ხელშეკრულება გასაცნობად',
    desk_contract_no_contact: 'ტელეფონი ან ელფოსტა არ არის — ჩატი თავად აირჩიეთ.',
    desk_contract_mint_links: 'ხელმოწერის ბმულების შექმნა',
    desk_contract_choose_client: 'აირჩიეთ კლიენტი',
    desk_contract_progress_restored: 'შეუნახავი მონაცემები აღდგა (შენახული ამ მოწყობილობაზე, {when})',
    desk_contract_progress_discard: 'წაშლა და თავიდან დაწყება',
    desk_contract_draft_unfinished: 'დაუსრულებელი',
    desk_contract_draft_open: 'შევსების გაგრძელება',
    desk_contract_send_confirm: 'გავუგზავნოთ ხელმოწერის ბმული — {role}: {name}{to}?',
    contracts_form_drafts: 'დაუსრულებელი ფორმები',
    desk_contract_draft_delete: 'შავი ვერსიის წაშლა',
    desk_contract_draft_delete_confirm: 'წავშალოთ ეს დაუსრულებელი ხელშეკრულება? მასში შეყვანილი მონაცემები დაიკარგება.',
    desk_contract_tpl_rent_residential_v1: 'ქირავნობის ხელშეკრულება',
    desk_contract_tpl_rent_commercial_v1: 'კომერციული ფართის ქირავნობა',
    desk_contract_tpl_agency_service_v1: 'მომსახურების ხელშეკრულება (მესაკუთრე)',
    desk_contract_tpl_tenant_agency_service_v1: 'მომსახურების ხელშეკრულება (დამქირავებელი)',
    desk_contract_tpl_advance_v1: 'ავანსისა და ქონების დაჯავშნის შეთანხმება',
    desk_contract_tpl_binder_v1: 'ბეს გადაცემის შეთანხმება',
    desk_contract_void_btn: 'გაუქმება',
    desk_contract_void_why: 'რატომ უქმდება?',
    desk_contract_void_confirm: 'ხელშეკრულების გაუქმება',
    desk_contract_new: 'ხელშეკრულება',
    desk_contract_new_title: 'ახალი ხელშეკრულება',
    desk_contract_fee_model_owner: 'იხდის მესაკუთრე',
    desk_contract_fee_model_both: 'იხდიან მესაკუთრე და დამქირავებელი',
    desk_contract_tenant_part_of_both: 'დამქირავებლის წილი, როცა ორივე იხდის',
    desk_contract_policy_OWNER_PAYS_RESIDENTIAL_RENTAL_V1: 'იხდის მესაკუთრე — თანამშრომლობის განაკვეთით',
    desk_contract_policy_BOTH_PAY_RESIDENTIAL_RENTAL_V1: 'იხდიან მესაკუთრე და დამქირავებელი — ქირის 20% + 20%',
    desk_contract_choose_type: 'აირჩიეთ ტიპი',
    desk_contract_types_none: 'ხელშეკრულების შაბლონები არ არის.',
    desk_contract_open_page: 'ხელშეკრულების გვერდის გახსნა',
    desk_contract_page_loading: 'იტვირთება…',
    desk_contract_not_found: 'ხელშეკრულება ვერ მოიძებნა.',
    desk_contract_bad_link: 'ამ ბმულში ხელშეკრულება მითითებული არ არის.',
    desk_contract_copy_page: 'გვერდის ბმულის კოპირება',
    desk_contract_back: 'უკან',
    desk_contract_meeting_line: 'შეხვედრა',
    desk_contract_document: 'დოკუმენტი',
    desk_contract_links_section: 'ხელმოწერის ბმულები',
    desk_contract_save_btn: 'შენახვა',
    desk_contract_saved: 'შენახულია ახალ ვერსიად — ხელმოწერის ბმულები განახლდა, ხელახლა გააგზავნეთ.',
    desk_contract_created: 'შეიქმნა. შექმენით ხელმოწერის ბმულები და გააგზავნეთ.',
    desk_contract_edit_discard_btn: 'ცვლილება — ხელმოწერები გაუქმდება',
    desk_contract_edit_discard_hint: 'უკვე ხელი მოაწერა: {who}. ცვლილების შენახვა ახალ ვერსიას ქმნის; მათი ხელმოწერა ძველ ვერსიაზე რჩება და ხელახლა უნდა მოაწერონ.',
    desk_contract_edit_discard_hint_short: 'შენახვა უკვე გაცემულ ხელმოწერებს აუქმებს.',
    desk_contract_signatures: 'ხელმოწერები',
    desk_contract_sig_none: 'არ არის ხელმოწერილი',
    desk_contract_method_remote_link: 'პირადი ბმულით, დისტანციურად',
    desk_contract_method_assisted_device: 'აგენტის მოწყობილობიდან',
    desk_contract_method_paper_uploaded: 'ქაღალდზე ხელმოწერილი, ატვირთული',
    desk_contract_method_qualified_esign: 'კვალიფიციური ელ. ხელმოწერა',
    desk_contract_print: 'ბეჭდვა',
    desk_contract_err_signed: 'სრულად ხელმოწერილია — აღარ იცვლება. გააუქმეთ და შექმენით ახალი.',
    desk_contract_err_partial: 'ამ ვერსიას უკვე მოაწერა ვიღაცამ ხელი. გამოიყენეთ „ცვლილება — ხელმოწერები გაუქმდება“.',
    desk_contract_msg_text: 'გამარჯობა, {name}. გთხოვთ გაეცნოთ და ხელი მოაწეროთ ხელშეკრულებას ამ ბმულზე: {url} — senTavus',
    desk_contract_msg_text_en: 'Hello, {name}. Please read and sign the agreement at this link: {url} — senTavus',
    desk_contract_lang_ru: 'რუსული',
    desk_contract_lang_second: 'მეორე ენა',
    desk_contract_lang_none: 'არა',
    desk_contract_lang_primary_hint: 'უპირატესი ტექსტი',
    desk_contract_name_in_en: 'სახელი ინგლისურად',
    desk_contract_name_in_ru: 'სახელი რუსულად',
    desk_contract_name_in_ph: 'როგორც ამ ენაზე უნდა ეწეროს (არასავალდებულო)',
    desk_contract_name_in_ka: 'სახელი ქართულად',
    desk_contract_legal_name: 'სახელი, გვარი — როგორც პირადობაშია',
    desk_contract_link_lang: 'ბმულის ენა',
    desk_contract_party_kind: 'მხარის ტიპი',
    desk_contract_party_kind_person: 'ფიზიკური პირი',
    desk_contract_party_kind_company: 'კომპანია',
    desk_contract_company_name: 'კომპანიის იურიდიული სახელწოდება — როგორც რეესტრშია',
    desk_contract_company_name_ph: 'მაგ. შპს ალფა',
    desk_contract_id_kind: 'დოკუმენტი',
    desk_contract_id_personal_no: 'პირადობის მოწმობა',
    desk_contract_id_passport_hint: 'ასოები და ციფრები, ნებისმიერი ქვეყნის ფორმატით',
    desk_contract_idnum_personal_no: 'პირადი ნომერი · 11 ციფრი',
    desk_contract_idnum_passport: 'პასპორტის ნომერი · ასოები და ციფრები, ქვეყნის ფორმატით',
    desk_contract_idnum_company_reg: 'საიდენტიფიკაციო ნომერი',
    desk_contract_cobroke_block: 'მეორე აგენტი',
    desk_contract_notice_channels: 'შეტყობინებების არხები',
    desk_contract_notice_channels_help: 'ამ კონტაქტებზე SMS-ით, WhatsApp-ით ან ელფოსტით გაგზავნილი შეტყობინება ჩაბარებულად ითვლება მომდევნო სამუშაო დღეს. მხარე, რომელსაც გაზიარებული კონტაქტი არ აქვს, არ მიეთითება — წერილობითი შეტყობინება მისამართზე მაინც მოქმედებს.',
    desk_contract_notice_other: 'სხვა არხი',
    desk_contract_contact_share: 'საკონტაქტო ინფორმაციის გაზიარება მეორე მხარეს',
    desk_contract_contact_hide: 'კონტაქტის დამალვა ხელმოწერამდე',
    desk_contract_contact_omit: 'კონტაქტის დამალვა',
    desk_contract_contact_omit_on: 'ხელშეკრულებაში არ მიეთითება; სხვა მხარეები ვერ ნახავენ.',
    desk_contract_contact_omit_off: 'მიეთითება ხელშეკრულებაში:',
    desk_contract_contact_hide_on: 'მეორე მხარე ვერ ნახავს, სანამ ყველა არ მოაწერს ხელს; შემდეგ მიეთითება ხელმოწერილ PDF-ში.',
    desk_contract_many_residents: 'მუდმივ მაცხოვრებელთა უჩვეულოდ დიდი რაოდენობაა. დაადასტურეთ გამქირავებელთან, რომ სწორედ ეს შეთანხმდა.',
    desk_contract_contact_hide_off: 'მეორე მხარე ხედავს თავიდანვე და მიეთითება ხელშეკრულებაში.',
    desk_contract_contact_share_on: 'ყველა ხელმოწერის შემდეგ მეორე მხარე მიიღებს:',
    desk_contract_contact_share_off: 'არ გაზიარდება — შეტყობინებები senTavus-ის მეშვეობით. ხელმომწერს შეუძლია შეცვალოს თავის გვერდზე.',
    desk_contract_notice_same: 'გამოიყენება ზემოთ მითითებული საკონტაქტო ინფორმაცია',
    desk_contract_notice_hide: 'დამალვა',
    desk_contract_notice_missing: 'არხი არ არის — ბმულები ვერ გაიგზავნება, სანამ არ მიეთითება',
    desk_contract_notice_address: 'ფიზიკური მისამართი',
    desk_contract_notice_private_help: 'კონფიდენციალურია: მეორე მხარე ამას ვერ ხედავს. თითოეული ხელმომწერი თავისას ადასტურებს ხელმოწერის გვერდზე.',
    desk_contract_notice_none: 'გაზიარებული კონტაქტი არ არის — წერილობით, მისამართზე',
    desk_contract_notice_phone: 'ტელეფონი (SMS / WhatsApp)',
    desk_contract_notice_use_party: 'მხარის კონტაქტების გამოყენება',
    desk_contract_fee_cobroke_note: 'სხვა აგენტის მონაწილეობისას ჩაწერეთ სრული საკომისიო, რომელსაც მესაკუთრე იხდის — ორივე წილი ერთად (მაგ. 600 = 300 + 300).',
    desk_contract_agency_share: 'senTavus-ის წილი',
    desk_contract_agency_share_zero: 'საკომისიოში ჩაწერეთ სრული თანხა, რომელსაც მესაკუთრე იხდის — ორივე წილი ერთად (მაგ. 600, თუ 300 + 300).',
    desk_contract_share_auto: 'საკომისიოს ნახევარი, შევსებულია ავტომატურად',
    desk_contract_id_11_error: 'პირადი ნომერი ზუსტად 11 ციფრია.',
    desk_contract_id_duplicate_error: 'ორივე მხარეს ერთი და იგივე ნომერი უწერია. ერთ-ერთი მათგანი სხვა ადამიანისაა.',
    desk_contract_date_end_before_start: 'ქირავნობა დასრულდებოდა დაწყებამდე.',
    desk_contract_date_deadline_before_agreement: 'ვადა უფრო ადრეა, ვიდრე იმ ხელშეკრულების თარიღი, რომელიც მას აწესებს.',
    desk_contract_amount_exceeds_advance: 'თანხა აღემატება ავანსის ოდენობას.',
    desk_contract_amount_currency_differs_from_advance: 'თანხა ავანსის ვალუტაში არ არის.',
    desk_contract_amount_negative: 'თანხა უარყოფითი არ შეიძლება იყოს.',
    desk_contract_reason_required_for_smaller_refund: 'მიუთითეთ, რატომ ბრუნდება ავანსზე ნაკლები თანხა.',
    desk_contract_reason_without_smaller_refund: 'ბრუნდება ავანსის სრული ოდენობა — მიზეზი წაშალეთ.',
    desk_contract_err_advance_amount: 'ავანსის ნაწილი აღემატება ავანსს ან სხვა ვალუტაშია.',
    desk_contract_date_deadline_after_move_in: 'ძირითადი ხელშეკრულების გაფორმების ვადა ქირავნობის დაწყების შემდეგაა.',
    desk_contract_date_contingency_before_agreement: 'პირობის ვადა უფრო ადრეა, ვიდრე იმ ხელშეკრულების თარიღი, რომელიც მას აწესებს.',
    desk_contract_parts_total: 'ნაწილები ჯამში {n}-ია, გადახდილი თანხა კი {m}.',
    desk_contract_parts_currency: 'ნაწილები იმავე ვალუტაში უნდა იყოს, რაშიც გადახდილი თანხაა.',
    desk_contract_agent_none: 'ავტორიზებული აგენტი არ არის',
    desk_contract_err_fund_receiver: 'თანხის მიმღებად მითითებულ პირს კომპანიის თანხის მიღების უფლება აღარ აქვს — აირჩიეთ სხვა',
    desk_annex_date_handover: 'ჩაბარების თარიღი და დრო',
    desk_annex_date_return: 'ქონების დაბრუნებისა და გასაღებების ჩაბარების თარიღი',
    desk_annex_date_needed: 'ხელმოწერამდე სავალდებულოა',
    desk_annex_carry_over: 'ჩაბარების აქტიდან გადმოტანა',
    desk_return_sec_inventory: 'ინვენტარი — ჩაბარებისას აღწერილი',
    desk_contract_fxdate_agreement: 'ხელშეკრულების თარიღი',
    desk_contract_fxdate_payment: 'გადახდის თარიღი',
    desk_contract_fxdate_today: 'დღეს',
    desk_contract_bad_value: 'ხელშეკრულების ამ ვერსიაში ამ პასუხის დაფიქსირება შეუძლებელია.',
    desk_contract_share: 'გაზიარება',
    desk_contract_shared: 'გაზიარება ✓',
    desk_contract_share_tip: 'ჩანს მეორე მხარისთვის და იბეჭდება ხელშეკრულებაში',
    desk_contract_id_passport: 'პასპორტი',
    desk_contract_id_company_reg: 'კომპანიის საიდენტიფიკაციო ნომერი',
    desk_contract_id_number: 'ნომერი',
    desk_contract_id_number_ph: 'სავალდებულო — როგორც დოკუმენტშია',
    desk_contract_rep_name: 'წარმომადგენელი — სახელი, გვარი',
    desk_contract_rep_position: 'თანამდებობა / უფლებამოსილება',
    desk_contract_rep_position_ph: 'მაგ. დირექტორი',
    desk_contract_phone: 'ტელეფონი',
    desk_contract_email: 'ელფოსტა',
    desk_contract_share_phone: 'ჩანს მეორე მხარისთვის და ხელშეკრულებაში',
    desk_contract_share_email: 'ჩანს მეორე მხარისთვის და ხელშეკრულებაში',
    desk_contract_identity_note: 'ეს მონაცემები უნდა შეავსოთ ხელმოწერის ბმულის გაგზავნამდე. მხარეები მონაცემებს ამოწმებენ და ხელმოწერით ადასტურებენ. ხელმომწერს მათი შეცვლა არ შეუძლია. ხელმოწერის შემდეგ ნებისმიერი ცვლილება ქმნის ხელშეკრულების ახალ ვერსიას.',
    desk_contract_identity_confirmed: 'მხარემ დაადასტურა',
    desk_contract_lang_first_prevails: 'პირველი ენა უპირატესია',
    desk_contract_lang_prevails_note: 'ქართული გამორთულია — უპირატესია {lang} ტექსტი.',
    desk_contract_err_drift: 'თარგმანები არ ემთხვევა — იხილეთ ქვემოთ ჩამოთვლილი პუნქტები.',
    desk_contract_drift_title: 'თარგმანის შეუსაბამობა',
    desk_contract_drift_placeholders: 'პუნქტი {clause_no}: {lang} ტექსტში სხვა ველებია',
    desk_contract_drift_missing_text: 'პუნქტი {clause_no}: {lang} ტექსტი აკლია',
    desk_contract_sections: 'სექციები',
    desk_contract_sections_hint: 'შეგიძლიათ გამორთოთ მხოლოდ დამატებითი სექციები.',
    desk_contract_sections_locked: 'ხელმოწერის შემდეგ სექციები დაკეტილია.',
    desk_contract_section_mandatory: 'ყოველთვის შედის',
    desk_contract_section_conditional: 'სექცია დამოკიდებულია შესაბამის ველზე.',
    desk_contract_section_follows_answer: 'პასუხის მიხედვით',
    desk_contract_section_custom: 'საკუთარი',
    desk_contract_section_custom_title: 'საკუთარი სექცია',
    desk_contract_section_on: 'ჩართულია — გამორთვა',
    desk_contract_section_off: 'გამორთულია — ჩართვა',
    desk_contract_section_add: 'სექციის დამატება',
    desk_contract_section_remove: 'სექციის წაშლა',
    desk_contract_section_editors: 'სექციების ტექსტი',
    desk_contract_clause_text_hint: '{{…}} — ზემოთ მოცემული ფორმის მნიშვნელობებია და დოკუმენტში ავტომატურად ჩაისმება.',
    desk_contract_clause_shown_when: 'ჩანს, როცა',
    desk_contract_clause_edited: 'შეცვლილი',
    desk_contract_clause_reset: '↺ საწყისი ტექსტი',
    desk_contract_photos_soon: 'ფოტოები — მალე',
    desk_contract_photos_tip: 'ქონების მდგომარეობის ფოტოები — დანართი, რომელსაც მხარეები ხელშეკრულებასთან ერთად აწერენ ხელს',
    desk_contract_annex_pending_note: 'ფოტოები ერთვის ხელშეკრულების მიმდინარე ვერსიას.',
    // ── დახურული ხელშეკრულება (მიგრაცია 1046): ხელმოწერები დოკუმენტზე, PDF ──
    desk_contract_closed_chip: 'დახურულია · ხელმოწერილია',
    desk_contract_completed_at: 'დასრულდა · {at}',
    desk_contract_signed_at: 'ხელი მოაწერა · {at}',
    desk_contract_viewed_lang: 'ნახა ენაზე',
    desk_contract_doc_hash: 'დოკუმენტის ჰეში (SHA-256)',
    desk_contract_prevails_short: 'უპირატესია',
    desk_contract_annex_photo_n: 'ფოტო {n}',
    desk_contract_sig_email_will: 'PDF გაიგზავნება: {email}',
    desk_contract_sig_email_sent: 'PDF გაგზავნილია: {email}',
    desk_contract_sig_no_email: 'ელფოსტა არ არის მითითებული — PDF-ს ელფოსტით ვერ მიიღებს',
    desk_contract_pdf_btn: 'PDF',
    desk_contract_pdf_loading: 'PDF მზადდება…',
    desk_contract_pdf_missing: 'PDF ჯერ არ არის ხელმისაწვდომი — სცადეთ მოგვიანებით',
    desk_contract_pdf_send_btn: 'PDF-ის გაგზავნა ელფოსტაზე',
    desk_contract_pdf_sending: 'იგზავნება…',
    desk_contract_pdf_sent_at: 'PDF გაგზავნილია · {at}',
    desk_contract_mail_unconfigured: 'ელფოსტის გაგზავნა ჯერ არ არის კონფიგურირებული — ჩამოტვირთეთ PDF და გააგზავნეთ ხელით',
    desk_contract_send_ok: 'გაიგზავნა: {to}',
    desk_contract_send_fail: 'ვერ გაიგზავნა',
    desk_contract_send_reason_no_email: 'ელფოსტა არ იყო მითითებული',
    desk_contract_send_reason_send_failed: 'საფოსტო სერვერმა უარი თქვა',
    desk_contract_err_not_signed: 'ჯერ ყველას არ მოუწერია ხელი.',
    // Prepayment list (ContractComposer PrepayListInput, template v3 / migration 1048).
    desk_contract_prepay_which: 'რომელი',
    desk_terms_prepay_months: 'წინასწარ გადასახდელი (თვე)',
    desk_terms_prepay_part: 'ნაწილი',
    desk_contract_prepay_full: 'სრულად',
    desk_contract_prepay_paid: 'გადახდილი',
    desk_contract_prepay_remaining: 'დარჩენილი',
    desk_contract_prepay_due: 'ვადა',
    desk_contract_prepay_add: '＋ თვის დამატება',
    desk_contract_prepay_remove: 'წაშლა',
    desk_contract_prepay_none: 'წინასწარ არაფერს იხდის.',
    desk_contract_prepay_none_required: 'დააჭირეთ „＋ თვის დამატება“ და აირჩიეთ თვე, რომლის ქირასაც დამქირავებელი ხელმოწერისას იხდის — მინიმუმ ერთი.',
    // ── დიზაინის აწყობა (2026-09-11, design/CONTRACTS_DESIGNER_HANDOFF_2026-09-11.md) ──
    desk_contract_chip_closed: 'დახურულია',
    desk_contract_chip_signed_of: '{n}/{total} ხელმოწერილი',
    // 🔁 ხელშეკრულების რედაქციები (მფლობელი 2026-10-08, core 3097/3098)
    desk_contract_chip_superseded: 'ჩანაცვლებულია',
    desk_contract_chip_not_yet_effective: 'ხელმოწერილია · ჯერ არ მოქმედებს',
    desk_contract_status_expired: 'ვადაგასული',
    desk_revision_edition: 'რედაქცია {n}',
    desk_revision_new_title: 'ხელშეკრულების ახალი რედაქცია {n}',
    desk_revision_new_replaces: 'ცვლის რედაქცია {n}-ს, ხელმოწერილს {at} (#{hash}). მასში არაფერი იცვლება და არაფერი იშლება.',
    desk_revision_new_rule: 'მოქმედი რედაქცია ძალაში რჩება მანამ, სანამ ამ რედაქციას ყველა მხარე ხელს არ მოაწერს და ის ძალაში არ შევა (დღეს, ან თქვენ მიერ მითითებული თარიღიდან).',
    desk_revision_new_carried: 'შესახლება არ მეორდება: ახალი გასაღების გადაცემა და პირველადი გადახდა არ არის. უკვე გადახდილი დეპოზიტი და წინასწარ გადახდილი ქირა გადმოდის; თავდაპირველი ჩაბარების აქტი რჩება ამ ქირავნობის აქტად.',
    desk_revision_replaces: 'ცვლის რედაქცია {n}-ს, ხელმოწერილს {at}.',
    desk_revision_open: 'გახსნა',
    desk_revision_open_successor: 'გახსენით რედაქცია, რომელიც მას ცვლის',
    desk_revision_effective_date: 'ძალაში შედის {d}-ს (ან ბოლო ხელმოწერისას, თუ ეს უფრო გვიანაა).',
    desk_revision_changes: 'რა შეიცვალა',
    desk_revision_wording: 'ტექსტი განახლდა მიმდინარე შაბლონის მიხედვით (v{from} → v{to})',
    desk_revision_wording_clause: 'პუნქტი {n}',
    desk_revision_wording_removed: 'ეს პუნქტი ხელშეკრულებაში აღარ არის.',
    desk_revision_no_changes: 'ხელმოწერილ რედაქციასთან შედარებით ჯერ არაფერი შეცვლილა.',
    desk_revision_state_pending_until: 'ხელმოსაწერად შეთავაზებულია {at}-მდე',
    desk_revision_state_pending_unsent: 'ჯერ არ გაგზავნილა — 7 დღე იწყება გაგზავნისას',
    desk_revision_state_expired: 'ვადა ამოიწურა ხელმოწერის გარეშე — ძალაში რჩება რედაქცია {n}',
    desk_revision_state_not_yet_effective: 'ყველამ მოაწერა ხელი — ძალაში შედის {at}; მანამდე მოქმედებს წინა რედაქცია',
    desk_revision_state_in_force: 'მოქმედებს {at}-დან',
    desk_revision_state_in_force_plain: 'მოქმედებს',
    desk_revision_state_superseded: 'ჩანაცვლებულია {at}-დან — ინახება როგორც ხელმოწერილის ჩანაწერი',
    desk_revision_state_void: 'გაუქმებულია',
    desk_revision_release_pending: 'ჩანაცვლების პუნქტი ელოდება იურიდიულ განხილვას. ამ რედაქციის მომზადება და წაკითხვა შეიძლება, ხელმოსაწერად გაგზავნა — ჯერ არა.',
    desk_revision_release_btn: 'ამ ხელშეკრულების ხელმოსაწერად დაშვება (მფლობელის თანხმობა)',
    desk_revision_released_at: 'მფლობელმა ხელმოსაწერად დაუშვა {at}',
    desk_revision_pending_exists: 'მზადდება ახალი რედაქცია:',
    desk_revision_chain: 'ამ ქირავნობის რედაქციები',
    desk_revision_chain_pending: 'მზადდება',
    desk_revision_chain_expired: 'ვადაგასული',
    desk_revision_chain_void: 'გაუქმებული',
    desk_revision_chain_not_yet_effective: 'ხელმოწერილი, ჯერ არ მოქმედებს',
    desk_revision_chain_in_force: 'მოქმედებს',
    desk_revision_chain_superseded: 'ჩანაცვლებული',
    desk_revision_new_btn: 'ახალი რედაქცია — პირობების შეცვლა და ხელახლა ხელმოწერა',
    desk_revision_new_btn_hint: 'ახალ რედაქციას ყველა მხარე აწერს ხელს; ძალაში შესვლისას ის ამას ცვლის. ეს ხელშეკრულება უცვლელი რჩება.',
    desk_contract_lang_short_ka: 'ქარ',
    desk_contract_lang_short_en: 'ENG',
    desk_contract_lang_short_ru: 'РУС',
    desk_contract_lang_add: 'ამ ენის დამატება',
    desk_contract_lang_remove: 'ამ ენის მოხსნა',
    desk_contract_lang_locked: 'ენები დაკეტილია — ცვლილება ახალი ვერსიაა',
    desk_contract_group_property: 'ობიექტი',
    desk_contract_group_money: 'ვადა და თანხები',
    desk_contract_group_use: 'სარგებლობა და პირობები',
    desk_contract_group_other: 'სხვა',
    desk_contract_src_version: 'v{v}-დან',
    desk_contract_sections_tap_hint: 'დააჭირეთ სექციას ტექსტის სანახავად ან შესაცვლელად',
    desk_contract_clause_edit: 'შეცვლა',
    desk_contract_clause_original: '↺ საწყისი',
    desk_contract_clause_empty: 'ტექსტი ჯერ არ არის',
    desk_contract_marker_hint: 'მწვანე მარკერები მნიშვნელობებია — ზედ არ დაწეროთ.',
    desk_contract_collapse: 'აკეცვა',
    desk_contract_done: 'მზადაა',
    desk_contract_drift_ph_named: 'პუნქტი {clause_no} · {detail} აკლია {lang} ტექსტში',
    desk_contract_drift_text_named: 'პუნქტი {clause_no} · {lang} ტექსტი აკლია',
    desk_contract_drift_short: 'შეუსაბამობა',
    desk_contract_section_always_on: 'ყოველთვის',
    desk_contract_photos_row: 'ფოტოები',
    desk_contract_soon: 'მალე',
    desk_contract_bar_sections: 'ჩართულია {on}/{total} სექცია',
    desk_contract_bar_fields_left: 'შესავსებია {n}',
    desk_contract_bar_ready: 'მზადაა',
    desk_contract_fields_left: 'შესავსებია {n} ველი: {list}',
    desk_contract_save_new_version: 'შენახვა · ახალი ვერსია',
    desk_contract_rerender_tip: 'ახალი ვერსია ფურცელზე არსებული მნიშვნელობებით; ბმულები თავიდან გაიცემა.',
    desk_contract_frozen_who: '{name}-მა ხელი მოაწერა {at}.',
    desk_contract_frozen_hint: 'ტექსტი გაყინულია. ნებისმიერი ცვლილება აუქმებს ხელმოწერებს და გამოსცემს ახალ ვერსიას — v{n}; ყველა თავიდან აწერს ხელს.',
    desk_contract_change_btn: 'ცვლილება — ხელმოწერები გაუქმდება',
    desk_contract_closed_line: 'ხელმოწერილია {at} · v{v} · #{hash}',
    desk_contract_pdf_download: 'PDF-ის ჩამოტვირთვა',
    desk_contract_parties_links: 'მხარეები და ბმულები',
    desk_contract_history: 'ისტორია',
    desk_contract_history_none: 'ჯერ არაფერი',
    // The condition-photo annex card (design §9.4 / §8.1, contract/AnnexCard.jsx).
    desk_contract_annex_title: 'დანართი · ქონების მდგომარეობა',
    desk_contract_annex_count: '{n} ფოტო',
    desk_contract_annex_signed_by: 'ხელმოწერილია: {roles}',
    desk_contract_annex_add: '＋ ფოტოს დამატება',
    desk_contract_annex_uploading: 'იტვირთება {done} / {total}',
    desk_contract_annex_empty: 'ფოტოები უნდა ასახავდეს ქონების მდგომარეობას ჩაბარების მომენტში. დაამატეთ ისინი ხელმოწერის ბმულების გაგზავნამდე.',
    desk_contract_annex_note: 'მხარეები ხელმოწერამდე ხედავენ სწორედ ამ ფოტოებს. ხელმოწერის შემდეგ ფოტოს დამატება, წაშლა ან შეცვლა ქმნის ახალ ვერსიას.',
    desk_contract_annex_confirm: 'ეს გააუქმებს {n} ხელმოწერას — გაგრძელება?',
    desk_contract_annex_confirm_yes: 'გაგრძელება',
    desk_contract_annex_caption_ph: 'წარწერა',
    desk_contract_annex_room_ph: 'ოთახი',
    desk_contract_annex_rooms: 'მისაღები|სამზარეულო|საძინებელი|სააბაზანო|დერეფანი|აივანი',
    desk_contract_annex_remove: 'ფოტოს წაშლა',
    desk_contract_annex_readonly: 'ხელშეკრულება დახურულია — დანართი აღარ იცვლება.',
    desk_contract_annex_after_signing: 'ეს ხელშეკრულება ხელმოწერილია ჩაბარების აქტის გარეშე. შეავსეთ აქტი ახლა, გასაღებების გადაცემისას: თარიღი, მდგომარეობა, გასაღებები და მრიცხველები (ფოტოებიც, თუ გაქვთ). შემდეგ გამქირავებელი და დამქირავებელი დაადასტურებენ მას იმავე ბმულიდან, რომლითაც ხელი მოაწერეს.',
    desk_contract_annex_after_signing_home: 'ჩაბარების აქტი ქირავნობას ეკუთვნის და ინახება მე-{n} რედაქციასთან, რომელიც აქტის გარეშე მოეწერა.',
    desk_contract_annex_after_signing_open: 'შეავსეთ იქ →',
    desk_contract_annex_pdf: 'ჩაბარების აქტის PDF',
    desk_contract_send_no_act: 'ამ ხელშეკრულებას ჯერ არ აქვს ჩაბარების აქტი — არც მდგომარეობის ჩანაწერი და არც ფოტოები. ხელმომწერებს ეცნობებათ, რომ აქტი შედგება და მოეწერება გასაღებების გადაცემისას. გავაგზავნოთ მაინც?',
    desk_contract_send_no_act_yes: 'გაგზავნა აქტის გარეშე',
    desk_contract_send_no_act_fill: 'ჯერ აქტის შევსება',
    desk_contract_annex_err_signed: 'ხელშეკრულება სრულად ხელმოწერილია — დანართი ვეღარ შეიცვლება.',
    desk_contract_annex_err_type: 'ფაილის ტიპი არ არის მხარდაჭერილი — გამოაგზავნეთ ფოტო (JPEG, PNG, HEIC).',
    desk_contract_annex_err_storage: 'ფოტოების საცავი სერვერზე ჯერ არ არის კონფიგურირებული.',
    desk_contract_annex_err_too_large: '„{name}“ ზედმეტად დიდია გასაგზავნად ({mb} MB).',
    desk_contract_event_created: 'შეიქმნა',
    desk_contract_event_rendered: 'დაფორმირდა',
    desk_contract_event_link_minted: 'ბმულები გაიცა',
    desk_contract_event_delivered: 'გაიგზავნა',
    desk_contract_event_opened: 'გაიხსნა',
    desk_contract_event_signed: 'ხელი მოეწერა',
    desk_contract_event_declined: 'უარყოფილია',
    desk_contract_event_rerendered: 'ახალი ვერსია',
    desk_contract_history_until: 'ბოლოს',
    desk_contract_event_annex_rerendered: 'ჩაბარების ჩანაწერი განახლდა',
    desk_contract_event_return_annex_rerendered: 'დაბრუნების ჩანაწერი განახლდა',
    desk_contract_event_annex_signed: 'ჩაბარების აქტს ხელი მოეწერა',
    desk_contract_event_return_act_signed: 'დაბრუნების აქტს ხელი მოეწერა',
    desk_contract_event_annex_photo_added: 'ჩაბარების აქტს ფოტო დაემატა',
    desk_contract_event_annex_photo_removed: 'ჩაბარების აქტიდან ფოტო წაიშალა',
    desk_contract_event_annex_sign_failed: 'ჩაბარების აქტზე ხელმოწერა ვერ მოხერხდა',
    desk_contract_event_agency_rep_changed: 'სააგენტოს ხელმომწერი შეიცვალა',
    desk_contract_event_commercial_policy_changed: 'კომერციული პირობები შეიცვალა',
    desk_contract_event_fund_receiver_changed: 'თანხის მიმღები შეიცვალა',
    desk_contract_event_identity_confirmed: 'პირადობა დადასტურდა',
    desk_contract_event_payment_evidence_reversed: 'გადახდის ჩანაწერი გაუქმდა',
    desk_contract_event_pdf_downloaded: 'PDF ჩამოიტვირთა',
    desk_contract_event_pdf_mail_failed: 'PDF-ის ელფოსტით გაგზავნა ვერ მოხერხდა',
    desk_contract_event_pdf_render_failed: 'PDF ვერ შეიქმნა',
    desk_contract_event_review_link_minted: 'გასაცნობი ბმული შეიქმნა',
    desk_contract_event_review_link_revoked: 'გასაცნობი ბმული გაუქმდა',
    desk_contract_event_review_opened: 'გასაცნობი ბმული გაიხსნა',
    desk_contract_event_sign_blocked_fund_receiver: 'ხელმოწერა შეჩერდა — თანხის მიმღები არ არის უფლებამოსილი',
    desk_contract_event_template_upgraded: 'გადავიდა ხელშეკრულების ბოლო ტექსტზე',
    desk_contract_event_tenant_agency_fee_received: 'დამქირავებლის საკომისიო მიღებულია',
    desk_contract_event_voided: 'გაუქმდა',
    desk_contract_event_completed: 'დასრულდა — ყველამ მოაწერა ხელი',
    desk_contract_event_pdf_rendered: 'PDF შეიქმნა',
    desk_contract_event_pdf_sent: 'PDF გაიგზავნა ელფოსტით',
    desk_contract_tl_not_minted: 'ბმული ჯერ არ არის',
    desk_contract_tl_ready: 'მზადაა',
    desk_contract_tl_sent: 'გაიგზავნა ({via})',
    desk_contract_tl_sent_none: 'გაიგზავნა',
    desk_contract_tl_opened: 'გახსნა ({lang})',
    desk_contract_tl_signed: 'ხელი მოაწერა',
    desk_contract_tl_declined: 'უარყო · {reason}',
    desk_contract_handover_btn: 'ჩემი ტელეფონის გადაცემა — {name}',
    desk_contract_handover_tip: 'ამ მოწყობილობაზე იხსნება მისი საკუთარი ბმული, რომელიც გადაცემულად მოინიშნება; ხელმოწერა ჩაიწერება როგორც აგენტის მოწყობილობიდან.',
    desk_contract_new_contract: 'ახალი ხელშეკრულება',
    desk_contract_type_agency_soon: 'სააგენტოს მომსახურების ხელშეკრულება',
    desk_contract_type_sale_soon: 'ნასყიდობა',
  }
}

export function t(key) {
  const lang = getLanguage()
  return STRINGS[lang]?.[key] ?? STRINGS['en']?.[key] ?? key
}

// The same key in a NAMED language — for bilingual labels (the property modal shows the section name in
// Georgian and English side by side). Falls back like t(); returns '' when that language lacks the key,
// so a caller never prints a raw key as the second label.
export function tIn(lang, key) {
  return STRINGS[lang]?.[key] ?? ''
}

// tOr — translate with a REAL fallback for dynamic keys. `t(key) || fallback` is
// structurally dead (t() returns the key itself when missing, never a falsy), so a
// vocabulary value with no translation rendered as the literal key string — every
// closed-deal lead badge showed "desk_coop_end_deal_closed" until 2026-07-23.
export function tOr(key, fallback) {
  const v = t(key)
  return v === key ? fallback : v
}

// tLog — translate a log message template and fill in {placeholders}
export function tLog(key, vars = {}) {
  let str = t(key)
  Object.entries(vars).forEach(([k, v]) => {
    str = str.replaceAll(`{${k}}`, v ?? '')
  })
  return str
}

export function tDays() { return t('days') }
